'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import {
	AlertTriangle,
	ArrowRight,
	BadgeCheck,
	Bell,
	Bot,
	Building2,
	CalendarCheck,
	CheckCircle2,
	ClipboardCheck,
	Clock3,
	ChevronLeft,
	ChevronRight,
	Download,
	Eye,
	FileText,
	Fingerprint,
	Gift,
	GitBranch,
	Mail,
	MapPin,
	Pencil,
	Plus,
	RefreshCw,
	Save,
	Search,
	ShieldCheck,
	Target,
	Trash2,
	TrendingUp,
	UsersRound,
	WalletCards
} from 'lucide-react';
import type { ComponentType, FormEvent, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, getErrorMessage } from '$lib/api/client';
import {
	DEDUCTION_PRESETS,
	EARNING_PRESETS,
	formatWon,
	importRowFailReasonLabel,
	importRowStatusLabel,
	importRowStatusVariant,
	importStatusLabel,
	importStatusVariant,
	skipReasonLabel
} from '$lib/payroll';
import type {
	AdminCompanyResponse,
	AdminPaymentResponse,
	AgentActionDraftCreateRequest,
	AgentActionDraftDecisionRequest,
	AgentActionDraftResponse,
	AgentActionDraftStatus,
	AgentAutomationFlowResponse,
	AgentAutomationStatus,
	AgentCardResponse,
	AgentImpactPreviewResponse,
	AgentKnowledgeSourceResponse,
	AgentOperationsChecklistItem,
	AgentOperationsChecklistStatus,
	AgentOperationsRiskItem,
	AgentOperationsRiskSeverity,
	AgentOperationsSummaryResponse,
	AgentPermissionScopeResponse,
	AgentPermissionStatus,
	AgentStatus,
	AgentTaskPriority,
	AgentTaskSuggestionResponse,
	AppHrInsightsResponse,
	AuditLogResponse,
	ApprovalDecisionRequest,
	ApprovalResponse,
	ApprovalLineResponse,
	ApprovalReferenceResponse,
	AttendanceOperationsChecklistItem,
	AttendanceOperationsChecklistStatus,
	AttendanceOperationsDepartmentInsight,
	AttendanceOperationsRiskItem,
	AttendanceOperationsRiskSeverity,
	AttendanceOperationsSummaryResponse,
	AttendanceOperationsWorkPolicyMix,
	AttendanceRawEventResponse,
	AttendanceResponse,
	BillingCycle,
	CertificateIssuePolicyResponse,
	CertificateRequestResponse,
	CertificateOperationsChecklistStatus,
	CertificateOperationsRiskSeverity,
	CertificateOperationsSummaryResponse,
	CertificateSelfServiceSummaryResponse,
	CertificateType,
	AssignCompanyAffiliateEmployeesRequest,
	CompanyAffiliateResponse,
	CompanyResponse,
	CompanyUsageResponse,
	ContractExpiryActionStatus,
	ContractType,
	CreateDigitalContractBulkRequest,
	CreateDigitalContractPacketRequest,
	CreateCompanyAffiliateRequest,
	CreateDigitalContractRequest,
	CreateDigitalContractTemplateRequest,
	LegacyDigitalContractUploadRequest,
	CreateEmployeeCustomFieldChangeRequest,
	CreateEmployeeCustomFieldDefinitionRequest,
	CreateEmployeeProfileChangeRequest,
	CreateEmployeeSearchPresetRequest,
	CreateExpensePolicyRuleRequest,
	CreateLeaveRequest,
	CreateOpenApiClientRequest,
	CreatePerformanceGoalCommentRequest,
	CreatePerformanceGoalRequest,
	CreatePerformanceGoalApprovalRequest,
	CreateRecruitingCandidateRequest,
	CreateRecruitingEvaluationRequest,
	CreateRecruitingJobPostingRequest,
	CreateSelfServiceCertificateRequest,
	CreatePersonnelDocumentRequest,
	CreatePersonnelEventRequest,
	CreatePersonnelNoteRequest,
	UpdateCertificateIssuePoliciesRequest,
	UpdateContractExpiryActionRequest,
	UpdateMeetingActionItemsRequest,
	UpdatePersonnelEventRequest,
	UpdatePersonnelNoteRequest,
	GrantPaidSubscriptionRequest,
	HireRecruitingCandidateRequest,
	PaymentResponse,
	DashboardPendingWorkRequestResponse,
	DashboardResponse,
	DigitalContractRequestResponse,
	DigitalContractPacketResponse,
	DigitalContractChecklistStatus,
	DigitalContractRiskLevel,
	DigitalContractRiskSeverity,
	DigitalContractStatus,
	DigitalContractSummaryResponse,
	DigitalContractTemplateFieldType,
	DigitalContractTemplateResponse,
	EarningItemRequest,
	EmployeeJobCatalogBulkDeleteRequest,
	EmployeeJobCatalogBulkDeleteResponse,
	EmployeeJobCatalogBulkImportRequest,
	EmployeeJobCatalogBulkImportResponse,
	EmployeeJobCatalogCategory,
	EmployeeJobCatalogResponse,
	EmployeeContract,
	EmployeeCustomFieldChangeRequestResponse,
	EmployeeCustomFieldChangeRequestStatus,
	EmployeeCustomFieldType,
	EmployeeCustomFieldValueResponse,
	EmployeePersonnelFileResponse,
	EmployeeProfile,
	EmployeeProfileChangeRequestResponse,
	EmployeeProfileChangeRequestStatus,
	EmployeeResponse,
	EmployeeAttendanceStatus,
	EmployeeInviteStatus,
	EmployeeProfileCompletionStatus,
	EmployeeSearchPresetResponse,
	ExpenseAccountingExportRequest,
	ExpenseAccountingExportResponse,
	ExpenseAccountingReviewRequest,
	ExpenseAccountingStatus,
	ExpenseCategory,
	ExpenseCloseChecklistStatus,
	ExpenseCloseReadinessLevel,
	ExpenseCloseRiskSeverity,
	ExpenseDraftSuggestionResponse,
	ExpenseEvidenceType,
	ExpenseManagementSummaryResponse,
	ExpensePaymentMethod,
	ExpensePolicyAction,
	ExpensePolicyRuleResponse,
	ExpenseResponse,
	ExpenseStatus,
	Gender,
	GroupInsuranceChecklistItem,
	GroupInsuranceChecklistStatus,
	GroupInsuranceEnrollmentItem,
	GroupInsuranceEnrollmentStatus,
	GroupInsurancePaymentCycle,
	GroupInsurancePlanType,
	GroupInsurancePolicyStatus,
	GroupInsuranceReadinessRiskLevel,
	GroupInsuranceSummaryResponse,
	HolidayResponse,
	InviteResponse,
	InviteEmailDeliveryStatus,
	InvitePreviewResponse,
	LeaveBalanceGrantResponse,
	LeavePromotionProgressResponse,
	LeavePromotionTarget,
	LeaveBalanceResponse,
	LeavePolicyResponse,
	LeaveRequestResponse,
	TeamLeaveCalendarEntryResponse,
	LocationVerificationMethod,
	MeetingActionItemResponse,
	MeetingChecklistItem,
	MeetingChecklistStatus,
	MeetingKnowledgeRiskLevel,
	MeetingResponse,
	MeetingStatus,
	MeetingSummaryResponse,
	MonthlyAttendanceSummaryResponse,
	NotificationResponse,
	NotificationSummaryResponse,
	OneOnOneActionImpact,
	OneOnOneActionItemResponse,
	OneOnOneActionItemStatus,
	OneOnOneOperationsActionImpactMix,
	OneOnOneOperationsChecklistItem,
	OneOnOneOperationsChecklistStatus,
	OneOnOneOperationsPreparationCandidate,
	OneOnOneOperationsRelationshipInsight,
	OneOnOneOperationsRiskItem,
	OneOnOneOperationsRiskSeverity,
	OneOnOneOperationsSessionInsight,
	OneOnOneOperationsSummaryResponse,
	OneOnOneSessionResponse,
	OneOnOneSessionStatus,
	OneOnOneSummaryResponse,
	OpenApiClientResponse,
	OpenApiClientStatus,
	ApplyOrganizationChangePlansResponse,
	CreateOrganizationChangePlanRequest,
	OrganizationDepartmentResponse,
	OrganizationChangePlanResponse,
	PageResponse,
	PerformanceGoalApprovalRequestResponse,
	PerformanceGoalApprovalRequestType,
	PerformanceGoalApprovalStatus,
	PerformanceGoalCommentResponse,
	PerformanceGoalOperationsChecklistItem,
	PerformanceGoalOperationsChecklistStatus,
	PerformanceGoalOperationsOwnerInsight,
	PerformanceGoalOperationsRiskItem,
	PerformanceGoalOperationsRiskSeverity,
	PerformanceGoalOperationsScopeInsight,
	PerformanceGoalOperationsSummaryResponse,
	PersonnelChecklistItem,
	PersonnelChecklistStatus,
	PersonnelDepartmentInsight,
	PersonnelLifecycleAlert,
	PersonnelLifecycleAlertSeverity,
	PersonnelManagementSummaryResponse,
	PersonnelProfileKnowledgeStatus,
	PersonnelProfileKnowledgeSummary,
	PersonnelRetireeAccessItem,
	PersonnelRetireeAccessRiskLevel,
	PersonnelScheduleEvent,
	PersonnelScheduleResponse,
	PersonnelScheduleSeverity,
	PersonnelScheduleType,
	PersonnelSearchFilterOption,
	PersonnelSearchReadiness,
	PersonnelSearchReadinessStatus,
	PersonnelSearchSegment,
	PayrollSettings,
	HistoricalPayslipCreateRequest,
	PayrollAction,
	PayrollImportCommitResult,
	PayrollImportDetailResponse,
	PayrollImportRow,
	PayrollImportRowLine,
	PayrollImportRowUpdateRequest,
	PayrollImportSummary,
	PayrollSettlementChecklistItem,
	PayrollSettlementIssue,
	PayrollSettlementPayslipBasis,
	PayrollSettlementResponse,
	PayrollSettlementStatus,
	PayrollTemplateResponse,
	PayrollTemplateSummary,
	PayrollTemplateUpsertRequest,
	PersonnelDocumentCategory,
	PersonnelDocumentResponse,
	PersonnelEventResponse,
	PersonnelEventSeverity,
	PersonnelEventType,
	PersonnelNoteCategory,
	PersonnelNoteResponse,
	PerformanceGoalResponse,
	PerformanceGoalScope,
	PerformanceGoalStatus,
	PerformanceGoalSummaryResponse,
	PerformanceReviewAnalysisParticipantReport,
	PerformanceReviewAnalysisResponse,
	PerformanceReviewAnalysisScoreBucket,
	PerformanceReviewCycleResponse,
	PerformanceReviewCycleStatus,
	PerformanceReviewOperationsCalibrationCandidate,
	PerformanceReviewOperationsChecklistItem,
	PerformanceReviewOperationsChecklistStatus,
	PerformanceReviewOperationsCycleInsight,
	PerformanceReviewOperationsDepartmentInsight,
	PerformanceReviewOperationsRiskItem,
	PerformanceReviewOperationsRiskSeverity,
	PerformanceReviewOperationsScoreDistribution,
	PerformanceReviewOperationsSummaryResponse,
	PerformanceReviewSummaryResponse,
	PayslipGenerationPreviewResponse,
	PayslipGenerationReadyEmployee,
	PayslipResponse,
	PayslipRevealResponse,
	PayslipSummaryResponse,
	PlanCode,
	PlanPriceResponse,
	RecruitingCandidateResponse,
	RecruitingCandidateStage,
	RecruitingDecisionCandidateResponse,
	RecruitingJobPostingResponse,
	RecruitingJobStatus,
	RecruitingPrivacyDeletionRunResponse,
	RecruitingRecommendation,
	RecruitingSummaryResponse,
	ReviewEmployeeProfileChangeRequest,
	SecurityAccessRoleSummary,
	SecurityChecklistItem,
	SecurityComplianceStatus,
	SecurityComplianceSummaryResponse,
	SecurityControlItem,
	SecurityEvidenceItem,
	SecurityRecentEvent,
	SecurityRiskItem,
	SecurityRiskSeverity,
	SubscriptionResponse,
	UpsertEmployeeCustomFieldValuesRequest,
	UpdateWorkflowTemplateApprovalLineRequest,
	UpdateRecruitingCandidateStageRequest,
	UsageSummaryResponse,
	WeeklyComplianceSummaryResponse,
	WeeklyOvertimeRiskResponse,
	YearEndChecklistStatus,
	YearEndDeductionItem,
	YearEndSettlementResponse,
	YearEndSettlementStatus,
	YearEndSettlementSummaryResponse,
	CreateWorkflowDocumentCommentRequest,
	CreateWorkflowDocumentRequest,
	CreateWorkflowSearchPresetRequest,
	CreateWorkflowTemplateRequest,
	WorkPolicyResponse,
	WorkPolicyShift,
	WorkPolicyBreak,
	WorkflowDocumentActivityResponse,
	WorkflowDocumentActivityType,
	WorkflowDocumentCommentResponse,
	WorkflowDocumentResponse,
	WorkflowDocumentStatus,
	WorkflowOperationsApprovalBottleneckItem,
	WorkflowOperationsCategoryInsight,
	WorkflowOperationsChecklistItem,
	WorkflowOperationsChecklistStatus,
	WorkflowOperationsFieldTypeMix,
	WorkflowOperationsPendingApproverInsight,
	WorkflowOperationsRiskItem,
	WorkflowOperationsRiskSeverity,
	WorkflowOperationsSummaryResponse,
	WorkflowSearchPresetResponse,
	WorkflowSummaryResponse,
	WorkflowTemplateBulkApprovalLineResponse,
	WorkflowTemplateFieldResponse,
	WorkflowTemplateFieldType,
	WorkflowTemplateResponse
} from '$lib/api/types';
export { FieldOperationsPage } from '$lib/app/field-operations-page';
export { PartnerAdvisoryPage } from '$lib/app/partner-advisory-page';
export { CustomerSuccessPage } from '$lib/app/customer-success-page';
export { EngagementPage } from '$lib/app/engagement-page';
export { EmployeeLifecyclePage } from '$lib/app/employee-lifecycle-page';
export { EmployeeNumberPolicyPage } from '$lib/app/employee-number-policy-page';
export { LearningOperationsPage } from '$lib/app/learning-operations-page';
export { NewsFeedPage } from '$lib/app/news-feed-page';
export { OrganizationInsightsPage } from '$lib/app/organization-insights-page';
import { AttendanceActionPanel } from '$lib/attendance/attendance-action-panel';
import { useTodayAttendance } from '$lib/attendance/use-today-attendance';
import { useAuth } from '$lib/app/auth-context';
import { type MatchedRoute, resolveEndpoint } from '$lib/app/routes';
import {
	validateApprovalLineForm,
	type ApprovalLineFormValues
} from '$lib/approval/approval-line-form';
import { validateChangePasswordForm } from '$lib/auth/auth-form';
import {
	parseBillingSelection,
	validateCheckoutPaymentForm,
	type BillablePlanCode
} from '$lib/billing/payer-info';
import { validateCertificateForm } from '$lib/certificate/certificate-form';
import { validateCompanyForm, type CompanyFormValues } from '$lib/company/company-form';
import {
	toLocalDateString,
	validateExpenseForm,
	type ExpenseFormValues
} from '$lib/expense/expense-form';
import { validateOfficeLocationForm, type OfficeLocationForm } from '$lib/company/office-location';
import { validateHolidayForm } from '$lib/holiday/holiday-form';
import {
	validateLeaveAdminActionForm,
	type LeaveAdminActionKind
} from '$lib/leave/admin-action-form';
import { validateLeavePolicyForm, type LeavePolicyFormValues } from '$lib/leave/policy-form';
import {
	isHalfDayLeaveType,
	isHourlyLeaveType,
	validateLeaveRequestForm,
	type LeaveRequestFormInput
} from '$lib/leave/request-form';
import {
	Badge,
	BottomSheet,
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	CommandCard,
	DangerCard,
	EmptyState,
	FilterChips,
	Input,
	Label,
	ListRow,
	LocationCard,
	MetricCard,
	MobileHeader,
	MoneyBreakdown,
	PageHeader,
	PriorityApprovalCard,
	SectionHeader,
	SettingToggleRow,
	Spinner,
	StatusChip,
	useConfirm
} from '$lib/ui';
import {
	formFromPolicy,
	validateAssignPolicyForm,
	validateWorkPolicyForm,
	type WorkPolicyFormValues
} from '$lib/work-policy/policy-form';
import { cn } from '$lib/utils';

type RequestState<T> =
	| { status: 'idle' }
	| { status: 'loading' }
	| { status: 'success'; data: T }
	| { status: 'error'; message: string };

const DUPLICATE_EMAIL_ERROR_TEXT = '이미 사용 중인 이메일';
const EXISTING_INVITED_ACCOUNT_MESSAGE =
	'이미 가입한 이메일입니다. 기존 계정으로 로그인해 초대를 수락해주세요.';

function inviteLoginHref(inviteToken: string) {
	return inviteToken ? `/login?inviteToken=${encodeURIComponent(inviteToken)}` : '/login';
}

function AuthCard({
	title,
	description,
	children
}: {
	title: string;
	description: string;
	children: ReactNode;
}) {
	return (
		<div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle className="text-lg">{title}</CardTitle>
					<CardDescription>{description}</CardDescription>
				</CardHeader>
				<CardContent>{children}</CardContent>
			</Card>
		</div>
	);
}

function FormError({ message }: { message: string | null }) {
	if (!message) return null;
	return (
		<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">{message}</div>
	);
}

type LandingIcon = ComponentType<{ className?: string }>;

const landingMetrics = [
	{ value: '한 번 입력', label: '직원·회사 기준을 반복 입력 없이 연결' },
	{ value: '권한별 화면', label: '사장님·관리자·직원이 필요한 일만 확인' },
	{ value: '월말까지', label: '근태 기록을 휴가·급여·리포트로 이어서 정리' }
] as const;

const landingModules: Array<{
	title: string;
	description: string;
	icon: LandingIcon;
	tone: string;
}> = [
	{
		title: '출퇴근',
		description: '오늘 출근 상태, 지각, 결근, 위치 기준을 한 화면에서 확인합니다.',
		icon: Clock3,
		tone: 'bg-primary/10 text-primary'
	},
	{
		title: '휴가',
		description: '신청, 잔여일, 관리자 승인, 정책 설정까지 같은 흐름으로 이어집니다.',
		icon: CalendarCheck,
		tone: 'bg-success/10 text-success'
	},
	{
		title: '결재',
		description: '휴가, 증명서, 경비, 급여 요청을 결재선 기준으로 추적합니다.',
		icon: GitBranch,
		tone: 'bg-warning/20 text-warning-foreground'
	},
	{
		title: '직원',
		description: '초대, 인사정보, 계약, 재직 상태를 회사 단위로 분리해 관리합니다.',
		icon: UsersRound,
		tone: 'bg-muted text-foreground'
	},
	{
		title: '급여',
		description: '급여 설정, 명세서 생성, 직원별 지급 내역을 한 흐름으로 연결합니다.',
		icon: WalletCards,
		tone: 'bg-destructive/10 text-destructive'
	},
	{
		title: '리포트',
		description: '근태와 휴가 데이터를 운영 리포트로 묶어 반복 점검을 줄입니다.',
		icon: FileText,
		tone: 'bg-card text-foreground'
	}
];

const landingAudiences: Array<{
	title: string;
	description: string;
	items: readonly string[];
	icon: LandingIcon;
}> = [
	{
		title: '사장님',
		description: '오늘 처리할 승인, 구독 상태, 팀 운영 지표를 먼저 봅니다.',
		items: ['대시보드 현황', '결제와 구독', '감사 로그'],
		icon: Building2
	},
	{
		title: '인사 담당자',
		description: '직원 정보, 휴가 정책, 증명서와 급여 준비를 같은 기준으로 관리합니다.',
		items: ['직원 초대', '휴가 잔여 조정', '급여명세 생성'],
		icon: UsersRound
	},
	{
		title: '직원',
		description: '내 출퇴근, 휴가 신청, 급여명세와 알림을 개인 화면에서 확인합니다.',
		items: ['출퇴근 기록', '휴가 신청', '내 급여명세'],
		icon: BadgeCheck
	}
] as const;

const onboardingSteps = [
	{
		title: '회사 기준 등록',
		description: '회사 정보, 근무 위치, 근태 정책, 휴가 기준을 먼저 정리합니다.'
	},
	{
		title: '직원 초대',
		description: '초대 링크로 계정을 연결하고 직원별 역할과 재직 정보를 맞춥니다.'
	},
	{
		title: '운영 시작',
		description: '출퇴근 기록과 휴가 결재를 모아 급여명세와 리포트까지 이어갑니다.'
	}
] as const;

const trustItems = [
	'회사별 데이터 분리',
	'역할 기반 화면 권한',
	'결재·변경 이력 추적',
	'증명서 검증 링크'
] as const;

function LandingMetricGrid({ className }: { className: string }) {
	return (
		<div className={className}>
			{landingMetrics.map((metric) => (
				<div
					key={metric.label}
					className="rounded-lg border border-border/80 bg-card/85 p-4 shadow-sm"
				>
					<p className="text-lg font-semibold">{metric.value}</p>
					<p className="mt-1 text-xs leading-5 text-muted-foreground">{metric.label}</p>
				</div>
			))}
		</div>
	);
}

export function LandingPage() {
	return (
		<main className="min-h-screen bg-[linear-gradient(180deg,var(--background)_0%,var(--muted)_52%,var(--background)_100%)]">
			<section className="mx-auto flex min-h-[88svh] max-w-7xl flex-col px-5 py-5 sm:px-6 lg:px-8">
				<header className="flex items-center justify-between gap-4">
					<Link href="/" className="flex items-center gap-3 text-sm font-semibold">
						<span className="flex size-9 items-center justify-center rounded-md bg-foreground text-background">
							<Building2 className="size-4" />
						</span>
						AntFlow
					</Link>
					<nav className="hidden items-center gap-2 text-sm font-semibold text-muted-foreground md:flex">
						<a
							className="rounded-md px-3 py-2 transition-colors hover:bg-background hover:text-foreground"
							href="#service"
						>
							서비스
						</a>
						<a
							className="rounded-md px-3 py-2 transition-colors hover:bg-background hover:text-foreground"
							href="#onboarding"
						>
							도입 흐름
						</a>
						<a
							className="rounded-md px-3 py-2 transition-colors hover:bg-background hover:text-foreground"
							href="#security"
						>
							보안
						</a>
					</nav>
					<div className="flex items-center gap-2">
						<Link
							className="inline-flex h-10 items-center justify-center rounded-md px-3 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
							href="/login"
						>
							로그인
						</Link>
						<Link
							className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
							href="/signup"
						>
							회사 시작하기
						</Link>
					</div>
				</header>

				<div className="grid flex-1 items-center gap-10 py-12 lg:grid-cols-[0.92fr_1.08fr] lg:py-14">
					<div className="space-y-8">
						<Badge variant="default">한국형 HR·근태·급여 SaaS</Badge>
						<div className="space-y-5">
							<h1 className="max-w-3xl text-4xl font-semibold tracking-normal text-foreground sm:text-5xl lg:text-6xl">
								AntFlow
							</h1>
							<p className="max-w-2xl text-xl font-semibold leading-8 text-foreground sm:text-2xl">
								출퇴근부터 급여명세까지, 회사 운영의 반복 업무를 한 흐름으로 정리합니다.
							</p>
							<p className="max-w-2xl text-base leading-8 text-muted-foreground">
								직원 초대, 근태 기록, 휴가 결재, 급여명세, 증명서와 운영 리포트를 회사 기준에 맞춰
								이어주는 실무형 인사관리 도구입니다.
							</p>
						</div>
						<div className="flex flex-wrap gap-3">
							<Link
								className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
								href="/signup"
							>
								회사 시작하기
								<ArrowRight className="size-4" />
							</Link>
							<a
								className="inline-flex h-11 items-center justify-center rounded-md border border-border bg-background/80 px-5 text-sm font-semibold transition-colors hover:bg-muted"
								href="#service"
							>
								서비스 보기
							</a>
						</div>
						<LandingMetricGrid className="hidden max-w-2xl gap-3 sm:grid-cols-3 lg:grid" />
					</div>

					<div className="relative">
						<div className="absolute -left-4 top-7 hidden rounded-lg border border-border bg-card p-4 shadow-lg lg:block">
							<p className="text-xs font-semibold text-muted-foreground">오늘 처리할 일</p>
							<p className="mt-1 text-2xl font-semibold">12건</p>
						</div>
						<div className="relative min-h-[340px] overflow-hidden rounded-lg border border-border bg-card shadow-xl sm:min-h-[430px]">
							<Image
								src="/landing/antflow-preview.png"
								alt="AntFlow dashboard preview"
								fill
								sizes="(min-width: 1024px) 58vw, 100vw"
								className="object-cover object-left-top"
								priority
							/>
						</div>
						<div className="absolute -bottom-5 right-4 rounded-lg border border-border bg-card p-4 shadow-lg">
							<div className="flex items-center gap-2 text-sm font-semibold text-success">
								<CheckCircle2 className="size-4" />
								오늘 업무가 이어집니다
							</div>
							<p className="mt-1 text-xs text-muted-foreground">
								근태·휴가·결재를 한 화면 흐름으로
							</p>
						</div>
					</div>
					<LandingMetricGrid className="grid gap-3 sm:grid-cols-3 lg:hidden" />
				</div>
			</section>

			<section id="service" className="border-y border-border/70 bg-card/70 py-16">
				<div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
					<div className="grid gap-8 lg:grid-cols-[0.75fr_1.25fr]">
						<div className="space-y-4">
							<Badge variant="outline">서비스 구성</Badge>
							<h2 className="text-3xl font-semibold tracking-normal">
								팀 운영에 필요한 화면을 모듈별로 묶었습니다.
							</h2>
							<p className="leading-7 text-muted-foreground">
								AntFlow는 인사 담당자, 관리자, 직원이 같은 데이터를 각자 필요한 권한과 언어로 보는
								구조입니다. 작은 회사도 흩어진 스프레드시트 없이 매일의 운영 흐름을 정리할 수
								있습니다.
							</p>
						</div>
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
							{landingModules.map((module) => {
								const Icon = module.icon;
								return (
									<div
										key={module.title}
										className="rounded-lg border border-border bg-background p-5 shadow-sm"
									>
										<div
											className={`mb-4 flex size-10 items-center justify-center rounded-md ${module.tone}`}
										>
											<Icon className="size-5" />
										</div>
										<h3 className="text-base font-semibold">{module.title}</h3>
										<p className="mt-2 text-sm leading-6 text-muted-foreground">
											{module.description}
										</p>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</section>

			<section className="mx-auto max-w-7xl px-5 py-16 sm:px-6 lg:px-8">
				<div className="mb-8 max-w-3xl space-y-4">
					<Badge variant="outline">권한별 화면</Badge>
					<h2 className="text-3xl font-semibold tracking-normal">
						권한별 업무 공간을 따로 제공합니다.
					</h2>
					<p className="leading-7 text-muted-foreground">
						같은 회사 데이터라도 보는 사람의 역할에 따라 할 일이 다릅니다. AntFlow는 사장님, 인사
						담당자, 직원의 기본 동선을 분리해 불필요한 화면 이동을 줄입니다.
					</p>
				</div>
				<div className="grid gap-3 lg:grid-cols-3">
					{landingAudiences.map((audience) => {
						const Icon = audience.icon;
						return (
							<div
								key={audience.title}
								className="rounded-lg border border-border bg-card p-5 shadow-sm"
							>
								<div className="mb-5 flex size-11 items-center justify-center rounded-md bg-primary/10 text-primary">
									<Icon className="size-5" />
								</div>
								<h3 className="text-lg font-semibold">{audience.title}</h3>
								<p className="mt-2 text-sm leading-6 text-muted-foreground">
									{audience.description}
								</p>
								<ul className="mt-5 space-y-2">
									{audience.items.map((item) => (
										<li key={item} className="flex items-center gap-2 text-sm">
											<CheckCircle2 className="size-4 text-success" />
											{item}
										</li>
									))}
								</ul>
							</div>
						);
					})}
				</div>
			</section>

			<section
				id="onboarding"
				className="mx-auto grid max-w-7xl gap-8 px-5 py-16 sm:px-6 lg:grid-cols-[1fr_1fr] lg:px-8"
			>
				<div className="space-y-5">
					<Badge variant="outline">도입 흐름</Badge>
					<h2 className="text-3xl font-semibold tracking-normal">도입은 세 단계로 시작합니다.</h2>
					<p className="leading-7 text-muted-foreground">
						처음부터 모든 기능을 켤 필요는 없습니다. 회사 기준을 먼저 만들고, 직원을 초대하고, 매일
						쌓이는 기록을 결재와 정산으로 이어가면 됩니다.
					</p>
				</div>
				<div className="space-y-3">
					{onboardingSteps.map((step, index) => (
						<div
							key={step.title}
							className="flex gap-4 rounded-lg border border-border bg-card p-4 shadow-sm"
						>
							<span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-foreground text-sm font-semibold text-background">
								{index + 1}
							</span>
							<div>
								<p className="font-semibold">{step.title}</p>
								<p className="mt-1 text-sm text-muted-foreground">{step.description}</p>
							</div>
						</div>
					))}
				</div>
			</section>

			<section id="security" className="bg-foreground py-16 text-background">
				<div className="mx-auto grid max-w-7xl gap-8 px-5 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
					<div className="space-y-4">
						<Badge className="border-background/20 bg-background/10 text-background">
							운영 신뢰
						</Badge>
						<h2 className="text-3xl font-semibold tracking-normal">
							보안과 권한 흐름을 운영 화면에 녹였습니다.
						</h2>
						<p className="leading-7 text-background/75">
							회사별 데이터 경계, 역할 기반 접근, 결재 이력과 증명서 검증 흐름을 한 서비스 안에서
							다룹니다. 담당자는 필요한 권한만 열고, 직원은 본인 업무만 확인합니다.
						</p>
					</div>
					<div className="grid gap-3 sm:grid-cols-2">
						{trustItems.map((item) => (
							<div
								key={item}
								className="rounded-lg border border-background/15 bg-background/10 p-4"
							>
								<div className="flex items-center gap-2 text-sm font-semibold">
									<ShieldCheck className="size-4" />
									{item}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-14 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
				<div>
					<p className="text-sm font-semibold text-primary">AntFlow 시작</p>
					<h2 className="mt-2 text-2xl font-semibold tracking-normal">
						회사 운영 데이터를 한 흐름으로 모을 준비가 됐습니다.
					</h2>
				</div>
				<div className="flex flex-wrap gap-3">
					<Link
						className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
						href="/signup"
					>
						회사 시작하기
						<ArrowRight className="size-4" />
					</Link>
					<Link
						className="inline-flex h-11 items-center justify-center rounded-md border border-border bg-background px-5 text-sm font-semibold transition-colors hover:bg-muted"
						href="/login"
					>
						계정으로 이동
					</Link>
				</div>
			</section>
		</main>
	);
}

export function LoginPage() {
	const auth = useAuth();
	const router = useRouter();
	const searchParams = useSearchParams();
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSubmitting(true);
		setError(null);
		try {
			await auth.login(email, password);
			const inviteToken = searchParams.get('inviteToken');
			if (inviteToken) {
				await api.post('/employees/accept-invite', { inviteCode: inviteToken });
				router.push('/company/setup?verified=invite');
			}
		} catch (nextError) {
			setError(getErrorMessage(nextError, '로그인하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<AuthCard title="로그인" description="AntFlow 계정으로 계속합니다.">
			<form className="space-y-4" onSubmit={submit}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="login-email">이메일</Label>
					<Input
						id="login-email"
						type="email"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						required
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="login-password">비밀번호</Label>
					<Input
						id="login-password"
						type="password"
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						required
					/>
				</div>
				<Button className="w-full" type="submit" disabled={submitting}>
					{submitting ? '로그인 중...' : '로그인'}
				</Button>
				<div className="flex justify-between text-sm text-muted-foreground">
					<Link href="/forgot-password">비밀번호 찾기</Link>
					<Link href="/signup">회원가입</Link>
				</div>
			</form>
		</AuthCard>
	);
}

export function SignupPage() {
	const auth = useAuth();
	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSubmitting(true);
		setError(null);
		try {
			await auth.signup(email, password, name);
		} catch (nextError) {
			setError(getErrorMessage(nextError, '가입하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<AuthCard title="회원가입" description="회사 관리자 계정을 생성합니다.">
			<form className="space-y-4" onSubmit={submit}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="signup-name">이름</Label>
					<Input
						id="signup-name"
						value={name}
						onChange={(event) => setName(event.target.value)}
						required
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="signup-email">이메일</Label>
					<Input
						id="signup-email"
						type="email"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						required
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="signup-password">비밀번호</Label>
					<Input
						id="signup-password"
						type="password"
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						required
					/>
				</div>
				<Button className="w-full" disabled={submitting}>
					{submitting ? '가입 중...' : '가입'}
				</Button>
			</form>
		</AuthCard>
	);
}

export function InviteSignupPage() {
	const auth = useAuth();
	const searchParams = useSearchParams();
	const inviteToken = searchParams.get('token') ?? '';
	const [preview, setPreview] = useState<RequestState<InvitePreviewResponse>>({ status: 'idle' });
	const [name, setName] = useState('');
	const [password, setPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const loginHref = inviteLoginHref(inviteToken);
	const isExistingAccountError = error === EXISTING_INVITED_ACCOUNT_MESSAGE;

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (!inviteToken) {
				if (active) setPreview({ status: 'error', message: '초대 토큰이 없습니다.' });
				return;
			}
			if (active) setPreview({ status: 'loading' });
			try {
				const data = await api.get<InvitePreviewResponse>(
					`/auth/signup/invite?token=${encodeURIComponent(inviteToken)}`
				);
				if (!active) return;
				setName(data.employeeName);
				setPreview({ status: 'success', data });
			} catch (nextError) {
				if (active) setPreview({ status: 'error', message: getErrorMessage(nextError) });
			}
		});
		return () => {
			active = false;
		};
	}, [inviteToken]);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (preview.status !== 'success') return;
		setSubmitting(true);
		setError(null);
		try {
			await auth.signupWithInvite({
				inviteToken,
				name,
				email: preview.data.employeeEmail,
				password
			});
		} catch (nextError) {
			const message = getErrorMessage(nextError, '초대 가입에 실패했습니다.');
			setError(
				message.includes(DUPLICATE_EMAIL_ERROR_TEXT) ? EXISTING_INVITED_ACCOUNT_MESSAGE : message
			);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<AuthCard title="초대 가입" description="초대받은 회사에 합류합니다.">
			{preview.status === 'loading' ? <Spinner /> : null}
			{preview.status === 'error' ? <FormError message={preview.message} /> : null}
			{preview.status === 'success' ? (
				<form className="space-y-4" onSubmit={submit}>
					<FormError message={error} />
					<div className="rounded-lg bg-muted/50 p-4 text-sm">
						<p className="font-semibold">{preview.data.companyName}</p>
						<p className="text-muted-foreground">{preview.data.employeeEmail}</p>
					</div>
					<div className="rounded-lg border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
						이미 계정이 있나요?{' '}
						<Link className="font-semibold text-primary hover:underline" href={loginHref}>
							기존 계정으로 로그인해 수락
						</Link>
					</div>
					<div className="space-y-2">
						<Label htmlFor="invite-name">이름</Label>
						<Input
							id="invite-name"
							value={name}
							onChange={(event) => setName(event.target.value)}
							required
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="invite-password">비밀번호</Label>
						<Input
							id="invite-password"
							type="password"
							value={password}
							onChange={(event) => setPassword(event.target.value)}
							required
						/>
					</div>
					<Button className="w-full" disabled={submitting}>
						{submitting ? '가입 중...' : '초대 수락'}
					</Button>
					{isExistingAccountError ? (
						<Link
							className="flex h-11 items-center justify-center rounded-md border border-border bg-background text-sm font-semibold transition-colors hover:bg-muted"
							href={loginHref}
						>
							기존 계정으로 로그인
						</Link>
					) : null}
				</form>
			) : null}
		</AuthCard>
	);
}

export function ForgotPasswordPage() {
	const [email, setEmail] = useState('');
	const [status, setStatus] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setStatus(null);
		setError(null);
		try {
			await api.post('/auth/password-reset/request', { email });
			setStatus('비밀번호 재설정 메일을 보냈습니다.');
		} catch (nextError) {
			setError(getErrorMessage(nextError));
		}
	}

	return (
		<AuthCard title="비밀번호 찾기" description="가입 이메일로 재설정 링크를 보냅니다.">
			<form className="space-y-4" onSubmit={submit}>
				<FormError message={error} />
				{status ? (
					<div className="rounded-lg bg-success/10 px-4 py-3 text-sm text-success">{status}</div>
				) : null}
				<div className="space-y-2">
					<Label htmlFor="forgot-email">이메일</Label>
					<Input
						id="forgot-email"
						type="email"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						required
					/>
				</div>
				<Button className="w-full">재설정 메일 보내기</Button>
			</form>
		</AuthCard>
	);
}

export function ResetPasswordPage() {
	const searchParams = useSearchParams();
	const token = searchParams.get('token') ?? '';
	const [password, setPassword] = useState('');
	const [status, setStatus] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setStatus(null);
		setError(null);
		try {
			await api.post('/auth/password-reset/confirm', { token, newPassword: password });
			setStatus('비밀번호를 변경했습니다. 새 비밀번호로 로그인하세요.');
		} catch (nextError) {
			setError(getErrorMessage(nextError));
		}
	}

	return (
		<AuthCard title="비밀번호 재설정" description="새 비밀번호를 입력합니다.">
			<form className="space-y-4" onSubmit={submit}>
				<FormError message={error} />
				{status ? (
					<div className="rounded-lg bg-success/10 px-4 py-3 text-sm text-success">{status}</div>
				) : null}
				<div className="space-y-2">
					<Label htmlFor="reset-password">새 비밀번호</Label>
					<Input
						id="reset-password"
						type="password"
						value={password}
						onChange={(event) => setPassword(event.target.value)}
						required
					/>
				</div>
				<Button className="w-full">비밀번호 변경</Button>
			</form>
		</AuthCard>
	);
}

export function CertificateVerifyPage() {
	return (
		<AuthCard title="증명서 검증" description="검증번호 또는 링크로 증명서 진위를 확인합니다.">
			<div className="space-y-4">
				<div className="flex items-center gap-3 rounded-lg bg-success/10 p-4 text-sm text-success">
					<ShieldCheck className="size-5" />
					백엔드 검증 API와 연동되는 React 화면입니다.
				</div>
				<Button className="w-full" variant="outline">
					검증하기
				</Button>
			</div>
		</AuthCard>
	);
}

function extractRows(data: unknown): unknown[] {
	if (Array.isArray(data)) return data;
	if (
		typeof data === 'object' &&
		data !== null &&
		Array.isArray((data as { content?: unknown }).content)
	) {
		return (data as { content: unknown[] }).content;
	}
	return [];
}

function flattenSummary(data: unknown): Array<[string, string]> {
	if (typeof data !== 'object' || data === null || Array.isArray(data)) return [];
	return Object.entries(data as Record<string, unknown>)
		.filter(([, value]) => typeof value !== 'object' || value === null)
		.slice(0, 8)
		.map(([key, value]) => [key, value == null ? '-' : String(value)]);
}

function valueLabel(value: unknown): string {
	if (value == null) return '-';
	if (typeof value === 'object') return JSON.stringify(value);
	return String(value);
}

function numberLabel(value: number | null | undefined): string {
	return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('ko-KR') : '0';
}

function minutesLabel(minutes: number | null | undefined): string {
	if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return '0분';
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	if (hours === 0) return `${rest}분`;
	if (rest === 0) return `${hours}시간`;
	return `${hours}시간 ${rest}분`;
}

function attendanceBreakMinutes(
	attendance: AttendanceResponse | null | undefined
): number | null | undefined {
	const hasBreakRecords = (attendance?.breaks?.length ?? 0) > 0;
	if (hasBreakRecords || attendance?.onBreak) {
		return attendance?.recordedBreakMinutes ?? 0;
	}
	return attendance?.breakMinutes ?? attendance?.recordedBreakMinutes;
}

function dateLabel(value: string | null | undefined): string {
	if (!value) return '-';
	return value.slice(0, 10);
}

function timeLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const timePart = value.includes('T') ? value.split('T')[1] : value;
	return timePart?.slice(0, 5) || '-';
}

function statusLabel(status: string | null | undefined): string {
	const labels: Record<string, string> = {
		NORMAL: '정상',
		LATE: '지각',
		ABSENT: '결근',
		HALF_DAY: '반차',
		PENDING: '대기',
		APPROVED: '승인',
		REJECTED: '반려',
		CANCELLED: '취소'
	};
	return status ? (labels[status] ?? status) : '-';
}

function StatusBadge({ status }: { status: string | null | undefined }) {
	const variant =
		status === 'APPROVED' || status === 'NORMAL'
			? 'success'
			: status === 'PENDING'
				? 'warning'
				: 'outline';
	return <Badge variant={variant}>{statusLabel(status)}</Badge>;
}

function DashboardMetricCard({
	title,
	value,
	description,
	tone
}: {
	title: string;
	value: number | null | undefined;
	description: string;
	tone: string;
}) {
	return (
		<Card className="min-h-32">
			<CardContent className="flex h-full flex-col justify-between p-5">
				<div className="flex items-center justify-between gap-3">
					<p className="text-sm font-medium text-muted-foreground">{title}</p>
					<span className={`size-2 rounded-full ${tone}`} />
				</div>
				<div>
					<p className="text-3xl font-semibold tracking-normal">{numberLabel(value)}</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function DashboardAttendanceList({ items }: { items: AttendanceResponse[] }) {
	if (items.length === 0) return <EmptyState message="최근 출퇴근 기록이 없습니다." />;
	return (
		<div className="divide-y divide-border rounded-lg border border-border">
			{items.slice(0, 5).map((attendance) => (
				<div
					key={attendance.id ?? `${attendance.employeeId}-${attendance.date}`}
					className="grid gap-2 p-4 sm:grid-cols-[1fr_auto]"
				>
					<div>
						<p className="font-semibold">
							{attendance.employeeName ?? `직원 #${attendance.employeeId}`}
						</p>
						<p className="mt-1 text-sm text-muted-foreground">
							{dateLabel(attendance.date)} · 출근 {timeLabel(attendance.checkInTime)} · 퇴근{' '}
							{timeLabel(attendance.checkOutTime)}
						</p>
					</div>
					<div className="flex items-center gap-2 sm:justify-end">
						<StatusBadge status={attendance.status} />
						<span className="text-sm text-muted-foreground">
							{attendance.workHoursFormatted ?? minutesLabel(attendance.workMinutes)}
						</span>
					</div>
				</div>
			))}
		</div>
	);
}

function DashboardLeaveList({ items }: { items: LeaveRequestResponse[] }) {
	if (items.length === 0) return <EmptyState message="대기 중인 휴가 신청이 없습니다." />;
	return (
		<div className="space-y-3">
			{items.slice(0, 4).map((leave) => (
				<div key={leave.id} className="rounded-lg border border-border bg-muted/20 p-4">
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div>
							<p className="font-semibold">{leave.employeeName ?? `직원 #${leave.employeeId}`}</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{dateLabel(leave.startDate)} - {dateLabel(leave.endDate)} · {leave.days}일
							</p>
						</div>
						<StatusBadge status={leave.status} />
					</div>
				</div>
			))}
		</div>
	);
}

function DashboardWorkRequestList({ items }: { items: DashboardPendingWorkRequestResponse[] }) {
	if (items.length === 0)
		return <EmptyState message="처리할 근무 정정/초과근무 요청이 없습니다." />;
	return (
		<div className="space-y-3">
			{items.slice(0, 4).map((request) => (
				<div
					key={`${request.requestType}-${request.requestId}`}
					className="rounded-lg border border-border p-4"
				>
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div>
							<p className="font-semibold">
								{request.requestType === 'ATTENDANCE_ADJUSTMENT' ? '출퇴근 정정' : '초과근무'} ·{' '}
								{request.employeeName ?? `직원 #${request.employeeId}`}
							</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{dateLabel(request.targetDate)} · {request.reason ?? '사유 없음'}
							</p>
						</div>
						<StatusBadge status={request.status} />
					</div>
				</div>
			))}
		</div>
	);
}

function WeeklyRiskList({ items }: { items: WeeklyOvertimeRiskResponse[] }) {
	if (items.length === 0) return <EmptyState message="이번 주 초과근무 위험 직원이 없습니다." />;
	return (
		<div className="space-y-3">
			{items.slice(0, 4).map((risk) => (
				<div
					key={`${risk.employeeId}-${risk.weekStart}`}
					className="rounded-lg border border-border p-4"
				>
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div>
							<p className="font-semibold">{risk.employeeName ?? `직원 #${risk.employeeId}`}</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{dateLabel(risk.weekStart)} - {dateLabel(risk.weekEnd)}
							</p>
						</div>
						<Badge variant={risk.overLimit ? 'destructive' : 'warning'}>
							{minutesLabel(risk.overtimeMinutes)} / {minutesLabel(risk.overtimeLimitMinutes)}
						</Badge>
					</div>
				</div>
			))}
		</div>
	);
}

export function DashboardPage() {
	const [state, setState] = useState<RequestState<DashboardResponse>>({ status: 'idle' });
	const todayAttendance = useTodayAttendance();

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const data = await api.get<DashboardResponse>('/dashboard');
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	if (state.status === 'loading' || state.status === 'idle') {
		return (
			<Card>
				<CardContent className="flex min-h-80 items-center justify-center">
					<Spinner size="lg" />
				</CardContent>
			</Card>
		);
	}

	if (state.status === 'error') {
		return (
			<div className="space-y-6">
				<PageHeader title="대시보드" description="팀 현황과 처리할 일을 확인합니다." />
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			</div>
		);
	}

	const dashboard = state.data;
	const pendingWorkRequests = dashboard.pendingWorkRequests ?? 0;
	const attendance = todayAttendance.state.status === 'success' ? todayAttendance.state.data : null;
	const attendanceLoading =
		todayAttendance.state.status === 'idle' || todayAttendance.state.status === 'loading';
	const attendanceError =
		todayAttendance.actionError ??
		(todayAttendance.state.status === 'error' ? todayAttendance.state.message : null);

	return (
		<div className="space-y-6">
			<PageHeader
				title="대시보드"
				description="팀 근태, 휴가, 근무 요청을 한 화면에서 점검합니다."
				actions={
					<Button
						variant="outline"
						onClick={() => {
							void load();
							void todayAttendance.load();
						}}
					>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>
			<AttendanceActionPanel
				attendance={attendance}
				loading={attendanceLoading}
				busy={todayAttendance.actionBusy}
				error={attendanceError}
				onAction={(kind, options) => void todayAttendance.performAction(kind, options)}
				onUpdate={(request) => todayAttendance.updateToday(request)}
				onCreateBreak={(request) => todayAttendance.createBreakRecord(request)}
				onUpdateBreak={(breakId, request) => todayAttendance.updateBreakRecord(breakId, request)}
				onDeleteBreak={(breakId) => todayAttendance.deleteBreakRecord(breakId)}
			/>
			<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<DashboardMetricCard
					title="전체 직원"
					value={dashboard.totalEmployees}
					description="회사에 등록된 직원"
					tone="bg-primary"
				/>
				<DashboardMetricCard
					title="출근"
					value={dashboard.presentCount}
					description="오늘 출근 처리"
					tone="bg-success"
				/>
				<DashboardMetricCard
					title="지각/결근"
					value={(dashboard.lateCount ?? 0) + (dashboard.absentCount ?? 0)}
					description="확인이 필요한 근태"
					tone="bg-warning"
				/>
				<DashboardMetricCard
					title="대기 요청"
					value={pendingWorkRequests + (dashboard.pendingLeaveRequests ?? 0)}
					description="휴가·근무 승인 대기"
					tone="bg-destructive"
				/>
			</div>
			<div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
				<Card>
					<CardHeader>
						<CardTitle>최근 출퇴근</CardTitle>
						<CardDescription>오늘 업데이트된 직원 근무 기록입니다.</CardDescription>
					</CardHeader>
					<CardContent>
						<DashboardAttendanceList items={dashboard.recentAttendances ?? []} />
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle>대기 중인 휴가</CardTitle>
						<CardDescription>관리자 검토가 필요한 휴가 신청입니다.</CardDescription>
					</CardHeader>
					<CardContent>
						<DashboardLeaveList items={dashboard.pendingLeaveList ?? []} />
					</CardContent>
				</Card>
			</div>
			<div className="grid gap-4 xl:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle>근무 요청</CardTitle>
						<CardDescription>출퇴근 정정과 초과근무 승인 대기 목록입니다.</CardDescription>
					</CardHeader>
					<CardContent>
						<DashboardWorkRequestList items={dashboard.pendingWorkRequestList ?? []} />
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle>주간 초과근무 위험</CardTitle>
						<CardDescription>한도 초과 또는 임박한 직원을 확인합니다.</CardDescription>
					</CardHeader>
					<CardContent>
						<WeeklyRiskList items={dashboard.weeklyOvertimeRisks ?? []} />
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

type HrInsightRisk = AppHrInsightsResponse['topRisks'][number];
type HrInsightDataConfidence = NonNullable<AppHrInsightsResponse['dataConfidence']>;
type HrInsightPeriodBenchmark = NonNullable<AppHrInsightsResponse['periodBenchmark']>;
type HrInsightComparisonSegment = NonNullable<
	AppHrInsightsResponse['organizationSegments']
>[number];
type HrInsightGrowthSignal = NonNullable<AppHrInsightsResponse['growthSignals']>[number];
type HrInsightDecisionSignal = NonNullable<AppHrInsightsResponse['decisionSignals']>[number];
type HrInsightFilterOptions = NonNullable<AppHrInsightsResponse['filterOptions']>;
type HrInsightIcon = ComponentType<{ className?: string }>;

function percentLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '0%';
	return `${value.toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
}

function clampPercent(value: number | null | undefined): number {
	if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
	return Math.min(100, Math.max(0, value));
}

function insightSeverityLabel(severity: HrInsightRisk['severity']): string {
	const labels: Record<HrInsightRisk['severity'], string> = {
		CAUTION: '주의',
		NEAR_LIMIT: '임박',
		NEEDS_REVIEW: '검토 필요',
		OVER_LIMIT: '초과'
	};
	return labels[severity] ?? severity;
}

function insightSeverityVariant(
	severity: HrInsightRisk['severity']
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (severity === 'OVER_LIMIT' || severity === 'NEEDS_REVIEW') return 'destructive';
	if (severity === 'CAUTION' || severity === 'NEAR_LIMIT') return 'warning';
	return 'outline';
}

function insightReliabilityLabel(label: HrInsightDataConfidence['reliabilityLabel']): string {
	const labels: Record<HrInsightDataConfidence['reliabilityLabel'], string> = {
		HIGH: '높은 신뢰',
		PARTIAL: '부분 신뢰',
		INSUFFICIENT: '데이터 부족'
	};
	return labels[label] ?? label;
}

function insightReliabilityVariant(
	label: HrInsightDataConfidence['reliabilityLabel']
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (label === 'HIGH') return 'success';
	if (label === 'PARTIAL') return 'warning';
	return 'outline';
}

function insightGrowthStatusLabel(status: HrInsightGrowthSignal['status']): string {
	const labels: Record<HrInsightGrowthSignal['status'], string> = {
		READY: '정상',
		WATCH: '관찰',
		WAITING: '대기'
	};
	return labels[status] ?? status;
}

function insightGrowthStatusVariant(
	status: HrInsightGrowthSignal['status']
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'READY') return 'success';
	if (status === 'WATCH') return 'warning';
	return 'outline';
}

function insightDecisionSeverityLabel(severity: HrInsightDecisionSignal['severity']): string {
	const labels: Record<HrInsightDecisionSignal['severity'], string> = {
		READY: '정상',
		INFO: '참고',
		WARNING: '주의',
		CRITICAL: '긴급'
	};
	return labels[severity] ?? severity;
}

function insightDecisionSeverityVariant(
	severity: HrInsightDecisionSignal['severity']
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (severity === 'READY') return 'success';
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	return 'outline';
}

function hoursLabel(value: number | null | undefined): string {
	return `${numberLabel(value)}시간`;
}

function insightActionHref(path: string): string {
	if (path.startsWith('/reports/attendance/summary')) {
		const query = path.includes('?') ? path.slice(path.indexOf('?')) : '';
		return `/reports${query}`;
	}
	if (path.startsWith('/reports/weekly-compliance/summary')) {
		const query = path.includes('?') ? path.slice(path.indexOf('?')) : '';
		return `/reports${query}`;
	}
	if (path.startsWith('/app/approvals')) return '/approvals';
	return path;
}

function HrInsightMetricCard({
	title,
	value,
	description,
	icon: Icon,
	barPercent,
	tone
}: {
	title: string;
	value: string;
	description: string;
	icon: HrInsightIcon;
	barPercent?: number;
	tone: string;
}) {
	return (
		<Card className="min-h-36">
			<CardContent className="flex h-full flex-col justify-between p-4 sm:p-5">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<p className="text-sm font-medium text-muted-foreground">{title}</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">{value}</p>
					</div>
					<span className={`rounded-md p-2 text-white ${tone}`}>
						<Icon className="size-4" />
					</span>
				</div>
				<div className="mt-4 space-y-2">
					{typeof barPercent === 'number' ? (
						<div className="h-2 overflow-hidden rounded-full bg-muted">
							<div
								className="h-full rounded-full bg-success"
								style={{ width: `${clampPercent(barPercent)}%` }}
							/>
						</div>
					) : null}
					<p className="text-xs leading-5 text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function HrInsightsRiskList({ risks }: { risks: HrInsightRisk[] }) {
	if (risks.length === 0) return <EmptyState message="주요 리스크가 없습니다." />;
	return (
		<div className="space-y-3">
			{risks.map((risk) => (
				<div
					key={`${risk.type}-${risk.employeeId}`}
					className="rounded-lg border border-border p-4"
				>
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-semibold">{risk.employeeName ?? `직원 #${risk.employeeId}`}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.title}</p>
						</div>
						<Badge variant={insightSeverityVariant(risk.severity)}>
							{insightSeverityLabel(risk.severity)}
						</Badge>
					</div>
					<p className="mt-3 text-sm font-medium">{risk.metric}</p>
				</div>
			))}
		</div>
	);
}

function HrInsightActionLink({
	href,
	title,
	description,
	icon: Icon
}: {
	href: string;
	title: string;
	description: string;
	icon: HrInsightIcon;
}) {
	return (
		<Link
			href={href}
			className="flex min-h-16 items-center gap-3 rounded-lg border border-border bg-card p-4 text-sm transition-colors hover:bg-muted/40"
		>
			<span className="rounded-md bg-primary/10 p-2 text-primary">
				<Icon className="size-4" />
			</span>
			<span className="min-w-0 flex-1">
				<span className="block font-semibold">{title}</span>
				<span className="mt-1 block truncate text-xs text-muted-foreground">{description}</span>
			</span>
			<ArrowRight className="size-4 shrink-0 text-muted-foreground" />
		</Link>
	);
}

function HrInsightDataConfidencePanel({
	dataConfidence,
	periodBenchmark,
	filterOptions
}: {
	dataConfidence?: HrInsightDataConfidence;
	periodBenchmark?: HrInsightPeriodBenchmark;
	filterOptions?: HrInsightFilterOptions;
}) {
	if (!dataConfidence && !periodBenchmark && !filterOptions) return null;
	const coverages = dataConfidence
		? [
				{ label: '계정 연결', value: dataConfidence.linkedAccountCoveragePercent },
				{ label: '프로필 완성', value: dataConfidence.profileCompletionPercent },
				{ label: '근태 기록', value: dataConfidence.attendanceCoveragePercent },
				{ label: '평가 제출', value: dataConfidence.reviewCoveragePercent }
			]
		: [];
	return (
		<Card>
			<CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<CardTitle>데이터 신뢰도</CardTitle>
					<CardDescription>
						열람 권한이 있는 HR 데이터의 커버리지와 전월 대비 변화를 함께 봅니다.
					</CardDescription>
				</div>
				{dataConfidence ? (
					<Badge variant={insightReliabilityVariant(dataConfidence.reliabilityLabel)}>
						{insightReliabilityLabel(dataConfidence.reliabilityLabel)}
					</Badge>
				) : null}
			</CardHeader>
			<CardContent className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
				<div className="grid grid-cols-2 gap-3">
					{coverages.map((item) => (
						<div key={item.label} className="rounded-lg border border-border p-3">
							<div className="flex items-center justify-between gap-2">
								<p className="text-xs font-medium text-muted-foreground">{item.label}</p>
								<p className="text-sm font-semibold">{percentLabel(item.value)}</p>
							</div>
							<div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
								<div
									className="h-full rounded-full bg-primary"
									style={{ width: `${clampPercent(item.value)}%` }}
								/>
							</div>
						</div>
					))}
				</div>
				<div className="grid gap-3 sm:grid-cols-3">
					{periodBenchmark ? (
						<>
							<div className="rounded-lg border border-border p-3">
								<p className="text-xs font-medium text-muted-foreground">평균 근무</p>
								<p className="mt-2 text-lg font-semibold">
									{hoursLabel(periodBenchmark.averageWorkHours)}
								</p>
								<p className="mt-1 text-xs text-muted-foreground">
									전월 {hoursLabel(periodBenchmark.previousAverageWorkHours)}
								</p>
							</div>
							<div className="rounded-lg border border-border p-3">
								<p className="text-xs font-medium text-muted-foreground">변화율</p>
								<p className="mt-2 text-lg font-semibold">
									전월 대비 {percentLabel(periodBenchmark.workHoursDeltaPercent)}
								</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{dateLabel(periodBenchmark.periodStart)} - {dateLabel(periodBenchmark.periodEnd)}
								</p>
							</div>
							<div className="rounded-lg border border-border p-3">
								<p className="text-xs font-medium text-muted-foreground">편차</p>
								<p className="mt-2 text-lg font-semibold">
									표준편차 {hoursLabel(periodBenchmark.workHoursStandardDeviation)}
								</p>
								<p className="mt-1 text-xs text-muted-foreground">
									초과근무 평균 {hoursLabel(periodBenchmark.averageOvertimeHours)}
								</p>
							</div>
						</>
					) : null}
					{filterOptions ? (
						<div className="rounded-lg border border-border p-3 sm:col-span-3">
							<p className="text-xs font-medium text-muted-foreground">분석 필터</p>
							<div className="mt-2 flex flex-wrap gap-2">
								{[...filterOptions.departments, ...filterOptions.positions]
									.slice(0, 8)
									.map((filter) => (
										<Badge key={filter} variant="outline">
											{filter}
										</Badge>
									))}
							</div>
						</div>
					) : null}
				</div>
			</CardContent>
		</Card>
	);
}

function HrInsightSegmentList({
	title,
	segments
}: {
	title: string;
	segments: HrInsightComparisonSegment[];
}) {
	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between gap-3">
				<p className="text-sm font-semibold">{title}</p>
				<Badge variant="outline">{numberLabel(segments.length)}개</Badge>
			</div>
			{segments.length > 0 ? (
				segments.map((segment) => (
					<div
						key={`${segment.dimension}-${segment.value}`}
						className="rounded-lg border border-border p-3"
					>
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="font-semibold">{segment.value}</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{numberLabel(segment.employeeCount)}명 · 위험{' '}
									{numberLabel(segment.monthlyRiskCount)}건
								</p>
							</div>
							<Badge variant={segment.signal === 'WATCH' ? 'warning' : 'success'}>
								{segment.signal === 'WATCH' ? '관찰' : '안정'}
							</Badge>
						</div>
						<div className="mt-3 grid grid-cols-2 gap-2 text-xs">
							<span className="rounded-md bg-muted px-2 py-1">
								평균 {hoursLabel(segment.averageWorkHours)}
							</span>
							<span className="rounded-md bg-muted px-2 py-1">
								초과 {hoursLabel(segment.averageOvertimeHours)}
							</span>
						</div>
					</div>
				))
			) : (
				<EmptyState message="분석할 세그먼트가 없습니다." />
			)}
		</div>
	);
}

function HrInsightGrowthSignals({ signals }: { signals: HrInsightGrowthSignal[] }) {
	return (
		<div className="grid gap-3 sm:grid-cols-2">
			{signals.map((signal) => (
				<div key={signal.code} className="rounded-lg border border-border p-3">
					<div className="flex items-start justify-between gap-3">
						<div>
							<p className="text-sm font-semibold">{signal.label}</p>
							<p className="mt-2 text-xl font-semibold tracking-normal">{signal.value}</p>
						</div>
						<Badge variant={insightGrowthStatusVariant(signal.status)}>
							{insightGrowthStatusLabel(signal.status)}
						</Badge>
					</div>
					<p className="mt-2 text-xs leading-5 text-muted-foreground">{signal.description}</p>
				</div>
			))}
		</div>
	);
}

function HrInsightDecisionSignals({ signals }: { signals: HrInsightDecisionSignal[] }) {
	if (signals.length === 0) return <EmptyState message="의사결정 신호가 없습니다." />;
	return (
		<div className="space-y-3">
			{signals.map((signal) => (
				<div key={signal.code} className="rounded-lg border border-border p-3">
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-semibold">{signal.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{signal.metric}</p>
						</div>
						<Badge variant={insightDecisionSeverityVariant(signal.severity)}>
							{insightDecisionSeverityLabel(signal.severity)}
						</Badge>
					</div>
					<p className="mt-3 text-xs leading-5 text-muted-foreground">{signal.recommendation}</p>
				</div>
			))}
		</div>
	);
}

export function HrInsightsPage() {
	const [state, setState] = useState<RequestState<AppHrInsightsResponse>>({ status: 'idle' });

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const data = await api.get<AppHrInsightsResponse>('/app/insights/hr');
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="인사이트"
				description="출근, 결재, 주 52시간, 월간 근태 리스크를 모바일에서 바로 점검합니다."
				actions={
					<Button variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<HrInsightMetricCard
							title="출근율"
							value={percentLabel(data.workforce.attendanceRatePercent)}
							description={`출근 ${numberLabel(data.workforce.presentCount)}명 / 전체 ${numberLabel(
								data.workforce.totalEmployees
							)}명`}
							icon={UsersRound}
							barPercent={data.workforce.attendanceRatePercent}
							tone="bg-success"
						/>
						<HrInsightMetricCard
							title="대기 결재"
							value={`${numberLabel(data.approvals.pendingTotal)}건`}
							description={`휴가 ${numberLabel(data.approvals.pendingLeaveRequests)} · 정정 ${numberLabel(
								data.approvals.pendingAttendanceAdjustmentRequests
							)} · 초과 ${numberLabel(data.approvals.pendingOvertimeWorkRequests)}`}
							icon={CheckCircle2}
							tone="bg-primary"
						/>
						<HrInsightMetricCard
							title="주간 리스크"
							value={`${numberLabel(data.workRisks.weeklyComplianceRiskCount)}명`}
							description={`한도 초과 ${numberLabel(
								data.workRisks.weeklyComplianceOverLimitCount
							)}명 · 초과근무 위험 ${numberLabel(data.workRisks.weeklyOvertimeRiskCount)}명`}
							icon={Clock3}
							tone="bg-warning"
						/>
						<HrInsightMetricCard
							title="근태 점검"
							value={`${numberLabel(data.attendanceQuality.monthlyRiskCount)}건`}
							description={`검토 ${numberLabel(
								data.attendanceQuality.monthlyNeedsReviewCount
							)} · 주의 ${numberLabel(data.attendanceQuality.monthlyCautionCount)}`}
							icon={CalendarCheck}
							tone="bg-destructive"
						/>
					</div>

					<HrInsightDataConfidencePanel
						dataConfidence={data.dataConfidence}
						periodBenchmark={data.periodBenchmark}
						filterOptions={data.filterOptions}
					/>

					{(data.organizationSegments?.length ?? 0) > 0 ||
					(data.positionSegments?.length ?? 0) > 0 ||
					(data.growthSignals?.length ?? 0) > 0 ||
					(data.decisionSignals?.length ?? 0) > 0 ? (
						<div className="grid items-start gap-4 xl:grid-cols-[1fr_1fr]">
							<Card>
								<CardHeader>
									<CardTitle>비교 분석</CardTitle>
									<CardDescription>
										조직과 직무 기준으로 근무 시간, 초과근무, 월간 리스크를 비교합니다.
									</CardDescription>
								</CardHeader>
								<CardContent className="grid gap-5 lg:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
									<HrInsightSegmentList title="조직별" segments={data.organizationSegments ?? []} />
									<HrInsightSegmentList
										title="직무·직책별"
										segments={data.positionSegments ?? []}
									/>
								</CardContent>
							</Card>

							<div className="space-y-4">
								<Card>
									<CardHeader>
										<CardTitle>성장 신호</CardTitle>
										<CardDescription>
											채용, 리텐션, 목표, 평가 데이터를 한 화면에서 연결합니다.
										</CardDescription>
									</CardHeader>
									<CardContent>
										<HrInsightGrowthSignals signals={data.growthSignals ?? []} />
									</CardContent>
								</Card>

								<Card>
									<CardHeader>
										<CardTitle>의사결정 신호</CardTitle>
										<CardDescription>
											전 기간 대비와 표준편차를 함께 보고 편향된 해석을 줄입니다.
										</CardDescription>
									</CardHeader>
									<CardContent>
										<HrInsightDecisionSignals signals={data.decisionSignals ?? []} />
									</CardContent>
								</Card>
							</div>
						</div>
					) : null}

					<div className="grid items-start gap-4 xl:grid-cols-[1.05fr_0.95fr]">
						<Card>
							<CardHeader>
								<CardTitle>주요 리스크</CardTitle>
								<CardDescription>
									{data.month} 월간 근태와 {dateLabel(data.weekStart)} 시작 주의 위험 신호입니다.
								</CardDescription>
							</CardHeader>
							<CardContent>
								<HrInsightsRiskList risks={data.topRisks} />
							</CardContent>
						</Card>

						<div className="space-y-4">
							<Card>
								<CardHeader>
									<CardTitle>운영 요약</CardTitle>
									<CardDescription>
										{dateLabel(data.weekStart)} - {dateLabel(data.weekEnd)}
									</CardDescription>
								</CardHeader>
								<CardContent className="grid grid-cols-2 gap-3 text-sm">
									<div className="rounded-lg border border-border p-3">
										<p className="text-xs font-medium text-muted-foreground">지각률</p>
										<p className="mt-2 text-lg font-semibold">
											{percentLabel(data.workforce.lateRatePercent)}
										</p>
									</div>
									<div className="rounded-lg border border-border p-3">
										<p className="text-xs font-medium text-muted-foreground">휴가 중</p>
										<p className="mt-2 text-lg font-semibold">
											{numberLabel(data.workforce.onLeaveCount)}명
										</p>
									</div>
									<div className="rounded-lg border border-border p-3">
										<p className="text-xs font-medium text-muted-foreground">결근</p>
										<p className="mt-2 text-lg font-semibold">
											{numberLabel(data.workforce.absentCount)}명
										</p>
									</div>
									<div className="rounded-lg border border-border p-3">
										<p className="text-xs font-medium text-muted-foreground">초과 한도</p>
										<p className="mt-2 text-lg font-semibold">
											{numberLabel(data.workRisks.weeklyOvertimeOverLimitCount)}명
										</p>
									</div>
								</CardContent>
							</Card>

							<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
								<HrInsightActionLink
									href={insightActionHref(data.actionPaths.dashboardPath)}
									title="대시보드"
									description="오늘 근태와 대기 업무를 다시 확인"
									icon={FileText}
								/>
								<HrInsightActionLink
									href={insightActionHref(data.actionPaths.pendingApprovalsPath)}
									title="대기 결재"
									description={`${numberLabel(data.approvals.pendingTotal)}건 처리 필요`}
									icon={CheckCircle2}
								/>
								<HrInsightActionLink
									href={insightActionHref(data.actionPaths.attendanceSummaryPath)}
									title="근태 리포트"
									description={`${data.month} 월간 근태 상세`}
									icon={CalendarCheck}
								/>
								<HrInsightActionLink
									href={insightActionHref(data.actionPaths.weeklyCompliancePath)}
									title="주간 준수"
									description={`${dateLabel(data.weekStart)} 시작 주 근로시간`}
									icon={Clock3}
								/>
							</div>
						</div>
					</div>
				</>
			) : null}
		</div>
	);
}

type HomeData = {
	leaveBalance: LeaveBalanceResponse | null;
	notifications: NotificationResponse[];
};

function extractNotifications(data: unknown): NotificationResponse[] {
	if (Array.isArray(data)) return data as NotificationResponse[];
	if (
		typeof data === 'object' &&
		data !== null &&
		Array.isArray((data as { content?: unknown }).content)
	) {
		return (data as { content: NotificationResponse[] }).content;
	}
	return [];
}

const homeRoleLabel: Record<string, string> = {
	OWNER: '대표',
	ADMIN: '관리자',
	EMPLOYEE: '직원',
	SUPER_ADMIN: '슈퍼관리자'
};

export function HomePage() {
	const [state, setState] = useState<RequestState<HomeData>>({ status: 'idle' });
	const todayAttendance = useTodayAttendance();
	const { user } = useAuth();

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [leaveBalance, notifications] = await Promise.all([
					api.get<LeaveBalanceResponse>('/leave/balance').catch(() => null),
					api
						.get<unknown>('/notifications')
						.then(extractNotifications)
						.catch(() => [])
				]);
				setState({ status: 'success', data: { leaveBalance, notifications } });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	if (state.status === 'loading' || state.status === 'idle') {
		return (
			<div className="mx-auto max-w-2xl space-y-5 lg:max-w-3xl">
				<MobileHeader
					title="오늘의 HR"
					subtitle={user ? `${user.name} · ${homeRoleLabel[user.role] ?? user.role}` : undefined}
				/>
				<Card>
					<CardContent className="flex min-h-80 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			</div>
		);
	}

	if (state.status === 'error') {
		return (
			<div className="mx-auto max-w-2xl space-y-5 lg:max-w-3xl">
				<MobileHeader
					title="오늘의 HR"
					subtitle={user ? `${user.name} · ${homeRoleLabel[user.role] ?? user.role}` : undefined}
				/>
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			</div>
		);
	}

	const { leaveBalance, notifications } = state.data;
	const attendance = todayAttendance.state.status === 'success' ? todayAttendance.state.data : null;
	const attendanceLoading =
		todayAttendance.state.status === 'idle' || todayAttendance.state.status === 'loading';
	const attendanceError =
		todayAttendance.actionError ??
		(todayAttendance.state.status === 'error' ? todayAttendance.state.message : null);
	const attendanceStatusLabel = attendance?.checkOutTime
		? '퇴근 완료'
		: attendance?.checkInTime
			? '근무 중'
			: '출근 전';
	const unreadCount = notifications.filter((n) => !n.isRead).length;

	return (
		<div className="mx-auto max-w-2xl space-y-5 lg:max-w-3xl">
			<MobileHeader
				title="오늘의 HR"
				subtitle={user ? `${user.name} · ${homeRoleLabel[user.role] ?? user.role}` : undefined}
				trailing={
					user ? (
						<span
							aria-label={`${user.name} 프로필`}
							className="grid size-9 shrink-0 place-items-center rounded-full bg-primary text-[13px] font-bold text-primary-foreground"
						>
							{user.name.charAt(0)}
						</span>
					) : null
				}
			/>

			{/* 빠른 출퇴근: CommandCard(헤더) + AttendanceActionPanel(형제) */}
			<CommandCard
				chip={<StatusChip tone="blue">{attendanceStatusLabel}</StatusChip>}
				title="빠른 출퇴근"
				subtitle="오늘의 근태를 바로 처리합니다"
			/>
			<AttendanceActionPanel
				attendance={attendance}
				loading={attendanceLoading}
				busy={todayAttendance.actionBusy}
				error={attendanceError}
				onAction={(kind, options) => void todayAttendance.performAction(kind, options)}
				onUpdate={(request) => todayAttendance.updateToday(request)}
				onCreateBreak={(request) => todayAttendance.createBreakRecord(request)}
				onUpdateBreak={(breakId, request) => todayAttendance.updateBreakRecord(breakId, request)}
				onDeleteBreak={(breakId) => todayAttendance.deleteBreakRecord(breakId)}
			/>

			{/* 2 MetricCard: 결재 대기 데이터 없음 → grid-cols-2 */}
			<div className="grid grid-cols-2 gap-3">
				<MetricCard
					label="잔여 연차"
					value={`${numberLabel(leaveBalance?.availableDays ?? leaveBalance?.remainingDays)}일`}
					tone="primary"
				/>
				<MetricCard
					label="알림"
					value={numberLabel(unreadCount)}
					tone={unreadCount > 0 ? 'danger' : 'default'}
				/>
			</div>

			<section className="space-y-3">
				<SectionHeader
					action={
						<Button
							variant="outline"
							size="sm"
							onClick={() => {
								void load();
								void todayAttendance.load();
							}}
						>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					}
				>
					바로 처리할 일
				</SectionHeader>
				{notifications.length === 0 ? (
					<EmptyState message="새 알림이 없습니다." />
				) : (
					<div className="space-y-2">
						{notifications.slice(0, 5).map((notification) => (
							<ListRow
								key={notification.id}
								title={notification.title}
								subtitle={notification.message ?? notification.createdAt.slice(0, 10)}
								iconTone={notification.isRead ? 'neutral' : 'primary'}
								trailing={
									<StatusChip tone={notification.isRead ? 'neutral' : 'blue'}>
										{notification.isRead ? '읽음' : '안읽음'}
									</StatusChip>
								}
							/>
						))}
					</div>
				)}
			</section>
		</div>
	);
}

function employeeStatusLabel(status: EmployeeResponse['status'] | null | undefined): string {
	const labels: Record<EmployeeResponse['status'], string> = {
		ACTIVE: '재직',
		INACTIVE: '비활성',
		RESIGNED: '퇴사'
	};
	return status ? labels[status] : '-';
}

function employeeStatusVariant(status: EmployeeResponse['status'] | null | undefined) {
	if (status === 'ACTIVE') return 'success';
	if (status === 'RESIGNED') return 'outline';
	return 'warning';
}

function inviteStatusLabel(status: EmployeeResponse['inviteStatus'] | null | undefined): string {
	const labels: Record<NonNullable<EmployeeResponse['inviteStatus']>, string> = {
		LINKED: '초대 완료',
		INVITED: '초대 중',
		NOT_INVITED: '미초대'
	};
	return status ? labels[status] : '미초대';
}

function inviteStatusVariant(status: EmployeeResponse['inviteStatus'] | null | undefined) {
	if (status === 'LINKED') return 'success';
	if (status === 'INVITED') return 'warning';
	return 'outline';
}

type InviteFeedback = {
	kind: 'success' | 'error';
	message: string;
	inviteUrl?: string | null;
};

function canResendInvite(employee: EmployeeResponse) {
	return employee.status === 'ACTIVE' && employee.inviteStatus === 'INVITED';
}

function inviteFeedbackFromResponse(response: InviteResponse): InviteFeedback {
	if (response.manualDeliveryRequired && response.inviteUrl) {
		return {
			kind: 'success',
			message: '재초대 링크를 생성했습니다. 직접 전달해주세요.',
			inviteUrl: response.inviteUrl
		};
	}
	return { kind: 'success', message: '재초대 메일 발송을 요청했습니다.' };
}

function InviteFeedbackBanner({ feedback }: { feedback: InviteFeedback | null }) {
	if (!feedback) return null;
	const className =
		feedback.kind === 'success'
			? 'rounded-lg bg-success/10 px-4 py-3 text-sm text-success'
			: 'rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive';
	return (
		<div className={className}>
			<p>{feedback.message}</p>
			{feedback.inviteUrl ? (
				<p className="mt-2 break-all font-mono text-xs" aria-label="재초대 링크">
					{feedback.inviteUrl}
				</p>
			) : null}
		</div>
	);
}

function ResendInviteButton({
	employee,
	onDone,
	size = 'sm'
}: {
	employee: EmployeeResponse;
	onDone: (feedback: InviteFeedback) => void | Promise<void>;
	size?: 'sm' | 'md';
}) {
	const [submitting, setSubmitting] = useState(false);
	if (!canResendInvite(employee)) return null;

	async function handleClick() {
		setSubmitting(true);
		try {
			const response = await api.post<InviteResponse>(`/employees/${employee.id}/invite/resend`);
			await onDone(inviteFeedbackFromResponse(response));
		} catch (error) {
			await onDone({ kind: 'error', message: getErrorMessage(error, '재초대하지 못했습니다.') });
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Button
			type="button"
			variant="outline"
			size={size}
			onClick={() => void handleClick()}
			disabled={submitting}
			aria-label={`${employee.name} 재초대`}
		>
			<Mail className="size-4" />
			{submitting ? '요청 중...' : '재초대'}
		</Button>
	);
}

function employeeProfileLabel(employee: EmployeeResponse): string {
	if (typeof employee.profileCompletionRate === 'number') {
		return `프로필 ${employee.profileCompletionRate}%`;
	}
	return employee.profileComplete ? '프로필 완료' : '프로필 미완성';
}

function EmployeeStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: number;
	description: string;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<p className="text-sm font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal sm:mt-3 sm:text-3xl">
					{numberLabel(value)}
				</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

type EmployeesPageData = {
	employees: PageResponse<EmployeeResponse>;
	operations: PersonnelManagementSummaryResponse | null;
	personnelSchedule: PersonnelScheduleResponse | null;
	jobCatalogItems: EmployeeJobCatalogResponse[];
	searchPresets: EmployeeSearchPresetResponse[];
};

type EmployeeStatusFilter = '' | EmployeeResponse['status'];
type EmployeeInviteStatusFilter = '' | EmployeeInviteStatus;
type EmployeeInviteEmailDeliveryStatusFilter = '' | InviteEmailDeliveryStatus;
type EmployeeProfileCompletionFilter = '' | EmployeeProfileCompletionStatus;
type EmployeeAttendanceStatusFilter = '' | EmployeeAttendanceStatus;

type EmployeeSearchFilters = {
	keyword: string;
	status: EmployeeStatusFilter;
	inviteStatus: EmployeeInviteStatusFilter;
	inviteEmailDeliveryStatus: EmployeeInviteEmailDeliveryStatusFilter;
	profileCompletion: EmployeeProfileCompletionFilter;
	attendanceStatus: EmployeeAttendanceStatusFilter;
	department: string;
	departmentUnassigned: boolean;
};

const emptyEmployeeSearchFilters: EmployeeSearchFilters = {
	keyword: '',
	status: '',
	inviteStatus: '',
	inviteEmailDeliveryStatus: '',
	profileCompletion: '',
	attendanceStatus: '',
	department: '',
	departmentUnassigned: false
};

const employeeStatusFilterOptions: Array<{ value: EmployeeStatusFilter; label: string }> = [
	{ value: '', label: '전체 상태' },
	{ value: 'ACTIVE', label: '재직' },
	{ value: 'INACTIVE', label: '휴직' },
	{ value: 'RESIGNED', label: '퇴직' }
];

const employeeInviteStatusFilterOptions: Array<{
	value: EmployeeInviteStatusFilter;
	label: string;
}> = [
	{ value: '', label: '전체 초대' },
	{ value: 'LINKED', label: '가입 완료' },
	{ value: 'INVITED', label: '초대 발송' },
	{ value: 'NOT_INVITED', label: '미초대' }
];

const employeeInviteEmailStatusFilterOptions: Array<{
	value: EmployeeInviteEmailDeliveryStatusFilter;
	label: string;
}> = [
	{ value: '', label: '전체 메일' },
	{ value: 'QUEUED', label: '메일 대기' },
	{ value: 'SENT', label: '메일 발송' },
	{ value: 'DISABLED', label: '메일 비활성' },
	{ value: 'FAILED', label: '메일 실패' }
];

const employeeProfileCompletionFilterOptions: Array<{
	value: EmployeeProfileCompletionFilter;
	label: string;
}> = [
	{ value: '', label: '전체 프로필' },
	{ value: 'COMPLETE', label: '프로필 완성' },
	{ value: 'INCOMPLETE', label: '프로필 미완성' }
];

const employeeAttendanceStatusFilterOptions: Array<{
	value: EmployeeAttendanceStatusFilter;
	label: string;
}> = [
	{ value: '', label: '전체 근태' },
	{ value: 'ENABLED', label: '근태 사용' },
	{ value: 'DISABLED', label: '근태 미사용' },
	{ value: 'DEVICE_ASSIGNED', label: '기기 등록' },
	{ value: 'DEVICE_MISSING', label: '기기 미등록' }
];

function cloneEmployeeSearchFilters(filters: EmployeeSearchFilters): EmployeeSearchFilters {
	return { ...filters };
}

function hasEmployeeSearchFilter(filters: EmployeeSearchFilters): boolean {
	return (
		filters.keyword.trim().length > 0 ||
		filters.status !== '' ||
		filters.inviteStatus !== '' ||
		filters.inviteEmailDeliveryStatus !== '' ||
		filters.profileCompletion !== '' ||
		filters.attendanceStatus !== '' ||
		filters.department.trim().length > 0 ||
		filters.departmentUnassigned
	);
}

function employeeListPath(filters: EmployeeSearchFilters): string {
	const params = new URLSearchParams({ page: '0', size: '20' });
	const keyword = filters.keyword.trim();
	const department = filters.department.trim();
	if (keyword) params.set('keyword', keyword);
	if (filters.status) params.set('status', filters.status);
	if (filters.inviteStatus) params.set('inviteStatus', filters.inviteStatus);
	if (filters.inviteEmailDeliveryStatus) {
		params.set('inviteEmailDeliveryStatus', filters.inviteEmailDeliveryStatus);
	}
	if (filters.profileCompletion) params.set('profileCompletion', filters.profileCompletion);
	if (filters.attendanceStatus) params.set('attendanceStatus', filters.attendanceStatus);
	if (filters.departmentUnassigned) {
		params.set('departmentUnassigned', 'true');
	} else if (department) {
		params.set('department', department);
	}
	return `/employees?${params.toString()}`;
}

function requestFromEmployeeSearchFilters(
	name: string,
	filters: EmployeeSearchFilters
): CreateEmployeeSearchPresetRequest {
	return {
		name: name.trim(),
		keyword: filters.keyword.trim() || null,
		status: filters.status || null,
		inviteStatus: filters.inviteStatus || null,
		inviteEmailDeliveryStatus: filters.inviteEmailDeliveryStatus || null,
		profileCompletion: filters.profileCompletion || null,
		attendanceStatus: filters.attendanceStatus || null,
		department: filters.departmentUnassigned ? null : filters.department.trim() || null,
		departmentUnassigned: filters.departmentUnassigned
	};
}

function filtersFromEmployeeSearchPreset(
	preset: EmployeeSearchPresetResponse
): EmployeeSearchFilters {
	return {
		keyword: preset.keyword ?? '',
		status: preset.status ?? '',
		inviteStatus: preset.inviteStatus ?? '',
		inviteEmailDeliveryStatus: preset.inviteEmailDeliveryStatus ?? '',
		profileCompletion: preset.profileCompletion ?? '',
		attendanceStatus: preset.attendanceStatus ?? '',
		department: preset.departmentUnassigned ? '' : (preset.department ?? ''),
		departmentUnassigned: preset.departmentUnassigned
	};
}

const employeeJobCatalogCategoryOrder: EmployeeJobCatalogCategory[] = [
	'JOB',
	'JOB_GROUP',
	'JOB_TITLE',
	'JOB_POSITION',
	'JOB_GRADE'
];

const employeeJobCatalogCategoryLabels: Record<EmployeeJobCatalogCategory, string> = {
	JOB: '직무',
	JOB_GROUP: '직군',
	JOB_TITLE: '직책',
	JOB_POSITION: '직위',
	JOB_GRADE: '직급'
};

function personnelChecklistVariant(
	status: PersonnelChecklistStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function personnelChecklistStatusLabel(status: PersonnelChecklistStatus): string {
	if (status === 'READY') return '완료';
	if (status === 'WARNING') return '주의';
	if (status === 'WAITING') return '대기';
	return '-';
}

function personnelAlertVariant(
	severity: PersonnelLifecycleAlertSeverity
): 'warning' | 'destructive' | 'outline' {
	if (severity === 'ERROR') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	return 'outline';
}

function personnelRetireeAccessRiskVariant(
	level: PersonnelRetireeAccessRiskLevel
): 'success' | 'warning' | 'destructive' {
	if (level === 'CRITICAL') return 'destructive';
	if (level === 'WARNING') return 'warning';
	return 'success';
}

function personnelSearchReadinessVariant(
	status: PersonnelSearchReadinessStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function personnelProfileKnowledgeVariant(
	status: PersonnelProfileKnowledgeStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function retireeAccessStatusLabel(status: PersonnelRetireeAccessItem['status']): string {
	if (status === 'RESIGNED') return '퇴직';
	if (status === 'INACTIVE') return '비활성';
	return status;
}

const personnelScheduleTypeOptions: Array<{
	value: PersonnelScheduleType;
	label: string;
	description: string;
}> = [
	{
		value: 'BIRTHDAY',
		label: '생일',
		description: '생일을 맞는 구성원'
	},
	{
		value: 'JOIN_ANNIVERSARY',
		label: '입사기념일',
		description: '근속 케어가 필요한 구성원'
	},
	{
		value: 'CONTRACT_EXPIRY',
		label: '계약 만료',
		description: '계약 갱신 검토 대상'
	}
];

const contractExpiryActionOptions: Array<{
	status: Exclude<ContractExpiryActionStatus, 'PENDING'>;
	label: string;
	note: string;
	Icon: ComponentType<{ className?: string }>;
}> = [
	{
		status: 'RENEWAL_PLANNED',
		label: '갱신 예정',
		note: '인사 일정에서 갱신 예정으로 표시',
		Icon: RefreshCw
	},
	{
		status: 'NON_RENEWAL_PLANNED',
		label: '종료 예정',
		note: '인사 일정에서 종료 예정으로 표시',
		Icon: AlertTriangle
	},
	{
		status: 'COMPLETED',
		label: '조치 완료',
		note: '인사 일정에서 조치 완료로 표시',
		Icon: CheckCircle2
	}
];

function personnelScheduleTypeLabel(type: PersonnelScheduleType | string): string {
	const option = personnelScheduleTypeOptions.find((item) => item.value === type);
	return option?.label ?? type;
}

function personnelScheduleSeverityVariant(
	severity: PersonnelScheduleSeverity
): 'warning' | 'destructive' | 'outline' {
	if (severity === 'ERROR') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	return 'outline';
}

function normalizePersonnelScheduleTypes(types: PersonnelScheduleType[] | null | undefined) {
	const ordered = personnelScheduleTypeOptions.map((option) => option.value);
	const selected = new Set(types?.length ? types : ordered);
	return ordered.filter((type) => selected.has(type));
}

function personnelSchedulePath(from: string, to: string, types: PersonnelScheduleType[]): string {
	const params = new URLSearchParams();
	if (from) params.set('from', from);
	if (to) params.set('to', to);
	if (types.length > 0) params.set('types', types.join(','));
	const query = params.toString();
	return query ? `/employees/operations/schedule?${query}` : '/employees/operations/schedule';
}

function contractExpiryActionVariant(
	status: ContractExpiryActionStatus | null | undefined,
	severity: PersonnelScheduleSeverity
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'COMPLETED' || status === 'RENEWAL_PLANNED') return 'success';
	if (status === 'NON_RENEWAL_PLANNED') return 'outline';
	return personnelScheduleSeverityVariant(severity);
}

function PersonnelChecklistCard({ item }: { item: PersonnelChecklistItem }) {
	const Icon =
		item.status === 'WARNING' ? AlertTriangle : item.status === 'READY' ? CheckCircle2 : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={personnelChecklistVariant(item.status)}>
					{personnelChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function PersonnelRetireeAccessPanel({
	operations
}: {
	operations: PersonnelManagementSummaryResponse;
}) {
	const riskLevel = operations.retireeAccessRiskLevel ?? 'READY';
	const queue = operations.retireeAccessQueue ?? [];
	const riskLabel = operations.retireeAccessRiskLabel ?? '퇴직자 접근 안정';
	const riskSummary =
		operations.retireeAccessRiskSummary ?? '퇴직·비활성 구성원의 계정 연결 리스크가 없습니다.';
	const recommendedAction =
		operations.retireeAccessRecommendedAction ??
		'퇴직 처리 시 계정 연결 해제와 인사기록 접근 권한을 계속 점검하세요.';
	const departedLinkedAccountCount = operations.departedLinkedAccountCount ?? 0;
	const resignedEmployeeCount = operations.resignedEmployeeCount ?? 0;

	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<ShieldCheck className="size-4 text-muted-foreground" />
						<p className="font-semibold">퇴직자 접근 리스크</p>
					</div>
					<p className="mt-2 text-sm text-muted-foreground">{riskSummary}</p>
				</div>
				<Badge variant={personnelRetireeAccessRiskVariant(riskLevel)}>{riskLabel}</Badge>
			</div>

			<div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">퇴직 구성원</p>
					<p className="mt-1 text-lg font-semibold tracking-normal">
						{numberLabel(resignedEmployeeCount)}명
					</p>
				</div>
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">계정 연결 잔존</p>
					<p className="mt-1 text-lg font-semibold tracking-normal">
						{numberLabel(departedLinkedAccountCount)}명
					</p>
				</div>
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">권장 조치</p>
					<p className="mt-1 text-sm font-medium">{recommendedAction}</p>
				</div>
			</div>

			<div className="mt-4 space-y-3">
				{queue.length > 0 ? (
					queue.map((item) => (
						<div
							key={item.employeeId}
							className="flex flex-col gap-2 rounded-md border border-border bg-muted/20 p-3 sm:flex-row sm:items-start sm:justify-between"
						>
							<div className="min-w-0">
								<p className="font-medium">{item.employeeName}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{item.department ?? '부서 미지정'} · {item.position ?? '직책 미지정'}
								</p>
							</div>
							<div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
								<Badge variant="outline">{retireeAccessStatusLabel(item.status)}</Badge>
								<Badge variant="warning">{item.actionLabel}</Badge>
							</div>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">
						계정 연결이 남은 퇴직·비활성 구성원이 없습니다.
					</p>
				)}
			</div>
		</div>
	);
}

function PersonnelSearchReadinessPanel({
	operations
}: {
	operations: PersonnelManagementSummaryResponse;
}) {
	const readiness: PersonnelSearchReadiness = operations.searchReadiness ?? {
		status: 'READY',
		label: '검색 운영 가능',
		filterCoveragePercent: 100,
		searchableEmployeeCount: operations.totalEmployeeCount,
		savedSearchPresetCount: operations.searchSegments.length,
		scheduleAlertCount:
			operations.upcomingAnniversaryCount +
			(operations.upcomingBirthdayCount ?? 0) +
			operations.contractExpiringCount,
		recommendedAction: '저장 검색 조건과 생애주기 알림을 정기적으로 점검하세요.'
	};
	const filterOptions: PersonnelSearchFilterOption[] = operations.filterOptions ?? [];

	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<Search className="size-4 text-muted-foreground" />
						<p className="font-semibold">검색 준비도</p>
					</div>
					<p className="mt-2 text-sm text-muted-foreground">{readiness.recommendedAction}</p>
				</div>
				<Badge variant={personnelSearchReadinessVariant(readiness.status)}>{readiness.label}</Badge>
			</div>

			<div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">필터 커버리지</p>
					<p className="mt-1 text-lg font-semibold tracking-normal">
						{numberLabel(readiness.filterCoveragePercent)}%
					</p>
				</div>
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">검색 가능한 구성원</p>
					<p className="mt-1 text-lg font-semibold tracking-normal">
						{numberLabel(readiness.searchableEmployeeCount)}명
					</p>
				</div>
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">저장 조건</p>
					<p className="mt-1 text-sm font-semibold">
						저장 조건 {numberLabel(readiness.savedSearchPresetCount)}개
					</p>
				</div>
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">일정 알림</p>
					<p className="mt-1 text-lg font-semibold tracking-normal">
						{numberLabel(readiness.scheduleAlertCount)}건
					</p>
				</div>
			</div>

			<div className="mt-4">
				<div className="flex items-center justify-between gap-3">
					<p className="font-semibold">필터 옵션</p>
					<Badge variant="outline">{numberLabel(filterOptions.length)}개 조건</Badge>
				</div>
				<div className="mt-3 grid gap-2 lg:grid-cols-2">
					{filterOptions.length > 0 ? (
						filterOptions.slice(0, 8).map((option) => (
							<div
								key={`${option.type}-${option.filterHint}`}
								className="flex min-w-0 items-start justify-between gap-3 rounded-md border border-border bg-muted/20 p-3"
							>
								<div className="min-w-0">
									<p className="font-medium">{option.label}</p>
									<p className="mt-1 break-all font-mono text-xs text-muted-foreground">
										{option.filterHint}
									</p>
								</div>
								<Badge variant={option.count > 0 ? 'warning' : 'outline'}>
									{numberLabel(option.count)}명
								</Badge>
							</div>
						))
					) : (
						<p className="text-sm text-muted-foreground">사용 가능한 검색 필터 옵션이 없습니다.</p>
					)}
				</div>
			</div>
		</div>
	);
}

function PersonnelProfileKnowledgePanel({
	operations
}: {
	operations: PersonnelManagementSummaryResponse;
}) {
	const hasActiveEmployees = operations.activeEmployeeCount > 0;
	const summary: PersonnelProfileKnowledgeSummary = operations.profileKnowledgeSummary ?? {
		status: hasActiveEmployees ? 'WAITING' : 'READY',
		label: hasActiveEmployees ? '성장 기록 데이터 부족' : '성장 기록 운영 가능',
		coveragePercent: hasActiveEmployees ? 0 : 100,
		educationRecordCount: 0,
		personnelNoteCount: 0,
		awardEventCount: 0,
		disciplineEventCount: 0,
		employeesWithGrowthRecordCount: 0,
		employeesWithoutGrowthRecordCount: operations.activeEmployeeCount,
		recommendedAction: hasActiveEmployees
			? '직원 상세에서 교육 정보와 관리자 인사노트를 먼저 등록해 성장 기록 기준을 만드세요.'
			: '교육 이력, 관리자 인사노트, 수상·징계 기록을 평가·보상 주기와 함께 점검하세요.'
	};
	const stats = [
		{
			label: '교육 기록',
			count: summary.educationRecordCount,
			description: '학력과 교육 이력',
			Icon: FileText
		},
		{
			label: '관리자 인사노트',
			count: summary.personnelNoteCount,
			description: '성장·리스크 메모',
			Icon: BadgeCheck
		},
		{
			label: '수상',
			count: summary.awardEventCount,
			description: '포상과 인정 기록',
			Icon: Gift
		},
		{
			label: '징계',
			count: summary.disciplineEventCount,
			description: '주의와 후속 조치',
			Icon: AlertTriangle
		}
	];

	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<BadgeCheck className="size-4 text-muted-foreground" />
						<p className="font-semibold">성장 기록 커버리지</p>
					</div>
					<p className="mt-2 text-sm text-muted-foreground">{summary.recommendedAction}</p>
				</div>
				<Badge variant={personnelProfileKnowledgeVariant(summary.status)}>{summary.label}</Badge>
			</div>

			<div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3">
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">커버리지</p>
					<p className="mt-1 text-lg font-semibold tracking-normal">
						{numberLabel(summary.coveragePercent)}%
					</p>
				</div>
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">기록 보유</p>
					<p className="mt-1 text-lg font-semibold tracking-normal">
						{numberLabel(summary.employeesWithGrowthRecordCount)}명
					</p>
				</div>
				<div className="rounded-md border border-border bg-muted/20 p-3">
					<p className="text-xs text-muted-foreground">기록 미등록</p>
					<p className="mt-1 text-sm font-semibold">
						기록 미등록 {numberLabel(summary.employeesWithoutGrowthRecordCount)}명
					</p>
				</div>
			</div>

			<div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
				{stats.map(({ label, count, description, Icon }) => (
					<div key={label} className="rounded-md border border-border bg-muted/20 p-3">
						<div className="flex items-center justify-between gap-2">
							<p className="text-sm font-semibold">{label}</p>
							<Icon className="size-4 text-muted-foreground" />
						</div>
						<p className="mt-2 text-lg font-semibold tracking-normal">{numberLabel(count)}건</p>
						<p className="mt-1 text-xs text-muted-foreground">{description}</p>
					</div>
				))}
			</div>
		</div>
	);
}

function PersonnelSearchSegments({ segments }: { segments: PersonnelSearchSegment[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<Search className="size-4 text-muted-foreground" />
				<p className="font-semibold">저장 검색 세그먼트</p>
			</div>
			<div className="mt-4 space-y-3">
				{segments.map((segment) => (
					<div key={segment.label} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{segment.label}</p>
							<p className="mt-1 text-sm text-muted-foreground">{segment.description}</p>
							<p className="mt-1 break-all font-mono text-xs text-muted-foreground">
								{segment.filterHint}
							</p>
						</div>
						<Badge variant={segment.count > 0 ? 'warning' : 'outline'}>
							{numberLabel(segment.count)}명
						</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function PersonnelLifecycleAlerts({ alerts }: { alerts: PersonnelLifecycleAlert[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<CalendarCheck className="size-4 text-muted-foreground" />
				<p className="font-semibold">온·오프보딩 알림</p>
			</div>
			<div className="mt-4 space-y-3">
				{alerts.length > 0 ? (
					alerts.map((alert) => (
						<div key={`${alert.type}-${alert.employeeId}-${alert.dueDate}`} className="space-y-1">
							<div className="flex items-start justify-between gap-3">
								<p className="font-medium">{alert.title}</p>
								<Badge variant={personnelAlertVariant(alert.severity)}>
									{dateLabel(alert.dueDate)}
								</Badge>
							</div>
							<p className="text-sm text-muted-foreground">{alert.detail}</p>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">처리할 생애주기 알림이 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function PersonnelSchedulePanel({
	initialSchedule
}: {
	initialSchedule: PersonnelScheduleResponse;
}) {
	const [schedule, setSchedule] = useState(initialSchedule);
	const [from, setFrom] = useState(initialSchedule.from);
	const [to, setTo] = useState(initialSchedule.to);
	const [selectedTypes, setSelectedTypes] = useState<PersonnelScheduleType[]>(
		normalizePersonnelScheduleTypes(initialSchedule.selectedTypes)
	);
	const [loading, setLoading] = useState(false);
	const [actioningContractKey, setActioningContractKey] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [feedback, setFeedback] = useState<string | null>(null);

	function toggleType(type: PersonnelScheduleType) {
		setSelectedTypes((current) => {
			if (current.includes(type)) {
				return current.length === 1 ? current : current.filter((item) => item !== type);
			}
			const next = new Set([...current, type]);
			return personnelScheduleTypeOptions
				.map((option) => option.value)
				.filter((optionType) => next.has(optionType));
		});
	}

	async function refreshSchedule() {
		setLoading(true);
		setFeedback(null);
		setNotice(null);
		try {
			const next = await api.get<PersonnelScheduleResponse>(
				personnelSchedulePath(from, to, selectedTypes)
			);
			setSchedule(next);
			setFrom(next.from);
			setTo(next.to);
			setSelectedTypes(normalizePersonnelScheduleTypes(next.selectedTypes));
		} catch (error) {
			setFeedback(getErrorMessage(error, '인사 일정을 불러오지 못했습니다.'));
		} finally {
			setLoading(false);
		}
	}

	async function updateContractExpiryAction(
		event: PersonnelScheduleEvent,
		status: Exclude<ContractExpiryActionStatus, 'PENDING'>
	) {
		if (!event.contractId) return;
		const option = contractExpiryActionOptions.find((item) => item.status === status);
		if (!option) return;

		const request: UpdateContractExpiryActionRequest = {
			expiryActionStatus: status,
			expiryActionNote: option.note
		};
		const targetKey = personnelScheduleEventKey(event);
		setActioningContractKey(targetKey);
		setFeedback(null);
		setNotice(null);
		try {
			const contract = await api.patch<EmployeeContract>(
				`/employees/${event.employeeId}/contracts/${event.contractId}/expiry-action`,
				request
			);
			setSchedule((current) => ({
				...current,
				events: current.events.map((item) =>
					personnelScheduleEventKey(item) === targetKey
						? personnelScheduleEventWithUpdatedContract(item, contract)
						: item
				)
			}));
			setNotice('계약 만료 조치 상태를 갱신했습니다.');
		} catch (error) {
			setFeedback(getErrorMessage(error, '계약 만료 조치 상태를 갱신하지 못했습니다.'));
		} finally {
			setActioningContractKey(null);
		}
	}

	const stats = [
		{
			label: '생일',
			count: schedule.birthdayCount,
			description: '축하와 케어 액션',
			Icon: Gift
		},
		{
			label: '입사기념일',
			count: schedule.joinAnniversaryCount,
			description: '근속 리텐션 점검',
			Icon: CalendarCheck
		},
		{
			label: '계약 만료',
			count: schedule.contractExpiryCount,
			description: '갱신 검토 대상',
			Icon: AlertTriangle
		}
	];

	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<CalendarCheck className="size-4 text-muted-foreground" />
						<p className="font-semibold">인사 일정</p>
					</div>
					<p className="mt-2 text-sm text-muted-foreground">
						생일, 입사기념일, 계약 만료 예정자를 기간과 유형별로 확인합니다.
					</p>
				</div>
				<Badge variant={schedule.totalEventCount > 0 ? 'warning' : 'success'}>
					{numberLabel(schedule.totalEventCount)}건
				</Badge>
			</div>

			<div className="mt-4 grid gap-3 md:grid-cols-3">
				{stats.map(({ label, count, description, Icon }) => (
					<div key={label} className="rounded-md border border-border bg-muted/20 p-3">
						<div className="flex items-center justify-between gap-2">
							<p className="text-sm font-semibold">{label}</p>
							<Icon className="size-4 text-muted-foreground" />
						</div>
						<p className="mt-2 text-lg font-semibold tracking-normal">{numberLabel(count)}건</p>
						<p className="mt-1 text-xs text-muted-foreground">{description}</p>
					</div>
				))}
			</div>

			<div className="mt-4 grid gap-3 xl:grid-cols-[1fr_1fr_2fr_auto] xl:items-end">
				<div className="space-y-1">
					<Label htmlFor="personnel-schedule-from">일정 시작일</Label>
					<Input
						id="personnel-schedule-from"
						type="date"
						value={from}
						onChange={(event) => setFrom(event.target.value)}
					/>
				</div>
				<div className="space-y-1">
					<Label htmlFor="personnel-schedule-to">일정 종료일</Label>
					<Input
						id="personnel-schedule-to"
						type="date"
						value={to}
						onChange={(event) => setTo(event.target.value)}
					/>
				</div>
				<div className="grid gap-2 sm:grid-cols-3">
					{personnelScheduleTypeOptions.map((option) => (
						<label
							key={option.value}
							className="flex min-h-10 items-start gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm"
						>
							<input
								type="checkbox"
								checked={selectedTypes.includes(option.value)}
								onChange={() => toggleType(option.value)}
								aria-label={option.label}
							/>
							<span className="min-w-0">
								<span className="block font-medium">{option.label}</span>
								<span className="block text-xs text-muted-foreground">{option.description}</span>
							</span>
						</label>
					))}
				</div>
				<Button type="button" onClick={() => void refreshSchedule()} disabled={loading}>
					{loading ? <Spinner size="sm" /> : <RefreshCw className="size-4" />}
					일정 조회
				</Button>
			</div>

			{feedback ? (
				<div className="mt-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
					{feedback}
				</div>
			) : null}
			{notice ? (
				<div className="mt-3 rounded-md bg-success/10 px-3 py-2 text-sm text-success">{notice}</div>
			) : null}

			<div className="mt-4 space-y-3">
				{schedule.events.length > 0 ? (
					schedule.events.map((event) => (
						<PersonnelScheduleEventRow
							key={personnelScheduleEventKey(event)}
							event={event}
							actioningContractKey={actioningContractKey}
							onExpiryAction={updateContractExpiryAction}
						/>
					))
				) : (
					<p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
						선택한 기간에 처리할 인사 일정이 없습니다.
					</p>
				)}
			</div>
		</div>
	);
}

function personnelScheduleEventKey(event: PersonnelScheduleEvent): string {
	return `${event.type}-${event.employeeId}-${event.eventDate}-${event.contractId ?? 'none'}`;
}

function personnelScheduleEventWithUpdatedContract(
	event: PersonnelScheduleEvent,
	contract: EmployeeContract
): PersonnelScheduleEvent {
	const expiryActionStatus = contract.expiryActionStatus ?? event.expiryActionStatus ?? null;
	const expiryActionLabel =
		contract.expiryActionLabel ?? event.expiryActionLabel ?? event.actionLabel;
	return {
		...event,
		actionLabel: expiryActionLabel,
		severity: expiryActionStatus === 'PENDING' ? event.severity : 'INFO',
		expiryActionStatus,
		expiryActionLabel,
		expiryActionNote: contract.expiryActionNote ?? null,
		expiryActionedAt: contract.expiryActionedAt ?? null
	};
}

function PersonnelScheduleEventRow({
	event,
	actioningContractKey,
	onExpiryAction
}: {
	event: PersonnelScheduleEvent;
	actioningContractKey: string | null;
	onExpiryAction: (
		event: PersonnelScheduleEvent,
		status: Exclude<ContractExpiryActionStatus, 'PENDING'>
	) => void | Promise<void>;
}) {
	const isContractExpiry = event.type === 'CONTRACT_EXPIRY' && event.contractId != null;
	const actioning = actioningContractKey === personnelScheduleEventKey(event);
	const actionLabel = event.expiryActionLabel ?? event.actionLabel;
	return (
		<div className="flex flex-col gap-3 rounded-md border border-border bg-muted/20 p-3 lg:flex-row lg:items-start lg:justify-between">
			<div className="min-w-0">
				<div className="flex flex-wrap items-center gap-2">
					<p className="font-medium">{event.title}</p>
					<Badge variant="outline">{personnelScheduleTypeLabel(event.type)}</Badge>
				</div>
				<p className="mt-1 text-sm text-muted-foreground">{event.detail}</p>
				<p className="mt-1 text-xs text-muted-foreground">
					{event.department ?? '부서 미지정'} · {event.position ?? '직책 미지정'}
				</p>
				{isContractExpiry && event.expiryActionNote ? (
					<p className="mt-2 text-xs text-muted-foreground">{event.expiryActionNote}</p>
				) : null}
			</div>
			<div className="flex shrink-0 flex-col gap-2 lg:items-end">
				<div className="flex flex-wrap gap-2 lg:justify-end">
					<Badge variant={personnelScheduleSeverityVariant(event.severity)}>
						{dateLabel(event.eventDate)}
					</Badge>
					<Badge variant="outline">
						D{event.daysUntil >= 0 ? `-${event.daysUntil}` : `+${Math.abs(event.daysUntil)}`}
					</Badge>
					<Badge variant={contractExpiryActionVariant(event.expiryActionStatus, event.severity)}>
						{actionLabel}
					</Badge>
				</div>
				{isContractExpiry ? (
					<div className="flex flex-wrap gap-2 lg:justify-end">
						{contractExpiryActionOptions.map(({ status, label, Icon }) => (
							<Button
								key={status}
								type="button"
								variant="outline"
								size="sm"
								disabled={actioning}
								aria-label={`${event.title} ${label}으로 표시`}
								onClick={() => void onExpiryAction(event, status)}
							>
								{actioning ? <Spinner size="sm" /> : <Icon className="size-4" />}
								{label}
							</Button>
						))}
					</div>
				) : null}
			</div>
		</div>
	);
}

function PersonnelDepartmentInsights({ insights }: { insights: PersonnelDepartmentInsight[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<Building2 className="size-4 text-muted-foreground" />
				<p className="font-semibold">조직 현황</p>
			</div>
			<div className="mt-4 space-y-3">
				{insights.length > 0 ? (
					insights.map((insight) => (
						<div key={insight.department} className="flex items-start justify-between gap-3">
							<div>
								<p className="font-medium">{insight.department}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									재직 {numberLabel(insight.activeEmployees)}명 · 프로필{' '}
									{numberLabel(insight.profileCompleteRatePercent)}%
								</p>
								{departmentInviteDeliveryDetail(insight) ? (
									<p className="mt-1 text-xs text-muted-foreground">
										{departmentInviteDeliveryDetail(insight)}
									</p>
								) : null}
							</div>
							<div className="flex shrink-0 flex-col items-end gap-2">
								<Badge variant={insight.profileCompleteRatePercent >= 80 ? 'success' : 'warning'}>
									{insight.riskLabel}
								</Badge>
								{(insight.inviteManualDeliveryRequiredEmployees ?? 0) > 0 ? (
									<Badge variant="warning">
										수동 전달 {numberLabel(insight.inviteManualDeliveryRequiredEmployees)}명
									</Badge>
								) : null}
							</div>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">조직별 구성원 지표가 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function departmentInviteDeliveryDetail(insight: PersonnelDepartmentInsight): string {
	const failed = insight.inviteEmailDeliveryFailedEmployees ?? 0;
	const disabled = insight.inviteEmailDeliveryDisabledEmployees ?? 0;
	if (failed === 0 && disabled === 0) return '';
	return `초대 메일 실패 ${numberLabel(failed)}명 · 메일 비활성 ${numberLabel(disabled)}명`;
}

function PersonnelOperationsPanel({
	operations,
	personnelSchedule
}: {
	operations: PersonnelManagementSummaryResponse;
	personnelSchedule: PersonnelScheduleResponse | null;
}) {
	const certificateTotal = operations.pendingCertificateCount + operations.issuedCertificateCount;
	return (
		<Card>
			<CardHeader>
				<CardTitle>구성원 운영</CardTitle>
				<CardDescription>
					구성원 검색, 조직 현황, 문서·증명서, 셀프서비스 승인과 온·오프보딩 알림을 함께 점검합니다.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">프로필 완성</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.profileCompleteCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							보완 필요 {numberLabel(operations.profileIncompleteCount)}명
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">조직</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.departmentCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							재직 {numberLabel(operations.activeEmployeeCount)}명
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">문서·증명서</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.personnelDocumentCount + certificateTotal)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							발급 대기 {numberLabel(operations.pendingCertificateCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">셀프서비스</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.selfServiceRequestCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							활성 양식 {numberLabel(operations.workflowTemplateCount)}개
						</p>
					</div>
				</div>

				<PersonnelRetireeAccessPanel operations={operations} />
				{personnelSchedule ? <PersonnelSchedulePanel initialSchedule={personnelSchedule} /> : null}
				<PersonnelSearchReadinessPanel operations={operations} />
				<PersonnelProfileKnowledgePanel operations={operations} />

				<div className="grid gap-3 lg:grid-cols-2">
					{operations.checklist.map((item) => (
						<PersonnelChecklistCard key={item.code} item={item} />
					))}
				</div>

				<div className="grid gap-4 xl:grid-cols-3">
					<PersonnelSearchSegments segments={operations.searchSegments} />
					<PersonnelLifecycleAlerts alerts={operations.lifecycleAlerts} />
					<PersonnelDepartmentInsights insights={operations.departmentInsights} />
				</div>
			</CardContent>
		</Card>
	);
}

function EmployeeJobCatalogPanel({
	items,
	onDone
}: {
	items: EmployeeJobCatalogResponse[];
	onDone: () => Promise<void>;
}) {
	const [category, setCategory] = useState<EmployeeJobCatalogCategory>('JOB');
	const [pasteText, setPasteText] = useState('');
	const [selectedIds, setSelectedIds] = useState<number[]>([]);
	const [importResult, setImportResult] = useState<EmployeeJobCatalogBulkImportResponse | null>(
		null
	);
	const [deleteResult, setDeleteResult] = useState<EmployeeJobCatalogBulkDeleteResponse | null>(
		null
	);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState<'import' | 'delete' | null>(null);

	const availableItemIds = useMemo(() => new Set(items.map((item) => item.id)), [items]);
	const selectedItemIds = useMemo(
		() => selectedIds.filter((id) => availableItemIds.has(id)),
		[selectedIds, availableItemIds]
	);
	const groupedItems = useMemo(() => {
		const grouped = employeeJobCatalogCategoryOrder.reduce(
			(accumulator, currentCategory) => {
				accumulator[currentCategory] = [];
				return accumulator;
			},
			{} as Record<EmployeeJobCatalogCategory, EmployeeJobCatalogResponse[]>
		);
		const sortedItems = [...items].sort(
			(left, right) =>
				left.sortOrder - right.sortOrder ||
				left.name.localeCompare(right.name, 'ko-KR') ||
				left.id - right.id
		);
		for (const item of sortedItems) {
			grouped[item.category]?.push(item);
		}
		return grouped;
	}, [items]);

	function toggleSelection(itemId: number) {
		setSelectedIds((currentIds) =>
			currentIds.includes(itemId)
				? currentIds.filter((id) => id !== itemId)
				: [...currentIds, itemId]
		);
	}

	async function handleImport(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const normalizedPasteText = pasteText.trim();
		if (!normalizedPasteText) {
			setError('등록할 직5 항목을 입력해주세요.');
			return;
		}

		const request: EmployeeJobCatalogBulkImportRequest = {
			category,
			pasteText
		};

		setSubmitting('import');
		setError(null);
		try {
			const result = await api.post<EmployeeJobCatalogBulkImportResponse>(
				'/employees/job-catalog/bulk-import',
				request
			);
			setImportResult(result);
			setDeleteResult(null);
			setPasteText('');
			await onDone();
		} catch (importError) {
			setError(getErrorMessage(importError, '직5 항목을 등록하지 못했습니다.'));
		} finally {
			setSubmitting(null);
		}
	}

	async function handleDeleteSelected() {
		if (selectedItemIds.length === 0) {
			setError('삭제할 직5 항목을 선택해주세요.');
			return;
		}

		const request: EmployeeJobCatalogBulkDeleteRequest = { ids: selectedItemIds };
		setSubmitting('delete');
		setError(null);
		try {
			const result = await api.delete<EmployeeJobCatalogBulkDeleteResponse>(
				'/employees/job-catalog/bulk-delete',
				request
			);
			setDeleteResult(result);
			setImportResult(null);
			setSelectedIds([]);
			await onDone();
		} catch (deleteError) {
			setError(getErrorMessage(deleteError, '선택한 직5 항목을 삭제하지 못했습니다.'));
		} finally {
			setSubmitting(null);
		}
	}

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>직5 마스터</CardTitle>
					<CardDescription>
						직무, 직군, 직책, 직위, 직급 기준정보를 붙여넣기 등록과 선택 삭제로 정리합니다.
					</CardDescription>
				</div>
				<Button
					type="button"
					variant="outline"
					onClick={() => void handleDeleteSelected()}
					disabled={selectedItemIds.length === 0 || submitting === 'delete'}
				>
					<Trash2 className="size-4" />
					{submitting === 'delete'
						? '삭제 중...'
						: `선택 삭제 ${numberLabel(selectedItemIds.length)}개`}
				</Button>
			</CardHeader>
			<CardContent className="space-y-5">
				<form
					className="grid gap-3 lg:grid-cols-[180px_1fr_auto] lg:items-start"
					onSubmit={handleImport}
				>
					<select
						value={category}
						onChange={(event) => setCategory(event.target.value as EmployeeJobCatalogCategory)}
						className={dialogControlClassName}
						aria-label="직5 분류"
					>
						{employeeJobCatalogCategoryOrder.map((itemCategory) => (
							<option key={itemCategory} value={itemCategory}>
								{employeeJobCatalogCategoryLabels[itemCategory]}
							</option>
						))}
					</select>
					<textarea
						value={pasteText}
						onChange={(event) => setPasteText(event.target.value)}
						className={`${dialogTextAreaClassName} min-h-28`}
						placeholder="엑셀 셀을 복사해 붙여넣으세요. 줄바꿈과 탭을 모두 항목으로 처리합니다."
						aria-label="직5 붙여넣기"
					/>
					<Button type="submit" disabled={submitting === 'import'} className="lg:min-w-28">
						<Plus className="size-4" />
						{submitting === 'import' ? '등록 중...' : '일괄 등록'}
					</Button>
				</form>

				{error ? (
					<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
						{error}
					</div>
				) : null}
				{importResult ? (
					<div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4 text-sm">
						<p className="font-semibold">
							{numberLabel(importResult.createdCount)}개 등록 · 중복{' '}
							{numberLabel(importResult.duplicateCount)}개 · 오류{' '}
							{numberLabel(importResult.invalidCount)}개
						</p>
						{importResult.duplicateValues.length > 0 ? (
							<p className="text-muted-foreground">
								중복: {importResult.duplicateValues.join(', ')}
							</p>
						) : null}
						{importResult.invalidRows.length > 0 ? (
							<p className="text-muted-foreground">
								오류:{' '}
								{importResult.invalidRows
									.map((row) => `${row.rowNumber}행 ${row.reason}`)
									.join(', ')}
							</p>
						) : null}
					</div>
				) : null}
				{deleteResult ? (
					<div className="rounded-lg border border-border bg-muted/20 p-4 text-sm font-semibold">
						{numberLabel(deleteResult.deletedCount)}개 삭제 · 건너뜀{' '}
						{numberLabel(deleteResult.skippedIds.length)}개
					</div>
				) : null}

				<div className="grid gap-3 xl:grid-cols-5">
					{employeeJobCatalogCategoryOrder.map((itemCategory) => {
						const categoryItems = groupedItems[itemCategory];
						return (
							<div key={itemCategory} className="rounded-lg border border-border bg-background p-4">
								<div className="flex items-center justify-between gap-3">
									<p className="font-semibold">{employeeJobCatalogCategoryLabels[itemCategory]}</p>
									<Badge variant={categoryItems.length > 0 ? 'success' : 'outline'}>
										{numberLabel(categoryItems.length)}개
									</Badge>
								</div>
								<div className="mt-4 space-y-2">
									{categoryItems.length > 0 ? (
										categoryItems.map((item) => (
											<label
												key={item.id}
												className="flex min-h-9 items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm"
											>
												<input
													type="checkbox"
													checked={selectedIds.includes(item.id)}
													onChange={() => toggleSelection(item.id)}
													aria-label={`${item.name} 선택`}
												/>
												<span className="min-w-0 break-words">{item.name}</span>
											</label>
										))
									) : (
										<p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
											등록된 항목 없음
										</p>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</CardContent>
		</Card>
	);
}

function EmployeeMobileCard({
	employee,
	onInviteFeedback
}: {
	employee: EmployeeResponse;
	onInviteFeedback: (feedback: InviteFeedback) => void | Promise<void>;
}) {
	return (
		<div className="min-w-0 rounded-lg border border-border bg-card p-4 shadow-sm md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="truncate font-semibold">{employee.name}</p>
					<p className="mt-1 truncate text-sm text-muted-foreground">
						{employee.department ?? '부서 미지정'} · {employee.position ?? '직책 미지정'}
					</p>
				</div>
				<Badge variant={employeeStatusVariant(employee.status)}>
					{employeeStatusLabel(employee.status)}
				</Badge>
			</div>
			<div className="mt-3 grid min-w-0 gap-3 text-sm">
				<div className="flex min-w-0 flex-wrap gap-2">
					<Badge variant={inviteStatusVariant(employee.inviteStatus)}>
						{inviteStatusLabel(employee.inviteStatus)}
					</Badge>
					<Badge variant={employee.attendanceEnabled ? 'success' : 'outline'}>
						{employee.attendanceEnabled ? '출퇴근 사용' : '출퇴근 미사용'}
					</Badge>
					<Badge variant={employee.profileComplete ? 'success' : 'warning'}>
						{employeeProfileLabel(employee)}
					</Badge>
				</div>
				<div className="flex min-w-0 items-center gap-2 text-muted-foreground">
					<Mail className="size-4 shrink-0" />
					<span className="block min-w-0 flex-1 truncate">{employee.email ?? '이메일 미등록'}</span>
				</div>
			</div>
			{canResendInvite(employee) ? (
				<div className="mt-4">
					<ResendInviteButton employee={employee} onDone={onInviteFeedback} />
				</div>
			) : null}
			<Link
				href={`/employees/${employee.id}`}
				aria-label={`${employee.name} 상세 보기`}
				className="mt-4 inline-flex h-9 items-center justify-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-semibold transition-colors hover:bg-muted"
			>
				상세
				<ChevronRight className="size-4" />
			</Link>
		</div>
	);
}

function EmployeeTable({
	employees,
	onInviteFeedback
}: {
	employees: EmployeeResponse[];
	onInviteFeedback: (feedback: InviteFeedback) => void | Promise<void>;
}) {
	if (employees.length === 0) return <EmptyState message="조건에 맞는 직원이 없습니다." />;

	return (
		<>
			<div className="space-y-3 md:hidden">
				{employees.map((employee) => (
					<EmployeeMobileCard
						key={employee.id}
						employee={employee}
						onInviteFeedback={onInviteFeedback}
					/>
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[760px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">직원</th>
							<th className="px-4 py-3 font-semibold">소속</th>
							<th className="px-4 py-3 font-semibold">입사일</th>
							<th className="px-4 py-3 font-semibold">상태</th>
							<th className="px-4 py-3 font-semibold">초대</th>
							<th className="px-4 py-3 font-semibold">프로필</th>
							<th className="px-4 py-3 text-right font-semibold">상세</th>
						</tr>
					</thead>
					<tbody>
						{employees.map((employee) => (
							<tr key={employee.id} className="border-t border-border">
								<td className="px-4 py-4">
									<div className="min-w-0">
										<p className="font-semibold">{employee.name}</p>
										<p className="mt-1 max-w-[15rem] truncate text-xs text-muted-foreground">
											{employee.email ?? '이메일 미등록'}
										</p>
									</div>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									<div>{employee.department ?? '-'}</div>
									<div className="mt-1 text-xs">{employee.position ?? '-'}</div>
								</td>
								<td className="px-4 py-4 text-muted-foreground">{dateLabel(employee.joinDate)}</td>
								<td className="px-4 py-4">
									<Badge variant={employeeStatusVariant(employee.status)}>
										{employeeStatusLabel(employee.status)}
									</Badge>
								</td>
								<td className="px-4 py-4">
									<div className="flex flex-col items-start gap-2">
										<Badge variant={inviteStatusVariant(employee.inviteStatus)}>
											{inviteStatusLabel(employee.inviteStatus)}
										</Badge>
										<ResendInviteButton employee={employee} onDone={onInviteFeedback} />
									</div>
								</td>
								<td className="px-4 py-4">
									<Badge variant={employee.profileComplete ? 'success' : 'warning'}>
										{employeeProfileLabel(employee)}
									</Badge>
								</td>
								<td className="px-4 py-4 text-right">
									<Link
										href={`/employees/${employee.id}`}
										aria-label={`${employee.name} 상세 보기`}
										className="inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
									>
										보기
										<ChevronRight className="size-4" />
									</Link>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

function dayHourLabel(days: number | null | undefined, hours?: number | null): string {
	const safeDays = typeof days === 'number' && Number.isFinite(days) ? days : 0;
	const safeHours = typeof hours === 'number' && Number.isFinite(hours) ? hours : 0;
	if (safeHours > 0 && safeDays < 1) return `${safeHours}시간`;
	if (safeHours > 0) return `${safeDays}일 ${safeHours}시간`;
	return `${safeDays}일`;
}

function leaveTypeLabel(type: string | null | undefined): string {
	const labels: Record<string, string> = {
		ANNUAL: '연차',
		HALF_AM: '오전 반차',
		HALF_PM: '오후 반차',
		HOURLY: '시간 휴가',
		SICK: '병가',
		SPECIAL: '특별 휴가'
	};
	return type ? (labels[type] ?? type) : '-';
}

function LeaveStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: string;
	description: string;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<p className="text-sm font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal sm:mt-3 sm:text-3xl">{value}</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function LeaveRequestMobileCard({ request }: { request: LeaveRequestResponse }) {
	return (
		<div className="rounded-lg border border-border bg-card p-4 shadow-sm md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="truncate font-semibold">
						{request.employeeName ?? `직원 #${request.employeeId}`}
					</p>
					<p className="mt-1 text-sm text-muted-foreground">
						{leaveTypeLabel(request.type)} · {dayHourLabel(request.days, request.durationHours)}
					</p>
				</div>
				<StatusBadge status={request.status} />
			</div>
			<div className="mt-3 text-sm text-muted-foreground">
				<p className="truncate">
					{dateLabel(request.startDate)} - {dateLabel(request.endDate)}
					{request.startTime ? ` · ${timeLabel(request.startTime)}` : ''}
				</p>
				{request.reason ? <p className="mt-1 truncate text-foreground">{request.reason}</p> : null}
			</div>
		</div>
	);
}

function LeaveRequestTable({ requests }: { requests: LeaveRequestResponse[] }) {
	if (requests.length === 0) return <EmptyState message="휴가 신청 내역이 없습니다." />;

	return (
		<>
			<div className="space-y-3 md:hidden">
				{requests.map((request) => (
					<LeaveRequestMobileCard key={request.id} request={request} />
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[760px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">직원</th>
							<th className="px-4 py-3 font-semibold">유형</th>
							<th className="px-4 py-3 font-semibold">기간</th>
							<th className="px-4 py-3 font-semibold">일수</th>
							<th className="px-4 py-3 font-semibold">상태</th>
							<th className="px-4 py-3 font-semibold">결재자</th>
							<th className="px-4 py-3 font-semibold">사유</th>
						</tr>
					</thead>
					<tbody>
						{requests.map((request) => (
							<tr key={request.id} className="border-t border-border">
								<td className="px-4 py-4 font-semibold">
									{request.employeeName ?? `직원 #${request.employeeId}`}
								</td>
								<td className="px-4 py-4">{leaveTypeLabel(request.type)}</td>
								<td className="px-4 py-4 text-muted-foreground">
									{dateLabel(request.startDate)} - {dateLabel(request.endDate)}
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{dayHourLabel(request.days, request.durationHours)}
								</td>
								<td className="px-4 py-4">
									<StatusBadge status={request.status} />
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{request.currentApproverName ?? '-'}
								</td>
								<td className="max-w-[14rem] truncate px-4 py-4 text-muted-foreground">
									{request.reason ?? '-'}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

function currentMonthRange(): { startDate: string; endDate: string } {
	const today = new Date();
	const start = new Date(today.getFullYear(), today.getMonth(), 1);
	const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
	const dateValue = (date: Date) =>
		`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
	return {
		startDate: dateValue(start),
		endDate: dateValue(end)
	};
}

function teamLeavePeriod(entry: TeamLeaveCalendarEntryResponse): string {
	return entry.startDate === entry.endDate
		? dateLabel(entry.startDate)
		: `${dateLabel(entry.startDate)} - ${dateLabel(entry.endDate)}`;
}

function teamLeaveTimeDetail(entry: TeamLeaveCalendarEntryResponse): string | null {
	if (!entry.startTime || !entry.durationHours) return null;
	return `${timeLabel(entry.startTime)} · ${entry.durationHours}시간`;
}

function TeamLeaveCalendarCard({
	entries,
	range
}: {
	entries: TeamLeaveCalendarEntryResponse[];
	range: { startDate: string; endDate: string };
}) {
	const sortedEntries = [...entries].sort((a, b) =>
		`${a.startDate}-${a.employeeName ?? ''}`.localeCompare(`${b.startDate}-${b.employeeName ?? ''}`)
	);

	return (
		<Card>
			<CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<CardTitle>팀 휴가 캘린더</CardTitle>
					<CardDescription>
						{dateLabel(range.startDate)} - {dateLabel(range.endDate)} 승인된 팀 휴가를 확인합니다.
					</CardDescription>
				</div>
				<Badge variant="secondary">{numberLabel(sortedEntries.length)}건</Badge>
			</CardHeader>
			<CardContent>
				{sortedEntries.length === 0 ? (
					<EmptyState message="이번 달 승인된 팀 휴가가 없습니다." />
				) : (
					<div className="grid gap-3 lg:grid-cols-2">
						{sortedEntries.map((entry) => (
							<div
								key={entry.requestId}
								className="rounded-lg border border-border bg-muted/20 p-4"
							>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<p className="truncate font-semibold">
											{entry.employeeName ?? `직원 #${entry.employeeId}`}
										</p>
										<p className="mt-1 truncate text-sm text-muted-foreground">
											{[entry.department, entry.position].filter(Boolean).join(' · ') || '-'}
										</p>
									</div>
									<Badge variant="outline">{leaveTypeLabel(entry.type)}</Badge>
								</div>
								<div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
									<div>
										<p>{teamLeavePeriod(entry)}</p>
										{teamLeaveTimeDetail(entry) ? <p>{teamLeaveTimeDetail(entry)}</p> : null}
									</div>
									<p className="sm:text-right">{dayHourLabel(entry.days, entry.durationHours)}</p>
								</div>
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

type LeavePageData = {
	balance: LeaveBalanceResponse | null;
	requests: PageResponse<LeaveRequestResponse>;
	approvalLines: ApprovalLineResponse[];
	calendarEntries: TeamLeaveCalendarEntryResponse[];
	calendarRange: { startDate: string; endDate: string };
};

function defaultLeaveRequestForm(approvalLines: ApprovalLineResponse[]): LeaveRequestFormInput {
	return {
		type: 'ANNUAL',
		startDate: '',
		endDate: '',
		startTime: '09:00',
		durationHours: 1,
		reason: '',
		approvalLineId: approvalLines[0]?.id ? String(approvalLines[0].id) : ''
	};
}

function LeaveRequestFormCard({
	approvalLines,
	error,
	submitting,
	onSubmit
}: {
	approvalLines: ApprovalLineResponse[];
	error: string | null;
	submitting: boolean;
	onSubmit: (request: CreateLeaveRequest) => Promise<boolean>;
}) {
	const [form, setForm] = useState<LeaveRequestFormInput>(() =>
		defaultLeaveRequestForm(approvalLines)
	);
	const [validationError, setValidationError] = useState<string | null>(null);
	const hourly = isHourlyLeaveType(form.type);
	const halfDay = isHalfDayLeaveType(form.type);

	function updateForm<T extends keyof LeaveRequestFormInput>(
		key: T,
		value: LeaveRequestFormInput[T]
	) {
		setForm((current) => ({ ...current, [key]: value }));
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateLeaveRequestForm(form);
		if (!result.ok) {
			setValidationError(result.message);
			return;
		}
		setValidationError(null);
		const submitted = await onSubmit(result.request);
		if (submitted) {
			setForm(defaultLeaveRequestForm(approvalLines));
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>휴가 신청</CardTitle>
				<CardDescription>결재선 기준으로 연차, 반차, 시간 휴가를 신청합니다.</CardDescription>
			</CardHeader>
			<CardContent>
				<form className="space-y-4" onSubmit={handleSubmit}>
					<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
						<div className="space-y-1.5">
							<Label htmlFor="leave-request-type">휴가 유형</Label>
							<select
								id="leave-request-type"
								value={form.type}
								onChange={(event) => updateForm('type', event.target.value)}
								className={dialogControlClassName}
							>
								<option value="ANNUAL">연차</option>
								<option value="HALF_AM">오전 반차</option>
								<option value="HALF_PM">오후 반차</option>
								<option value="HOURLY">시간 휴가</option>
								<option value="SICK">병가</option>
								<option value="SPECIAL">특별 휴가</option>
							</select>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="leave-request-start-date">시작일</Label>
							<Input
								id="leave-request-start-date"
								type="date"
								value={form.startDate}
								onChange={(event) => updateForm('startDate', event.target.value)}
							/>
						</div>
						{hourly ? (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="leave-request-start-time">시작 시각</Label>
									<Input
										id="leave-request-start-time"
										type="time"
										value={form.startTime}
										onChange={(event) => updateForm('startTime', event.target.value)}
									/>
								</div>
								<div className="space-y-1.5">
									<Label htmlFor="leave-request-duration">사용 시간</Label>
									<Input
										id="leave-request-duration"
										type="number"
										min={1}
										max={8}
										value={form.durationHours}
										onChange={(event) => updateForm('durationHours', event.target.value)}
									/>
								</div>
							</>
						) : null}
						{!hourly && !halfDay ? (
							<div className="space-y-1.5">
								<Label htmlFor="leave-request-end-date">종료일</Label>
								<Input
									id="leave-request-end-date"
									type="date"
									value={form.endDate}
									onChange={(event) => updateForm('endDate', event.target.value)}
								/>
							</div>
						) : null}
						<div className="space-y-1.5">
							<Label htmlFor="leave-request-line">결재선</Label>
							<select
								id="leave-request-line"
								value={form.approvalLineId}
								onChange={(event) => updateForm('approvalLineId', event.target.value)}
								className={dialogControlClassName}
								disabled={approvalLines.length === 0}
							>
								<option value="">선택</option>
								{approvalLines.map((line) => (
									<option key={line.id} value={line.id}>
										{line.name}
									</option>
								))}
							</select>
						</div>
						<div className="space-y-1.5 md:col-span-2 xl:col-span-4">
							<Label htmlFor="leave-request-reason">사유</Label>
							<textarea
								id="leave-request-reason"
								value={form.reason}
								onChange={(event) => updateForm('reason', event.target.value)}
								className={`${dialogTextAreaClassName} min-h-20`}
								maxLength={500}
							/>
						</div>
					</div>
					{approvalLines.length === 0 ? (
						<div
							className="rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning-foreground"
							role="alert"
						>
							사용 가능한 결재선이 없습니다.
						</div>
					) : null}
					{validationError ? (
						<div
							className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
							role="alert"
						>
							{validationError}
						</div>
					) : null}
					{error ? (
						<div
							className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
							role="alert"
						>
							{error}
						</div>
					) : null}
					<div className="flex justify-end">
						<Button type="submit" disabled={submitting || approvalLines.length === 0}>
							{submitting ? <Spinner size="sm" /> : <CalendarCheck className="size-4" />}
							휴가 신청
						</Button>
					</div>
				</form>
			</CardContent>
		</Card>
	);
}

export function LeavePage() {
	const [state, setState] = useState<RequestState<LeavePageData>>({ status: 'idle' });
	const [query, setQuery] = useState('');
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const calendarRange = currentMonthRange();
				const calendarPath = `/leave/calendar?startDate=${calendarRange.startDate}&endDate=${calendarRange.endDate}`;
				const [balance, requests, approvalLines, calendarEntries] = await Promise.all([
					api.get<LeaveBalanceResponse>('/leave/balance').catch(() => null),
					api.get<PageResponse<LeaveRequestResponse>>('/leave/requests/page?page=0&size=20'),
					api.get<ApprovalLineResponse[]>('/approval-lines').catch(() => []),
					api.get<TeamLeaveCalendarEntryResponse[]>(calendarPath).catch(() => [])
				]);
				setState({
					status: 'success',
					data: { balance, requests, approvalLines, calendarEntries, calendarRange }
				});
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const requests = useMemo(
		() => (state.status === 'success' ? state.data.requests.content : []),
		[state]
	);
	const filteredRequests = useMemo(() => {
		const keyword = query.trim().toLowerCase();
		if (!keyword) return requests;
		return requests.filter((request) =>
			[
				request.employeeName,
				request.type,
				request.status,
				request.reason,
				request.currentApproverName
			]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(keyword))
		);
	}, [query, requests]);

	const balance = state.status === 'success' ? state.data.balance : null;
	const pendingCount = requests.filter((request) => request.status === 'PENDING').length;

	async function submitLeaveRequest(request: CreateLeaveRequest): Promise<boolean> {
		setSubmitting(true);
		setSubmitError(null);
		try {
			await api.post<LeaveRequestResponse>('/leave/requests', request);
			await load();
			return true;
		} catch (error) {
			setSubmitError(getErrorMessage(error));
			return false;
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="휴가"
				description="휴가 잔여, 신청 가능 일수, 신청 내역을 확인합니다."
				actions={
					<Button variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{state.status === 'success' ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<LeaveStatCard
							title="신청 가능"
							value={dayHourLabel(
								balance?.requestableDisplayDays ?? balance?.requestableDays,
								balance?.requestableDisplayHours
							)}
							description={`${balance?.year ?? new Date().getFullYear()}년 기준`}
						/>
						<LeaveStatCard
							title="사용 가능"
							value={dayHourLabel(
								balance?.availableDisplayDays ?? balance?.availableDays,
								balance?.availableDisplayHours
							)}
							description="대기 신청 제외"
						/>
						<LeaveStatCard
							title="사용 완료"
							value={dayHourLabel(balance?.usedDays)}
							description="승인되어 차감된 휴가"
						/>
						<LeaveStatCard
							title="대기 신청"
							value={numberLabel(pendingCount)}
							description="검토 중인 휴가"
						/>
					</div>

					<LeaveRequestFormCard
						approvalLines={state.data.approvalLines}
						error={submitError}
						submitting={submitting}
						onSubmit={submitLeaveRequest}
					/>

					<TeamLeaveCalendarCard
						entries={state.data.calendarEntries}
						range={state.data.calendarRange}
					/>

					<Card>
						<CardHeader className="gap-4 lg:flex-row lg:items-center lg:justify-between">
							<div>
								<CardTitle>휴가 현황</CardTitle>
								<CardDescription>직원별 신청 기간, 유형, 결재 상태를 확인합니다.</CardDescription>
							</div>
							<div className="relative w-full lg:w-80">
								<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									value={query}
									onChange={(event) => setQuery(event.target.value)}
									placeholder="직원, 유형, 상태 검색"
									className="pl-9"
									aria-label="휴가 검색"
								/>
							</div>
						</CardHeader>
						<CardContent>
							<LeaveRequestTable requests={filteredRequests} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}

function approvalStatusLabel(status: ApprovalResponse['status'] | null | undefined): string {
	const labels: Record<ApprovalResponse['status'], string> = {
		IN_PROGRESS: '진행 중',
		APPROVED: '승인 완료',
		REJECTED: '반려',
		CANCELLED: '취소'
	};
	return status ? labels[status] : '-';
}

function approvalStepActionLabel(label: string | null | undefined): string {
	const normalized = label?.trim();
	return normalized || '승인';
}

function approvalStatusVariant(
	status: ApprovalResponse['status'] | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'APPROVED') return 'success';
	if (status === 'IN_PROGRESS') return 'warning';
	if (status === 'REJECTED') return 'destructive';
	return 'outline';
}

function approvalEntityLabel(entityType: string | null | undefined): string {
	const labels: Record<string, string> = {
		LEAVE_REQUEST: '휴가',
		ATTENDANCE_ADJUSTMENT: '근태 정정',
		OVERTIME_WORK: '초과근무',
		EXPENSE: '경비',
		CERTIFICATE: '증명서',
		PAYROLL: '급여'
	};
	return entityType ? (labels[entityType] ?? entityType) : '-';
}

function approvalTitle(approval: ApprovalResponse): string {
	return approval.documentSummary?.title || `${approvalEntityLabel(approval.entityType)} 결재`;
}

function ApprovalStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: number;
	description: string;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<p className="text-sm font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal sm:mt-3 sm:text-3xl">
					{numberLabel(value)}
				</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function ApprovalTable({
	approvals,
	canAct,
	onApprove,
	onReject
}: {
	approvals: ApprovalResponse[];
	canAct?: boolean;
	onApprove?: (id: number) => void;
	onReject?: (id: number) => void;
}) {
	if (approvals.length === 0) return <EmptyState message="결재 문서가 없습니다." />;

	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[840px] text-left text-sm">
				<thead className="bg-muted/40 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-semibold">문서</th>
						<th className="px-4 py-3 font-semibold">유형</th>
						<th className="px-4 py-3 font-semibold">요청자</th>
						<th className="px-4 py-3 font-semibold">현재 결재자</th>
						<th className="px-4 py-3 font-semibold">상태</th>
						<th className="px-4 py-3 font-semibold">요청일</th>
						<th className="px-4 py-3 text-right font-semibold">{canAct ? '처리' : '상세'}</th>
					</tr>
				</thead>
				<tbody>
					{approvals.map((approval) => {
						const title = approvalTitle(approval);
						return (
							<tr key={approval.id} className="border-t border-border">
								<td className="px-4 py-4">
									<div className="min-w-0">
										<p className="font-semibold">{title}</p>
										{approval.documentSummary?.subtitle ? (
											<p className="mt-1 max-w-[16rem] truncate text-xs text-muted-foreground">
												{approval.documentSummary.subtitle}
											</p>
										) : null}
									</div>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{approvalEntityLabel(approval.entityType)}
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{approval.requesterName ?? `직원 #${approval.requesterId}`}
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{approval.currentApproverName ?? '-'}
								</td>
								<td className="px-4 py-4">
									<Badge variant={approvalStatusVariant(approval.status)}>
										{approvalStatusLabel(approval.status)}
									</Badge>
								</td>
								<td className="px-4 py-4 text-muted-foreground">{dateLabel(approval.createdAt)}</td>
								<td className="px-4 py-4 text-right">
									<div className="flex items-center justify-end gap-2">
										{canAct ? (
											<>
												<Button variant="outline" size="sm" onClick={() => onReject?.(approval.id)}>
													반려
												</Button>
												<Button size="sm" onClick={() => onApprove?.(approval.id)}>
													승인
												</Button>
											</>
										) : null}
										<Link
											href={`/approvals/${approval.id}`}
											aria-label={`${title} 상세 보기`}
											className="inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
										>
											보기
											<ChevronRight className="size-4" />
										</Link>
									</div>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

const approvalCategoryOptions = [
	{ value: 'all', label: '전체' },
	{ value: 'IN_PROGRESS', label: '진행 중' },
	{ value: 'APPROVED', label: '승인 완료' },
	{ value: 'REJECTED', label: '반려' }
];

function approvalChipTone(
	status: ApprovalResponse['status']
): 'amber' | 'green' | 'red' | 'neutral' {
	if (status === 'IN_PROGRESS') return 'amber';
	if (status === 'APPROVED') return 'green';
	if (status === 'REJECTED') return 'red';
	return 'neutral';
}

function approvalMetaLines(approval: ApprovalResponse): string[] {
	const lines: string[] = [];
	if (approval.requesterName) lines.push(`요청자: ${approval.requesterName}`);
	if (approval.currentApproverName) lines.push(`현재 결재자: ${approval.currentApproverName}`);
	const fields = approval.documentSummary?.fields.slice(0, 2) ?? [];
	for (const f of fields) lines.push(`${f.label}: ${f.value}`);
	if (lines.length === 0) lines.push(dateLabel(approval.createdAt));
	return lines;
}

type ApprovalTab = 'pending' | 'requested' | 'referenced' | 'company';

const approvalTabEndpoints: Record<ApprovalTab, string> = {
	pending: '/approvals/pending/me/page?page=0&size=20',
	requested: '/approvals/requested/me?page=0&size=20',
	referenced: '/approvals/referenced/me?page=0&size=20',
	company: '/approvals/company?page=0&size=20'
};

function approvalTabOptions(isAdmin: boolean): { value: ApprovalTab; label: string }[] {
	const options: { value: ApprovalTab; label: string }[] = [
		{ value: 'pending', label: '처리할 결재' },
		{ value: 'requested', label: '요청 내역' },
		{ value: 'referenced', label: '참조 문서' }
	];
	if (isAdmin) options.push({ value: 'company', label: '회사 전체' });
	return options;
}

export function ApprovalsPage() {
	const { isAdmin } = useAuth();
	const [tab, setTab] = useState<ApprovalTab>('pending');
	const [state, setState] = useState<RequestState<PageResponse<ApprovalResponse>>>({
		status: 'idle'
	});
	const [query, setQuery] = useState('');
	const [filterCategory, setFilterCategory] = useState('all');
	const [filterOpen, setFilterOpen] = useState(false);
	const [actionError, setActionError] = useState<string | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const data = await api.get<PageResponse<ApprovalResponse>>(approvalTabEndpoints[tab]);
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[tab]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const approvals = useMemo(() => (state.status === 'success' ? state.data.content : []), [state]);
	const filteredApprovals = useMemo(() => {
		const filtered =
			filterCategory === 'all' ? approvals : approvals.filter((a) => a.status === filterCategory);
		const keyword = query.trim().toLowerCase();
		if (!keyword) return filtered;
		return filtered.filter((approval) =>
			[
				approvalTitle(approval),
				approval.entityType,
				approvalStatusLabel(approval.status),
				approval.requesterName,
				approval.currentApproverName,
				approval.documentSummary?.subtitle
			]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(keyword))
		);
	}, [approvals, query, filterCategory]);

	const inProgressCount = approvals.filter((approval) => approval.status === 'IN_PROGRESS').length;
	const approvedCount = approvals.filter((approval) => approval.status === 'APPROVED').length;
	const rejectedCount = approvals.filter((approval) => approval.status === 'REJECTED').length;

	async function approve(id: number) {
		setActionError(null);
		try {
			await api.post(`/approvals/${id}/approve`, {});
			await load();
		} catch (err) {
			setActionError(getErrorMessage(err, '승인 처리 중 오류가 발생했습니다.'));
		}
	}

	async function reject(id: number) {
		setActionError(null);
		try {
			await api.post(`/approvals/${id}/reject`, {});
			await load();
		} catch (err) {
			setActionError(getErrorMessage(err, '반려 처리 중 오류가 발생했습니다.'));
		}
	}

	const canAct = tab === 'pending';

	return (
		<div className="space-y-5">
			<MobileHeader
				title="결재함"
				subtitle={`대기 ${inProgressCount}건`}
				trailing={
					<Button variant="outline" size="sm" onClick={() => setFilterOpen(true)}>
						필터
					</Button>
				}
			/>

			<FilterChips
				options={approvalTabOptions(isAdmin)}
				value={tab}
				onChange={(v) => setTab(v as ApprovalTab)}
			/>

			<div className="md:hidden flex items-center justify-between gap-2">
				<FilterChips
					options={approvalCategoryOptions}
					value={filterCategory}
					onChange={setFilterCategory}
				/>
				<Button variant="outline" size="sm" onClick={() => void load()}>
					<RefreshCw className="size-4" />
					새로고침
				</Button>
			</div>

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{actionError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{actionError}
				</div>
			) : null}

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'success' ? (
				<>
					<div className="grid grid-cols-3 gap-3">
						<MetricCard
							label="진행 중"
							value={`${numberLabel(inProgressCount)}건`}
							tone="warning"
						/>
						<MetricCard
							label="승인 완료"
							value={`${numberLabel(approvedCount)}건`}
							tone="primary"
						/>
						<MetricCard label="반려" value={`${numberLabel(rejectedCount)}건`} tone="danger" />
					</div>

					{/* Mobile card list */}
					<div className="space-y-3 md:hidden">
						{filteredApprovals.length === 0 ? (
							<EmptyState message="결재 문서가 없습니다." />
						) : canAct ? (
							filteredApprovals.map((a) => (
								<PriorityApprovalCard
									key={a.id}
									chip={<StatusChip tone="amber">진행 중</StatusChip>}
									title={approvalTitle(a)}
									requester={a.requesterName ?? `직원 #${a.requesterId}`}
									metaLines={approvalMetaLines(a)}
									onApprove={() => void approve(a.id)}
									onReject={() => void reject(a.id)}
								/>
							))
						) : (
							filteredApprovals.map((a) => (
								<ListRow
									key={a.id}
									title={approvalTitle(a)}
									subtitle={`${approvalEntityLabel(a.entityType)} · ${a.requesterName ?? `직원 #${a.requesterId}`}`}
									iconTone={
										a.status === 'IN_PROGRESS'
											? 'warning'
											: a.status === 'APPROVED'
												? 'green'
												: 'danger'
									}
									trailing={
										<StatusChip tone={approvalChipTone(a.status)}>
											{approvalStatusLabel(a.status)}
										</StatusChip>
									}
									href={`/approvals/${a.id}`}
								/>
							))
						)}
					</div>

					{/* Desktop table */}
					<div className="hidden md:block">
						<Card>
							<CardHeader className="gap-4 lg:flex-row lg:items-center lg:justify-between">
								<div>
									<CardTitle>결재 현황</CardTitle>
									<CardDescription>
										문서, 요청자, 현재 결재자와 진행 상태를 확인합니다.
									</CardDescription>
								</div>
								<div className="relative w-full lg:w-80">
									<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										value={query}
										onChange={(event) => setQuery(event.target.value)}
										placeholder="문서, 요청자, 결재자 검색"
										className="pl-9"
										aria-label="결재 검색"
									/>
								</div>
							</CardHeader>
							<CardContent>
								<ApprovalTable
									approvals={filteredApprovals}
									canAct={canAct}
									onApprove={(id) => void approve(id)}
									onReject={(id) => void reject(id)}
								/>
							</CardContent>
						</Card>
					</div>
				</>
			) : null}

			<BottomSheet open={filterOpen} onClose={() => setFilterOpen(false)} title="필터">
				<div className="space-y-4">
					<div className="space-y-2">
						<label className="text-xs font-medium text-muted-foreground">검색</label>
						<div className="relative">
							<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={query}
								onChange={(event) => setQuery(event.target.value)}
								placeholder="문서, 요청자, 결재자 검색"
								className="pl-9"
								aria-label="결재 검색 (필터)"
							/>
						</div>
					</div>
					<div className="space-y-2">
						<label className="text-xs font-medium text-muted-foreground">상태</label>
						<FilterChips
							options={approvalCategoryOptions}
							value={filterCategory}
							onChange={setFilterCategory}
							className="flex-wrap"
						/>
					</div>
					<Button className="w-full" onClick={() => setFilterOpen(false)}>
						적용
					</Button>
				</div>
			</BottomSheet>
		</div>
	);
}

type WorkflowsPageData = {
	summary: WorkflowSummaryResponse;
	operations: WorkflowOperationsSummaryResponse | null;
	templates: WorkflowTemplateResponse[];
	documents: WorkflowDocumentResponse[];
	searchPresets: WorkflowSearchPresetResponse[];
	approvalLines: ApprovalLineResponse[];
	employees: EmployeeResponse[];
};

type WorkflowTemplateFieldForm = {
	key: string;
	label: string;
	type: WorkflowTemplateFieldType;
	required: boolean;
	optionsText: string;
};

type WorkflowTemplateForm = {
	name: string;
	description: string;
	category: string;
	defaultApprovalLineId: string;
	fields: WorkflowTemplateFieldForm[];
};

type WorkflowDocumentForm = {
	templateId: string;
	title: string;
	approvalLineId: string;
	fieldValues: Record<string, string>;
};

type WorkflowDocumentExportFilters = {
	templateId: string;
	status: '' | WorkflowDocumentStatus;
	submittedFrom: string;
	submittedTo: string;
};

type WorkflowDocumentFilters = WorkflowDocumentExportFilters & {
	keyword: string;
};

const emptyWorkflowDocumentFilters: WorkflowDocumentFilters = {
	templateId: '',
	status: '',
	submittedFrom: '',
	submittedTo: '',
	keyword: ''
};

function defaultWorkflowField(): WorkflowTemplateFieldForm {
	return {
		key: '',
		label: '',
		type: 'TEXT',
		required: true,
		optionsText: ''
	};
}

function workflowDocumentStatusLabel(status: WorkflowDocumentStatus): string {
	const labels: Record<WorkflowDocumentStatus, string> = {
		IN_PROGRESS: '진행 중',
		APPROVED: '승인 완료',
		REJECTED: '반려',
		CANCELLED: '취소'
	};
	return labels[status];
}

function workflowDocumentStatusVariant(
	status: WorkflowDocumentStatus
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'APPROVED') return 'success';
	if (status === 'IN_PROGRESS') return 'warning';
	if (status === 'REJECTED') return 'destructive';
	return 'outline';
}

function workflowDocumentActivityLabel(type: WorkflowDocumentActivityType): string {
	const labels: Record<WorkflowDocumentActivityType, string> = {
		SUBMITTED: '제출',
		APPROVAL_REQUESTED: '결재 요청',
		APPROVAL_APPROVED: '승인',
		APPROVAL_REJECTED: '반려',
		APPROVAL_SKIPPED: '건너뜀',
		REFERENCE_ADDED: '공유',
		COMMENTED: '댓글'
	};
	return labels[type];
}

function workflowDocumentActivityVariant(
	type: WorkflowDocumentActivityType
): 'success' | 'warning' | 'outline' | 'destructive' | 'secondary' {
	if (type === 'APPROVAL_APPROVED') return 'success';
	if (type === 'APPROVAL_REJECTED') return 'destructive';
	if (type === 'APPROVAL_REQUESTED' || type === 'SUBMITTED') return 'warning';
	if (type === 'COMMENTED') return 'secondary';
	return 'outline';
}

function workflowCommentActivityFromResponse(
	comment: WorkflowDocumentCommentResponse
): WorkflowDocumentActivityResponse {
	const actorName = comment.authorEmployeeName ?? `직원 #${comment.authorEmployeeId}`;
	return {
		id: `comment:${comment.id}`,
		activityType: 'COMMENTED',
		title: '댓글',
		description: `${actorName}님이 댓글을 남겼습니다.`,
		actorEmployeeId: comment.authorEmployeeId,
		actorEmployeeName: comment.authorEmployeeName,
		targetEmployeeId: null,
		targetEmployeeName: null,
		stepOrder: null,
		actionLabel: null,
		comment: comment.content,
		occurredAt: comment.createdAt
	};
}

function workflowFieldTypeLabel(type: WorkflowTemplateFieldType): string {
	const labels: Record<WorkflowTemplateFieldType, string> = {
		TEXT: '텍스트',
		NUMBER: '숫자',
		AMOUNT: '금액',
		DATE: '날짜',
		SELECT: '선택',
		EMPLOYEE: '직원',
		REFERENCE: '참조'
	};
	return labels[type];
}

function workflowApprovalLineLabel(
	lines: ApprovalLineResponse[],
	lineId: number | null | undefined
): string {
	if (!lineId) return '결재선 미지정';
	return lines.find((line) => line.id === lineId)?.name ?? `결재선 #${lineId}`;
}

function workflowTemplateForDocument(
	templates: WorkflowTemplateResponse[],
	document: WorkflowDocumentResponse
) {
	return templates.find((template) => template.id === document.templateId) ?? null;
}

function workflowFieldForValue(
	template: WorkflowTemplateResponse | null,
	key: string
): WorkflowTemplateFieldResponse | null {
	return template?.fields.find((field) => field.key === key) ?? null;
}

function workflowFieldValueLabel(
	value: string | null | undefined,
	field: WorkflowTemplateFieldResponse | null
): string {
	if (value == null || value === '') return '-';
	if (field?.type === 'AMOUNT' || field?.type === 'NUMBER') {
		const numeric = Number(value);
		if (Number.isFinite(numeric)) return numberLabel(numeric);
	}
	return value;
}

function workflowInitialFieldValues(template: WorkflowTemplateResponse | null | undefined) {
	return Object.fromEntries((template?.fields ?? []).map((field) => [field.key, '']));
}

function workflowDocumentExportPath(filters: WorkflowDocumentExportFilters): string {
	const params = new URLSearchParams();
	if (filters.templateId) params.set('templateId', filters.templateId);
	if (filters.status) params.set('status', filters.status);
	if (filters.submittedFrom) params.set('submittedFrom', filters.submittedFrom);
	if (filters.submittedTo) params.set('submittedTo', filters.submittedTo);
	const query = params.toString();
	return query ? `/workflows/documents/export?${query}` : '/workflows/documents/export';
}

function workflowDocumentListPath(filters: WorkflowDocumentFilters): string {
	const params = new URLSearchParams();
	if (filters.templateId) params.set('templateId', filters.templateId);
	if (filters.status) params.set('status', filters.status);
	if (filters.submittedFrom) params.set('submittedFrom', filters.submittedFrom);
	if (filters.submittedTo) params.set('submittedTo', filters.submittedTo);
	if (filters.keyword.trim()) params.set('keyword', filters.keyword.trim());
	const query = params.toString();
	return query ? `/workflows/documents?${query}` : '/workflows/documents';
}

function cloneWorkflowDocumentFilters(filters: WorkflowDocumentFilters): WorkflowDocumentFilters {
	return { ...filters, keyword: filters.keyword.trim() };
}

function hasWorkflowDocumentFilter(filters: WorkflowDocumentFilters): boolean {
	return Boolean(
		filters.templateId ||
		filters.status ||
		filters.submittedFrom ||
		filters.submittedTo ||
		filters.keyword.trim()
	);
}

function workflowFiltersFromPreset(preset: WorkflowSearchPresetResponse): WorkflowDocumentFilters {
	return {
		templateId: preset.templateId == null ? '' : String(preset.templateId),
		status: preset.status ?? '',
		submittedFrom: preset.submittedFrom ?? '',
		submittedTo: preset.submittedTo ?? '',
		keyword: preset.keyword ?? ''
	};
}

function workflowSearchPresetRequest(
	name: string,
	filters: WorkflowDocumentFilters
): CreateWorkflowSearchPresetRequest {
	return {
		name: name.trim(),
		templateId: filters.templateId ? Number(filters.templateId) : null,
		status: filters.status || null,
		submittedFrom: filters.submittedFrom || null,
		submittedTo: filters.submittedTo || null,
		keyword: filters.keyword.trim() || null
	};
}

function workflowShareResultMessage(
	references: ApprovalReferenceResponse[],
	employees: EmployeeResponse[]
): string {
	if (references.length === 0) {
		return '이미 공유된 구성원입니다.';
	}
	if (references.length === 1) {
		const reference = references[0];
		const employeeName =
			reference.employeeName ??
			employees.find((employee) => employee.id === reference.employeeId)?.name ??
			`직원 #${reference.employeeId}`;
		return `${employeeName}에게 문서를 공유했습니다.`;
	}
	return `${numberLabel(references.length)}명에게 문서를 공유했습니다.`;
}

function WorkflowStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: number;
	description: string;
}) {
	return (
		<Card>
			<CardContent className="p-4">
				<p className="text-xs font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal">{numberLabel(value)}</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function workflowOperationsChecklistVariant(
	status: WorkflowOperationsChecklistStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function workflowOperationsChecklistStatusLabel(status: WorkflowOperationsChecklistStatus): string {
	if (status === 'READY') return '완료';
	if (status === 'WARNING') return '주의';
	if (status === 'WAITING') return '대기';
	return '-';
}

function workflowOperationsRiskVariant(
	severity: WorkflowOperationsRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function WorkflowOperationsChecklistCard({ item }: { item: WorkflowOperationsChecklistItem }) {
	const Icon =
		item.status === 'WARNING' ? AlertTriangle : item.status === 'READY' ? CheckCircle2 : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={workflowOperationsChecklistVariant(item.status)}>
					{workflowOperationsChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function WorkflowOperationsRisks({ risks }: { risks: WorkflowOperationsRiskItem[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">운영 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={workflowOperationsRiskVariant(risk.severity)}>
							{numberLabel(risk.count)}건
						</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function WorkflowOperationsCategories({
	insights
}: {
	insights: WorkflowOperationsCategoryInsight[];
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<Building2 className="size-4 text-muted-foreground" />
				<p className="font-semibold">카테고리</p>
			</div>
			<div className="mt-4 space-y-3">
				{insights.length > 0 ? (
					insights.map((insight) => (
						<div key={insight.category} className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="font-medium">{insight.category}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									문서 {numberLabel(insight.documentCount)}건 · 진행{' '}
									{numberLabel(insight.inProgressDocumentCount)}건 · 반려{' '}
									{numberLabel(insight.rejectedDocumentCount)}건
								</p>
							</div>
							<Badge variant="outline">양식 {numberLabel(insight.templateCount)}개</Badge>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">카테고리 데이터가 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function WorkflowOperationsPendingApprovers({
	approvers
}: {
	approvers: WorkflowOperationsPendingApproverInsight[];
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<UsersRound className="size-4 text-muted-foreground" />
				<p className="font-semibold">승인자 대기</p>
			</div>
			<div className="mt-4 space-y-3">
				{approvers.length > 0 ? (
					approvers.map((approver) => (
						<div
							key={`${approver.approverId ?? 'unknown'}-${approver.oldestRequestedAt ?? 'none'}`}
							className="flex items-start justify-between gap-3"
						>
							<div className="min-w-0">
								<p className="font-medium">{approver.approverName ?? '승인자 미지정'}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									가장 오래된 요청 {dateLabel(approver.oldestRequestedAt)}
								</p>
							</div>
							<Badge variant="warning">{numberLabel(approver.pendingCount)}건</Badge>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">현재 단계 승인 대기가 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function workflowOperationsSlaRiskLevel(
	operations: WorkflowOperationsSummaryResponse
): WorkflowOperationsRiskSeverity {
	if (operations.approvalSlaRiskLevel) return operations.approvalSlaRiskLevel;
	if (operations.staleInProgressDocumentCount > 0) return 'CRITICAL';
	if (operations.pendingApprovalCount > 0) return 'WARNING';
	return 'READY';
}

function WorkflowOperationsBottleneckItem({
	item
}: {
	item: WorkflowOperationsApprovalBottleneckItem;
}) {
	return (
		<div className="rounded-md bg-muted/25 px-3 py-2">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="truncate text-sm font-semibold">{item.approverName ?? '승인자 미지정'}</p>
					<p className="mt-1 text-xs text-muted-foreground">
						{numberLabel(item.pendingCount)}건 · 가장 오래된 요청{' '}
						{dateLabel(item.oldestRequestedAt)}
					</p>
					<p className="mt-1 text-xs font-medium text-foreground">{item.actionLabel}</p>
				</div>
				<Badge variant={workflowOperationsRiskVariant(item.riskLevel)}>
					{numberLabel(item.pendingDays)}일 대기
				</Badge>
			</div>
		</div>
	);
}

function WorkflowOperationsSlaPanel({
	operations
}: {
	operations: WorkflowOperationsSummaryResponse;
}) {
	const riskLevel = workflowOperationsSlaRiskLevel(operations);
	const bottlenecks = operations.approvalBottleneckQueue ?? [];
	const riskLabel = operations.approvalSlaRiskLabel ?? '승인 SLA 점검';
	const riskSummary =
		operations.approvalSlaRiskSummary ??
		`승인 대기 ${numberLabel(operations.pendingApprovalCount)}건과 장기 대기 ${numberLabel(
			operations.staleInProgressDocumentCount
		)}건을 추적합니다.`;
	const recommendedAction =
		operations.approvalSlaRecommendedAction ??
		'결재함의 오래된 요청과 승인자별 대기 건수를 확인하세요.';

	return (
		<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.8fr)]">
			<div className="rounded-lg border border-border bg-background p-4">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<div className="flex items-center gap-2">
							<Clock3 className="size-4 text-muted-foreground" />
							<p className="font-semibold">승인 SLA</p>
						</div>
						<p className="mt-2 text-sm text-muted-foreground">{riskSummary}</p>
					</div>
					<Badge variant={workflowOperationsRiskVariant(riskLevel)}>{riskLabel}</Badge>
				</div>
				<div className="mt-4 rounded-md bg-muted/25 px-3 py-2">
					<p className="text-xs text-muted-foreground">권장 액션</p>
					<p className="mt-1 text-sm font-medium">{recommendedAction}</p>
				</div>
			</div>

			<div className="rounded-lg border border-border bg-background p-4">
				<div className="flex items-center justify-between gap-3">
					<div className="flex items-center gap-2">
						<UsersRound className="size-4 text-muted-foreground" />
						<p className="font-semibold">승인 병목 큐</p>
					</div>
					<Badge variant={bottlenecks.length > 0 ? 'warning' : 'success'}>
						{numberLabel(bottlenecks.length)}명
					</Badge>
				</div>
				<div className="mt-4 space-y-2">
					{bottlenecks.length > 0 ? (
						bottlenecks.map((item) => (
							<WorkflowOperationsBottleneckItem
								key={`${item.approverId ?? 'unknown'}-${item.oldestRequestedAt ?? 'none'}`}
								item={item}
							/>
						))
					) : (
						<p className="text-sm text-muted-foreground">승인자별 병목 없이 처리 중입니다.</p>
					)}
				</div>
			</div>
		</div>
	);
}

function WorkflowOperationsFieldMix({ fields }: { fields: WorkflowOperationsFieldTypeMix[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<GitBranch className="size-4 text-muted-foreground" />
				<p className="font-semibold">필드 구성</p>
			</div>
			<div className="mt-4 flex flex-wrap gap-2">
				{fields.length > 0 ? (
					fields.map((field) => (
						<Badge key={field.type} variant="outline">
							{field.label} {numberLabel(field.count)}
						</Badge>
					))
				) : (
					<p className="text-sm text-muted-foreground">등록된 양식 필드가 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function WorkflowOperationsPanel({
	operations
}: {
	operations: WorkflowOperationsSummaryResponse;
}) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>워크플로우 운영</CardTitle>
				<CardDescription>
					작성, 요청, 승인, 양식 구성, 결재 대기와 데이터 내보내기 준비 상태를 점검합니다.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">양식</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.activeTemplateCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							카테고리 {numberLabel(operations.categoryCount)}개
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">커스텀 필드</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.customFieldCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							기본 결재선 {numberLabel(operations.templatesWithDefaultApprovalLineCount)}개
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">승인 라인</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.activeApprovalLineCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							단계 {numberLabel(operations.approvalLineStepCount)}개
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">대기 승인</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.pendingApprovalCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							장기 대기 {numberLabel(operations.staleInProgressDocumentCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">내보내기</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.exportReadyDocumentCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							완료 {numberLabel(operations.approvedDocumentCount)}건
						</p>
					</div>
				</div>

				<WorkflowOperationsSlaPanel operations={operations} />

				<div className="grid gap-3 lg:grid-cols-2">
					{operations.checklist.map((item) => (
						<WorkflowOperationsChecklistCard key={item.code} item={item} />
					))}
				</div>

				<div className="grid gap-4 xl:grid-cols-3">
					<WorkflowOperationsRisks risks={operations.riskItems} />
					<WorkflowOperationsCategories insights={operations.categoryInsights} />
					<WorkflowOperationsPendingApprovers approvers={operations.pendingApproverInsights} />
				</div>

				<div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
					<WorkflowOperationsFieldMix fields={operations.fieldTypeMix} />
					<div className="flex flex-wrap gap-2 rounded-lg border border-border bg-muted/20 p-4">
						<Badge variant="outline">
							<FileText className="mr-1 size-3" />
							전체 문서 {numberLabel(operations.totalDocumentCount)}
						</Badge>
						<Badge variant={operations.inProgressDocumentCount > 0 ? 'warning' : 'success'}>
							<ClipboardCheck className="mr-1 size-3" />
							진행 중 {numberLabel(operations.inProgressDocumentCount)}
						</Badge>
						<Badge variant={operations.rejectedDocumentCount > 0 ? 'warning' : 'success'}>
							<AlertTriangle className="mr-1 size-3" />
							반려 {numberLabel(operations.rejectedDocumentCount)}
						</Badge>
						<Badge variant="outline">
							<Download className="mr-1 size-3" />
							원천 데이터
						</Badge>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function WorkflowTemplateDialog({
	approvalLines,
	onClose,
	onSubmitted
}: {
	approvalLines: ApprovalLineResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [form, setForm] = useState<WorkflowTemplateForm>({
		name: '',
		description: '',
		category: '',
		defaultApprovalLineId: approvalLines[0]?.id ? String(approvalLines[0].id) : '',
		fields: [
			{ key: 'amount', label: '금액', type: 'AMOUNT', required: true, optionsText: '' },
			{ key: 'reason', label: '사유', type: 'TEXT', required: true, optionsText: '' }
		]
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	function updateField(index: number, next: Partial<WorkflowTemplateFieldForm>) {
		setForm((current) => ({
			...current,
			fields: current.fields.map((field, fieldIndex) =>
				fieldIndex === index ? { ...field, ...next } : field
			)
		}));
	}

	function addField() {
		setForm((current) => ({ ...current, fields: [...current.fields, defaultWorkflowField()] }));
	}

	function removeField(index: number) {
		setForm((current) => ({
			...current,
			fields: current.fields.filter((_, fieldIndex) => fieldIndex !== index)
		}));
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const name = form.name.trim();
		if (!name) {
			setError('양식명을 입력해주세요.');
			return;
		}
		const invalidField = form.fields.find(
			(field) => !field.key.trim() || !field.label.trim() || !field.type
		);
		if (form.fields.length === 0 || invalidField) {
			setError('필드 키와 라벨을 모두 입력해주세요.');
			return;
		}

		const request: CreateWorkflowTemplateRequest = {
			name,
			description: form.description.trim() || undefined,
			category: form.category.trim() || undefined,
			defaultApprovalLineId: form.defaultApprovalLineId
				? Number(form.defaultApprovalLineId)
				: undefined,
			fields: form.fields.map((field) => ({
				key: field.key.trim(),
				label: field.label.trim(),
				type: field.type,
				required: field.required,
				options: field.optionsText
					.split(',')
					.map((option) => option.trim())
					.filter(Boolean)
			}))
		};

		setSubmitting(true);
		try {
			await api.post<WorkflowTemplateResponse>('/workflows/templates', request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="양식 만들기"
			description="업무별 문서 필드와 기본 결재선을 설정합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="workflow-template-name">양식명</Label>
						<Input
							id="workflow-template-name"
							value={form.name}
							onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="workflow-template-category">카테고리</Label>
						<Input
							id="workflow-template-category"
							value={form.category}
							onChange={(event) =>
								setForm((current) => ({ ...current, category: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="workflow-template-description">설명</Label>
					<textarea
						id="workflow-template-description"
						value={form.description}
						onChange={(event) =>
							setForm((current) => ({ ...current, description: event.target.value }))
						}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="workflow-template-line">기본 결재선</Label>
					<select
						id="workflow-template-line"
						value={form.defaultApprovalLineId}
						onChange={(event) =>
							setForm((current) => ({ ...current, defaultApprovalLineId: event.target.value }))
						}
						className={dialogControlClassName}
					>
						<option value="">선택</option>
						{approvalLines.map((line) => (
							<option key={line.id} value={line.id}>
								{line.name}
							</option>
						))}
					</select>
				</div>
				<div className="space-y-3">
					<div className="flex items-center justify-between gap-3">
						<Label>필드</Label>
						<Button type="button" size="sm" variant="outline" onClick={addField}>
							<Plus className="size-4" />
							필드 추가
						</Button>
					</div>
					{form.fields.map((field, index) => (
						<div key={`${field.key}-${index}`} className="rounded-lg border border-border p-3">
							<div className="grid gap-3 md:grid-cols-[1fr_1fr_9rem_5rem_auto]">
								<Input
									value={field.key}
									onChange={(event) => updateField(index, { key: event.target.value })}
									placeholder="key"
									aria-label={`필드 ${index + 1} 키`}
								/>
								<Input
									value={field.label}
									onChange={(event) => updateField(index, { label: event.target.value })}
									placeholder="라벨"
									aria-label={`필드 ${index + 1} 라벨`}
								/>
								<select
									value={field.type}
									onChange={(event) =>
										updateField(index, { type: event.target.value as WorkflowTemplateFieldType })
									}
									className={dialogControlClassName}
									aria-label={`필드 ${index + 1} 타입`}
								>
									{(
										['TEXT', 'NUMBER', 'AMOUNT', 'DATE', 'SELECT', 'EMPLOYEE', 'REFERENCE'] as const
									).map((type) => (
										<option key={type} value={type}>
											{workflowFieldTypeLabel(type)}
										</option>
									))}
								</select>
								<label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
									<input
										type="checkbox"
										checked={field.required}
										onChange={(event) => updateField(index, { required: event.target.checked })}
									/>
									필수
								</label>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => removeField(index)}
									disabled={form.fields.length <= 1}
								>
									삭제
								</Button>
							</div>
							{field.type === 'SELECT' ? (
								<Input
									value={field.optionsText}
									onChange={(event) => updateField(index, { optionsText: event.target.value })}
									placeholder="선택지, 쉼표로 구분"
									className="mt-3"
									aria-label={`필드 ${index + 1} 선택지`}
								/>
							) : null}
						</div>
					))}
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						저장
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function WorkflowTemplateApprovalLineBulkDialog({
	templates,
	approvalLines,
	onClose,
	onSubmitted
}: {
	templates: WorkflowTemplateResponse[];
	approvalLines: ApprovalLineResponse[];
	onClose: () => void;
	onSubmitted: (response: WorkflowTemplateBulkApprovalLineResponse) => Promise<void>;
}) {
	const activeApprovalLines = approvalLines.filter((line) => line.active);
	const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);
	const [defaultApprovalLineId, setDefaultApprovalLineId] = useState(
		activeApprovalLines[0]?.id ? String(activeApprovalLines[0].id) : ''
	);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	function toggleTemplate(templateId: number, checked: boolean) {
		setSelectedTemplateIds((current) => {
			if (checked) return current.includes(templateId) ? current : [...current, templateId];
			return current.filter((id) => id !== templateId);
		});
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (selectedTemplateIds.length === 0) {
			setError('변경할 양식을 1개 이상 선택해주세요.');
			return;
		}
		if (!defaultApprovalLineId) {
			setError('변경할 결재선을 선택해주세요.');
			return;
		}

		const request: UpdateWorkflowTemplateApprovalLineRequest = {
			templateIds: selectedTemplateIds,
			defaultApprovalLineId: Number(defaultApprovalLineId)
		};

		setSubmitting(true);
		setError(null);
		try {
			const response = await api.patch<WorkflowTemplateBulkApprovalLineResponse>(
				'/workflows/templates/default-approval-line',
				request
			);
			await onSubmitted(response);
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '결재선을 일괄 변경하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="결재선 일괄 변경"
			description="여러 워크플로우 양식의 기본 결재선을 한 번에 변경합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="workflow-bulk-approval-line">변경할 결재선</Label>
					<select
						id="workflow-bulk-approval-line"
						value={defaultApprovalLineId}
						onChange={(event) => setDefaultApprovalLineId(event.target.value)}
						className={dialogControlClassName}
						aria-label="변경할 결재선"
						disabled={activeApprovalLines.length === 0}
					>
						<option value="">선택</option>
						{activeApprovalLines.map((line) => (
							<option key={line.id} value={line.id}>
								{line.name}
							</option>
						))}
					</select>
				</div>
				<div className="space-y-3">
					<div className="flex items-center justify-between gap-3">
						<Label>변경할 양식</Label>
						<Badge variant="outline">{numberLabel(selectedTemplateIds.length)}개 선택</Badge>
					</div>
					<div className="max-h-80 space-y-2 overflow-y-auto rounded-lg border border-border p-2">
						{templates.length === 0 ? (
							<p className="p-3 text-sm text-muted-foreground">변경할 양식이 없습니다.</p>
						) : (
							templates.map((template) => (
								<label
									key={template.id}
									className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2 hover:bg-muted/50"
								>
									<input
										type="checkbox"
										checked={selectedTemplateIds.includes(template.id)}
										onChange={(event) => toggleTemplate(template.id, event.target.checked)}
										aria-label={`${template.name} 선택`}
										className="mt-1"
									/>
									<span className="min-w-0">
										<span className="block font-medium">{template.name}</span>
										<span className="mt-1 block text-xs text-muted-foreground">
											{template.category ?? '카테고리 없음'} · 현재{' '}
											{workflowApprovalLineLabel(approvalLines, template.defaultApprovalLineId)}
										</span>
									</span>
								</label>
							))
						)}
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button
						type="submit"
						disabled={submitting || templates.length === 0 || activeApprovalLines.length === 0}
					>
						일괄 변경
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function WorkflowDocumentDialog({
	templates,
	approvalLines,
	onClose,
	onSubmitted
}: {
	templates: WorkflowTemplateResponse[];
	approvalLines: ApprovalLineResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const firstTemplate = templates[0];
	const [form, setForm] = useState<WorkflowDocumentForm>({
		templateId: firstTemplate ? String(firstTemplate.id) : '',
		title: '',
		approvalLineId: firstTemplate?.defaultApprovalLineId
			? String(firstTemplate.defaultApprovalLineId)
			: '',
		fieldValues: workflowInitialFieldValues(firstTemplate)
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const selectedTemplate = useMemo(
		() => templates.find((template) => String(template.id) === form.templateId) ?? null,
		[form.templateId, templates]
	);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!selectedTemplate) {
			setError('제출할 양식을 선택해주세요.');
			return;
		}
		if (!form.title.trim()) {
			setError('문서 제목을 입력해주세요.');
			return;
		}
		const missingField = selectedTemplate.fields.find(
			(field) => field.required && !form.fieldValues[field.key]?.trim()
		);
		if (missingField) {
			setError(`${missingField.label} 필드를 입력해주세요.`);
			return;
		}
		const request: CreateWorkflowDocumentRequest = {
			templateId: selectedTemplate.id,
			title: form.title.trim(),
			approvalLineId: form.approvalLineId ? Number(form.approvalLineId) : undefined,
			fieldValues: selectedTemplate.fields.map((field) => ({
				key: field.key,
				value: form.fieldValues[field.key] ?? ''
			}))
		};

		setSubmitting(true);
		try {
			await api.post<WorkflowDocumentResponse>('/workflows/documents', request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="문서 제출"
			description="선택한 양식의 필드를 입력하고 결재를 요청합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="workflow-document-template">양식</Label>
						<select
							id="workflow-document-template"
							value={form.templateId}
							onChange={(event) =>
								setForm((current) => {
									const nextTemplate =
										templates.find((template) => String(template.id) === event.target.value) ??
										null;
									return {
										...current,
										templateId: event.target.value,
										approvalLineId: nextTemplate?.defaultApprovalLineId?.toString() ?? '',
										fieldValues: workflowInitialFieldValues(nextTemplate)
									};
								})
							}
							className={dialogControlClassName}
						>
							{templates.map((template) => (
								<option key={template.id} value={template.id}>
									{template.name}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="workflow-document-line">결재선</Label>
						<select
							id="workflow-document-line"
							value={form.approvalLineId}
							onChange={(event) =>
								setForm((current) => ({ ...current, approvalLineId: event.target.value }))
							}
							className={dialogControlClassName}
						>
							<option value="">양식 기본값</option>
							{approvalLines.map((line) => (
								<option key={line.id} value={line.id}>
									{line.name}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="workflow-document-title">문서 제목</Label>
					<Input
						id="workflow-document-title"
						value={form.title}
						onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
					/>
				</div>
				{selectedTemplate ? (
					<div className="grid gap-4 sm:grid-cols-2">
						{selectedTemplate.fields.map((field) => (
							<div key={field.key} className="space-y-2">
								<Label htmlFor={`workflow-field-${field.key}`}>
									{field.label}
									{field.required ? ' *' : ''}
								</Label>
								{field.type === 'SELECT' && field.options?.length ? (
									<select
										id={`workflow-field-${field.key}`}
										value={form.fieldValues[field.key] ?? ''}
										onChange={(event) =>
											setForm((current) => ({
												...current,
												fieldValues: {
													...current.fieldValues,
													[field.key]: event.target.value
												}
											}))
										}
										className={dialogControlClassName}
									>
										<option value="">선택</option>
										{field.options.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>
								) : (
									<Input
										id={`workflow-field-${field.key}`}
										type={
											field.type === 'DATE'
												? 'date'
												: field.type === 'AMOUNT' || field.type === 'NUMBER'
													? 'number'
													: 'text'
										}
										value={form.fieldValues[field.key] ?? ''}
										onChange={(event) =>
											setForm((current) => ({
												...current,
												fieldValues: {
													...current.fieldValues,
													[field.key]: event.target.value
												}
											}))
										}
									/>
								)}
							</div>
						))}
					</div>
				) : null}
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || templates.length === 0}>
						제출
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function WorkflowDocumentShareDialog({
	document,
	employees,
	onClose,
	onShared
}: {
	document: WorkflowDocumentResponse;
	employees: EmployeeResponse[];
	onClose: () => void;
	onShared: (message: string) => void;
}) {
	const [query, setQuery] = useState('');
	const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const normalizedQuery = query.trim().toLowerCase();
	const selectedEmployeeIdSet = useMemo(() => new Set(selectedEmployeeIds), [selectedEmployeeIds]);
	const candidates = employees
		.filter((employee) => employee.status === 'ACTIVE')
		.filter((employee) => employee.id !== document.requesterEmployeeId)
		.filter((employee) => {
			if (!normalizedQuery) return true;
			return [employee.name, employee.department, employee.position]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(normalizedQuery));
		});

	function toggleEmployee(employeeId: number, checked: boolean) {
		setSelectedEmployeeIds((current) =>
			checked
				? Array.from(new Set([...current, employeeId]))
				: current.filter((id) => id !== employeeId)
		);
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (selectedEmployeeIds.length === 0) {
			setError('공유할 구성원을 선택해주세요.');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			const references = await api.post<ApprovalReferenceResponse[]>(
				`/workflows/documents/${document.id}/shares`,
				{ employeeIds: selectedEmployeeIds }
			);
			onShared(workflowShareResultMessage(references, employees));
			onClose();
		} catch (shareError) {
			setError(getErrorMessage(shareError, '문서를 공유하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="문서 공유"
			description={`${document.title} 문서를 참여자가 아닌 구성원에게 참조로 공유합니다.`}
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="workflow-share-search">구성원 검색</Label>
					<div className="relative">
						<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							id="workflow-share-search"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="이름, 부서, 직책"
							className="pl-9"
						/>
					</div>
				</div>
				<div className="space-y-2">
					<div className="flex items-center justify-between gap-3">
						<Label>공유 대상</Label>
						<Badge variant="outline">{numberLabel(selectedEmployeeIds.length)}명 선택</Badge>
					</div>
					<div className="max-h-80 space-y-2 overflow-y-auto rounded-lg border border-border p-2">
						{candidates.length === 0 ? (
							<p className="p-3 text-sm text-muted-foreground">공유할 수 있는 구성원이 없습니다.</p>
						) : (
							candidates.map((employee) => (
								<label
									key={employee.id}
									className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2 hover:bg-muted/50"
								>
									<input
										type="checkbox"
										checked={selectedEmployeeIdSet.has(employee.id)}
										onChange={(event) => toggleEmployee(employee.id, event.target.checked)}
										aria-label={`${employee.name} 선택`}
										className="mt-1"
									/>
									<span className="min-w-0">
										<span className="block font-medium">{employee.name}</span>
										<span className="mt-1 block text-xs text-muted-foreground">
											{employee.department ?? '소속 없음'} · {employee.position ?? '직책 없음'}
										</span>
									</span>
								</label>
							))
						)}
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || candidates.length === 0}>
						문서 공유
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function WorkflowDocumentActivityDialog({
	document,
	onClose
}: {
	document: WorkflowDocumentResponse;
	onClose: () => void;
}) {
	const [activitiesState, setActivitiesState] = useState<
		RequestState<WorkflowDocumentActivityResponse[]>
	>({ status: 'loading' });
	const [comment, setComment] = useState('');
	const [commentError, setCommentError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		let active = true;
		void api
			.get<WorkflowDocumentActivityResponse[]>(`/workflows/documents/${document.id}/activities`)
			.then((activities) => {
				if (active) setActivitiesState({ status: 'success', data: activities });
			})
			.catch((error) => {
				if (active) {
					setActivitiesState({
						status: 'error',
						message: getErrorMessage(error, '문서 활동 내역을 불러오지 못했습니다.')
					});
				}
			});
		return () => {
			active = false;
		};
	}, [document.id]);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const content = comment.trim();
		if (!content) {
			setCommentError('댓글 내용을 입력해주세요.');
			return;
		}

		const request: CreateWorkflowDocumentCommentRequest = { content };
		setSubmitting(true);
		setCommentError(null);
		try {
			const savedComment = await api.post<WorkflowDocumentCommentResponse>(
				`/workflows/documents/${document.id}/comments`,
				request
			);
			setActivitiesState((current) =>
				current.status === 'success'
					? {
							status: 'success',
							data: [...current.data, workflowCommentActivityFromResponse(savedComment)]
						}
					: current
			);
			setComment('');
		} catch (error) {
			setCommentError(getErrorMessage(error, '댓글을 남기지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="문서 활동 내역"
			description={`${document.title} 문서의 작성, 승인, 공유, 댓글 흐름입니다.`}
			onClose={onClose}
		>
			<div className="space-y-4">
				{activitiesState.status === 'loading' || activitiesState.status === 'idle' ? (
					<div className="flex min-h-48 items-center justify-center">
						<Spinner />
					</div>
				) : null}
				{activitiesState.status === 'error' ? (
					<div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
						{activitiesState.message}
					</div>
				) : null}
				{activitiesState.status === 'success' ? (
					activitiesState.data.length === 0 ? (
						<EmptyState message="기록된 활동 내역이 없습니다." />
					) : (
						<ol className="max-h-96 space-y-3 overflow-y-auto pr-1">
							{activitiesState.data.map((activity) => (
								<li key={activity.id} className="rounded-lg border border-border p-3">
									<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
										<div className="min-w-0 space-y-2">
											<div className="flex flex-wrap items-center gap-2">
												<Badge variant={workflowDocumentActivityVariant(activity.activityType)}>
													{workflowDocumentActivityLabel(activity.activityType)}
												</Badge>
												<p className="font-semibold">{activity.title}</p>
											</div>
											<p className="text-sm text-muted-foreground">{activity.description}</p>
											{activity.comment ? (
												<p className="rounded-md bg-muted px-3 py-2 text-sm">{activity.comment}</p>
											) : null}
										</div>
										<span className="shrink-0 text-xs text-muted-foreground">
											{dateTimeLabel(activity.occurredAt)}
										</span>
									</div>
								</li>
							))}
						</ol>
					)
				) : null}
				<form
					className="space-y-3 rounded-lg border border-border bg-muted/20 p-3"
					onSubmit={handleSubmit}
				>
					<FormError message={commentError} />
					<div className="space-y-2">
						<Label htmlFor="workflow-activity-comment">활동 댓글</Label>
						<textarea
							id="workflow-activity-comment"
							value={comment}
							onChange={(event) => setComment(event.target.value)}
							className={`${dialogTextAreaClassName} min-h-20`}
							placeholder="문서 진행 상황에 남길 의견"
						/>
					</div>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="outline" onClick={onClose}>
							닫기
						</Button>
						<Button type="submit" disabled={submitting}>
							{submitting ? '등록 중...' : '댓글 남기기'}
						</Button>
					</div>
				</form>
			</div>
		</Modal>
	);
}

export function WorkflowsPage() {
	const [state, setState] = useState<RequestState<WorkflowsPageData>>({ status: 'idle' });
	const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
	const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
	const [templateApprovalLineDialogOpen, setTemplateApprovalLineDialogOpen] = useState(false);
	const [shareDialogDocument, setShareDialogDocument] = useState<WorkflowDocumentResponse | null>(
		null
	);
	const [activityDialogDocument, setActivityDialogDocument] =
		useState<WorkflowDocumentResponse | null>(null);
	const [templateFeedback, setTemplateFeedback] = useState<string | null>(null);
	const [shareFeedback, setShareFeedback] = useState<string | null>(null);
	const [filters, setFilters] = useState<WorkflowDocumentFilters>(emptyWorkflowDocumentFilters);
	const [appliedFilters, setAppliedFilters] = useState<WorkflowDocumentFilters>(
		emptyWorkflowDocumentFilters
	);
	const [presetName, setPresetName] = useState('');
	const [filterFeedback, setFilterFeedback] = useState<string | null>(null);
	const [filterSubmitting, setFilterSubmitting] = useState<string | null>(null);
	const [exportFilters, setExportFilters] = useState<WorkflowDocumentExportFilters>({
		templateId: '',
		status: '',
		submittedFrom: '',
		submittedTo: ''
	});
	const [exporting, setExporting] = useState(false);
	const [exportError, setExportError] = useState<string | null>(null);

	const loadForFilters = useCallback(async (nextFilters: WorkflowDocumentFilters) => {
		setState({ status: 'loading' });
		try {
			const [
				summary,
				operations,
				templates,
				documents,
				searchPresets,
				approvalLines,
				employeePage
			] = await Promise.all([
				api.get<WorkflowSummaryResponse>('/workflows/summary'),
				api
					.get<WorkflowOperationsSummaryResponse>('/workflows/operations/summary')
					.catch(() => null),
				api.get<WorkflowTemplateResponse[]>('/workflows/templates'),
				api.get<WorkflowDocumentResponse[]>(workflowDocumentListPath(nextFilters)),
				api.get<WorkflowSearchPresetResponse[]>('/workflows/search-presets').catch(() => []),
				api.get<ApprovalLineResponse[]>('/approval-lines'),
				api.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200').catch(() => ({
					content: [],
					page: 0,
					size: 200,
					totalElements: 0,
					totalPages: 0
				}))
			]);
			setState({
				status: 'success',
				data: {
					summary,
					operations,
					templates,
					documents,
					searchPresets,
					approvalLines,
					employees: employeePage.content
				}
			});
		} catch (error) {
			setState({ status: 'error', message: getErrorMessage(error) });
		}
	}, []);

	const load = useCallback(async () => {
		await loadForFilters(appliedFilters);
	}, [appliedFilters, loadForFilters]);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await loadForFilters(emptyWorkflowDocumentFilters);
		});
		return () => {
			active = false;
		};
	}, [loadForFilters]);

	const data = state.status === 'success' ? state.data : null;
	const hasActiveWorkflowApprovalLine = data?.approvalLines.some((line) => line.active) ?? false;

	async function handleApplyFilters(nextFilters: WorkflowDocumentFilters = filters) {
		const snapshot = cloneWorkflowDocumentFilters(nextFilters);
		setFilterSubmitting('search');
		setFilterFeedback(null);
		try {
			setAppliedFilters(snapshot);
			await loadForFilters(snapshot);
		} finally {
			setFilterSubmitting(null);
		}
	}

	async function handleClearFilters() {
		const snapshot = cloneWorkflowDocumentFilters(emptyWorkflowDocumentFilters);
		setFilters(snapshot);
		await handleApplyFilters(snapshot);
	}

	async function handleApplyPreset(preset: WorkflowSearchPresetResponse) {
		const snapshot = workflowFiltersFromPreset(preset);
		setFilters(snapshot);
		setFilterSubmitting(`apply-${preset.id}`);
		setFilterFeedback(null);
		try {
			setAppliedFilters(snapshot);
			await loadForFilters(snapshot);
		} finally {
			setFilterSubmitting(null);
		}
	}

	async function handleSavePreset() {
		if (!presetName.trim()) {
			setFilterFeedback('저장 조건 이름을 입력해주세요.');
			return;
		}
		if (!hasWorkflowDocumentFilter(filters)) {
			setFilterFeedback('저장할 검색 조건을 1개 이상 선택해주세요.');
			return;
		}
		const snapshot = cloneWorkflowDocumentFilters(filters);
		setFilterSubmitting('save');
		setFilterFeedback(null);
		try {
			await api.post<WorkflowSearchPresetResponse>(
				'/workflows/search-presets',
				workflowSearchPresetRequest(presetName, snapshot)
			);
			setPresetName('');
			setAppliedFilters(snapshot);
			await loadForFilters(snapshot);
			setFilterFeedback('검색 조건을 저장했습니다.');
		} catch (error) {
			setFilterFeedback(getErrorMessage(error, '검색 조건을 저장하지 못했습니다.'));
		} finally {
			setFilterSubmitting(null);
		}
	}

	async function handleDeletePreset(preset: WorkflowSearchPresetResponse) {
		setFilterSubmitting(`delete-${preset.id}`);
		setFilterFeedback(null);
		try {
			await api.delete(`/workflows/search-presets/${preset.id}`);
			await loadForFilters(appliedFilters);
			setFilterFeedback('검색 조건을 삭제했습니다.');
		} catch (error) {
			setFilterFeedback(getErrorMessage(error, '검색 조건을 삭제하지 못했습니다.'));
		} finally {
			setFilterSubmitting(null);
		}
	}

	async function handleTemplateApprovalLineBulkSubmitted(
		response: WorkflowTemplateBulkApprovalLineResponse
	) {
		await load();
		setTemplateFeedback(
			`${numberLabel(response.updatedCount)}개 양식의 기본 결재선을 변경했습니다.`
		);
	}

	async function downloadDocuments() {
		setExportError(null);
		setExporting(true);
		try {
			await api.downloadFile(workflowDocumentExportPath(exportFilters), 'workflow_documents.csv');
		} catch (error) {
			setExportError(getErrorMessage(error, '워크플로우 문서 데이터를 다운로드하지 못했습니다.'));
		} finally {
			setExporting(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="워크플로우"
				description="자유 양식 문서를 작성하고 결재 진행 상태를 확인합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						<Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(true)}>
							<Plus className="size-4" />
							양식 만들기
						</Button>
						<Button type="button" onClick={() => setDocumentDialogOpen(true)}>
							<ClipboardCheck className="size-4" />
							문서 제출
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-5">
						<WorkflowStatCard
							title="활성 양식"
							value={data.summary.activeTemplateCount}
							description="작성 가능한 문서 양식"
						/>
						<WorkflowStatCard
							title="전체 문서"
							value={data.summary.totalDocumentCount}
							description="제출된 워크플로우"
						/>
						<WorkflowStatCard
							title="진행 중"
							value={data.summary.inProgressDocumentCount}
							description="결재 진행 문서"
						/>
						<WorkflowStatCard
							title="승인 완료"
							value={data.summary.approvedDocumentCount}
							description="처리 완료 문서"
						/>
						<WorkflowStatCard
							title="반려"
							value={data.summary.rejectedDocumentCount}
							description="재작성 필요 문서"
						/>
					</div>

					{data.operations ? <WorkflowOperationsPanel operations={data.operations} /> : null}

					<div className="grid gap-6 xl:grid-cols-[1fr_22rem]">
						<Card>
							<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
								<div>
									<CardTitle>문서함</CardTitle>
									<CardDescription>
										양식, 요청자, 결재선과 입력값을 함께 확인하고 반복 검색 조건을 저장합니다.
									</CardDescription>
								</div>
								<Badge variant={hasWorkflowDocumentFilter(appliedFilters) ? 'success' : 'outline'}>
									{hasWorkflowDocumentFilter(appliedFilters) ? '필터 적용 중' : '전체 문서'}
								</Badge>
							</CardHeader>
							<CardContent>
								<form
									className="mb-4 space-y-4 rounded-lg border border-border bg-muted/20 p-3"
									onSubmit={(event) => {
										event.preventDefault();
										void handleApplyFilters();
									}}
								>
									<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(12rem,1fr)_minmax(10rem,12rem)_10rem_10rem_10rem]">
										<div className="space-y-2">
											<Label htmlFor="workflow-search-keyword">워크플로우 검색어</Label>
											<div className="relative">
												<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
												<Input
													id="workflow-search-keyword"
													value={filters.keyword}
													onChange={(event) =>
														setFilters((current) => ({ ...current, keyword: event.target.value }))
													}
													placeholder="제목, 요청자, 입력값"
													className="pl-9"
													aria-label="워크플로우 검색어"
												/>
											</div>
										</div>
										<div className="space-y-2">
											<Label htmlFor="workflow-search-template">문서 양식</Label>
											<select
												id="workflow-search-template"
												value={filters.templateId}
												onChange={(event) =>
													setFilters((current) => ({
														...current,
														templateId: event.target.value
													}))
												}
												className={dialogControlClassName}
												aria-label="문서 양식"
											>
												<option value="">전체 양식</option>
												{data.templates.map((template) => (
													<option key={template.id} value={template.id}>
														{template.name}
													</option>
												))}
											</select>
										</div>
										<div className="space-y-2">
											<Label htmlFor="workflow-search-status">문서 상태</Label>
											<select
												id="workflow-search-status"
												value={filters.status}
												onChange={(event) =>
													setFilters((current) => ({
														...current,
														status: event.target.value as WorkflowDocumentFilters['status']
													}))
												}
												className={dialogControlClassName}
												aria-label="문서 상태"
											>
												<option value="">전체 상태</option>
												{(['IN_PROGRESS', 'APPROVED', 'REJECTED', 'CANCELLED'] as const).map(
													(status) => (
														<option key={status} value={status}>
															{workflowDocumentStatusLabel(status)}
														</option>
													)
												)}
											</select>
										</div>
										<div className="space-y-2">
											<Label htmlFor="workflow-search-from">검색 시작일</Label>
											<Input
												id="workflow-search-from"
												type="date"
												value={filters.submittedFrom}
												onChange={(event) =>
													setFilters((current) => ({
														...current,
														submittedFrom: event.target.value
													}))
												}
												aria-label="검색 시작일"
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="workflow-search-to">검색 종료일</Label>
											<Input
												id="workflow-search-to"
												type="date"
												value={filters.submittedTo}
												onChange={(event) =>
													setFilters((current) => ({
														...current,
														submittedTo: event.target.value
													}))
												}
												aria-label="검색 종료일"
											/>
										</div>
									</div>

									<div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
										<div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] xl:w-[560px]">
											<div className="space-y-2">
												<Label htmlFor="workflow-search-preset-name">
													워크플로우 저장 조건 이름
												</Label>
												<Input
													id="workflow-search-preset-name"
													value={presetName}
													onChange={(event) => setPresetName(event.target.value)}
													placeholder="예: 승인 완료 내보내기"
													aria-label="워크플로우 저장 조건 이름"
												/>
											</div>
											<Button
												type="button"
												variant="outline"
												onClick={() => void handleSavePreset()}
												disabled={filterSubmitting === 'save'}
												className="sm:mt-7"
											>
												<Plus className="size-4" />
												{filterSubmitting === 'save' ? '저장 중...' : '조건 저장'}
											</Button>
										</div>
										<div className="flex flex-wrap gap-2 xl:justify-end">
											<Button
												type="button"
												variant="outline"
												onClick={() => void handleClearFilters()}
												disabled={filterSubmitting === 'search'}
											>
												초기화
											</Button>
											<Button type="submit" disabled={filterSubmitting === 'search'}>
												<Search className="size-4" />
												{filterSubmitting === 'search' ? '검색 중...' : '검색'}
											</Button>
										</div>
									</div>

									{filterFeedback ? (
										<div className="rounded-md border border-border bg-background px-3 py-2 text-sm">
											{filterFeedback}
										</div>
									) : null}
									{shareFeedback ? (
										<div className="rounded-md border border-success/30 bg-success/10 px-3 py-2 text-sm text-success-foreground">
											{shareFeedback}
										</div>
									) : null}

									<div className="rounded-lg border border-border bg-background p-3">
										<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
											<div>
												<p className="font-semibold">저장된 검색 조건</p>
												<p className="mt-1 text-sm text-muted-foreground">
													반복 조회하는 문서 조건을 한 번에 적용합니다.
												</p>
											</div>
											<Badge variant="outline">{numberLabel(data.searchPresets.length)}개</Badge>
										</div>
										<div className="mt-3 grid gap-3 lg:grid-cols-2">
											{data.searchPresets.length > 0 ? (
												data.searchPresets.map((preset) => (
													<div
														key={preset.id}
														className="flex min-w-0 items-start gap-2 rounded-md border border-border bg-muted/20 p-3"
													>
														<button
															type="button"
															className="min-w-0 flex-1 text-left"
															onClick={() => void handleApplyPreset(preset)}
															disabled={filterSubmitting === `apply-${preset.id}`}
															aria-label={`${preset.name} 적용`}
														>
															<p className="font-medium">{preset.name}</p>
															<p className="mt-1 break-words text-sm text-muted-foreground">
																{preset.filterSummary}
															</p>
														</button>
														<Button
															type="button"
															variant="ghost"
															size="sm"
															onClick={() => void handleDeletePreset(preset)}
															disabled={filterSubmitting === `delete-${preset.id}`}
															aria-label={`${preset.name} 삭제`}
														>
															<Trash2 className="size-4" />
														</Button>
													</div>
												))
											) : (
												<p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
													저장된 검색 조건이 없습니다.
												</p>
											)}
										</div>
									</div>
								</form>
								<div className="mb-4 space-y-3 rounded-lg border border-border bg-muted/20 p-3">
									<div className="grid gap-3 md:grid-cols-[minmax(10rem,1fr)_10rem_10rem_10rem_auto]">
										<div className="space-y-2">
											<Label htmlFor="workflow-export-template">내보낼 양식</Label>
											<select
												id="workflow-export-template"
												value={exportFilters.templateId}
												onChange={(event) =>
													setExportFilters((current) => ({
														...current,
														templateId: event.target.value
													}))
												}
												className={dialogControlClassName}
											>
												<option value="">전체 양식</option>
												{data.templates.map((template) => (
													<option key={template.id} value={template.id}>
														{template.name}
													</option>
												))}
											</select>
										</div>
										<div className="space-y-2">
											<Label htmlFor="workflow-export-status">내보낼 상태</Label>
											<select
												id="workflow-export-status"
												value={exportFilters.status}
												onChange={(event) =>
													setExportFilters((current) => ({
														...current,
														status: event.target.value as WorkflowDocumentExportFilters['status']
													}))
												}
												className={dialogControlClassName}
											>
												<option value="">전체 상태</option>
												{(['IN_PROGRESS', 'APPROVED', 'REJECTED', 'CANCELLED'] as const).map(
													(status) => (
														<option key={status} value={status}>
															{workflowDocumentStatusLabel(status)}
														</option>
													)
												)}
											</select>
										</div>
										<div className="space-y-2">
											<Label htmlFor="workflow-export-from">제출 시작일</Label>
											<Input
												id="workflow-export-from"
												type="date"
												value={exportFilters.submittedFrom}
												onChange={(event) =>
													setExportFilters((current) => ({
														...current,
														submittedFrom: event.target.value
													}))
												}
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="workflow-export-to">제출 종료일</Label>
											<Input
												id="workflow-export-to"
												type="date"
												value={exportFilters.submittedTo}
												onChange={(event) =>
													setExportFilters((current) => ({
														...current,
														submittedTo: event.target.value
													}))
												}
											/>
										</div>
										<div className="flex items-end">
											<Button
												type="button"
												variant="outline"
												onClick={() => void downloadDocuments()}
												disabled={exporting}
												className="w-full"
											>
												<Download className="size-4" />
												문서 데이터 다운로드
											</Button>
										</div>
									</div>
									<FormError message={exportError} />
								</div>
								{data.documents.length === 0 ? (
									<EmptyState message="제출된 워크플로우 문서가 없습니다." />
								) : (
									<div className="overflow-x-auto rounded-lg border border-border">
										<table className="w-full min-w-[1040px] text-left text-sm">
											<thead className="bg-muted/40 text-xs text-muted-foreground">
												<tr>
													<th className="px-4 py-3 font-semibold">문서</th>
													<th className="px-4 py-3 font-semibold">요청자</th>
													<th className="px-4 py-3 font-semibold">입력값</th>
													<th className="px-4 py-3 font-semibold">결재선</th>
													<th className="px-4 py-3 font-semibold">상태</th>
													<th className="px-4 py-3 font-semibold">제출일</th>
													<th className="px-4 py-3 font-semibold">활동</th>
													<th className="px-4 py-3 font-semibold">공유</th>
												</tr>
											</thead>
											<tbody>
												{data.documents.map((document) => {
													const template = workflowTemplateForDocument(data.templates, document);
													return (
														<tr key={document.id} className="border-t border-border align-top">
															<td className="px-4 py-4">
																<p className="font-semibold">{document.title}</p>
																<p className="mt-1 text-xs text-muted-foreground">
																	{document.templateName ?? template?.name ?? '양식 없음'}
																</p>
															</td>
															<td className="px-4 py-4 text-muted-foreground">
																{document.requesterEmployeeName ??
																	`직원 #${document.requesterEmployeeId}`}
															</td>
															<td className="px-4 py-4">
																<div className="flex flex-wrap gap-2">
																	{document.fieldValues.map((value) => {
																		const field = workflowFieldForValue(template, value.key);
																		return (
																			<span
																				key={`${document.id}-${value.key}`}
																				className="inline-flex items-center gap-2 rounded-md bg-muted px-2 py-1 text-xs"
																			>
																				<span className="text-muted-foreground">{value.label}</span>
																				<span className="font-semibold">
																					{workflowFieldValueLabel(value.value, field)}
																				</span>
																			</span>
																		);
																	})}
																</div>
															</td>
															<td className="px-4 py-4 text-muted-foreground">
																{workflowApprovalLineLabel(
																	data.approvalLines,
																	document.approvalLineId
																)}
															</td>
															<td className="px-4 py-4">
																<Badge variant={workflowDocumentStatusVariant(document.status)}>
																	{workflowDocumentStatusLabel(document.status)}
																</Badge>
															</td>
															<td className="px-4 py-4 text-muted-foreground">
																{dateLabel(document.submittedAt)}
															</td>
															<td className="px-4 py-4">
																<Button
																	type="button"
																	variant="outline"
																	size="sm"
																	onClick={() => setActivityDialogDocument(document)}
																	aria-label={`${document.title} 활동 내역`}
																>
																	<Clock3 className="size-4" />
																	활동
																</Button>
															</td>
															<td className="px-4 py-4">
																<Button
																	type="button"
																	variant="outline"
																	size="sm"
																	onClick={() => {
																		setShareFeedback(null);
																		setShareDialogDocument(document);
																	}}
																	disabled={!document.approvalId || data.employees.length === 0}
																	aria-label={`${document.title} 공유`}
																>
																	<UsersRound className="size-4" />
																	공유
																</Button>
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								)}
							</CardContent>
						</Card>

						<div className="space-y-6">
							<Card>
								<CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between">
									<div className="space-y-1">
										<CardTitle>양식</CardTitle>
										<CardDescription>필드 구성과 기본 결재선을 확인합니다.</CardDescription>
									</div>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											setTemplateFeedback(null);
											setTemplateApprovalLineDialogOpen(true);
										}}
										disabled={data.templates.length === 0 || !hasActiveWorkflowApprovalLine}
									>
										<GitBranch className="size-4" />
										결재선 일괄 변경
									</Button>
								</CardHeader>
								<CardContent className="space-y-3">
									{templateFeedback ? (
										<div className="rounded-md border border-success/30 bg-success/10 px-3 py-2 text-sm text-success-foreground">
											{templateFeedback}
										</div>
									) : null}
									{data.templates.length === 0 ? (
										<EmptyState message="활성 워크플로우 양식이 없습니다." />
									) : (
										data.templates.map((template) => (
											<div key={template.id} className="rounded-lg border border-border p-3">
												<div className="flex items-start justify-between gap-3">
													<div>
														<p className="font-semibold">{template.name}</p>
														<p className="mt-1 text-xs text-muted-foreground">
															{template.category ?? '카테고리 없음'} ·{' '}
															{workflowApprovalLineLabel(
																data.approvalLines,
																template.defaultApprovalLineId
															)}
														</p>
													</div>
													<Badge variant="outline">
														{numberLabel(template.fields.length)}개 필드
													</Badge>
												</div>
												<div className="mt-3 flex flex-wrap gap-2">
													{template.fields.map((field) => (
														<Badge key={`${template.id}-${field.key}`} variant="secondary">
															{field.label} · {workflowFieldTypeLabel(field.type)}
														</Badge>
													))}
												</div>
											</div>
										))
									)}
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>결재선</CardTitle>
									<CardDescription>워크플로우에서 사용할 승인 경로입니다.</CardDescription>
								</CardHeader>
								<CardContent className="space-y-3">
									{data.approvalLines.length === 0 ? (
										<EmptyState message="등록된 결재선이 없습니다." />
									) : (
										data.approvalLines.map((line) => (
											<div key={line.id} className="rounded-lg border border-border p-3">
												<p className="font-semibold">{line.name}</p>
												<p className="mt-1 text-xs text-muted-foreground">
													{line.description ?? '설명 없음'}
												</p>
												<div className="mt-3 flex flex-wrap gap-2">
													{line.steps.map((step) => (
														<Badge key={`${line.id}-${step.stepOrder}`} variant="outline">
															{numberLabel(step.stepOrder)}단계 ·{' '}
															{approvalStepActionLabel(step.actionLabel)} · 직원 #{step.approverId}
														</Badge>
													))}
												</div>
											</div>
										))
									)}
								</CardContent>
							</Card>
						</div>
					</div>

					{templateDialogOpen ? (
						<WorkflowTemplateDialog
							approvalLines={data.approvalLines}
							onClose={() => setTemplateDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{documentDialogOpen ? (
						<WorkflowDocumentDialog
							templates={data.templates}
							approvalLines={data.approvalLines}
							onClose={() => setDocumentDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{templateApprovalLineDialogOpen ? (
						<WorkflowTemplateApprovalLineBulkDialog
							templates={data.templates}
							approvalLines={data.approvalLines}
							onClose={() => setTemplateApprovalLineDialogOpen(false)}
							onSubmitted={handleTemplateApprovalLineBulkSubmitted}
						/>
					) : null}
					{shareDialogDocument ? (
						<WorkflowDocumentShareDialog
							document={shareDialogDocument}
							employees={data.employees}
							onClose={() => setShareDialogDocument(null)}
							onShared={setShareFeedback}
						/>
					) : null}
					{activityDialogDocument ? (
						<WorkflowDocumentActivityDialog
							key={activityDialogDocument.id}
							document={activityDialogDocument}
							onClose={() => setActivityDialogDocument(null)}
						/>
					) : null}
				</>
			) : null}
		</div>
	);
}

function agentStatusLabel(status: string): string {
	const labels: Record<string, string> = {
		ACTIVE: '활성',
		READY: '준비',
		WATCH: '점검',
		WARNING: '주의',
		WAITING: '대기',
		DISABLED: '비활성'
	};
	return labels[status] ?? status;
}

function agentStatusVariant(
	status:
		| AgentOperationsChecklistStatus
		| AgentStatus
		| AgentAutomationStatus
		| AgentPermissionStatus
): 'success' | 'warning' | 'outline' | 'secondary' {
	if (status === 'ACTIVE' || status === 'READY') return 'success';
	if (status === 'WARNING' || status === 'WATCH') return 'warning';
	return 'outline';
}

function agentRiskVariant(
	severity: AgentOperationsRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function agentPriorityVariant(
	priority: AgentTaskPriority
): 'destructive' | 'warning' | 'outline' | 'secondary' {
	if (priority === 'CRITICAL') return 'destructive';
	if (priority === 'HIGH') return 'warning';
	if (priority === 'MEDIUM') return 'outline';
	return 'secondary';
}

function agentActionDraftStatusLabel(status: AgentActionDraftStatus): string {
	const labels: Record<AgentActionDraftStatus, string> = {
		DRAFT: '초안',
		PENDING_APPROVAL: '승인 대기',
		APPROVED: '승인됨',
		EXECUTED: '실행 완료',
		DISMISSED: '보류'
	};
	return labels[status];
}

function agentActionDraftStatusVariant(
	status: AgentActionDraftStatus
): 'success' | 'warning' | 'outline' | 'secondary' {
	if (status === 'EXECUTED' || status === 'APPROVED') return 'success';
	if (status === 'PENDING_APPROVAL') return 'warning';
	if (status === 'DISMISSED') return 'secondary';
	return 'outline';
}

function AgentStatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">{numberLabel(value)}</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function AgentChecklistGrid({ items }: { items: AgentOperationsChecklistItem[] }) {
	return (
		<div className="grid gap-3 md:grid-cols-2">
			{items.map((item) => {
				const Icon =
					item.status === 'READY'
						? CheckCircle2
						: item.status === 'WARNING'
							? AlertTriangle
							: Clock3;
				return (
					<div key={item.code} className="rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="font-semibold">{item.label}</p>
									<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={agentStatusVariant(item.status)}>
								{agentStatusLabel(item.status)}
							</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function AgentRiskList({ risks }: { risks: AgentOperationsRiskItem[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">실행 안전장치</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={agentRiskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function AgentCardsPanel({ agents }: { agents: AgentCardResponse[] }) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>에이전트 카탈로그</CardTitle>
				<CardDescription>
					전문가 에이전트와 조직 맞춤 에이전트의 공개 범위와 연결 상태입니다.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<div className="grid gap-3 lg:grid-cols-2">
					{agents.map((agent) => (
						<div key={agent.code} className="rounded-lg border border-border bg-background p-4">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<div className="flex flex-wrap items-center gap-2">
										<p className="font-semibold">{agent.name}</p>
										<Badge variant={agent.type === 'EXPERT' ? 'default' : 'outline'}>
											{agent.type === 'EXPERT' ? '전문가' : '커스텀'}
										</Badge>
									</div>
									<p className="mt-1 text-sm text-muted-foreground">{agent.description}</p>
								</div>
								<Badge variant={agentStatusVariant(agent.status)}>
									{agentStatusLabel(agent.status)}
								</Badge>
							</div>
							<div className="mt-4 grid grid-cols-3 gap-2 text-xs">
								<div className="rounded-md bg-muted/50 p-2">
									<p className="text-muted-foreground">공개</p>
									<p className="mt-1 truncate font-semibold">{agent.visibility}</p>
								</div>
								<div className="rounded-md bg-muted/50 p-2">
									<p className="text-muted-foreground">지식</p>
									<p className="mt-1 font-semibold">{numberLabel(agent.knowledgeSourceCount)}</p>
								</div>
								<div className="rounded-md bg-muted/50 p-2">
									<p className="text-muted-foreground">제안</p>
									<p className="mt-1 font-semibold">{numberLabel(agent.suggestionCount)}</p>
								</div>
							</div>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

function AgentKnowledgeSourcesPanel({ sources }: { sources: AgentKnowledgeSourceResponse[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<FileText className="size-4 text-muted-foreground" />
				<p className="font-semibold">지식 소스</p>
			</div>
			<div className="mt-4 space-y-3">
				{sources.map((source) => (
					<div key={source.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{source.label}</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{source.category} · {source.sensitivity} · 연결 에이전트{' '}
								{numberLabel(source.linkedAgentCount)}
							</p>
						</div>
						<Badge variant={source.enabled ? 'success' : 'warning'}>
							{numberLabel(source.recordCount)}건
						</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function AgentTaskSuggestionsPanel({
	suggestions,
	onCreateDraft,
	busy
}: {
	suggestions: AgentTaskSuggestionResponse[];
	onCreateDraft: (suggestion: AgentTaskSuggestionResponse) => Promise<void>;
	busy: boolean;
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<Target className="size-4 text-muted-foreground" />
				<p className="font-semibold">업무 제안</p>
			</div>
			<div className="mt-4 space-y-3">
				{suggestions.map((suggestion) => (
					<div key={suggestion.code} className="rounded-md bg-muted/40 p-3">
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="font-medium">{suggestion.title}</p>
								<p className="mt-1 text-sm text-muted-foreground">{suggestion.context}</p>
							</div>
							<Badge variant={agentPriorityVariant(suggestion.priority)}>
								{suggestion.priority}
							</Badge>
						</div>
						<div className="mt-3 flex flex-wrap gap-2">
							<Badge variant="outline">{suggestion.source}</Badge>
							<Badge variant={suggestion.humanApprovalRequired ? 'warning' : 'secondary'}>
								{suggestion.humanApprovalRequired ? '사람 승인 필요' : '자동 제안'}
							</Badge>
							<Badge variant="outline">{suggestion.actionLabel}</Badge>
						</div>
						<div className="mt-3">
							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={() => void onCreateDraft(suggestion)}
								disabled={busy}
							>
								<Plus className="size-4" />
								실행 초안 만들기
							</Button>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function AgentImpactPreviewPanel({ previews }: { previews: AgentImpactPreviewResponse[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex items-center gap-2">
					<TrendingUp className="size-4 text-muted-foreground" />
					<p className="font-semibold">실행 영향 미리보기</p>
				</div>
				<Badge variant={previews.length > 0 ? 'warning' : 'outline'}>
					{numberLabel(previews.length)}건
				</Badge>
			</div>
			{previews.length === 0 ? (
				<div className="mt-4">
					<EmptyState message="검토할 실행 영향이 없습니다." />
				</div>
			) : (
				<div className="mt-4 grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
					{previews.map((preview) => (
						<div key={preview.suggestionCode} className="rounded-md bg-muted/40 p-3">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="font-medium">{preview.title}</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{preview.expectedImpactLabel}
									</p>
								</div>
								<Badge variant={agentPriorityVariant(preview.severity)}>{preview.severity}</Badge>
							</div>
							<div className="mt-3 flex flex-wrap gap-2">
								<Badge variant="outline">영향 {numberLabel(preview.affectedItemCount)}건</Badge>
								<Badge variant={preview.guardrailLabel.includes('사람') ? 'warning' : 'secondary'}>
									{preview.guardrailLabel}
								</Badge>
							</div>
							<p className="mt-3 text-xs text-muted-foreground">{preview.evidenceLabel}</p>
							<div className="mt-3">
								<div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
									<span>준비도</span>
									<span>{numberLabel(preview.readinessPercent)}%</span>
								</div>
								<div className="mt-1 h-2 rounded-full bg-background">
									<div
										className="h-2 rounded-full bg-primary"
										style={{ width: `${Math.max(0, Math.min(100, preview.readinessPercent))}%` }}
									/>
								</div>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

function AgentActionDraftsPanel({
	drafts,
	onApprove,
	onExecute,
	onDismiss,
	busy
}: {
	drafts: AgentActionDraftResponse[];
	onApprove: (draft: AgentActionDraftResponse) => Promise<void>;
	onExecute: (draft: AgentActionDraftResponse) => Promise<void>;
	onDismiss: (draft: AgentActionDraftResponse) => Promise<void>;
	busy: boolean;
}) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>실행 초안 큐</CardTitle>
				<CardDescription>
					에이전트가 제안한 작업을 실행 전 검토하고, 승인 가드레일을 거쳐 운영 상태로 넘깁니다.
				</CardDescription>
			</CardHeader>
			<CardContent>
				{drafts.length === 0 ? (
					<EmptyState message="생성된 실행 초안이 없습니다." />
				) : (
					<div className="space-y-3">
						{drafts.map((draft) => (
							<div key={draft.id} className="rounded-lg border border-border bg-background p-4">
								<div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
									<div className="min-w-0">
										<div className="flex flex-wrap items-center gap-2">
											<p className="font-semibold">{draft.title}</p>
											<Badge variant={agentPriorityVariant(draft.priority)}>{draft.priority}</Badge>
											<Badge variant={agentActionDraftStatusVariant(draft.status)}>
												{agentActionDraftStatusLabel(draft.status)}
											</Badge>
										</div>
										<p className="mt-2 text-sm text-muted-foreground">{draft.summary}</p>
										<div className="mt-3 flex flex-wrap gap-2">
											<Badge variant="outline">{draft.source}</Badge>
											<Badge variant="outline">{draft.actionType}</Badge>
											{draft.humanApprovalRequired ? (
												<Badge variant="warning">사람 승인 필요</Badge>
											) : null}
										</div>
									</div>
									<div className="flex shrink-0 flex-wrap gap-2">
										{draft.status === 'PENDING_APPROVAL' ? (
											<Button
												type="button"
												size="sm"
												variant="outline"
												onClick={() => void onApprove(draft)}
												disabled={busy}
											>
												<CheckCircle2 className="size-4" />
												승인
											</Button>
										) : null}
										{draft.status === 'APPROVED' || draft.status === 'DRAFT' ? (
											<Button
												type="button"
												size="sm"
												onClick={() => void onExecute(draft)}
												disabled={busy}
											>
												<BadgeCheck className="size-4" />
												실행 완료
											</Button>
										) : null}
										{draft.status !== 'EXECUTED' && draft.status !== 'DISMISSED' ? (
											<Button
												type="button"
												size="sm"
												variant="ghost"
												onClick={() => void onDismiss(draft)}
												disabled={busy}
											>
												보류
											</Button>
										) : null}
									</div>
								</div>
								{draft.steps.length > 0 ? (
									<div className="mt-4 grid gap-2 md:grid-cols-3">
										{draft.steps.map((step) => (
											<div key={`${draft.id}-${step.order}`} className="rounded-md bg-muted/40 p-3">
												<p className="text-xs font-medium text-muted-foreground">
													STEP {step.order}
												</p>
												<p className="mt-1 font-medium">{step.label}</p>
												<p className="mt-1 text-xs text-muted-foreground">{step.description}</p>
											</div>
										))}
									</div>
								) : null}
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function AgentAutomationPanel({ flows }: { flows: AgentAutomationFlowResponse[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<GitBranch className="size-4 text-muted-foreground" />
				<p className="font-semibold">루틴 자동화</p>
			</div>
			<div className="mt-4 space-y-3">
				{flows.map((flow) => (
					<div key={flow.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{flow.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{flow.schedule} · {flow.triggerLabel}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">{flow.nextRunHint}</p>
						</div>
						<div className="flex shrink-0 flex-col items-end gap-2">
							<Badge variant={agentStatusVariant(flow.status)}>
								{agentStatusLabel(flow.status)}
							</Badge>
							{flow.humanApprovalRequired ? <Badge variant="warning">승인 후 실행</Badge> : null}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function AgentPermissionPanel({ scopes }: { scopes: AgentPermissionScopeResponse[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<ShieldCheck className="size-4 text-muted-foreground" />
				<p className="font-semibold">권한 범위</p>
			</div>
			<div className="mt-4 space-y-3">
				{scopes.map((scope) => (
					<div key={scope.dimension}>
						<div className="flex items-start justify-between gap-3">
							<div>
								<p className="font-medium">{scope.label}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									대상 {numberLabel(scope.subjectCount)} · 에이전트 {numberLabel(scope.agentCount)}
								</p>
							</div>
							<Badge variant={agentStatusVariant(scope.status)}>
								{numberLabel(scope.coveragePercent)}%
							</Badge>
						</div>
						<div className="mt-2 h-2 rounded-full bg-muted">
							<div
								className="h-2 rounded-full bg-primary"
								style={{ width: `${Math.max(0, Math.min(100, scope.coveragePercent))}%` }}
							/>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

type AgentPageData = {
	summary: AgentOperationsSummaryResponse;
	actionDrafts: AgentActionDraftResponse[];
};

export function AgentsPage() {
	const [state, setState] = useState<RequestState<AgentPageData>>({ status: 'idle' });
	const [feedback, setFeedback] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	const [actionBusy, setActionBusy] = useState(false);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [summary, actionDrafts] = await Promise.all([
					api.get<AgentOperationsSummaryResponse>('/agents/operations/summary'),
					api.get<AgentActionDraftResponse[]>('/agents/operations/action-drafts')
				]);
				setState({ status: 'success', data: { summary, actionDrafts } });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const summary = data?.summary ?? null;

	function replaceDraft(nextDraft: AgentActionDraftResponse) {
		setState((current) => {
			if (current.status !== 'success') return current;
			const exists = current.data.actionDrafts.some((draft) => draft.id === nextDraft.id);
			const actionDrafts = exists
				? current.data.actionDrafts.map((draft) => (draft.id === nextDraft.id ? nextDraft : draft))
				: [nextDraft, ...current.data.actionDrafts];
			return { status: 'success', data: { ...current.data, actionDrafts } };
		});
	}

	async function handleCreateActionDraft(suggestion: AgentTaskSuggestionResponse) {
		const request: AgentActionDraftCreateRequest = {
			suggestionCode: suggestion.code,
			note: `${suggestion.title} 실행 초안`
		};
		setActionBusy(true);
		setActionError(null);
		try {
			const draft = await api.post<AgentActionDraftResponse>(
				'/agents/operations/action-drafts',
				request
			);
			replaceDraft(draft);
			setFeedback('실행 초안을 생성했습니다.');
		} catch (error) {
			setActionError(getErrorMessage(error, '실행 초안을 생성하지 못했습니다.'));
		} finally {
			setActionBusy(false);
		}
	}

	async function handleApproveActionDraft(draft: AgentActionDraftResponse) {
		const request: AgentActionDraftDecisionRequest = { note: '에이전트 실행 초안 승인' };
		setActionBusy(true);
		setActionError(null);
		try {
			const approved = await api.post<AgentActionDraftResponse>(
				`/agents/operations/action-drafts/${draft.id}/approve`,
				request
			);
			replaceDraft(approved);
			setFeedback('실행 초안을 승인했습니다.');
		} catch (error) {
			setActionError(getErrorMessage(error, '실행 초안을 승인하지 못했습니다.'));
		} finally {
			setActionBusy(false);
		}
	}

	async function handleExecuteActionDraft(draft: AgentActionDraftResponse) {
		setActionBusy(true);
		setActionError(null);
		try {
			const executed = await api.post<AgentActionDraftResponse>(
				`/agents/operations/action-drafts/${draft.id}/execute`
			);
			replaceDraft(executed);
			setFeedback('실행 초안을 완료했습니다.');
		} catch (error) {
			setActionError(getErrorMessage(error, '실행 초안을 완료하지 못했습니다.'));
		} finally {
			setActionBusy(false);
		}
	}

	async function handleDismissActionDraft(draft: AgentActionDraftResponse) {
		const request: AgentActionDraftDecisionRequest = { note: '에이전트 실행 초안 보류' };
		setActionBusy(true);
		setActionError(null);
		try {
			const dismissed = await api.post<AgentActionDraftResponse>(
				`/agents/operations/action-drafts/${draft.id}/dismiss`,
				request
			);
			replaceDraft(dismissed);
			setFeedback('실행 초안을 보류했습니다.');
		} catch (error) {
			setActionError(getErrorMessage(error, '실행 초안을 보류하지 못했습니다.'));
		} finally {
			setActionBusy(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="에이전트"
				description="조직별 AI 에이전트, 지식 연결, 공개 범위, 자동화 실행 안전장치를 관리합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{feedback ? (
				<div className="rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
					{feedback}
				</div>
			) : null}

			{actionError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{actionError}
				</div>
			) : null}

			{summary && data ? (
				<>
					<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
						<AgentStatCard
							title="전체 에이전트"
							value={summary.totalAgentCount}
							description={`전문가 ${numberLabel(summary.expertAgentCount)} · 커스텀 ${numberLabel(
								summary.customAgentCount
							)}`}
							icon={Bot}
						/>
						<AgentStatCard
							title="공개 범위"
							value={summary.scopedAgentCount}
							description={`권한 커버리지 ${numberLabel(summary.accessControlCoveragePercent)}%`}
							icon={ShieldCheck}
						/>
						<AgentStatCard
							title="지식 준비도"
							value={summary.knowledgeReadinessPercent}
							description={`${numberLabel(summary.enabledKnowledgeSourceCount)}/${numberLabel(
								summary.knowledgeSourceCount
							)} 소스 연결`}
							icon={FileText}
						/>
						<AgentStatCard
							title="승인 대기"
							value={summary.pendingHumanApprovalCount}
							description={`High 이상 제안 ${numberLabel(summary.highPrioritySuggestionCount)}건`}
							icon={AlertTriangle}
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>에이전트 운영</CardTitle>
							<CardDescription>
								커스텀 생성, 전문가 에이전트, 지식 제어, 우선순위 분류와 사람 승인 가드를
								점검합니다.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-5">
							<AgentChecklistGrid items={summary.checklist} />
							<AgentImpactPreviewPanel previews={summary.impactPreviews ?? []} />
							<div className="grid gap-4 xl:grid-cols-4">
								<AgentRiskList risks={summary.riskItems} />
								<AgentKnowledgeSourcesPanel sources={summary.knowledgeSources} />
								<AgentTaskSuggestionsPanel
									suggestions={summary.taskSuggestions}
									onCreateDraft={handleCreateActionDraft}
									busy={actionBusy}
								/>
								<AgentPermissionPanel scopes={summary.permissionScopes} />
							</div>
						</CardContent>
					</Card>

					<AgentActionDraftsPanel
						drafts={data.actionDrafts}
						onApprove={handleApproveActionDraft}
						onExecute={handleExecuteActionDraft}
						onDismiss={handleDismissActionDraft}
						busy={actionBusy}
					/>

					<AgentCardsPanel agents={summary.agentCards} />

					<div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
						<AgentAutomationPanel flows={summary.automationFlows} />
						<div className="rounded-lg border border-border bg-muted/20 p-4">
							<div className="flex items-center gap-2">
								<BadgeCheck className="size-4 text-muted-foreground" />
								<p className="font-semibold">운영 연결</p>
							</div>
							<div className="mt-4 flex flex-wrap gap-2">
								<Badge variant="outline">
									외부 맥락 {numberLabel(summary.externalIntegrationCount)}
								</Badge>
								<Badge variant="outline">자동화 {numberLabel(summary.automationFlowCount)}</Badge>
								<Badge variant={summary.localOnlyArtifactCount > 0 ? 'warning' : 'success'}>
									로컬 전용 {numberLabel(summary.localOnlyArtifactCount)}
								</Badge>
								<Badge variant="outline">활성 {numberLabel(summary.activeAgentCount)}</Badge>
							</div>
						</div>
					</div>
				</>
			) : null}
		</div>
	);
}

function securityStatusLabel(status: string): string {
	const labels: Record<string, string> = {
		READY: '준비',
		WATCH: '점검',
		WARNING: '주의'
	};
	return labels[status] ?? status;
}

function securityStatusVariant(
	status: SecurityComplianceStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING' || status === 'WATCH') return 'warning';
	return 'outline';
}

function securityRiskVariant(
	severity: SecurityRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function SecurityStatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: number | string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">
						{typeof value === 'number' ? numberLabel(value) : value}
					</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

function SecurityChecklistPanel({ items }: { items: SecurityChecklistItem[] }) {
	return (
		<div className="grid gap-3 md:grid-cols-2">
			{items.map((item) => {
				const Icon =
					item.status === 'READY'
						? CheckCircle2
						: item.status === 'WARNING'
							? AlertTriangle
							: Clock3;
				return (
					<div key={item.code} className="rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="flex min-w-0 items-start gap-3">
								<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
									<Icon className="size-4" />
								</div>
								<div className="min-w-0">
									<p className="font-semibold">{item.label}</p>
									<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
								</div>
							</div>
							<Badge variant={securityStatusVariant(item.status)}>
								{securityStatusLabel(item.status)}
							</Badge>
						</div>
					</div>
				);
			})}
		</div>
	);
}

function SecurityRiskPanel({ risks }: { risks: SecurityRiskItem[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">보안 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={securityRiskVariant(risk.severity)}>{numberLabel(risk.count)}</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function SecurityAccessPanel({ roles }: { roles: SecurityAccessRoleSummary[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<UsersRound className="size-4 text-muted-foreground" />
				<p className="font-semibold">권한 분포</p>
			</div>
			<div className="mt-4 space-y-4">
				{roles.map((role) => (
					<div key={role.role}>
						<div className="flex items-start justify-between gap-3">
							<div>
								<p className="font-medium">{role.label}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									활성 {numberLabel(role.activeUserCount)} · 비활성{' '}
									{numberLabel(role.inactiveUserCount)}
								</p>
							</div>
							<Badge variant={securityStatusVariant(role.status)}>
								{numberLabel(role.sharePercent)}%
							</Badge>
						</div>
						<div className="mt-2 h-2 rounded-full bg-muted">
							<div
								className="h-2 rounded-full bg-primary"
								style={{ width: `${Math.max(0, Math.min(100, role.sharePercent))}%` }}
							/>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function SecurityControlPanel({ controls }: { controls: SecurityControlItem[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<ShieldCheck className="size-4 text-muted-foreground" />
				<p className="font-semibold">보안 통제</p>
			</div>
			<div className="mt-4 space-y-3">
				{controls.map((control) => (
					<div key={control.code}>
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="font-medium">{control.label}</p>
								<p className="mt-1 text-sm text-muted-foreground">{control.description}</p>
							</div>
							<Badge variant={securityStatusVariant(control.status)}>
								{numberLabel(control.coveragePercent)}%
							</Badge>
						</div>
						<div className="mt-2 h-2 rounded-full bg-muted">
							<div
								className="h-2 rounded-full bg-primary"
								style={{ width: `${Math.max(0, Math.min(100, control.coveragePercent))}%` }}
							/>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function SecurityRecentEventsPanel({ events }: { events: SecurityRecentEvent[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<FileText className="size-4 text-muted-foreground" />
				<p className="font-semibold">최근 감사 이벤트</p>
			</div>
			<div className="mt-4 space-y-3">
				{events.length > 0 ? (
					events.map((event) => (
						<div
							key={`${event.action}-${event.entityType}-${event.entityId ?? 'none'}-${event.occurredAt ?? 'none'}`}
							className="rounded-md bg-muted/40 p-3"
						>
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="font-medium">{event.action}</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{event.entityType} #{event.entityId ?? '-'} · {event.actorName ?? '알 수 없음'}
									</p>
								</div>
								<Badge variant="outline">{dateLabel(event.occurredAt)}</Badge>
							</div>
							<p className="mt-2 text-xs text-muted-foreground">
								{timeLabel(event.occurredAt)} · {event.ipAddress ?? 'IP 없음'}
							</p>
						</div>
					))
				) : (
					<EmptyState message="최근 감사 이벤트가 없습니다." />
				)}
			</div>
		</div>
	);
}

function SecurityEvidencePanel({ evidenceItems }: { evidenceItems: SecurityEvidenceItem[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<BadgeCheck className="size-4 text-muted-foreground" />
				<p className="font-semibold">운영 증거</p>
			</div>
			<div className="mt-4 space-y-3">
				{evidenceItems.map((item) => (
					<div key={item.code} className="rounded-md bg-muted/40 p-3">
						<p className="font-medium">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				))}
			</div>
		</div>
	);
}

export function SecurityCompliancePage() {
	const [state, setState] = useState<RequestState<SecurityComplianceSummaryResponse>>({
		status: 'idle'
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const summary = await api.get<SecurityComplianceSummaryResponse>(
					'/security/operations/summary'
				);
				setState({ status: 'success', data: summary });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="보안"
				description="권한, 세션, 감사 로그, 민감정보 보호와 보안 통제 상태를 점검합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
						<SecurityStatCard
							title="보안 점수"
							value={data.complianceScorePercent}
							description={`구독 ${data.subscriptionStatus}`}
							icon={ShieldCheck}
						/>
						<SecurityStatCard
							title="활성 사용자"
							value={data.activeUserCount}
							description={`관리 권한 ${numberLabel(data.ownerCount + data.adminUserCount)}명`}
							icon={UsersRound}
						/>
						<SecurityStatCard
							title="감사 로그"
							value={data.auditLogCount30d}
							description={`30일 커버리지 ${numberLabel(data.auditCoveragePercent)}%`}
							icon={FileText}
						/>
						<SecurityStatCard
							title="인사정보 조회"
							value={data.profileViewEventCount30d}
							description={`최근 30일 ${numberLabel(data.profileViewEventCount30d)}건`}
							icon={Eye}
						/>
						<SecurityStatCard
							title="활성 세션"
							value={data.activeSessionCount}
							description={`비활성 계정 ${numberLabel(data.inactiveUserCount)}명`}
							icon={Clock3}
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>보안 운영</CardTitle>
							<CardDescription>
								테넌트 격리, 권한 계층, 감사 추적, 세션 폐기, 로그인 보호와 민감정보 reveal guard를
								확인합니다.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-5">
							<SecurityChecklistPanel items={data.checklist} />
							<div className="grid gap-4 xl:grid-cols-3">
								<SecurityRiskPanel risks={data.riskItems} />
								<SecurityAccessPanel roles={data.accessRoles} />
								<SecurityControlPanel controls={data.controlItems} />
							</div>
						</CardContent>
					</Card>

					<div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
						<SecurityRecentEventsPanel events={data.recentEvents} />
						<SecurityEvidencePanel evidenceItems={data.evidenceItems} />
					</div>

					<div className="flex flex-wrap gap-2 rounded-lg border border-border bg-muted/20 p-4">
						<Badge variant="outline">고위험 {numberLabel(data.highRiskEventCount30d)}</Badge>
						<Badge variant="outline">
							민감정보 {numberLabel(data.sensitiveAccessEventCount30d)}
						</Badge>
						<Badge variant="outline">
							임퍼소네이션 {numberLabel(data.impersonationEventCount30d)}
						</Badge>
						<Badge variant="outline">관리 권한 {numberLabel(data.privilegedAccessPercent)}%</Badge>
					</div>
				</>
			) : null}
		</div>
	);
}

function openApiClientStatusLabel(status: OpenApiClientStatus): string {
	const labels: Record<OpenApiClientStatus, string> = {
		ACTIVE: '활성',
		REVOKED: '폐기'
	};
	return labels[status];
}

function openApiClientStatusVariant(status: OpenApiClientStatus): 'success' | 'outline' {
	return status === 'ACTIVE' ? 'success' : 'outline';
}

function parseOpenApiListInput(value: string): string[] {
	return Array.from(
		new Set(
			value
				.split(/[\n,]+/)
				.map((item) => item.trim())
				.filter(Boolean)
		)
	);
}

function OpenApiStatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="flex min-h-32 flex-col justify-between p-4">
				<div className="flex items-center justify-between gap-3">
					<p className="text-xs font-medium text-muted-foreground">{title}</p>
					<div className="rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
				</div>
				<div>
					<p className="text-2xl font-semibold tracking-normal">{value}</p>
					<p className="mt-1 text-xs text-muted-foreground">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}

export function OpenApiClientsPage() {
	const [state, setState] = useState<RequestState<OpenApiClientResponse[]>>({
		status: 'idle'
	});
	const [form, setForm] = useState({
		name: '',
		allowedIpRangesText: '',
		scopesText: ''
	});
	const [formError, setFormError] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [actionClientId, setActionClientId] = useState<string | null>(null);
	const [secretNotice, setSecretNotice] = useState<OpenApiClientResponse | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const clients = await api.get<OpenApiClientResponse[]>('/open-api/clients');
				setState({ status: 'success', data: clients });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const clients = state.status === 'success' ? state.data : [];
	const activeClients = clients.filter((client) => client.status === 'ACTIVE');
	const revokedClients = clients.filter((client) => client.status === 'REVOKED');
	const allowedIpCount = new Set(clients.flatMap((client) => client.allowedIpRanges)).size;
	const scopeCount = new Set(clients.flatMap((client) => client.scopes)).size;

	function mergeClient(nextClient: OpenApiClientResponse) {
		setState((current) => {
			const currentData = current.status === 'success' ? current.data : [];
			const exists = currentData.some((client) => client.id === nextClient.id);
			return {
				status: 'success',
				data: exists
					? currentData.map((client) => (client.id === nextClient.id ? nextClient : client))
					: [nextClient, ...currentData]
			};
		});
	}

	async function handleCreate(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const name = form.name.trim();
		const allowedIpRanges = parseOpenApiListInput(form.allowedIpRangesText);
		const scopes = parseOpenApiListInput(form.scopesText);

		if (!name) {
			setFormError('클라이언트 이름을 입력해주세요.');
			return;
		}
		if (allowedIpRanges.length === 0) {
			setFormError('접근 허용 IP를 1개 이상 입력해주세요.');
			return;
		}
		if (scopes.length === 0) {
			setFormError('권한 범위를 1개 이상 입력해주세요.');
			return;
		}

		const request: CreateOpenApiClientRequest = {
			name,
			allowedIpRanges,
			scopes
		};

		setSubmitting(true);
		setFormError(null);
		setActionError(null);
		try {
			const created = await api.post<OpenApiClientResponse>('/open-api/clients', request);
			mergeClient(created);
			setSecretNotice(created);
			setForm({ name: '', allowedIpRangesText: '', scopesText: '' });
		} catch (error) {
			setFormError(getErrorMessage(error));
		} finally {
			setSubmitting(false);
		}
	}

	async function handleRotate(client: OpenApiClientResponse) {
		setActionClientId(`rotate-${client.id}`);
		setActionError(null);
		try {
			const updated = await api.post<OpenApiClientResponse>(
				`/open-api/clients/${client.id}/rotate-secret`
			);
			mergeClient(updated);
			setSecretNotice(updated);
		} catch (error) {
			setActionError(getErrorMessage(error));
		} finally {
			setActionClientId(null);
		}
	}

	async function handleRevoke(client: OpenApiClientResponse) {
		setActionClientId(`revoke-${client.id}`);
		setActionError(null);
		try {
			await api.post<void>(`/open-api/clients/${client.id}/revoke`);
			mergeClient({ ...client, status: 'REVOKED', revokedAt: new Date().toISOString() });
		} catch (error) {
			setActionError(getErrorMessage(error));
		} finally {
			setActionClientId(null);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="Open API"
				description="외부 HRIS, 급여, 근태 기기 연동용 Client Credential, 권한 범위, 접근 허용 IP를 관리합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{state.status === 'success' ? (
				<>
					<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
						<OpenApiStatCard
							title="활성 Credential"
							value={numberLabel(activeClients.length)}
							description={`폐기 ${numberLabel(revokedClients.length)}개`}
							icon={ShieldCheck}
						/>
						<OpenApiStatCard
							title="접근 허용 IP"
							value={numberLabel(allowedIpCount)}
							description="단일 IP와 CIDR 범위를 허용"
							icon={Clock3}
						/>
						<OpenApiStatCard
							title="권한 범위"
							value={numberLabel(scopeCount)}
							description="연동별 최소 권한을 분리"
							icon={GitBranch}
						/>
						<OpenApiStatCard
							title="최근 발급"
							value={clients.length > 0 ? dateLabel(clients[0]?.createdAt) : '-'}
							description="가장 최근 생성일 기준"
							icon={FileText}
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>Credential 생성</CardTitle>
							<CardDescription>
								secret은 생성 또는 회전 직후 한 번만 표시됩니다. 외부 시스템 서버 IP만 허용 목록에
								등록하세요.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<form className="space-y-4" onSubmit={handleCreate}>
								<FormError message={formError} />
								<div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
									<div className="space-y-2">
										<Label htmlFor="open-api-client-name">클라이언트 이름</Label>
										<Input
											id="open-api-client-name"
											value={form.name}
											placeholder="Payroll Connector"
											onChange={(event) =>
												setForm((current) => ({ ...current, name: event.target.value }))
											}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="open-api-scopes">권한 범위</Label>
										<Input
											id="open-api-scopes"
											value={form.scopesText}
											placeholder="employees:read, attendance:read"
											onChange={(event) =>
												setForm((current) => ({ ...current, scopesText: event.target.value }))
											}
										/>
									</div>
								</div>
								<div className="space-y-2">
									<Label htmlFor="open-api-allowed-ips">접근 허용 IP</Label>
									<textarea
										id="open-api-allowed-ips"
										className={dialogTextAreaClassName}
										value={form.allowedIpRangesText}
										placeholder={'203.0.113.10\n198.51.100.0/24'}
										onChange={(event) =>
											setForm((current) => ({
												...current,
												allowedIpRangesText: event.target.value
											}))
										}
									/>
									<p className="text-xs text-muted-foreground">
										쉼표 또는 줄바꿈으로 여러 IP/CIDR을 입력할 수 있습니다.
									</p>
								</div>
								<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
									<p className="text-xs text-muted-foreground">
										Client ID와 secret은 외부 연동 서버에만 저장하고 브라우저에 재노출하지 않습니다.
									</p>
									<Button type="submit" disabled={submitting}>
										<Plus className="size-4" />
										Credential 생성
									</Button>
								</div>
							</form>

							{secretNotice?.oneTimeClientSecret ? (
								<div className="mt-4 rounded-lg border border-success/30 bg-success/10 p-4">
									<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
										<div>
											<p className="text-sm font-semibold text-success">1회용 Client Secret</p>
											<p className="mt-1 text-xs text-muted-foreground">
												{secretNotice.name} 연동 서버에 지금 저장하세요. 이후에는 preview만
												표시됩니다.
											</p>
										</div>
										<Badge variant="outline">{secretNotice.clientId}</Badge>
									</div>
									<code className="mt-3 block overflow-x-auto rounded-md bg-background px-3 py-2 text-sm">
										{secretNotice.oneTimeClientSecret}
									</code>
								</div>
							) : null}
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Credential 목록</CardTitle>
							<CardDescription>
								연동별 권한 범위와 접근 허용 IP를 분리해 외부 호출 권한을 좁게 유지합니다.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							<FormError message={actionError} />
							{clients.length > 0 ? (
								<div className="overflow-hidden rounded-lg border border-border text-sm">
									<div className="hidden grid-cols-[1.15fr_1.25fr_1.25fr_0.85fr_0.7fr_1fr] gap-4 bg-muted/40 px-4 py-3 text-xs font-semibold text-muted-foreground md:grid">
										<span>클라이언트</span>
										<span>접근 허용 IP</span>
										<span>권한 범위</span>
										<span>최근 사용</span>
										<span>상태</span>
										<span className="text-right">작업</span>
									</div>
									<div className="divide-y divide-border">
										{clients.map((client) => (
											<div
												key={client.id}
												className="grid gap-4 p-4 md:grid-cols-[1.15fr_1.25fr_1.25fr_0.85fr_0.7fr_1fr]"
											>
												<div>
													<p className="mb-1 text-xs font-semibold text-muted-foreground md:hidden">
														클라이언트
													</p>
													<p className="font-medium">{client.name}</p>
													<p className="mt-1 font-mono text-xs text-muted-foreground">
														{client.clientId}
													</p>
													<p className="mt-1 text-xs text-muted-foreground">
														secret {client.secretPreview}
													</p>
												</div>
												<div>
													<p className="mb-1 text-xs font-semibold text-muted-foreground md:hidden">
														접근 허용 IP
													</p>
													<div className="flex flex-wrap gap-2">
														{client.allowedIpRanges.map((range) => (
															<Badge key={range} variant="outline">
																{range}
															</Badge>
														))}
													</div>
												</div>
												<div>
													<p className="mb-1 text-xs font-semibold text-muted-foreground md:hidden">
														권한 범위
													</p>
													<div className="flex flex-wrap gap-2">
														{client.scopes.map((scope) => (
															<Badge key={scope} variant="secondary">
																{scope}
															</Badge>
														))}
													</div>
												</div>
												<div className="text-muted-foreground">
													<p className="mb-1 text-xs font-semibold text-muted-foreground md:hidden">
														최근 사용
													</p>
													<p>{dateLabel(client.lastUsedAt)}</p>
													<p className="mt-1 text-xs">{timeLabel(client.lastUsedAt)}</p>
												</div>
												<div>
													<p className="mb-1 text-xs font-semibold text-muted-foreground md:hidden">
														상태
													</p>
													<Badge variant={openApiClientStatusVariant(client.status)}>
														{openApiClientStatusLabel(client.status)}
													</Badge>
													{client.revokedAt ? (
														<p className="mt-1 text-xs text-muted-foreground">
															폐기 {dateLabel(client.revokedAt)}
														</p>
													) : null}
												</div>
												<div>
													<p className="mb-1 text-xs font-semibold text-muted-foreground md:hidden">
														작업
													</p>
													<div className="flex flex-wrap gap-2 md:justify-end">
														<Button
															type="button"
															variant="outline"
															size="sm"
															className="whitespace-nowrap"
															disabled={
																client.status !== 'ACTIVE' ||
																actionClientId === `rotate-${client.id}`
															}
															onClick={() => void handleRotate(client)}
														>
															Secret 회전
														</Button>
														<Button
															type="button"
															variant="ghost"
															size="sm"
															className="whitespace-nowrap"
															disabled={
																client.status !== 'ACTIVE' ||
																actionClientId === `revoke-${client.id}`
															}
															onClick={() => void handleRevoke(client)}
														>
															폐기
														</Button>
													</div>
												</div>
											</div>
										))}
									</div>
								</div>
							) : (
								<EmptyState message="등록된 Open API Credential이 없습니다." />
							)}
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}

type DigitalContractsPageData = {
	summary: DigitalContractSummaryResponse;
	templates: DigitalContractTemplateResponse[];
	requests: DigitalContractRequestResponse[];
	employees: EmployeeResponse[];
};

type DigitalContractTemplateFieldForm = {
	key: string;
	label: string;
	type: DigitalContractTemplateFieldType;
	required: boolean;
	optionsText: string;
};

type DigitalContractTemplateForm = {
	name: string;
	description: string;
	contractType: ContractType;
	contentTemplate: string;
	fields: DigitalContractTemplateFieldForm[];
};

type DigitalContractRequestForm = {
	templateId: string;
	employeeId: string;
	title: string;
	contractType: ContractType;
	startedAt: string;
	endedAt: string;
	annualSalary: string;
	jobTitle: string;
	jobDescription: string;
	fieldValues: Record<string, string>;
};

type DigitalContractBulkRequestForm = {
	templateId: string;
	selectedEmployeeIds: number[];
	titlePrefix: string;
	contractType: ContractType;
	startedAt: string;
	endedAt: string;
	annualSalary: string;
	jobTitle: string;
	jobDescription: string;
	fieldValues: Record<string, string>;
};

const digitalContractTypeOptions: Array<{ value: ContractType; label: string }> = [
	{ value: 'PERMANENT', label: '정규직' },
	{ value: 'CONTRACT', label: '계약직' },
	{ value: 'PART_TIME', label: '파트타임' },
	{ value: 'INTERN', label: '인턴' },
	{ value: 'FREELANCE', label: '프리랜서' }
];

function contractTypeLabel(type: ContractType | string): string {
	return digitalContractTypeOptions.find((option) => option.value === type)?.label ?? type;
}

function defaultDigitalContractField(): DigitalContractTemplateFieldForm {
	return { key: '', label: '', type: 'TEXT', required: true, optionsText: '' };
}

function defaultDigitalContractTemplateContent(): string {
	return [
		'근로자 {{employeeName}}은 회사가 지정한 장소에서 {{workplace}} 직무를 수행합니다.',
		'계약 연봉은 {{annualSalary}}원이며 근로계약 조건은 본 전자계약 발송 내역을 따릅니다.'
	].join('\n');
}

function digitalContractStatusLabel(status: DigitalContractStatus): string {
	const labels: Record<DigitalContractStatus, string> = {
		SENT: '서명 대기',
		SIGNED: '서명 완료',
		CANCELLED: '취소',
		EXPIRED: '만료'
	};
	return labels[status];
}

function digitalContractStatusVariant(
	status: DigitalContractStatus
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'SIGNED') return 'success';
	if (status === 'SENT') return 'warning';
	if (status === 'CANCELLED') return 'destructive';
	return 'outline';
}

function digitalContractRiskVariant(
	severity: DigitalContractRiskSeverity | DigitalContractRiskLevel | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function digitalContractChecklistVariant(
	status: DigitalContractChecklistStatus | null | undefined
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function digitalContractChecklistLabel(status: DigitalContractChecklistStatus | null | undefined) {
	const labels: Record<DigitalContractChecklistStatus, string> = {
		READY: '정상',
		WARNING: '주의',
		REVIEW: '점검'
	};
	return status ? labels[status] : '점검';
}

function digitalContractFieldTypeLabel(type: DigitalContractTemplateFieldType): string {
	const labels: Record<DigitalContractTemplateFieldType, string> = {
		TEXT: '텍스트',
		NUMBER: '숫자',
		AMOUNT: '금액',
		DATE: '날짜',
		SELECT: '선택',
		EMPLOYEE: '직원'
	};
	return labels[type];
}

function digitalContractEmployeeSeed(
	employee: EmployeeResponse | null | undefined,
	annualSalary: string
): Record<string, string> {
	return {
		employeeName: employee?.name ?? '',
		employeeEmail: employee?.email ?? '',
		email: employee?.email ?? '',
		department: employee?.department ?? '',
		position: employee?.position ?? '',
		jobTitle: employee?.position ?? '',
		joinDate: employee?.joinDate ?? '',
		workplace: employee?.department ?? '',
		annualSalary
	};
}

function digitalContractInitialFieldValues(
	template: DigitalContractTemplateResponse | null | undefined,
	seed: Record<string, string> = {}
) {
	return Object.fromEntries(
		(template?.fields ?? []).map((field) => [field.key, seed[field.key] ?? ''])
	);
}

const digitalContractEmployeeAutoFieldKeys = new Set([
	'employeeName',
	'employeeEmail',
	'email',
	'department',
	'position',
	'jobTitle',
	'joinDate'
]);

function isDigitalContractEmployeeAutoField(key: string): boolean {
	return digitalContractEmployeeAutoFieldKeys.has(key);
}

function digitalContractBulkInitialFieldValues(
	template: DigitalContractTemplateResponse | null | undefined,
	annualSalary: string
) {
	const seed: Record<string, string> = {
		annualSalary,
		contractYear: new Date().getFullYear().toString()
	};
	return Object.fromEntries(
		(template?.fields ?? [])
			.filter((field) => !isDigitalContractEmployeeAutoField(field.key))
			.map((field) => [field.key, seed[field.key] ?? ''])
	);
}

function digitalContractFieldValueLabel(
	value: string | null | undefined,
	type: DigitalContractTemplateFieldType | null | undefined
): string {
	if (value == null || value === '') return '-';
	if (type === 'AMOUNT' || type === 'NUMBER') {
		const numeric = Number(value);
		if (Number.isFinite(numeric))
			return type === 'AMOUNT' ? wonLabel(numeric) : numberLabel(numeric);
	}
	return value;
}

function DigitalContractStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: string;
	description: string;
}) {
	return (
		<Card className="min-w-40 sm:min-w-0">
			<CardContent className="p-4">
				<p className="text-xs font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal">{value}</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function DigitalContractInsightRow({
	label,
	value,
	description,
	tone = 'default'
}: {
	label: string;
	value: string;
	description: string;
	tone?: 'default' | 'warning' | 'success';
}) {
	const valueClassName =
		tone === 'warning'
			? 'text-warning-foreground'
			: tone === 'success'
				? 'text-success'
				: 'text-foreground';
	return (
		<div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-background px-3 py-2.5">
			<div className="min-w-0">
				<p className="text-sm font-semibold">{label}</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</div>
			<p className={cn('shrink-0 text-lg font-semibold tracking-normal', valueClassName)}>
				{value}
			</p>
		</div>
	);
}

function DigitalContractOperationsPanel({ summary }: { summary: DigitalContractSummaryResponse }) {
	const checklist = summary.checklist ?? [];
	const riskItems = summary.riskItems ?? [];
	const pendingQueue = summary.pendingSignatureQueue ?? [];

	return (
		<Card>
			<CardHeader className="gap-3">
				<div className="flex items-start justify-between gap-3">
					<div className="space-y-1">
						<CardTitle>전자계약 운영 리스크</CardTitle>
						<CardDescription>
							{summary.riskSummary ?? '전자계약 서명 지연과 템플릿 준비 상태를 추적합니다.'}
						</CardDescription>
					</div>
					<Badge variant={digitalContractRiskVariant(summary.riskLevel)}>
						{summary.riskLabel ?? '점검'}
					</Badge>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="rounded-md border border-border p-3">
					<div className="flex items-center gap-2 text-sm font-semibold">
						<AlertTriangle className="size-4 text-muted-foreground" />
						리스크
					</div>
					<p className="mt-2 text-xs text-muted-foreground">
						{summary.recommendedAction ?? '계약 템플릿과 서명 대기 상태를 확인하세요.'}
					</p>
					<div className="mt-3 space-y-2">
						{riskItems.map((item) => (
							<div
								key={item.code}
								className="flex items-center justify-between gap-3 rounded-md bg-muted/25 px-3 py-2"
							>
								<div className="min-w-0">
									<p className="truncate text-sm font-semibold">{item.title}</p>
									<p className="mt-1 text-xs text-muted-foreground">{item.actionLabel}</p>
								</div>
								<div className="flex shrink-0 items-center gap-2">
									<span className="text-sm font-semibold">{numberLabel(item.count)}건</span>
									<Badge variant={digitalContractRiskVariant(item.severity)}>{item.severity}</Badge>
								</div>
							</div>
						))}
					</div>
				</div>

				<div className="rounded-md border border-border p-3">
					<div className="flex items-center gap-2 text-sm font-semibold">
						<Clock3 className="size-4 text-muted-foreground" />
						오래된 서명 대기열
					</div>
					<div className="mt-3 space-y-2">
						{pendingQueue.length > 0 ? (
							pendingQueue.map((item) => (
								<div key={item.id} className="rounded-md bg-muted/25 px-3 py-2">
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="truncate text-sm font-semibold">{item.title}</p>
											<p className="mt-1 text-xs text-muted-foreground">
												{item.employeeName ?? `직원 #${item.employeeId}`} ·{' '}
												{item.templateName ?? `템플릿 #${item.templateId}`}
											</p>
										</div>
										<Badge variant={item.stale ? 'warning' : 'outline'}>
											{numberLabel(item.pendingDays)}일 대기
										</Badge>
									</div>
								</div>
							))
						) : (
							<EmptyState message="서명 대기 중인 계약이 없습니다." />
						)}
					</div>
				</div>

				<div className="rounded-md border border-border p-3">
					<p className="text-sm font-semibold">운영 체크</p>
					<div className="mt-3 space-y-2">
						{checklist.map((item) => (
							<div key={item.code} className="rounded-md bg-muted/25 p-3">
								<div className="flex items-start justify-between gap-2">
									<p className="text-sm font-semibold">{item.label}</p>
									<Badge variant={digitalContractChecklistVariant(item.status)}>
										{digitalContractChecklistLabel(item.status)}
									</Badge>
								</div>
								<p className="mt-2 text-xs text-muted-foreground">{item.description}</p>
							</div>
						))}
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function DigitalContractTemplateDialog({
	onClose,
	onSubmitted
}: {
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [form, setForm] = useState<DigitalContractTemplateForm>({
		name: '표준 근로계약서',
		description: '입사와 계약 갱신에 사용하는 기본 전자계약 양식',
		contractType: 'PERMANENT',
		contentTemplate: defaultDigitalContractTemplateContent(),
		fields: [
			{ key: 'employeeName', label: '근로자명', type: 'TEXT', required: true, optionsText: '' },
			{ key: 'annualSalary', label: '연봉', type: 'AMOUNT', required: true, optionsText: '' },
			{ key: 'workplace', label: '근무 장소', type: 'TEXT', required: true, optionsText: '' }
		]
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	function updateField(index: number, next: Partial<DigitalContractTemplateFieldForm>) {
		setForm((current) => ({
			...current,
			fields: current.fields.map((field, fieldIndex) =>
				fieldIndex === index ? { ...field, ...next } : field
			)
		}));
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const name = form.name.trim();
		const contentTemplate = form.contentTemplate.trim();
		if (!name) {
			setError('템플릿명을 입력해주세요.');
			return;
		}
		if (!contentTemplate) {
			setError('계약서 본문을 입력해주세요.');
			return;
		}
		if (
			form.fields.length === 0 ||
			form.fields.some((field) => !field.key.trim() || !field.label.trim())
		) {
			setError('계약 필드 키와 라벨을 모두 입력해주세요.');
			return;
		}

		const request: CreateDigitalContractTemplateRequest = {
			name,
			description: form.description.trim() || undefined,
			contractType: form.contractType,
			contentTemplate,
			fields: form.fields.map((field) => ({
				key: field.key.trim(),
				label: field.label.trim(),
				type: field.type,
				required: field.required,
				options: field.optionsText
					.split(',')
					.map((option) => option.trim())
					.filter(Boolean)
			}))
		};

		setSubmitting(true);
		try {
			await api.post<DigitalContractTemplateResponse>('/digital-contracts/templates', request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="전자계약 템플릿 만들기"
			description="계약 유형, 본문 치환 키, 필수 입력 필드를 설정합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-template-name">템플릿명</Label>
						<Input
							id="digital-contract-template-name"
							value={form.name}
							onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-template-type">계약 유형</Label>
						<select
							id="digital-contract-template-type"
							value={form.contractType}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									contractType: event.target.value as ContractType
								}))
							}
							className={dialogControlClassName}
						>
							{digitalContractTypeOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="digital-contract-template-description">설명</Label>
					<Input
						id="digital-contract-template-description"
						value={form.description}
						onChange={(event) =>
							setForm((current) => ({ ...current, description: event.target.value }))
						}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="digital-contract-template-content">계약서 본문</Label>
					<textarea
						id="digital-contract-template-content"
						value={form.contentTemplate}
						onChange={(event) =>
							setForm((current) => ({ ...current, contentTemplate: event.target.value }))
						}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="space-y-3">
					<div className="flex items-center justify-between gap-3">
						<Label>계약 필드</Label>
						<Button
							type="button"
							size="sm"
							variant="outline"
							onClick={() =>
								setForm((current) => ({
									...current,
									fields: [...current.fields, defaultDigitalContractField()]
								}))
							}
						>
							<Plus className="size-4" />
							필드 추가
						</Button>
					</div>
					{form.fields.map((field, index) => (
						<div key={`${field.key}-${index}`} className="rounded-lg border border-border p-3">
							<div className="grid gap-3 md:grid-cols-[1fr_1fr_9rem_5rem_auto]">
								<Input
									value={field.key}
									onChange={(event) => updateField(index, { key: event.target.value })}
									placeholder="key"
									aria-label={`계약 필드 ${index + 1} 키`}
								/>
								<Input
									value={field.label}
									onChange={(event) => updateField(index, { label: event.target.value })}
									placeholder="라벨"
									aria-label={`계약 필드 ${index + 1} 라벨`}
								/>
								<select
									value={field.type}
									onChange={(event) =>
										updateField(index, {
											type: event.target.value as DigitalContractTemplateFieldType
										})
									}
									className={dialogControlClassName}
									aria-label={`계약 필드 ${index + 1} 타입`}
								>
									{(['TEXT', 'NUMBER', 'AMOUNT', 'DATE', 'SELECT', 'EMPLOYEE'] as const).map(
										(type) => (
											<option key={type} value={type}>
												{digitalContractFieldTypeLabel(type)}
											</option>
										)
									)}
								</select>
								<label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
									<input
										type="checkbox"
										checked={field.required}
										onChange={(event) => updateField(index, { required: event.target.checked })}
									/>
									필수
								</label>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() =>
										setForm((current) => ({
											...current,
											fields: current.fields.filter((_, fieldIndex) => fieldIndex !== index)
										}))
									}
									disabled={form.fields.length <= 1}
								>
									삭제
								</Button>
							</div>
							{field.type === 'SELECT' ? (
								<Input
									value={field.optionsText}
									onChange={(event) => updateField(index, { optionsText: event.target.value })}
									placeholder="선택지, 쉼표로 구분"
									className="mt-3"
									aria-label={`계약 필드 ${index + 1} 선택지`}
								/>
							) : null}
						</div>
					))}
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						저장
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function DigitalContractRequestDialog({
	templates,
	employees,
	onClose,
	onSubmitted
}: {
	templates: DigitalContractTemplateResponse[];
	employees: EmployeeResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const firstTemplate = templates[0];
	const firstEmployee = employees.find((employee) => employee.status === 'ACTIVE') ?? employees[0];
	const [form, setForm] = useState<DigitalContractRequestForm>({
		templateId: firstTemplate ? String(firstTemplate.id) : '',
		employeeId: firstEmployee ? String(firstEmployee.id) : '',
		title: firstEmployee ? `${firstEmployee.name} 근로계약서` : '',
		contractType: firstTemplate?.contractType ?? 'PERMANENT',
		startedAt: new Date().toISOString().slice(0, 10),
		endedAt: '',
		annualSalary: '',
		jobTitle: firstEmployee?.position ?? '',
		jobDescription: '',
		fieldValues: digitalContractInitialFieldValues(
			firstTemplate,
			digitalContractEmployeeSeed(firstEmployee, '')
		)
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const selectedTemplate = useMemo(
		() => templates.find((template) => String(template.id) === form.templateId) ?? null,
		[form.templateId, templates]
	);
	const selectedEmployee = useMemo(
		() => employees.find((employee) => String(employee.id) === form.employeeId) ?? null,
		[form.employeeId, employees]
	);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!selectedTemplate) {
			setError('발송할 템플릿을 선택해주세요.');
			return;
		}
		if (!selectedEmployee) {
			setError('계약 대상 직원을 선택해주세요.');
			return;
		}
		if (!form.title.trim()) {
			setError('계약 제목을 입력해주세요.');
			return;
		}
		if (!form.startedAt) {
			setError('계약 시작일을 입력해주세요.');
			return;
		}
		const missingField = selectedTemplate.fields.find(
			(field) => field.required && !form.fieldValues[field.key]?.trim()
		);
		if (missingField) {
			setError(`${missingField.label} 필드를 입력해주세요.`);
			return;
		}
		const salary = form.annualSalary.trim() ? Number(form.annualSalary) : undefined;
		if (salary !== undefined && !Number.isFinite(salary)) {
			setError('연봉은 숫자로 입력해주세요.');
			return;
		}

		const request: CreateDigitalContractRequest = {
			templateId: selectedTemplate.id,
			employeeId: selectedEmployee.id,
			title: form.title.trim(),
			contractType: form.contractType,
			startedAt: form.startedAt,
			endedAt: form.endedAt || undefined,
			annualSalary: salary,
			jobTitle: form.jobTitle.trim() || undefined,
			jobDescription: form.jobDescription.trim() || undefined,
			fieldValues: selectedTemplate.fields.map((field) => ({
				key: field.key,
				value: form.fieldValues[field.key] ?? ''
			}))
		};

		setSubmitting(true);
		try {
			await api.post<DigitalContractRequestResponse>('/digital-contracts/requests', request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="계약 발송"
			description="직원 정보와 계약 조건을 입력해 전자서명을 요청합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-request-template">템플릿</Label>
						<select
							id="digital-contract-request-template"
							value={form.templateId}
							onChange={(event) =>
								setForm((current) => {
									const nextTemplate =
										templates.find((template) => String(template.id) === event.target.value) ??
										null;
									return {
										...current,
										templateId: event.target.value,
										contractType: nextTemplate?.contractType ?? current.contractType,
										fieldValues: digitalContractInitialFieldValues(
											nextTemplate,
											digitalContractEmployeeSeed(selectedEmployee, current.annualSalary)
										)
									};
								})
							}
							className={dialogControlClassName}
						>
							{templates.map((template) => (
								<option key={template.id} value={template.id}>
									{template.name}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-request-employee">직원</Label>
						<select
							id="digital-contract-request-employee"
							value={form.employeeId}
							onChange={(event) =>
								setForm((current) => {
									const nextEmployee =
										employees.find((employee) => String(employee.id) === event.target.value) ??
										null;
									return {
										...current,
										employeeId: event.target.value,
										title: current.title || (nextEmployee ? `${nextEmployee.name} 근로계약서` : ''),
										jobTitle: current.jobTitle || nextEmployee?.position || '',
										fieldValues: {
											...current.fieldValues,
											...digitalContractEmployeeSeed(nextEmployee, current.annualSalary)
										}
									};
								})
							}
							className={dialogControlClassName}
						>
							{employees.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="digital-contract-request-title">계약 제목</Label>
					<Input
						id="digital-contract-request-title"
						value={form.title}
						onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
					/>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-request-type">계약 유형</Label>
						<select
							id="digital-contract-request-type"
							value={form.contractType}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									contractType: event.target.value as ContractType
								}))
							}
							className={dialogControlClassName}
						>
							{digitalContractTypeOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-request-salary">연봉</Label>
						<Input
							id="digital-contract-request-salary"
							type="number"
							min="0"
							value={form.annualSalary}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									annualSalary: event.target.value,
									fieldValues: { ...current.fieldValues, annualSalary: event.target.value }
								}))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-request-started-at">시작일</Label>
						<Input
							id="digital-contract-request-started-at"
							type="date"
							value={form.startedAt}
							onChange={(event) =>
								setForm((current) => ({ ...current, startedAt: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-request-ended-at">종료일</Label>
						<Input
							id="digital-contract-request-ended-at"
							type="date"
							value={form.endedAt}
							onChange={(event) =>
								setForm((current) => ({ ...current, endedAt: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-request-job-title">직무</Label>
						<Input
							id="digital-contract-request-job-title"
							value={form.jobTitle}
							onChange={(event) =>
								setForm((current) => ({ ...current, jobTitle: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-request-job-description">직무 설명</Label>
						<Input
							id="digital-contract-request-job-description"
							value={form.jobDescription}
							onChange={(event) =>
								setForm((current) => ({ ...current, jobDescription: event.target.value }))
							}
						/>
					</div>
				</div>
				{selectedTemplate ? (
					<div className="grid gap-4 sm:grid-cols-2">
						{selectedTemplate.fields.map((field) => (
							<div key={field.key} className="space-y-2">
								<Label htmlFor={`digital-contract-field-${field.key}`}>
									{field.label}
									{field.required ? ' *' : ''}
								</Label>
								{field.type === 'SELECT' && field.options?.length ? (
									<select
										id={`digital-contract-field-${field.key}`}
										value={form.fieldValues[field.key] ?? ''}
										onChange={(event) =>
											setForm((current) => ({
												...current,
												fieldValues: { ...current.fieldValues, [field.key]: event.target.value }
											}))
										}
										className={dialogControlClassName}
									>
										<option value="">선택</option>
										{field.options.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>
								) : field.type === 'EMPLOYEE' ? (
									<select
										id={`digital-contract-field-${field.key}`}
										value={form.fieldValues[field.key] ?? ''}
										onChange={(event) =>
											setForm((current) => ({
												...current,
												fieldValues: { ...current.fieldValues, [field.key]: event.target.value }
											}))
										}
										className={dialogControlClassName}
									>
										<option value="">선택</option>
										{employees.map((employee) => (
											<option key={employee.id} value={employee.name}>
												{employee.name}
											</option>
										))}
									</select>
								) : (
									<Input
										id={`digital-contract-field-${field.key}`}
										type={
											field.type === 'DATE'
												? 'date'
												: field.type === 'AMOUNT' || field.type === 'NUMBER'
													? 'number'
													: 'text'
										}
										value={form.fieldValues[field.key] ?? ''}
										onChange={(event) =>
											setForm((current) => ({
												...current,
												fieldValues: { ...current.fieldValues, [field.key]: event.target.value }
											}))
										}
									/>
								)}
							</div>
						))}
					</div>
				) : null}
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button
						type="submit"
						disabled={submitting || templates.length === 0 || employees.length === 0}
					>
						발송
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function DigitalContractBulkRequestDialog({
	templates,
	employees,
	onClose,
	onSubmitted
}: {
	templates: DigitalContractTemplateResponse[];
	employees: EmployeeResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const firstTemplate = templates[0];
	const year = new Date().getFullYear().toString();
	const [form, setForm] = useState<DigitalContractBulkRequestForm>({
		templateId: firstTemplate ? String(firstTemplate.id) : '',
		selectedEmployeeIds: employees.slice(0, 100).map((employee) => employee.id),
		titlePrefix: `${year} 근로계약서`,
		contractType: firstTemplate?.contractType ?? 'PERMANENT',
		startedAt: new Date().toISOString().slice(0, 10),
		endedAt: '',
		annualSalary: '',
		jobTitle: '',
		jobDescription: '',
		fieldValues: digitalContractBulkInitialFieldValues(firstTemplate, '')
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const selectedTemplate = useMemo(
		() => templates.find((template) => String(template.id) === form.templateId) ?? null,
		[form.templateId, templates]
	);
	const selectedEmployeeIds = new Set(form.selectedEmployeeIds);
	const selectedEmployees = employees.filter((employee) => selectedEmployeeIds.has(employee.id));
	const commonFields = (selectedTemplate?.fields ?? []).filter(
		(field) => !isDigitalContractEmployeeAutoField(field.key)
	);
	const allVisibleSelected =
		employees.length > 0 && employees.every((employee) => selectedEmployeeIds.has(employee.id));

	function toggleEmployee(employeeId: number, checked: boolean) {
		setForm((current) => ({
			...current,
			selectedEmployeeIds: checked
				? Array.from(new Set([...current.selectedEmployeeIds, employeeId]))
				: current.selectedEmployeeIds.filter((id) => id !== employeeId)
		}));
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!selectedTemplate) {
			setError('발송할 템플릿을 선택해주세요.');
			return;
		}
		if (form.selectedEmployeeIds.length === 0) {
			setError('계약 대상 직원을 선택해주세요.');
			return;
		}
		if (!form.titlePrefix.trim()) {
			setError('계약 제목 접두어를 입력해주세요.');
			return;
		}
		if (!form.startedAt) {
			setError('계약 시작일을 입력해주세요.');
			return;
		}
		const missingField = commonFields.find(
			(field) => field.required && !form.fieldValues[field.key]?.trim()
		);
		if (missingField) {
			setError(`${missingField.label} 필드를 입력해주세요.`);
			return;
		}
		const salary = form.annualSalary.trim() ? Number(form.annualSalary) : undefined;
		if (salary !== undefined && !Number.isFinite(salary)) {
			setError('연봉은 숫자로 입력해주세요.');
			return;
		}

		const request: CreateDigitalContractBulkRequest = {
			templateId: selectedTemplate.id,
			employeeIds: form.selectedEmployeeIds,
			titlePrefix: form.titlePrefix.trim(),
			contractType: form.contractType,
			startedAt: form.startedAt,
			endedAt: form.endedAt || undefined,
			annualSalary: salary,
			jobTitle: form.jobTitle.trim() || undefined,
			jobDescription: form.jobDescription.trim() || undefined,
			fieldValues: commonFields.map((field) => ({
				key: field.key,
				value: form.fieldValues[field.key] ?? ''
			}))
		};

		setSubmitting(true);
		try {
			await api.post<DigitalContractRequestResponse[]>('/digital-contracts/requests/bulk', request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="계약 대량 발송"
			description="선택한 직원별 자동입력값을 채워 전자서명 요청을 한 번에 생성합니다."
			onClose={onClose}
		>
			<form className="space-y-5" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="flex flex-wrap items-center gap-2">
					<Badge variant="secondary">{numberLabel(form.selectedEmployeeIds.length)}명 선택</Badge>
					<Badge variant="outline">직원명 · 이메일 · 부서 · 직무 · 입사일 자동입력</Badge>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-bulk-template">템플릿</Label>
						<select
							id="digital-contract-bulk-template"
							value={form.templateId}
							onChange={(event) =>
								setForm((current) => {
									const nextTemplate =
										templates.find((template) => String(template.id) === event.target.value) ??
										null;
									return {
										...current,
										templateId: event.target.value,
										contractType: nextTemplate?.contractType ?? current.contractType,
										fieldValues: digitalContractBulkInitialFieldValues(
											nextTemplate,
											current.annualSalary
										)
									};
								})
							}
							className={dialogControlClassName}
						>
							{templates.map((template) => (
								<option key={template.id} value={template.id}>
									{template.name}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-bulk-title">계약 제목 접두어</Label>
						<Input
							id="digital-contract-bulk-title"
							value={form.titlePrefix}
							onChange={(event) =>
								setForm((current) => ({ ...current, titlePrefix: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-bulk-type">계약 유형</Label>
						<select
							id="digital-contract-bulk-type"
							value={form.contractType}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									contractType: event.target.value as ContractType
								}))
							}
							className={dialogControlClassName}
						>
							{digitalContractTypeOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-bulk-salary">공통 연봉</Label>
						<Input
							id="digital-contract-bulk-salary"
							type="number"
							min="0"
							value={form.annualSalary}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									annualSalary: event.target.value,
									fieldValues: { ...current.fieldValues, annualSalary: event.target.value }
								}))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-bulk-started-at">시작일</Label>
						<Input
							id="digital-contract-bulk-started-at"
							type="date"
							value={form.startedAt}
							onChange={(event) =>
								setForm((current) => ({ ...current, startedAt: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-bulk-ended-at">종료일</Label>
						<Input
							id="digital-contract-bulk-ended-at"
							type="date"
							value={form.endedAt}
							onChange={(event) =>
								setForm((current) => ({ ...current, endedAt: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-bulk-job-title">공통 직무</Label>
						<Input
							id="digital-contract-bulk-job-title"
							value={form.jobTitle}
							onChange={(event) =>
								setForm((current) => ({ ...current, jobTitle: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-bulk-job-description">공통 직무 설명</Label>
						<Input
							id="digital-contract-bulk-job-description"
							value={form.jobDescription}
							onChange={(event) =>
								setForm((current) => ({ ...current, jobDescription: event.target.value }))
							}
						/>
					</div>
				</div>
				{commonFields.length > 0 ? (
					<div className="grid gap-4 sm:grid-cols-2">
						{commonFields.map((field) => (
							<div key={field.key} className="space-y-2">
								<Label htmlFor={`digital-contract-bulk-field-${field.key}`}>
									{field.label}
									{field.required ? ' *' : ''}
								</Label>
								{field.type === 'SELECT' && field.options?.length ? (
									<select
										id={`digital-contract-bulk-field-${field.key}`}
										value={form.fieldValues[field.key] ?? ''}
										onChange={(event) =>
											setForm((current) => ({
												...current,
												fieldValues: { ...current.fieldValues, [field.key]: event.target.value }
											}))
										}
										className={dialogControlClassName}
									>
										<option value="">선택</option>
										{field.options.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>
								) : (
									<Input
										id={`digital-contract-bulk-field-${field.key}`}
										type={
											field.type === 'DATE'
												? 'date'
												: field.type === 'AMOUNT' || field.type === 'NUMBER'
													? 'number'
													: 'text'
										}
										value={form.fieldValues[field.key] ?? ''}
										onChange={(event) =>
											setForm((current) => ({
												...current,
												fieldValues: { ...current.fieldValues, [field.key]: event.target.value }
											}))
										}
									/>
								)}
							</div>
						))}
					</div>
				) : null}
				<div className="rounded-md border border-border">
					<div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-3 py-2">
						<div>
							<p className="text-sm font-semibold">발송 대상</p>
							<p className="mt-1 text-xs text-muted-foreground">
								활성 직원 최대 100명까지 한 번에 발송합니다.
							</p>
						</div>
						<label className="flex items-center gap-2 text-sm font-medium">
							<input
								type="checkbox"
								checked={allVisibleSelected}
								onChange={(event) =>
									setForm((current) => ({
										...current,
										selectedEmployeeIds: event.target.checked
											? employees.slice(0, 100).map((employee) => employee.id)
											: []
									}))
								}
							/>
							전체 선택
						</label>
					</div>
					<div className="max-h-64 overflow-y-auto p-2">
						{employees.length === 0 ? (
							<EmptyState message="계약을 발송할 활성 직원이 없습니다." />
						) : (
							employees.map((employee) => (
								<label
									key={employee.id}
									className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/40"
								>
									<input
										type="checkbox"
										checked={selectedEmployeeIds.has(employee.id)}
										onChange={(event) => toggleEmployee(employee.id, event.target.checked)}
									/>
									<span className="min-w-0 flex-1">
										<span className="block truncate text-sm font-semibold">{employee.name}</span>
										<span className="block truncate text-xs text-muted-foreground">
											{employee.email ?? '이메일 없음'} · {employee.department ?? '부서 없음'} ·{' '}
											{employee.position ?? '직무 없음'} · {dateLabel(employee.joinDate)}
										</span>
									</span>
								</label>
							))
						)}
					</div>
				</div>
				{selectedEmployees.length > 0 ? (
					<p className="text-xs text-muted-foreground">
						첫 대상자 {selectedEmployees[0].name} 기준으로 {form.titlePrefix.trim() || '계약서'}{' '}
						제목이 생성됩니다.
					</p>
				) : null}
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button
						type="submit"
						disabled={submitting || templates.length === 0 || employees.length === 0}
					>
						대량 발송 실행
					</Button>
				</div>
			</form>
		</Modal>
	);
}

type DigitalContractPacketForm = {
	employeeId: string;
	employmentVariant: 'STANDARD' | 'SUCCESSION';
	title: string;
	contractType: ContractType;
	startedAt: string;
	endedAt: string;
	annualSalary: string;
	jobTitle: string;
	jobDescription: string;
	representativeName: string;
	paymentDay: string;
	workplace: string;
};

function DigitalContractPacketDialog({
	employees,
	onClose,
	onSubmitted
}: {
	employees: EmployeeResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const firstEmployee = employees[0] ?? null;
	const [form, setForm] = useState<DigitalContractPacketForm>({
		employeeId: firstEmployee ? String(firstEmployee.id) : '',
		employmentVariant: 'STANDARD',
		title: '입사 서류 전자계약',
		contractType: 'PERMANENT',
		startedAt: new Date().toISOString().slice(0, 10),
		endedAt: '',
		annualSalary: '',
		jobTitle: firstEmployee?.position ?? '',
		jobDescription: '',
		representativeName: '',
		paymentDay: '익월 10일',
		workplace: ''
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const employeeId = Number(form.employeeId);
		const annualSalary = form.annualSalary.trim() ? Number(form.annualSalary) : undefined;
		if (!Number.isInteger(employeeId) || employeeId <= 0) {
			setError('계약 대상 직원을 선택해주세요.');
			return;
		}
		if (
			!form.title.trim() ||
			!form.startedAt ||
			!form.representativeName.trim() ||
			!form.paymentDay.trim()
		) {
			setError('제목, 시작일, 대표이사, 지급일을 입력해주세요.');
			return;
		}
		if (annualSalary !== undefined && (!Number.isFinite(annualSalary) || annualSalary < 0)) {
			setError('연봉은 0 이상의 숫자로 입력해주세요.');
			return;
		}
		const request: CreateDigitalContractPacketRequest = {
			employeeId,
			employmentVariant: form.employmentVariant,
			title: form.title.trim(),
			contractType: form.contractType,
			startedAt: form.startedAt,
			endedAt: form.endedAt || undefined,
			annualSalary,
			jobTitle: form.jobTitle.trim() || undefined,
			jobDescription: form.jobDescription.trim() || undefined,
			fieldValues: [
				{ key: 'representativeName', value: form.representativeName.trim() },
				{ key: 'paymentDay', value: form.paymentDay.trim() },
				{ key: 'workplace', value: form.workplace.trim() }
			]
		};
		setSubmitting(true);
		setError(null);
		try {
			await api.post<DigitalContractPacketResponse>('/digital-contracts/packets', request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="입사 서류 묶음 발송"
			description="근로계약서 1종과 부속 서약서 6종을 한 통의 이메일로 발송합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={(event) => void submit(event)}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-packet-employee">직원</Label>
						<select
							id="digital-contract-packet-employee"
							value={form.employeeId}
							onChange={(event) => {
								const employee = employees.find((item) => String(item.id) === event.target.value);
								setForm((current) => ({
									...current,
									employeeId: event.target.value,
									jobTitle: employee?.position ?? current.jobTitle
								}));
							}}
							className={dialogControlClassName}
						>
							{employees.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name} · {employee.email ?? '이메일 없음'}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-packet-variant">근로계약서</Label>
						<select
							id="digital-contract-packet-variant"
							value={form.employmentVariant}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									employmentVariant: event.target
										.value as DigitalContractPacketForm['employmentVariant']
								}))
							}
							className={dialogControlClassName}
						>
							<option value="STANDARD">일반 정규직</option>
							<option value="SUCCESSION">법인전환·고용승계</option>
						</select>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-packet-title">묶음 제목</Label>
						<Input
							id="digital-contract-packet-title"
							value={form.title}
							onChange={(event) =>
								setForm((current) => ({ ...current, title: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-packet-representative">대표이사</Label>
						<Input
							id="digital-contract-packet-representative"
							value={form.representativeName}
							onChange={(event) =>
								setForm((current) => ({ ...current, representativeName: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-packet-start">시작일</Label>
						<Input
							id="digital-contract-packet-start"
							type="date"
							value={form.startedAt}
							onChange={(event) =>
								setForm((current) => ({ ...current, startedAt: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-packet-payday">지급일</Label>
						<Input
							id="digital-contract-packet-payday"
							value={form.paymentDay}
							onChange={(event) =>
								setForm((current) => ({ ...current, paymentDay: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="digital-contract-packet-salary">연봉</Label>
						<Input
							id="digital-contract-packet-salary"
							type="number"
							min="0"
							value={form.annualSalary}
							onChange={(event) =>
								setForm((current) => ({ ...current, annualSalary: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="digital-contract-packet-job">직무</Label>
						<Input
							id="digital-contract-packet-job"
							value={form.jobTitle}
							onChange={(event) =>
								setForm((current) => ({ ...current, jobTitle: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="digital-contract-packet-workplace">근무장소</Label>
					<Input
						id="digital-contract-packet-workplace"
						value={form.workplace}
						onChange={(event) =>
							setForm((current) => ({ ...current, workplace: event.target.value }))
						}
						placeholder="비워두면 회사 사업장 주소를 사용합니다."
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || employees.length === 0}>
						{submitting ? '발송 중...' : '7종 묶음 발송'}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

type LegacyDigitalContractMigrationForm = {
	employeeId: string;
	includedDocumentTypes: DigitalContractRequestResponse['documentType'][];
	title: string;
	contractType: ContractType;
	startedAt: string;
	endedAt: string;
	payrollApproved: boolean;
	verificationNote: string;
	file: File | null;
};

const legacyDigitalContractDocumentTypeOptions: Array<{
	value: DigitalContractRequestResponse['documentType'];
	label: string;
}> = [
	{ value: 'EMPLOYMENT_CONTRACT', label: '근로계약서' },
	{ value: 'NDA_SECURITY_PLEDGE', label: '비밀유지·보안각서' },
	{ value: 'IP_ASSIGNMENT', label: '지식재산권 귀속 동의서' },
	{ value: 'PERSONAL_INFO_CONSENT', label: '개인정보 수집·이용 동의서' },
	{ value: 'NON_COMPETE_PLEDGE', label: '겸직·경업 제한 서약서' },
	{ value: 'POLICY_ACKNOWLEDGEMENT', label: '회사 규정 준수 서약서' },
	{ value: 'EQUIPMENT_PLEDGE', label: '회사 지급 장비 사용 서약서' }
];

function LegacyDigitalContractMigrationDialog({
	employees,
	onClose,
	onSubmitted
}: {
	employees: EmployeeResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const firstEmployee = employees[0] ?? null;
	const [form, setForm] = useState<LegacyDigitalContractMigrationForm>({
		employeeId: firstEmployee ? String(firstEmployee.id) : '',
		includedDocumentTypes: ['EMPLOYMENT_CONTRACT'],
		title: '기존 근로계약서',
		contractType: 'PERMANENT',
		startedAt: firstEmployee?.joinDate ?? new Date().toISOString().slice(0, 10),
		endedAt: '',
		payrollApproved: false,
		verificationNote: '',
		file: null
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const isEmploymentContract = form.includedDocumentTypes.includes('EMPLOYMENT_CONTRACT');

	function toggleDocumentType(
		documentType: DigitalContractRequestResponse['documentType'],
		checked: boolean
	) {
		setForm((current) => {
			const includedDocumentTypes = checked
				? [...current.includedDocumentTypes, documentType]
				: current.includedDocumentTypes.filter((item) => item !== documentType);
			return {
				...current,
				includedDocumentTypes,
				payrollApproved: includedDocumentTypes.includes('EMPLOYMENT_CONTRACT')
					? current.payrollApproved
					: false
			};
		});
	}

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const employeeId = Number(form.employeeId);
		if (!Number.isInteger(employeeId) || employeeId <= 0) {
			setError('문서를 연결할 직원을 선택해주세요.');
			return;
		}
		if (form.includedDocumentTypes.length === 0) {
			setError('이 원본 파일에 포함된 서류를 1개 이상 선택해주세요.');
			return;
		}
		if (!form.title.trim() || !form.startedAt || !form.file) {
			setError('문서 제목, 적용 시작일, 원본 파일을 입력해주세요.');
			return;
		}
		const metadata: LegacyDigitalContractUploadRequest = {
			employeeId,
			includedDocumentTypes: form.includedDocumentTypes,
			title: form.title.trim(),
			contractType: form.contractType,
			startedAt: form.startedAt,
			endedAt: form.endedAt || undefined,
			payrollApproved: isEmploymentContract ? form.payrollApproved : false,
			verificationNote: form.verificationNote.trim() || undefined
		};
		const formData = new FormData();
		formData.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
		formData.append('file', form.file);
		setSubmitting(true);
		setError(null);
		try {
			await api.uploadFile<DigitalContractRequestResponse>(
				'/digital-contracts/legacy-documents',
				formData
			);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="기존 문서 이관"
			description="이미 체결된 문서를 다시 송부하지 않고 원본으로 보관합니다. 파일 하나에 포함된 서류를 모두 체크하면 이관 이력과 급여 검증에 함께 반영됩니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={(event) => void submit(event)}>
				<FormError message={error} />
				<div className="space-y-2">
					<div>
						<Label>이 파일에 포함된 서류</Label>
						<p className="mt-1 text-xs text-muted-foreground">
							원본 하나에 여러 서류가 함께 있으면 모두 선택하세요.
						</p>
					</div>
					<div className="grid gap-2 rounded-xl border border-border bg-muted/20 p-3 sm:grid-cols-2">
						{legacyDigitalContractDocumentTypeOptions.map((option) => {
							const checked = form.includedDocumentTypes.includes(option.value);
							return (
								<label
									key={option.value}
									className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${checked ? 'border-primary/40 bg-primary/5 font-semibold text-foreground' : 'border-transparent bg-background/70 text-muted-foreground hover:border-border hover:text-foreground'}`}
								>
									<input
										type="checkbox"
										aria-label={option.label}
										checked={checked}
										onChange={(event) => toggleDocumentType(option.value, event.target.checked)}
									/>
									<span>{option.label}</span>
								</label>
							);
						})}
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="legacy-contract-employee">직원</Label>
						<select
							id="legacy-contract-employee"
							value={form.employeeId}
							onChange={(event) =>
								setForm((current) => ({ ...current, employeeId: event.target.value }))
							}
							className={dialogControlClassName}
						>
							{employees.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name} · {employee.status === 'ACTIVE' ? '재직' : employee.status}
								</option>
							))}
						</select>
					</div>
					<div className="rounded-xl border border-dashed border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
						<p className="font-medium text-foreground">파일별 이관</p>
						<p className="mt-1 text-xs">
							서로 다른 원본 파일은 이관을 완료한 뒤 같은 방식으로 추가하세요.
						</p>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="legacy-contract-title">문서 제목</Label>
						<Input
							id="legacy-contract-title"
							value={form.title}
							onChange={(event) =>
								setForm((current) => ({ ...current, title: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="legacy-contract-file">원본 파일</Label>
						<Input
							id="legacy-contract-file"
							type="file"
							accept=".pdf,.png,.jpg,.jpeg,.docx"
							onChange={(event) =>
								setForm((current) => ({ ...current, file: event.target.files?.[0] ?? null }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="legacy-contract-start">적용 시작일</Label>
						<Input
							id="legacy-contract-start"
							type="date"
							value={form.startedAt}
							onChange={(event) =>
								setForm((current) => ({ ...current, startedAt: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="legacy-contract-end">종료일 (선택)</Label>
						<Input
							id="legacy-contract-end"
							type="date"
							value={form.endedAt}
							onChange={(event) =>
								setForm((current) => ({ ...current, endedAt: event.target.value }))
							}
						/>
					</div>
				</div>
				{isEmploymentContract ? (
					<label className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 p-3 text-sm">
						<input
							type="checkbox"
							checked={form.payrollApproved}
							onChange={(event) =>
								setForm((current) => ({ ...current, payrollApproved: event.target.checked }))
							}
						/>
						<span>
							<span className="font-semibold">급여 산정 근거로 승인</span>
							<span className="mt-1 block text-muted-foreground">
								원본을 확인했고 해당 기간의 급여 발행에 사용할 수 있는 근로계약서임을 기록합니다.
							</span>
						</span>
					</label>
				) : null}
				<div className="space-y-2">
					<Label htmlFor="legacy-contract-note">확인 메모 (선택)</Label>
					<textarea
						id="legacy-contract-note"
						value={form.verificationNote}
						onChange={(event) =>
							setForm((current) => ({ ...current, verificationNote: event.target.value }))
						}
						placeholder="예: 2026-07-10 원본 대조 완료"
						className={`${dialogControlClassName} min-h-24 resize-y`}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || employees.length === 0}>
						{submitting ? '이관 중...' : '원본 문서 이관'}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

export function DigitalContractsPage() {
	const [state, setState] = useState<RequestState<DigitalContractsPageData>>({ status: 'idle' });
	const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
	const [requestDialogOpen, setRequestDialogOpen] = useState(false);
	const [bulkRequestDialogOpen, setBulkRequestDialogOpen] = useState(false);
	const [packetDialogOpen, setPacketDialogOpen] = useState(false);
	const [legacyMigrationDialogOpen, setLegacyMigrationDialogOpen] = useState(false);
	const [installingStandardTemplates, setInstallingStandardTemplates] = useState(false);
	const [resendingRequestId, setResendingRequestId] = useState<number | null>(null);
	const [deliveryError, setDeliveryError] = useState<string | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [summary, templates, requests, employeesPage] = await Promise.all([
					api.get<DigitalContractSummaryResponse>('/digital-contracts/summary'),
					api.get<DigitalContractTemplateResponse[]>('/digital-contracts/templates'),
					api.get<DigitalContractRequestResponse[]>('/digital-contracts/requests'),
					api.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200')
				]);
				setState({
					status: 'success',
					data: { summary, templates, requests, employees: employeesPage.content }
				});
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const signRate =
		data && data.summary.totalRequestCount > 0
			? `${numberLabel(data.summary.signatureCompletionRatePercent)}%`
			: '0%';
	const activeEmployees = data?.employees.filter((employee) => employee.status === 'ACTIVE') ?? [];
	const inactiveRequestCount = data
		? data.summary.cancelledRequestCount + data.summary.expiredRequestCount
		: 0;

	async function installStandardTemplates() {
		setInstallingStandardTemplates(true);
		try {
			await api.post<DigitalContractTemplateResponse[]>(
				'/digital-contracts/templates/standard',
				{}
			);
			await load();
		} finally {
			setInstallingStandardTemplates(false);
		}
	}

	async function resendContract(requestId: number) {
		setResendingRequestId(requestId);
		setDeliveryError(null);
		try {
			await api.post<DigitalContractRequestResponse>(
				`/digital-contracts/requests/${requestId}/resend`,
				{}
			);
			await load();
		} catch (error) {
			setDeliveryError(getErrorMessage(error));
		} finally {
			setResendingRequestId(null);
		}
	}

	async function downloadMigratedDocument(request: DigitalContractRequestResponse) {
		if (!request.fileName) return;
		setDeliveryError(null);
		try {
			await api.downloadFile(`/digital-contracts/requests/${request.id}/file`, request.fileName);
		} catch (error) {
			setDeliveryError(getErrorMessage(error));
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="전자계약"
				description="근로계약 템플릿을 만들고 직원에게 전자서명을 요청합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						<Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(true)}>
							<Plus className="size-4" />
							템플릿 만들기
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => void installStandardTemplates()}
							disabled={installingStandardTemplates}
						>
							<ClipboardCheck className="size-4" />
							{installingStandardTemplates ? '표준 서식 준비 중...' : '표준 서식 가져오기'}
						</Button>
						<Button type="button" variant="outline" onClick={() => setBulkRequestDialogOpen(true)}>
							<UsersRound className="size-4" />
							계약 대량 발송
						</Button>
						<Button type="button" variant="outline" onClick={() => setPacketDialogOpen(true)}>
							<ClipboardCheck className="size-4" />
							입사 서류 묶음 발송
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => setLegacyMigrationDialogOpen(true)}
						>
							<ClipboardCheck className="size-4" />
							기존 문서 이관
						</Button>
						<Button type="button" onClick={() => setRequestDialogOpen(true)}>
							<ClipboardCheck className="size-4" />
							계약 발송
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{deliveryError ? <FormError message={deliveryError} /> : null}

			{data ? (
				<>
					<div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 xl:grid-cols-5">
						<DigitalContractStatCard
							title="활성 템플릿"
							value={numberLabel(data.summary.activeTemplateCount)}
							description="발송 가능한 계약 양식"
						/>
						<DigitalContractStatCard
							title="전체 요청"
							value={numberLabel(data.summary.totalRequestCount)}
							description="누적 전자계약 발송"
						/>
						<DigitalContractStatCard
							title="서명 대기"
							value={numberLabel(data.summary.sentRequestCount)}
							description="직원 서명 필요"
						/>
						<DigitalContractStatCard
							title="서명 완료"
							value={numberLabel(data.summary.signedRequestCount)}
							description="계약 생성 완료"
						/>
						<DigitalContractStatCard title="서명률" value={signRate} description="완료 요청 기준" />
					</div>

					<div className="grid gap-6 xl:grid-cols-[1fr_24rem]">
						<Card>
							<CardHeader>
								<CardTitle>계약 발송함</CardTitle>
								<CardDescription>
									대상 직원, 계약 조건, 서명 상태를 함께 확인합니다.
								</CardDescription>
							</CardHeader>
							<CardContent>
								{data.requests.length === 0 ? (
									<EmptyState message="발송된 전자계약이 없습니다." />
								) : (
									<div className="overflow-x-auto rounded-lg border border-border">
										<table className="w-full min-w-[880px] text-left text-sm">
											<thead className="bg-muted/40 text-xs text-muted-foreground">
												<tr>
													<th className="px-4 py-3 font-semibold">계약</th>
													<th className="px-4 py-3 font-semibold">직원</th>
													<th className="px-4 py-3 font-semibold">조건</th>
													<th className="px-4 py-3 font-semibold">입력값</th>
													<th className="px-4 py-3 font-semibold">상태</th>
													<th className="px-4 py-3 font-semibold">발송/서명</th>
												</tr>
											</thead>
											<tbody>
												{data.requests.map((request) => {
													const template = data.templates.find(
														(item) => item.id === request.templateId
													);
													return (
														<tr key={request.id} className="border-t border-border align-top">
															<td className="px-4 py-4">
																<p className="font-semibold">{request.title}</p>
																<p className="mt-1 text-xs text-muted-foreground">
																	{request.templateName ?? template?.name ?? '템플릿 없음'} ·{' '}
																	{contractTypeLabel(request.contractType)}
																</p>
																{request.source === 'LEGACY_MIGRATION' ? (
																	<div className="mt-2 flex flex-wrap gap-1.5">
																		{(request.legacyDocumentTypes?.length
																			? request.legacyDocumentTypes
																			: [request.documentType]
																		).map((documentType) => (
																			<Badge
																				key={`${request.id}-${documentType}`}
																				variant="secondary"
																			>
																				{digitalContractDocumentTypeLabel(documentType)}
																			</Badge>
																		))}
																	</div>
																) : null}
															</td>
															<td className="px-4 py-4">
																<p className="font-medium">
																	{request.employeeName ?? `직원 #${request.employeeId}`}
																</p>
																<p className="mt-1 text-xs text-muted-foreground">
																	{request.jobTitle ?? '직무 미입력'}
																</p>
															</td>
															<td className="px-4 py-4 text-muted-foreground">
																<p>
																	{dateLabel(request.startedAt)} - {dateLabel(request.endedAt)}
																</p>
																<p className="mt-1">
																	{request.salaryRegistered ? '연봉 등록' : '연봉 미등록'}
																</p>
															</td>
															<td className="px-4 py-4">
																<div className="flex flex-wrap gap-2">
																	{request.fieldValues.map((value) => {
																		const field = template?.fields.find(
																			(item) => item.key === value.key
																		);
																		return (
																			<span
																				key={`${request.id}-${value.key}`}
																				className="inline-flex items-center gap-2 rounded-md bg-muted px-2 py-1 text-xs"
																			>
																				<span className="text-muted-foreground">{value.label}</span>
																				<span className="font-semibold">
																					{digitalContractFieldValueLabel(value.value, field?.type)}
																				</span>
																			</span>
																		);
																	})}
																</div>
															</td>
															<td className="px-4 py-4">
																<Badge variant={digitalContractStatusVariant(request.status)}>
																	{digitalContractStatusLabel(request.status)}
																</Badge>
															</td>
															<td className="px-4 py-4 text-muted-foreground">
																<p>{dateLabel(request.sentAt)}</p>
																<p className="mt-1">{dateLabel(request.signedAt)}</p>
																{request.deliveryStatus ? (
																	<p
																		className={
																			request.deliveryStatus === 'FAILED'
																				? 'mt-1 text-xs text-destructive'
																				: 'mt-1 text-xs'
																		}
																	>
																		{request.deliveryStatus === 'SENT'
																			? '이메일 전송 완료'
																			: request.deliveryStatus === 'FAILED'
																				? '이메일 전송 실패'
																				: request.deliveryStatus === 'DISABLED'
																					? '이메일 발송 안 함'
																					: '이메일 전송 대기'}
																	</p>
																) : null}
																{request.status === 'SENT' &&
																request.source !== 'LEGACY_MIGRATION' ? (
																	<Button
																		type="button"
																		variant="outline"
																		size="sm"
																		className="mt-2"
																		disabled={resendingRequestId === request.id}
																		onClick={() => void resendContract(request.id)}
																	>
																		{resendingRequestId === request.id
																			? '재발송 중...'
																			: '이메일 재발송'}
																	</Button>
																) : null}
																{request.fileName ? (
																	<Button
																		type="button"
																		variant="outline"
																		size="sm"
																		className="mt-2"
																		onClick={() => void downloadMigratedDocument(request)}
																	>
																		원본 내려받기
																	</Button>
																) : null}
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								)}
							</CardContent>
						</Card>

						<div className="space-y-6">
							<DigitalContractOperationsPanel summary={data.summary} />

							<Card>
								<CardHeader>
									<CardTitle>운영 신호</CardTitle>
									<CardDescription>
										계약 발송 이후 병목과 템플릿 활용도를 추적합니다.
									</CardDescription>
								</CardHeader>
								<CardContent className="space-y-3">
									<DigitalContractInsightRow
										label="최근 30일 발송"
										value={`${numberLabel(data.summary.recentlySentRequestCount)}건`}
										description="최근 입사·갱신 계약 처리량"
										tone="success"
									/>
									<DigitalContractInsightRow
										label="장기 미서명"
										value={`${numberLabel(data.summary.staleSentRequestCount)}건`}
										description="7일 이상 서명 대기 중"
										tone={data.summary.staleSentRequestCount > 0 ? 'warning' : 'default'}
									/>
									<DigitalContractInsightRow
										label="템플릿 활용"
										value={`${numberLabel(data.summary.usedTemplateCount)} / ${numberLabel(
											data.summary.activeTemplateCount
										)}`}
										description="발송 이력이 있는 활성 양식"
									/>
									<DigitalContractInsightRow
										label="취소·만료"
										value={`${numberLabel(inactiveRequestCount)}건`}
										description="재발송 또는 조건 확인 필요"
										tone={inactiveRequestCount > 0 ? 'warning' : 'default'}
									/>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>템플릿</CardTitle>
									<CardDescription>계약 유형과 서명 입력 필드를 관리합니다.</CardDescription>
								</CardHeader>
								<CardContent className="space-y-3">
									{data.templates.length === 0 ? (
										<EmptyState message="등록된 전자계약 템플릿이 없습니다." />
									) : (
										data.templates.map((template) => (
											<div key={template.id} className="rounded-lg border border-border p-3">
												<div className="flex items-start justify-between gap-3">
													<div>
														<p className="font-semibold">{template.name}</p>
														<p className="mt-1 text-xs text-muted-foreground">
															{contractTypeLabel(template.contractType)} ·{' '}
															{template.description ?? '설명 없음'}
														</p>
													</div>
													<Badge variant={template.active ? 'success' : 'outline'}>
														{template.active ? '활성' : '비활성'}
													</Badge>
												</div>
												<div className="mt-3 flex flex-wrap gap-2">
													{template.fields.map((field) => (
														<Badge key={`${template.id}-${field.key}`} variant="secondary">
															{field.label} · {digitalContractFieldTypeLabel(field.type)}
														</Badge>
													))}
												</div>
											</div>
										))
									)}
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>계약 대상</CardTitle>
									<CardDescription>활성 직원에게 전자계약을 발송할 수 있습니다.</CardDescription>
								</CardHeader>
								<CardContent className="space-y-3">
									{activeEmployees.length === 0 ? (
										<EmptyState message="계약을 발송할 활성 직원이 없습니다." />
									) : (
										activeEmployees.slice(0, 6).map((employee) => (
											<div
												key={employee.id}
												className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
											>
												<div className="min-w-0">
													<p className="truncate font-semibold">{employee.name}</p>
													<p className="truncate text-xs text-muted-foreground">
														{employee.department ?? '부서 없음'} ·{' '}
														{employee.position ?? '직무 없음'}
													</p>
												</div>
												<Badge variant="outline">{dateLabel(employee.joinDate)}</Badge>
											</div>
										))
									)}
								</CardContent>
							</Card>
						</div>
					</div>

					{templateDialogOpen ? (
						<DigitalContractTemplateDialog
							onClose={() => setTemplateDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{requestDialogOpen ? (
						<DigitalContractRequestDialog
							templates={data.templates}
							employees={activeEmployees}
							onClose={() => setRequestDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{bulkRequestDialogOpen ? (
						<DigitalContractBulkRequestDialog
							templates={data.templates}
							employees={activeEmployees}
							onClose={() => setBulkRequestDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{packetDialogOpen ? (
						<DigitalContractPacketDialog
							employees={activeEmployees}
							onClose={() => setPacketDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{legacyMigrationDialogOpen ? (
						<LegacyDigitalContractMigrationDialog
							employees={data.employees}
							onClose={() => setLegacyMigrationDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
				</>
			) : null}
		</div>
	);
}

type RecruitingPageData = {
	summary: RecruitingSummaryResponse;
	jobs: RecruitingJobPostingResponse[];
	candidates: RecruitingCandidateResponse[];
	employees: EmployeeResponse[];
};

type RecruitingJobForm = {
	title: string;
	department: string;
	position: string;
	employmentType: string;
	location: string;
	description: string;
	requirements: string;
	hiringManagerEmployeeId: string;
	applicationQuestionsText: string;
	publish: boolean;
};

type RecruitingCandidateForm = {
	jobPostingId: string;
	name: string;
	email: string;
	phone: string;
	source: string;
	resumeUrl: string;
	coverLetter: string;
	answersJson: string;
};

type RecruitingEvaluationForm = {
	candidateId: string;
	evaluatorEmployeeId: string;
	stage: RecruitingCandidateStage;
	score: string;
	recommendation: RecruitingRecommendation;
	note: string;
};

type RecruitingStageForm = {
	candidateId: string;
	stage: RecruitingCandidateStage;
	note: string;
};

type RecruitingHireForm = {
	candidateId: string;
	joinDate: string;
	department: string;
	position: string;
	employeeNumber: string;
};

const recruitingStageOrder: RecruitingCandidateStage[] = [
	'APPLIED',
	'SCREENING',
	'INTERVIEW',
	'OFFER',
	'HIRED',
	'REJECTED'
];

const recruitingRecommendationOptions: Array<{ value: RecruitingRecommendation; label: string }> = [
	{ value: 'STRONG_YES', label: '강력 추천' },
	{ value: 'YES', label: '추천' },
	{ value: 'NO', label: '보류' },
	{ value: 'STRONG_NO', label: '비추천' }
];

function recruitingStageLabel(stage: RecruitingCandidateStage): string {
	const labels: Record<RecruitingCandidateStage, string> = {
		APPLIED: '지원',
		SCREENING: '서류 검토',
		INTERVIEW: '인터뷰',
		OFFER: '오퍼',
		HIRED: '합격',
		REJECTED: '불합격'
	};
	return labels[stage];
}

function recruitingStageVariant(
	stage: RecruitingCandidateStage
): 'success' | 'warning' | 'outline' | 'destructive' | 'secondary' {
	if (stage === 'HIRED') return 'success';
	if (stage === 'INTERVIEW' || stage === 'OFFER') return 'warning';
	if (stage === 'REJECTED') return 'destructive';
	if (stage === 'SCREENING') return 'outline';
	return 'secondary';
}

function recruitingJobStatusLabel(status: RecruitingJobStatus): string {
	const labels: Record<RecruitingJobStatus, string> = {
		DRAFT: '초안',
		OPEN: '공개',
		CLOSED: '마감'
	};
	return labels[status];
}

function recruitingRecommendationLabel(recommendation: RecruitingRecommendation): string {
	return (
		recruitingRecommendationOptions.find((option) => option.value === recommendation)?.label ??
		recommendation
	);
}

function recruitingPrivacyDeletionLabel(candidate: RecruitingCandidateResponse): string | null {
	if (candidate.privacyDeletedAt) return `삭제 완료 ${dateLabel(candidate.privacyDeletedAt)}`;
	if (candidate.privacyDeletionDueAt)
		return `삭제 예정 ${dateLabel(candidate.privacyDeletionDueAt)}`;
	return null;
}

function recruitingDecisionVariant(
	reasonCode: RecruitingDecisionCandidateResponse['reasonCode']
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (reasonCode === 'OFFER_READY' || reasonCode === 'STRONG_SIGNAL') return 'success';
	if (reasonCode === 'EVALUATION_NEEDED') return 'warning';
	if (reasonCode === 'REJECTION_RISK') return 'destructive';
	return 'outline';
}

function recruitingCareerSiteVariant(
	level: RecruitingSummaryResponse['careerSiteRiskLevel'] | null | undefined
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (level === 'READY') return 'success';
	if (level === 'CRITICAL') return 'destructive';
	if (level === 'WARNING') return 'warning';
	return 'outline';
}

function RecruitingStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: string;
	description: string;
}) {
	return (
		<Card className="min-w-40 sm:min-w-0">
			<CardContent className="p-4">
				<p className="text-xs font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal">{value}</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function RecruitingSiteReadinessPanel({ summary }: { summary: RecruitingSummaryResponse }) {
	const riskLevel =
		summary.careerSiteRiskLevel ?? (summary.openJobCount > 0 ? 'READY' : 'CRITICAL');
	const readyJobCount = summary.careerSiteReadyJobCount ?? summary.openJobCount;
	const readinessPercent =
		typeof summary.careerSiteReadinessPercent === 'number'
			? summary.careerSiteReadinessPercent
			: summary.openJobCount > 0
				? 100
				: 0;
	const sourceInsights = summary.sourceInsights ?? [];

	return (
		<div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(20rem,0.85fr)]">
			<Card>
				<CardHeader className="gap-3">
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div className="min-w-0 space-y-1">
							<CardTitle>채용 사이트 준비도</CardTitle>
							<CardDescription>
								{summary.careerSiteRiskSummary ??
									'공개 공고의 URL, 지원서 질문, 자격 요건, 지원자 유입 상태를 점검합니다.'}
							</CardDescription>
						</div>
						<div className="flex flex-wrap justify-end gap-2">
							<Badge variant="outline">공개 준비 {percentLabel(readinessPercent)}</Badge>
							<Badge variant={recruitingCareerSiteVariant(riskLevel)}>
								{summary.careerSiteRiskLabel ?? '준비도 점검'}
							</Badge>
						</div>
					</div>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
						<div className="rounded-md border border-border bg-muted/20 p-3">
							<p className="text-xs text-muted-foreground">공개 준비</p>
							<p className="mt-1 text-lg font-semibold tracking-normal">
								{percentLabel(readinessPercent)}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">
								{numberLabel(readyJobCount)} / {numberLabel(summary.openJobCount)}개 공고
							</p>
						</div>
						<div className="rounded-md border border-border bg-muted/20 p-3">
							<p className="text-xs text-muted-foreground">공개 URL</p>
							<p className="mt-1 text-lg font-semibold tracking-normal">
								{numberLabel(summary.openJobsWithPublicSlugCount)}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">채용 사이트 연결</p>
						</div>
						<div className="rounded-md border border-border bg-muted/20 p-3">
							<p className="text-xs text-muted-foreground">지원서 질문</p>
							<p className="mt-1 text-lg font-semibold tracking-normal">
								{numberLabel(summary.openJobsWithApplicationQuestionsCount)}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">구조화된 지원서</p>
						</div>
						<div className="rounded-md border border-border bg-muted/20 p-3">
							<p className="text-xs text-muted-foreground">지원자 유입</p>
							<p className="mt-1 text-lg font-semibold tracking-normal">
								{numberLabel(summary.openJobsWithCandidatesCount)}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">후보자 보유 공고</p>
						</div>
					</div>
					<div className="rounded-md bg-muted/25 px-3 py-2">
						<p className="text-xs text-muted-foreground">권장 액션</p>
						<p className="mt-1 text-sm font-medium">
							{summary.careerSiteRecommendedAction ??
								'공개 공고 품질과 채널별 전환 성과를 정기적으로 점검하세요.'}
						</p>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<div className="flex items-start justify-between gap-3">
						<div>
							<CardTitle>소싱 채널</CardTitle>
							<CardDescription>지원 경로별 후보자와 합격 전환을 비교합니다.</CardDescription>
						</div>
						<Badge variant={summary.candidateSourceChannelCount ? 'success' : 'outline'}>
							{numberLabel(summary.candidateSourceChannelCount)}개
						</Badge>
					</div>
				</CardHeader>
				<CardContent className="space-y-3">
					{sourceInsights.length > 0 ? (
						sourceInsights.map((source) => (
							<div key={source.source} className="rounded-lg border border-border p-3">
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<p className="truncate font-semibold">{source.source}</p>
										<p className="mt-1 text-xs text-muted-foreground">
											진행 {numberLabel(source.activeCandidateCount)}명 · 합격{' '}
											{numberLabel(source.hiredCandidateCount)}명
										</p>
									</div>
									<Badge variant="outline">{numberLabel(source.candidateCount)}명</Badge>
								</div>
								<div className="mt-3 flex items-center justify-between gap-3 text-xs">
									<span className="text-muted-foreground">합격 전환</span>
									<span className="font-semibold">
										전환 {percentLabel(source.hireConversionRatePercent)}
									</span>
								</div>
							</div>
						))
					) : (
						<EmptyState message="지원 경로 데이터가 없습니다." />
					)}
				</CardContent>
			</Card>
		</div>
	);
}

function RecruitingDecisionCandidates({
	candidates
}: {
	candidates: RecruitingDecisionCandidateResponse[];
}) {
	const visibleCandidates = candidates.slice(0, 5);

	return (
		<Card>
			<CardHeader>
				<div className="flex items-start justify-between gap-3">
					<div>
						<CardTitle>의사결정 후보</CardTitle>
						<CardDescription>오퍼, 평가 공백, 강한 신호가 있는 후보자입니다.</CardDescription>
					</div>
					<Badge variant={candidates.length > 0 ? 'warning' : 'outline'}>
						{numberLabel(candidates.length)}명
					</Badge>
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				{visibleCandidates.length === 0 ? (
					<EmptyState message="지금 결정할 채용 후보가 없습니다." />
				) : (
					visibleCandidates.map((candidate) => (
						<div key={candidate.candidateId} className="rounded-lg border border-border p-3">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="truncate font-semibold">{candidate.name}</p>
									<p className="mt-1 truncate text-xs text-muted-foreground">
										{candidate.jobTitle ?? '공고 없음'}
									</p>
								</div>
								<Badge variant={recruitingStageVariant(candidate.stage)}>
									{recruitingStageLabel(candidate.stage)}
								</Badge>
							</div>
							<div className="mt-3 flex flex-wrap gap-2">
								<Badge
									className="max-w-full whitespace-normal break-keep text-left leading-5"
									variant={recruitingDecisionVariant(candidate.reasonCode)}
								>
									{candidate.reasonLabel}
								</Badge>
								<Badge variant="secondary">
									평균 {candidate.evaluationCount > 0 ? candidate.averageScore : '-'}
								</Badge>
								<Badge variant="outline">평가 {numberLabel(candidate.evaluationCount)}개</Badge>
								{candidate.latestRecommendation ? (
									<Badge variant="outline">
										{recruitingRecommendationLabel(candidate.latestRecommendation)}
									</Badge>
								) : null}
							</div>
							<p className="mt-3 text-xs leading-5 text-muted-foreground">
								{candidate.actionLabel}
							</p>
						</div>
					))
				)}
			</CardContent>
		</Card>
	);
}

function RecruitingJobDialog({
	employees,
	onClose,
	onSubmitted
}: {
	employees: EmployeeResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [form, setForm] = useState<RecruitingJobForm>({
		title: '',
		department: '',
		position: '',
		employmentType: 'FULL_TIME',
		location: '서울',
		description: '',
		requirements: '',
		hiringManagerEmployeeId: employees[0]?.id ? String(employees[0].id) : '',
		applicationQuestionsText: '지원 동기를 알려주세요.',
		publish: true
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!form.title.trim()) {
			setError('공고 제목을 입력해주세요.');
			return;
		}
		if (!form.description.trim()) {
			setError('공고 설명을 입력해주세요.');
			return;
		}
		const request: CreateRecruitingJobPostingRequest = {
			title: form.title.trim(),
			department: form.department.trim() || undefined,
			position: form.position.trim() || undefined,
			employmentType: form.employmentType.trim() || undefined,
			location: form.location.trim() || undefined,
			description: form.description.trim(),
			requirements: form.requirements.trim() || undefined,
			hiringManagerEmployeeId: form.hiringManagerEmployeeId
				? Number(form.hiringManagerEmployeeId)
				: undefined,
			applicationQuestions: form.applicationQuestionsText
				.split('\n')
				.map((question) => question.trim())
				.filter(Boolean),
			publish: form.publish
		};
		setSubmitting(true);
		try {
			await api.post<RecruitingJobPostingResponse>('/recruiting/jobs', request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="채용 공고 만들기"
			description="공고 내용과 지원서 질문을 설정합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-job-title">공고 제목</Label>
						<Input
							id="recruiting-job-title"
							value={form.title}
							onChange={(event) =>
								setForm((current) => ({ ...current, title: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-job-manager">채용 담당자</Label>
						<select
							id="recruiting-job-manager"
							value={form.hiringManagerEmployeeId}
							onChange={(event) =>
								setForm((current) => ({ ...current, hiringManagerEmployeeId: event.target.value }))
							}
							className={dialogControlClassName}
						>
							<option value="">선택</option>
							{employees.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-job-department">부서</Label>
						<Input
							id="recruiting-job-department"
							value={form.department}
							onChange={(event) =>
								setForm((current) => ({ ...current, department: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-job-position">포지션</Label>
						<Input
							id="recruiting-job-position"
							value={form.position}
							onChange={(event) =>
								setForm((current) => ({ ...current, position: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-job-employment-type">고용 형태</Label>
						<Input
							id="recruiting-job-employment-type"
							value={form.employmentType}
							onChange={(event) =>
								setForm((current) => ({ ...current, employmentType: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-job-location">근무지</Label>
						<Input
							id="recruiting-job-location"
							value={form.location}
							onChange={(event) =>
								setForm((current) => ({ ...current, location: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="recruiting-job-description">공고 설명</Label>
					<textarea
						id="recruiting-job-description"
						value={form.description}
						onChange={(event) =>
							setForm((current) => ({ ...current, description: event.target.value }))
						}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="recruiting-job-requirements">자격 요건</Label>
					<textarea
						id="recruiting-job-requirements"
						value={form.requirements}
						onChange={(event) =>
							setForm((current) => ({ ...current, requirements: event.target.value }))
						}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="recruiting-job-questions">지원서 질문</Label>
					<textarea
						id="recruiting-job-questions"
						value={form.applicationQuestionsText}
						onChange={(event) =>
							setForm((current) => ({ ...current, applicationQuestionsText: event.target.value }))
						}
						className={dialogTextAreaClassName}
					/>
				</div>
				<label className="flex items-center gap-2 text-sm font-medium">
					<input
						type="checkbox"
						checked={form.publish}
						onChange={(event) =>
							setForm((current) => ({ ...current, publish: event.target.checked }))
						}
					/>
					바로 공개
				</label>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						저장
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function RecruitingCandidateDialog({
	jobs,
	onClose,
	onSubmitted
}: {
	jobs: RecruitingJobPostingResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const firstJob = jobs.find((job) => job.status === 'OPEN') ?? jobs[0];
	const [form, setForm] = useState<RecruitingCandidateForm>({
		jobPostingId: firstJob ? String(firstJob.id) : '',
		name: '',
		email: '',
		phone: '',
		source: '채용 사이트',
		resumeUrl: '',
		coverLetter: '',
		answersJson: '{}'
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!form.jobPostingId) {
			setError('지원 공고를 선택해주세요.');
			return;
		}
		if (!form.name.trim()) {
			setError('후보자 이름을 입력해주세요.');
			return;
		}
		const request: CreateRecruitingCandidateRequest = {
			jobPostingId: Number(form.jobPostingId),
			name: form.name.trim(),
			email: form.email.trim() || undefined,
			phone: form.phone.trim() || undefined,
			source: form.source.trim() || undefined,
			resumeUrl: form.resumeUrl.trim() || undefined,
			coverLetter: form.coverLetter.trim() || undefined,
			answersJson: form.answersJson.trim() || undefined
		};
		setSubmitting(true);
		try {
			await api.post<RecruitingCandidateResponse>('/recruiting/candidates', request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="후보자 등록"
			description="지원자를 공고와 전형 파이프라인에 연결합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-candidate-job">지원 공고</Label>
						<select
							id="recruiting-candidate-job"
							value={form.jobPostingId}
							onChange={(event) =>
								setForm((current) => ({ ...current, jobPostingId: event.target.value }))
							}
							className={dialogControlClassName}
						>
							{jobs.map((job) => (
								<option key={job.id} value={job.id}>
									{job.title}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-candidate-source">지원 경로</Label>
						<Input
							id="recruiting-candidate-source"
							value={form.source}
							onChange={(event) =>
								setForm((current) => ({ ...current, source: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-candidate-name">이름</Label>
						<Input
							id="recruiting-candidate-name"
							value={form.name}
							onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-candidate-email">이메일</Label>
						<Input
							id="recruiting-candidate-email"
							type="email"
							value={form.email}
							onChange={(event) =>
								setForm((current) => ({ ...current, email: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-candidate-phone">전화번호</Label>
						<Input
							id="recruiting-candidate-phone"
							value={form.phone}
							onChange={(event) =>
								setForm((current) => ({ ...current, phone: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-candidate-resume">이력서 URL</Label>
						<Input
							id="recruiting-candidate-resume"
							value={form.resumeUrl}
							onChange={(event) =>
								setForm((current) => ({ ...current, resumeUrl: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="recruiting-candidate-cover">자기소개</Label>
					<textarea
						id="recruiting-candidate-cover"
						value={form.coverLetter}
						onChange={(event) =>
							setForm((current) => ({ ...current, coverLetter: event.target.value }))
						}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="recruiting-candidate-answers">지원서 답변 JSON</Label>
					<textarea
						id="recruiting-candidate-answers"
						value={form.answersJson}
						onChange={(event) =>
							setForm((current) => ({ ...current, answersJson: event.target.value }))
						}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || jobs.length === 0}>
						등록
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function RecruitingEvaluationDialog({
	candidates,
	employees,
	onClose,
	onSubmitted
}: {
	candidates: RecruitingCandidateResponse[];
	employees: EmployeeResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const firstCandidate =
		candidates.find((candidate) => candidate.stage === 'INTERVIEW') ?? candidates[0];
	const [form, setForm] = useState<RecruitingEvaluationForm>({
		candidateId: firstCandidate ? String(firstCandidate.id) : '',
		evaluatorEmployeeId: employees[0]?.id ? String(employees[0].id) : '',
		stage: firstCandidate?.stage ?? 'INTERVIEW',
		score: '4',
		recommendation: 'YES',
		note: ''
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!form.candidateId || !form.evaluatorEmployeeId) {
			setError('후보자와 평가자를 선택해주세요.');
			return;
		}
		const score = Number(form.score);
		if (!Number.isFinite(score) || score < 1 || score > 5) {
			setError('점수는 1~5 사이로 입력해주세요.');
			return;
		}
		const request: CreateRecruitingEvaluationRequest = {
			evaluatorEmployeeId: Number(form.evaluatorEmployeeId),
			stage: form.stage,
			score,
			recommendation: form.recommendation,
			note: form.note.trim() || undefined
		};
		setSubmitting(true);
		try {
			await api.post<RecruitingCandidateResponse>(
				`/recruiting/candidates/${form.candidateId}/evaluations`,
				request
			);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="평가 등록"
			description="전형 단계별 평가 점수와 판단 근거를 남깁니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-evaluation-candidate">후보자</Label>
						<select
							id="recruiting-evaluation-candidate"
							value={form.candidateId}
							onChange={(event) => {
								const nextCandidate = candidates.find(
									(candidate) => String(candidate.id) === event.target.value
								);
								setForm((current) => ({
									...current,
									candidateId: event.target.value,
									stage: nextCandidate?.stage ?? current.stage
								}));
							}}
							className={dialogControlClassName}
						>
							{candidates.map((candidate) => (
								<option key={candidate.id} value={candidate.id}>
									{candidate.name}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-evaluation-evaluator">평가자</Label>
						<select
							id="recruiting-evaluation-evaluator"
							value={form.evaluatorEmployeeId}
							onChange={(event) =>
								setForm((current) => ({ ...current, evaluatorEmployeeId: event.target.value }))
							}
							className={dialogControlClassName}
						>
							{employees.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-3">
					<div className="space-y-2">
						<Label htmlFor="recruiting-evaluation-stage">전형 단계</Label>
						<select
							id="recruiting-evaluation-stage"
							value={form.stage}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									stage: event.target.value as RecruitingCandidateStage
								}))
							}
							className={dialogControlClassName}
						>
							{recruitingStageOrder.map((stage) => (
								<option key={stage} value={stage}>
									{recruitingStageLabel(stage)}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-evaluation-score">점수</Label>
						<Input
							id="recruiting-evaluation-score"
							type="number"
							min="1"
							max="5"
							step="0.5"
							value={form.score}
							onChange={(event) =>
								setForm((current) => ({ ...current, score: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-evaluation-recommendation">추천</Label>
						<select
							id="recruiting-evaluation-recommendation"
							value={form.recommendation}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									recommendation: event.target.value as RecruitingRecommendation
								}))
							}
							className={dialogControlClassName}
						>
							{recruitingRecommendationOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="recruiting-evaluation-note">평가 메모</Label>
					<textarea
						id="recruiting-evaluation-note"
						value={form.note}
						onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button
						type="submit"
						disabled={submitting || candidates.length === 0 || employees.length === 0}
					>
						저장
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function RecruitingStageDialog({
	candidates,
	onClose,
	onSubmitted
}: {
	candidates: RecruitingCandidateResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const firstCandidate = candidates[0];
	const [form, setForm] = useState<RecruitingStageForm>({
		candidateId: firstCandidate ? String(firstCandidate.id) : '',
		stage: firstCandidate?.stage ?? 'SCREENING',
		note: ''
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!form.candidateId) {
			setError('단계를 변경할 후보자를 선택해주세요.');
			return;
		}
		const request: UpdateRecruitingCandidateStageRequest = {
			stage: form.stage,
			note: form.note.trim() || undefined
		};
		setSubmitting(true);
		try {
			await api.patch<RecruitingCandidateResponse>(
				`/recruiting/candidates/${form.candidateId}/stage`,
				request
			);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="단계 변경"
			description="후보자의 전형 파이프라인 단계를 업데이트합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-stage-candidate">후보자</Label>
						<select
							id="recruiting-stage-candidate"
							value={form.candidateId}
							onChange={(event) => {
								const nextCandidate = candidates.find(
									(candidate) => String(candidate.id) === event.target.value
								);
								setForm((current) => ({
									...current,
									candidateId: event.target.value,
									stage: nextCandidate?.stage ?? current.stage
								}));
							}}
							className={dialogControlClassName}
						>
							{candidates.map((candidate) => (
								<option key={candidate.id} value={candidate.id}>
									{candidate.name}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-stage-next">전형 단계</Label>
						<select
							id="recruiting-stage-next"
							value={form.stage}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									stage: event.target.value as RecruitingCandidateStage
								}))
							}
							className={dialogControlClassName}
						>
							{recruitingStageOrder.map((stage) => (
								<option key={stage} value={stage}>
									{recruitingStageLabel(stage)}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="recruiting-stage-note">메모</Label>
					<textarea
						id="recruiting-stage-note"
						value={form.note}
						onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || candidates.length === 0}>
						변경
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function RecruitingHireDialog({
	candidates,
	onClose,
	onSubmitted
}: {
	candidates: RecruitingCandidateResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const firstCandidate =
		candidates.find((candidate) => candidate.stage === 'OFFER') ?? candidates[0];
	const [form, setForm] = useState<RecruitingHireForm>({
		candidateId: firstCandidate ? String(firstCandidate.id) : '',
		joinDate: new Date().toISOString().slice(0, 10),
		department: '',
		position: '',
		employeeNumber: ''
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!form.candidateId) {
			setError('합격 처리할 후보자를 선택해주세요.');
			return;
		}
		if (!form.joinDate) {
			setError('입사일을 입력해주세요.');
			return;
		}
		const request: HireRecruitingCandidateRequest = {
			joinDate: form.joinDate,
			department: form.department.trim() || undefined,
			position: form.position.trim() || undefined,
			employeeNumber: form.employeeNumber.trim() || undefined
		};
		setSubmitting(true);
		try {
			await api.post<RecruitingCandidateResponse>(
				`/recruiting/candidates/${form.candidateId}/hire`,
				request
			);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="최종 합격 처리"
			description="후보자를 직원 레코드로 전환합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="recruiting-hire-candidate">후보자</Label>
					<select
						id="recruiting-hire-candidate"
						value={form.candidateId}
						onChange={(event) =>
							setForm((current) => ({ ...current, candidateId: event.target.value }))
						}
						className={dialogControlClassName}
					>
						{candidates.map((candidate) => (
							<option key={candidate.id} value={candidate.id}>
								{candidate.name}
							</option>
						))}
					</select>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-hire-join-date">입사일</Label>
						<Input
							id="recruiting-hire-join-date"
							type="date"
							value={form.joinDate}
							onChange={(event) =>
								setForm((current) => ({ ...current, joinDate: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-hire-employee-number">사원번호</Label>
						<Input
							id="recruiting-hire-employee-number"
							value={form.employeeNumber}
							onChange={(event) =>
								setForm((current) => ({ ...current, employeeNumber: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="recruiting-hire-department">부서</Label>
						<Input
							id="recruiting-hire-department"
							value={form.department}
							onChange={(event) =>
								setForm((current) => ({ ...current, department: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="recruiting-hire-position">직급</Label>
						<Input
							id="recruiting-hire-position"
							value={form.position}
							onChange={(event) =>
								setForm((current) => ({ ...current, position: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || candidates.length === 0}>
						전환
					</Button>
				</div>
			</form>
		</Modal>
	);
}

export function RecruitingPage() {
	const [state, setState] = useState<RequestState<RecruitingPageData>>({ status: 'idle' });
	const [jobDialogOpen, setJobDialogOpen] = useState(false);
	const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
	const [stageDialogOpen, setStageDialogOpen] = useState(false);
	const [evaluationDialogOpen, setEvaluationDialogOpen] = useState(false);
	const [hireDialogOpen, setHireDialogOpen] = useState(false);
	const [privacyDeletionSubmitting, setPrivacyDeletionSubmitting] = useState(false);
	const [privacyDeletionMessage, setPrivacyDeletionMessage] = useState<string | null>(null);
	const [privacyDeletionError, setPrivacyDeletionError] = useState<string | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [summary, jobs, candidates, employeesPage] = await Promise.all([
					api.get<RecruitingSummaryResponse>('/recruiting/summary'),
					api.get<RecruitingJobPostingResponse[]>('/recruiting/jobs'),
					api.get<RecruitingCandidateResponse[]>('/recruiting/candidates'),
					api.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200')
				]);
				setState({
					status: 'success',
					data: { summary, jobs, candidates, employees: employeesPage.content }
				});
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const activeCandidates =
		data?.candidates.filter(
			(candidate) => candidate.stage !== 'HIRED' && candidate.stage !== 'REJECTED'
		) ?? [];
	const offerCandidates = data?.candidates.filter((candidate) => candidate.stage === 'OFFER') ?? [];
	const privacyDeletionActionCount =
		(data?.summary.privacyDeletionDueSoonCount ?? 0) +
		(data?.summary.privacyDeletionOverdueCount ?? 0);

	async function runPrivacyDeletion() {
		setPrivacyDeletionSubmitting(true);
		setPrivacyDeletionError(null);
		setPrivacyDeletionMessage(null);
		try {
			const result = await api.post<RecruitingPrivacyDeletionRunResponse>(
				'/recruiting/candidates/privacy-deletion/run'
			);
			setPrivacyDeletionMessage(
				`${numberLabel(result.deletedCount)}명 후보자 개인정보를 삭제했습니다.`
			);
			await load();
		} catch (error) {
			setPrivacyDeletionError(getErrorMessage(error, '개인정보 삭제를 실행하지 못했습니다.'));
		} finally {
			setPrivacyDeletionSubmitting(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="채용"
				description="공고, 후보자 전형, 평가와 최종 합격 처리를 한 흐름으로 관리합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						<Button type="button" variant="outline" onClick={() => setJobDialogOpen(true)}>
							<Plus className="size-4" />
							공고 만들기
						</Button>
						<Button type="button" variant="outline" onClick={() => setCandidateDialogOpen(true)}>
							<UsersRound className="size-4" />
							후보자 등록
						</Button>
						<Button type="button" variant="outline" onClick={() => setStageDialogOpen(true)}>
							<GitBranch className="size-4" />
							단계 변경
						</Button>
						<Button type="button" variant="outline" onClick={() => setEvaluationDialogOpen(true)}>
							<ClipboardCheck className="size-4" />
							평가 등록
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => void runPrivacyDeletion()}
							disabled={
								privacyDeletionSubmitting || (data?.summary.privacyDeletionOverdueCount ?? 0) === 0
							}
						>
							<Trash2 className="size-4" />
							{privacyDeletionSubmitting ? '삭제 실행 중...' : '기한 초과 삭제 실행'}
						</Button>
						<Button type="button" onClick={() => setHireDialogOpen(true)}>
							<BadgeCheck className="size-4" />
							합격 처리
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{privacyDeletionError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{privacyDeletionError}
				</div>
			) : null}
			{privacyDeletionMessage ? (
				<div className="rounded-lg bg-success/10 px-4 py-3 text-sm text-success">
					{privacyDeletionMessage}
				</div>
			) : null}

			{data ? (
				<>
					<div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-4 sm:overflow-visible sm:px-0 xl:grid-cols-6">
						<RecruitingStatCard
							title="공개 공고"
							value={numberLabel(data.summary.openJobCount)}
							description="지원 가능한 포지션"
						/>
						<RecruitingStatCard
							title="전체 후보자"
							value={numberLabel(data.summary.totalCandidateCount)}
							description="누적 지원자"
						/>
						<RecruitingStatCard
							title="진행 중"
							value={numberLabel(data.summary.activeCandidateCount)}
							description="검토 중인 후보자"
						/>
						<RecruitingStatCard
							title="평가 대기"
							value={numberLabel(data.summary.pendingEvaluationCount)}
							description="인터뷰 후 기록 필요"
						/>
						<RecruitingStatCard
							title="평균 점수"
							value={data.summary.averageScore ? String(data.summary.averageScore) : '-'}
							description={`합격 ${numberLabel(data.summary.hiredCandidateCount)}명`}
						/>
						<RecruitingStatCard
							title="개인정보 삭제"
							value={numberLabel(privacyDeletionActionCount)}
							description={`예정 ${numberLabel(data.summary.privacyDeletionDueSoonCount)}명 · 초과 ${numberLabel(
								data.summary.privacyDeletionOverdueCount
							)}명`}
						/>
					</div>

					<RecruitingSiteReadinessPanel summary={data.summary} />

					<div className="grid min-w-0 gap-6 xl:grid-cols-[1fr_24rem]">
						<Card className="min-w-0">
							<CardHeader>
								<CardTitle>후보자 파이프라인</CardTitle>
								<CardDescription>
									공고, 전형 단계, 평가 평균과 최근 평가를 함께 확인합니다.
								</CardDescription>
							</CardHeader>
							<CardContent>
								{data.candidates.length === 0 ? (
									<EmptyState message="등록된 후보자가 없습니다." />
								) : (
									<div className="min-w-0 overflow-x-auto rounded-lg border border-border">
										<table className="w-full min-w-[900px] text-left text-sm">
											<thead className="bg-muted/40 text-xs text-muted-foreground">
												<tr>
													<th className="px-4 py-3 font-semibold">후보자</th>
													<th className="px-4 py-3 font-semibold">공고</th>
													<th className="px-4 py-3 font-semibold">단계</th>
													<th className="px-4 py-3 font-semibold">평가</th>
													<th className="px-4 py-3 font-semibold">최근 기록</th>
													<th className="px-4 py-3 font-semibold">보존</th>
													<th className="px-4 py-3 font-semibold">지원일</th>
												</tr>
											</thead>
											<tbody>
												{data.candidates.map((candidate) => {
													const latestEvaluation = candidate.evaluations[0];
													return (
														<tr key={candidate.id} className="border-t border-border align-top">
															<td className="px-4 py-4">
																<p className="font-semibold">{candidate.name}</p>
																<p className="mt-1 text-xs text-muted-foreground">
																	{candidate.email ?? '이메일 없음'} ·{' '}
																	{candidate.source ?? '경로 없음'}
																</p>
															</td>
															<td className="px-4 py-4">
																<p className="font-medium">{candidate.jobTitle ?? '공고 없음'}</p>
																<p className="mt-1 text-xs text-muted-foreground">
																	{candidate.resumeUrl ? '이력서 링크 있음' : '이력서 링크 없음'}
																</p>
															</td>
															<td className="px-4 py-4">
																<Badge variant={recruitingStageVariant(candidate.stage)}>
																	{recruitingStageLabel(candidate.stage)}
																</Badge>
																<p className="mt-2 text-xs text-muted-foreground">
																	{candidate.stageNote ?? '메모 없음'}
																</p>
															</td>
															<td className="px-4 py-4">
																<p className="font-semibold">
																	{candidate.evaluationCount > 0 ? candidate.averageScore : '-'}
																</p>
																<p className="mt-1 text-xs text-muted-foreground">
																	{numberLabel(candidate.evaluationCount)}개 평가
																</p>
															</td>
															<td className="px-4 py-4">
																{latestEvaluation ? (
																	<div className="space-y-1">
																		<p className="font-medium">
																			{recruitingRecommendationLabel(
																				latestEvaluation.recommendation
																			)}
																		</p>
																		<p className="text-xs text-muted-foreground">
																			{latestEvaluation.evaluatorName ?? '평가자 없음'} ·{' '}
																			{latestEvaluation.note ?? '메모 없음'}
																		</p>
																	</div>
																) : (
																	<span className="text-muted-foreground">평가 없음</span>
																)}
															</td>
															<td className="px-4 py-4">
																{recruitingPrivacyDeletionLabel(candidate) ? (
																	<Badge
																		variant={
																			candidate.privacyDeleted
																				? 'outline'
																				: candidate.privacyDeletionDueSoon
																					? 'warning'
																					: 'secondary'
																		}
																	>
																		{recruitingPrivacyDeletionLabel(candidate)}
																	</Badge>
																) : (
																	<span className="text-muted-foreground">보존 중</span>
																)}
															</td>
															<td className="px-4 py-4 text-muted-foreground">
																{dateLabel(candidate.appliedAt)}
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								)}
							</CardContent>
						</Card>

						<div className="space-y-6">
							<RecruitingDecisionCandidates candidates={data.summary.decisionCandidates ?? []} />

							<Card>
								<CardHeader>
									<CardTitle>체크리스트</CardTitle>
									<CardDescription>놓치기 쉬운 채용 오퍼레이션을 모아봅니다.</CardDescription>
								</CardHeader>
								<CardContent className="space-y-3">
									{data.summary.checklist.length === 0 ? (
										<EmptyState message="처리할 채용 작업이 없습니다." />
									) : (
										data.summary.checklist.map((item) => (
											<div key={item.type} className="rounded-lg border border-border p-3">
												<div className="flex items-start justify-between gap-3">
													<div>
														<p className="font-semibold">{item.title}</p>
														<p className="mt-1 text-xs text-muted-foreground">{item.description}</p>
													</div>
													<Badge variant="warning">{numberLabel(item.count)}건</Badge>
												</div>
											</div>
										))
									)}
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>공고</CardTitle>
									<CardDescription>공개 상태와 지원자 수를 확인합니다.</CardDescription>
								</CardHeader>
								<CardContent className="space-y-3">
									{data.jobs.length === 0 ? (
										<EmptyState message="등록된 채용 공고가 없습니다." />
									) : (
										data.jobs.map((job) => (
											<div key={job.id} className="rounded-lg border border-border p-3">
												<div className="flex items-start justify-between gap-3">
													<div>
														<p className="font-semibold">{job.title}</p>
														<p className="mt-1 text-xs text-muted-foreground">
															{job.department ?? '부서 없음'} · {job.location ?? '근무지 없음'}
														</p>
													</div>
													<Badge variant={job.status === 'OPEN' ? 'success' : 'outline'}>
														{recruitingJobStatusLabel(job.status)}
													</Badge>
												</div>
												<div className="mt-3 flex flex-wrap gap-2">
													<Badge variant="secondary">
														{numberLabel(job.candidateCount)}명 지원
													</Badge>
													{job.publicSlug ? (
														<Badge variant="outline">{job.publicSlug}</Badge>
													) : null}
												</div>
											</div>
										))
									)}
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>단계별 현황</CardTitle>
									<CardDescription>후보자 전형 분포입니다.</CardDescription>
								</CardHeader>
								<CardContent className="space-y-2">
									{recruitingStageOrder.map((stage) => {
										const count = data.candidates.filter(
											(candidate) => candidate.stage === stage
										).length;
										return (
											<div key={stage} className="flex items-center justify-between gap-3 text-sm">
												<span className="text-muted-foreground">{recruitingStageLabel(stage)}</span>
												<Badge variant={recruitingStageVariant(stage)}>
													{numberLabel(count)}명
												</Badge>
											</div>
										);
									})}
								</CardContent>
							</Card>
						</div>
					</div>

					{jobDialogOpen ? (
						<RecruitingJobDialog
							employees={data.employees}
							onClose={() => setJobDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{candidateDialogOpen ? (
						<RecruitingCandidateDialog
							jobs={data.jobs}
							onClose={() => setCandidateDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{stageDialogOpen ? (
						<RecruitingStageDialog
							candidates={activeCandidates}
							onClose={() => setStageDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{evaluationDialogOpen ? (
						<RecruitingEvaluationDialog
							candidates={activeCandidates}
							employees={data.employees}
							onClose={() => setEvaluationDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
					{hireDialogOpen ? (
						<RecruitingHireDialog
							candidates={offerCandidates.length > 0 ? offerCandidates : activeCandidates}
							onClose={() => setHireDialogOpen(false)}
							onSubmitted={load}
						/>
					) : null}
				</>
			) : null}
		</div>
	);
}

type PayrollPageData = {
	settings: PayrollSettings;
	templates: PayrollTemplateSummary[];
	payslips: PayslipSummaryResponse[];
	preview: PayslipGenerationPreviewResponse | null;
	settlement: PayrollSettlementResponse | null;
	payYear: number;
	payMonth: number;
};

type PayrollEmployeeRow = {
	employeeId: number;
	employeeName: string | null;
	template: PayrollTemplateSummary | null;
	payslip: PayslipSummaryResponse | null;
	ready: PayslipGenerationReadyEmployee | null;
	skippedReason: string | null;
	action: PayrollAction | null;
};

function currentPayrollPeriod(): { payYear: number; payMonth: number } {
	const now = new Date();
	return { payYear: now.getFullYear(), payMonth: now.getMonth() + 1 };
}

function payrollMonthLabel(payYear: number, payMonth: number): string {
	return `${payYear}년 ${payMonth}월`;
}

function paydayLabel(settings: PayrollSettings): string {
	return `${settings.paymentMonthOffset === 1 ? '익월' : '당월'} ${settings.paymentDay}일`;
}

function payrollStatusLabel(status: PayslipSummaryResponse['status'] | null | undefined): string {
	const labels: Record<PayslipSummaryResponse['status'], string> = {
		DRAFT: '초안',
		CONFIRMED: '확정',
		CANCELLED: '취소'
	};
	return status ? labels[status] : '-';
}

function payrollStatusVariant(
	status: PayslipSummaryResponse['status'] | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'CONFIRMED') return 'success';
	if (status === 'DRAFT') return 'warning';
	if (status === 'CANCELLED') return 'destructive';
	return 'outline';
}

function payrollSettlementStatusLabel(status: PayrollSettlementStatus | null | undefined): string {
	const labels: Record<PayrollSettlementStatus, string> = {
		EMPTY: '대상 없음',
		BLOCKED: '점검 필요',
		READY_TO_GENERATE: '생성 준비',
		READY_TO_CONFIRM: '확정 대기',
		CONFIRMED: '정산 완료',
		IN_PROGRESS: '진행 중'
	};
	return status ? labels[status] : '-';
}

function payrollSettlementStatusVariant(
	status: PayrollSettlementStatus | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'CONFIRMED') return 'success';
	if (status === 'READY_TO_CONFIRM' || status === 'READY_TO_GENERATE' || status === 'IN_PROGRESS') {
		return 'warning';
	}
	if (status === 'BLOCKED') return 'destructive';
	return 'outline';
}

function payrollChecklistVariant(
	status: PayrollSettlementChecklistItem['status'] | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING' || status === 'WAITING') return 'warning';
	if (status === 'BLOCKED') return 'destructive';
	return 'outline';
}

function payrollChecklistStatusLabel(
	status: PayrollSettlementChecklistItem['status'] | null | undefined
): string {
	const labels: Record<PayrollSettlementChecklistItem['status'], string> = {
		READY: '완료',
		WAITING: '대기',
		PENDING: '예정',
		WARNING: '주의',
		BLOCKED: '차단'
	};
	return status ? labels[status] : '-';
}

function payrollLedgerStatusLabel(
	status: PayrollSettlementResponse['ledgerStatus'] | null | undefined
): string {
	if (status === 'READY_TO_EXPORT') return '전표 가능';
	if (status === 'WAITING_CONFIRMATION') return '전표 대기';
	if (status === 'NEEDS_GENERATION') return '생성 필요';
	if (status === 'BLOCKED') return '차단';
	return '-';
}

function payrollRiskVariant(level: PayrollSettlementResponse['riskLevel'] | null | undefined) {
	if (level === 'HIGH') return 'destructive';
	if (level === 'MEDIUM') return 'warning';
	return 'success';
}

function payrollRiskIcon(
	level: PayrollSettlementResponse['riskLevel'] | null | undefined
): ReactNode {
	if (level === 'HIGH') return <AlertTriangle className="size-4" />;
	if (level === 'MEDIUM') return <Clock3 className="size-4" />;
	return <CheckCircle2 className="size-4" />;
}

function payrollTemplateLabel(template: PayrollTemplateSummary | null): string {
	if (!template) return '미등록';
	return `${numberLabel(template.itemCount)}개 항목`;
}

function wonLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
	return `${numberLabel(value)}원`;
}

function buildPayrollRows(data: PayrollPageData): PayrollEmployeeRow[] {
	const employeeIds = new Set<number>();
	const employeeNames = new Map<number, string>();
	const templateMap = new Map<number, PayrollTemplateSummary>();
	const payslipMap = new Map<number, PayslipSummaryResponse>();
	const readyMap = new Map<number, PayslipGenerationReadyEmployee>();
	const skippedMap = new Map<number, { reason: string; action: PayrollAction | null }>();
	const rememberEmployeeName = (employeeId: number, employeeName?: string | null) => {
		if (employeeName) employeeNames.set(employeeId, employeeName);
	};

	for (const template of data.templates) {
		employeeIds.add(template.employeeId);
		rememberEmployeeName(template.employeeId, template.employeeName);
		templateMap.set(template.employeeId, template);
	}
	for (const payslip of data.payslips) {
		employeeIds.add(payslip.employeeId);
		rememberEmployeeName(payslip.employeeId, payslip.employeeName);
		payslipMap.set(payslip.employeeId, payslip);
	}
	for (const employeeId of data.preview?.readyEmployeeIds ?? []) {
		employeeIds.add(employeeId);
	}
	for (const ready of data.preview?.readyEmployees ?? []) {
		employeeIds.add(ready.employeeId);
		rememberEmployeeName(ready.employeeId, ready.employeeName);
		readyMap.set(ready.employeeId, ready);
	}
	for (const skipped of data.preview?.skipped ?? []) {
		employeeIds.add(skipped.employeeId);
		rememberEmployeeName(skipped.employeeId, skipped.employeeName);
		skippedMap.set(skipped.employeeId, { reason: skipped.reason, action: skipped.action ?? null });
	}

	return Array.from(employeeIds)
		.sort((a, b) => a - b)
		.map((employeeId) => ({
			employeeId,
			employeeName: employeeNames.get(employeeId) ?? null,
			template: templateMap.get(employeeId) ?? null,
			payslip: payslipMap.get(employeeId) ?? null,
			ready: readyMap.get(employeeId) ?? null,
			skippedReason: skippedMap.get(employeeId)?.reason ?? null,
			action: skippedMap.get(employeeId)?.action ?? null
		}));
}

function payrollEmployeeLabel(
	employee: Pick<PayrollEmployeeRow, 'employeeId' | 'employeeName'>
): string {
	return employee.employeeName ?? `직원 #${employee.employeeId}`;
}

function PayrollStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: string;
	description: string;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<p className="text-sm font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal sm:mt-3">{value}</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function PayrollChecklistItemView({ item }: { item: PayrollSettlementChecklistItem }) {
	const Icon =
		item.status === 'READY' ? CheckCircle2 : item.status === 'BLOCKED' ? AlertTriangle : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={payrollChecklistVariant(item.status)}>
					{payrollChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function PayrollSettlementIssues({ issues }: { issues: PayrollSettlementIssue[] }) {
	if (issues.length === 0) {
		return (
			<div className="rounded-lg border border-border bg-success/5 px-4 py-3 text-sm text-success">
				차단 이슈 없음
			</div>
		);
	}
	return (
		<div className="space-y-2">
			{issues.map((issue) => (
				<div
					key={`${issue.employeeId}-${issue.code}`}
					className="rounded-lg border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm"
				>
					<div className="flex flex-wrap items-center gap-2">
						<Badge variant={issue.severity === 'ERROR' ? 'destructive' : 'warning'}>
							{issue.severity === 'ERROR' ? '오류' : '주의'}
						</Badge>
						<span className="font-semibold">
							{issue.employeeName ?? `직원 #${issue.employeeId}`}
						</span>
						<span className="text-muted-foreground">{issue.code}</span>
					</div>
					<p className="mt-2 text-muted-foreground">{issue.message}</p>
				</div>
			))}
		</div>
	);
}

function PayrollBasisMobileCard({ payslip }: { payslip: PayrollSettlementPayslipBasis }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4 md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div>
					<p className="font-semibold">{payslip.employeeName ?? `직원 #${payslip.employeeId}`}</p>
					<p className="mt-1 text-sm text-muted-foreground">{payslip.calculationBasis}</p>
				</div>
				<Badge variant={payrollStatusVariant(payslip.status)}>
					{payrollStatusLabel(payslip.status)}
				</Badge>
			</div>
			<div className="mt-4 grid grid-cols-3 gap-3 text-sm">
				<div>
					<p className="text-xs text-muted-foreground">지급</p>
					<p className="mt-1 font-semibold">{wonLabel(payslip.grossPay)}</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">공제</p>
					<p className="mt-1 font-semibold">{wonLabel(payslip.totalDeduction)}</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">실수령</p>
					<p className="mt-1 font-semibold">{wonLabel(payslip.netPay)}</p>
				</div>
			</div>
		</div>
	);
}

function PayrollBasisTable({ payslips }: { payslips: PayrollSettlementPayslipBasis[] }) {
	if (payslips.length === 0) return <EmptyState message="생성된 급여 산출 근거가 없습니다." />;

	return (
		<>
			<div className="space-y-3 md:hidden">
				{payslips.map((payslip) => (
					<PayrollBasisMobileCard key={payslip.id} payslip={payslip} />
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[980px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">직원</th>
							<th className="px-4 py-3 font-semibold">상태</th>
							<th className="px-4 py-3 font-semibold">산출 근거</th>
							<th className="px-4 py-3 text-right font-semibold">지급</th>
							<th className="px-4 py-3 text-right font-semibold">공제</th>
							<th className="px-4 py-3 text-right font-semibold">실수령</th>
						</tr>
					</thead>
					<tbody>
						{payslips.map((payslip) => (
							<tr key={payslip.id} className="border-t border-border">
								<td className="px-4 py-4 font-semibold">
									{payslip.employeeName ?? `직원 #${payslip.employeeId}`}
								</td>
								<td className="px-4 py-4">
									<Badge variant={payrollStatusVariant(payslip.status)}>
										{payrollStatusLabel(payslip.status)}
									</Badge>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									<div className="max-w-xl">
										<p>{payslip.calculationBasis}</p>
										<p className="mt-1 text-xs">
											{payslip.policyApplied ? '정책 적용' : '정책 미적용'} · 지급일{' '}
											{dateLabel(payslip.paymentDate)}
										</p>
									</div>
								</td>
								<td className="px-4 py-4 text-right font-medium">{wonLabel(payslip.grossPay)}</td>
								<td className="px-4 py-4 text-right text-muted-foreground">
									{wonLabel(payslip.totalDeduction)}
								</td>
								<td className="px-4 py-4 text-right font-semibold">{wonLabel(payslip.netPay)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

function PayrollSettlementPanel({
	settlement,
	onGenerate,
	onConfirm,
	onResend,
	generating,
	confirming,
	resending,
	actionError
}: {
	settlement: PayrollSettlementResponse | null;
	onGenerate: () => void;
	onConfirm: () => void;
	onResend: () => void;
	generating: boolean;
	confirming: boolean;
	resending: boolean;
	actionError: string | null;
}) {
	if (!settlement) return null;
	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>급여 정산</CardTitle>
					<CardDescription>
						{payrollMonthLabel(settlement.payYear, settlement.payMonth)} 정산 상태와 산출 근거를
						확인합니다.
					</CardDescription>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Badge variant={payrollSettlementStatusVariant(settlement.status)}>
						{payrollSettlementStatusLabel(settlement.status)}
					</Badge>
					<Badge variant={settlement.ledgerStatus === 'READY_TO_EXPORT' ? 'success' : 'outline'}>
						{payrollLedgerStatusLabel(settlement.ledgerStatus)}
					</Badge>
					<Button
						type="button"
						variant="outline"
						onClick={onGenerate}
						disabled={generating || confirming || settlement.status === 'CONFIRMED'}
					>
						{generating ? '초안 생성 중...' : '초안 생성'}
					</Button>
					<Button
						type="button"
						onClick={onConfirm}
						disabled={generating || confirming || settlement.draftCount === 0}
					>
						{confirming ? '확정 중...' : '확정·발행'}
					</Button>
					{settlement.confirmedCount > 0 ? (
						<Button
							type="button"
							variant="outline"
							onClick={onResend}
							disabled={generating || confirming || resending}
						>
							<Bell className="size-4" />
							{resending ? '재발송 중...' : '알림 재발송'}
						</Button>
					) : null}
				</div>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="rounded-lg border border-border bg-muted/20 p-4">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-2">
								<span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground">
									{payrollRiskIcon(settlement.riskLevel)}
								</span>
								<Badge variant={payrollRiskVariant(settlement.riskLevel)}>
									{settlement.riskLabel}
								</Badge>
							</div>
							<p className="mt-3 text-sm font-semibold">{settlement.riskSummary}</p>
							<p className="mt-1 text-sm text-muted-foreground">{settlement.recommendedAction}</p>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
					<div className="rounded-lg border border-border bg-background p-4">
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<UsersRound className="size-4" />
							<span>정산 대상</span>
						</div>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(settlement.targetEmployeeCount)}명
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							생성 {numberLabel(settlement.generatedCount)}명
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<ClipboardCheck className="size-4" />
							<span>확정 현황</span>
						</div>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(settlement.confirmedCount)} / {numberLabel(settlement.generatedCount)}건
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							초안 {numberLabel(settlement.draftCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<ShieldCheck className="size-4" />
							<span>공제 합계</span>
						</div>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{wonLabel(settlement.totalDeduction)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							지급 {wonLabel(settlement.grossPayTotal)}
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<WalletCards className="size-4" />
							<span>실수령 합계</span>
						</div>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{wonLabel(settlement.netPayTotal)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							지급일 {dateLabel(settlement.paymentDate)}
						</p>
					</div>
				</div>

				<div className="grid gap-3 lg:grid-cols-2">
					{settlement.checklist.map((item) => (
						<PayrollChecklistItemView key={item.code} item={item} />
					))}
				</div>

				<PayrollSettlementIssues issues={settlement.issues} />
				{actionError ? <FormError message={actionError} /> : null}

				<div className="space-y-3">
					<div>
						<h3 className="text-sm font-semibold tracking-normal">산출 근거</h3>
						<p className="mt-1 text-sm text-muted-foreground">
							정책 버전, 세액표, 지급·공제 항목 구성을 직원별로 확인합니다.
						</p>
					</div>
					<PayrollBasisTable payslips={settlement.payslips} />
				</div>
			</CardContent>
		</Card>
	);
}

function PayrollReadinessBadge({ row }: { row: PayrollEmployeeRow }) {
	if (row.payslip) {
		return (
			<Badge variant={payrollStatusVariant(row.payslip.status)}>
				{payrollStatusLabel(row.payslip.status)}
			</Badge>
		);
	}
	if (row.skippedReason === 'PAYROLL_EXCLUDED') return <Badge variant="outline">정산 제외</Badge>;
	if (row.skippedReason) return <Badge variant="destructive">제외</Badge>;
	if (row.ready) return <Badge variant="success">생성 가능</Badge>;
	return <Badge variant="outline">미생성</Badge>;
}

function PayrollRowAction({
	row,
	onTemplateSetup
}: {
	row: PayrollEmployeeRow;
	onTemplateSetup: (row: PayrollEmployeeRow) => void;
}) {
	const employeeName = payrollEmployeeLabel(row);
	if (row.action === 'CREATE_TEMPLATE') {
		return (
			<Button
				type="button"
				variant="outline"
				size="sm"
				onClick={() => onTemplateSetup(row)}
				aria-label={`${employeeName} 템플릿 설정`}
			>
				템플릿 설정
			</Button>
		);
	}
	if (row.action === 'INCLUDE_IN_PAYROLL') {
		return (
			<Link
				href={`/employees/${row.employeeId}`}
				aria-label={`${employeeName} 급여 정산 설정`}
				className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-2 text-xs font-semibold transition-colors hover:bg-muted"
			>
				설정 변경
			</Link>
		);
	}
	if (row.action === 'OPEN_CONTRACTS') {
		return (
			<Link
				href="/digital-contracts"
				aria-label={`${employeeName} 근로계약서 확인`}
				className="inline-flex h-8 items-center justify-center rounded-md border border-border bg-background px-2 text-xs font-semibold transition-colors hover:bg-muted"
			>
				계약 확인
			</Link>
		);
	}
	return <span className="text-xs text-muted-foreground">-</span>;
}

function PayrollMobileCard({
	row,
	onTemplateSetup
}: {
	row: PayrollEmployeeRow;
	onTemplateSetup: (row: PayrollEmployeeRow) => void;
}) {
	return (
		<div className="rounded-lg border border-border bg-card p-4 shadow-sm md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="font-semibold">{payrollEmployeeLabel(row)}</p>
					<p className="mt-1 text-sm text-muted-foreground">{payrollTemplateLabel(row.template)}</p>
				</div>
				<PayrollReadinessBadge row={row} />
			</div>
			<div className="mt-3 grid grid-cols-2 gap-3 text-sm text-muted-foreground">
				<div>
					<p className="text-xs font-medium">기본급</p>
					<p className="mt-1 text-foreground">{wonLabel(row.ready?.basicPay)}</p>
				</div>
				<div>
					<p className="text-xs font-medium">지급일</p>
					<p className="mt-1 text-foreground">{dateLabel(row.payslip?.paymentDate)}</p>
				</div>
			</div>
			{row.skippedReason ? (
				<div className="mt-3 flex flex-wrap items-center justify-between gap-2">
					<p
						className={
							row.skippedReason === 'PAYROLL_EXCLUDED'
								? 'text-sm text-muted-foreground'
								: 'text-sm text-destructive'
						}
					>
						{skipReasonLabel(row.skippedReason)}
					</p>
					<PayrollRowAction row={row} onTemplateSetup={onTemplateSetup} />
				</div>
			) : null}
		</div>
	);
}

function PayrollTable({
	rows,
	onTemplateSetup
}: {
	rows: PayrollEmployeeRow[];
	onTemplateSetup: (row: PayrollEmployeeRow) => void;
}) {
	if (rows.length === 0) return <EmptyState message="급여 운영 데이터가 없습니다." />;

	return (
		<>
			<div className="space-y-3 md:hidden">
				{rows.map((row) => (
					<PayrollMobileCard key={row.employeeId} row={row} onTemplateSetup={onTemplateSetup} />
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[920px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">직원</th>
							<th className="px-4 py-3 font-semibold">템플릿</th>
							<th className="px-4 py-3 font-semibold">기본급</th>
							<th className="px-4 py-3 font-semibold">산정 기간</th>
							<th className="px-4 py-3 font-semibold">상태</th>
							<th className="px-4 py-3 font-semibold">지급일</th>
							<th className="px-4 py-3 text-right font-semibold">명세서</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => {
							const period =
								row.ready?.basicPayPeriodStart && row.ready?.basicPayPeriodEnd
									? `${dateLabel(row.ready.basicPayPeriodStart)} - ${dateLabel(row.ready.basicPayPeriodEnd)}`
									: '-';
							return (
								<tr key={row.employeeId} className="border-t border-border">
									<td className="px-4 py-4">
										<Link
											href={`/payroll/payslips/${row.employeeId}`}
											className="font-semibold text-primary hover:underline"
										>
											{payrollEmployeeLabel(row)}
										</Link>
										{row.skippedReason ? (
											<p
												className={
													row.skippedReason === 'PAYROLL_EXCLUDED'
														? 'mt-1 text-xs text-muted-foreground'
														: 'mt-1 text-xs text-destructive'
												}
											>
												{skipReasonLabel(row.skippedReason)}
											</p>
										) : null}
									</td>
									<td className="px-4 py-4 text-muted-foreground">
										<div>
											<p>{payrollTemplateLabel(row.template)}</p>
											{row.template?.memo ? (
												<p className="mt-1 text-xs">{row.template.memo}</p>
											) : null}
										</div>
									</td>
									<td className="px-4 py-4 text-muted-foreground">
										{wonLabel(row.ready?.basicPay)}
									</td>
									<td className="px-4 py-4 text-muted-foreground">{period}</td>
									<td className="px-4 py-4">
										<PayrollReadinessBadge row={row} />
									</td>
									<td className="px-4 py-4 text-muted-foreground">
										{dateLabel(row.payslip?.paymentDate)}
									</td>
									<td className="px-4 py-4 text-right">
										{row.payslip ? (
											<Link
												href={`/payroll/payslips/${row.employeeId}/${row.payslip.id}`}
												aria-label={`직원 #${row.employeeId} 급여명세서 보기`}
												className="inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
											>
												보기
												<ChevronRight className="size-4" />
											</Link>
										) : (
											<PayrollRowAction row={row} onTemplateSetup={onTemplateSetup} />
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</>
	);
}

function PayrollTemplateEditorModal({
	employeeId,
	employeeName,
	initialMemo,
	onClose,
	onSaved
}: {
	employeeId: number;
	employeeName: string;
	initialMemo: string;
	onClose: () => void;
	onSaved: () => Promise<void>;
}) {
	const [memo, setMemo] = useState(initialMemo);
	const [items, setItems] = useState<EarningItemRequest[]>([]);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function addPreset(label: string) {
		const preset = EARNING_PRESETS.find((candidate) => candidate.label === label);
		if (!preset) return;
		setItems((current) => [...current, { ...preset.row }]);
	}

	function updateItem(index: number, patch: Partial<EarningItemRequest>) {
		setItems((current) =>
			current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
		);
	}

	function removeItem(index: number) {
		setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
	}

	async function save(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (items.length === 0) {
			setError('지급 항목을 1개 이상 추가해주세요.');
			return;
		}
		const normalizedItems = items.map((item) => ({
			code: item.code.trim(),
			name: item.name.trim(),
			taxable: item.taxable,
			amount: item.amount,
			description: item.description?.trim() || undefined
		}));
		if (
			normalizedItems.some(
				(item) => !item.code || !item.name || !Number.isFinite(item.amount) || item.amount < 0
			)
		) {
			setError('모든 지급 항목의 코드, 이름, 0원 이상의 금액을 입력해주세요.');
			return;
		}

		setSaving(true);
		setError(null);
		try {
			const request: PayrollTemplateUpsertRequest = {
				memo: memo.trim(),
				items: normalizedItems
			};
			await api.put<PayrollTemplateResponse>(`/employees/${employeeId}/payroll-template`, request);
			await onSaved();
		} catch (error) {
			setError(getErrorMessage(error, '급여 템플릿을 저장하지 못했습니다.'));
		} finally {
			setSaving(false);
		}
	}

	return (
		<Modal
			title={`${employeeName} 급여 템플릿 설정`}
			description="월 급여 산정에 사용할 고정 지급 항목을 입력합니다."
			onClose={onClose}
		>
			<form className="space-y-5" onSubmit={(event) => void save(event)}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="payroll-template-memo">메모</Label>
					<Input
						id="payroll-template-memo"
						value={memo}
						onChange={(event) => setMemo(event.target.value)}
						placeholder="예: 정기 급여"
					/>
				</div>
				<div className="space-y-3">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div>
							<p className="font-semibold">지급 항목</p>
							<p className="mt-1 text-sm text-muted-foreground">
								필요한 항목을 추가하고 월 금액을 입력하세요.
							</p>
						</div>
						<div className="flex flex-wrap gap-2">
							{EARNING_PRESETS.map((preset) => (
								<Button
									key={preset.label}
									type="button"
									variant="outline"
									size="sm"
									onClick={() => addPreset(preset.label)}
								>
									{preset.label} 추가
								</Button>
							))}
						</div>
					</div>
					{items.length === 0 ? (
						<p className="rounded-lg border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground">
							지급 항목을 추가해주세요.
						</p>
					) : (
						<div className="space-y-3">
							{items.map((item, index) => {
								const itemLabel = item.name || `지급 항목 ${index + 1}`;
								return (
									<div
										key={`${item.code}-${index}`}
										className="rounded-lg border border-border bg-muted/20 p-4"
									>
										<div className="grid gap-3 sm:grid-cols-2">
											<div className="space-y-2">
												<Label htmlFor={`payroll-template-item-${index}-name`}>이름</Label>
												<Input
													id={`payroll-template-item-${index}-name`}
													value={item.name}
													onChange={(event) => updateItem(index, { name: event.target.value })}
												/>
											</div>
											<div className="space-y-2">
												<Label htmlFor={`payroll-template-item-${index}-code`}>코드</Label>
												<Input
													id={`payroll-template-item-${index}-code`}
													value={item.code}
													onChange={(event) => updateItem(index, { code: event.target.value })}
												/>
											</div>
											<div className="space-y-2">
												<Label htmlFor={`payroll-template-item-${index}-amount`}>
													{itemLabel} 금액
												</Label>
												<Input
													id={`payroll-template-item-${index}-amount`}
													type="number"
													min="0"
													value={item.amount}
													onChange={(event) =>
														updateItem(index, { amount: Number(event.target.value) })
													}
												/>
											</div>
											<label className="flex items-end gap-2 pb-2 text-sm text-muted-foreground">
												<input
													type="checkbox"
													checked={item.taxable}
													onChange={(event) => updateItem(index, { taxable: event.target.checked })}
												/>
												과세
											</label>
										</div>
										<div className="mt-3 flex justify-end">
											<Button
												type="button"
												variant="ghost"
												size="sm"
												onClick={() => removeItem(index)}
											>
												<Trash2 className="size-4" />
												삭제
											</Button>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose} disabled={saving}>
						취소
					</Button>
					<Button type="submit" disabled={saving}>
						<Save className="size-4" />
						{saving ? '저장 중...' : '템플릿 저장'}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function PayrollHistoricalPayslipModal({
	onClose,
	onSaved
}: {
	onClose: () => void;
	onSaved: () => Promise<void>;
}) {
	const now = new Date();
	const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
	const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
	const [employeeId, setEmployeeId] = useState('');
	const [payYear, setPayYear] = useState(String(previousMonth.getFullYear()));
	const [payMonth, setPayMonth] = useState(String(previousMonth.getMonth() + 1));
	const [paymentDate, setPaymentDate] = useState('');
	const [memo, setMemo] = useState('');
	const [earnings, setEarnings] = useState<EarningItemRequest[]>([]);
	const [deductions, setDeductions] = useState<EarningItemRequest[]>([]);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let active = true;
		void api
			.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200')
			.then((page) => {
				if (active) setEmployees(page.content);
			})
			.catch(() => {
				if (active) setEmployees([]);
			});
		return () => {
			active = false;
		};
	}, []);

	const grossTotal = earnings.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
	const deductionTotal = deductions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

	function addRow(
		setRows: typeof setEarnings,
		presets: { label: string; row: EarningItemRequest }[],
		label: string
	) {
		const preset = presets.find((candidate) => candidate.label === label);
		if (!preset) return;
		setRows((current) => [...current, { ...preset.row }]);
	}

	function updateRow(setRows: typeof setEarnings, index: number, patch: Partial<EarningItemRequest>) {
		setRows((current) =>
			current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
		);
	}

	function removeRow(setRows: typeof setEarnings, index: number) {
		setRows((current) => current.filter((_, itemIndex) => itemIndex !== index));
	}

	function normalizeRows(rows: EarningItemRequest[]): EarningItemRequest[] {
		return rows.map((item) => ({
			code: item.code.trim(),
			name: item.name.trim(),
			taxable: item.taxable,
			amount: item.amount,
			description: item.description?.trim() || undefined
		}));
	}

	async function save(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!employeeId) {
			setError('직원을 선택해주세요.');
			return;
		}
		const year = Number(payYear);
		const month = Number(payMonth);
		const current = new Date();
		if (year * 100 + month >= current.getFullYear() * 100 + current.getMonth() + 1) {
			setError('과거 귀속월(현재월 이전)만 등록할 수 있습니다. 당월분은 명세서 생성·확정 플로우를 이용하세요.');
			return;
		}
		if (earnings.length === 0) {
			setError('지급 항목을 1개 이상 추가해주세요.');
			return;
		}
		const normalizedEarnings = normalizeRows(earnings);
		const normalizedDeductions = normalizeRows(deductions);
		if (
			[...normalizedEarnings, ...normalizedDeductions].some(
				(item) => !item.code || !item.name || !Number.isFinite(item.amount) || item.amount < 0
			)
		) {
			setError('모든 항목의 코드, 이름, 0원 이상의 금액을 입력해주세요.');
			return;
		}

		setSaving(true);
		setError(null);
		try {
			const request: HistoricalPayslipCreateRequest = {
				payYear: year,
				payMonth: month,
				paymentDate: paymentDate || undefined,
				memo: memo.trim() || undefined,
				earnings: normalizedEarnings,
				deductions: normalizedDeductions
			};
			await api.post<PayslipResponse>(`/employees/${employeeId}/payslips/historical`, request);
			await onSaved();
		} catch (error) {
			setError(getErrorMessage(error, '과거 명세서를 등록하지 못했습니다.'));
		} finally {
			setSaving(false);
		}
	}

	function renderRows(
		rows: EarningItemRequest[],
		setRows: typeof setEarnings,
		idPrefix: string,
		taxableEditable: boolean
	) {
		if (rows.length === 0) {
			return (
				<p className="rounded-lg border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground">
					항목을 추가해주세요.
				</p>
			);
		}
		return (
			<div className="space-y-3">
				{rows.map((item, index) => {
					const itemLabel = item.name || `항목 ${index + 1}`;
					return (
						<div
							key={`${idPrefix}-${item.code}-${index}`}
							className="rounded-lg border border-border bg-muted/20 p-4"
						>
							<div className="grid gap-3 sm:grid-cols-2">
								<div className="space-y-2">
									<Label htmlFor={`${idPrefix}-${index}-name`}>이름</Label>
									<Input
										id={`${idPrefix}-${index}-name`}
										value={item.name}
										onChange={(event) => updateRow(setRows, index, { name: event.target.value })}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor={`${idPrefix}-${index}-code`}>코드</Label>
									<Input
										id={`${idPrefix}-${index}-code`}
										value={item.code}
										onChange={(event) => updateRow(setRows, index, { code: event.target.value })}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor={`${idPrefix}-${index}-amount`}>{itemLabel} 금액</Label>
									<Input
										id={`${idPrefix}-${index}-amount`}
										type="number"
										min="0"
										value={item.amount}
										onChange={(event) =>
											updateRow(setRows, index, { amount: Number(event.target.value) })
										}
									/>
								</div>
								{taxableEditable ? (
									<label className="flex items-end gap-2 pb-2 text-sm text-muted-foreground">
										<input
											type="checkbox"
											checked={item.taxable}
											onChange={(event) =>
												updateRow(setRows, index, { taxable: event.target.checked })
											}
										/>
										과세
									</label>
								) : null}
							</div>
							<div className="mt-3 flex justify-end">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => removeRow(setRows, index)}
								>
									<Trash2 className="size-4" />
									삭제
								</Button>
							</div>
						</div>
					);
				})}
			</div>
		);
	}

	return (
		<Modal
			title="과거 급여명세서 등록"
			description="antflow 도입 전 명세서를 값 그대로 등록합니다. 재계산 없이 즉시 확정되며 알림은 발송되지 않습니다."
			onClose={onClose}
		>
			<form className="space-y-5" onSubmit={(event) => void save(event)}>
				<FormError message={error} />
				<div className="grid gap-3 sm:grid-cols-3">
					<div className="space-y-2">
						<Label htmlFor="historical-employee">직원</Label>
						<select
							id="historical-employee"
							className={dialogControlClassName}
							value={employeeId}
							onChange={(event) => setEmployeeId(event.target.value)}
						>
							<option value="">직원 선택</option>
							{employees.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="historical-pay-year">귀속 연도</Label>
						<select
							id="historical-pay-year"
							className={dialogControlClassName}
							value={payYear}
							onChange={(event) => setPayYear(event.target.value)}
						>
							{Array.from({ length: 11 }, (_, offset) => now.getFullYear() - offset).map((year) => (
								<option key={year} value={year}>
									{year}년
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="historical-pay-month">귀속 월</Label>
						<select
							id="historical-pay-month"
							className={dialogControlClassName}
							value={payMonth}
							onChange={(event) => setPayMonth(event.target.value)}
						>
							{Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
								<option key={month} value={month}>
									{month}월
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="grid gap-3 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="historical-payment-date">지급일 (선택)</Label>
						<Input
							id="historical-payment-date"
							type="date"
							value={paymentDate}
							onChange={(event) => setPaymentDate(event.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="historical-memo">메모 (선택)</Label>
						<Input
							id="historical-memo"
							value={memo}
							onChange={(event) => setMemo(event.target.value)}
							placeholder="예: 이전 시스템 명세서"
						/>
					</div>
				</div>
				<div className="space-y-3">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<p className="font-semibold">지급 항목</p>
						<div className="flex flex-wrap gap-2">
							{EARNING_PRESETS.map((preset) => (
								<Button
									key={preset.label}
									type="button"
									variant="outline"
									size="sm"
									onClick={() => addRow(setEarnings, EARNING_PRESETS, preset.label)}
								>
									{preset.label} 추가
								</Button>
							))}
						</div>
					</div>
					{renderRows(earnings, setEarnings, 'historical-earning', true)}
				</div>
				<div className="space-y-3">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<p className="font-semibold">공제 항목</p>
						<div className="flex flex-wrap gap-2">
							{DEDUCTION_PRESETS.map((preset) => (
								<Button
									key={preset.label}
									type="button"
									variant="outline"
									size="sm"
									onClick={() => addRow(setDeductions, DEDUCTION_PRESETS, preset.label)}
								>
									{preset.label} 추가
								</Button>
							))}
						</div>
					</div>
					{renderRows(deductions, setDeductions, 'historical-deduction', false)}
				</div>
				<div className="rounded-lg border border-border bg-muted/20 p-4 text-sm">
					<div className="flex flex-wrap justify-between gap-2">
						<span className="text-muted-foreground">지급 합계</span>
						<span className="font-semibold">{formatWon(grossTotal)}</span>
					</div>
					<div className="mt-1 flex flex-wrap justify-between gap-2">
						<span className="text-muted-foreground">공제 합계</span>
						<span className="font-semibold">{formatWon(deductionTotal)}</span>
					</div>
					<div className="mt-1 flex flex-wrap justify-between gap-2">
						<span className="text-muted-foreground">차인지급액</span>
						<span className="font-semibold">{formatWon(grossTotal - deductionTotal)}</span>
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose} disabled={saving}>
						취소
					</Button>
					<Button type="submit" disabled={saving}>
						<Save className="size-4" />
						{saving ? '등록 중...' : '등록'}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function PayrollSettingsDialog({
	settings,
	onClose,
	onSaved
}: {
	settings: PayrollSettings;
	onClose: () => void;
	onSaved: () => Promise<void>;
}) {
	const [form, setForm] = useState({
		paymentMonthOffset: String(settings.paymentMonthOffset),
		paymentDay: String(settings.paymentDay),
		autoGenerateEnabled: settings.autoGenerateEnabled,
		autoGenerateDay: String(settings.autoGenerateDay),
		autoConfirmEnabled: settings.autoConfirmEnabled,
		issueMonthOffset: String(settings.issueMonthOffset),
		issueDay: String(settings.issueDay),
		overtimeHourlyWage: String(settings.overtimeHourlyWage),
		payslipGreeting: settings.payslipGreeting ?? '',
		salaryIncludesMealAllowance: settings.salaryIncludesMealAllowance,
		mealAllowanceMonthly: String(settings.mealAllowanceMonthly)
	});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function update(patch: Partial<typeof form>) {
		setForm((current) => ({ ...current, ...patch }));
	}

	const issueBeforeGenerate =
		form.autoConfirmEnabled &&
		form.autoGenerateEnabled &&
		Number(form.issueMonthOffset) === 0 &&
		Number(form.issueDay) < Number(form.autoGenerateDay);
	const issueAfterPayment =
		form.autoConfirmEnabled &&
		(Number(form.issueMonthOffset) > Number(form.paymentMonthOffset) ||
			(Number(form.issueMonthOffset) === Number(form.paymentMonthOffset) &&
				Number(form.issueDay) > Number(form.paymentDay)));

	async function save(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const paymentDay = Number(form.paymentDay);
		const autoGenerateDay = Number(form.autoGenerateDay);
		const issueDay = Number(form.issueDay);
		const overtimeHourlyWage = Number(form.overtimeHourlyWage);
		const dayValid = (value: number) => Number.isInteger(value) && value >= 1 && value <= 31;
		if (!dayValid(paymentDay) || !dayValid(autoGenerateDay) || !dayValid(issueDay)) {
			setError('지급일·자동생성일·발행일은 1~31 사이여야 합니다.');
			return;
		}
		if (!Number.isFinite(overtimeHourlyWage) || overtimeHourlyWage < 0) {
			setError('초과근무 시급은 0 이상이어야 합니다.');
			return;
		}
		const mealAllowanceMonthly = Number(form.mealAllowanceMonthly);
		if (!Number.isFinite(mealAllowanceMonthly) || mealAllowanceMonthly < 0) {
			setError('식대 월액은 0 이상이어야 합니다.');
			return;
		}
		setSaving(true);
		setError(null);
		try {
			await api.put<PayrollSettings>('/payroll/settings', {
				paymentMonthOffset: Number(form.paymentMonthOffset) as 0 | 1,
				paymentDay,
				autoGenerateDay,
				autoGenerateEnabled: form.autoGenerateEnabled,
				overtimeHourlyWage,
				requireSignedEmploymentContract: settings.requireSignedEmploymentContract,
				autoConfirmEnabled: form.autoConfirmEnabled,
				issueMonthOffset: Number(form.issueMonthOffset) as 0 | 1,
				issueDay,
				payslipGreeting: form.payslipGreeting,
				salaryIncludesMealAllowance: form.salaryIncludesMealAllowance,
				mealAllowanceMonthly: Number(form.mealAllowanceMonthly)
			});
			await onSaved();
		} catch (error) {
			setError(getErrorMessage(error, '급여 설정을 저장하지 못했습니다.'));
		} finally {
			setSaving(false);
		}
	}

	return (
		<Modal
			title="급여 설정"
			description="지급일, 명세서 자동 생성일, 자동 발행(확정) 일정을 설정합니다."
			onClose={onClose}
		>
			<form className="space-y-5" onSubmit={(event) => void save(event)}>
				<FormError message={error} />
				<div className="grid gap-3 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="payroll-settings-payment-offset">지급월 기준</Label>
						<select
							id="payroll-settings-payment-offset"
							className={dialogControlClassName}
							value={form.paymentMonthOffset}
							onChange={(event) => update({ paymentMonthOffset: event.target.value })}
						>
							<option value="0">당월 지급</option>
							<option value="1">익월 지급</option>
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="payroll-settings-payment-day">지급일</Label>
						<Input
							id="payroll-settings-payment-day"
							type="number"
							min="1"
							max="31"
							value={form.paymentDay}
							onChange={(event) => update({ paymentDay: event.target.value })}
						/>
					</div>
				</div>
				<div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
					<label className="flex items-center gap-2 text-sm font-medium">
						<input
							type="checkbox"
							checked={form.autoGenerateEnabled}
							onChange={(event) => update({ autoGenerateEnabled: event.target.checked })}
						/>
						자동 생성 사용
					</label>
					<p className="text-sm text-muted-foreground">
						자동생성일 새벽에 당월 귀속 명세서 초안을 자동으로 만듭니다.
					</p>
					<div className="space-y-2">
						<Label htmlFor="payroll-settings-generate-day">자동생성일</Label>
						<Input
							id="payroll-settings-generate-day"
							type="number"
							min="1"
							max="31"
							value={form.autoGenerateDay}
							onChange={(event) => update({ autoGenerateDay: event.target.value })}
							disabled={!form.autoGenerateEnabled}
						/>
					</div>
				</div>
				<div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
					<label className="flex items-center gap-2 text-sm font-medium">
						<input
							type="checkbox"
							checked={form.autoConfirmEnabled}
							onChange={(event) => update({ autoConfirmEnabled: event.target.checked })}
						/>
						자동 발행 사용
					</label>
					<p className="text-sm text-muted-foreground">
						발행일 새벽에 해당 귀속월 초안을 자동으로 확정하고 직원에게 앱·푸시·이메일 알림을
						발송합니다. 예: 7월 귀속분을 익월 1일 발행, 익월 10일 지급.
					</p>
					<div className="grid gap-3 sm:grid-cols-2">
						<div className="space-y-2">
							<Label htmlFor="payroll-settings-issue-offset">발행월 기준</Label>
							<select
								id="payroll-settings-issue-offset"
								className={dialogControlClassName}
								value={form.issueMonthOffset}
								onChange={(event) => update({ issueMonthOffset: event.target.value })}
								disabled={!form.autoConfirmEnabled}
							>
								<option value="0">귀속월 당월 발행</option>
								<option value="1">익월 발행</option>
							</select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="payroll-settings-issue-day">발행일</Label>
							<Input
								id="payroll-settings-issue-day"
								type="number"
								min="1"
								max="31"
								value={form.issueDay}
								onChange={(event) => update({ issueDay: event.target.value })}
								disabled={!form.autoConfirmEnabled}
							/>
						</div>
					</div>
					{issueBeforeGenerate ? (
						<p className="rounded-md bg-warning/15 px-3 py-2 text-sm text-warning-foreground">
							당월 발행인데 발행일이 자동생성일보다 앞서 있어 해당 월 명세서가 생성되기 전에 발행
							시점이 지나갑니다. 발행일을 자동생성일 이후로 설정하세요.
						</p>
					) : null}
					{issueAfterPayment ? (
						<p className="rounded-md bg-warning/15 px-3 py-2 text-sm text-warning-foreground">
							발행일이 지급일 이후라 직원이 이체 전에 명세서를 확인할 수 없습니다.
						</p>
					) : null}
				</div>
				<div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
					<label className="flex items-center gap-2 text-sm font-medium">
						<input
							type="checkbox"
							checked={form.salaryIncludesMealAllowance}
							onChange={(event) => update({ salaryIncludesMealAllowance: event.target.checked })}
						/>
						연봉에 식대 포함(비과세 분리)
					</label>
					<p className="text-sm text-muted-foreground">
						연봉계약으로 자동 생성할 때 월급여에서 식대를 비과세로 분리합니다. 총지급은 같고
						공제가 줄어 실수령이 늘어납니다. 직원 템플릿에 식대가 이미 있으면 분리하지 않습니다.
					</p>
					<div className="space-y-2">
						<Label htmlFor="payroll-settings-meal-allowance">식대 월액</Label>
						<Input
							id="payroll-settings-meal-allowance"
							type="number"
							min="0"
							value={form.mealAllowanceMonthly}
							onChange={(event) => update({ mealAllowanceMonthly: event.target.value })}
							disabled={!form.salaryIncludesMealAllowance}
						/>
						<p className="text-sm text-muted-foreground">
							비과세 한도(월 200,000원)를 넘는 금액은 자동으로 과세 처리됩니다.
						</p>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="payroll-settings-overtime-wage">초과근무 시급</Label>
					<Input
						id="payroll-settings-overtime-wage"
						type="number"
						min="0"
						value={form.overtimeHourlyWage}
						onChange={(event) => update({ overtimeHourlyWage: event.target.value })}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="payroll-settings-greeting">감사 인사말</Label>
					<textarea
						id="payroll-settings-greeting"
						className={dialogTextAreaClassName}
						maxLength={500}
						value={form.payslipGreeting}
						onChange={(event) => update({ payslipGreeting: event.target.value })}
						placeholder="미입력 시 기본 문구가 표시됩니다. (예: 이번 한 달도 수고 많으셨습니다. 감사합니다.)"
					/>
					<p className="text-sm text-muted-foreground">
						직원이 보는 급여명세서 하단에 표시됩니다. 비우면 기본 문구로 돌아갑니다.
					</p>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose} disabled={saving}>
						취소
					</Button>
					<Button type="submit" disabled={saving}>
						<Save className="size-4" />
						{saving ? '저장 중...' : '설정 저장'}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

export function PayrollPage() {
	const [state, setState] = useState<RequestState<PayrollPageData>>({ status: 'idle' });
	const [query, setQuery] = useState('');
	const [templateEditorTarget, setTemplateEditorTarget] = useState<PayrollEmployeeRow | null>(null);
	const [updatingContractRequirement, setUpdatingContractRequirement] = useState(false);
	const [contractRequirementError, setContractRequirementError] = useState<string | null>(null);
	const [generatingPayslips, setGeneratingPayslips] = useState(false);
	const [confirmingPayslips, setConfirmingPayslips] = useState(false);
	const [resendingNotifications, setResendingNotifications] = useState(false);
	const [payrollActionError, setPayrollActionError] = useState<string | null>(null);
	const [period, setPeriod] = useState(currentPayrollPeriod());
	const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
	const [historicalDialogOpen, setHistoricalDialogOpen] = useState(false);

	const load = useMemo(
		() => async () => {
			const { payYear, payMonth } = period;
			setState({ status: 'loading' });
			try {
				const [settings, templates, payslips, preview, settlement] = await Promise.all([
					api.get<PayrollSettings>('/payroll/settings'),
					api.get<PayrollTemplateSummary[]>('/payroll/templates').catch(() => []),
					api
						.get<
							PayslipSummaryResponse[]
						>(`/payroll/payslips?payYear=${payYear}&payMonth=${payMonth}`)
						.catch(() => []),
					api
						.post<PayslipGenerationPreviewResponse>('/payroll/payslips/generate/preview', {
							payYear,
							payMonth
						})
						.catch(() => null),
					api
						.get<PayrollSettlementResponse>(
							`/payroll/payslips/settlement?payYear=${payYear}&payMonth=${payMonth}`
						)
						.catch(() => null)
				]);
				setState({
					status: 'success',
					data: { settings, templates, payslips, preview, settlement, payYear, payMonth }
				});
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[period]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	function movePeriod(delta: number) {
		setPeriod((current) => {
			const base = new Date(current.payYear, current.payMonth - 1 + delta, 1);
			return { payYear: base.getFullYear(), payMonth: base.getMonth() + 1 };
		});
	}

	const data = state.status === 'success' ? state.data : null;
	const rows = useMemo(() => (data ? buildPayrollRows(data) : []), [data]);
	const filteredRows = useMemo(() => {
		const keyword = query.trim().toLowerCase();
		if (!keyword) return rows;
		return rows.filter((row) =>
			[
				payrollEmployeeLabel(row),
				payrollTemplateLabel(row.template),
				row.template?.memo,
				payrollStatusLabel(row.payslip?.status),
				row.skippedReason
			]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(keyword))
		);
	}, [query, rows]);

	const confirmedCount =
		data?.settlement?.confirmedCount ??
		data?.payslips.filter((payslip) => payslip.status === 'CONFIRMED').length ??
		0;
	const draftCount =
		data?.settlement?.draftCount ??
		data?.payslips.filter((payslip) => payslip.status === 'DRAFT').length ??
		0;

	async function toggleSignedEmploymentContractRequirement() {
		if (!data) return;
		setUpdatingContractRequirement(true);
		setContractRequirementError(null);
		try {
			await api.put<PayrollSettings>('/payroll/settings', {
				paymentMonthOffset: data.settings.paymentMonthOffset,
				paymentDay: data.settings.paymentDay,
				autoGenerateDay: data.settings.autoGenerateDay,
				autoGenerateEnabled: data.settings.autoGenerateEnabled,
				overtimeHourlyWage: data.settings.overtimeHourlyWage,
				requireSignedEmploymentContract: !data.settings.requireSignedEmploymentContract,
				autoConfirmEnabled: data.settings.autoConfirmEnabled,
				issueMonthOffset: data.settings.issueMonthOffset,
				issueDay: data.settings.issueDay,
				payslipGreeting: data.settings.payslipGreeting,
				salaryIncludesMealAllowance: data.settings.salaryIncludesMealAllowance,
				mealAllowanceMonthly: data.settings.mealAllowanceMonthly
			});
			await load();
		} catch (error) {
			setContractRequirementError(getErrorMessage(error));
		} finally {
			setUpdatingContractRequirement(false);
		}
	}

	async function generatePayslips() {
		if (!data) return;
		setGeneratingPayslips(true);
		setPayrollActionError(null);
		try {
			await api.post('/payroll/payslips/generate', {
				payYear: data.payYear,
				payMonth: data.payMonth
			});
			await load();
		} catch (error) {
			setPayrollActionError(getErrorMessage(error));
		} finally {
			setGeneratingPayslips(false);
		}
	}

	async function confirmPayslips() {
		if (!data) return;
		const confirmed = window.confirm(
			`${payrollMonthLabel(data.payYear, data.payMonth)} 초안 명세서를 확정·발행할까요? 확정된 명세서는 근로자가 열람할 수 있습니다.`
		);
		if (!confirmed) return;
		setConfirmingPayslips(true);
		setPayrollActionError(null);
		try {
			await api.post('/payroll/payslips/confirm', {
				payYear: data.payYear,
				payMonth: data.payMonth
			});
			await load();
		} catch (error) {
			setPayrollActionError(getErrorMessage(error));
		} finally {
			setConfirmingPayslips(false);
		}
	}

	async function resendMonthNotifications() {
		if (!data) return;
		const confirmed = window.confirm(
			`${payrollMonthLabel(data.payYear, data.payMonth)} 확정 명세서의 발행 알림(앱·푸시·이메일)을 다시 발송할까요? 직원에게 중복 알림이 갈 수 있습니다.`
		);
		if (!confirmed) return;
		setResendingNotifications(true);
		setPayrollActionError(null);
		try {
			await api.post('/payroll/payslips/resend', {
				payYear: data.payYear,
				payMonth: data.payMonth
			});
		} catch (error) {
			setPayrollActionError(getErrorMessage(error));
		} finally {
			setResendingNotifications(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="급여"
				description="급여 설정, 생성 준비 상태, 월별 명세서 진행 상황을 확인합니다."
				actions={
					<div className="flex flex-wrap items-center gap-2">
						<div className="flex items-center gap-1 rounded-md border border-border px-1 py-0.5">
							<Button
								variant="ghost"
								size="sm"
								aria-label="이전 달"
								onClick={() => movePeriod(-1)}
							>
								<ChevronLeft className="size-4" />
							</Button>
							<span className="min-w-24 text-center text-sm font-medium">
								{payrollMonthLabel(period.payYear, period.payMonth)}
							</span>
							<Button
								variant="ghost"
								size="sm"
								aria-label="다음 달"
								onClick={() => movePeriod(1)}
							>
								<ChevronRight className="size-4" />
							</Button>
						</div>
						<Button
							variant="outline"
							onClick={() => setSettingsDialogOpen(true)}
							disabled={!data}
						>
							급여 설정
						</Button>
						<Button variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<PayrollStatCard
							title="지급일"
							value={paydayLabel(data.settings)}
							description={`${payrollMonthLabel(data.payYear, data.payMonth)} 귀속분`}
						/>
						<PayrollStatCard
							title="자동 생성"
							value={
								data.settings.autoGenerateEnabled ? `${data.settings.autoGenerateDay}일` : '꺼짐'
							}
							description={`자동 발행 ${
								data.settings.autoConfirmEnabled
									? `${data.settings.issueMonthOffset === 1 ? '익월' : '당월'} ${data.settings.issueDay}일`
									: '꺼짐'
							}`}
						/>
						<PayrollStatCard
							title="생성 준비"
							value={`${numberLabel(data.preview?.readyCount)} / ${numberLabel(data.preview?.targetCount)}명`}
							description={`지급 예정일 ${dateLabel(data.preview?.paymentDate)}`}
						/>
						<PayrollStatCard
							title="명세서"
							value={`${numberLabel(data.settlement?.generatedCount ?? data.payslips.length)}건`}
							description={`확정 ${numberLabel(confirmedCount)}건 · 초안 ${numberLabel(draftCount)}건`}
						/>
					</div>

					<Card>
						<CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
							<div>
								<CardTitle>근로계약서 확인</CardTitle>
								<CardDescription>
									활성화하면 전자서명 완료 계약서 또는 관리자가 승인한 기존 근로계약서가 없는 직원은
									급여명세서를 생성할 수 없습니다.
								</CardDescription>
							</div>
							<Badge
								variant={data.settings.requireSignedEmploymentContract ? 'success' : 'outline'}
							>
								{data.settings.requireSignedEmploymentContract ? '의무 적용 중' : '현재 적용 안 함'}
							</Badge>
						</CardHeader>
						<CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
							<p className="text-sm text-muted-foreground">
								기존 회사는 전자계약의 ‘기존 문서 이관’에서 원본을 올리고 급여 유효 승인을 한 뒤
								활성화하세요.
							</p>
							<Button
								type="button"
								variant="outline"
								disabled={updatingContractRequirement}
								onClick={() => void toggleSignedEmploymentContractRequirement()}
							>
								<ShieldCheck className="size-4" />
								{updatingContractRequirement
									? '저장 중...'
									: data.settings.requireSignedEmploymentContract
										? '의무 해제'
										: '의무 적용'}
							</Button>
						</CardContent>
						{contractRequirementError ? (
							<div className="px-6 pb-5">
								<FormError message={contractRequirementError} />
							</div>
						) : null}
					</Card>

					<PayrollSettlementPanel
						settlement={data.settlement}
						onGenerate={() => void generatePayslips()}
						onConfirm={() => void confirmPayslips()}
						onResend={() => void resendMonthNotifications()}
						generating={generatingPayslips}
						confirming={confirmingPayslips}
						resending={resendingNotifications}
						actionError={payrollActionError}
					/>

					<Card>
						<CardHeader className="gap-4 lg:flex-row lg:items-center lg:justify-between">
							<div>
								<CardTitle>급여 운영</CardTitle>
								<CardDescription>
									직원별 템플릿, 생성 가능 여부, 명세서 상태를 확인합니다.
								</CardDescription>
							</div>
							<div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
								<Button
									type="button"
									variant="outline"
									onClick={() => setHistoricalDialogOpen(true)}
								>
									<Plus className="size-4" />
									과거 명세서 등록
								</Button>
								<div className="relative w-full lg:w-80">
									<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										value={query}
										onChange={(event) => setQuery(event.target.value)}
										placeholder="직원, 상태, 메모 검색"
										className="pl-9"
										aria-label="급여 검색"
									/>
								</div>
							</div>
						</CardHeader>
						<CardContent>
							<PayrollTable rows={filteredRows} onTemplateSetup={setTemplateEditorTarget} />
						</CardContent>
					</Card>
				</>
			) : null}
			{templateEditorTarget ? (
				<PayrollTemplateEditorModal
					employeeId={templateEditorTarget.employeeId}
					employeeName={payrollEmployeeLabel(templateEditorTarget)}
					initialMemo={templateEditorTarget.template?.memo ?? ''}
					onClose={() => setTemplateEditorTarget(null)}
					onSaved={async () => {
						setTemplateEditorTarget(null);
						await load();
					}}
				/>
			) : null}
			{settingsDialogOpen && data ? (
				<PayrollSettingsDialog
					settings={data.settings}
					onClose={() => setSettingsDialogOpen(false)}
					onSaved={async () => {
						setSettingsDialogOpen(false);
						await load();
					}}
				/>
			) : null}
			{historicalDialogOpen ? (
				<PayrollHistoricalPayslipModal
					onClose={() => setHistoricalDialogOpen(false)}
					onSaved={async () => {
						setHistoricalDialogOpen(false);
						await load();
					}}
				/>
			) : null}
		</div>
	);
}

export function PayrollImportsPage() {
	const [state, setState] = useState<RequestState<PayrollImportSummary[]>>({ status: 'idle' });
	const [uploading, setUploading] = useState(false);
	const [actionError, setActionError] = useState<string | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const imports = await api.get<PayrollImportSummary[]>('/payroll/imports');
				setState({ status: 'success', data: imports });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function upload(files: FileList | null) {
		const file = files?.[0];
		if (!file) return;
		setUploading(true);
		setActionError(null);
		try {
			const formData = new FormData();
			formData.append('file', file);
			await api.uploadFile<PayrollImportSummary>('/payroll/imports', formData);
			await load();
		} catch (error) {
			setActionError(getErrorMessage(error, '파일을 업로드하지 못했습니다.'));
		} finally {
			setUploading(false);
		}
	}

	async function removeImport(job: PayrollImportSummary) {
		const confirmed = window.confirm(
			`${job.fileName} 가져오기 작업을 삭제할까요? 추출 행과 원본 파일이 함께 삭제됩니다.`
		);
		if (!confirmed) return;
		setActionError(null);
		try {
			await api.delete(`/payroll/imports/${job.id}`);
			await load();
		} catch (error) {
			setActionError(getErrorMessage(error, '가져오기 작업을 삭제하지 못했습니다.'));
		}
	}

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="급여 가져오기"
				description="기존 급여대장·명세서 파일을 AI로 추출해 antflow 이용 전 과거 명세서로 등록합니다."
				actions={
					<Button variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			<Card>
				<CardHeader>
					<CardTitle>파일 업로드</CardTitle>
					<CardDescription>
						pdf, png, jpg, xlsx, csv 파일을 업로드하면 AI가 급여명세서 행을 추출합니다. 추출 결과는
						검토·수정 후에만 등록됩니다.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					<div className="space-y-2">
						<Label htmlFor="payroll-import-file">급여 파일 업로드</Label>
						<input
							id="payroll-import-file"
							type="file"
							accept=".pdf,.png,.jpg,.jpeg,.xlsx,.csv"
							className={dialogControlClassName}
							disabled={uploading}
							onChange={(event) => {
								void upload(event.target.files);
								event.target.value = '';
							}}
						/>
					</div>
					{uploading ? <p className="text-sm text-muted-foreground">업로드 중...</p> : null}
					{actionError ? <FormError message={actionError} /> : null}
				</CardContent>
			</Card>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-40 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<Card>
					<CardHeader>
						<CardTitle>가져오기 작업</CardTitle>
						<CardDescription>파일별 추출 상태를 확인하고 상세에서 행을 검토합니다.</CardDescription>
					</CardHeader>
					<CardContent>
						{data.length === 0 ? (
							<EmptyState message="아직 가져오기 작업이 없습니다. 파일을 업로드해 시작하세요." />
						) : (
							<div className="overflow-x-auto rounded-lg border border-border">
								<table className="w-full min-w-[720px] text-left text-sm">
									<thead className="bg-muted/40 text-xs text-muted-foreground">
										<tr>
											<th className="px-4 py-3 font-semibold">파일</th>
											<th className="px-4 py-3 font-semibold">유형</th>
											<th className="px-4 py-3 font-semibold">상태</th>
											<th className="px-4 py-3 font-semibold">추출 시각</th>
											<th className="px-4 py-3 font-semibold">오류</th>
											<th className="px-4 py-3 font-semibold">작업</th>
										</tr>
									</thead>
									<tbody>
										{data.map((job) => (
											<tr key={job.id} className="border-t border-border">
												<td className="px-4 py-4">
													<Link
														href={`/payroll/imports/${job.id}`}
														className="font-semibold text-primary hover:underline"
													>
														{job.fileName}
													</Link>
												</td>
												<td className="px-4 py-4 text-muted-foreground">{job.sourceType ?? '-'}</td>
												<td className="px-4 py-4">
													<Badge variant={importStatusVariant(job.status)}>
														{importStatusLabel(job.status)}
													</Badge>
												</td>
												<td className="px-4 py-4 text-muted-foreground">
													{dateTimeLabel(job.extractedAt)}
												</td>
												<td className="px-4 py-4 text-muted-foreground">
													{job.errorMessage ?? '-'}
												</td>
												<td className="px-4 py-4">
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => void removeImport(job)}
													>
														<Trash2 className="size-4" />
														삭제
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</CardContent>
				</Card>
			) : null}
		</div>
	);
}

type ImportRowTotals = {
	grossSum: number;
	deductionSum: number;
	mismatch: boolean;
};

function importRowTotals(row: PayrollImportRow): ImportRowTotals {
	const grossSum = (row.payload?.earnings ?? []).reduce((sum, line) => sum + (line.amount ?? 0), 0);
	const deductionSum = (row.payload?.deductions ?? []).reduce(
		(sum, line) => sum + (line.amount ?? 0),
		0
	);
	const mismatch =
		(row.payload?.grossPay != null && row.payload.grossPay !== grossSum) ||
		(row.payload?.totalDeduction != null && row.payload.totalDeduction !== deductionSum);
	return { grossSum, deductionSum, mismatch };
}

function ImportRowEditDialog({
	importId,
	row,
	employees,
	onClose,
	onSaved
}: {
	importId: string;
	row: PayrollImportRow;
	employees: EmployeeResponse[];
	onClose: () => void;
	onSaved: () => Promise<void>;
}) {
	const [employeeId, setEmployeeId] = useState(
		row.matchedEmployeeId != null ? String(row.matchedEmployeeId) : ''
	);
	const [payYear, setPayYear] = useState(row.payYear != null ? String(row.payYear) : '');
	const [payMonth, setPayMonth] = useState(row.payMonth != null ? String(row.payMonth) : '');
	const [paymentDate, setPaymentDate] = useState(row.paymentDate ?? '');
	const [earnings, setEarnings] = useState<PayrollImportRowLine[]>(row.payload?.earnings ?? []);
	const [deductions, setDeductions] = useState<PayrollImportRowLine[]>(
		row.payload?.deductions ?? []
	);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function updateLine(
		setRows: typeof setEarnings,
		index: number,
		patch: Partial<PayrollImportRowLine>
	) {
		setRows((current) =>
			current.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line))
		);
	}

	async function save(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const year = Number(payYear);
		const month = Number(payMonth);
		if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
			setError('귀속 연월을 확인해주세요.');
			return;
		}
		if (earnings.length === 0) {
			setError('지급 항목이 1개 이상 필요합니다.');
			return;
		}
		setSaving(true);
		setError(null);
		try {
			const request: PayrollImportRowUpdateRequest = {
				employeeId: employeeId ? Number(employeeId) : null,
				payYear: year,
				payMonth: month,
				paymentDate: paymentDate || undefined,
				earnings,
				deductions
			};
			await api.put<PayrollImportRow>(`/payroll/imports/${importId}/rows/${row.id}`, request);
			await onSaved();
		} catch (error) {
			setError(getErrorMessage(error, '추출 행을 저장하지 못했습니다.'));
		} finally {
			setSaving(false);
		}
	}

	function renderLines(
		lines: PayrollImportRowLine[],
		setRows: typeof setEarnings,
		idPrefix: string,
		title: string
	) {
		return (
			<div className="space-y-2">
				<p className="font-semibold">{title}</p>
				{lines.length === 0 ? (
					<p className="rounded-lg border border-dashed border-border bg-muted/20 p-3 text-sm text-muted-foreground">
						항목 없음
					</p>
				) : (
					<div className="space-y-2">
						{lines.map((line, index) => (
							<div
								key={`${idPrefix}-${index}`}
								className="grid gap-2 rounded-lg border border-border bg-muted/20 p-3 sm:grid-cols-2"
							>
								<div className="space-y-1">
									<Label htmlFor={`${idPrefix}-${index}-name`}>이름</Label>
									<Input
										id={`${idPrefix}-${index}-name`}
										value={line.name}
										onChange={(event) => updateLine(setRows, index, { name: event.target.value })}
									/>
								</div>
								<div className="space-y-1">
									<Label htmlFor={`${idPrefix}-${index}-amount`}>금액</Label>
									<Input
										id={`${idPrefix}-${index}-amount`}
										type="number"
										min="0"
										value={line.amount}
										onChange={(event) =>
											updateLine(setRows, index, { amount: Number(event.target.value) })
										}
									/>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		);
	}

	return (
		<Modal
			title="추출 행 수정"
			description="직원 매칭·귀속연월·항목 금액을 수정합니다. 합계는 서버에서 재계산됩니다."
			onClose={onClose}
		>
			<form className="space-y-5" onSubmit={(event) => void save(event)}>
				<FormError message={error} />
				<div className="grid gap-3 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="import-row-employee">직원 매칭</Label>
						<select
							id="import-row-employee"
							className={dialogControlClassName}
							value={employeeId}
							onChange={(event) => setEmployeeId(event.target.value)}
						>
							<option value="">매칭 안 함</option>
							{employees.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="import-row-payment-date">지급일</Label>
						<Input
							id="import-row-payment-date"
							type="date"
							value={paymentDate}
							onChange={(event) => setPaymentDate(event.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="import-row-pay-year">귀속 연도</Label>
						<Input
							id="import-row-pay-year"
							type="number"
							value={payYear}
							onChange={(event) => setPayYear(event.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="import-row-pay-month">귀속 월</Label>
						<Input
							id="import-row-pay-month"
							type="number"
							min="1"
							max="12"
							value={payMonth}
							onChange={(event) => setPayMonth(event.target.value)}
						/>
					</div>
				</div>
				{renderLines(earnings, setEarnings, 'import-row-earning', '지급 항목')}
				{renderLines(deductions, setDeductions, 'import-row-deduction', '공제 항목')}
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose} disabled={saving}>
						취소
					</Button>
					<Button type="submit" disabled={saving}>
						<Save className="size-4" />
						{saving ? '저장 중...' : '행 저장'}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

export function PayrollImportDetailPage({ route }: { route: MatchedRoute }) {
	const importId = route.params.id;
	const [state, setState] = useState<
		RequestState<{ detail: PayrollImportDetailResponse; employees: EmployeeResponse[] }>
	>({ status: 'idle' });
	const [commitResult, setCommitResult] = useState<PayrollImportCommitResult | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);
	const [extracting, setExtracting] = useState(false);
	const [committing, setCommitting] = useState(false);
	const [editTarget, setEditTarget] = useState<PayrollImportRow | null>(null);

	const load = useMemo(
		() => async () => {
			// 폴링 재호출 시에는 성공 상태를 유지해 스피너 깜빡임을 막는다
			setState((current) => (current.status === 'success' ? current : { status: 'loading' }));
			try {
				const [detail, employees] = await Promise.all([
					api.get<PayrollImportDetailResponse>(`/payroll/imports/${importId}`),
					api
						.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200')
						.then((page) => page.content)
						.catch(() => [] as EmployeeResponse[])
				]);
				setState({ status: 'success', data: { detail, employees } });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[importId]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const status = data?.detail.summary.status ?? null;

	// EXTRACTING 동안 3초 폴링 — 상태 전이/언마운트 시 정리
	useEffect(() => {
		if (status !== 'EXTRACTING') return;
		const timer = window.setInterval(() => {
			void load();
		}, 3000);
		return () => window.clearInterval(timer);
	}, [status, load]);

	const pendingMatchedCount =
		data?.detail.rows.filter((row) => row.status === 'PENDING' && row.matchedEmployeeId != null)
			.length ?? 0;

	async function startExtract() {
		setExtracting(true);
		setActionError(null);
		try {
			await api.post(`/payroll/imports/${importId}/extract`);
			await load();
		} catch (error) {
			setActionError(getErrorMessage(error, '추출을 시작하지 못했습니다.'));
		} finally {
			setExtracting(false);
		}
	}

	async function commitRows() {
		if (!data) return;
		const confirmed = window.confirm(
			`검토 대기 ${pendingMatchedCount}행을 과거 명세서로 등록할까요? 재계산 없이 즉시 확정되며 알림은 발송되지 않습니다.`
		);
		if (!confirmed) return;
		setCommitting(true);
		setActionError(null);
		try {
			const result = await api.post<PayrollImportCommitResult>(
				`/payroll/imports/${importId}/commit`,
				{}
			);
			setCommitResult(result);
			await load();
		} catch (error) {
			setActionError(getErrorMessage(error, '검토 행을 등록하지 못했습니다.'));
		} finally {
			setCommitting(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="급여 가져오기 상세"
				description={data ? data.detail.summary.fileName : '추출된 행을 검토하고 등록합니다.'}
				actions={
					<Button variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<Card>
						<CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
							<div>
								<CardTitle>추출 상태</CardTitle>
								<CardDescription>
									{data.detail.summary.sourceType ?? '-'} ·{' '}
									{data.detail.summary.aiModel ?? 'AI 모델 미지정'} · 추출{' '}
									{dateTimeLabel(data.detail.summary.extractedAt)}
								</CardDescription>
							</div>
							<div className="flex flex-wrap items-center gap-2">
								<Badge variant={importStatusVariant(data.detail.summary.status)}>
									{importStatusLabel(data.detail.summary.status)}
								</Badge>
								{data.detail.summary.status === 'UPLOADED' ||
								data.detail.summary.status === 'FAILED' ? (
									<Button type="button" disabled={extracting} onClick={() => void startExtract()}>
										{extracting
											? '요청 중...'
											: data.detail.summary.status === 'FAILED'
												? '재추출'
												: '추출 시작'}
									</Button>
								) : null}
							</div>
						</CardHeader>
						{data.detail.summary.status === 'EXTRACTING' ? (
							<CardContent className="flex items-center gap-3 text-sm text-muted-foreground">
								<Spinner size="sm" />
								AI가 급여 행을 추출하고 있습니다. 완료되면 자동으로 갱신됩니다.
							</CardContent>
						) : null}
						{data.detail.summary.status === 'FAILED' && data.detail.summary.errorMessage ? (
							<CardContent>
								<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
									{data.detail.summary.errorMessage}
								</div>
							</CardContent>
						) : null}
					</Card>

					{commitResult ? (
						<Card>
							<CardHeader>
								<CardTitle>등록 결과</CardTitle>
								<CardDescription>{`${commitResult.committedCount}건 등록 완료`}</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="overflow-x-auto rounded-lg border border-border">
									<table className="w-full min-w-[560px] text-left text-sm">
										<thead className="bg-muted/40 text-xs text-muted-foreground">
											<tr>
												<th className="px-4 py-3 font-semibold">행</th>
												<th className="px-4 py-3 font-semibold">결과</th>
												<th className="px-4 py-3 font-semibold">사유</th>
												<th className="px-4 py-3 font-semibold">명세서</th>
											</tr>
										</thead>
										<tbody>
											{commitResult.rows.map((row) => (
												<tr key={row.rowId} className="border-t border-border">
													<td className="px-4 py-3">#{row.rowId}</td>
													<td className="px-4 py-3">
														<div className="flex flex-wrap items-center gap-2">
															<Badge variant={importRowStatusVariant(row.status)}>
																{importRowStatusLabel(row.status)}
															</Badge>
															{row.totalsMismatch ? (
																<Badge variant="warning">합계 불일치</Badge>
															) : null}
														</div>
													</td>
													<td className="px-4 py-3 text-muted-foreground">
														{importRowFailReasonLabel(row.failReason)}
													</td>
													<td className="px-4 py-3">
														{row.payslipId != null ? '등록됨' : '-'}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</CardContent>
						</Card>
					) : null}

					{data.detail.summary.status === 'EXTRACTED' ? (
						<Card>
							<CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
								<div>
									<CardTitle>추출 행 검토</CardTitle>
									<CardDescription>
										{data.detail.rowsTruncated
											? `전체 ${data.detail.totalRows}행 중 미리보기 ${data.detail.rows.length}행`
											: `${data.detail.rows.length}행`}{' '}
										· 직원 매칭과 금액을 확인한 뒤 등록하세요. 등록 시 재계산 없이 즉시 확정되며
										알림은 발송되지 않습니다.
									</CardDescription>
								</div>
								<Button
									type="button"
									disabled={committing || pendingMatchedCount === 0}
									onClick={() => void commitRows()}
								>
									{committing ? '등록 중...' : '검토 행 등록'}
								</Button>
							</CardHeader>
							<CardContent>
								{actionError ? (
									<div className="mb-3">
										<FormError message={actionError} />
									</div>
								) : null}
								{data.detail.rows.length === 0 ? (
									<EmptyState message="추출된 행이 없습니다." />
								) : (
									<div className="overflow-x-auto rounded-lg border border-border">
										<table className="w-full min-w-[880px] text-left text-sm">
											<thead className="bg-muted/40 text-xs text-muted-foreground">
												<tr>
													<th className="px-4 py-3 font-semibold">#</th>
													<th className="px-4 py-3 font-semibold">직원</th>
													<th className="px-4 py-3 font-semibold">귀속</th>
													<th className="px-4 py-3 font-semibold">지급 합계</th>
													<th className="px-4 py-3 font-semibold">공제 합계</th>
													<th className="px-4 py-3 font-semibold">확인</th>
													<th className="px-4 py-3 font-semibold">상태</th>
													<th className="px-4 py-3 font-semibold">작업</th>
												</tr>
											</thead>
											<tbody>
												{data.detail.rows.map((row) => {
													const totals = importRowTotals(row);
													return (
														<tr key={row.id} className="border-t border-border">
															<td className="px-4 py-3 text-muted-foreground">
																{row.rowIndex != null ? row.rowIndex + 1 : '-'}
															</td>
															<td className="px-4 py-3">
																<div className="flex flex-wrap items-center gap-2">
																	<span className="font-semibold">{row.employeeName ?? '-'}</span>
																	{row.matchedEmployeeId == null ? (
																		<Badge variant="warning">매칭 필요</Badge>
																	) : (
																		<Badge variant="outline">매칭됨</Badge>
																	)}
																</div>
															</td>
															<td className="px-4 py-3 text-muted-foreground">
																{row.payYear != null && row.payMonth != null
																	? `${row.payYear}.${String(row.payMonth).padStart(2, '0')}`
																	: '누락'}
															</td>
															<td className="px-4 py-3">{formatWon(totals.grossSum)}</td>
															<td className="px-4 py-3">{formatWon(totals.deductionSum)}</td>
															<td className="px-4 py-3">
																{totals.mismatch ? (
																	<Badge variant="warning">합계 불일치</Badge>
																) : (
																	<span className="text-muted-foreground">-</span>
																)}
															</td>
															<td className="px-4 py-3">
																<Badge variant={importRowStatusVariant(row.status)}>
																	{importRowStatusLabel(row.status)}
																</Badge>
															</td>
															<td className="px-4 py-3">
																{row.status !== 'COMMITTED' ? (
																	<Button
																		type="button"
																		variant="outline"
																		size="sm"
																		onClick={() => setEditTarget(row)}
																	>
																		<Pencil className="size-4" />
																		수정
																	</Button>
																) : null}
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								)}
							</CardContent>
						</Card>
					) : null}
				</>
			) : null}
			{editTarget && data ? (
				<ImportRowEditDialog
					importId={importId}
					row={editTarget}
					employees={data.employees}
					onClose={() => setEditTarget(null)}
					onSaved={async () => {
						setEditTarget(null);
						await load();
					}}
				/>
			) : null}
		</div>
	);
}

function yearEndStatusLabel(status: YearEndSettlementStatus | null | undefined): string {
	const labels: Record<YearEndSettlementStatus, string> = {
		PENDING_EMPLOYEE: '제출 대기',
		SUBMITTED: '검토 대기',
		NEEDS_CORRECTION: '수정 요청',
		APPROVED: '검토 승인',
		FINALIZED: '최종 확정'
	};
	return status ? labels[status] : '-';
}

function yearEndStatusVariant(
	status: YearEndSettlementStatus | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'APPROVED' || status === 'FINALIZED') return 'success';
	if (status === 'SUBMITTED' || status === 'PENDING_EMPLOYEE') return 'warning';
	if (status === 'NEEDS_CORRECTION') return 'destructive';
	return 'outline';
}

function yearEndChecklistVariant(
	status: YearEndChecklistStatus | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function yearEndChecklistStatusLabel(status: YearEndChecklistStatus | null | undefined): string {
	if (status === 'READY') return '완료';
	if (status === 'WARNING') return '주의';
	if (status === 'WAITING') return '대기';
	if (status === 'PENDING') return '예정';
	return '-';
}

function yearEndSpecialFlagLabel(flag: string): string {
	const labels: Record<string, string> = {
		MID_YEAR_JOINER: '중도 입사자',
		PAYROLL_DATA_MISSING: '급여자료 누락',
		TAX_REDUCTION: '세액 감면'
	};
	return labels[flag] ?? flag;
}

function yearEndDeductionCategoryLabel(category: YearEndDeductionItem['category']): string {
	const labels: Record<YearEndDeductionItem['category'], string> = {
		CREDIT_CARD: '신용카드',
		MEDICAL: '의료비',
		EDUCATION: '교육비',
		HOUSING: '주택자금',
		DONATION: '기부금',
		INSURANCE: '보험료',
		PENSION: '연금',
		OTHER: '기타'
	};
	return labels[category];
}

function YearEndChecklistItemView({
	item
}: {
	item: YearEndSettlementSummaryResponse['checklist'][number];
}) {
	const Icon =
		item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={yearEndChecklistVariant(item.status)}>
					{yearEndChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function YearEndEmployeeMobileCard({ employee }: { employee: YearEndSettlementResponse }) {
	const firstWarning = employee.deductions.find((item) => item.warningMessage);
	return (
		<div className="rounded-lg border border-border bg-background p-4 md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div>
					<p className="font-semibold">직원 #{employee.employeeId}</p>
					<p className="mt-1 text-sm text-muted-foreground">
						급여 {wonLabel(employee.annualGrossPay)} · 원천징수 {wonLabel(employee.annualTaxPaid)}
					</p>
				</div>
				<Badge variant={yearEndStatusVariant(employee.status)}>
					{yearEndStatusLabel(employee.status)}
				</Badge>
			</div>
			<div className="mt-4 grid grid-cols-2 gap-3 text-sm">
				<div>
					<p className="text-xs text-muted-foreground">환급 예정</p>
					<p className="mt-1 font-semibold">{wonLabel(employee.expectedRefund)}</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">징수 예정</p>
					<p className="mt-1 font-semibold">{wonLabel(employee.expectedCollection)}</p>
				</div>
			</div>
			{employee.specialReviewFlags.length > 0 ? (
				<div className="mt-3 flex flex-wrap gap-2">
					{employee.specialReviewFlags.map((flag) => (
						<Badge key={flag} variant="warning">
							{yearEndSpecialFlagLabel(flag)}
						</Badge>
					))}
				</div>
			) : null}
			{firstWarning?.warningMessage ? (
				<p className="mt-3 text-sm text-warning-foreground">{firstWarning.warningMessage}</p>
			) : null}
		</div>
	);
}

function YearEndEmployeeTable({ employees }: { employees: YearEndSettlementResponse[] }) {
	if (employees.length === 0) {
		return <EmptyState message="연말정산 대상자가 없습니다." />;
	}
	return (
		<>
			<div className="space-y-3 md:hidden">
				{employees.map((employee) => (
					<YearEndEmployeeMobileCard key={employee.id} employee={employee} />
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[1080px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">직원</th>
							<th className="px-4 py-3 font-semibold">상태</th>
							<th className="px-4 py-3 font-semibold">특이자</th>
							<th className="px-4 py-3 text-right font-semibold">연간 급여</th>
							<th className="px-4 py-3 text-right font-semibold">원천징수</th>
							<th className="px-4 py-3 text-right font-semibold">환급</th>
							<th className="px-4 py-3 text-right font-semibold">징수</th>
							<th className="px-4 py-3 font-semibold">공제 검토</th>
						</tr>
					</thead>
					<tbody>
						{employees.map((employee) => {
							const warning = employee.deductions.find((item) => item.warningMessage);
							return (
								<tr key={employee.id} className="border-t border-border">
									<td className="px-4 py-4 font-semibold">직원 #{employee.employeeId}</td>
									<td className="px-4 py-4">
										<Badge variant={yearEndStatusVariant(employee.status)}>
											{yearEndStatusLabel(employee.status)}
										</Badge>
									</td>
									<td className="px-4 py-4">
										{employee.specialReviewFlags.length > 0 ? (
											<div className="flex flex-wrap gap-2">
												{employee.specialReviewFlags.map((flag) => (
													<Badge key={flag} variant="warning">
														{yearEndSpecialFlagLabel(flag)}
													</Badge>
												))}
											</div>
										) : (
											<span className="text-muted-foreground">-</span>
										)}
									</td>
									<td className="px-4 py-4 text-right">{wonLabel(employee.annualGrossPay)}</td>
									<td className="px-4 py-4 text-right text-muted-foreground">
										{wonLabel(employee.annualTaxPaid)}
									</td>
									<td className="px-4 py-4 text-right font-semibold">
										{wonLabel(employee.expectedRefund)}
									</td>
									<td className="px-4 py-4 text-right text-muted-foreground">
										{wonLabel(employee.expectedCollection)}
									</td>
									<td className="px-4 py-4">
										{warning ? (
											<div>
												<p className="font-medium">
													{yearEndDeductionCategoryLabel(warning.category)} ·{' '}
													{wonLabel(warning.acceptedAmount)} 인정
												</p>
												<p className="mt-1 text-xs text-warning-foreground">
													{warning.warningMessage}
												</p>
											</div>
										) : (
											<span className="text-muted-foreground">검토 이슈 없음</span>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</>
	);
}

export function YearEndSettlementPage() {
	const [state, setState] = useState<RequestState<YearEndSettlementSummaryResponse>>({
		status: 'idle'
	});
	const year = new Date().getFullYear();

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const data = await api.get<YearEndSettlementSummaryResponse>(
					`/payroll/year-end-settlements/summary?year=${year}`
				);
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[year]
	);

	useEffect(() => {
		queueMicrotask(() => {
			void load();
		});
	}, [load]);

	async function openSettlement() {
		await api.post('/payroll/year-end-settlements/open', { year });
		await load();
	}

	const data = state.status === 'success' ? state.data : null;
	const reviewReadyCount = data ? data.approvedCount + data.finalizedCount : 0;

	return (
		<div className="space-y-6">
			<PageHeader
				title="연말정산"
				description="급여자료 반영, 공제자료 제출, 검토 경고와 환급·징수 결과를 관리합니다."
				actions={
					<div className="flex gap-2">
						<Button variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						<Button onClick={() => void openSettlement()}>
							<FileText className="size-4" />
							연말정산 오픈
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<PayrollStatCard
							title="대상자"
							value={`${numberLabel(data.targetEmployeeCount)}명`}
							description={`${data.year}년 정산 대상`}
						/>
						<PayrollStatCard
							title="검토 진행"
							value={`${numberLabel(reviewReadyCount)} / ${numberLabel(data.targetEmployeeCount)}명`}
							description={`수정 요청 ${numberLabel(data.needsCorrectionCount)}명`}
						/>
						<PayrollStatCard
							title="특이자"
							value={`${numberLabel(data.specialReviewCount)}명`}
							description={`공제 경고 ${numberLabel(data.deductionWarningCount)}건`}
						/>
						<PayrollStatCard
							title="환급 예정"
							value={wonLabel(data.totalExpectedRefund)}
							description={`징수 예정 ${wonLabel(data.totalExpectedCollection)}`}
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>연말정산 대시보드</CardTitle>
							<CardDescription>
								Flex 벤치마크 기준의 급여자료 반영, 제출서류, 공제 검증, 결과 확정 흐름을
								점검합니다.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-5">
							<div className="grid gap-3 lg:grid-cols-2">
								{data.checklist.map((item) => (
									<YearEndChecklistItemView key={item.code} item={item} />
								))}
							</div>
							<div className="grid gap-3 lg:grid-cols-3">
								<div className="rounded-lg border border-border bg-background p-4">
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<WalletCards className="size-4" />
										<span>연간 급여</span>
									</div>
									<p className="mt-2 text-2xl font-semibold tracking-normal">
										{wonLabel(data.totalAnnualGrossPay)}
									</p>
									<p className="mt-1 text-xs text-muted-foreground">
										확정 급여명세서 기반 자동 집계
									</p>
								</div>
								<div className="rounded-lg border border-border bg-background p-4">
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<AlertTriangle className="size-4" />
										<span>보완 대상</span>
									</div>
									<p className="mt-2 text-2xl font-semibold tracking-normal">
										{numberLabel(data.deductionWarningCount)}건
									</p>
									<p className="mt-1 text-xs text-muted-foreground">한도 초과와 제출서류 경고</p>
								</div>
								<div className="rounded-lg border border-border bg-background p-4">
									<div className="flex items-center gap-2 text-sm text-muted-foreground">
										<ShieldCheck className="size-4" />
										<span>영수증 제공</span>
									</div>
									<p className="mt-2 text-2xl font-semibold tracking-normal">
										{numberLabel(data.finalizedCount)}명
									</p>
									<p className="mt-1 text-xs text-muted-foreground">
										최종 확정 후 원천징수영수증 발급 가능
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>대상자 검토</CardTitle>
							<CardDescription>
								직원별 제출 상태, 특이자 플래그, 공제자료 경고와 환급·징수 예상액을 확인합니다.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<YearEndEmployeeTable employees={data.employees} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}

function groupInsurancePlanLabel(planType: GroupInsurancePlanType | null | undefined): string {
	const labels: Record<GroupInsurancePlanType, string> = {
		START: '스타터 플랜',
		GROWTH: '성장 플랜',
		PREMIUM: '프리미엄 플랜'
	};
	return planType ? labels[planType] : '플랜 미선택';
}

function groupInsurancePolicyStatusLabel(
	status: GroupInsurancePolicyStatus | null | undefined
): string {
	if (status === 'APPLIED') return '청약 접수';
	if (status === 'ACTIVE') return '보장 중';
	if (status === 'EXPIRED') return '만료';
	return '-';
}

function groupInsurancePaymentCycleLabel(
	cycle: GroupInsurancePaymentCycle | null | undefined
): string {
	if (cycle === 'MONTHLY') return '월 분납';
	if (cycle === 'YEARLY') return '연납';
	return '-';
}

function groupInsuranceEnrollmentStatusLabel(status: GroupInsuranceEnrollmentStatus): string {
	const labels: Record<GroupInsuranceEnrollmentStatus, string> = {
		ELIGIBLE: '가입 대상',
		ENROLLED: '가입 완료',
		WAIVED: '가입 보류',
		TERMINATED: '해지'
	};
	return labels[status];
}

function groupInsuranceStatusVariant(
	status: GroupInsuranceEnrollmentStatus | GroupInsurancePolicyStatus | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'ACTIVE' || status === 'ENROLLED') return 'success';
	if (status === 'APPLIED' || status === 'ELIGIBLE') return 'warning';
	if (status === 'TERMINATED' || status === 'EXPIRED') return 'destructive';
	return 'outline';
}

function groupInsuranceChecklistVariant(
	status: GroupInsuranceChecklistStatus | null | undefined
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function groupInsuranceChecklistStatusLabel(
	status: GroupInsuranceChecklistStatus | null | undefined
): string {
	if (status === 'READY') return '완료';
	if (status === 'WARNING') return '주의';
	if (status === 'WAITING') return '대기';
	return '-';
}

function groupInsuranceReadinessVariant(
	level: GroupInsuranceReadinessRiskLevel | null | undefined
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (level === 'READY') return 'success';
	if (level === 'WARNING') return 'warning';
	if (level === 'CRITICAL') return 'destructive';
	return 'outline';
}

function groupInsuranceCoveragePercent(summary: GroupInsuranceSummaryResponse): number {
	if (typeof summary.coverageReadinessPercent === 'number') {
		return summary.coverageReadinessPercent;
	}
	if (summary.activeEmployeeCount === 0) {
		return 0;
	}
	return Math.round((summary.enrolledCount * 1000) / summary.activeEmployeeCount) / 10;
}

function groupInsuranceReadinessLabel(summary: GroupInsuranceSummaryResponse): string {
	if (summary.readinessRiskLabel) return summary.readinessRiskLabel;
	if (!summary.policy) return '청약 전';
	if (summary.activeEmployeeCount > summary.enrolledCount) return '가입 결정 대기';
	return '보장 운영 준비 완료';
}

function groupInsuranceReadinessSummary(summary: GroupInsuranceSummaryResponse): string {
	if (summary.readinessRiskSummary) return summary.readinessRiskSummary;
	if (!summary.policy) return '단체보험 플랜이 없어 보장 적용과 월 청구를 시작할 수 없습니다.';
	return `활성 직원 ${numberLabel(summary.activeEmployeeCount)}명 중 ${numberLabel(summary.enrolledCount)}명 보장 적용`;
}

function groupInsuranceRecommendedAction(summary: GroupInsuranceSummaryResponse): string {
	if (summary.readinessRecommendedAction) return summary.readinessRecommendedAction;
	if (!summary.policy) return '단체보험 청약을 완료하고 활성 직원 가입 대상을 생성하세요.';
	return '입퇴사 동기화 후 가입 대상자의 보장 적용 상태를 확정하세요.';
}

function groupInsuranceExpectedMonthlyBilling(summary: GroupInsuranceSummaryResponse): number {
	if (typeof summary.expectedMonthlyBillingAmount === 'number') {
		return summary.expectedMonthlyBillingAmount;
	}
	if (summary.policy) {
		return summary.policy.monthlyPremiumPerEmployee * summary.activeEmployeeCount;
	}
	return summary.monthlyBillingAmount;
}

function GroupInsuranceReadinessMetric({
	label,
	value,
	description
}: {
	label: string;
	value: string;
	description: string;
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<p className="text-xs font-medium text-muted-foreground">{label}</p>
			<p className="mt-2 text-lg font-semibold tracking-normal">{value}</p>
			<p className="mt-1 text-xs text-muted-foreground">{description}</p>
		</div>
	);
}

function GroupInsuranceReadinessPanel({ summary }: { summary: GroupInsuranceSummaryResponse }) {
	const coveragePercent = groupInsuranceCoveragePercent(summary);
	const riskLevel = summary.readinessRiskLevel ?? (coveragePercent >= 100 ? 'READY' : 'WARNING');
	const unsyncedEmployeeCount = summary.unsyncedEmployeeCount ?? 0;
	const uncoveredActiveEmployeeCount =
		summary.uncoveredActiveEmployeeCount ??
		Math.max(summary.activeEmployeeCount - summary.enrolledCount, 0);
	const expectedMonthlyBillingAmount = groupInsuranceExpectedMonthlyBilling(summary);
	const billingGapAmount =
		summary.billingGapAmount ??
		Math.max(expectedMonthlyBillingAmount - summary.monthlyBillingAmount, 0);

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>보험 운영 준비도</CardTitle>
					<CardDescription>{groupInsuranceReadinessSummary(summary)}</CardDescription>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Badge variant={groupInsuranceReadinessVariant(riskLevel)}>
						{groupInsuranceReadinessLabel(summary)}
					</Badge>
					<Badge variant={coveragePercent >= 100 ? 'success' : 'warning'}>
						보장 적용 {percentLabel(coveragePercent)}
					</Badge>
				</div>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="rounded-lg border border-border bg-muted/30 p-4">
					<div className="flex items-start gap-3">
						<div className="rounded-md bg-background p-2 text-muted-foreground">
							<ShieldCheck className="size-4" />
						</div>
						<div className="min-w-0">
							<p className="text-sm font-semibold">권장 조치</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{groupInsuranceRecommendedAction(summary)}
							</p>
						</div>
					</div>
				</div>
				<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
					<GroupInsuranceReadinessMetric
						label="입퇴사 동기화"
						value={`${numberLabel(summary.syncedEnrollmentCount ?? summary.enrollments.length)}명`}
						description={`누락 ${numberLabel(unsyncedEmployeeCount)}명`}
					/>
					<GroupInsuranceReadinessMetric
						label="미보장 직원"
						value={`${numberLabel(uncoveredActiveEmployeeCount)}명`}
						description={`활성 직원 ${numberLabel(summary.activeEmployeeCount)}명`}
					/>
					<GroupInsuranceReadinessMetric
						label="예상 월 청구"
						value={wonLabel(expectedMonthlyBillingAmount)}
						description={`확정 ${wonLabel(summary.monthlyBillingAmount)}`}
					/>
					<GroupInsuranceReadinessMetric
						label="청구 갭"
						value={wonLabel(billingGapAmount)}
						description={`보류 ${numberLabel(summary.eligibleCount + summary.waivedCount)}명`}
					/>
				</div>
			</CardContent>
		</Card>
	);
}

function GroupInsuranceChecklistView({ item }: { item: GroupInsuranceChecklistItem }) {
	const Icon =
		item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={groupInsuranceChecklistVariant(item.status)}>
					{groupInsuranceChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function GroupInsuranceEnrollmentMobileCard({
	enrollment
}: {
	enrollment: GroupInsuranceEnrollmentItem;
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4 md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div>
					<p className="font-semibold">직원 #{enrollment.employeeId}</p>
					<p className="mt-1 text-sm text-muted-foreground">
						적용일 {dateLabel(enrollment.effectiveStartDate)}
					</p>
				</div>
				<Badge variant={groupInsuranceStatusVariant(enrollment.status)}>
					{groupInsuranceEnrollmentStatusLabel(enrollment.status)}
				</Badge>
			</div>
			<div className="mt-4 grid grid-cols-2 gap-3 text-sm">
				<div>
					<p className="text-xs text-muted-foreground">월 보험료</p>
					<p className="mt-1 font-semibold">{wonLabel(enrollment.employeeMonthlyPremium)}</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">가족 보장</p>
					<p className="mt-1 font-semibold">{numberLabel(enrollment.familyMemberCount)}명</p>
				</div>
			</div>
			{enrollment.memo ? (
				<p className="mt-3 text-sm text-muted-foreground">{enrollment.memo}</p>
			) : null}
		</div>
	);
}

function GroupInsuranceEnrollmentTable({
	enrollments
}: {
	enrollments: GroupInsuranceEnrollmentItem[];
}) {
	if (enrollments.length === 0) {
		return <EmptyState message="단체보험 가입 대상자가 없습니다." />;
	}
	return (
		<>
			<div className="space-y-3 md:hidden">
				{enrollments.map((enrollment) => (
					<GroupInsuranceEnrollmentMobileCard key={enrollment.id} enrollment={enrollment} />
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[860px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">직원</th>
							<th className="px-4 py-3 font-semibold">가입 상태</th>
							<th className="px-4 py-3 text-right font-semibold">월 보험료</th>
							<th className="px-4 py-3 text-right font-semibold">가족 보장</th>
							<th className="px-4 py-3 font-semibold">적용 기간</th>
							<th className="px-4 py-3 font-semibold">메모</th>
						</tr>
					</thead>
					<tbody>
						{enrollments.map((enrollment) => (
							<tr key={enrollment.id} className="border-t border-border">
								<td className="px-4 py-4 font-semibold">직원 #{enrollment.employeeId}</td>
								<td className="px-4 py-4">
									<Badge variant={groupInsuranceStatusVariant(enrollment.status)}>
										{groupInsuranceEnrollmentStatusLabel(enrollment.status)}
									</Badge>
								</td>
								<td className="px-4 py-4 text-right font-semibold">
									{wonLabel(enrollment.employeeMonthlyPremium)}
								</td>
								<td className="px-4 py-4 text-right text-muted-foreground">
									{numberLabel(enrollment.familyMemberCount)}명
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{dateLabel(enrollment.effectiveStartDate)} ~{' '}
									{dateLabel(enrollment.effectiveEndDate)}
								</td>
								<td className="px-4 py-4 text-muted-foreground">{enrollment.memo ?? '-'}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

export function GroupInsurancePage() {
	const [state, setState] = useState<RequestState<GroupInsuranceSummaryResponse>>({
		status: 'idle'
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const data = await api.get<GroupInsuranceSummaryResponse>(
					'/benefits/group-insurance/summary'
				);
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		queueMicrotask(() => {
			void load();
		});
	}, [load]);

	async function applyInsurance() {
		const nextMonth = new Date();
		nextMonth.setMonth(nextMonth.getMonth() + 1);
		const coverageStartDate = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}-01`;
		await api.post('/benefits/group-insurance/apply', {
			planType: 'GROWTH',
			paymentCycle: 'MONTHLY',
			familyCoverageEnabled: true,
			coverageStartDate
		});
		await load();
	}

	async function syncEmployees() {
		await api.post('/benefits/group-insurance/sync-employees');
		await load();
	}

	const data = state.status === 'success' ? state.data : null;
	const policy = data?.policy ?? null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="임직원 단체보험"
				description="성장 단계별 보장 플랜, 월 분납 보험료, 입퇴사 연동 가입 현황을 관리합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						<Button variant="outline" onClick={() => void syncEmployees()}>
							<UsersRound className="size-4" />
							입퇴사 동기화
						</Button>
						<Button onClick={() => void applyInsurance()}>
							<FileText className="size-4" />
							단체보험 청약
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<PayrollStatCard
							title="선택 플랜"
							value={groupInsurancePlanLabel(policy?.planType)}
							description={
								policy
									? `${policy.insurerName} · ${groupInsurancePolicyStatusLabel(policy.status)}`
									: '청약 전'
							}
						/>
						<PayrollStatCard
							title="가입 완료"
							value={`${numberLabel(data.enrolledCount)} / ${numberLabel(data.activeEmployeeCount)}명`}
							description={`가입 대상 ${numberLabel(data.eligibleCount)}명 · 보류 ${numberLabel(data.waivedCount)}명`}
						/>
						<PayrollStatCard
							title="월 납입"
							value={wonLabel(data.monthlyBillingAmount)}
							description={`직원 기본 ${wonLabel(policy?.monthlyPremiumPerEmployee)}`}
						/>
						<PayrollStatCard
							title="분납 방식"
							value={groupInsurancePaymentCycleLabel(policy?.paymentCycle)}
							description={
								policy
									? `${dateLabel(policy.coverageStartDate)} ~ ${dateLabel(policy.coverageEndDate)}`
									: '시작일 미정'
							}
						/>
					</div>

					<GroupInsuranceReadinessPanel summary={data} />

					<Card>
						<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
							<div>
								<CardTitle>단체보험 운영</CardTitle>
								<CardDescription>
									Flex 벤치마크 기준의 규모 무관 청약, 보장 항목, 월 분납, 입퇴사 연동을 점검합니다.
								</CardDescription>
							</div>
							<div className="flex flex-wrap items-center gap-2">
								<Badge variant={groupInsuranceStatusVariant(policy?.status)}>
									{groupInsurancePolicyStatusLabel(policy?.status)}
								</Badge>
								{policy?.familyCoverageEnabled ? <Badge variant="success">가족 보장</Badge> : null}
							</div>
						</CardHeader>
						<CardContent className="space-y-5">
							<div className="grid gap-3 lg:grid-cols-2">
								{data.checklist.map((item) => (
									<GroupInsuranceChecklistView key={item.code} item={item} />
								))}
							</div>
							<div className="grid gap-3 lg:grid-cols-3">
								{data.planOptions.map((option) => (
									<div
										key={option.planType}
										className={cn(
											'rounded-lg border border-border bg-background p-4',
											option.planType === policy?.planType && 'border-primary bg-primary/5'
										)}
									>
										<div className="flex items-start justify-between gap-3">
											<div>
												<p className="font-semibold">{groupInsurancePlanLabel(option.planType)}</p>
												<p className="mt-1 text-sm text-muted-foreground">{option.description}</p>
											</div>
											{option.planType === policy?.planType ? (
												<Badge variant="success">선택</Badge>
											) : null}
										</div>
										<p className="mt-4 text-2xl font-semibold tracking-normal">
											{wonLabel(option.monthlyPremiumPerEmployee)}
										</p>
										<p className="mt-1 text-xs text-muted-foreground">1인 기준 월 보험료</p>
									</div>
								))}
							</div>
							<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
								{data.coverageItems.map((item) => (
									<div
										key={item.code}
										className="rounded-lg border border-border bg-background p-4"
									>
										<div className="flex items-center gap-2 text-sm text-muted-foreground">
											<ShieldCheck className="size-4" />
											<span>{item.label}</span>
										</div>
										<p className="mt-2 text-sm text-muted-foreground">{item.description}</p>
									</div>
								))}
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>가입 대상자</CardTitle>
							<CardDescription>
								직원별 가입 상태, 가족 보장 인원, 월 보험료와 해지 여부를 확인합니다.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<GroupInsuranceEnrollmentTable enrollments={data.enrollments} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}

function meetingStatusLabel(status: MeetingStatus): string {
	const labels: Record<MeetingStatus, string> = {
		UNSCHEDULED: '일정 미정',
		SCHEDULED: '예정',
		RECORDED: '녹취 완료',
		SUMMARIZED: '요약 완료'
	};
	return labels[status];
}

function meetingStatusVariant(status: MeetingStatus): 'success' | 'warning' | 'outline' {
	if (status === 'SUMMARIZED') return 'success';
	if (status === 'RECORDED' || status === 'SCHEDULED') return 'warning';
	return 'outline';
}

function meetingChecklistVariant(
	status: MeetingChecklistStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function meetingChecklistStatusLabel(status: MeetingChecklistStatus): string {
	if (status === 'READY') return '완료';
	if (status === 'WARNING') return '주의';
	if (status === 'WAITING') return '대기';
	return '-';
}

function meetingKnowledgeRiskVariant(
	level: MeetingKnowledgeRiskLevel | null | undefined
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (level === 'READY') return 'success';
	if (level === 'WARNING') return 'warning';
	if (level === 'CRITICAL') return 'destructive';
	return 'outline';
}

function meetingPercent(numerator: number, denominator: number): number {
	if (denominator <= 0) return 0;
	return Math.round((numerator * 1000) / denominator) / 10;
}

function meetingSummaryCoverage(summary: MeetingSummaryResponse): number {
	if (typeof summary.summaryCoveragePercent === 'number') return summary.summaryCoveragePercent;
	return meetingPercent(summary.summarizedCount, summary.totalMeetingCount);
}

function meetingActionItemCoverage(summary: MeetingSummaryResponse): number {
	if (typeof summary.actionItemCoveragePercent === 'number')
		return summary.actionItemCoveragePercent;
	return meetingPercent(summary.actionItemCount, summary.totalMeetingCount);
}

function meetingCalendarSyncPercent(summary: MeetingSummaryResponse): number {
	if (typeof summary.calendarSyncPercent === 'number') return summary.calendarSyncPercent;
	return meetingPercent(summary.calendarSyncedCount, summary.totalMeetingCount);
}

function meetingKnowledgeLabel(summary: MeetingSummaryResponse): string {
	if (summary.knowledgeRiskLabel) return summary.knowledgeRiskLabel;
	if (summary.totalMeetingCount === 0) return '미팅 기록 없음';
	if (summary.summarizedCount < summary.totalMeetingCount) return 'AI 노트 축적 필요';
	if (summary.actionItemCount < summary.totalMeetingCount) return '액션 아이템 담당자 필요';
	if (summary.calendarSyncedCount < summary.totalMeetingCount) return '캘린더 연동 필요';
	return '미팅 지식화 준비 완료';
}

function meetingKnowledgeSummary(summary: MeetingSummaryResponse): string {
	if (summary.knowledgeRiskSummary) return summary.knowledgeRiskSummary;
	if (summary.totalMeetingCount === 0) return '조직 지식으로 축적된 미팅 기록이 아직 없습니다.';
	return `전체 미팅 ${numberLabel(summary.totalMeetingCount)}건 중 ${numberLabel(summary.summarizedCount)}건이 자동 노트로 지식화되었습니다.`;
}

function meetingRecommendedAction(summary: MeetingSummaryResponse): string {
	if (summary.knowledgeRecommendedAction) return summary.knowledgeRecommendedAction;
	const needsAiNote =
		summary.meetingsNeedingAiNoteCount ??
		Math.max(summary.totalMeetingCount - summary.summarizedCount, 0);
	if (needsAiNote > 0) return `AI 노트가 없는 ${numberLabel(needsAiNote)}건을 요약하세요.`;
	return '미팅 노트를 성과 평가와 목표 리뷰 근거로 정기적으로 연결하세요.';
}

type MeetingActionItemForm = {
	title: string;
	ownerEmployeeId: string;
	dueDate: string;
	priority: MeetingActionItemResponse['priority'];
	sourceText: string;
	status: MeetingActionItemResponse['status'];
};

function meetingActionItems(meeting: MeetingResponse): MeetingActionItemResponse[] {
	return meeting.actionItems ?? [];
}

function meetingActionItemMetaLabel(item: MeetingActionItemResponse): string {
	return [
		item.ownerEmployeeId == null ? '담당자 미정' : `직원 #${item.ownerEmployeeId}`,
		item.dueDate ?? '기한 미정',
		item.priority
	].join(' · ');
}

function meetingActionItemForm(item: MeetingActionItemResponse): MeetingActionItemForm {
	return {
		title: item.title,
		ownerEmployeeId: item.ownerEmployeeId == null ? '' : String(item.ownerEmployeeId),
		dueDate: item.dueDate ?? '',
		priority: item.priority,
		sourceText: item.sourceText ?? '',
		status: item.status
	};
}

function meetingActionItemRequest(item: MeetingActionItemForm): MeetingActionItemResponse {
	return {
		title: item.title.trim(),
		ownerEmployeeId: item.ownerEmployeeId ? Number(item.ownerEmployeeId) : null,
		dueDate: item.dueDate || null,
		priority: item.priority,
		sourceText: item.sourceText.trim() || null,
		status: item.status
	};
}

function MeetingKnowledgeMetric({
	label,
	value,
	description
}: {
	label: string;
	value: string;
	description: string;
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<p className="text-xs font-medium text-muted-foreground">{label}</p>
			<p className="mt-2 text-lg font-semibold tracking-normal">{value}</p>
			<p className="mt-1 text-xs text-muted-foreground">{description}</p>
		</div>
	);
}

function MeetingKnowledgeReadinessPanel({ summary }: { summary: MeetingSummaryResponse }) {
	const summaryCoverage = meetingSummaryCoverage(summary);
	const actionItemCoverage = meetingActionItemCoverage(summary);
	const calendarSyncPercent = meetingCalendarSyncPercent(summary);
	const needsAiNote =
		summary.meetingsNeedingAiNoteCount ??
		Math.max(summary.totalMeetingCount - summary.summarizedCount, 0);
	const unsyncedCalendarMeetingCount =
		summary.unsyncedCalendarMeetingCount ??
		Math.max(summary.totalMeetingCount - summary.calendarSyncedCount, 0);
	const evaluationEvidenceCount =
		summary.evaluationEvidenceCount ??
		summary.meetings.filter((meeting) => meeting.evaluationEvidence).length;
	const riskLevel =
		summary.knowledgeRiskLevel ??
		(summary.totalMeetingCount === 0 ? 'CRITICAL' : needsAiNote > 0 ? 'WARNING' : 'READY');

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>미팅 지식 운영 준비도</CardTitle>
					<CardDescription>{meetingKnowledgeSummary(summary)}</CardDescription>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Badge variant={meetingKnowledgeRiskVariant(riskLevel)}>
						{meetingKnowledgeLabel(summary)}
					</Badge>
					<Badge variant={summaryCoverage >= 80 ? 'success' : 'warning'}>
						요약 커버리지 {percentLabel(summaryCoverage)}
					</Badge>
				</div>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="rounded-lg border border-border bg-muted/30 p-4">
					<div className="flex items-start gap-3">
						<div className="rounded-md bg-background p-2 text-muted-foreground">
							<ClipboardCheck className="size-4" />
						</div>
						<div className="min-w-0">
							<p className="text-sm font-semibold">권장 조치</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{meetingRecommendedAction(summary)}
							</p>
						</div>
					</div>
				</div>
				<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
					<MeetingKnowledgeMetric
						label="AI 노트 필요"
						value={`${numberLabel(needsAiNote)}건`}
						description={`요약 완료 ${numberLabel(summary.summarizedCount)}건`}
					/>
					<MeetingKnowledgeMetric
						label="액션 아이템"
						value={percentLabel(actionItemCoverage)}
						description={`${numberLabel(summary.actionItemCount)}건 담당자 지정`}
					/>
					<MeetingKnowledgeMetric
						label="캘린더 맥락"
						value={percentLabel(calendarSyncPercent)}
						description={`미연동 ${numberLabel(unsyncedCalendarMeetingCount)}건`}
					/>
					<MeetingKnowledgeMetric
						label="성과 근거"
						value={`${numberLabel(evaluationEvidenceCount)}건`}
						description="평가·목표 리뷰 참고 자료"
					/>
				</div>
			</CardContent>
		</Card>
	);
}

function MeetingActionItemsPanel({
	meetings,
	onEdit
}: {
	meetings: MeetingResponse[];
	onEdit: (meeting: MeetingResponse) => void;
}) {
	const rows = meetings.flatMap((meeting) =>
		meetingActionItems(meeting).map((actionItem) => ({ meeting, actionItem }))
	);

	return (
		<Card>
			<CardHeader>
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div>
						<CardTitle>후속 과제</CardTitle>
						<CardDescription>
							미팅 요약에서 생성된 담당자, 기한, 우선순위 기반 실행 항목입니다.
						</CardDescription>
					</div>
					<Badge variant={rows.length > 0 ? 'warning' : 'outline'}>
						{numberLabel(rows.filter((row) => row.actionItem.status === 'OPEN').length)}개 진행
					</Badge>
				</div>
			</CardHeader>
			<CardContent>
				{rows.length === 0 ? (
					<EmptyState message="구조화된 후속 과제가 없습니다." />
				) : (
					<div className="space-y-3">
						{rows.map(({ meeting, actionItem }) => (
							<div
								key={`${meeting.id}-${actionItem.title}-${actionItem.ownerEmployeeId ?? 'none'}`}
								className="flex flex-col gap-3 rounded-lg border border-border bg-background p-4 sm:flex-row sm:items-start sm:justify-between"
							>
								<div className="min-w-0">
									<div className="flex flex-wrap items-center gap-2">
										<p className="font-semibold">{actionItem.title}</p>
										<Badge variant={actionItem.status === 'DONE' ? 'success' : 'warning'}>
											{actionItem.status === 'DONE' ? '완료' : '진행'}
										</Badge>
									</div>
									<p className="mt-1 text-sm font-medium text-muted-foreground">
										{meetingActionItemMetaLabel(actionItem)}
									</p>
									<p className="mt-2 text-xs text-muted-foreground">
										{meeting.title} · {actionItem.sourceText ?? '출처 문장 없음'}
									</p>
								</div>
								<Button type="button" size="sm" variant="outline" onClick={() => onEdit(meeting)}>
									<Pencil className="size-4" />
									후속 과제 편집
								</Button>
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function MeetingActionItemsDialog({
	meeting,
	onClose,
	onSubmitted
}: {
	meeting: MeetingResponse;
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [items, setItems] = useState<MeetingActionItemForm[]>(
		meetingActionItems(meeting).length > 0
			? meetingActionItems(meeting).map(meetingActionItemForm)
			: [
					{
						title: '',
						ownerEmployeeId: '',
						dueDate: '',
						priority: 'MEDIUM',
						sourceText: '',
						status: 'OPEN'
					}
				]
	);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	function updateItem(index: number, next: Partial<MeetingActionItemForm>) {
		setItems((current) =>
			current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...next } : item))
		);
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const normalized = items.filter((item) => item.title.trim());
		if (normalized.length === 0) {
			setError('후속 과제를 1개 이상 입력해주세요.');
			return;
		}
		if (
			normalized.some(
				(item) => item.ownerEmployeeId && !Number.isFinite(Number(item.ownerEmployeeId))
			)
		) {
			setError('담당자 ID는 숫자로 입력해주세요.');
			return;
		}
		const request: UpdateMeetingActionItemsRequest = {
			actionItems: normalized.map(meetingActionItemRequest)
		};
		setSubmitting(true);
		try {
			await api.post(`/meetings/${meeting.id}/action-items`, request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal title="후속 과제 편집" description={meeting.title} onClose={onClose}>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				{items.map((item, index) => (
					<div key={index} className="rounded-lg border border-border p-4">
						<div className="grid gap-4 sm:grid-cols-2">
							<div className="space-y-2">
								<Label htmlFor={`meeting-action-title-${index}`}>과제</Label>
								<Input
									id={`meeting-action-title-${index}`}
									value={item.title}
									onChange={(event) => updateItem(index, { title: event.target.value })}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor={`meeting-action-owner-${index}`}>담당자 ID</Label>
								<Input
									id={`meeting-action-owner-${index}`}
									inputMode="numeric"
									value={item.ownerEmployeeId}
									onChange={(event) => updateItem(index, { ownerEmployeeId: event.target.value })}
								/>
							</div>
						</div>
						<div className="mt-4 grid gap-4 sm:grid-cols-3">
							<div className="space-y-2">
								<Label htmlFor={`meeting-action-due-${index}`}>기한</Label>
								<Input
									id={`meeting-action-due-${index}`}
									type="date"
									value={item.dueDate}
									onChange={(event) => updateItem(index, { dueDate: event.target.value })}
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor={`meeting-action-priority-${index}`}>우선순위</Label>
								<select
									id={`meeting-action-priority-${index}`}
									value={item.priority}
									onChange={(event) =>
										updateItem(index, {
											priority: event.target.value as MeetingActionItemResponse['priority']
										})
									}
									className={dialogControlClassName}
								>
									<option value="HIGH">HIGH</option>
									<option value="MEDIUM">MEDIUM</option>
									<option value="LOW">LOW</option>
								</select>
							</div>
							<div className="space-y-2">
								<Label htmlFor={`meeting-action-status-${index}`}>상태</Label>
								<select
									id={`meeting-action-status-${index}`}
									value={item.status}
									onChange={(event) =>
										updateItem(index, {
											status: event.target.value as MeetingActionItemResponse['status']
										})
									}
									className={dialogControlClassName}
								>
									<option value="OPEN">진행</option>
									<option value="DONE">완료</option>
								</select>
							</div>
						</div>
						<div className="mt-4 space-y-2">
							<Label htmlFor={`meeting-action-source-${index}`}>출처 문장</Label>
							<Input
								id={`meeting-action-source-${index}`}
								value={item.sourceText}
								onChange={(event) => updateItem(index, { sourceText: event.target.value })}
							/>
						</div>
					</div>
				))}
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						저장
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function recurrenceLabel(value: string | null | undefined): string {
	if (!value) return '단발';
	if (value === 'WEEKLY') return '매주';
	if (value === 'BIWEEKLY') return '격주';
	if (value === 'MONTHLY') return '매월';
	return value;
}

function meetingTimeLabel(value: string | null | undefined): string {
	if (!value) return '일정 미정';
	return value.replace('T', ' ').slice(0, 16);
}

function MeetingChecklistView({ item }: { item: MeetingChecklistItem }) {
	const Icon =
		item.status === 'READY' ? CheckCircle2 : item.status === 'WARNING' ? AlertTriangle : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={meetingChecklistVariant(item.status)}>
					{meetingChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function MeetingMobileCard({
	meeting,
	onSummarize
}: {
	meeting: MeetingResponse;
	onSummarize: (meeting: MeetingResponse) => void;
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4 md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div>
					<p className="font-semibold">{meeting.title}</p>
					<p className="mt-1 text-sm text-muted-foreground">
						{meetingTimeLabel(meeting.scheduledAt)}
					</p>
				</div>
				<Badge variant={meetingStatusVariant(meeting.status)}>
					{meetingStatusLabel(meeting.status)}
				</Badge>
			</div>
			<div className="mt-4 grid grid-cols-2 gap-3 text-sm">
				<div>
					<p className="text-xs text-muted-foreground">참석자</p>
					<p className="mt-1 font-semibold">{numberLabel(meeting.participants.length)}명</p>
				</div>
				<div>
					<p className="text-xs text-muted-foreground">반복</p>
					<p className="mt-1 font-semibold">{recurrenceLabel(meeting.recurrenceRule)}</p>
				</div>
			</div>
			{meeting.decisionsText ? (
				<p className="mt-3 text-sm font-medium">{meeting.decisionsText}</p>
			) : null}
			{meeting.actionItemsText ? (
				<p className="mt-1 text-sm text-muted-foreground">{meeting.actionItemsText}</p>
			) : null}
			{meeting.status !== 'SUMMARIZED' ? (
				<Button
					className="mt-4 w-full"
					size="sm"
					variant="outline"
					onClick={() => onSummarize(meeting)}
				>
					AI 노트 생성
				</Button>
			) : null}
		</div>
	);
}

function MeetingTable({
	meetings,
	onSummarize
}: {
	meetings: MeetingResponse[];
	onSummarize: (meeting: MeetingResponse) => void;
}) {
	if (meetings.length === 0) {
		return <EmptyState message="등록된 미팅이 없습니다." />;
	}
	return (
		<>
			<div className="space-y-3 md:hidden">
				{meetings.map((meeting) => (
					<MeetingMobileCard key={meeting.id} meeting={meeting} onSummarize={onSummarize} />
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[1080px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">미팅</th>
							<th className="px-4 py-3 font-semibold">상태</th>
							<th className="px-4 py-3 font-semibold">일시</th>
							<th className="px-4 py-3 text-right font-semibold">참석자</th>
							<th className="px-4 py-3 font-semibold">반복/캘린더</th>
							<th className="px-4 py-3 font-semibold">결정·액션</th>
							<th className="px-4 py-3 text-right font-semibold">작업</th>
						</tr>
					</thead>
					<tbody>
						{meetings.map((meeting) => (
							<tr key={meeting.id} className="border-t border-border">
								<td className="px-4 py-4">
									<p className="font-semibold">{meeting.title}</p>
									<p className="mt-1 max-w-xs truncate text-xs text-muted-foreground">
										{meeting.purpose ?? meeting.location ?? '-'}
									</p>
								</td>
								<td className="px-4 py-4">
									<Badge variant={meetingStatusVariant(meeting.status)}>
										{meetingStatusLabel(meeting.status)}
									</Badge>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{meetingTimeLabel(meeting.scheduledAt)}
								</td>
								<td className="px-4 py-4 text-right">
									{numberLabel(meeting.participants.length)}명
								</td>
								<td className="px-4 py-4">
									<div className="flex flex-wrap gap-2">
										<Badge variant="outline">{recurrenceLabel(meeting.recurrenceRule)}</Badge>
										<Badge variant={meeting.calendarSynced ? 'success' : 'warning'}>
											{meeting.calendarSynced ? '캘린더 연동' : '연동 필요'}
										</Badge>
									</div>
								</td>
								<td className="px-4 py-4">
									<p className="font-medium">{meeting.decisionsText ?? '결정사항 미정리'}</p>
									<p className="mt-1 max-w-sm truncate text-xs text-muted-foreground">
										{meeting.actionItemsText ?? meeting.summary ?? 'AI 노트 생성 필요'}
									</p>
								</td>
								<td className="px-4 py-4 text-right">
									{meeting.status !== 'SUMMARIZED' ? (
										<Button size="sm" variant="outline" onClick={() => onSummarize(meeting)}>
											AI 노트 생성
										</Button>
									) : (
										<Badge variant={meeting.evaluationEvidence ? 'success' : 'outline'}>
											{meeting.evaluationEvidence ? '평가 근거' : '지식화'}
										</Badge>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

export function MeetingsPage() {
	const [state, setState] = useState<RequestState<MeetingSummaryResponse>>({ status: 'idle' });
	const [actionItemsDialogMeeting, setActionItemsDialogMeeting] = useState<MeetingResponse | null>(
		null
	);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const data = await api.get<MeetingSummaryResponse>('/meetings/summary');
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		queueMicrotask(() => {
			void load();
		});
	}, [load]);

	async function createMeeting() {
		await api.post('/meetings', {
			title: '제품 출시 주간 회의',
			purpose: '출시 범위와 리스크를 정리합니다.',
			scheduledAt: '2026-07-01T10:00:00',
			durationMinutes: 60,
			location: '회의실 A',
			organizerEmployeeId: 1,
			participantEmployeeIds: [1],
			recurrenceRule: 'WEEKLY',
			calendarSynced: true,
			noteTemplate: '논의 / 결정 / 액션'
		});
		await load();
	}

	async function summarizeMeeting(meeting: MeetingResponse) {
		await api.post(`/meetings/${meeting.id}/summarize`, {
			transcriptText:
				'제품 출시 범위는 베타 기능으로 제한하고 7월 1일에 공개합니다. 담당자는 QA 체크리스트를 완료합니다.'
		});
		await load();
	}

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="미팅"
				description="회의 기록을 자동 노트, 결정사항, 액션 아이템, 성과 평가 근거로 축적합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						<Button onClick={() => void createMeeting()}>
							<FileText className="size-4" />
							미팅 생성
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<PayrollStatCard
							title="전체 미팅"
							value={`${numberLabel(data.totalMeetingCount)}건`}
							description={`예정 ${numberLabel(data.scheduledCount)}건 · 미정 ${numberLabel(data.unscheduledCount)}건`}
						/>
						<PayrollStatCard
							title="자동 노트"
							value={`${numberLabel(data.summarizedCount)}건`}
							description={`액션 아이템 ${numberLabel(data.actionItemCount)}건`}
						/>
						<PayrollStatCard
							title="반복 미팅"
							value={`${numberLabel(data.recurringCount)}건`}
							description="템플릿 자동 복사 대상"
						/>
						<PayrollStatCard
							title="캘린더 연동"
							value={`${numberLabel(data.calendarSyncedCount)}건`}
							description="양방향 동기화된 미팅"
						/>
					</div>

					<MeetingKnowledgeReadinessPanel summary={data} />

					<MeetingActionItemsPanel
						meetings={data.meetings}
						onEdit={(meeting) => setActionItemsDialogMeeting(meeting)}
					/>

					<Card>
						<CardHeader>
							<CardTitle>미팅 지식화</CardTitle>
							<CardDescription>
								Flex 벤치마크 기준의 자동 노트, 맥락 타임라인, 반복 미팅, 캘린더 연동, 평가 근거
								축적을 점검합니다.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-5">
							<div className="grid gap-3 lg:grid-cols-2">
								{data.checklist.map((item) => (
									<MeetingChecklistView key={item.code} item={item} />
								))}
							</div>
							<div className="grid gap-3 lg:grid-cols-3">
								{data.knowledgeItems.length > 0 ? (
									data.knowledgeItems.map((item) => (
										<div
											key={`${item.title}-${item.actionItem}`}
											className="rounded-lg border border-border bg-background p-4"
										>
											<div className="flex items-start justify-between gap-3">
												<div>
													<p className="font-semibold">{item.title}</p>
													<p className="mt-2 text-sm text-muted-foreground">{item.actionItem}</p>
												</div>
												<Badge variant="success">{item.evidenceLabel}</Badge>
											</div>
										</div>
									))
								) : (
									<div className="rounded-lg border border-dashed border-border bg-background p-4 text-sm text-muted-foreground lg:col-span-3">
										요약된 미팅이 생기면 결정과 액션 아이템이 조직 지식으로 표시됩니다.
									</div>
								)}
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>미팅 목록</CardTitle>
							<CardDescription>
								예정·미정·지난 미팅과 참석자, 결정사항, 캘린더 동기화 상태를 확인합니다.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<MeetingTable
								meetings={data.meetings}
								onSummarize={(meeting) => void summarizeMeeting(meeting)}
							/>
						</CardContent>
					</Card>

					{actionItemsDialogMeeting ? (
						<MeetingActionItemsDialog
							meeting={actionItemsDialogMeeting}
							onClose={() => setActionItemsDialogMeeting(null)}
							onSubmitted={load}
						/>
					) : null}
				</>
			) : null}
		</div>
	);
}

type ReportsPageData = {
	monthlyAttendance: MonthlyAttendanceSummaryResponse[];
	weeklyCompliance: WeeklyComplianceSummaryResponse[];
	leavePromotionTargets: LeavePromotionTarget[];
	leavePromotionProgress: LeavePromotionProgressResponse[];
	month: string;
	weekStart: string;
	year: number;
};

type ReportEmployeeRow = {
	employeeId: number;
	name: string;
	department: string | null;
	position: string | null;
	attendance: MonthlyAttendanceSummaryResponse | null;
	compliance: WeeklyComplianceSummaryResponse | null;
	promotion: LeavePromotionProgressResponse | null;
	promotionTarget: LeavePromotionTarget | null;
};

function pad2(value: number): string {
	return String(value).padStart(2, '0');
}

function monthParam(date: Date): string {
	return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`;
}

function monthStartParam(month: string): string {
	return `${month}-01`;
}

function monthEndParam(month: string): string {
	const [year, monthNumber] = month.split('-').map(Number);
	return dateParam(new Date(year, monthNumber, 0));
}

function dateParam(date: Date): string {
	return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function currentWeekStartParam(date = new Date()): string {
	const next = new Date(date);
	const day = next.getDay();
	const offset = day === 0 ? -6 : 1 - day;
	next.setDate(next.getDate() + offset);
	return dateParam(next);
}

function isMonthParam(value: string | null): value is string {
	return typeof value === 'string' && /^\d{4}-\d{2}$/.test(value);
}

function isDateParam(value: string | null): value is string {
	return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function reportAttendanceStatusLabel(
	status: MonthlyAttendanceSummaryResponse['status'] | null | undefined
): string {
	const labels: Record<MonthlyAttendanceSummaryResponse['status'], string> = {
		OK: '정상',
		CAUTION: '주의',
		NEEDS_REVIEW: '검토 필요'
	};
	return status ? labels[status] : '-';
}

function reportComplianceStatusLabel(
	status: WeeklyComplianceSummaryResponse['status'] | null | undefined
): string {
	const labels: Record<WeeklyComplianceSummaryResponse['status'], string> = {
		OK: '정상',
		NEAR_LIMIT: '임박',
		OVER_LIMIT: '초과'
	};
	return status ? labels[status] : '-';
}

function reportStatusVariant(
	status:
		| MonthlyAttendanceSummaryResponse['status']
		| WeeklyComplianceSummaryResponse['status']
		| null
		| undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'OK') return 'success';
	if (status === 'CAUTION' || status === 'NEAR_LIMIT') return 'warning';
	if (status === 'NEEDS_REVIEW' || status === 'OVER_LIMIT') return 'destructive';
	return 'outline';
}

function leavePlanStatusLabel(
	status: LeavePromotionProgressResponse['planStatus'] | null | undefined
): string {
	if (status === 'SUBMITTED') return '제출';
	if (status === 'NOT_SUBMITTED') return '미제출';
	return '-';
}

function daysLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
	return `${numberLabel(value)}일`;
}

function buildReportRows(data: ReportsPageData): ReportEmployeeRow[] {
	const employeeIds = new Set<number>();
	const attendanceMap = new Map<number, MonthlyAttendanceSummaryResponse>();
	const complianceMap = new Map<number, WeeklyComplianceSummaryResponse>();
	const promotionMap = new Map<number, LeavePromotionProgressResponse>();
	const targetMap = new Map<number, LeavePromotionTarget>();

	for (const attendance of data.monthlyAttendance) {
		employeeIds.add(attendance.employeeId);
		attendanceMap.set(attendance.employeeId, attendance);
	}
	for (const compliance of data.weeklyCompliance) {
		employeeIds.add(compliance.employeeId);
		complianceMap.set(compliance.employeeId, compliance);
	}
	for (const promotion of data.leavePromotionProgress) {
		employeeIds.add(promotion.employeeId);
		promotionMap.set(promotion.employeeId, promotion);
	}
	for (const target of data.leavePromotionTargets) {
		employeeIds.add(target.employeeId);
		targetMap.set(target.employeeId, target);
	}

	return Array.from(employeeIds)
		.sort((a, b) => a - b)
		.map((employeeId) => {
			const attendance = attendanceMap.get(employeeId) ?? null;
			const compliance = complianceMap.get(employeeId) ?? null;
			const promotion = promotionMap.get(employeeId) ?? null;
			const promotionTarget = targetMap.get(employeeId) ?? null;
			const name =
				attendance?.employeeName ??
				compliance?.employeeName ??
				promotion?.employeeName ??
				promotionTarget?.name ??
				`직원 #${employeeId}`;
			const department =
				attendance?.department ??
				compliance?.department ??
				promotion?.department ??
				promotionTarget?.department ??
				null;
			const position =
				attendance?.position ??
				compliance?.position ??
				promotion?.position ??
				promotionTarget?.position ??
				null;
			return {
				employeeId,
				name,
				department,
				position,
				attendance,
				compliance,
				promotion,
				promotionTarget
			};
		});
}

function ReportStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: string;
	description: string;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<p className="text-sm font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal sm:mt-3">{value}</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function ReportMobileCard({ row }: { row: ReportEmployeeRow }) {
	return (
		<div className="rounded-lg border border-border bg-card p-4 shadow-sm md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="font-semibold">{row.name}</p>
					<p className="mt-1 text-sm text-muted-foreground">
						{[row.department, row.position].filter(Boolean).join(' · ') ||
							`직원 #${row.employeeId}`}
					</p>
				</div>
				<Badge variant={reportStatusVariant(row.compliance?.status)}>
					{reportComplianceStatusLabel(row.compliance?.status)}
				</Badge>
			</div>
			<div className="mt-3 grid grid-cols-2 gap-3 text-sm">
				<div>
					<p className="text-xs font-medium text-muted-foreground">근태</p>
					<p className="mt-1">{reportAttendanceStatusLabel(row.attendance?.status)}</p>
				</div>
				<div>
					<p className="text-xs font-medium text-muted-foreground">초과근무</p>
					<p className="mt-1">{minutesLabel(row.compliance?.overtimeMinutes)}</p>
				</div>
				<div>
					<p className="text-xs font-medium text-muted-foreground">미체크</p>
					<p className="mt-1">{numberLabel(row.attendance?.missingCheckoutDays)}건</p>
				</div>
				<div>
					<p className="text-xs font-medium text-muted-foreground">연차 계획</p>
					<p className="mt-1">{leavePlanStatusLabel(row.promotion?.planStatus)}</p>
				</div>
			</div>
		</div>
	);
}

function ReportsTable({ rows }: { rows: ReportEmployeeRow[] }) {
	if (rows.length === 0) return <EmptyState message="리포트 데이터가 없습니다." />;

	return (
		<>
			<div className="space-y-3 md:hidden">
				{rows.map((row) => (
					<ReportMobileCard key={row.employeeId} row={row} />
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[980px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">직원</th>
							<th className="px-4 py-3 font-semibold">월간 근태</th>
							<th className="px-4 py-3 font-semibold">지각/결근</th>
							<th className="px-4 py-3 font-semibold">주간 준수</th>
							<th className="px-4 py-3 font-semibold">근무/초과</th>
							<th className="px-4 py-3 font-semibold">연차 촉진</th>
							<th className="px-4 py-3 font-semibold">미계획</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr key={row.employeeId} className="border-t border-border">
								<td className="px-4 py-4">
									<p className="font-semibold">{row.name}</p>
									<p className="mt-1 text-xs text-muted-foreground">
										{[row.department, row.position].filter(Boolean).join(' · ') ||
											`직원 #${row.employeeId}`}
									</p>
								</td>
								<td className="px-4 py-4">
									<Badge variant={reportStatusVariant(row.attendance?.status)}>
										{reportAttendanceStatusLabel(row.attendance?.status)}
									</Badge>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{numberLabel(row.attendance?.lateDays)} /{' '}
									{numberLabel(row.attendance?.absentDays)}
								</td>
								<td className="px-4 py-4">
									<Badge variant={reportStatusVariant(row.compliance?.status)}>
										{reportComplianceStatusLabel(row.compliance?.status)}
									</Badge>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{minutesLabel(row.compliance?.workMinutes)} /{' '}
									{minutesLabel(row.compliance?.overtimeMinutes)}
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{leavePlanStatusLabel(row.promotion?.planStatus)}
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{daysLabel(row.promotion?.unplannedDays ?? row.promotionTarget?.remainingDays)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

export function ReportsPage() {
	const searchParams = useSearchParams();
	const monthQuery = searchParams?.get('month') ?? null;
	const weekStartQuery = searchParams?.get('weekStart') ?? null;
	const yearQuery = searchParams?.get('year') ?? null;
	const initialSourceMonth = isMonthParam(monthQuery) ? monthQuery : monthParam(new Date());
	const [state, setState] = useState<RequestState<ReportsPageData>>({ status: 'idle' });
	const [query, setQuery] = useState('');
	const [downloadError, setDownloadError] = useState<string | null>(null);
	const [sourceStartDate, setSourceStartDate] = useState(() => monthStartParam(initialSourceMonth));
	const [sourceEndDate, setSourceEndDate] = useState(() => monthEndParam(initialSourceMonth));

	const load = useMemo(
		() => async () => {
			const now = new Date();
			const parsedYear = yearQuery ? Number(yearQuery) : Number.NaN;
			const month = isMonthParam(monthQuery) ? monthQuery : monthParam(now);
			const weekStart = isDateParam(weekStartQuery) ? weekStartQuery : currentWeekStartParam(now);
			const year =
				Number.isInteger(parsedYear) && parsedYear >= 2000 && parsedYear <= 2100
					? parsedYear
					: now.getFullYear();
			setState({ status: 'loading' });
			setDownloadError(null);
			try {
				const [monthlyAttendance, weeklyCompliance, leavePromotionTargets, leavePromotionProgress] =
					await Promise.all([
						api.get<MonthlyAttendanceSummaryResponse[]>(
							`/reports/attendance/summary?month=${month}`
						),
						api.get<WeeklyComplianceSummaryResponse[]>(
							`/reports/weekly-compliance/summary?weekStart=${weekStart}`
						),
						api.get<LeavePromotionTarget[]>(`/reports/leave-promotion?year=${year}`),
						api.get<LeavePromotionProgressResponse[]>(
							`/reports/leave-promotion/progress?year=${year}`
						)
					]);
				setState({
					status: 'success',
					data: {
						monthlyAttendance,
						weeklyCompliance,
						leavePromotionTargets,
						leavePromotionProgress,
						month,
						weekStart,
						year
					}
				});
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[monthQuery, weekStartQuery, yearQuery]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const rows = useMemo(() => (data ? buildReportRows(data) : []), [data]);
	const filteredRows = useMemo(() => {
		const keyword = query.trim().toLowerCase();
		if (!keyword) return rows;
		return rows.filter((row) =>
			[
				row.name,
				row.department,
				row.position,
				reportAttendanceStatusLabel(row.attendance?.status),
				reportComplianceStatusLabel(row.compliance?.status),
				leavePlanStatusLabel(row.promotion?.planStatus)
			]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(keyword))
		);
	}, [query, rows]);

	const attendanceIssueCount =
		data?.monthlyAttendance.filter((item) => item.status !== 'OK').length ?? 0;
	const weeklyRiskCount = data?.weeklyCompliance.filter((item) => item.status !== 'OK').length ?? 0;
	const promotionTargetCount = data?.leavePromotionTargets.length ?? 0;
	const submittedPlanCount =
		data?.leavePromotionProgress.filter((item) => item.planStatus === 'SUBMITTED').length ?? 0;
	const missingCheckoutCount =
		data?.monthlyAttendance.reduce(
			(sum, item) => sum + item.missingCheckoutDays + item.locationUnverifiedDays,
			0
		) ?? 0;

	const downloadReport = async (kind: 'attendance' | 'attendance-source' | 'weekly' | 'leave') => {
		const current = data;
		if (!current) return;
		setDownloadError(null);
		try {
			if (kind === 'attendance') {
				await api.downloadFile(
					`/reports/attendance?month=${current.month}`,
					`attendance_${current.month}.csv`
				);
			} else if (kind === 'attendance-source') {
				if (
					!isDateParam(sourceStartDate) ||
					!isDateParam(sourceEndDate) ||
					sourceStartDate > sourceEndDate
				) {
					setDownloadError('원천 데이터 기간을 확인해주세요.');
					return;
				}
				await api.downloadFile(
					`/reports/attendance/source?startDate=${sourceStartDate}&endDate=${sourceEndDate}`,
					`attendance_source_${sourceStartDate}_${sourceEndDate}.csv`
				);
			} else if (kind === 'weekly') {
				await api.downloadFile(
					`/reports/weekly-compliance?weekStart=${current.weekStart}`,
					`weekly_compliance_${current.weekStart}.csv`
				);
			} else {
				await api.downloadFile(
					`/reports/leave-usage?year=${current.year}`,
					`leave_usage_${current.year}.csv`
				);
			}
		} catch (error) {
			setDownloadError(getErrorMessage(error, '리포트를 다운로드하지 못했습니다.'));
		}
	};

	return (
		<div className="space-y-6">
			<PageHeader
				title="리포트"
				description="근태, 주간 근로시간, 연차 촉진 현황을 운영 지표로 확인합니다."
				actions={
					<Button variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{downloadError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{downloadError}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<ReportStatCard
							title="근태 점검"
							value={`${numberLabel(attendanceIssueCount)}명`}
							description={`${data.month} 월간 이상 상태`}
						/>
						<ReportStatCard
							title="주 52시간 위험"
							value={`${numberLabel(weeklyRiskCount)}명`}
							description={`${dateLabel(data.weekStart)} 시작 주`}
						/>
						<ReportStatCard
							title="연차 촉진"
							value={`${numberLabel(promotionTargetCount)}명`}
							description={`계획 제출 ${numberLabel(submittedPlanCount)}명`}
						/>
						<ReportStatCard
							title="누락 점검"
							value={`${numberLabel(missingCheckoutCount)}건`}
							description="퇴근 누락과 위치 미확인 합계"
						/>
					</div>

					<Card>
						<CardHeader className="gap-4 xl:flex-row xl:items-center xl:justify-between">
							<div>
								<CardTitle>리포트 운영</CardTitle>
								<CardDescription>
									직원별 근태 이상, 근로시간 준수, 연차 촉진 상태를 함께 확인합니다.
								</CardDescription>
							</div>
							<div className="flex w-full flex-col gap-3 xl:w-auto">
								<div className="flex w-full flex-col gap-2 sm:flex-row xl:justify-end">
									<div className="relative w-full xl:w-72">
										<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
										<Input
											value={query}
											onChange={(event) => setQuery(event.target.value)}
											placeholder="직원, 상태, 부서 검색"
											className="pl-9"
											aria-label="리포트 검색"
										/>
									</div>
									<div className="grid grid-cols-3 gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => void downloadReport('attendance')}
										>
											<Download className="size-4" />
											근태
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() => void downloadReport('weekly')}
										>
											<Download className="size-4" />
											주간
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() => void downloadReport('leave')}
										>
											<Download className="size-4" />
											휴가
										</Button>
									</div>
								</div>
								<div className="grid w-full gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] xl:w-[44rem]">
									<div className="space-y-1.5">
										<Label htmlFor="attendance-source-start" className="text-xs">
											원천 데이터 시작일
										</Label>
										<Input
											id="attendance-source-start"
											type="date"
											value={sourceStartDate}
											onChange={(event) => setSourceStartDate(event.target.value)}
										/>
									</div>
									<div className="space-y-1.5">
										<Label htmlFor="attendance-source-end" className="text-xs">
											원천 데이터 종료일
										</Label>
										<Input
											id="attendance-source-end"
											type="date"
											value={sourceEndDate}
											onChange={(event) => setSourceEndDate(event.target.value)}
										/>
									</div>
									<Button
										variant="outline"
										size="sm"
										className="h-10 whitespace-nowrap sm:self-end"
										onClick={() => void downloadReport('attendance-source')}
									>
										<Download className="size-4" />
										근태 원천 데이터
									</Button>
								</div>
							</div>
						</CardHeader>
						<CardContent>
							<ReportsTable rows={filteredRows} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}

type BillingPageData = {
	subscription: SubscriptionResponse;
	planPrices: PlanPriceResponse[];
	payments: PageResponse<PaymentResponse>;
};

function subscriptionStatusLabel(
	status: SubscriptionResponse['status'] | null | undefined
): string {
	const labels: Record<SubscriptionResponse['status'], string> = {
		FREE: '무료',
		ACTIVE: '활성',
		PAST_DUE: '연체',
		EXPIRED: '만료'
	};
	return status ? labels[status] : '-';
}

function billingCycleLabel(
	cycle: PaymentResponse['billingCycle'] | SubscriptionResponse['billingCycle'] | null | undefined
): string {
	if (cycle === 'MONTHLY') return '월간';
	if (cycle === 'YEARLY') return '연간';
	return '-';
}

function paymentStatusLabel(status: PaymentResponse['status'] | null | undefined): string {
	const labels: Record<PaymentResponse['status'], string> = {
		PENDING: '결제 대기',
		APPROVED: '승인',
		REJECTED: '반려',
		EXPIRED: '만료'
	};
	return status ? labels[status] : '-';
}

function paymentStatusVariant(
	status: PaymentResponse['status'] | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'APPROVED') return 'success';
	if (status === 'PENDING') return 'warning';
	if (status === 'REJECTED') return 'destructive';
	return 'outline';
}

function billingPeriodLabel(subscription: SubscriptionResponse): string {
	if (!subscription.currentPeriodStart || !subscription.currentPeriodEnd) return '-';
	return `${dateLabel(subscription.currentPeriodStart)} - ${dateLabel(subscription.currentPeriodEnd)}`;
}

function BillingStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: string;
	description: string;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<p className="text-sm font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal sm:mt-3">{value}</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function PlanPriceGrid({ prices }: { prices: PlanPriceResponse[] }) {
	if (prices.length === 0) return <EmptyState message="등록된 요금표가 없습니다." />;
	return (
		<div className="grid gap-3 md:grid-cols-3">
			{prices.map((price) => (
				<div
					key={`${price.plan}-${price.billingCycle}`}
					className="rounded-lg border border-border bg-muted/20 p-4"
				>
					<div className="flex items-start justify-between gap-3">
						<div>
							<p className="font-semibold">{price.plan} 플랜</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{billingCycleLabel(price.billingCycle)}
							</p>
						</div>
						<Badge variant="outline">최대 {numberLabel(price.maxEmployees)}명</Badge>
					</div>
					<p className="mt-4 text-2xl font-semibold tracking-normal">{wonLabel(price.amountKrw)}</p>
				</div>
			))}
		</div>
	);
}

function BillingMobileCard({ payment }: { payment: PaymentResponse }) {
	return (
		<div className="rounded-lg border border-border bg-card p-4 shadow-sm md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<Link
						href={`/billing/payments/${payment.id}`}
						aria-label={`결제 #${payment.id} 상세 보기`}
						className="inline-flex max-w-full items-center gap-1 font-semibold transition-colors hover:text-primary"
					>
						<span className="truncate">결제 #{payment.id}</span>
						<ChevronRight className="size-4 shrink-0" />
					</Link>
					<p className="mt-1 text-sm text-muted-foreground">
						{payment.plan} · {billingCycleLabel(payment.billingCycle)}
					</p>
				</div>
				<Badge variant={paymentStatusVariant(payment.status)}>
					{paymentStatusLabel(payment.status)}
				</Badge>
			</div>
			<div className="mt-3 grid grid-cols-2 gap-3 text-sm">
				<div>
					<p className="text-xs font-medium text-muted-foreground">금액</p>
					<p className="mt-1">{wonLabel(payment.amountKrw)}</p>
				</div>
				<div>
					<p className="text-xs font-medium text-muted-foreground">만료</p>
					<p className="mt-1">{dateLabel(payment.expiresAt)}</p>
				</div>
			</div>
		</div>
	);
}

function BillingPaymentTable({ payments }: { payments: PaymentResponse[] }) {
	if (payments.length === 0) return <EmptyState message="결제 요청 이력이 없습니다." />;

	return (
		<>
			<div className="space-y-3 md:hidden">
				{payments.map((payment) => (
					<BillingMobileCard key={payment.id} payment={payment} />
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[860px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">요청</th>
							<th className="px-4 py-3 font-semibold">플랜</th>
							<th className="px-4 py-3 font-semibold">금액</th>
							<th className="px-4 py-3 font-semibold">입금자</th>
							<th className="px-4 py-3 font-semibold">상태</th>
							<th className="px-4 py-3 font-semibold">만료</th>
							<th className="px-4 py-3 text-right font-semibold">상세</th>
						</tr>
					</thead>
					<tbody>
						{payments.map((payment) => (
							<tr key={payment.id} className="border-t border-border">
								<td className="px-4 py-4">
									<p className="font-semibold">결제 #{payment.id}</p>
									<p className="mt-1 text-xs text-muted-foreground">
										{dateLabel(payment.createdAt)}
									</p>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{payment.plan} · {billingCycleLabel(payment.billingCycle)}
								</td>
								<td className="px-4 py-4 text-muted-foreground">{wonLabel(payment.amountKrw)}</td>
								<td className="px-4 py-4 text-muted-foreground">{payment.depositorName ?? '-'}</td>
								<td className="px-4 py-4">
									<Badge variant={paymentStatusVariant(payment.status)}>
										{paymentStatusLabel(payment.status)}
									</Badge>
								</td>
								<td className="px-4 py-4 text-muted-foreground">{dateLabel(payment.expiresAt)}</td>
								<td className="px-4 py-4 text-right">
									<Link
										href={`/billing/payments/${payment.id}`}
										aria-label={`결제 #${payment.id} 상세 보기`}
										className="inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
									>
										보기
										<ChevronRight className="size-4" />
									</Link>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

export function BillingPage() {
	const [state, setState] = useState<RequestState<BillingPageData>>({ status: 'idle' });
	const [query, setQuery] = useState('');

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [subscription, planPrices, payments] = await Promise.all([
					api.get<SubscriptionResponse>('/billing/subscription'),
					api.get<PlanPriceResponse[]>('/billing/plan-prices').catch(() => []),
					api.get<PageResponse<PaymentResponse>>('/billing/payments?page=0&size=20').catch(() => ({
						content: [],
						page: 0,
						size: 20,
						totalElements: 0,
						totalPages: 0
					}))
				]);
				setState({ status: 'success', data: { subscription, planPrices, payments } });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const payments = useMemo(() => data?.payments.content ?? [], [data]);
	const filteredPayments = useMemo(() => {
		const keyword = query.trim().toLowerCase();
		if (!keyword) return payments;
		return payments.filter((payment) =>
			[
				`결제 #${payment.id}`,
				payment.plan,
				billingCycleLabel(payment.billingCycle),
				paymentStatusLabel(payment.status),
				payment.depositorName,
				payment.bankAccount
			]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(keyword))
		);
	}, [payments, query]);

	const pendingCount = payments.filter((payment) => payment.status === 'PENDING').length;
	const approvedCount = payments.filter((payment) => payment.status === 'APPROVED').length;

	return (
		<div className="space-y-6">
			<PageHeader
				title="구독"
				description="요금제, 사업자 청구 정보, 결제 요청 이력을 관리합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Link
							href="/billing/checkout"
							className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
						>
							결제 요청
							<ChevronRight className="size-4" />
						</Link>
						<Button variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<BillingStatCard
							title="현재 플랜"
							value={data.subscription.plan}
							description={`${billingCycleLabel(data.subscription.billingCycle)} 결제 기준`}
						/>
						<BillingStatCard
							title="구독 상태"
							value={subscriptionStatusLabel(data.subscription.status)}
							description={billingPeriodLabel(data.subscription)}
						/>
						<BillingStatCard
							title="대기 결제"
							value={`${numberLabel(pendingCount)}건`}
							description={`승인 완료 ${numberLabel(approvedCount)}건`}
						/>
						<BillingStatCard
							title="청구 정보"
							value={data.subscription.payerBusinessName ?? '-'}
							description={data.subscription.payerEmail ?? '청구 이메일 미등록'}
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>요금제</CardTitle>
							<CardDescription>
								현재 사용할 수 있는 플랜과 월간/연간 가격을 확인합니다.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<PlanPriceGrid prices={data.planPrices} />
						</CardContent>
					</Card>

					<Card>
						<CardHeader className="gap-4 lg:flex-row lg:items-center lg:justify-between">
							<div>
								<CardTitle>구독 운영</CardTitle>
								<CardDescription>
									결제 요청 상태, 입금자, 만료일을 기준으로 이력을 확인합니다.
								</CardDescription>
							</div>
							<div className="relative w-full lg:w-80">
								<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
								<Input
									value={query}
									onChange={(event) => setQuery(event.target.value)}
									placeholder="플랜, 상태, 입금자 검색"
									className="pl-9"
									aria-label="결제 이력 검색"
								/>
							</div>
						</CardHeader>
						<CardContent>
							<BillingPaymentTable payments={filteredPayments} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}

type ExpensesPageData = {
	summary: ExpenseManagementSummaryResponse;
	expenses: ExpenseResponse[];
	policies: ExpensePolicyRuleResponse[];
	approvalLines: ApprovalLineResponse[];
	draftSuggestions: ExpenseDraftSuggestionResponse[];
	managementMode: boolean;
};

type ExpensePolicyForm = {
	name: string;
	department: string;
	category: ExpenseCategory | '';
	paymentMethod: ExpensePaymentMethod | '';
	maxAmount: string;
	requireAttachment: boolean;
	action: ExpensePolicyAction;
};

type ExpenseAccountingReviewForm = {
	expenseId: string;
	debitAccount: string;
	creditAccount: string;
};

function defaultExpenseRequestForm(approvalLines: ApprovalLineResponse[]): ExpenseFormValues {
	return {
		category: 'MEAL',
		amount: '',
		expenseDate: toLocalDateString(new Date()),
		description: '',
		attachmentUrl: '',
		approvalLineId: approvalLines[0]?.id ?? '',
		evidenceType: 'OTHER',
		paymentMethod: 'ETC',
		merchantName: ''
	};
}

const expenseCategoryOptions: Array<{ value: ExpenseCategory; label: string }> = [
	{ value: 'MEAL', label: '식대' },
	{ value: 'TRANSPORT', label: '교통' },
	{ value: 'SUPPLIES', label: '비품' },
	{ value: 'ACCOMMODATION', label: '숙박' },
	{ value: 'ETC', label: '기타' }
];

const expensePaymentMethodOptions: Array<{ value: ExpensePaymentMethod; label: string }> = [
	{ value: 'CORPORATE_CARD', label: '법인카드' },
	{ value: 'PERSONAL_CARD', label: '개인카드' },
	{ value: 'BANK_TRANSFER', label: '계좌이체' },
	{ value: 'CASH', label: '현금' },
	{ value: 'ETC', label: '기타' }
];

const expenseEvidenceTypeOptions: Array<{ value: ExpenseEvidenceType; label: string }> = [
	{ value: 'CORPORATE_CARD', label: '법인카드' },
	{ value: 'TAX_INVOICE', label: '세금계산서' },
	{ value: 'CASH_RECEIPT', label: '현금영수증' },
	{ value: 'PERSONAL_CARD', label: '개인카드' },
	{ value: 'OTHER', label: '기타' }
];

function expenseCategoryLabel(category: ExpenseCategory | null | undefined): string {
	const labels: Record<ExpenseCategory, string> = {
		MEAL: '식대',
		TRANSPORT: '교통',
		SUPPLIES: '비품',
		ACCOMMODATION: '숙박',
		ETC: '기타'
	};
	return category ? labels[category] : '-';
}

function expenseStatusLabel(status: ExpenseStatus | null | undefined): string {
	const labels: Record<ExpenseStatus, string> = {
		PENDING: '대기',
		APPROVED: '승인',
		REJECTED: '반려',
		CANCELLED: '취소'
	};
	return status ? labels[status] : '-';
}

function expenseStatusVariant(
	status: ExpenseStatus | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'APPROVED') return 'success';
	if (status === 'PENDING') return 'warning';
	if (status === 'REJECTED') return 'destructive';
	return 'outline';
}

function expenseEvidenceTypeLabel(type: ExpenseEvidenceType | null | undefined): string {
	const labels: Record<ExpenseEvidenceType, string> = {
		CORPORATE_CARD: '법인카드',
		TAX_INVOICE: '세금계산서',
		CASH_RECEIPT: '현금영수증',
		PERSONAL_CARD: '개인카드',
		OTHER: '기타'
	};
	return type ? labels[type] : '-';
}

function expensePaymentMethodLabel(method: ExpensePaymentMethod | null | undefined): string {
	return method
		? (expensePaymentMethodOptions.find((option) => option.value === method)?.label ?? method)
		: '-';
}

function expenseAccountingStatusLabel(status: ExpenseAccountingStatus | null | undefined): string {
	const labels: Record<ExpenseAccountingStatus, string> = {
		NOT_READY: '미대상',
		READY: '검토 대기',
		REVIEWED: '검토 완료',
		EXPORTED: '내보냄'
	};
	return status ? labels[status] : '-';
}

function expenseAccountingStatusVariant(
	status: ExpenseAccountingStatus | null | undefined
): 'success' | 'warning' | 'outline' | 'destructive' | 'secondary' {
	if (status === 'EXPORTED') return 'success';
	if (status === 'READY') return 'warning';
	if (status === 'REVIEWED') return 'outline';
	if (status === 'NOT_READY') return 'secondary';
	return 'secondary';
}

function expensePolicyActionLabel(action: ExpensePolicyAction): string {
	const labels: Record<ExpensePolicyAction, string> = {
		WARN: '주의 표시',
		REQUIRE_MEMO: '사유 필수',
		BLOCK: '신청 차단'
	};
	return labels[action];
}

function buildExpenseRows(expenses: ExpenseResponse[]): ExpenseResponse[] {
	return [...expenses].sort((a, b) => {
		const left = a.createdAt || a.expenseDate;
		const right = b.createdAt || b.expenseDate;
		return right.localeCompare(left);
	});
}

function expenseAmountTotal(expenses: ExpenseResponse[]): number {
	return expenses.reduce(
		(sum, expense) => sum + (Number.isFinite(expense.amount) ? expense.amount : 0),
		0
	);
}

function expenseCloseReadinessLevelFromCounts({
	policyViolationCount,
	unassignedReceiptCount,
	accountingReadyCount,
	pendingExpenseCount
}: Pick<
	ExpenseManagementSummaryResponse,
	'policyViolationCount' | 'unassignedReceiptCount' | 'accountingReadyCount' | 'pendingExpenseCount'
>): ExpenseCloseReadinessLevel {
	if (policyViolationCount > 0 || unassignedReceiptCount > 0) return 'CRITICAL';
	if (accountingReadyCount > 0 || pendingExpenseCount > 0) return 'WARNING';
	return 'READY';
}

function expenseCloseReadinessLevel(
	summary: ExpenseManagementSummaryResponse
): ExpenseCloseReadinessLevel {
	return summary.closeReadinessLevel ?? expenseCloseReadinessLevelFromCounts(summary);
}

function expenseCloseReadinessLabel(summary: ExpenseManagementSummaryResponse): string {
	if (summary.closeReadinessLabel) return summary.closeReadinessLabel;
	const labels: Record<ExpenseCloseReadinessLevel, string> = {
		READY: '마감 준비 완료',
		WARNING: '마감 전 확인 필요',
		CRITICAL: '마감 전 보완 필요'
	};
	return labels[expenseCloseReadinessLevel(summary)];
}

function expenseCloseReadinessSummaryText(summary: ExpenseManagementSummaryResponse): string {
	if (summary.closeReadinessSummary) return summary.closeReadinessSummary;
	const level = expenseCloseReadinessLevel(summary);
	if (level === 'CRITICAL') {
		return [
			summary.policyViolationCount > 0
				? `정책 위반 ${numberLabel(summary.policyViolationCount)}건`
				: null,
			summary.unassignedReceiptCount > 0
				? `미배정 증빙 ${numberLabel(summary.unassignedReceiptCount)}건`
				: null,
			summary.accountingReadyCount > 0
				? `회계 검토 대기 ${numberLabel(summary.accountingReadyCount)}건`
				: null
		]
			.filter(Boolean)
			.join(', ')
			.concat('이 마감을 막고 있습니다.');
	}
	if (level === 'WARNING') {
		return [
			summary.pendingExpenseCount > 0
				? `승인 대기 ${numberLabel(summary.pendingExpenseCount)}건`
				: null,
			summary.accountingReadyCount > 0
				? `회계 검토 대기 ${numberLabel(summary.accountingReadyCount)}건`
				: null
		]
			.filter(Boolean)
			.join(', ')
			.concat('을 처리하면 전표 내보내기를 진행할 수 있습니다.');
	}
	return '마감 차단 항목이 없습니다. 검토 완료 비용은 전표 내보내기로 진행할 수 있습니다.';
}

function expenseCloseRecommendedAction(summary: ExpenseManagementSummaryResponse): string {
	if (summary.closeRecommendedAction) return summary.closeRecommendedAction;
	const level = expenseCloseReadinessLevel(summary);
	if (level === 'CRITICAL') {
		return '정책 위반과 미배정 증빙을 먼저 보완하고, 승인된 비용의 차변·대변 계정을 지정하세요.';
	}
	if (summary.accountingReadyCount > 0) {
		return '승인된 비용의 차변·대변 계정을 지정하고 검토 완료 건을 전표로 내보내세요.';
	}
	if (summary.pendingExpenseCount > 0) {
		return '승인 대기 비용을 처리한 뒤 회계 검토 대상으로 전환하세요.';
	}
	return '검토 완료 비용을 전표 배치로 내보내고 마감 증적을 보관하세요.';
}

function expenseReadinessVariant(
	level: ExpenseCloseReadinessLevel
): 'success' | 'warning' | 'destructive' {
	if (level === 'READY') return 'success';
	if (level === 'WARNING') return 'warning';
	return 'destructive';
}

function expenseChecklistVariant(
	status: ExpenseCloseChecklistStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function expenseChecklistStatusLabel(status: ExpenseCloseChecklistStatus): string {
	const labels: Record<ExpenseCloseChecklistStatus, string> = {
		READY: '완료',
		WAITING: '대기',
		WARNING: '확인'
	};
	return labels[status];
}

function expenseRiskVariant(
	severity: ExpenseCloseRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function buildLegacyExpenseSummary(
	myExpenses: ExpenseResponse[],
	pendingExpenses: ExpenseResponse[]
): ExpenseManagementSummaryResponse {
	const all = buildExpenseRows([...myExpenses, ...pendingExpenses]);
	const pendingExpenseCount = all.filter((expense) => expense.status === 'PENDING').length;
	const policyViolationCount = all.filter((expense) => expense.policyViolation).length;
	const accountingReadyCount = all.filter((expense) => expense.accountingStatus === 'READY').length;
	const accountingReviewedCount = all.filter(
		(expense) => expense.accountingStatus === 'REVIEWED'
	).length;
	const accountingExportedCount = all.filter(
		(expense) => expense.accountingStatus === 'EXPORTED'
	).length;
	const unassignedReceiptCount = all.filter((expense) => expense.assignedEmployeeId == null).length;
	const closeReadinessLevel = expenseCloseReadinessLevelFromCounts({
		pendingExpenseCount,
		policyViolationCount,
		accountingReadyCount,
		unassignedReceiptCount
	});

	return {
		totalExpenseCount: all.length,
		pendingExpenseCount,
		policyViolationCount,
		accountingReadyCount,
		accountingReviewedCount,
		accountingExportedCount,
		unassignedReceiptCount,
		totalAmount: expenseAmountTotal(all),
		closeReadinessLevel
	};
}

function ExpenseStatCard({
	title,
	value,
	description
}: {
	title: string;
	value: string;
	description: string;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<p className="text-sm font-medium text-muted-foreground">{title}</p>
				<p className="mt-2 text-2xl font-semibold tracking-normal sm:mt-3">{value}</p>
				<p className="mt-1 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function expenseCloseFallbackChecklist(
	summary: ExpenseManagementSummaryResponse
): NonNullable<ExpenseManagementSummaryResponse['checklist']> {
	return [
		{
			code: 'POLICY_GUARDRAIL',
			label: '비용 정책',
			status: summary.policyViolationCount > 0 ? 'WARNING' : 'READY',
			description:
				summary.policyViolationCount > 0
					? '정책 위반 비용을 보완합니다.'
					: '정책 위반 비용이 없습니다.'
		},
		{
			code: 'RECEIPT_OWNERSHIP',
			label: '증빙 담당',
			status: summary.unassignedReceiptCount > 0 ? 'WARNING' : 'READY',
			description:
				summary.unassignedReceiptCount > 0
					? '미배정 증빙의 담당자를 지정합니다.'
					: '모든 증빙에 담당자가 지정되어 있습니다.'
		},
		{
			code: 'ACCOUNTING_REVIEW',
			label: '회계 검토',
			status: summary.accountingReadyCount > 0 ? 'WARNING' : 'READY',
			description:
				summary.accountingReadyCount > 0
					? '승인된 비용의 계정을 검토합니다.'
					: '회계 검토 대기 비용이 없습니다.'
		},
		{
			code: 'EXPORT_READY',
			label: '전표 내보내기',
			status: (summary.accountingReviewedCount ?? 0) > 0 ? 'READY' : 'WAITING',
			description:
				(summary.accountingReviewedCount ?? 0) > 0
					? '전표 배치로 내보낼 비용이 있습니다.'
					: '검토 완료 비용을 만든 뒤 내보낼 수 있습니다.'
		}
	];
}

function expenseCloseFallbackRiskItems(
	summary: ExpenseManagementSummaryResponse
): NonNullable<ExpenseManagementSummaryResponse['riskItems']> {
	return [
		{
			code: 'POLICY_VIOLATION',
			title: '정책 위반',
			count: summary.policyViolationCount,
			severity: summary.policyViolationCount > 0 ? 'CRITICAL' : 'READY',
			actionLabel: '보완 요청'
		},
		{
			code: 'UNASSIGNED_RECEIPT',
			title: '미배정 증빙',
			count: summary.unassignedReceiptCount,
			severity: summary.unassignedReceiptCount > 0 ? 'CRITICAL' : 'READY',
			actionLabel: '담당자 배정'
		},
		{
			code: 'ACCOUNTING_REVIEW',
			title: '회계 검토 대기',
			count: summary.accountingReadyCount,
			severity: summary.accountingReadyCount > 0 ? 'WARNING' : 'READY',
			actionLabel: '계정 검토'
		},
		{
			code: 'PENDING_APPROVAL',
			title: '승인 대기',
			count: summary.pendingExpenseCount,
			severity: summary.pendingExpenseCount > 0 ? 'WARNING' : 'READY',
			actionLabel: '승인 독려'
		}
	];
}

function ExpenseCloseReadinessPanel({ summary }: { summary: ExpenseManagementSummaryResponse }) {
	const level = expenseCloseReadinessLevel(summary);
	const checklist = summary.checklist?.length
		? summary.checklist
		: expenseCloseFallbackChecklist(summary);
	const riskItems = summary.riskItems?.length
		? summary.riskItems
		: expenseCloseFallbackRiskItems(summary);
	const closeQueue = summary.closeQueue ?? [];
	const accountingReviewedCount = summary.accountingReviewedCount ?? 0;

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div className="min-w-0 space-y-2">
					<div className="flex flex-wrap items-center gap-2">
						<CardTitle className="flex items-center gap-2">
							<ClipboardCheck className="size-5 text-primary" />
							비용 마감 준비도
						</CardTitle>
						<Badge variant={expenseReadinessVariant(level)}>
							{expenseCloseReadinessLabel(summary)}
						</Badge>
					</div>
					<CardDescription>{expenseCloseReadinessSummaryText(summary)}</CardDescription>
					<p className="text-sm font-medium text-foreground">
						{expenseCloseRecommendedAction(summary)}
					</p>
				</div>
				<div className="grid w-full gap-2 text-sm sm:grid-cols-3 lg:w-[30rem]">
					<div className="rounded-lg border border-border bg-muted/20 p-3">
						<p className="text-xs font-medium text-muted-foreground">검토 완료</p>
						<p className="mt-1 text-lg font-semibold tracking-normal">
							{numberLabel(accountingReviewedCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-muted/20 p-3">
						<p className="text-xs font-medium text-muted-foreground">회계 검토 대기</p>
						<p className="mt-1 text-lg font-semibold tracking-normal">
							{numberLabel(summary.accountingReadyCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-muted/20 p-3">
						<p className="text-xs font-medium text-muted-foreground">미배정 증빙</p>
						<p className="mt-1 text-lg font-semibold tracking-normal">
							{numberLabel(summary.unassignedReceiptCount)}건
						</p>
					</div>
				</div>
			</CardHeader>
			<CardContent className="grid gap-4 lg:grid-cols-[1fr_1.1fr]">
				<div className="space-y-4">
					<div className="space-y-2">
						<p className="text-sm font-semibold">마감 리스크</p>
						<div className="grid gap-2 sm:grid-cols-2">
							{riskItems.map((risk) => (
								<div key={risk.code} className="rounded-lg border border-border p-3">
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="font-semibold">{risk.title}</p>
											<p className="mt-1 text-xs text-muted-foreground">{risk.actionLabel}</p>
										</div>
										<Badge variant={expenseRiskVariant(risk.severity)}>
											{numberLabel(risk.count)}건
										</Badge>
									</div>
								</div>
							))}
						</div>
					</div>
					<div className="space-y-2">
						<p className="text-sm font-semibold">마감 체크리스트</p>
						<div className="grid gap-2 sm:grid-cols-2">
							{checklist.map((item) => (
								<div key={item.code} className="rounded-lg border border-border p-3">
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="font-semibold">{item.label}</p>
											<p className="mt-1 text-xs text-muted-foreground">{item.description}</p>
										</div>
										<Badge variant={expenseChecklistVariant(item.status)}>
											{expenseChecklistStatusLabel(item.status)}
										</Badge>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
				<div className="space-y-2">
					<div className="flex items-center justify-between gap-3">
						<p className="text-sm font-semibold">우선 처리 큐</p>
						<Badge variant={closeQueue.length > 0 ? 'warning' : 'success'}>
							{numberLabel(closeQueue.length)}건
						</Badge>
					</div>
					{closeQueue.length > 0 ? (
						<div className="space-y-2">
							{closeQueue.map((item) => (
								<div key={item.expenseId} className="rounded-lg border border-border p-3">
									<div className="flex flex-wrap items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="font-semibold">
												{item.merchantName ?? `직원 #${item.employeeId} 비용`}
											</p>
											<p className="mt-1 text-xs text-muted-foreground">
												{dateLabel(item.expenseDate)} · {wonLabel(item.amount)}
											</p>
										</div>
										<Badge variant={item.policyViolation ? 'destructive' : 'warning'}>
											{item.actionLabel}
										</Badge>
									</div>
									<div className="mt-3 flex flex-wrap gap-2">
										<Badge variant={expenseStatusVariant(item.status)}>
											{expenseStatusLabel(item.status)}
										</Badge>
										<Badge variant={expenseAccountingStatusVariant(item.accountingStatus)}>
											{expenseAccountingStatusLabel(item.accountingStatus)}
										</Badge>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
							마감 차단 항목이 없습니다.
						</div>
					)}
				</div>
			</CardContent>
		</Card>
	);
}

function ExpenseRequestDialog({
	approvalLines,
	draftSuggestions,
	onClose,
	onSubmitted
}: {
	approvalLines: ApprovalLineResponse[];
	draftSuggestions: ExpenseDraftSuggestionResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [form, setForm] = useState<ExpenseFormValues>(() =>
		defaultExpenseRequestForm(approvalLines)
	);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	function applySuggestion(suggestion: ExpenseDraftSuggestionResponse) {
		setForm((current) => ({
			...current,
			category: suggestion.category,
			amount: suggestion.amount,
			expenseDate: suggestion.expenseDate,
			description: suggestion.description ?? '',
			attachmentUrl: suggestion.attachmentUrl ?? '',
			evidenceType: suggestion.evidenceType,
			paymentMethod: suggestion.paymentMethod,
			merchantName: suggestion.merchantName ?? ''
		}));
		setError(null);
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateExpenseForm(form);
		if (!result.ok) {
			setError(result.message);
			return;
		}

		setSubmitting(true);
		try {
			await api.post<ExpenseResponse>('/expenses', result.request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="경비 신청"
			description="증빙과 결재선을 입력해 경비를 신청합니다."
			onClose={onClose}
		>
			<form className="space-y-5" onSubmit={(event) => void handleSubmit(event)}>
				{error ? (
					<div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
						{error}
					</div>
				) : null}
				<div className="space-y-3">
					<div className="flex items-center justify-between gap-3">
						<p className="text-sm font-semibold">최근 경비 재사용</p>
						<Badge variant={draftSuggestions.length > 0 ? 'outline' : 'secondary'}>
							{numberLabel(draftSuggestions.length)}건
						</Badge>
					</div>
					{draftSuggestions.length > 0 ? (
						<div className="grid gap-2 sm:grid-cols-2">
							{draftSuggestions.map((suggestion) => (
								<div
									key={suggestion.sourceExpenseId}
									className="rounded-lg border border-border p-3"
								>
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="font-semibold">
												{suggestion.merchantName ?? expenseCategoryLabel(suggestion.category)}
											</p>
											<p className="mt-1 text-xs text-muted-foreground">
												{expenseCategoryLabel(suggestion.category)} · {wonLabel(suggestion.amount)}
											</p>
										</div>
										<Badge variant={suggestion.usageCount > 1 ? 'success' : 'outline'}>
											{numberLabel(suggestion.usageCount)}회
										</Badge>
									</div>
									<p className="mt-2 text-xs text-muted-foreground">{suggestion.policyHint}</p>
									<Button
										type="button"
										variant="outline"
										size="sm"
										className="mt-3 w-full"
										onClick={() => applySuggestion(suggestion)}
									>
										{suggestion.actionLabel}
									</Button>
								</div>
							))}
						</div>
					) : (
						<div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
							재사용할 최근 경비가 없습니다.
						</div>
					)}
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="expense-request-category">분류</Label>
						<select
							id="expense-request-category"
							className={dialogControlClassName}
							value={form.category}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									category: event.target.value as ExpenseCategory
								}))
							}
						>
							{expenseCategoryOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-request-amount">금액</Label>
						<Input
							id="expense-request-amount"
							type="number"
							min="1"
							value={form.amount ?? ''}
							onChange={(event) =>
								setForm((current) => ({ ...current, amount: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-request-date">지출일</Label>
						<Input
							id="expense-request-date"
							type="date"
							value={form.expenseDate}
							onChange={(event) =>
								setForm((current) => ({ ...current, expenseDate: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-request-line">결재선</Label>
						<select
							id="expense-request-line"
							className={dialogControlClassName}
							value={form.approvalLineId ?? ''}
							onChange={(event) =>
								setForm((current) => ({ ...current, approvalLineId: event.target.value }))
							}
						>
							<option value="">결재선 선택</option>
							{approvalLines.map((line) => (
								<option key={line.id} value={line.id}>
									{line.name}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-request-evidence">증빙 유형</Label>
						<select
							id="expense-request-evidence"
							className={dialogControlClassName}
							value={form.evidenceType ?? 'OTHER'}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									evidenceType: event.target.value as ExpenseEvidenceType
								}))
							}
						>
							{expenseEvidenceTypeOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-request-payment">결제수단</Label>
						<select
							id="expense-request-payment"
							className={dialogControlClassName}
							value={form.paymentMethod ?? 'ETC'}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									paymentMethod: event.target.value as ExpensePaymentMethod
								}))
							}
						>
							{expensePaymentMethodOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-request-merchant">가맹점</Label>
						<Input
							id="expense-request-merchant"
							value={form.merchantName ?? ''}
							onChange={(event) =>
								setForm((current) => ({ ...current, merchantName: event.target.value }))
							}
							placeholder="가맹점 또는 사용처"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-request-attachment">첨부 URL</Label>
						<Input
							id="expense-request-attachment"
							value={form.attachmentUrl}
							onChange={(event) =>
								setForm((current) => ({ ...current, attachmentUrl: event.target.value }))
							}
							placeholder="https://..."
						/>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="expense-request-description">설명</Label>
					<textarea
						id="expense-request-description"
						className={dialogTextAreaClassName}
						value={form.description}
						onChange={(event) =>
							setForm((current) => ({ ...current, description: event.target.value }))
						}
						placeholder="사용 목적 또는 보완 사유"
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || approvalLines.length === 0}>
						신청
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function ExpenseMobileCard({ expense }: { expense: ExpenseResponse }) {
	return (
		<div className="rounded-lg border border-border bg-card p-4 shadow-sm md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="font-semibold">
						{expense.merchantName ?? expenseCategoryLabel(expense.category)}
					</p>
					<p className="mt-1 text-sm text-muted-foreground">
						{expense.description ?? expensePolicyMessage(expense) ?? `직원 #${expense.employeeId}`}
					</p>
				</div>
				<Badge variant={expenseStatusVariant(expense.status)} className="whitespace-nowrap">
					{expenseStatusLabel(expense.status)}
				</Badge>
			</div>
			<div className="mt-3 grid grid-cols-2 gap-3 text-sm">
				<div>
					<p className="text-xs font-medium text-muted-foreground">증빙</p>
					<p className="mt-1">{wonLabel(expense.amount)}</p>
					<p className="mt-1 text-xs text-muted-foreground">
						{expenseEvidenceTypeLabel(expense.evidenceType)}
					</p>
				</div>
				<div>
					<p className="text-xs font-medium text-muted-foreground">회계</p>
					<p className="mt-1">{expenseAccountingStatusLabel(expense.accountingStatus)}</p>
					<p className="mt-1 text-xs text-muted-foreground">{dateLabel(expense.expenseDate)}</p>
				</div>
			</div>
			{expense.policyViolation ? (
				<p className="mt-3 text-sm text-destructive">{expense.policyMessage ?? '정책 확인 필요'}</p>
			) : null}
		</div>
	);
}

function expensePolicyMessage(expense: ExpenseResponse): string | null {
	if (expense.policyViolation) return expense.policyMessage ?? '정책 확인 필요';
	return expense.policyMessage && expense.policyMessage !== '정책 검토 완료'
		? expense.policyMessage
		: null;
}

function ExpenseTable({ expenses }: { expenses: ExpenseResponse[] }) {
	if (expenses.length === 0) return <EmptyState message="경비 신청 내역이 없습니다." />;

	return (
		<>
			<div className="space-y-3 md:hidden">
				{expenses.map((expense) => (
					<ExpenseMobileCard key={expense.id} expense={expense} />
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[1080px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">증빙</th>
							<th className="px-4 py-3 font-semibold">직원</th>
							<th className="px-4 py-3 font-semibold">금액</th>
							<th className="px-4 py-3 font-semibold">정책</th>
							<th className="px-4 py-3 font-semibold">상태</th>
							<th className="px-4 py-3 font-semibold">회계</th>
							<th className="px-4 py-3 font-semibold">증빙</th>
						</tr>
					</thead>
					<tbody>
						{expenses.map((expense) => (
							<tr key={expense.id} className="border-t border-border align-top">
								<td className="px-4 py-4">
									<p className="font-semibold">
										{expense.merchantName ?? expenseCategoryLabel(expense.category)}
									</p>
									<p className="mt-1 text-xs text-muted-foreground">
										{expenseCategoryLabel(expense.category)} ·{' '}
										{expensePaymentMethodLabel(expense.paymentMethod)}
									</p>
									<p className="mt-1 text-xs text-muted-foreground">
										{dateLabel(expense.expenseDate)}
									</p>
								</td>
								<td className="px-4 py-4">
									<p className="text-muted-foreground">직원 #{expense.employeeId}</p>
									<p className="mt-1 text-xs text-muted-foreground">
										담당 {expense.assignedEmployeeId ? `#${expense.assignedEmployeeId}` : '미배정'}
									</p>
								</td>
								<td className="px-4 py-4 text-muted-foreground">{wonLabel(expense.amount)}</td>
								<td className="px-4 py-4">
									{expense.policyViolation ? (
										<div className="space-y-1">
											<Badge variant="destructive">정책 위반</Badge>
											<p className="text-xs text-destructive">{expense.policyMessage}</p>
										</div>
									) : (
										<div className="space-y-1">
											<Badge variant="success">검토 완료</Badge>
											<p className="text-xs text-muted-foreground">
												{expense.policyMessage ?? '정책 이상 없음'}
											</p>
										</div>
									)}
								</td>
								<td className="px-4 py-4">
									<Badge
										variant={expenseStatusVariant(expense.status)}
										className="whitespace-nowrap"
									>
										{expenseStatusLabel(expense.status)}
									</Badge>
								</td>
								<td className="px-4 py-4">
									<Badge variant={expenseAccountingStatusVariant(expense.accountingStatus)}>
										{expenseAccountingStatusLabel(expense.accountingStatus)}
									</Badge>
									<p className="mt-2 text-xs text-muted-foreground">
										{expense.debitAccount && expense.creditAccount
											? `${expense.debitAccount} / ${expense.creditAccount}`
											: (expense.exportBatchId ?? '전표 미작성')}
									</p>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{expense.attachmentUrl ? (
										<a
											href={expense.attachmentUrl}
											target="_blank"
											rel="noreferrer"
											className="inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
										>
											열기
											<ChevronRight className="size-4" />
										</a>
									) : (
										'-'
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

function ExpensePolicyRuleList({ policies }: { policies: ExpensePolicyRuleResponse[] }) {
	if (policies.length === 0) return <EmptyState message="등록된 비용 정책이 없습니다." />;
	return (
		<div className="space-y-3">
			{policies.map((policy) => (
				<div key={policy.id} className="rounded-lg border border-border p-3">
					<div className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-semibold">{policy.name}</p>
							<p className="mt-1 text-xs text-muted-foreground">
								{policy.department ?? '전체 조직'} · {expenseCategoryLabel(policy.category)} ·{' '}
								{expensePaymentMethodLabel(policy.paymentMethod)}
							</p>
						</div>
						<Badge variant={policy.action === 'BLOCK' ? 'destructive' : 'outline'}>
							{expensePolicyActionLabel(policy.action)}
						</Badge>
					</div>
					<p className="mt-2 text-xs text-muted-foreground">
						{policy.maxAmount ? `${wonLabel(policy.maxAmount)} 한도` : '금액 한도 없음'}
						{policy.requireAttachment ? ' · 첨부 필수' : ''}
					</p>
				</div>
			))}
		</div>
	);
}

function ExpensePolicyDialog({
	onClose,
	onSubmitted
}: {
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [form, setForm] = useState<ExpensePolicyForm>({
		name: '',
		department: '',
		category: 'MEAL',
		paymentMethod: 'CORPORATE_CARD',
		maxAmount: '',
		requireAttachment: true,
		action: 'REQUIRE_MEMO'
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!form.name.trim()) {
			setError('정책명을 입력해주세요.');
			return;
		}
		const request: CreateExpensePolicyRuleRequest = {
			name: form.name.trim(),
			department: form.department.trim() || undefined,
			category: form.category || undefined,
			paymentMethod: form.paymentMethod || undefined,
			maxAmount: form.maxAmount ? Number(form.maxAmount) : undefined,
			requireAttachment: form.requireAttachment,
			action: form.action
		};
		setSubmitting(true);
		try {
			await api.post<ExpensePolicyRuleResponse>('/expenses/management/policies', request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="정책 만들기"
			description="조직, 분류, 결제수단별 비용 검토 기준을 추가합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
				{error ? (
					<div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
						{error}
					</div>
				) : null}
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="expense-policy-name">정책명</Label>
						<Input
							id="expense-policy-name"
							value={form.name}
							onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
							placeholder="영업팀 식대 1.5만원"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-policy-department">조직</Label>
						<Input
							id="expense-policy-department"
							value={form.department}
							onChange={(event) =>
								setForm((current) => ({ ...current, department: event.target.value }))
							}
							placeholder="전체 또는 조직명"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-policy-category">분류</Label>
						<select
							id="expense-policy-category"
							className={dialogControlClassName}
							value={form.category}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									category: event.target.value as ExpenseCategory
								}))
							}
						>
							{expenseCategoryOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-policy-method">결제수단</Label>
						<select
							id="expense-policy-method"
							className={dialogControlClassName}
							value={form.paymentMethod}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									paymentMethod: event.target.value as ExpensePaymentMethod
								}))
							}
						>
							{expensePaymentMethodOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-policy-max">한도 금액</Label>
						<Input
							id="expense-policy-max"
							type="number"
							min="1"
							value={form.maxAmount}
							onChange={(event) =>
								setForm((current) => ({ ...current, maxAmount: event.target.value }))
							}
							placeholder="15000"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-policy-action">처리 방식</Label>
						<select
							id="expense-policy-action"
							className={dialogControlClassName}
							value={form.action}
							onChange={(event) =>
								setForm((current) => ({
									...current,
									action: event.target.value as ExpensePolicyAction
								}))
							}
						>
							<option value="WARN">주의 표시</option>
							<option value="REQUIRE_MEMO">사유 필수</option>
							<option value="BLOCK">신청 차단</option>
						</select>
					</div>
				</div>
				<label className="flex items-center gap-2 text-sm">
					<input
						type="checkbox"
						checked={form.requireAttachment}
						onChange={(event) =>
							setForm((current) => ({ ...current, requireAttachment: event.target.checked }))
						}
					/>
					증빙 첨부 필수
				</label>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						저장
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function ExpenseAccountingReviewDialog({
	expenses,
	onClose,
	onSubmitted
}: {
	expenses: ExpenseResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const candidates = expenses.filter(
		(expense) => expense.status === 'APPROVED' && expense.accountingStatus === 'READY'
	);
	const [form, setForm] = useState<ExpenseAccountingReviewForm>({
		expenseId: candidates[0]?.id ? String(candidates[0].id) : '',
		debitAccount: '복리후생비',
		creditAccount: '미지급금'
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!form.expenseId) {
			setError('검토할 경비를 선택해주세요.');
			return;
		}
		const request: ExpenseAccountingReviewRequest = {
			debitAccount: form.debitAccount.trim(),
			creditAccount: form.creditAccount.trim()
		};
		setSubmitting(true);
		try {
			await api.post<ExpenseResponse>(`/expenses/${form.expenseId}/accounting-review`, request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="검토 완료"
			description="승인된 비용에 차변/대변 계정을 지정합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
				{error ? (
					<div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
						{error}
					</div>
				) : null}
				<div className="space-y-2">
					<Label htmlFor="expense-review-id">경비</Label>
					<select
						id="expense-review-id"
						className={dialogControlClassName}
						value={form.expenseId}
						onChange={(event) =>
							setForm((current) => ({ ...current, expenseId: event.target.value }))
						}
					>
						<option value="">선택</option>
						{candidates.map((expense) => (
							<option key={expense.id} value={expense.id}>
								{expense.merchantName ?? expenseCategoryLabel(expense.category)} ·{' '}
								{wonLabel(expense.amount)}
							</option>
						))}
					</select>
				</div>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="expense-debit-account">차변 계정</Label>
						<Input
							id="expense-debit-account"
							value={form.debitAccount}
							onChange={(event) =>
								setForm((current) => ({ ...current, debitAccount: event.target.value }))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="expense-credit-account">대변 계정</Label>
						<Input
							id="expense-credit-account"
							value={form.creditAccount}
							onChange={(event) =>
								setForm((current) => ({ ...current, creditAccount: event.target.value }))
							}
						/>
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || candidates.length === 0}>
						저장
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function ExpenseExportDialog({
	reviewedCount,
	onClose,
	onSubmitted
}: {
	reviewedCount: number;
	onClose: () => void;
	onSubmitted: (response: ExpenseAccountingExportResponse) => Promise<void>;
}) {
	const [memo, setMemo] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const request: ExpenseAccountingExportRequest = { memo: memo.trim() || undefined };
		setSubmitting(true);
		try {
			const response = await api.post<ExpenseAccountingExportResponse>(
				'/expenses/management/accounting-export',
				request
			);
			await onSubmitted(response);
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="전표 내보내기"
			description="회계 검토가 완료된 비용을 전표 배치로 묶습니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
				{error ? (
					<div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
						{error}
					</div>
				) : null}
				<div className="rounded-lg border border-border bg-muted/20 p-4 text-sm">
					<p className="font-semibold">{numberLabel(reviewedCount)}건 내보내기 대기</p>
					<p className="mt-1 text-muted-foreground">
						검토 완료 상태의 비용만 전표 배치에 포함됩니다.
					</p>
				</div>
				<div className="space-y-2">
					<Label htmlFor="expense-export-memo">메모</Label>
					<Input
						id="expense-export-memo"
						value={memo}
						onChange={(event) => setMemo(event.target.value)}
						placeholder="4월 ERP 업로드"
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting || reviewedCount === 0}>
						내보내기
					</Button>
				</div>
			</form>
		</Modal>
	);
}

export function ExpensesPage() {
	const [state, setState] = useState<RequestState<ExpensesPageData>>({ status: 'idle' });
	const [query, setQuery] = useState('');
	const [requestDialogOpen, setRequestDialogOpen] = useState(false);
	const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
	const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
	const [exportDialogOpen, setExportDialogOpen] = useState(false);
	const [exportNotice, setExportNotice] = useState<string | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [summary, managementExpenses, policies, approvalLines, draftSuggestions] =
					await Promise.all([
						api
							.get<ExpenseManagementSummaryResponse>('/expenses/management/summary')
							.catch(() => null),
						api.get<ExpenseResponse[]>('/expenses/management').catch(() => null),
						api.get<ExpensePolicyRuleResponse[]>('/expenses/management/policies').catch(() => []),
						api.get<ApprovalLineResponse[]>('/approval-lines').catch(() => []),
						api.get<ExpenseDraftSuggestionResponse[]>('/expenses/draft-suggestions').catch(() => [])
					]);
				if (summary && managementExpenses) {
					setState({
						status: 'success',
						data: {
							summary,
							expenses: managementExpenses,
							policies,
							approvalLines,
							draftSuggestions,
							managementMode: true
						}
					});
					return;
				}
				const [myExpenses, pendingExpenses] = await Promise.all([
					api.get<ExpenseResponse[]>('/expenses'),
					api.get<ExpenseResponse[]>('/expenses/pending').catch(() => [])
				]);
				const expenses = buildExpenseRows([...myExpenses, ...pendingExpenses]);
				setState({
					status: 'success',
					data: {
						summary: buildLegacyExpenseSummary(myExpenses, pendingExpenses),
						expenses,
						policies: [],
						approvalLines,
						draftSuggestions,
						managementMode: false
					}
				});
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const rows = useMemo(() => (data ? buildExpenseRows(data.expenses) : []), [data]);
	const filteredRows = useMemo(() => {
		const keyword = query.trim().toLowerCase();
		if (!keyword) return rows;
		return rows.filter((expense) =>
			[
				expenseCategoryLabel(expense.category),
				expenseStatusLabel(expense.status),
				expenseAccountingStatusLabel(expense.accountingStatus),
				expenseEvidenceTypeLabel(expense.evidenceType),
				expensePaymentMethodLabel(expense.paymentMethod),
				expense.merchantName,
				expense.policyMessage,
				expense.description,
				expense.currency,
				`직원 #${expense.employeeId}`
			]
				.filter(Boolean)
				.some((value) => String(value).toLowerCase().includes(keyword))
		);
	}, [query, rows]);

	const reviewedCount =
		data?.summary.accountingReviewedCount ??
		rows.filter((expense) => expense.accountingStatus === 'REVIEWED').length;

	return (
		<div className="space-y-6">
			<PageHeader
				title="경비"
				description="증빙, 정책 검토, 승인 이후 전표 내보내기까지 비용 운영을 관리합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						<Button variant="outline" onClick={() => setRequestDialogOpen(true)}>
							<FileText className="size-4" />
							경비 신청
						</Button>
						<Button variant="outline" onClick={() => setPolicyDialogOpen(true)}>
							<Plus className="size-4" />
							정책 만들기
						</Button>
						<Button variant="outline" onClick={() => setReviewDialogOpen(true)}>
							<CheckCircle2 className="size-4" />
							검토 완료
						</Button>
						<Button onClick={() => setExportDialogOpen(true)}>
							<Download className="size-4" />
							전표 내보내기
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{exportNotice ? (
				<div className="rounded-lg bg-success/10 px-4 py-3 text-sm text-success-foreground">
					{exportNotice}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<ExpenseStatCard
							title="전체 비용"
							value={`${numberLabel(data.summary.totalExpenseCount)}건`}
							description={wonLabel(data.summary.totalAmount)}
						/>
						<ExpenseStatCard
							title="정책 위반"
							value={`${numberLabel(data.summary.policyViolationCount)}건`}
							description="한도·증빙·사유 확인 필요"
						/>
						<ExpenseStatCard
							title="회계 검토 대기"
							value={`${numberLabel(data.summary.accountingReadyCount)}건`}
							description="전표 계정 지정 필요"
						/>
						<ExpenseStatCard
							title="미배정 증빙"
							value={`${numberLabel(data.summary.unassignedReceiptCount)}건`}
							description={`내보냄 ${numberLabel(data.summary.accountingExportedCount)}건`}
						/>
					</div>

					<ExpenseCloseReadinessPanel summary={data.summary} />

					<div className="grid min-w-0 gap-6 xl:grid-cols-[1fr_24rem]">
						<Card className="min-w-0">
							<CardHeader className="gap-4 lg:flex-row lg:items-center lg:justify-between">
								<div>
									<CardTitle>비용 관리</CardTitle>
									<CardDescription>
										증빙, 정책 검토 결과, 승인 상태와 회계 처리 상태를 함께 확인합니다.
									</CardDescription>
								</div>
								<div className="relative w-full lg:w-80">
									<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										value={query}
										onChange={(event) => setQuery(event.target.value)}
										placeholder="가맹점, 정책, 상태 검색"
										className="pl-9"
										aria-label="경비 검색"
									/>
								</div>
							</CardHeader>
							<CardContent>
								<ExpenseTable expenses={filteredRows} />
							</CardContent>
						</Card>

						<div className="space-y-6">
							<Card>
								<CardHeader>
									<CardTitle>비용 정책</CardTitle>
									<CardDescription>조직, 분류, 결제수단별 자동 검토 기준입니다.</CardDescription>
								</CardHeader>
								<CardContent>
									<ExpensePolicyRuleList policies={data.policies} />
								</CardContent>
							</Card>
							<Card>
								<CardHeader>
									<CardTitle>회계 마감</CardTitle>
									<CardDescription>
										승인 후 계정 검토와 전표 내보내기 진행 상태입니다.
									</CardDescription>
								</CardHeader>
								<CardContent className="space-y-3 text-sm">
									<div className="flex items-center justify-between">
										<span className="text-muted-foreground">검토 완료</span>
										<Badge variant="outline">{numberLabel(reviewedCount)}건</Badge>
									</div>
									<div className="flex items-center justify-between">
										<span className="text-muted-foreground">승인 대기</span>
										<Badge variant="warning">
											{numberLabel(data.summary.pendingExpenseCount)}건
										</Badge>
									</div>
									<div className="flex items-center justify-between">
										<span className="text-muted-foreground">내보냄</span>
										<Badge variant="success">
											{numberLabel(data.summary.accountingExportedCount)}건
										</Badge>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>
				</>
			) : null}

			{requestDialogOpen && data ? (
				<ExpenseRequestDialog
					approvalLines={data.approvalLines}
					draftSuggestions={data.draftSuggestions}
					onClose={() => setRequestDialogOpen(false)}
					onSubmitted={load}
				/>
			) : null}
			{policyDialogOpen ? (
				<ExpensePolicyDialog onClose={() => setPolicyDialogOpen(false)} onSubmitted={load} />
			) : null}
			{reviewDialogOpen && data ? (
				<ExpenseAccountingReviewDialog
					expenses={data.expenses}
					onClose={() => setReviewDialogOpen(false)}
					onSubmitted={load}
				/>
			) : null}
			{exportDialogOpen ? (
				<ExpenseExportDialog
					reviewedCount={reviewedCount}
					onClose={() => setExportDialogOpen(false)}
					onSubmitted={async (response) => {
						setExportNotice(
							`${response.exportBatchId} 배치로 ${numberLabel(response.exportedCount)}건을 내보냈습니다.`
						);
						await load();
					}}
				/>
			) : null}
		</div>
	);
}

const dialogControlClassName =
	'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring/35 flex h-10 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const dialogTextAreaClassName =
	'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring/35 flex min-h-24 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

function Modal({
	title,
	description,
	children,
	onClose
}: {
	title: string;
	description?: string;
	children: ReactNode;
	onClose: () => void;
}) {
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [onClose]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
			<Card
				role="dialog"
				aria-modal="true"
				aria-label={title}
				className="max-h-[90svh] w-full max-w-2xl overflow-y-auto"
			>
				<CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
					<div className="space-y-1">
						<CardTitle>{title}</CardTitle>
						{description ? <CardDescription>{description}</CardDescription> : null}
					</div>
					<Button type="button" variant="ghost" size="sm" onClick={onClose} aria-label="닫기">
						닫기
					</Button>
				</CardHeader>
				<CardContent>{children}</CardContent>
			</Card>
		</div>
	);
}

type CertificatesPageData = {
	operations: CertificateOperationsSummaryResponse | null;
	selfServiceSummary: CertificateSelfServiceSummaryResponse;
	policies: CertificateIssuePolicyResponse[];
	requests: CertificateRequestResponse[];
	pending: CertificateRequestResponse[];
	issued: CertificateRequestResponse[];
	approvalLines: ApprovalLineResponse[];
};

type PayrollDocumentPeriodOption =
	CertificateSelfServiceSummaryResponse['availablePeriods'][number];

function certificateTypeLabel(type: CertificateRequestResponse['type'] | null | undefined): string {
	const labels: Record<CertificateType, string> = {
		EMPLOYMENT: '재직증명서',
		CAREER: '경력증명서',
		PAYSLIP: '급여명세서',
		WITHHOLDING_LEDGER: '원천징수부',
		WITHHOLDING_RECEIPT: '근로소득 원천징수영수증',
		EARNED_INCOME_TAX_CERTIFICATE: '갑근세 원천징수확인서'
	};
	return type ? labels[type] : '-';
}

function certificatePeriodLabel(
	certificate: Pick<CertificateRequestResponse, 'targetYear' | 'targetMonth'>
) {
	if (!certificate.targetYear) return '기간 미지정';
	if (certificate.targetMonth) return `${certificate.targetYear}년 ${certificate.targetMonth}월`;
	return `${certificate.targetYear}년`;
}

function certificateLanguageLabel(
	language: CertificateRequestResponse['language'] | null | undefined
) {
	return language === 'EN' ? '영어' : '한국어';
}

function certificateIssueOptionLabel(
	certificate: Pick<CertificateRequestResponse, 'language' | 'sealRequired'>
) {
	return `${certificateLanguageLabel(certificate.language)} · ${certificate.sealRequired ? '직인 포함' : '직인 제외'}`;
}

function payrollPeriodValue(period: Pick<PayrollDocumentPeriodOption, 'payYear' | 'payMonth'>) {
	return `${period.payYear}-${period.payMonth}`;
}

function availablePeriodSummaryLabel(periods: PayrollDocumentPeriodOption[]) {
	if (periods.length === 0) return '확정 급여 없음';
	if (periods.length === 1) return periods[0].label;
	return `${periods[0].label} 외 ${numberLabel(periods.length - 1)}개월`;
}

function isPayrollCertificateType(type: CertificateType) {
	return (
		type === 'PAYSLIP' ||
		type === 'WITHHOLDING_LEDGER' ||
		type === 'WITHHOLDING_RECEIPT' ||
		type === 'EARNED_INCOME_TAX_CERTIFICATE'
	);
}

function isMonthlyPayrollCertificateType(type: CertificateType) {
	return type === 'PAYSLIP';
}

function certificateStatusLabel(
	status: CertificateRequestResponse['status'] | null | undefined
): string {
	const labels: Record<CertificateRequestResponse['status'], string> = {
		PENDING: '발급 대기',
		ISSUED: '발급 완료',
		REJECTED: '반려',
		CANCELLED: '취소',
		REVOKED: '폐기'
	};
	return status ? labels[status] : '-';
}

function certificateStatusVariant(status: CertificateRequestResponse['status'] | null | undefined) {
	if (status === 'ISSUED') return 'success';
	if (status === 'PENDING') return 'warning';
	if (status === 'REJECTED' || status === 'REVOKED') return 'destructive';
	return 'outline';
}

function certificateOperationsRiskVariant(
	severity:
		| CertificateOperationsRiskSeverity
		| CertificateOperationsSummaryResponse['riskLevel']
		| null
		| undefined
) {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function certificateOperationsChecklistVariant(status: CertificateOperationsChecklistStatus) {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function certificateOperationsChecklistLabel(status: CertificateOperationsChecklistStatus) {
	const labels: Record<CertificateOperationsChecklistStatus, string> = {
		READY: '정상',
		WARNING: '주의',
		REVIEW: '점검'
	};
	return labels[status];
}

function mergeCertificates(
	...groups: CertificateRequestResponse[][]
): CertificateRequestResponse[] {
	const rows = new Map<number, CertificateRequestResponse>();
	for (const group of groups) {
		for (const request of group) rows.set(request.id, request);
	}
	return Array.from(rows.values()).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function CertificateRequestDialog({
	approvalLines,
	onClose,
	onSubmitted
}: {
	approvalLines: ApprovalLineResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [type, setType] = useState<CertificateRequestResponse['type']>('EMPLOYMENT');
	const [approvalLineId, setApprovalLineId] = useState(approvalLines[0]?.id ?? 0);
	const [purpose, setPurpose] = useState('');
	const [language, setLanguage] = useState<'KO' | 'EN'>('KO');
	const [sealRequired, setSealRequired] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateCertificateForm({
			type,
			approvalLineId,
			purpose,
			language,
			sealRequired
		});
		if (!result.ok) {
			setError(result.message);
			return;
		}
		setSubmitting(true);
		try {
			await api.post<CertificateRequestResponse>('/certificates', result.request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="증명서 신청"
			description="결재선을 선택해 재직·경력증명서를 신청합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="certificate-type">종류</Label>
						<select
							id="certificate-type"
							value={type}
							onChange={(event) =>
								setType(event.target.value as CertificateRequestResponse['type'])
							}
							className={dialogControlClassName}
						>
							<option value="EMPLOYMENT">재직증명서</option>
							<option value="CAREER">경력증명서</option>
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="certificate-line">결재선</Label>
						<select
							id="certificate-line"
							value={approvalLineId}
							onChange={(event) => setApprovalLineId(Number(event.target.value))}
							className={dialogControlClassName}
						>
							<option value={0}>결재선 선택</option>
							{approvalLines.map((line) => (
								<option key={line.id} value={line.id}>
									{line.name}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="certificate-purpose">용도</Label>
					<textarea
						id="certificate-purpose"
						value={purpose}
						onChange={(event) => setPurpose(event.target.value)}
						className={dialogTextAreaClassName}
						placeholder="제출처 또는 사용 목적"
					/>
				</div>
				<div className="grid gap-4 sm:grid-cols-[1fr_auto]">
					<div className="space-y-2">
						<Label htmlFor="certificate-language">언어</Label>
						<select
							id="certificate-language"
							value={language}
							onChange={(event) => setLanguage(event.target.value as 'KO' | 'EN')}
							className={dialogControlClassName}
						>
							<option value="KO">한국어</option>
							<option value="EN">영어</option>
						</select>
					</div>
					<label className="flex items-center gap-2 text-sm font-medium text-muted-foreground sm:min-h-10 sm:pt-8">
						<input
							type="checkbox"
							checked={sealRequired}
							onChange={(event) => setSealRequired(event.target.checked)}
						/>
						직인 포함
					</label>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						신청
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function CertificateSelfServicePanel({
	summary,
	onIssued
}: {
	summary: CertificateSelfServiceSummaryResponse;
	onIssued: () => Promise<void>;
}) {
	type CertificateLanguage = NonNullable<CreateSelfServiceCertificateRequest['language']>;
	const availablePeriods = useMemo(() => {
		if (summary.availablePeriods?.length) return summary.availablePeriods;
		if (summary.latestPayYear && summary.latestPayMonth) {
			return [
				{
					payYear: summary.latestPayYear,
					payMonth: summary.latestPayMonth,
					label: `${summary.latestPayYear}년 ${summary.latestPayMonth}월`
				}
			];
		}
		return [];
	}, [summary.availablePeriods, summary.latestPayMonth, summary.latestPayYear]);
	const availableYears = useMemo(() => {
		if (summary.availableYears?.length) return summary.availableYears;
		const years = Array.from(new Set(availablePeriods.map((period) => period.payYear)));
		if (years.length > 0) return years;
		return summary.latestPayYear ? [summary.latestPayYear] : [];
	}, [availablePeriods, summary.availableYears, summary.latestPayYear]);
	const availableCertificateTypes = summary.availableTypes;
	const defaultType = availableCertificateTypes[0] ?? 'EMPLOYMENT';
	const [type, setType] = useState<CertificateType>(defaultType);
	const [selectedPeriod, setSelectedPeriod] = useState(
		availablePeriods[0] ? payrollPeriodValue(availablePeriods[0]) : ''
	);
	const [targetYear, setTargetYear] = useState(String(availableYears[0] ?? ''));
	const [purpose, setPurpose] = useState('');
	const [language, setLanguage] = useState<CertificateLanguage>('KO');
	const [sealRequired, setSealRequired] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const activeType = availableCertificateTypes.includes(type) ? type : defaultType;
	const payrollType = isPayrollCertificateType(activeType);
	const monthRequired = isMonthlyPayrollCertificateType(activeType);
	const yearRequired = payrollType && !monthRequired;
	const defaultPeriodValue = availablePeriods[0] ? payrollPeriodValue(availablePeriods[0]) : '';
	const selectedPeriodExists = availablePeriods.some(
		(period) => payrollPeriodValue(period) === selectedPeriod
	);
	const activeSelectedPeriod = selectedPeriodExists ? selectedPeriod : defaultPeriodValue;
	const defaultYearValue = String(availableYears[0] ?? '');
	const parsedTargetYear = Number(targetYear);
	const activeTargetYear = availableYears.includes(parsedTargetYear)
		? targetYear
		: defaultYearValue;
	const canIssueActiveType =
		availableCertificateTypes.length > 0 &&
		(!monthRequired || availablePeriods.length > 0) &&
		(!yearRequired || availableYears.length > 0);

	function handleTypeChange(nextType: CertificateType) {
		setType(nextType);
		setError(null);
		if (isMonthlyPayrollCertificateType(nextType)) {
			setSelectedPeriod(availablePeriods[0] ? payrollPeriodValue(availablePeriods[0]) : '');
			return;
		}
		if (isPayrollCertificateType(nextType)) {
			setTargetYear(String(availableYears[0] ?? ''));
		}
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmedPurpose = purpose.trim();
		const request: CreateSelfServiceCertificateRequest = {
			type: activeType,
			purpose: trimmedPurpose || `${certificateTypeLabel(activeType)} 셀프 발급`,
			language,
			sealRequired
		};
		if (monthRequired) {
			const period = availablePeriods.find(
				(item) => payrollPeriodValue(item) === activeSelectedPeriod
			);
			if (!period) {
				setError('발급 가능한 급여월을 선택해주세요.');
				return;
			}
			request.targetYear = period.payYear;
			request.targetMonth = period.payMonth;
		} else if (yearRequired) {
			const parsedYear = Number(activeTargetYear);
			if (!Number.isInteger(parsedYear) || !availableYears.includes(parsedYear)) {
				setError('발급 가능한 연도를 선택해주세요.');
				return;
			}
			request.targetYear = parsedYear;
		}
		setSubmitting(true);
		setError(null);
		try {
			await api.post('/certificates/self-service', request);
			setPurpose('');
			await onIssued();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Card>
			<CardHeader className="gap-4 xl:flex-row xl:items-start xl:justify-between">
				<div className="space-y-1">
					<CardTitle>증명서 셀프 발급</CardTitle>
					<CardDescription>
						재직·경력증명서와 확정 급여 문서를 필요한 옵션으로 즉시 발급합니다.
					</CardDescription>
				</div>
				<Badge variant={canIssueActiveType ? 'success' : 'warning'}>
					{canIssueActiveType ? '셀프 발급 가능' : '발급 데이터 필요'}
				</Badge>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid grid-cols-3 gap-2 md:gap-3">
					<div className="rounded-md border border-border bg-muted/20 p-3 sm:p-4">
						<p className="text-[11px] font-semibold text-muted-foreground sm:text-xs">급여 원천</p>
						<p className="mt-1 text-sm font-semibold sm:mt-2 sm:text-lg">
							확정 급여 {numberLabel(summary.confirmedPayslipCount)}건
						</p>
					</div>
					<div className="rounded-md border border-border bg-muted/20 p-3 sm:p-4">
						<p className="text-[11px] font-semibold text-muted-foreground sm:text-xs">
							급여 발급 기간
						</p>
						<p className="mt-1 text-sm font-semibold sm:mt-2 sm:text-lg">
							{availablePeriodSummaryLabel(availablePeriods)}
						</p>
					</div>
					<div className="rounded-md border border-border bg-muted/20 p-3 sm:p-4">
						<p className="text-[11px] font-semibold text-muted-foreground sm:text-xs">
							발급 가능 문서
						</p>
						<p className="mt-1 text-sm font-semibold sm:mt-2 sm:text-lg">
							{numberLabel(summary.availableTypes.length)}종
						</p>
					</div>
				</div>

				<form
					className="grid gap-3 lg:grid-cols-[1.1fr_0.95fr_0.85fr_auto]"
					onSubmit={handleSubmit}
				>
					<div className="space-y-2">
						<Label htmlFor="self-service-certificate-type">문서</Label>
						<select
							id="self-service-certificate-type"
							value={activeType}
							onChange={(event) => handleTypeChange(event.target.value as CertificateType)}
							className={dialogControlClassName}
							disabled={availableCertificateTypes.length === 0}
						>
							{availableCertificateTypes.map((item) => (
								<option key={item} value={item}>
									{certificateTypeLabel(item)}
								</option>
							))}
						</select>
					</div>
					{monthRequired ? (
						<div className="space-y-2">
							<Label htmlFor="self-service-certificate-period">급여월</Label>
							<select
								id="self-service-certificate-period"
								value={activeSelectedPeriod}
								onChange={(event) => setSelectedPeriod(event.target.value)}
								className={dialogControlClassName}
								disabled={!canIssueActiveType}
							>
								{availablePeriods.map((period) => (
									<option key={payrollPeriodValue(period)} value={payrollPeriodValue(period)}>
										{period.label}
									</option>
								))}
							</select>
						</div>
					) : yearRequired ? (
						<div className="space-y-2">
							<Label htmlFor="self-service-certificate-year">대상 연도</Label>
							<select
								id="self-service-certificate-year"
								value={activeTargetYear}
								onChange={(event) => setTargetYear(event.target.value)}
								className={dialogControlClassName}
								disabled={!canIssueActiveType}
							>
								{availableYears.map((year) => (
									<option key={year} value={year}>
										{year}년
									</option>
								))}
							</select>
						</div>
					) : (
						<div className="space-y-2">
							<Label htmlFor="self-service-certificate-purpose">발급 사유</Label>
							<input
								id="self-service-certificate-purpose"
								value={purpose}
								onChange={(event) => setPurpose(event.target.value)}
								className={dialogControlClassName}
								placeholder="제출처 또는 사용 목적"
								disabled={!canIssueActiveType}
							/>
						</div>
					)}
					<div className="grid gap-3 sm:grid-cols-[1fr_auto] lg:grid-cols-1">
						<div className="space-y-2">
							<Label htmlFor="self-service-certificate-language">언어</Label>
							<select
								id="self-service-certificate-language"
								value={language}
								onChange={(event) => setLanguage(event.target.value as CertificateLanguage)}
								className={dialogControlClassName}
								disabled={!canIssueActiveType}
							>
								<option value="KO">한국어</option>
								<option value="EN">영어</option>
							</select>
						</div>
						<label className="flex items-center gap-2 text-sm font-medium text-muted-foreground lg:min-h-10 lg:pt-8">
							<input
								type="checkbox"
								checked={sealRequired}
								onChange={(event) => setSealRequired(event.target.checked)}
								disabled={!canIssueActiveType}
							/>
							직인 포함
						</label>
					</div>
					<div className="flex items-end">
						<Button
							type="submit"
							className="w-full lg:w-auto"
							disabled={!canIssueActiveType || submitting}
						>
							<Download className="size-4" />
							{certificateTypeLabel(activeType)} 셀프 발급
						</Button>
					</div>
				</form>
				<FormError message={error} />

				<div className="space-y-3">
					<div className="flex items-center gap-2 text-sm font-semibold">
						<FileText className="size-4 text-muted-foreground" />
						최근 셀프 발급
					</div>
					{summary.recentDocuments.length > 0 ? (
						<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
							{summary.recentDocuments.slice(0, 6).map((document) => (
								<div key={document.id} className="rounded-md border border-border p-4">
									<div className="flex items-start justify-between gap-3">
										<div className="min-w-0">
											<p className="truncate font-semibold">
												{certificateTypeLabel(document.type)}
											</p>
											<p className="mt-1 text-xs text-muted-foreground">
												{certificatePeriodLabel(document)} ·{' '}
												{document.issueNumber ?? '발급번호 없음'}
											</p>
											<p className="mt-1 text-xs text-muted-foreground">
												{certificateIssueOptionLabel(document)}
											</p>
										</div>
										<Badge variant={certificateStatusVariant(document.status)}>
											{certificateStatusLabel(document.status)}
										</Badge>
									</div>
								</div>
							))}
						</div>
					) : (
						<EmptyState message="최근 셀프 발급 문서가 없습니다." />
					)}
				</div>
			</CardContent>
		</Card>
	);
}

function CertificateOperationsPanel({
	summary
}: {
	summary: CertificateOperationsSummaryResponse;
}) {
	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div className="space-y-1">
					<CardTitle>증명서 운영 SLA</CardTitle>
					<CardDescription>{summary.riskSummary}</CardDescription>
				</div>
				<Badge variant={certificateOperationsRiskVariant(summary.riskLevel)}>
					{summary.riskLabel}
				</Badge>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
					<div className="rounded-md border border-border bg-muted/20 p-3">
						<p className="text-xs font-semibold text-muted-foreground">SLA 기준</p>
						<p className="mt-2 text-lg font-semibold">
							{numberLabel(summary.serviceLevelTargetHours)}시간
						</p>
					</div>
					<div className="rounded-md border border-border bg-muted/20 p-3">
						<p className="text-xs font-semibold text-muted-foreground">발급 대기</p>
						<p className="mt-2 text-lg font-semibold">{numberLabel(summary.pendingCount)}건</p>
					</div>
					<div className="rounded-md border border-border bg-muted/20 p-3">
						<p className="text-xs font-semibold text-muted-foreground">SLA 초과</p>
						<p className="mt-2 text-lg font-semibold text-destructive">
							{numberLabel(summary.overduePendingCount)}건
						</p>
					</div>
					<div className="rounded-md border border-border bg-muted/20 p-3">
						<p className="text-xs font-semibold text-muted-foreground">최장 대기</p>
						<p className="mt-2 text-lg font-semibold">
							{numberLabel(summary.oldestPendingHours)}시간
						</p>
					</div>
					<div className="rounded-md border border-border bg-muted/20 p-3">
						<p className="text-xs font-semibold text-muted-foreground">발급/폐기</p>
						<p className="mt-2 text-lg font-semibold">
							{numberLabel(summary.issuedCount)} / {numberLabel(summary.revokedCount)}
						</p>
					</div>
				</div>

				<div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]">
					<div className="rounded-md border border-border p-4">
						<div className="flex items-center justify-between gap-3">
							<div className="flex items-center gap-2 text-sm font-semibold">
								<AlertTriangle className="size-4 text-muted-foreground" />
								리스크
							</div>
							<p className="text-xs text-muted-foreground">{summary.recommendedAction}</p>
						</div>
						<div className="mt-4 space-y-3">
							{summary.riskItems.map((item) => (
								<div
									key={item.code}
									className="flex items-center justify-between gap-3 rounded-md bg-muted/25 px-3 py-2"
								>
									<div className="min-w-0">
										<p className="truncate text-sm font-semibold">{item.title}</p>
										<p className="mt-1 text-xs text-muted-foreground">{item.actionLabel}</p>
									</div>
									<div className="flex shrink-0 items-center gap-2">
										<span className="text-sm font-semibold">{numberLabel(item.count)}건</span>
										<Badge variant={certificateOperationsRiskVariant(item.severity)}>
											{item.severity}
										</Badge>
									</div>
								</div>
							))}
						</div>
					</div>

					<div className="rounded-md border border-border p-4">
						<div className="flex items-center gap-2 text-sm font-semibold">
							<Clock3 className="size-4 text-muted-foreground" />
							오래된 대기열
						</div>
						<div className="mt-4 space-y-3">
							{summary.pendingQueue.length > 0 ? (
								summary.pendingQueue.map((item) => (
									<div key={item.id} className="rounded-md bg-muted/25 px-3 py-2">
										<div className="flex items-start justify-between gap-3">
											<div className="min-w-0">
												<p className="truncate text-sm font-semibold">
													{item.employeeName ?? `직원 #${item.employeeId}`}
												</p>
												<p className="mt-1 text-xs text-muted-foreground">
													{item.typeLabel} · {item.purpose ?? '용도 미입력'}
												</p>
											</div>
											<Badge variant={item.overdue ? 'destructive' : 'outline'}>
												{numberLabel(item.pendingHours)}시간 대기
											</Badge>
										</div>
									</div>
								))
							) : (
								<EmptyState message="대기 중인 증명서가 없습니다." />
							)}
						</div>
					</div>
				</div>

				<div className="grid gap-3 lg:grid-cols-[1fr_1fr]">
					<div className="rounded-md border border-border p-4">
						<p className="text-sm font-semibold">운영 체크</p>
						<div className="mt-3 grid gap-2 md:grid-cols-3">
							{summary.checklist.map((item) => (
								<div key={item.code} className="rounded-md bg-muted/25 p-3">
									<div className="flex items-start justify-between gap-2">
										<p className="text-sm font-semibold">{item.label}</p>
										<Badge variant={certificateOperationsChecklistVariant(item.status)}>
											{certificateOperationsChecklistLabel(item.status)}
										</Badge>
									</div>
									<p className="mt-2 text-xs text-muted-foreground">{item.description}</p>
								</div>
							))}
						</div>
					</div>
					<div className="rounded-md border border-border p-4">
						<p className="text-sm font-semibold">유형별 대기</p>
						<div className="mt-3 flex flex-wrap gap-2">
							{summary.pendingByType.length > 0 ? (
								summary.pendingByType.map((item) => (
									<Badge key={item.type} variant="outline" className="px-3 py-1.5">
										{item.label} {numberLabel(item.count)}건
									</Badge>
								))
							) : (
								<span className="text-sm text-muted-foreground">대기 유형 없음</span>
							)}
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function CertificateIssuePolicyPanel({
	policies,
	onSaved
}: {
	policies: CertificateIssuePolicyResponse[];
	onSaved: (policies: CertificateIssuePolicyResponse[]) => void;
}) {
	const [drafts, setDrafts] = useState<CertificateIssuePolicyResponse[]>(policies);
	const [message, setMessage] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	function togglePolicy(type: CertificateType, checked: boolean) {
		setDrafts((current) =>
			current.map((policy) =>
				policy.type === type
					? { ...policy, selfServiceEnabled: checked, approvalRequired: !checked }
					: policy
			)
		);
		setMessage(null);
	}

	async function savePolicies() {
		setSaving(true);
		setMessage(null);
		try {
			const request: UpdateCertificateIssuePoliciesRequest = {
				policies: drafts.map((policy) => ({
					type: policy.type,
					selfServiceEnabled: policy.selfServiceEnabled
				}))
			};
			const saved = await api.put<CertificateIssuePolicyResponse[]>(
				'/certificates/policies',
				request
			);
			setDrafts(saved);
			onSaved(saved);
			setMessage('증명서 발급 정책을 저장했습니다.');
		} catch (error) {
			setMessage(getErrorMessage(error));
		} finally {
			setSaving(false);
		}
	}

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div className="space-y-1">
					<CardTitle>증명서 발급 정책</CardTitle>
					<CardDescription>
						재직·경력증명서를 즉시 셀프 발급할지, 결재 신청으로 받을지 설정합니다.
					</CardDescription>
				</div>
				<Button type="button" onClick={() => void savePolicies()} disabled={saving}>
					{saving ? <Spinner size="sm" /> : <Save className="size-4" />}
					정책 저장
				</Button>
			</CardHeader>
			<CardContent className="space-y-4">
				{message ? (
					<div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
						{message}
					</div>
				) : null}
				<div className="grid gap-3 md:grid-cols-2">
					{drafts.map((policy) => (
						<div key={policy.type} className="rounded-md border border-border bg-muted/20 p-4">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="font-semibold">{policy.label}</p>
									<p className="mt-1 text-xs text-muted-foreground">
										{policy.selfServiceEnabled
											? '구성원이 필요한 옵션으로 즉시 발급합니다.'
											: '구성원이 신청하면 결재선 검토 후 발급합니다.'}
									</p>
								</div>
								<Badge variant={policy.selfServiceEnabled ? 'success' : 'warning'}>
									{policy.selfServiceEnabled ? '즉시 발급' : '결재 신청 필요'}
								</Badge>
							</div>
							<label className="mt-4 flex items-center gap-2 text-sm font-medium">
								<input
									type="checkbox"
									checked={policy.selfServiceEnabled}
									onChange={(event) => togglePolicy(policy.type, event.target.checked)}
									aria-label={`${policy.label} 즉시 셀프 발급`}
								/>
								즉시 셀프 발급 허용
							</label>
						</div>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

function CertificateRevokeDialog({
	certificate,
	onClose,
	onSubmitted
}: {
	certificate: CertificateRequestResponse;
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [reason, setReason] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmed = reason.trim();
		if (!trimmed) {
			setError('폐기 사유를 입력해주세요.');
			return;
		}
		setSubmitting(true);
		try {
			await api.post<CertificateRequestResponse>(`/certificates/${certificate.id}/revoke`, {
				reason: trimmed
			});
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="증명서 폐기"
			description={`${certificate.issueNumber ?? certificate.id} 증명서를 폐기합니다.`}
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="certificate-revoke-reason">폐기 사유</Label>
					<textarea
						id="certificate-revoke-reason"
						value={reason}
						onChange={(event) => setReason(event.target.value)}
						className={dialogTextAreaClassName}
						placeholder="폐기 사유"
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" variant="destructive" disabled={submitting}>
						폐기
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function CertificateMobileCard({
	certificate,
	onCancel,
	onRevoke
}: {
	certificate: CertificateRequestResponse;
	onCancel: (certificate: CertificateRequestResponse) => void;
	onRevoke: (certificate: CertificateRequestResponse) => void;
}) {
	return (
		<div className="rounded-lg border border-border bg-card p-4 shadow-sm md:hidden">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="font-semibold">{certificateTypeLabel(certificate.type)}</p>
					<p className="mt-1 text-sm text-muted-foreground">
						{certificate.employeeName ?? `직원 #${certificate.employeeId}`} ·{' '}
						{certificate.purpose ?? '용도 미입력'}
					</p>
				</div>
				<Badge variant={certificateStatusVariant(certificate.status)}>
					{certificateStatusLabel(certificate.status)}
				</Badge>
			</div>
			<p className="mt-3 text-sm text-muted-foreground">
				{certificate.issueNumber ?? '발급번호 대기'} ·{' '}
				{dateLabel(certificate.issuedAt ?? certificate.createdAt)}
			</p>
			<p className="mt-1 text-sm text-muted-foreground">
				{certificateIssueOptionLabel(certificate)}
			</p>
			<div className="mt-3 flex flex-wrap gap-2">
				{certificate.status === 'PENDING' ? (
					<Button type="button" variant="outline" size="sm" onClick={() => onCancel(certificate)}>
						취소
					</Button>
				) : null}
				{certificate.status === 'ISSUED' ? (
					<Button type="button" variant="outline" size="sm" onClick={() => onRevoke(certificate)}>
						폐기
					</Button>
				) : null}
			</div>
		</div>
	);
}

function CertificateTable({
	certificates,
	emptyMessage,
	onCancel,
	onRevoke
}: {
	certificates: CertificateRequestResponse[];
	emptyMessage: string;
	onCancel: (certificate: CertificateRequestResponse) => void;
	onRevoke: (certificate: CertificateRequestResponse) => void;
}) {
	if (certificates.length === 0) return <EmptyState message={emptyMessage} />;

	return (
		<>
			<div className="space-y-3 md:hidden">
				{certificates.map((certificate) => (
					<CertificateMobileCard
						key={certificate.id}
						certificate={certificate}
						onCancel={onCancel}
						onRevoke={onRevoke}
					/>
				))}
			</div>
			<div className="hidden overflow-x-auto rounded-lg border border-border md:block">
				<table className="w-full min-w-[900px] text-left text-sm">
					<thead className="bg-muted/40 text-xs text-muted-foreground">
						<tr>
							<th className="px-4 py-3 font-semibold">종류</th>
							<th className="px-4 py-3 font-semibold">직원</th>
							<th className="px-4 py-3 font-semibold">용도</th>
							<th className="px-4 py-3 font-semibold">발급번호</th>
							<th className="px-4 py-3 font-semibold">상태</th>
							<th className="px-4 py-3 font-semibold">일자</th>
							<th className="px-4 py-3 text-right font-semibold">작업</th>
						</tr>
					</thead>
					<tbody>
						{certificates.map((certificate) => (
							<tr key={certificate.id} className="border-t border-border">
								<td className="px-4 py-4 font-semibold">
									{certificateTypeLabel(certificate.type)}
								</td>
								<td className="px-4 py-4">
									<p>{certificate.employeeName ?? `직원 #${certificate.employeeId}`}</p>
									<p className="mt-1 text-xs text-muted-foreground">
										{certificate.employeeDepartment ?? '-'} · {certificate.employeePosition ?? '-'}
									</p>
								</td>
								<td className="max-w-[14rem] px-4 py-4 text-muted-foreground">
									<p className="truncate">{certificate.purpose ?? '-'}</p>
									<p className="mt-1 truncate text-xs">
										{certificateIssueOptionLabel(certificate)}
									</p>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{certificate.issueNumber ?? '-'}
								</td>
								<td className="px-4 py-4">
									<Badge variant={certificateStatusVariant(certificate.status)}>
										{certificateStatusLabel(certificate.status)}
									</Badge>
								</td>
								<td className="px-4 py-4 text-muted-foreground">
									{dateLabel(certificate.issuedAt ?? certificate.createdAt)}
								</td>
								<td className="px-4 py-4 text-right">
									<div className="inline-flex gap-2">
										{certificate.issueNumber ? (
											<Link
												href={`/certificates/verify?issueNumber=${encodeURIComponent(certificate.issueNumber)}`}
												className="inline-flex h-8 items-center justify-center rounded-md px-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
											>
												진위 확인
											</Link>
										) : null}
										{certificate.status === 'PENDING' ? (
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={() => onCancel(certificate)}
											>
												취소
											</Button>
										) : null}
										{certificate.status === 'ISSUED' ? (
											<Button
												type="button"
												variant="outline"
												size="sm"
												onClick={() => onRevoke(certificate)}
											>
												폐기
											</Button>
										) : null}
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}

export function CertificatesPage() {
	const confirm = useConfirm();
	const [state, setState] = useState<RequestState<CertificatesPageData>>({ status: 'idle' });
	const [requestDialogOpen, setRequestDialogOpen] = useState(false);
	const [revokeTarget, setRevokeTarget] = useState<CertificateRequestResponse | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [operations, selfServiceSummary, policies, requests, pending, issued, approvalLines] =
					await Promise.all([
						api
							.get<CertificateOperationsSummaryResponse>('/certificates/operations/summary')
							.catch(() => null),
						api.get<CertificateSelfServiceSummaryResponse>('/certificates/self-service/summary'),
						api.get<CertificateIssuePolicyResponse[]>('/certificates/policies').catch(() => []),
						api.get<CertificateRequestResponse[]>('/certificates'),
						api.get<CertificateRequestResponse[]>('/certificates/pending').catch(() => []),
						api.get<CertificateRequestResponse[]>('/certificates/issued').catch(() => []),
						api.get<ApprovalLineResponse[]>('/approval-lines').catch(() => [])
					]);
				setState({
					status: 'success',
					data: {
						operations,
						selfServiceSummary,
						policies,
						requests,
						pending,
						issued,
						approvalLines
					}
				});
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function handleCancel(certificate: CertificateRequestResponse) {
		const ok = await confirm({
			title: '증명서 신청 취소',
			description: `${certificateTypeLabel(certificate.type)} 신청을 취소합니다.`,
			confirmLabel: '신청 취소',
			destructive: true
		});
		if (!ok) return;
		try {
			await api.delete(`/certificates/${certificate.id}`);
			await load();
		} catch (error) {
			setState({ status: 'error', message: getErrorMessage(error) });
		}
	}

	function handlePoliciesSaved(policies: CertificateIssuePolicyResponse[]) {
		setState((current) => {
			if (current.status !== 'success') return current;
			const payrollTypes = current.data.selfServiceSummary.availableTypes.filter(
				(type) => type !== 'EMPLOYMENT' && type !== 'CAREER'
			);
			const availableTypes = [
				...policies.filter((policy) => policy.selfServiceEnabled).map((policy) => policy.type),
				...payrollTypes
			];
			return {
				status: 'success',
				data: {
					...current.data,
					policies,
					selfServiceSummary: {
						...current.data.selfServiceSummary,
						policies,
						availableTypes
					}
				}
			};
		});
	}

	const data = state.status === 'success' ? state.data : null;
	const allRows = data ? mergeCertificates(data.requests, data.pending, data.issued) : [];
	const pendingRows = data
		? mergeCertificates(
				data.pending,
				data.requests.filter((item) => item.status === 'PENDING')
			)
		: [];
	const issuedRows = data
		? mergeCertificates(
				data.issued,
				data.requests.filter((item) => item.status !== 'PENDING')
			)
		: [];

	return (
		<div className="space-y-6">
			<PageHeader
				title="증명서"
				description="재직·경력증명서 신청, 발급 대기, 폐기 상태를 한 화면에서 확인합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button type="button" onClick={() => setRequestDialogOpen(true)}>
							<Plus className="size-4" />
							증명서 신청
						</Button>
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					{data.operations ? <CertificateOperationsPanel summary={data.operations} /> : null}

					{data.policies.length > 0 ? (
						<CertificateIssuePolicyPanel policies={data.policies} onSaved={handlePoliciesSaved} />
					) : null}

					<CertificateSelfServicePanel summary={data.selfServiceSummary} onIssued={load} />

					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<ExpenseStatCard
							title="전체 신청"
							value={`${numberLabel(allRows.length)}건`}
							description="신청과 발급 내역"
						/>
						<ExpenseStatCard
							title="발급 대기"
							value={`${numberLabel(pendingRows.length)}건`}
							description="결재 또는 발급 대기"
						/>
						<ExpenseStatCard
							title="발급 완료"
							value={`${numberLabel(issuedRows.filter((row) => row.status === 'ISSUED').length)}건`}
							description="진위 확인 가능"
						/>
						<ExpenseStatCard
							title="폐기/반려"
							value={`${numberLabel(allRows.filter((row) => row.status === 'REVOKED' || row.status === 'REJECTED').length)}건`}
							description="후속 확인 필요"
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>발급 대기 신청</CardTitle>
							<CardDescription>결재 또는 발급 처리가 필요한 증명서 신청입니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<CertificateTable
								certificates={pendingRows}
								emptyMessage="증명서 신청 내역이 없어요"
								onCancel={(certificate) => void handleCancel(certificate)}
								onRevoke={setRevokeTarget}
							/>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>발급/폐기 증명서</CardTitle>
							<CardDescription>발급번호가 있는 문서와 폐기·반려 상태를 추적합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<CertificateTable
								certificates={issuedRows}
								emptyMessage="발급된 증명서가 없습니다."
								onCancel={(certificate) => void handleCancel(certificate)}
								onRevoke={setRevokeTarget}
							/>
						</CardContent>
					</Card>
				</>
			) : null}

			{requestDialogOpen && data ? (
				<CertificateRequestDialog
					approvalLines={data.approvalLines}
					onClose={() => setRequestDialogOpen(false)}
					onSubmitted={load}
				/>
			) : null}
			{revokeTarget ? (
				<CertificateRevokeDialog
					certificate={revokeTarget}
					onClose={() => setRevokeTarget(null)}
					onSubmitted={load}
				/>
			) : null}
		</div>
	);
}

function HolidayDialog({
	holidays,
	onClose,
	onSubmitted
}: {
	holidays: HolidayResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [name, setName] = useState('');
	const [date, setDate] = useState('');
	const [recurring, setRecurring] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateHolidayForm({ name, date, recurring }, holidays);
		if (!result.ok) {
			setError(result.message);
			return;
		}
		setSubmitting(true);
		try {
			await api.post<HolidayResponse>('/holidays', result.request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="공휴일 추가"
			description="회사 휴무일과 매년 반복 여부를 등록합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="holiday-name">이름</Label>
						<Input
							id="holiday-name"
							value={name}
							onChange={(event) => setName(event.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="holiday-date">날짜</Label>
						<Input
							id="holiday-date"
							type="date"
							value={date}
							onChange={(event) => setDate(event.target.value)}
						/>
					</div>
				</div>
				<label className="flex items-center gap-2 text-sm">
					<input
						type="checkbox"
						checked={recurring}
						onChange={(event) => setRecurring(event.target.checked)}
						className="size-4 rounded border-border"
					/>
					매년 반복
				</label>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						추가
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function HolidayTable({
	holidays,
	onDelete
}: {
	holidays: HolidayResponse[];
	onDelete: (holiday: HolidayResponse) => void;
}) {
	if (holidays.length === 0) return <EmptyState message="등록된 공휴일이 없습니다." />;
	const sorted = [...holidays].sort((a, b) => a.date.localeCompare(b.date));

	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[640px] text-left text-sm">
				<thead className="bg-muted/40 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-semibold">이름</th>
						<th className="px-4 py-3 font-semibold">날짜</th>
						<th className="px-4 py-3 font-semibold">반복</th>
						<th className="px-4 py-3 text-right font-semibold">작업</th>
					</tr>
				</thead>
				<tbody>
					{sorted.map((holiday) => (
						<tr key={holiday.id} className="border-t border-border">
							<td className="px-4 py-4 font-semibold">{holiday.name}</td>
							<td className="px-4 py-4 text-muted-foreground">{dateLabel(holiday.date)}</td>
							<td className="px-4 py-4">
								<Badge variant={holiday.recurring ? 'success' : 'outline'}>
									{holiday.recurring ? '매년 반복' : '해당 연도만'}
								</Badge>
							</td>
							<td className="px-4 py-4 text-right">
								<Button type="button" variant="ghost" size="sm" onClick={() => onDelete(holiday)}>
									삭제
								</Button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function HolidaysPage() {
	const confirm = useConfirm();
	const year = new Date().getFullYear();
	const [state, setState] = useState<RequestState<HolidayResponse[]>>({ status: 'idle' });
	const [dialogOpen, setDialogOpen] = useState(false);
	const [initializingDefaults, setInitializingDefaults] = useState(false);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const holidays = await api.get<HolidayResponse[]>(`/holidays?year=${year}`);
				setState({ status: 'success', data: holidays });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[year]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function handleDelete(holiday: HolidayResponse) {
		const ok = await confirm({
			title: '공휴일 삭제',
			description: `${holiday.name} 공휴일을 삭제합니다. 기본 공휴일인 경우 다시 불러오지 않습니다.`,
			confirmLabel: '삭제',
			destructive: true
		});
		if (!ok) return;
		try {
			await api.delete(`/holidays/${holiday.id}`);
			await load();
		} catch (error) {
			setState({ status: 'error', message: getErrorMessage(error) });
		}
	}

	async function handleInitDefaults() {
		setInitializingDefaults(true);
		try {
			await api.post(`/holidays/init-defaults?year=${year}`);
			await load();
		} catch (error) {
			setState({ status: 'error', message: getErrorMessage(error) });
		} finally {
			setInitializingDefaults(false);
		}
	}

	const holidays = state.status === 'success' ? state.data : [];
	const recurringCount = holidays.filter((holiday) => holiday.recurring).length;
	const nextHoliday = [...holidays].sort((a, b) => a.date.localeCompare(b.date))[0];

	return (
		<div className="space-y-6">
			<PageHeader
				title="공휴일"
				description="회사 휴무일과 반복 공휴일을 관리합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button type="button" onClick={() => setDialogOpen(true)}>
							<Plus className="size-4" />
							공휴일 추가
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => void handleInitDefaults()}
							disabled={initializingDefaults}
						>
							<CalendarCheck className="size-4" />
							{initializingDefaults ? '불러오는 중...' : '기본 공휴일 불러오기'}
						</Button>
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			<div className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
				<p className="font-semibold text-foreground">기본 공휴일 초기화 보존 정책</p>
				<p className="mt-1">삭제한 기본 공휴일은 다시 불러오지 않습니다.</p>
			</div>

			{state.status === 'success' ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<ExpenseStatCard
							title="연간 휴무일"
							value={`${numberLabel(holidays.length)}일`}
							description="회사 등록 기준"
						/>
						<ExpenseStatCard
							title="매년 반복"
							value={`${numberLabel(recurringCount)}일`}
							description="매년 같은 날짜 적용"
						/>
						<ExpenseStatCard
							title="단일 휴무"
							value={`${numberLabel(holidays.length - recurringCount)}일`}
							description="특정 연도에만 적용"
						/>
						<ExpenseStatCard
							title="다음 휴무"
							value={nextHoliday ? dateLabel(nextHoliday.date) : '-'}
							description={nextHoliday?.name ?? '등록 필요'}
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>연간 휴무일</CardTitle>
							<CardDescription>공휴일 이름, 날짜, 반복 여부를 확인합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<HolidayTable
								holidays={holidays}
								onDelete={(holiday) => void handleDelete(holiday)}
							/>
						</CardContent>
					</Card>
				</>
			) : null}

			{dialogOpen ? (
				<HolidayDialog
					holidays={holidays}
					onClose={() => setDialogOpen(false)}
					onSubmitted={load}
				/>
			) : null}
		</div>
	);
}

const defaultWorkPolicyForm = (): WorkPolicyFormValues => ({
	name: '',
	type: 'FIXED',
	workStartTime: '09:00',
	workEndTime: '18:00',
	lateGraceMinutes: 0,
	checkInWindowStart: '08:00',
	checkInWindowEnd: '10:00',
	outOfWindowPolicy: 'FORCE_LATE',
	dailyWorkMinutes: 480,
	totalBreakMinutes: 60,
	autoCheckOutAfterHours: 20,
	attendanceAdjustmentRequestDeadlineDays: 7,
	unapprovedOvertimeRecognized: false,
	overtimeMinimumRequestMinutes: 30,
	overtimeCompensationMultiplier: 1.5,
	nightCompensationMultiplier: 1.5,
	holidayCompensationMultiplier: 2,
	shifts: [],
	breaks: []
});

function workPolicyTypeLabel(type: WorkPolicyResponse['type'] | null | undefined): string {
	const labels: Record<WorkPolicyResponse['type'], string> = {
		FIXED: '고정 근무',
		FLEXIBLE: '유연 근무',
		STAGGERED: '시차 근무'
	};
	return type ? labels[type] : '-';
}

function workDaysLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const labels: Record<string, string> = {
		MON: '월',
		TUE: '화',
		WED: '수',
		THU: '목',
		FRI: '금',
		SAT: '토',
		SUN: '일'
	};
	return value
		.split(',')
		.map((day) => labels[day] ?? day)
		.join(', ');
}

function workPolicyTimeLabel(policy: WorkPolicyResponse): string {
	if (policy.type === 'FIXED')
		return `${timeLabel(policy.workStartTime)} - ${timeLabel(policy.workEndTime)}`;
	if (policy.type === 'FLEXIBLE')
		return `${timeLabel(policy.checkInWindowStart)} - ${timeLabel(policy.checkInWindowEnd)}`;
	if (policy.shifts.length > 0) return `${policy.shifts.length}개 시프트`;
	return '-';
}

function WorkPolicyDialog({
	policy,
	onClose,
	onSubmitted
}: {
	policy?: WorkPolicyResponse;
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [form, setForm] = useState<WorkPolicyFormValues>(() =>
		policy ? formFromPolicy(policy) : defaultWorkPolicyForm()
	);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	function updateForm<T extends keyof WorkPolicyFormValues>(
		key: T,
		value: WorkPolicyFormValues[T]
	) {
		setForm((current) => ({ ...current, [key]: value }));
	}

	function addShiftRow() {
		updateForm('shifts', [
			...form.shifts,
			{
				label: '',
				startTime: '09:00',
				endTime: '18:00',
				lateGraceMinutes: 0,
				displayOrder: form.shifts.length
			}
		]);
	}

	function updateShiftRow(index: number, patch: Partial<WorkPolicyShift>) {
		updateForm(
			'shifts',
			form.shifts.map((shift, i) => (i === index ? { ...shift, ...patch } : shift))
		);
	}

	function removeShiftRow(index: number) {
		updateForm(
			'shifts',
			form.shifts.filter((_, i) => i !== index)
		);
	}

	function addBreakRow() {
		updateForm('breaks', [
			...form.breaks,
			{ startTime: '12:00', endTime: '13:00', paid: false, displayOrder: form.breaks.length }
		]);
	}

	function updateBreakRow(index: number, patch: Partial<WorkPolicyBreak>) {
		updateForm(
			'breaks',
			form.breaks.map((brk, i) => (i === index ? { ...brk, ...patch } : brk))
		);
	}

	function removeBreakRow(index: number) {
		updateForm(
			'breaks',
			form.breaks.filter((_, i) => i !== index)
		);
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateWorkPolicyForm(form);
		if (!result.ok) {
			setError(result.message);
			return;
		}
		setSubmitting(true);
		try {
			if (policy) {
				await api.patch<WorkPolicyResponse>(`/work-policies/${policy.id}`, result.request);
			} else {
				await api.post<WorkPolicyResponse>('/work-policies', result.request);
			}
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title={policy ? '근태 정책 수정' : '근태 정책 추가'}
			description="근무 유형과 출퇴근 기준을 설정합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="work-policy-name">이름</Label>
						<Input
							id="work-policy-name"
							value={form.name}
							onChange={(event) => updateForm('name', event.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="work-policy-type">유형</Label>
						<select
							id="work-policy-type"
							value={form.type}
							disabled={Boolean(policy)}
							onChange={(event) =>
								updateForm('type', event.target.value as WorkPolicyFormValues['type'])
							}
							className={dialogControlClassName}
						>
							<option value="FIXED">고정 근무</option>
							<option value="FLEXIBLE">유연 근무</option>
							<option value="STAGGERED">시차 근무</option>
						</select>
					</div>
				</div>

				{form.type === 'FIXED' ? (
					<div className="grid gap-4 sm:grid-cols-3">
						<div className="space-y-2">
							<Label htmlFor="work-policy-start">출근</Label>
							<Input
								id="work-policy-start"
								type="time"
								value={form.workStartTime}
								onChange={(event) => updateForm('workStartTime', event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="work-policy-end">퇴근</Label>
							<Input
								id="work-policy-end"
								type="time"
								value={form.workEndTime}
								onChange={(event) => updateForm('workEndTime', event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="work-policy-late-grace">지각 허용(분)</Label>
							<Input
								id="work-policy-late-grace"
								type="number"
								value={form.lateGraceMinutes}
								onChange={(event) => updateForm('lateGraceMinutes', Number(event.target.value))}
							/>
						</div>
					</div>
				) : null}

				{form.type === 'FLEXIBLE' ? (
					<div className="grid gap-4 sm:grid-cols-3">
						<div className="space-y-2">
							<Label htmlFor="work-policy-window-start">출근허용 시작</Label>
							<Input
								id="work-policy-window-start"
								type="time"
								value={form.checkInWindowStart}
								onChange={(event) => updateForm('checkInWindowStart', event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="work-policy-window-end">출근허용 종료</Label>
							<Input
								id="work-policy-window-end"
								type="time"
								value={form.checkInWindowEnd}
								onChange={(event) => updateForm('checkInWindowEnd', event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="work-policy-out-of-window">창밖 정책</Label>
							<select
								id="work-policy-out-of-window"
								value={form.outOfWindowPolicy}
								onChange={(event) =>
									updateForm(
										'outOfWindowPolicy',
										event.target.value as WorkPolicyFormValues['outOfWindowPolicy']
									)
								}
								className={dialogControlClassName}
							>
								<option value="FORCE_LATE">지각 처리</option>
								<option value="REJECT">출근 거부</option>
							</select>
						</div>
					</div>
				) : null}

				{form.type === 'STAGGERED' ? (
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<p className="text-xs font-medium text-muted-foreground">시프트</p>
							<Button type="button" variant="outline" size="sm" onClick={addShiftRow}>
								<Plus className="size-4" />
								시프트 추가
							</Button>
						</div>
						{form.shifts.length === 0 ? (
							<EmptyState message="시프트를 최소 1개 이상 추가해주세요." />
						) : (
							<div className="space-y-3">
								{form.shifts.map((shift, index) => (
									<div
										key={index}
										className="grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-5 sm:items-end"
									>
										<div className="space-y-1 sm:col-span-2">
											<Label htmlFor={`work-policy-shift-label-${index}`}>라벨</Label>
											<Input
												id={`work-policy-shift-label-${index}`}
												value={shift.label}
												onChange={(event) => updateShiftRow(index, { label: event.target.value })}
											/>
										</div>
										<div className="space-y-1">
											<Label htmlFor={`work-policy-shift-start-${index}`}>시작</Label>
											<Input
												id={`work-policy-shift-start-${index}`}
												type="time"
												value={shift.startTime}
												onChange={(event) =>
													updateShiftRow(index, { startTime: event.target.value })
												}
											/>
										</div>
										<div className="space-y-1">
											<Label htmlFor={`work-policy-shift-end-${index}`}>종료</Label>
											<Input
												id={`work-policy-shift-end-${index}`}
												type="time"
												value={shift.endTime}
												onChange={(event) => updateShiftRow(index, { endTime: event.target.value })}
											/>
										</div>
										<div className="flex items-end gap-2">
											<div className="flex-1 space-y-1">
												<Label htmlFor={`work-policy-shift-grace-${index}`}>지각유예(분)</Label>
												<Input
													id={`work-policy-shift-grace-${index}`}
													type="number"
													value={shift.lateGraceMinutes}
													onChange={(event) =>
														updateShiftRow(index, { lateGraceMinutes: Number(event.target.value) })
													}
												/>
											</div>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												aria-label={`${index + 1}번째 시프트 삭제`}
												onClick={() => removeShiftRow(index)}
											>
												삭제
											</Button>
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				) : null}

				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="work-policy-daily-minutes">일일 근무 분</Label>
						<Input
							id="work-policy-daily-minutes"
							type="number"
							value={form.dailyWorkMinutes}
							onChange={(event) => updateForm('dailyWorkMinutes', Number(event.target.value))}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="work-policy-break-minutes">휴게 분</Label>
						<Input
							id="work-policy-break-minutes"
							type="number"
							value={form.totalBreakMinutes}
							onChange={(event) => updateForm('totalBreakMinutes', Number(event.target.value))}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="work-policy-auto-checkout">자동퇴근(시간)</Label>
						<Input
							id="work-policy-auto-checkout"
							type="number"
							value={form.autoCheckOutAfterHours}
							onChange={(event) => updateForm('autoCheckOutAfterHours', Number(event.target.value))}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="work-policy-adjustment-deadline">정정 신청기한(일)</Label>
						<Input
							id="work-policy-adjustment-deadline"
							type="number"
							value={form.attendanceAdjustmentRequestDeadlineDays}
							onChange={(event) =>
								updateForm('attendanceAdjustmentRequestDeadlineDays', Number(event.target.value))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="work-policy-overtime-minimum">연장 최소 신청(분)</Label>
						<Input
							id="work-policy-overtime-minimum"
							type="number"
							value={form.overtimeMinimumRequestMinutes}
							onChange={(event) =>
								updateForm('overtimeMinimumRequestMinutes', Number(event.target.value))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="work-policy-overtime-multiplier">연장 보상 배율</Label>
						<Input
							id="work-policy-overtime-multiplier"
							type="number"
							step="0.1"
							value={form.overtimeCompensationMultiplier}
							onChange={(event) =>
								updateForm('overtimeCompensationMultiplier', Number(event.target.value))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="work-policy-night-multiplier">야간 보상 배율</Label>
						<Input
							id="work-policy-night-multiplier"
							type="number"
							step="0.1"
							value={form.nightCompensationMultiplier}
							onChange={(event) =>
								updateForm('nightCompensationMultiplier', Number(event.target.value))
							}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="work-policy-holiday-multiplier">휴일 보상 배율</Label>
						<Input
							id="work-policy-holiday-multiplier"
							type="number"
							step="0.1"
							value={form.holidayCompensationMultiplier}
							onChange={(event) =>
								updateForm('holidayCompensationMultiplier', Number(event.target.value))
							}
						/>
					</div>
				</div>

				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<p className="text-xs font-medium text-muted-foreground">휴게시간</p>
						<Button type="button" variant="outline" size="sm" onClick={addBreakRow}>
							<Plus className="size-4" />
							휴게 추가
						</Button>
					</div>
					{form.breaks.length === 0 ? (
						<p className="text-sm text-muted-foreground">등록된 휴게시간이 없습니다.</p>
					) : (
						<div className="space-y-3">
							{form.breaks.map((brk, index) => (
								<div
									key={index}
									className="grid gap-2 rounded-lg border border-border p-3 sm:grid-cols-4 sm:items-end"
								>
									<div className="space-y-1">
										<Label htmlFor={`work-policy-break-start-${index}`}>시작</Label>
										<Input
											id={`work-policy-break-start-${index}`}
											type="time"
											value={brk.startTime}
											onChange={(event) => updateBreakRow(index, { startTime: event.target.value })}
										/>
									</div>
									<div className="space-y-1">
										<Label htmlFor={`work-policy-break-end-${index}`}>종료</Label>
										<Input
											id={`work-policy-break-end-${index}`}
											type="time"
											value={brk.endTime}
											onChange={(event) => updateBreakRow(index, { endTime: event.target.value })}
										/>
									</div>
									<label className="flex items-center gap-2 text-sm">
										<input
											type="checkbox"
											checked={brk.paid}
											onChange={(event) => updateBreakRow(index, { paid: event.target.checked })}
											className="size-4 rounded border-border"
										/>
										유급
									</label>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										aria-label={`${index + 1}번째 휴게시간 삭제`}
										onClick={() => removeBreakRow(index)}
									>
										삭제
									</Button>
								</div>
							))}
						</div>
					)}
				</div>

				<label className="flex items-center gap-2 text-sm">
					<input
						type="checkbox"
						checked={form.unapprovedOvertimeRecognized}
						onChange={(event) => updateForm('unapprovedOvertimeRecognized', event.target.checked)}
						className="size-4 rounded border-border"
					/>
					미승인 연장근무 인정
				</label>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						저장
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function PolicyAssignDialog({
	policy,
	onClose,
	onSubmitted
}: {
	policy: WorkPolicyResponse;
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
	const [employeeIds, setEmployeeIds] = useState<number[]>([]);
	const [initiallyAssigned, setInitiallyAssigned] = useState<number[]>([]);
	const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			try {
				const [page, assigned] = await Promise.all([
					api.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200'),
					api.get<number[]>(`/work-policies/${policy.id}/assignments`)
				]);
				if (active) {
					setEmployees(page.content);
					setInitiallyAssigned(assigned);
					setEmployeeIds(assigned); // 이미 할당된 직원은 기본 체크
				}
			} catch (loadError) {
				if (active) setError(getErrorMessage(loadError));
			} finally {
				if (active) setLoading(false);
			}
		});
		return () => {
			active = false;
		};
	}, [policy.id]);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		// 체크 변화만 반영: 새로 체크된 직원은 할당, 체크 해제된 직원은 할당 해제.
		const toAssign = employeeIds.filter((id) => !initiallyAssigned.includes(id));
		const toUnassign = initiallyAssigned.filter((id) => !employeeIds.includes(id));
		if (toAssign.length === 0 && toUnassign.length === 0) {
			onClose();
			return;
		}
		const assignForm =
			toAssign.length > 0
				? validateAssignPolicyForm({ employeeIds: toAssign, effectiveFrom })
				: null;
		if (assignForm && !assignForm.ok) {
			setError(assignForm.message);
			return;
		}
		setSubmitting(true);
		try {
			if (assignForm && assignForm.ok) {
				await api.post(`/work-policies/${policy.id}/assign`, assignForm.request);
			}
			for (const employeeId of toUnassign) {
				await api.delete(`/work-policies/${policy.id}/assignments/${employeeId}`);
			}
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	function toggleEmployee(employeeId: number) {
		setEmployeeIds((current) =>
			current.includes(employeeId)
				? current.filter((id) => id !== employeeId)
				: [...current, employeeId]
		);
	}

	return (
		<Modal
			title="직원에게 정책 할당"
			description={`${policy.name} 정책을 선택한 직원에게 적용합니다.`}
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="policy-effective-from">적용 시작일</Label>
					<Input
						id="policy-effective-from"
						type="date"
						value={effectiveFrom}
						onChange={(event) => setEffectiveFrom(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<p className="text-xs font-medium text-muted-foreground">직원</p>
					{loading ? (
						<div className="flex min-h-20 items-center justify-center rounded-lg border border-border">
							<Spinner />
						</div>
					) : employees.length === 0 ? (
						<EmptyState message="할당할 직원이 없습니다." />
					) : (
						<div className="max-h-56 space-y-2 overflow-y-auto rounded-lg border border-border p-3">
							{employees.map((employee) => (
								<label
									key={employee.id}
									className="flex items-center justify-between gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted"
								>
									<span>
										<span className="font-semibold">{employee.name}</span>
										<span className="ml-2 text-muted-foreground">
											{employee.department ?? '부서 미지정'}
										</span>
									</span>
									<input
										type="checkbox"
										checked={employeeIds.includes(employee.id)}
										onChange={() => toggleEmployee(employee.id)}
										className="size-4 rounded border-border"
									/>
								</label>
							))}
						</div>
					)}
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						할당
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function WorkPolicyTable({
	policies,
	onAssign,
	onEdit
}: {
	policies: WorkPolicyResponse[];
	onAssign: (policy: WorkPolicyResponse) => void;
	onEdit: (policy: WorkPolicyResponse) => void;
}) {
	if (policies.length === 0) return <EmptyState message="등록된 근태 정책이 없습니다." />;

	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[880px] text-left text-sm">
				<thead className="bg-muted/40 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-semibold">정책</th>
						<th className="px-4 py-3 font-semibold">유형</th>
						<th className="px-4 py-3 font-semibold">근무 시간</th>
						<th className="px-4 py-3 font-semibold">근무일</th>
						<th className="px-4 py-3 font-semibold">휴게</th>
						<th className="px-4 py-3 font-semibold">직원</th>
						<th className="px-4 py-3 text-right font-semibold">관리</th>
					</tr>
				</thead>
				<tbody>
					{policies.map((policy) => (
						<tr key={policy.id} className="border-t border-border">
							<td className="px-4 py-4">
								<p className="font-semibold">{policy.name}</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{policy.active ? '활성' : '비활성'} · 자동 퇴근{' '}
									{numberLabel(policy.autoCheckOutAfterHours)}시간 후
								</p>
							</td>
							<td className="px-4 py-4">
								<Badge variant={policy.active ? 'success' : 'outline'}>
									{workPolicyTypeLabel(policy.type)}
								</Badge>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{workPolicyTimeLabel(policy)}</td>
							<td className="px-4 py-4 text-muted-foreground">{workDaysLabel(policy.workDays)}</td>
							<td className="px-4 py-4 text-muted-foreground">
								{minutesLabel(policy.totalBreakMinutes)}
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{numberLabel(policy.assignedEmployeeCount)}명
							</td>
							<td className="px-4 py-4 text-right">
								<div className="flex justify-end gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => onEdit(policy)}
										aria-label={`${policy.name} 상세보기`}
									>
										상세
									</Button>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => onAssign(policy)}
										aria-label={`${policy.name} 직원에게 정책 할당`}
									>
										할당
									</Button>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function WorkPoliciesPage() {
	const [state, setState] = useState<RequestState<WorkPolicyResponse[]>>({ status: 'idle' });
	const [dialogOpen, setDialogOpen] = useState(false);
	const [assignTarget, setAssignTarget] = useState<WorkPolicyResponse | null>(null);
	const [editTarget, setEditTarget] = useState<WorkPolicyResponse | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const policies = await api.get<WorkPolicyResponse[]>('/work-policies');
				setState({ status: 'success', data: policies });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const policies = state.status === 'success' ? state.data : [];
	const activeCount = policies.filter((policy) => policy.active).length;
	const assignedCount = policies.reduce((sum, policy) => sum + policy.assignedEmployeeCount, 0);

	return (
		<div className="space-y-6">
			<PageHeader
				title="근태 정책"
				description="근무 유형, 출퇴근 기준, 직원 할당 상태를 관리합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button type="button" onClick={() => setDialogOpen(true)}>
							<Plus className="size-4" />
							정책 추가
						</Button>
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					</div>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{state.status === 'success' ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<ExpenseStatCard
							title="근무 정책"
							value={`${numberLabel(policies.length)}개`}
							description="등록된 정책"
						/>
						<ExpenseStatCard
							title="활성 정책"
							value={`${numberLabel(activeCount)}개`}
							description="직원에게 적용 가능"
						/>
						<ExpenseStatCard
							title="할당 직원"
							value={`${numberLabel(assignedCount)}명`}
							description="정책 기준 집계"
						/>
						<ExpenseStatCard
							title="기본 근무"
							value={policies[0] ? workPolicyTimeLabel(policies[0]) : '-'}
							description={policies[0]?.name ?? '정책 등록 필요'}
						/>
					</div>

					<Card>
						<CardHeader>
							<CardTitle>근무 정책</CardTitle>
							<CardDescription>
								근무 유형, 근무 시간, 휴게 시간, 할당 인원을 확인합니다.
							</CardDescription>
						</CardHeader>
						<CardContent>
							<WorkPolicyTable
								policies={policies}
								onAssign={setAssignTarget}
								onEdit={setEditTarget}
							/>
						</CardContent>
					</Card>
				</>
			) : null}

			{dialogOpen ? (
				<WorkPolicyDialog onClose={() => setDialogOpen(false)} onSubmitted={load} />
			) : null}
			{editTarget ? (
				<WorkPolicyDialog
					policy={editTarget}
					onClose={() => setEditTarget(null)}
					onSubmitted={load}
				/>
			) : null}
			{assignTarget ? (
				<PolicyAssignDialog
					policy={assignTarget}
					onClose={() => setAssignTarget(null)}
					onSubmitted={load}
				/>
			) : null}
		</div>
	);
}

type AdminPageData = {
	summary?: UsageSummaryResponse;
	usageCompanies?: PageResponse<CompanyUsageResponse>;
	companies?: PageResponse<AdminCompanyResponse>;
	company?: AdminCompanyResponse;
	payments?: PageResponse<AdminPaymentResponse>;
	payment?: AdminPaymentResponse;
	auditLogs?: PageResponse<AuditLogResponse>;
};

function companyStatusVariant(active: boolean): 'success' | 'destructive' {
	return active ? 'success' : 'destructive';
}

function companyStatusLabel(active: boolean): string {
	return active ? '활성' : '정지';
}

function adminPlanSummary(summary: UsageSummaryResponse): string[] {
	return Object.entries(summary.companiesByPlan)
		.sort(([left], [right]) => left.localeCompare(right))
		.map(([plan, count]) => `${plan} ${numberLabel(count)}개`);
}

function AdminUsagePanel({
	summary,
	companies
}: {
	summary: UsageSummaryResponse;
	companies: CompanyUsageResponse[];
}) {
	return (
		<>
			<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
				<ExpenseStatCard
					title="전체 회사"
					value={`${numberLabel(summary.totalCompanies)}개`}
					description={`활성 ${numberLabel(summary.activeCompanies)}개`}
				/>
				<ExpenseStatCard
					title="정지 회사"
					value={`${numberLabel(summary.suspendedCompanies)}개`}
					description="로그인 제한 상태"
				/>
				<ExpenseStatCard
					title="전체 사용자"
					value={`${numberLabel(summary.totalUsers)}명`}
					description="SUPER_ADMIN 포함"
				/>
				<ExpenseStatCard
					title="전체 직원"
					value={`${numberLabel(summary.totalEmployees)}명`}
					description={adminPlanSummary(summary).join(' · ')}
				/>
			</div>
			<Card>
				<CardHeader>
					<CardTitle>플랜 분포</CardTitle>
					<CardDescription>가입 회사의 현재 플랜 구성을 확인합니다.</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex flex-wrap gap-2">
						{adminPlanSummary(summary).map((item) => (
							<Badge key={item} variant="outline">
								{item}
							</Badge>
						))}
					</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle>최근 30일 출퇴근</CardTitle>
					<CardDescription>회사별 사용자, 직원, 출퇴근 기록 수를 비교합니다.</CardDescription>
				</CardHeader>
				<CardContent>
					<AdminUsageTable companies={companies} />
				</CardContent>
			</Card>
		</>
	);
}

function AdminUsageTable({ companies }: { companies: CompanyUsageResponse[] }) {
	if (companies.length === 0) return <EmptyState message="사용량 데이터가 없습니다." />;
	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[760px] text-left text-sm">
				<thead className="bg-muted/40 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-semibold">회사</th>
						<th className="px-4 py-3 font-semibold">플랜</th>
						<th className="px-4 py-3 font-semibold">상태</th>
						<th className="px-4 py-3 font-semibold">사용자</th>
						<th className="px-4 py-3 font-semibold">직원</th>
						<th className="px-4 py-3 font-semibold">최근 30일 출퇴근</th>
					</tr>
				</thead>
				<tbody>
					{companies.map((company) => (
						<tr key={company.companyId} className="border-t border-border">
							<td className="px-4 py-4 font-semibold">{company.name}</td>
							<td className="px-4 py-4 text-muted-foreground">{company.plan}</td>
							<td className="px-4 py-4">
								<Badge variant={companyStatusVariant(company.active)}>
									{companyStatusLabel(company.active)}
								</Badge>
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{numberLabel(company.userCount)}명
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{numberLabel(company.employeeCount)}명
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{numberLabel(company.attendancesLast30d)}건
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function AdminCompanyTable({
	companies,
	onImpersonate
}: {
	companies: AdminCompanyResponse[];
	onImpersonate: (company: AdminCompanyResponse) => void;
}) {
	if (companies.length === 0) return <EmptyState message="회사 데이터가 없습니다." />;
	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[980px] text-left text-sm">
				<thead className="bg-muted/40 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-semibold">회사</th>
						<th className="px-4 py-3 font-semibold">플랜</th>
						<th className="px-4 py-3 font-semibold">상태</th>
						<th className="px-4 py-3 font-semibold">직원</th>
						<th className="px-4 py-3 font-semibold">사용자</th>
						<th className="px-4 py-3 font-semibold">생성일</th>
						<th className="px-4 py-3 text-right font-semibold">작업</th>
					</tr>
				</thead>
				<tbody>
					{companies.map((company) => (
						<tr key={company.id} className="border-t border-border">
							<td className="px-4 py-4">
								<Link
									href={`/admin/companies/${company.id}`}
									aria-label={`${company.name} 회사 상세 보기`}
									className="inline-flex items-center gap-1 font-semibold transition-colors hover:text-primary"
								>
									{company.name}
									<ChevronRight className="size-4" />
								</Link>
								<p className="mt-1 text-xs text-muted-foreground">
									{company.businessNumber ?? '사업자번호 미등록'}
								</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{company.plan} · 최대 {numberLabel(company.maxEmployees)}명
							</td>
							<td className="px-4 py-4">
								<Badge variant={companyStatusVariant(company.active)}>
									{companyStatusLabel(company.active)}
								</Badge>
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{numberLabel(company.employeeCount)}명
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{numberLabel(company.userCount)}명
							</td>
							<td className="px-4 py-4 text-muted-foreground">{dateLabel(company.createdAt)}</td>
							<td className="px-4 py-4 text-right">
								<div className="inline-flex gap-2">
									<Button
										type="button"
										variant="outline"
										size="sm"
										aria-label={`${company.name} 임퍼소네이션 시작`}
										onClick={() => onImpersonate(company)}
									>
										접속
									</Button>
								</div>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

type PaidPlanCode = Exclude<PlanCode, 'FREE'>;

const paidPlanOptions: Array<{ value: PaidPlanCode; label: string }> = [
	{ value: 'BASIC', label: 'BASIC (기본 정원 20)' },
	{ value: 'STANDARD', label: 'STANDARD (기본 정원 50)' },
	{ value: 'PRO', label: 'PRO (무제한)' }
];

function paidPlanDefault(currentPlan: string): PaidPlanCode {
	if (currentPlan === 'BASIC' || currentPlan === 'STANDARD' || currentPlan === 'PRO') {
		return currentPlan;
	}
	return 'STANDARD';
}

function paidPlanEmployeePlaceholder(plan: PaidPlanCode): string {
	const placeholders: Record<PaidPlanCode, string> = {
		BASIC: '20',
		STANDARD: '50',
		PRO: '무제한'
	};
	return placeholders[plan];
}

function PaidSubscriptionGrantDialog({
	company,
	onClose,
	onSubmit
}: {
	company: AdminCompanyResponse;
	onClose: () => void;
	onSubmit: (request: GrantPaidSubscriptionRequest) => Promise<void>;
}) {
	const [plan, setPlan] = useState<PaidPlanCode>(() => paidPlanDefault(company.plan));
	const [billingCycle, setBillingCycle] = useState<BillingCycle>('MONTHLY');
	const [grantDays, setGrantDays] = useState('30');
	const [maxEmployees, setMaxEmployees] = useState('');
	const [reason, setReason] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmedReason = reason.trim();
		const parsedGrantDays = grantDays.trim() ? Number(grantDays) : undefined;
		const parsedMaxEmployees = maxEmployees.trim() ? Number(maxEmployees) : undefined;

		if (!trimmedReason) {
			setError('제공 사유를 입력해주세요.');
			return;
		}
		if (
			parsedGrantDays !== undefined &&
			(!Number.isInteger(parsedGrantDays) || parsedGrantDays < 1 || parsedGrantDays > 1095)
		) {
			setError('제공 기간은 1~1095일 사이여야 합니다.');
			return;
		}
		if (
			parsedMaxEmployees !== undefined &&
			(!Number.isInteger(parsedMaxEmployees) || parsedMaxEmployees < 1)
		) {
			setError('직원 정원은 1명 이상이어야 합니다.');
			return;
		}

		const request: GrantPaidSubscriptionRequest = {
			plan,
			billingCycle,
			reason: trimmedReason
		};
		if (parsedGrantDays !== undefined) request.grantDays = parsedGrantDays;
		if (parsedMaxEmployees !== undefined) request.maxEmployees = parsedMaxEmployees;

		setSubmitting(true);
		setError(null);
		try {
			await onSubmit(request);
		} catch (submitError) {
			setError(getErrorMessage(submitError, '유료권 제공에 실패했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="유료권 제공"
			description={`${company.name} · 현재 ${company.plan} / 정원 ${numberLabel(company.maxEmployees)}명`}
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="paid-grant-plan">제공 플랜</Label>
						<select
							id="paid-grant-plan"
							aria-label="제공 플랜"
							value={plan}
							onChange={(event) => setPlan(event.target.value as PaidPlanCode)}
							className={dialogControlClassName}
						>
							{paidPlanOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="paid-grant-cycle">기준 주기</Label>
						<select
							id="paid-grant-cycle"
							aria-label="기준 주기"
							value={billingCycle}
							onChange={(event) => setBillingCycle(event.target.value as BillingCycle)}
							className={dialogControlClassName}
						>
							<option value="MONTHLY">월간</option>
							<option value="YEARLY">연간</option>
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="paid-grant-days">제공 기간</Label>
						<Input
							id="paid-grant-days"
							aria-label="제공 기간"
							type="number"
							min={1}
							max={1095}
							value={grantDays}
							onChange={(event) => setGrantDays(event.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="paid-grant-max-employees">직원 정원</Label>
						<Input
							id="paid-grant-max-employees"
							aria-label="직원 정원"
							type="number"
							min={1}
							value={maxEmployees}
							placeholder={paidPlanEmployeePlaceholder(plan)}
							onChange={(event) => setMaxEmployees(event.target.value)}
						/>
					</div>
				</div>
				<div className="space-y-2">
					<Label htmlFor="paid-grant-reason">제공 사유</Label>
					<textarea
						id="paid-grant-reason"
						aria-label="제공 사유"
						className={dialogTextAreaClassName}
						value={reason}
						placeholder="예: 초기 파트너 무상 제공"
						onChange={(event) => setReason(event.target.value)}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						{submitting ? '처리 중...' : '유료권 제공 확정'}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function AdminCompanyDetail({
	company,
	onImpersonate,
	onGrantPaidSubscription
}: {
	company: AdminCompanyResponse;
	onImpersonate: () => void;
	onGrantPaidSubscription: () => void;
}) {
	return (
		<>
			<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
				<ExpenseStatCard
					title="플랜"
					value={company.plan}
					description={`최대 ${numberLabel(company.maxEmployees)}명`}
				/>
				<ExpenseStatCard
					title="상태"
					value={companyStatusLabel(company.active)}
					description={`소유자 #${company.ownerId}`}
				/>
				<ExpenseStatCard
					title="직원"
					value={`${numberLabel(company.employeeCount)}명`}
					description="회사 HR 레코드"
				/>
				<ExpenseStatCard
					title="사용자"
					value={`${numberLabel(company.userCount)}명`}
					description="로그인 계정"
				/>
			</div>
			<Card>
				<CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
					<div>
						<CardTitle>{company.name}</CardTitle>
						<CardDescription>회사 상태, 플랜, 연락처, 등록 정보를 확인합니다.</CardDescription>
					</div>
					<div className="flex flex-wrap gap-2">
						<Button type="button" variant="outline" onClick={onGrantPaidSubscription}>
							<Gift className="size-4" />
							유료권 제공
						</Button>
						<Button type="button" onClick={onImpersonate}>
							임퍼소네이션 시작
						</Button>
					</div>
				</CardHeader>
				<CardContent className="grid gap-3 sm:grid-cols-2">
					{[
						['사업자번호', company.businessNumber ?? '-'],
						['주소', company.address ?? '-'],
						['전화', company.phone ?? '-'],
						['생성일', dateLabel(company.createdAt)],
						['수정일', dateLabel(company.updatedAt)],
						['회사 ID', `#${company.id}`]
					].map(([label, value]) => (
						<div key={label} className="rounded-lg border border-border bg-muted/20 p-4">
							<p className="text-xs font-medium text-muted-foreground">{label}</p>
							<p className="mt-1 text-sm font-semibold">{value}</p>
						</div>
					))}
				</CardContent>
			</Card>
		</>
	);
}

function AdminPaymentTable({ payments }: { payments: AdminPaymentResponse[] }) {
	if (payments.length === 0) return <EmptyState message="결제 요청이 없습니다." />;
	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[980px] text-left text-sm">
				<thead className="bg-muted/40 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-semibold">요청</th>
						<th className="px-4 py-3 font-semibold">회사</th>
						<th className="px-4 py-3 font-semibold">플랜</th>
						<th className="px-4 py-3 font-semibold">금액</th>
						<th className="px-4 py-3 font-semibold">입금자</th>
						<th className="px-4 py-3 font-semibold">상태</th>
						<th className="px-4 py-3 text-right font-semibold">상세</th>
					</tr>
				</thead>
				<tbody>
					{payments.map((payment) => (
						<tr key={payment.id} className="border-t border-border">
							<td className="px-4 py-4">
								<p className="font-semibold">결제 #{payment.id}</p>
								<p className="mt-1 text-xs text-muted-foreground">{dateLabel(payment.createdAt)}</p>
							</td>
							<td className="px-4 py-4 text-muted-foreground">{payment.companyName}</td>
							<td className="px-4 py-4 text-muted-foreground">
								{payment.plan} · {billingCycleLabel(payment.billingCycle)}
							</td>
							<td className="px-4 py-4 text-muted-foreground">{wonLabel(payment.amountKrw)}</td>
							<td className="px-4 py-4 text-muted-foreground">{payment.depositorName ?? '-'}</td>
							<td className="px-4 py-4">
								<Badge variant={paymentStatusVariant(payment.status)}>
									{paymentStatusLabel(payment.status)}
								</Badge>
							</td>
							<td className="px-4 py-4 text-right">
								<Link
									href={`/admin/payments/${payment.id}`}
									aria-label={`결제 #${payment.id} 상세 보기`}
									className="inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
								>
									보기
									<ChevronRight className="size-4" />
								</Link>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function AdminPaymentDetail({
	payment,
	onApprove,
	onReject
}: {
	payment: AdminPaymentResponse;
	onApprove: () => void;
	onReject: () => void;
}) {
	return (
		<>
			<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
				<ExpenseStatCard
					title="회사"
					value={payment.companyName}
					description={payment.requestedByUserEmail ?? '요청자 이메일 없음'}
				/>
				<ExpenseStatCard
					title="플랜"
					value={payment.plan}
					description={billingCycleLabel(payment.billingCycle)}
				/>
				<ExpenseStatCard
					title="금액"
					value={wonLabel(payment.amountKrw)}
					description={payment.depositorName ?? '입금자 미등록'}
				/>
				<ExpenseStatCard
					title="상태"
					value={paymentStatusLabel(payment.status)}
					description={`만료 ${dateLabel(payment.expiresAt)}`}
				/>
			</div>
			<Card>
				<CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
					<div>
						<CardTitle>결제 요청 #{payment.id}</CardTitle>
						<CardDescription>
							입금 정보와 청구 담당자 정보를 확인한 뒤 승인 또는 반려합니다.
						</CardDescription>
					</div>
					{payment.status === 'PENDING' ? (
						<div className="flex gap-2">
							<Button type="button" variant="outline" onClick={onReject}>
								반려
							</Button>
							<Button type="button" onClick={onApprove}>
								승인
							</Button>
						</div>
					) : null}
				</CardHeader>
				<CardContent className="grid gap-3 sm:grid-cols-2">
					{[
						['입금 계좌', payment.bankAccount ?? '-'],
						['입금자', payment.depositorName ?? '-'],
						['사업자명', payment.payerBusinessName ?? '-'],
						['사업자번호', payment.payerBusinessNumber ?? '-'],
						['청구 이메일', payment.payerEmail ?? '-'],
						['청구 전화', payment.payerPhone ?? '-']
					].map(([label, value]) => (
						<div key={label} className="rounded-lg border border-border bg-muted/20 p-4">
							<p className="text-xs font-medium text-muted-foreground">{label}</p>
							<p className="mt-1 text-sm font-semibold">{value}</p>
						</div>
					))}
				</CardContent>
			</Card>
		</>
	);
}

function PaymentApproveDialog({
	payment,
	onClose,
	onSubmitted
}: {
	payment: AdminPaymentResponse;
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleApprove() {
		setSubmitting(true);
		try {
			await api.post<AdminPaymentResponse>(`/admin/payments/${payment.id}/approve`);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="결제 승인"
			description={`${payment.companyName} ${wonLabel(payment.amountKrw)} 결제 요청을 승인합니다.`}
			onClose={onClose}
		>
			<div className="space-y-4">
				<FormError message={error} />
				<div className="rounded-lg border border-border bg-muted/20 p-4 text-sm">
					<p className="font-semibold">{payment.companyName}</p>
					<p className="mt-1 text-muted-foreground">
						{payment.plan} · {billingCycleLabel(payment.billingCycle)} ·{' '}
						{payment.depositorName ?? '-'}
					</p>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="button" onClick={() => void handleApprove()} disabled={submitting}>
						승인
					</Button>
				</div>
			</div>
		</Modal>
	);
}

function PaymentRejectDialog({
	payment,
	onClose,
	onSubmitted
}: {
	payment: AdminPaymentResponse;
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [reason, setReason] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmed = reason.trim();
		if (!trimmed) {
			setError('반려 사유를 입력해주세요.');
			return;
		}
		setSubmitting(true);
		try {
			await api.post<AdminPaymentResponse>(`/admin/payments/${payment.id}/reject`, {
				reason: trimmed
			});
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="결제 반려"
			description={`${payment.companyName} 결제 요청을 반려합니다.`}
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="payment-reject-reason">반려 사유</Label>
					<textarea
						id="payment-reject-reason"
						value={reason}
						onChange={(event) => setReason(event.target.value)}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" variant="destructive" disabled={submitting}>
						반려
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function AuditLogTable({ logs }: { logs: AuditLogResponse[] }) {
	if (logs.length === 0) return <EmptyState message="감사 로그가 없습니다." />;
	return (
		<div className="overflow-x-auto rounded-lg border border-border">
			<table className="w-full min-w-[900px] text-left text-sm">
				<thead className="bg-muted/40 text-xs text-muted-foreground">
					<tr>
						<th className="px-4 py-3 font-semibold">작업</th>
						<th className="px-4 py-3 font-semibold">회사</th>
						<th className="px-4 py-3 font-semibold">사용자</th>
						<th className="px-4 py-3 font-semibold">대상</th>
						<th className="px-4 py-3 font-semibold">IP</th>
						<th className="px-4 py-3 font-semibold">일시</th>
					</tr>
				</thead>
				<tbody>
					{logs.map((log) => (
						<tr key={log.id} className="border-t border-border">
							<td className="px-4 py-4 font-semibold">{log.action}</td>
							<td className="px-4 py-4 text-muted-foreground">
								{log.companyName ?? `회사 #${log.companyId}`}
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{log.userName ?? (log.userId ? `사용자 #${log.userId}` : '-')}
							</td>
							<td className="px-4 py-4 text-muted-foreground">
								{log.entityType}
								{log.entityId ? ` #${log.entityId}` : ''}
							</td>
							<td className="px-4 py-4 text-muted-foreground">{log.ipAddress ?? '-'}</td>
							<td className="px-4 py-4 text-muted-foreground">{dateLabel(log.createdAt)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

export function AdminConsolePage({ route }: { route: MatchedRoute }) {
	const auth = useAuth();
	const [state, setState] = useState<RequestState<AdminPageData>>({ status: 'idle' });
	const [approveTarget, setApproveTarget] = useState<AdminPaymentResponse | null>(null);
	const [rejectTarget, setRejectTarget] = useState<AdminPaymentResponse | null>(null);
	const [paidGrantTarget, setPaidGrantTarget] = useState<AdminCompanyResponse | null>(null);
	const [feedback, setFeedback] = useState<string | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				switch (route.definition.pattern) {
					case '/admin':
					case '/admin/usage': {
						const [summary, usageCompanies] = await Promise.all([
							api.get<UsageSummaryResponse>('/admin/usage/summary'),
							api.get<PageResponse<CompanyUsageResponse>>('/admin/usage/companies?page=0&size=20')
						]);
						setState({ status: 'success', data: { summary, usageCompanies } });
						return;
					}
					case '/admin/companies': {
						const companies = await api.get<PageResponse<AdminCompanyResponse>>(
							'/admin/companies?page=0&size=20'
						);
						setState({ status: 'success', data: { companies } });
						return;
					}
					case '/admin/companies/:id': {
						const company = await api.get<AdminCompanyResponse>(
							`/admin/companies/${route.params.id}`
						);
						setState({ status: 'success', data: { company } });
						return;
					}
					case '/admin/payments': {
						const payments = await api.get<PageResponse<AdminPaymentResponse>>(
							'/admin/payments?page=0&size=30'
						);
						setState({ status: 'success', data: { payments } });
						return;
					}
					case '/admin/payments/:id': {
						const payment = await api.get<AdminPaymentResponse>(
							`/admin/payments/${route.params.id}`
						);
						setState({ status: 'success', data: { payment } });
						return;
					}
					case '/admin/audit-logs': {
						const auditLogs = await api.get<PageResponse<AuditLogResponse>>(
							'/admin/audit-logs?page=0&size=50'
						);
						setState({ status: 'success', data: { auditLogs } });
						return;
					}
					default:
						setState({ status: 'success', data: {} });
				}
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[route.definition.pattern, route.params.id]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function handleImpersonate(company: AdminCompanyResponse) {
		setActionError(null);
		try {
			await auth.startImpersonation(company.id);
		} catch (error) {
			setActionError(getErrorMessage(error));
		}
	}

	async function handlePaidSubscriptionGrant(
		company: AdminCompanyResponse,
		request: GrantPaidSubscriptionRequest
	) {
		setActionError(null);
		await api.post<SubscriptionResponse>(
			`/admin/companies/${company.id}/subscription-grants`,
			request
		);
		setPaidGrantTarget(null);
		setFeedback('유료권을 제공했습니다.');
		await load();
	}

	const data = state.status === 'success' ? state.data : null;
	const pageTitle = route.definition.title;
	const pageDescription =
		route.definition.pattern === '/admin'
			? '전체 회사와 사용량, 플랜 분포를 운영 관점에서 확인합니다.'
			: route.definition.description;

	return (
		<div className="space-y-6">
			<PageHeader
				title={pageTitle}
				description={pageDescription}
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{actionError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{actionError}
				</div>
			) : null}

			{feedback ? (
				<div className="rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
					{feedback}
				</div>
			) : null}

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data?.summary && data.usageCompanies ? (
				<AdminUsagePanel summary={data.summary} companies={data.usageCompanies.content} />
			) : null}

			{data?.companies ? (
				<Card>
					<CardHeader>
						<CardTitle>회사 관리</CardTitle>
						<CardDescription>
							입점 회사, 구독 플랜, 활성 상태, 임퍼소네이션 진입을 관리합니다.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<AdminCompanyTable
							companies={data.companies.content}
							onImpersonate={(company) => void handleImpersonate(company)}
						/>
					</CardContent>
				</Card>
			) : null}

			{data?.company ? (
				<AdminCompanyDetail
					company={data.company}
					onImpersonate={() => void handleImpersonate(data.company!)}
					onGrantPaidSubscription={() => setPaidGrantTarget(data.company!)}
				/>
			) : null}

			{data?.payments ? (
				<Card>
					<CardHeader>
						<CardTitle>결제 승인</CardTitle>
						<CardDescription>
							무통장 입금 결제 요청의 회사, 입금자, 상태를 검토합니다.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<AdminPaymentTable payments={data.payments.content} />
					</CardContent>
				</Card>
			) : null}

			{data?.payment ? (
				<AdminPaymentDetail
					payment={data.payment}
					onApprove={() => setApproveTarget(data.payment!)}
					onReject={() => setRejectTarget(data.payment!)}
				/>
			) : null}

			{data?.auditLogs ? (
				<Card>
					<CardHeader>
						<CardTitle>감사 로그</CardTitle>
						<CardDescription>운영 콘솔과 주요 업무 액션의 감사 기록입니다.</CardDescription>
					</CardHeader>
					<CardContent>
						<AuditLogTable logs={data.auditLogs.content} />
					</CardContent>
				</Card>
			) : null}

			{approveTarget ? (
				<PaymentApproveDialog
					payment={approveTarget}
					onClose={() => setApproveTarget(null)}
					onSubmitted={load}
				/>
			) : null}
			{rejectTarget ? (
				<PaymentRejectDialog
					payment={rejectTarget}
					onClose={() => setRejectTarget(null)}
					onSubmitted={load}
				/>
			) : null}
			{paidGrantTarget ? (
				<PaidSubscriptionGrantDialog
					company={paidGrantTarget}
					onClose={() => setPaidGrantTarget(null)}
					onSubmit={(request) => handlePaidSubscriptionGrant(paidGrantTarget, request)}
				/>
			) : null}
		</div>
	);
}

type OrganizationPageData = {
	departments: OrganizationDepartmentResponse[];
	employees: EmployeeResponse[];
	affiliates: CompanyAffiliateResponse[];
	changePlans: OrganizationChangePlanResponse[];
};

function flattenOrganizationDepartments(
	departments: OrganizationDepartmentResponse[]
): OrganizationDepartmentResponse[] {
	return departments.flatMap((department) => [
		department,
		...flattenOrganizationDepartments(department.children ?? [])
	]);
}

function findOrganizationDepartment(
	departments: OrganizationDepartmentResponse[],
	id: number | null
): OrganizationDepartmentResponse | null {
	if (id == null) return null;
	for (const department of departments) {
		if (department.id === id) return department;
		const child = findOrganizationDepartment(department.children ?? [], id);
		if (child) return child;
	}
	return null;
}

function OrganizationDepartmentTree({
	departments,
	selectedDepartmentId,
	onSelect,
	depth = 0
}: {
	departments: OrganizationDepartmentResponse[];
	selectedDepartmentId: number | null;
	onSelect: (departmentId: number) => void;
	depth?: number;
}) {
	if (departments.length === 0) return <EmptyState message="등록된 부서가 없습니다." />;

	return (
		<div className={depth === 0 ? 'space-y-2' : 'mt-2 space-y-2 border-l border-border pl-3'}>
			{departments.map((department) => {
				const selected = department.id === selectedDepartmentId;
				return (
					<div key={department.id}>
						<button
							type="button"
							onClick={() => onSelect(department.id)}
							className={cn(
								'flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors',
								selected
									? 'border-primary bg-primary/10'
									: 'border-border bg-card hover:bg-muted/40'
							)}
						>
							<span className="min-w-0">
								<span className="block truncate text-sm font-semibold">{department.name}</span>
								<span className="mt-1 block truncate text-xs text-muted-foreground">
									{department.managerName ?? '관리자 미지정'}
								</span>
							</span>
							<span className="flex shrink-0 items-center gap-2">
								<Badge variant={department.active ? 'success' : 'outline'}>
									{department.active ? '활성' : '비활성'}
								</Badge>
								<span className="text-sm font-semibold">
									{numberLabel(department.employeeCount)}명
								</span>
							</span>
						</button>
						{department.children?.length ? (
							<OrganizationDepartmentTree
								departments={department.children}
								selectedDepartmentId={selectedDepartmentId}
								onSelect={onSelect}
								depth={depth + 1}
							/>
						) : null}
					</div>
				);
			})}
		</div>
	);
}

function OrganizationCreateForm({
	departments,
	employees,
	onDone
}: {
	departments: OrganizationDepartmentResponse[];
	employees: EmployeeResponse[];
	onDone: () => Promise<void>;
}) {
	const [name, setName] = useState('');
	const [parentId, setParentId] = useState('');
	const [managerEmployeeId, setManagerEmployeeId] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const flatDepartments = useMemo(() => flattenOrganizationDepartments(departments), [departments]);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmedName = name.trim();
		if (!trimmedName) {
			setError('부서명을 입력해주세요.');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			await api.post<OrganizationDepartmentResponse>('/organization/departments', {
				name: trimmedName,
				parentId: parentId ? Number(parentId) : null,
				managerEmployeeId: managerEmployeeId ? Number(managerEmployeeId) : null
			});
			setName('');
			setParentId('');
			setManagerEmployeeId('');
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '부서를 생성하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form className="space-y-4" onSubmit={handleSubmit}>
			<FormError message={error} />
			<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
				<div className="space-y-2">
					<Label htmlFor="organization-department-name">부서명</Label>
					<Input
						id="organization-department-name"
						value={name}
						onChange={(event) => setName(event.target.value)}
						placeholder="예: 플랫폼"
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="organization-parent">상위 부서</Label>
					<select
						id="organization-parent"
						value={parentId}
						onChange={(event) => setParentId(event.target.value)}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="">상위 없음</option>
						{flatDepartments.map((department) => (
							<option key={department.id} value={department.id}>
								{department.name}
							</option>
						))}
					</select>
				</div>
				<div className="space-y-2 sm:col-span-2 xl:col-span-1">
					<Label htmlFor="organization-manager">관리자</Label>
					<select
						id="organization-manager"
						value={managerEmployeeId}
						onChange={(event) => setManagerEmployeeId(event.target.value)}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="">관리자 미지정</option>
						{employees
							.filter((employee) => employee.status === 'ACTIVE')
							.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name}
								</option>
							))}
					</select>
				</div>
			</div>
			<Button type="submit" disabled={submitting} className="w-full">
				<Plus className="size-4" />
				{submitting ? '생성 중...' : '부서 만들기'}
			</Button>
		</form>
	);
}

function OrganizationAssignmentPanel({
	department,
	employees,
	onDone
}: {
	department: OrganizationDepartmentResponse | null;
	employees: EmployeeResponse[];
	onDone: () => Promise<void>;
}) {
	const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	if (!department) {
		return <EmptyState message="부서를 선택하면 직원을 배정할 수 있습니다." />;
	}
	const currentDepartment = department;

	const candidates = employees
		.filter((employee) => employee.status === 'ACTIVE')
		.filter((employee) => employee.department !== currentDepartment.name);

	function toggleEmployee(employeeId: number) {
		setSelectedEmployeeIds((current) =>
			current.includes(employeeId)
				? current.filter((id) => id !== employeeId)
				: [...current, employeeId]
		);
	}

	async function assignSelected() {
		if (selectedEmployeeIds.length === 0) {
			setError('배정할 직원을 선택해주세요.');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			await api.post<OrganizationDepartmentResponse>(
				`/organization/departments/${currentDepartment.id}/employees`,
				{ employeeIds: selectedEmployeeIds }
			);
			setSelectedEmployeeIds([]);
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '직원을 배정하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="space-y-4">
			<FormError message={error} />
			<div className="rounded-lg border border-border bg-muted/20 p-4">
				<p className="font-semibold">{currentDepartment.name}</p>
				<p className="mt-1 text-sm text-muted-foreground">
					관리자 {currentDepartment.managerName ?? '미지정'} · 현재{' '}
					{numberLabel(currentDepartment.employeeCount)}명
				</p>
			</div>
			{candidates.length === 0 ? (
				<EmptyState message="배정 가능한 직원이 없습니다." />
			) : (
				<div className="max-h-72 space-y-2 overflow-auto pr-1">
					{candidates.map((employee) => (
						<label
							key={employee.id}
							className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 text-sm hover:bg-muted/40"
						>
							<input
								type="checkbox"
								checked={selectedEmployeeIds.includes(employee.id)}
								onChange={() => toggleEmployee(employee.id)}
								className="size-4"
							/>
							<span className="min-w-0 flex-1">
								<span className="block font-semibold">{employee.name}</span>
								<span className="mt-1 block truncate text-xs text-muted-foreground">
									{employee.department ?? '부서 미지정'} · {employee.position ?? '직책 미지정'}
								</span>
							</span>
						</label>
					))}
				</div>
			)}
			<Button
				type="button"
				onClick={() => void assignSelected()}
				disabled={submitting || candidates.length === 0}
				className="w-full"
			>
				{submitting ? '배정 중...' : '선택 직원 배정'}
			</Button>
		</div>
	);
}

type CompanyAffiliateFormState = {
	name: string;
	businessRegistrationNumber: string;
	representativeName: string;
	address: string;
	memo: string;
};

const defaultCompanyAffiliateForm = (): CompanyAffiliateFormState => ({
	name: '',
	businessRegistrationNumber: '',
	representativeName: '',
	address: '',
	memo: ''
});

function CompanyAffiliatePanel({
	affiliates,
	employees,
	onDone
}: {
	affiliates: CompanyAffiliateResponse[];
	employees: EmployeeResponse[];
	onDone: () => Promise<void>;
}) {
	const [form, setForm] = useState<CompanyAffiliateFormState>(() => defaultCompanyAffiliateForm());
	const [selectedAffiliateId, setSelectedAffiliateId] = useState('');
	const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [assigning, setAssigning] = useState(false);
	const [clearingEmployeeId, setClearingEmployeeId] = useState<number | null>(null);

	const activeAffiliates = useMemo(
		() => affiliates.filter((affiliate) => affiliate.active),
		[affiliates]
	);
	const affiliateNameById = useMemo(() => {
		const names = new Map<number, string>();
		for (const affiliate of affiliates) {
			names.set(affiliate.id, affiliate.name);
		}
		return names;
	}, [affiliates]);
	const effectiveSelectedAffiliateId =
		selectedAffiliateId &&
		activeAffiliates.some((affiliate) => String(affiliate.id) === selectedAffiliateId)
			? selectedAffiliateId
			: activeAffiliates[0]
				? String(activeAffiliates[0].id)
				: '';
	const selectedAffiliateNumericId = effectiveSelectedAffiliateId
		? Number(effectiveSelectedAffiliateId)
		: null;
	const assignmentCandidates = useMemo(
		() =>
			employees
				.filter((employee) => employee.status === 'ACTIVE')
				.filter((employee) => (employee.affiliateCompanyId ?? null) !== selectedAffiliateNumericId),
		[employees, selectedAffiliateNumericId]
	);
	const assignedEmployees = useMemo(
		() => employees.filter((employee) => employee.affiliateCompanyId != null),
		[employees]
	);

	function updateForm(field: keyof CompanyAffiliateFormState, value: string) {
		setForm((current) => ({ ...current, [field]: value }));
	}

	function toggleEmployee(employeeId: number) {
		setSelectedEmployeeIds((current) =>
			current.includes(employeeId)
				? current.filter((id) => id !== employeeId)
				: [...current, employeeId]
		);
	}

	async function createAffiliate(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const name = form.name.trim();
		if (!name) {
			setError('계열사명을 입력해주세요.');
			return;
		}

		const request: CreateCompanyAffiliateRequest = { name };
		const businessRegistrationNumber = form.businessRegistrationNumber.trim();
		const representativeName = form.representativeName.trim();
		const address = form.address.trim();
		const memo = form.memo.trim();
		if (businessRegistrationNumber) request.businessRegistrationNumber = businessRegistrationNumber;
		if (representativeName) request.representativeName = representativeName;
		if (address) request.address = address;
		if (memo) request.memo = memo;

		setSubmitting(true);
		setError(null);
		setNotice(null);
		try {
			await api.post<CompanyAffiliateResponse>('/companies/affiliates', request);
			setForm(defaultCompanyAffiliateForm());
			setNotice('계열사를 추가했습니다.');
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '계열사를 추가하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	async function assignSelected() {
		if (!selectedAffiliateNumericId) {
			setError('배정할 계열사를 선택해주세요.');
			return;
		}
		if (selectedEmployeeIds.length === 0) {
			setError('배정할 직원을 선택해주세요.');
			return;
		}

		const request: AssignCompanyAffiliateEmployeesRequest = { employeeIds: selectedEmployeeIds };
		setAssigning(true);
		setError(null);
		setNotice(null);
		try {
			await api.post<CompanyAffiliateResponse>(
				`/companies/affiliates/${selectedAffiliateNumericId}/employees`,
				request
			);
			setSelectedEmployeeIds([]);
			setNotice('선택 직원을 계열사에 배정했습니다.');
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '직원 계열사를 배정하지 못했습니다.'));
		} finally {
			setAssigning(false);
		}
	}

	async function clearAffiliate(employeeId: number) {
		setClearingEmployeeId(employeeId);
		setError(null);
		setNotice(null);
		try {
			await api.delete<EmployeeResponse>(`/companies/affiliates/employees/${employeeId}`);
			setNotice('직원 계열사 배정을 해제했습니다.');
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '직원 계열사 배정을 해제하지 못했습니다.'));
		} finally {
			setClearingEmployeeId(null);
		}
	}

	return (
		<div className="space-y-5">
			<FormError message={error} />
			{notice ? (
				<div className="rounded-lg bg-success/10 px-3 py-2 text-sm font-medium text-success">
					{notice}
				</div>
			) : null}

			<div className="grid gap-3 md:grid-cols-2">
				{affiliates.length === 0 ? (
					<EmptyState message="등록된 계열사가 없습니다." />
				) : (
					affiliates.map((affiliate) => (
						<div key={affiliate.id} className="rounded-lg border border-border bg-muted/20 p-4">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="truncate text-sm font-semibold">{affiliate.name}</p>
									<p className="mt-1 text-xs text-muted-foreground">
										{affiliate.businessRegistrationNumber ?? '사업자번호 미입력'}
									</p>
								</div>
								<Badge variant={affiliate.active ? 'success' : 'outline'}>
									{affiliate.active ? '활성' : '비활성'}
								</Badge>
							</div>
							<div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
								<span className="font-medium text-foreground">
									{numberLabel(affiliate.employeeCount)}명 배정
								</span>
								<span>{affiliate.representativeName ?? '대표자 미입력'}</span>
								{affiliate.address ? <span>{affiliate.address}</span> : null}
							</div>
							{affiliate.memo ? (
								<p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{affiliate.memo}</p>
							) : null}
						</div>
					))
				)}
			</div>

			<form className="space-y-3 border-t border-border pt-4" onSubmit={createAffiliate}>
				<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
					<div className="space-y-2">
						<Label htmlFor="affiliate-name">계열사명</Label>
						<Input
							id="affiliate-name"
							value={form.name}
							onChange={(event) => updateForm('name', event.target.value)}
							placeholder="예: Antflow Japan"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="affiliate-business-number">사업자등록번호</Label>
						<Input
							id="affiliate-business-number"
							value={form.businessRegistrationNumber}
							onChange={(event) => updateForm('businessRegistrationNumber', event.target.value)}
							placeholder="000-00-00000"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="affiliate-representative">대표자</Label>
						<Input
							id="affiliate-representative"
							value={form.representativeName}
							onChange={(event) => updateForm('representativeName', event.target.value)}
							placeholder="대표자명"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="affiliate-address">주소</Label>
						<Input
							id="affiliate-address"
							value={form.address}
							onChange={(event) => updateForm('address', event.target.value)}
							placeholder="법인 주소"
						/>
					</div>
					<div className="space-y-2 sm:col-span-2">
						<Label htmlFor="affiliate-memo">메모</Label>
						<Input
							id="affiliate-memo"
							value={form.memo}
							onChange={(event) => updateForm('memo', event.target.value)}
							placeholder="관리 메모"
						/>
					</div>
				</div>
				<Button type="submit" disabled={submitting}>
					<Plus className="size-4" />
					{submitting ? '추가 중...' : '계열사 추가'}
				</Button>
			</form>

			<div className="grid gap-4 border-t border-border pt-4 xl:grid-cols-[1fr_0.9fr]">
				<div className="space-y-3">
					<div className="space-y-2">
						<Label htmlFor="affiliate-assignment-target">배정할 계열사</Label>
						<select
							id="affiliate-assignment-target"
							value={effectiveSelectedAffiliateId}
							onChange={(event) => {
								setSelectedAffiliateId(event.target.value);
								setSelectedEmployeeIds([]);
							}}
							className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
						>
							<option value="">계열사 선택</option>
							{activeAffiliates.map((affiliate) => (
								<option key={affiliate.id} value={affiliate.id}>
									{affiliate.name}
								</option>
							))}
						</select>
					</div>

					{assignmentCandidates.length === 0 ? (
						<EmptyState message="배정 가능한 직원이 없습니다." />
					) : (
						<div className="max-h-64 space-y-2 overflow-auto pr-1">
							{assignmentCandidates.map((employee) => (
								<label
									key={employee.id}
									className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 text-sm hover:bg-muted/40"
								>
									<input
										type="checkbox"
										aria-label={`${employee.name} 계열사 배정 선택`}
										checked={selectedEmployeeIds.includes(employee.id)}
										onChange={() => toggleEmployee(employee.id)}
										className="size-4"
									/>
									<span className="min-w-0 flex-1">
										<span className="block font-semibold">{employee.name}</span>
										<span className="mt-1 block truncate text-xs text-muted-foreground">
											{employee.department ?? '부서 미지정'} ·{' '}
											{employee.affiliateCompanyId
												? (affiliateNameById.get(employee.affiliateCompanyId) ?? '계열사 확인 필요')
												: '계열사 미지정'}
										</span>
									</span>
								</label>
							))}
						</div>
					)}
					<Button
						type="button"
						onClick={() => void assignSelected()}
						disabled={
							assigning || !effectiveSelectedAffiliateId || assignmentCandidates.length === 0
						}
						className="w-full"
					>
						{assigning ? '배정 중...' : '선택 직원 계열사 배정'}
					</Button>
				</div>

				<div className="space-y-3">
					<div>
						<p className="text-sm font-semibold">배정 현황</p>
						<p className="mt-1 text-xs text-muted-foreground">
							계열사 배정 {numberLabel(assignedEmployees.length)}명
						</p>
					</div>
					{assignedEmployees.length === 0 ? (
						<EmptyState message="계열사에 배정된 직원이 없습니다." />
					) : (
						<div className="max-h-64 space-y-2 overflow-auto pr-1">
							{assignedEmployees.map((employee) => (
								<div
									key={employee.id}
									className="flex items-center justify-between gap-3 rounded-lg border border-border p-3"
								>
									<div className="min-w-0">
										<p className="truncate text-sm font-semibold">{employee.name}</p>
										<p className="mt-1 truncate text-xs text-muted-foreground">
											{affiliateNameById.get(employee.affiliateCompanyId ?? -1) ??
												'계열사 확인 필요'}
										</p>
									</div>
									<Button
										type="button"
										variant="ghost"
										aria-label={`${employee.name} 계열사 해제`}
										onClick={() => void clearAffiliate(employee.id)}
										disabled={clearingEmployeeId === employee.id}
										className="shrink-0"
									>
										<Trash2 className="size-4" />
										{clearingEmployeeId === employee.id ? '해제 중...' : '해제'}
									</Button>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

function organizationChangeDepartmentFlow(plan: OrganizationChangePlanResponse): string {
	const current = plan.currentDepartment ?? '부서 미지정';
	const target = plan.targetDepartment ?? current;
	return `${current} → ${target}`;
}

function organizationChangePositionFlow(plan: OrganizationChangePlanResponse): string {
	const current = plan.currentPosition ?? '직책 미지정';
	const target = plan.targetPosition ?? current;
	return `${current} → ${target}`;
}

function organizationChangeImpactVariant(
	level: OrganizationChangePlanResponse['impactLevel']
): 'outline' | 'secondary' | 'warning' {
	if (level === 'HIGH') return 'warning';
	if (level === 'MEDIUM') return 'secondary';
	return 'outline';
}

function organizationChangeReadinessVariant(
	status: OrganizationChangePlanResponse['readinessStatus']
): 'outline' | 'success' | 'warning' {
	if (status === 'READY' || status === 'APPLIED') return 'success';
	if (status === 'REVIEW_REQUIRED') return 'warning';
	return 'outline';
}

function OrganizationChangePlanPanel({
	changePlans,
	departments,
	employees,
	onDone
}: {
	changePlans: OrganizationChangePlanResponse[];
	departments: OrganizationDepartmentResponse[];
	employees: EmployeeResponse[];
	onDone: () => Promise<void>;
}) {
	const [employeeId, setEmployeeId] = useState('');
	const [targetDepartment, setTargetDepartment] = useState('');
	const [targetPosition, setTargetPosition] = useState('');
	const [effectiveDate, setEffectiveDate] = useState(() => dateInputValue(new Date()));
	const [reason, setReason] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [applying, setApplying] = useState(false);
	const [cancellingPlanId, setCancellingPlanId] = useState<number | null>(null);
	const flatDepartments = useMemo(() => flattenOrganizationDepartments(departments), [departments]);
	const activeEmployees = useMemo(
		() => employees.filter((employee) => employee.status === 'ACTIVE'),
		[employees]
	);

	async function handleCreate(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmedDepartment = targetDepartment.trim();
		const trimmedPosition = targetPosition.trim();
		if (!employeeId) {
			setError('발령 대상 직원을 선택해주세요.');
			return;
		}
		if (!trimmedDepartment && !trimmedPosition) {
			setError('대상 부서 또는 직책을 입력해주세요.');
			return;
		}
		setSubmitting(true);
		setError(null);
		setNotice(null);
		try {
			const request: CreateOrganizationChangePlanRequest = {
				employeeId: Number(employeeId),
				targetDepartment: trimmedDepartment || null,
				targetPosition: trimmedPosition || null,
				effectiveDate,
				reason: reason.trim() || null
			};
			await api.post<OrganizationChangePlanResponse>('/organization/change-plans', request);
			setTargetDepartment('');
			setTargetPosition('');
			setReason('');
			setNotice('발령 계획을 예약했습니다.');
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '발령 계획을 생성하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	async function applyDuePlans() {
		setApplying(true);
		setError(null);
		setNotice(null);
		try {
			const response = await api.post<ApplyOrganizationChangePlansResponse>(
				'/organization/change-plans/apply-due'
			);
			setNotice(`${numberLabel(response.appliedCount)}건의 도래 발령을 적용했습니다.`);
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '도래 발령을 적용하지 못했습니다.'));
		} finally {
			setApplying(false);
		}
	}

	async function cancelPlan(planId: number) {
		setCancellingPlanId(planId);
		setError(null);
		setNotice(null);
		try {
			await api.post<OrganizationChangePlanResponse>(`/organization/change-plans/${planId}/cancel`);
			setNotice('발령 계획을 취소했습니다.');
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '발령 계획을 취소하지 못했습니다.'));
		} finally {
			setCancellingPlanId(null);
		}
	}

	return (
		<div className="space-y-4">
			<FormError message={error} />
			{notice ? (
				<div className="rounded-lg bg-success/10 px-3 py-2 text-sm font-medium text-success">
					{notice}
				</div>
			) : null}
			<div className="flex items-center justify-between gap-3">
				<div>
					<p className="text-sm font-semibold">발령 예정</p>
					<p className="mt-1 text-xs text-muted-foreground">
						예정 {numberLabel(changePlans.length)}건 · 도래분은 직원 정보에 반영됩니다.
					</p>
					<div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
						<span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-1">
							<Clock3 className="size-3.5" />
							매일 00:10 자동 적용
						</span>
						<span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-1">
							<CalendarCheck className="size-3.5" />
							수동 버튼은 즉시 보정
						</span>
					</div>
				</div>
				<Button
					type="button"
					variant="outline"
					onClick={() => void applyDuePlans()}
					disabled={applying}
				>
					<CalendarCheck className="size-4" />
					{applying ? '적용 중...' : '도래 발령 적용'}
				</Button>
			</div>

			{changePlans.length === 0 ? (
				<EmptyState message="예정된 발령이 없습니다." />
			) : (
				<div className="max-h-72 space-y-2 overflow-auto pr-1">
					{changePlans.map((plan) => (
						<div key={plan.id} className="rounded-lg border border-border p-3">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="truncate text-sm font-semibold">{plan.employeeName}</p>
									<p className="mt-1 text-sm">{organizationChangeDepartmentFlow(plan)}</p>
									<p className="mt-1 text-xs text-muted-foreground">
										직책 {organizationChangePositionFlow(plan)}
									</p>
								</div>
								<Badge variant="warning">{plan.effectiveDate}</Badge>
							</div>
							<div className="mt-3 flex flex-wrap gap-2">
								<Badge variant={organizationChangeImpactVariant(plan.impactLevel)}>
									{plan.impactLabel}
								</Badge>
								<Badge variant={organizationChangeReadinessVariant(plan.readinessStatus)}>
									{plan.readinessLabel}
								</Badge>
							</div>
							<div className="mt-3 flex items-start gap-2 rounded-md bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
								<CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-success" />
								<span>{plan.actionLabel}</span>
							</div>
							{plan.reason ? (
								<p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{plan.reason}</p>
							) : null}
							<Button
								type="button"
								variant="ghost"
								onClick={() => void cancelPlan(plan.id)}
								disabled={cancellingPlanId === plan.id}
								className="mt-2 w-full"
							>
								<Trash2 className="size-4" />
								{cancellingPlanId === plan.id ? '취소 중...' : '예정 취소'}
							</Button>
						</div>
					))}
				</div>
			)}

			<form className="space-y-3 border-t border-border pt-4" onSubmit={handleCreate}>
				<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
					<div className="space-y-2">
						<Label htmlFor="organization-change-employee">직원</Label>
						<select
							id="organization-change-employee"
							value={employeeId}
							onChange={(event) => setEmployeeId(event.target.value)}
							className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
						>
							<option value="">직원 선택</option>
							{activeEmployees.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name} · {employee.department ?? '부서 미지정'}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="organization-change-department">대상 부서</Label>
						<select
							id="organization-change-department"
							value={targetDepartment}
							onChange={(event) => setTargetDepartment(event.target.value)}
							className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
						>
							<option value="">부서 변경 없음</option>
							{flatDepartments.map((department) => (
								<option key={department.id} value={department.name}>
									{department.name}
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="organization-change-position">대상 직책</Label>
						<Input
							id="organization-change-position"
							value={targetPosition}
							onChange={(event) => setTargetPosition(event.target.value)}
							placeholder="예: 리드"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="organization-change-effective-date">발령일</Label>
						<Input
							id="organization-change-effective-date"
							type="date"
							value={effectiveDate}
							onChange={(event) => setEffectiveDate(event.target.value)}
						/>
					</div>
					<div className="space-y-2 sm:col-span-2 xl:col-span-1">
						<Label htmlFor="organization-change-reason">사유</Label>
						<Input
							id="organization-change-reason"
							value={reason}
							onChange={(event) => setReason(event.target.value)}
							placeholder="예: 팀 개편"
						/>
					</div>
				</div>
				<Button type="submit" disabled={submitting} className="w-full">
					<Plus className="size-4" />
					{submitting ? '예약 중...' : '발령 예약'}
				</Button>
			</form>
		</div>
	);
}

export function OrganizationPage() {
	const [state, setState] = useState<RequestState<OrganizationPageData>>({ status: 'idle' });
	const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);

	const load = useMemo(
		() => async () => {
			setState((current) => (current.status === 'success' ? current : { status: 'loading' }));
			try {
				const [departments, employeePage, affiliates, changePlans] = await Promise.all([
					api.get<OrganizationDepartmentResponse[]>('/organization/departments'),
					api.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200'),
					api.get<CompanyAffiliateResponse[]>('/companies/affiliates'),
					api.get<OrganizationChangePlanResponse[]>('/organization/change-plans?status=PLANNED')
				]);
				setState({
					status: 'success',
					data: { departments, employees: employeePage.content, affiliates, changePlans }
				});
				const flatDepartments = flattenOrganizationDepartments(departments);
				setSelectedDepartmentId((current) => current ?? flatDepartments[0]?.id ?? null);
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const flatDepartments = useMemo(
		() => (data ? flattenOrganizationDepartments(data.departments) : []),
		[data]
	);
	const selectedDepartment =
		data && selectedDepartmentId
			? findOrganizationDepartment(data.departments, selectedDepartmentId)
			: null;
	const totalEmployees = data?.employees.length ?? 0;
	const assignedEmployees = data?.employees.filter((employee) => employee.department).length ?? 0;
	const affiliateAssignedEmployees =
		data?.employees.filter((employee) => employee.affiliateCompanyId != null).length ?? 0;
	const managerCount = flatDepartments.filter((department) => department.managerEmployeeId).length;
	const affiliateCount = data?.affiliates.length ?? 0;
	const plannedChangeCount = data?.changePlans.length ?? 0;

	return (
		<div className="space-y-6">
			<PageHeader
				title="조직"
				description="부서 구조, 관리자, 직원 배정을 한 화면에서 관리합니다."
				actions={
					<Button variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
						<EmployeeStatCard
							title="부서"
							value={flatDepartments.length}
							description="등록된 조직 단위"
						/>
						<EmployeeStatCard
							title="부서 배정"
							value={assignedEmployees}
							description={`전체 ${numberLabel(totalEmployees)}명 중`}
						/>
						<EmployeeStatCard title="관리자" value={managerCount} description="책임자 지정 부서" />
						<EmployeeStatCard
							title="계열사"
							value={affiliateCount}
							description="연결된 법인 단위"
						/>
						<EmployeeStatCard
							title="계열 배정"
							value={affiliateAssignedEmployees}
							description={`전체 ${numberLabel(totalEmployees)}명 중`}
						/>
						<EmployeeStatCard
							title="발령 예정"
							value={plannedChangeCount}
							description="적용 대기 변경"
						/>
					</div>

					<div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
						<Card>
							<CardHeader>
								<CardTitle>조직도</CardTitle>
								<CardDescription>상위/하위 부서와 직원을 함께 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<OrganizationDepartmentTree
									departments={data.departments}
									selectedDepartmentId={selectedDepartmentId}
									onSelect={setSelectedDepartmentId}
								/>
							</CardContent>
						</Card>

						<div className="space-y-4">
							<Card>
								<CardHeader>
									<CardTitle>부서 생성</CardTitle>
									<CardDescription>기존 직원 부서명과 연결되는 조직 단위입니다.</CardDescription>
								</CardHeader>
								<CardContent>
									<OrganizationCreateForm
										departments={data.departments}
										employees={data.employees}
										onDone={load}
									/>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>직원 배정</CardTitle>
									<CardDescription>선택한 부서로 직원을 이동합니다.</CardDescription>
								</CardHeader>
								<CardContent>
									<OrganizationAssignmentPanel
										key={selectedDepartment?.id ?? 'none'}
										department={selectedDepartment}
										employees={data.employees}
										onDone={load}
									/>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>계열사 관리</CardTitle>
									<CardDescription>
										법인 단위 정보를 등록하고 구성원 소속을 전환합니다.
									</CardDescription>
								</CardHeader>
								<CardContent>
									<CompanyAffiliatePanel
										affiliates={data.affiliates}
										employees={data.employees}
										onDone={load}
									/>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>발령 예정</CardTitle>
									<CardDescription>미래 부서/직책 변경을 예약하고 적용합니다.</CardDescription>
								</CardHeader>
								<CardContent>
									<OrganizationChangePlanPanel
										changePlans={data.changePlans}
										departments={data.departments}
										employees={data.employees}
										onDone={load}
									/>
								</CardContent>
							</Card>
						</div>
					</div>
				</>
			) : null}
		</div>
	);
}

type GoalsPageData = {
	summary: PerformanceGoalSummaryResponse;
	operations: PerformanceGoalOperationsSummaryResponse | null;
	goals: PerformanceGoalResponse[];
	approvalRequests: PerformanceGoalApprovalRequestResponse[];
	employees: EmployeeResponse[];
};

const goalStatusLabels: Record<PerformanceGoalStatus, string> = {
	DRAFT: '초안',
	ACTIVE: '진행중',
	COMPLETED: '완료',
	CANCELED: '취소'
};

const goalScopeLabels: Record<PerformanceGoalScope, string> = {
	COMPANY: '전사',
	DEPARTMENT: '부서',
	EMPLOYEE: '개인'
};

const goalApprovalTypeLabels: Record<PerformanceGoalApprovalRequestType, string> = {
	CREATE: '생성',
	UPDATE: '변경'
};

const goalApprovalStatusLabels: Record<PerformanceGoalApprovalStatus, string> = {
	PENDING: '대기',
	APPROVED: '승인',
	REJECTED: '반려'
};

function goalStatusVariant(
	status: PerformanceGoalStatus
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'ACTIVE') return 'success';
	if (status === 'DRAFT') return 'warning';
	if (status === 'CANCELED') return 'destructive';
	return 'outline';
}

function goalApprovalStatusVariant(
	status: PerformanceGoalApprovalStatus
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'PENDING') return 'warning';
	if (status === 'APPROVED') return 'success';
	if (status === 'REJECTED') return 'destructive';
	return 'outline';
}

function boundedPercent(value: number | null | undefined): number {
	if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
	return Math.max(0, Math.min(100, Math.round(value)));
}

function goalPercentLabel(value: number | null | undefined): string {
	return `${numberLabel(boundedPercent(value))}%`;
}

function goalOwnerLabel(goal: PerformanceGoalResponse): string {
	if (goal.ownerEmployeeName) return goal.ownerEmployeeName;
	if (goal.department) return goal.department;
	return goalScopeLabels[goal.scope];
}

function dateInputValue(date: Date): string {
	return date.toISOString().slice(0, 10);
}

function defaultGoalEndDate(): string {
	const date = new Date();
	date.setDate(date.getDate() + 90);
	return dateInputValue(date);
}

function GoalsStatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<div className="flex items-start justify-between gap-3">
					<div>
						<p className="text-sm font-medium text-muted-foreground">{title}</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">{value}</p>
					</div>
					<span className="rounded-md bg-primary/10 p-2 text-primary">
						<Icon className="size-4" />
					</span>
				</div>
				<p className="mt-2 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function GoalProgressBar({ progress }: { progress: number }) {
	const bounded = boundedPercent(progress);
	return (
		<div className="h-2 overflow-hidden rounded-full bg-muted">
			<div
				className={cn(
					'h-full rounded-full transition-all',
					bounded >= 80 ? 'bg-success' : bounded >= 45 ? 'bg-primary' : 'bg-warning'
				)}
				style={{ width: `${bounded}%` }}
			/>
		</div>
	);
}

function goalOperationsChecklistVariant(
	status: PerformanceGoalOperationsChecklistStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function goalOperationsChecklistStatusLabel(
	status: PerformanceGoalOperationsChecklistStatus
): string {
	if (status === 'READY') return '완료';
	if (status === 'WARNING') return '주의';
	if (status === 'WAITING') return '대기';
	return '-';
}

function goalOperationsRiskVariant(
	severity: PerformanceGoalOperationsRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function GoalOperationsChecklistCard({ item }: { item: PerformanceGoalOperationsChecklistItem }) {
	const Icon =
		item.status === 'WARNING' ? AlertTriangle : item.status === 'READY' ? CheckCircle2 : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={goalOperationsChecklistVariant(item.status)}>
					{goalOperationsChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function GoalOperationsRisks({ risks }: { risks: PerformanceGoalOperationsRiskItem[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">운영 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={goalOperationsRiskVariant(risk.severity)}>
							{numberLabel(risk.count)}개
						</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function GoalOperationsScopes({ scopes }: { scopes: PerformanceGoalOperationsScopeInsight[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<GitBranch className="size-4 text-muted-foreground" />
				<p className="font-semibold">목표 범위</p>
			</div>
			<div className="mt-4 space-y-3">
				{scopes.map((scope) => (
					<div key={scope.scope} className="space-y-2">
						<div className="flex items-start justify-between gap-3">
							<div>
								<p className="font-medium">{scope.label}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{numberLabel(scope.goalCount)}개 · 지연 {numberLabel(scope.atRiskGoalCount)}개
								</p>
							</div>
							<Badge variant="outline">{goalPercentLabel(scope.averageProgressPercent)}</Badge>
						</div>
						<GoalProgressBar progress={scope.averageProgressPercent} />
					</div>
				))}
			</div>
		</div>
	);
}

function GoalOperationsOwners({ owners }: { owners: PerformanceGoalOperationsOwnerInsight[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<UsersRound className="size-4 text-muted-foreground" />
				<p className="font-semibold">담당자 체크인</p>
			</div>
			<div className="mt-4 space-y-3">
				{owners.length > 0 ? (
					owners.map((owner) => (
						<div
							key={owner.ownerEmployeeId ?? owner.ownerName ?? 'unknown'}
							className="flex items-start justify-between gap-3"
						>
							<div className="min-w-0">
								<p className="font-medium">{owner.ownerName ?? '담당자 미지정'}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									목표 {numberLabel(owner.goalCount)}개 · 마감 {dateLabel(owner.nearestDueDate)}
								</p>
							</div>
							<Badge variant={owner.atRiskGoalCount > 0 ? 'warning' : 'success'}>
								{goalPercentLabel(owner.averageProgressPercent)}
							</Badge>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">담당자가 지정된 목표가 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function GoalOperationsPanel({
	operations
}: {
	operations: PerformanceGoalOperationsSummaryResponse;
}) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>목표 운영</CardTitle>
				<CardDescription>
					목표 정렬, 핵심결과 진척, 체크인 지연과 평가 연계 준비 상태를 함께 점검합니다.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">정렬 커버리지</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{goalPercentLabel(operations.alignmentCoveragePercent)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							KR 연결 {numberLabel(operations.goalsWithKeyResultsCount)}개
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">핵심결과</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.keyResultCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							평균 {goalPercentLabel(operations.averageProgressPercent)}
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">지연 목표</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.atRiskGoalCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							체크인 지연 {numberLabel(operations.staleCheckInGoalCount)}개
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">마감 임박</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.dueSoonGoalCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							초안 {numberLabel(operations.draftGoalCount)}개
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">평가 근거</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.reviewReadyGoalCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							완료 {numberLabel(operations.completedGoalCount)}개
						</p>
					</div>
				</div>

				<div className="grid gap-3 lg:grid-cols-2">
					{operations.checklist.map((item) => (
						<GoalOperationsChecklistCard key={item.code} item={item} />
					))}
				</div>

				<div className="grid gap-4 xl:grid-cols-3">
					<GoalOperationsRisks risks={operations.riskItems} />
					<GoalOperationsScopes scopes={operations.scopeInsights} />
					<GoalOperationsOwners owners={operations.ownerInsights} />
				</div>

				<div className="flex flex-wrap gap-2 rounded-lg border border-border bg-muted/20 p-4">
					<Badge variant="outline">
						<Target className="mr-1 size-3" />
						전사 {numberLabel(operations.companyGoalCount)}
					</Badge>
					<Badge variant="outline">
						<Building2 className="mr-1 size-3" />
						부서 {numberLabel(operations.departmentGoalCount)}
					</Badge>
					<Badge variant="outline">
						<UsersRound className="mr-1 size-3" />
						개인 {numberLabel(operations.employeeGoalCount)}
					</Badge>
					<Badge variant={operations.draftGoalCount > 0 ? 'warning' : 'success'}>
						<ClipboardCheck className="mr-1 size-3" />
						승인 검토 {numberLabel(operations.draftGoalCount)}
					</Badge>
				</div>
			</CardContent>
		</Card>
	);
}

function GoalCreateForm({
	employees,
	onDone
}: {
	employees: EmployeeResponse[];
	onDone: () => Promise<void>;
}) {
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [scope, setScope] = useState<PerformanceGoalScope>('COMPANY');
	const [ownerEmployeeId, setOwnerEmployeeId] = useState('');
	const [department, setDepartment] = useState('');
	const [periodStart, setPeriodStart] = useState(() => dateInputValue(new Date()));
	const [periodEnd, setPeriodEnd] = useState(defaultGoalEndDate);
	const [keyResultTitle, setKeyResultTitle] = useState('');
	const [targetValue, setTargetValue] = useState('');
	const [currentValue, setCurrentValue] = useState('0');
	const [unit, setUnit] = useState('%');
	const [approvalReason, setApprovalReason] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		await submitGoal('direct');
	}

	async function submitGoal(mode: 'direct' | 'approval') {
		const trimmedTitle = title.trim();
		const trimmedKeyResultTitle = keyResultTitle.trim();
		const parsedTarget = Number(targetValue);
		const parsedCurrent = Number(currentValue || '0');
		const trimmedApprovalReason = approvalReason.trim();

		if (!trimmedTitle) {
			setError('목표명을 입력해주세요.');
			return;
		}
		if (scope === 'EMPLOYEE' && !ownerEmployeeId) {
			setError('개인 목표는 담당 직원을 선택해주세요.');
			return;
		}
		if (scope === 'DEPARTMENT' && !department.trim()) {
			setError('부서 목표는 부서명을 입력해주세요.');
			return;
		}
		if (periodEnd < periodStart) {
			setError('목표 종료일은 시작일 이후여야 합니다.');
			return;
		}
		if (trimmedKeyResultTitle && (!Number.isFinite(parsedTarget) || parsedTarget <= 0)) {
			setError('핵심결과 목표값은 0보다 커야 합니다.');
			return;
		}
		if (Number.isFinite(parsedCurrent) && parsedCurrent < 0) {
			setError('핵심결과 현재값은 0 이상이어야 합니다.');
			return;
		}
		if (mode === 'approval' && !trimmedApprovalReason) {
			setError('승인 요청 사유를 입력해주세요.');
			return;
		}

		setSubmitting(true);
		setError(null);
		try {
			const payload: CreatePerformanceGoalRequest = {
				title: trimmedTitle,
				description: description.trim() || null,
				scope,
				ownerEmployeeId: ownerEmployeeId ? Number(ownerEmployeeId) : null,
				department: department.trim() || null,
				periodStart,
				periodEnd,
				status: 'ACTIVE',
				keyResults: trimmedKeyResultTitle
					? [
							{
								title: trimmedKeyResultTitle,
								targetValue: parsedTarget,
								currentValue: Number.isFinite(parsedCurrent) ? parsedCurrent : 0,
								unit: unit.trim() || null,
								sortOrder: 1
							}
						]
					: []
			};
			if (mode === 'approval') {
				const approvalPayload: CreatePerformanceGoalApprovalRequest = {
					...payload,
					requestType: 'CREATE',
					reason: trimmedApprovalReason
				};
				await api.post('/performance/goals/approval-requests', approvalPayload);
			} else {
				await api.post<PerformanceGoalResponse>('/performance/goals', payload);
			}
			setTitle('');
			setDescription('');
			setScope('COMPANY');
			setOwnerEmployeeId('');
			setDepartment('');
			setKeyResultTitle('');
			setTargetValue('');
			setCurrentValue('0');
			setUnit('%');
			setApprovalReason('');
			await onDone();
		} catch (submitError) {
			setError(
				getErrorMessage(
					submitError,
					mode === 'approval'
						? '목표 승인 요청을 만들지 못했습니다.'
						: '목표를 생성하지 못했습니다.'
				)
			);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form className="space-y-4" onSubmit={handleSubmit}>
			<FormError message={error} />
			<div className="space-y-2">
				<Label htmlFor="goal-title">목표명</Label>
				<Input
					id="goal-title"
					value={title}
					onChange={(event) => setTitle(event.target.value)}
					placeholder="예: 하반기 채용 전환율 개선"
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="goal-description">설명</Label>
				<Input
					id="goal-description"
					value={description}
					onChange={(event) => setDescription(event.target.value)}
					placeholder="목표 배경이나 판단 기준"
				/>
			</div>
			<div className="grid gap-3 sm:grid-cols-2">
				<div className="space-y-2">
					<Label htmlFor="goal-scope">범위</Label>
					<select
						id="goal-scope"
						value={scope}
						onChange={(event) => setScope(event.target.value as PerformanceGoalScope)}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="COMPANY">전사</option>
						<option value="DEPARTMENT">부서</option>
						<option value="EMPLOYEE">개인</option>
					</select>
				</div>
				<div className="space-y-2">
					<Label htmlFor="goal-owner">담당자</Label>
					<select
						id="goal-owner"
						value={ownerEmployeeId}
						onChange={(event) => setOwnerEmployeeId(event.target.value)}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="">담당자 미지정</option>
						{employees
							.filter((employee) => employee.status === 'ACTIVE')
							.map((employee) => (
								<option key={employee.id} value={employee.id}>
									{employee.name}
								</option>
							))}
					</select>
				</div>
				<div className="space-y-2">
					<Label htmlFor="goal-department">부서</Label>
					<Input
						id="goal-department"
						value={department}
						onChange={(event) => setDepartment(event.target.value)}
						placeholder="예: 성장"
					/>
				</div>
				<div className="grid grid-cols-2 gap-2">
					<div className="space-y-2">
						<Label htmlFor="goal-start">시작일</Label>
						<Input
							id="goal-start"
							type="date"
							value={periodStart}
							onChange={(event) => setPeriodStart(event.target.value)}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="goal-end">종료일</Label>
						<Input
							id="goal-end"
							type="date"
							value={periodEnd}
							onChange={(event) => setPeriodEnd(event.target.value)}
						/>
					</div>
				</div>
			</div>
			<div className="rounded-lg border border-border bg-muted/20 p-3">
				<p className="text-xs font-semibold text-muted-foreground">첫 핵심결과</p>
				<div className="mt-3 grid gap-3 sm:grid-cols-[1fr_7rem_7rem_5rem]">
					<Input
						value={keyResultTitle}
						onChange={(event) => setKeyResultTitle(event.target.value)}
						placeholder="예: 지원자 전환율"
						aria-label="핵심결과명"
					/>
					<Input
						value={targetValue}
						onChange={(event) => setTargetValue(event.target.value)}
						type="number"
						min="0"
						step="0.01"
						placeholder="목표값"
						aria-label="핵심결과 목표값"
					/>
					<Input
						value={currentValue}
						onChange={(event) => setCurrentValue(event.target.value)}
						type="number"
						min="0"
						step="0.01"
						placeholder="현재값"
						aria-label="핵심결과 현재값"
					/>
					<Input
						value={unit}
						onChange={(event) => setUnit(event.target.value)}
						placeholder="단위"
						aria-label="핵심결과 단위"
					/>
				</div>
			</div>
			<div className="space-y-2">
				<Label htmlFor="goal-approval-reason">승인 요청 사유</Label>
				<Input
					id="goal-approval-reason"
					value={approvalReason}
					onChange={(event) => setApprovalReason(event.target.value)}
					placeholder="예: 조직장 확인 후 목표를 시작합니다"
				/>
			</div>
			<div className="grid gap-2 sm:grid-cols-2">
				<Button type="submit" disabled={submitting} variant="outline">
					<Plus className="size-4" />
					{submitting ? '처리 중...' : '목표 만들기'}
				</Button>
				<Button type="button" disabled={submitting} onClick={() => void submitGoal('approval')}>
					<ClipboardCheck className="size-4" />
					{submitting ? '처리 중...' : '승인 요청 만들기'}
				</Button>
			</div>
		</form>
	);
}

function GoalApprovalQueue({
	requests,
	onDone
}: {
	requests: PerformanceGoalApprovalRequestResponse[];
	onDone: () => Promise<void>;
}) {
	const [comments, setComments] = useState<Record<number, string>>({});
	const [submittingId, setSubmittingId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function decide(requestId: number, action: 'approve' | 'reject') {
		setSubmittingId(requestId);
		setError(null);
		try {
			await api.post(`/performance/goals/approval-requests/${requestId}/${action}`, {
				decisionComment: comments[requestId]?.trim() ?? ''
			});
			setComments((current) => ({ ...current, [requestId]: '' }));
			await onDone();
		} catch (decisionError) {
			setError(
				getErrorMessage(
					decisionError,
					action === 'approve'
						? '목표 승인 요청을 승인하지 못했습니다.'
						: '목표 승인 요청을 반려하지 못했습니다.'
				)
			);
		} finally {
			setSubmittingId(null);
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>목표 승인 큐</CardTitle>
				<CardDescription>
					생성/변경 요청을 검토하고 승인 결과를 실제 목표에 반영합니다.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<FormError message={error} />
				{requests.length === 0 ? (
					<EmptyState message="승인 대기 목표 요청이 없습니다." />
				) : (
					<div className="space-y-3">
						{requests.map((request) => {
							const pending = request.status === 'PENDING';
							return (
								<div key={request.id} className="rounded-lg border border-border bg-background p-4">
									<div className="flex flex-wrap items-start justify-between gap-3">
										<div className="min-w-0">
											<div className="flex flex-wrap items-center gap-2">
												<Badge variant="outline">
													{goalApprovalTypeLabels[request.requestType]}
												</Badge>
												<Badge variant={goalApprovalStatusVariant(request.status)}>
													{goalApprovalStatusLabels[request.status]}
												</Badge>
												<Badge variant="outline">{goalScopeLabels[request.scope]}</Badge>
											</div>
											<p className="mt-3 truncate font-semibold">{request.title}</p>
											<p className="mt-1 text-sm text-muted-foreground">
												{dateLabel(request.periodStart)} - {dateLabel(request.periodEnd)}
											</p>
										</div>
										{request.createdAt ? (
											<p className="shrink-0 text-xs text-muted-foreground">
												요청 {dateLabel(request.createdAt)}
											</p>
										) : null}
									</div>
									{request.reason ? (
										<p className="mt-3 rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
											{request.reason}
										</p>
									) : null}
									{request.keyResults.length > 0 ? (
										<div className="mt-3 flex flex-wrap gap-2">
											{request.keyResults.slice(0, 3).map((keyResult) => (
												<Badge
													key={`${request.id}-${keyResult.sortOrder}-${keyResult.title}`}
													variant="outline"
												>
													{keyResult.title} {numberLabel(keyResult.targetValue)}
													{keyResult.unit ?? ''}
												</Badge>
											))}
										</div>
									) : null}
									{request.decisionComment ? (
										<p className="mt-3 text-sm text-muted-foreground">
											처리 의견: {request.decisionComment}
										</p>
									) : null}
									{pending ? (
										<div className="mt-4 grid gap-2 sm:grid-cols-[1fr_auto_auto]">
											<Input
												value={comments[request.id] ?? ''}
												onChange={(event) =>
													setComments((current) => ({
														...current,
														[request.id]: event.target.value
													}))
												}
												placeholder="처리 의견"
												aria-label={`${request.title} 처리 의견`}
											/>
											<Button
												type="button"
												variant="outline"
												disabled={submittingId === request.id}
												onClick={() => void decide(request.id, 'reject')}
											>
												{submittingId === request.id ? '처리 중...' : '반려'}
											</Button>
											<Button
												type="button"
												disabled={submittingId === request.id}
												onClick={() => void decide(request.id, 'approve')}
											>
												{submittingId === request.id ? '처리 중...' : '승인'}
											</Button>
										</div>
									) : null}
								</div>
							);
						})}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function GoalList({
	goals,
	selectedGoalId,
	onSelect
}: {
	goals: PerformanceGoalResponse[];
	selectedGoalId: number | null;
	onSelect: (goalId: number) => void;
}) {
	if (goals.length === 0) return <EmptyState message="등록된 목표가 없습니다." />;

	return (
		<div className="space-y-3">
			{goals.map((goal) => {
				const selected = goal.id === selectedGoalId;
				return (
					<button
						key={goal.id}
						type="button"
						onClick={() => onSelect(goal.id)}
						className={cn(
							'w-full rounded-lg border p-4 text-left transition-colors',
							selected ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-muted/40'
						)}
					>
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="truncate font-semibold">{goal.title}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{goalOwnerLabel(goal)} · {dateLabel(goal.periodStart)} -{' '}
									{dateLabel(goal.periodEnd)}
								</p>
							</div>
							<div className="flex shrink-0 items-center gap-2">
								<Badge variant="outline">{goalScopeLabels[goal.scope]}</Badge>
								<Badge variant={goalStatusVariant(goal.status)}>
									{goalStatusLabels[goal.status]}
								</Badge>
							</div>
						</div>
						<div className="mt-4 space-y-2">
							<div className="flex items-center justify-between text-xs text-muted-foreground">
								<span>진행률</span>
								<span className="font-semibold text-foreground">
									{goalPercentLabel(goal.progressPercent)}
								</span>
							</div>
							<GoalProgressBar progress={goal.progressPercent} />
						</div>
					</button>
				);
			})}
		</div>
	);
}

function GoalProgressPanel({
	goal,
	onDone
}: {
	goal: PerformanceGoalResponse | null;
	onDone: () => Promise<void>;
}) {
	const [values, setValues] = useState<Record<number, string>>(() =>
		Object.fromEntries((goal?.keyResults ?? []).map((item) => [item.id, String(item.currentValue)]))
	);
	const [error, setError] = useState<string | null>(null);
	const [submittingId, setSubmittingId] = useState<number | null>(null);

	if (!goal) return <EmptyState message="목표를 선택하면 핵심결과를 업데이트할 수 있습니다." />;
	const selectedGoal = goal;

	async function updateProgress(keyResultId: number) {
		const currentValue = Number(values[keyResultId] ?? '');
		if (!Number.isFinite(currentValue) || currentValue < 0) {
			setError('현재값은 0 이상 숫자로 입력해주세요.');
			return;
		}
		setSubmittingId(keyResultId);
		setError(null);
		try {
			await api.patch<PerformanceGoalResponse>(
				`/performance/goals/${selectedGoal.id}/key-results/${keyResultId}/progress`,
				{ currentValue }
			);
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '진행률을 수정하지 못했습니다.'));
		} finally {
			setSubmittingId(null);
		}
	}

	return (
		<div className="space-y-4">
			<FormError message={error} />
			<div className="rounded-lg border border-border bg-muted/20 p-4">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<p className="font-semibold">{selectedGoal.title}</p>
						<p className="mt-1 text-sm text-muted-foreground">
							{goalOwnerLabel(selectedGoal)} · {goalScopeLabels[selectedGoal.scope]}
						</p>
					</div>
					<span className="text-lg font-semibold">
						{goalPercentLabel(selectedGoal.progressPercent)}
					</span>
				</div>
				<div className="mt-3">
					<GoalProgressBar progress={selectedGoal.progressPercent} />
				</div>
			</div>
			{selectedGoal.keyResults.length === 0 ? (
				<EmptyState message="등록된 핵심결과가 없습니다." />
			) : (
				<div className="space-y-3">
					{selectedGoal.keyResults.map((keyResult) => (
						<div key={keyResult.id} className="rounded-lg border border-border p-3">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="truncate text-sm font-semibold">{keyResult.title}</p>
									<p className="mt-1 text-xs text-muted-foreground">
										목표 {numberLabel(keyResult.targetValue)}
										{keyResult.unit ?? ''}
									</p>
								</div>
								<Badge variant={keyResult.progressPercent >= 80 ? 'success' : 'outline'}>
									{goalPercentLabel(keyResult.progressPercent)}
								</Badge>
							</div>
							<div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
								<Input
									value={values[keyResult.id] ?? ''}
									onChange={(event) =>
										setValues((current) => ({
											...current,
											[keyResult.id]: event.target.value
										}))
									}
									type="number"
									min="0"
									step="0.01"
									aria-label={`${keyResult.title} 현재값`}
								/>
								<Button
									type="button"
									variant="outline"
									onClick={() => void updateProgress(keyResult.id)}
									disabled={submittingId === keyResult.id}
								>
									{submittingId === keyResult.id ? '저장 중...' : '진행률 저장'}
								</Button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

function GoalCommentsPanel({ goal }: { goal: PerformanceGoalResponse | null }) {
	const [commentsState, setCommentsState] = useState<
		RequestState<PerformanceGoalCommentResponse[]>
	>({ status: 'idle' });
	const [content, setContent] = useState('');
	const [attachmentName, setAttachmentName] = useState('');
	const [attachmentUrl, setAttachmentUrl] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const loadComments = useMemo(
		() => async () => {
			if (!goal) {
				setCommentsState({ status: 'idle' });
				return;
			}
			setCommentsState({ status: 'loading' });
			try {
				const comments = await api.get<PerformanceGoalCommentResponse[]>(
					`/performance/goals/${goal.id}/comments`
				);
				setCommentsState({ status: 'success', data: comments });
			} catch (loadError) {
				setCommentsState({
					status: 'error',
					message: getErrorMessage(loadError, '목표 코멘트를 불러오지 못했습니다.')
				});
			}
		},
		[goal]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await loadComments();
		});
		return () => {
			active = false;
		};
	}, [loadComments]);

	if (!goal) return <EmptyState message="목표를 선택하면 코멘트를 확인할 수 있습니다." />;

	const selectedGoal = goal;
	const comments = commentsState.status === 'success' ? commentsState.data : [];

	async function submitComment(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const normalizedContent = content.trim();
		const normalizedAttachmentName = attachmentName.trim();
		const normalizedAttachmentUrl = attachmentUrl.trim();
		if (!normalizedContent) {
			setError('코멘트 내용을 입력해주세요.');
			return;
		}
		if (
			normalizedAttachmentUrl &&
			!normalizedAttachmentUrl.startsWith('https://') &&
			!normalizedAttachmentUrl.startsWith('http://')
		) {
			setError('첨부 링크는 http 또는 https 주소여야 합니다.');
			return;
		}

		const request: CreatePerformanceGoalCommentRequest = {
			content: normalizedContent,
			attachmentName: normalizedAttachmentName || undefined,
			attachmentUrl: normalizedAttachmentUrl || undefined,
			attachmentContentType: undefined
		};

		setSubmitting(true);
		setError(null);
		try {
			await api.post(`/performance/goals/${selectedGoal.id}/comments`, request);
			setContent('');
			setAttachmentName('');
			setAttachmentUrl('');
			await loadComments();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '목표 코멘트를 저장하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="space-y-4">
			<FormError message={error} />
			<form aria-label="목표 코멘트 작성" className="space-y-3" onSubmit={submitComment}>
				<div className="space-y-2">
					<Label htmlFor="goal-comment-content">코멘트</Label>
					<textarea
						id="goal-comment-content"
						value={content}
						onChange={(event) => setContent(event.target.value)}
						rows={3}
						placeholder="진행 상황, 의사결정, 참고 링크를 기록"
						className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
					/>
				</div>
				<div className="grid gap-3 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="goal-comment-attachment-name">첨부 파일명</Label>
						<Input
							id="goal-comment-attachment-name"
							value={attachmentName}
							onChange={(event) => setAttachmentName(event.target.value)}
							placeholder="예: review.pdf"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="goal-comment-attachment-url">첨부 링크</Label>
						<Input
							id="goal-comment-attachment-url"
							value={attachmentUrl}
							onChange={(event) => setAttachmentUrl(event.target.value)}
							placeholder="https://..."
						/>
					</div>
				</div>
				<Button type="submit" disabled={submitting} className="w-full sm:w-auto">
					{submitting ? '저장 중...' : '코멘트 저장'}
				</Button>
			</form>

			{commentsState.status === 'loading' || commentsState.status === 'idle' ? (
				<div className="flex min-h-24 items-center justify-center rounded-lg border border-border">
					<Spinner />
				</div>
			) : null}

			{commentsState.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{commentsState.message}
				</div>
			) : null}

			{commentsState.status === 'success' ? (
				comments.length === 0 ? (
					<EmptyState message="아직 등록된 목표 코멘트가 없습니다." />
				) : (
					<div className="space-y-3">
						{comments.map((comment) => (
							<div key={comment.id} className="rounded-lg border border-border p-4">
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<p className="font-semibold">
											{comment.authorEmployeeName ?? `직원 #${comment.authorEmployeeId}`}
										</p>
										<p className="mt-1 text-xs text-muted-foreground">
											{dateTimeLabel(comment.createdAt)}
										</p>
									</div>
								</div>
								<p className="mt-3 whitespace-pre-wrap text-sm">{comment.content}</p>
								{comment.attachmentUrl ? (
									<a
										href={comment.attachmentUrl}
										target="_blank"
										rel="noreferrer"
										className="mt-3 inline-flex max-w-full items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted"
									>
										<FileText className="size-4 shrink-0" />
										<span className="truncate">
											{comment.attachmentName ?? comment.attachmentUrl}
										</span>
									</a>
								) : null}
							</div>
						))}
					</div>
				)
			) : null}
		</div>
	);
}

export function GoalsPage() {
	const [state, setState] = useState<RequestState<GoalsPageData>>({ status: 'idle' });
	const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [summary, operations, goals, approvalRequests, employeePage] = await Promise.all([
					api.get<PerformanceGoalSummaryResponse>('/performance/goals/summary'),
					api
						.get<PerformanceGoalOperationsSummaryResponse>('/performance/goals/operations/summary')
						.catch(() => null),
					api.get<PerformanceGoalResponse[]>('/performance/goals'),
					api.get<PerformanceGoalApprovalRequestResponse[]>('/performance/goals/approval-requests'),
					api.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200')
				]);
				setState({
					status: 'success',
					data: { summary, operations, goals, approvalRequests, employees: employeePage.content }
				});
				setSelectedGoalId((current) =>
					goals.some((goal) => goal.id === current) ? current : (goals[0]?.id ?? null)
				);
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const selectedGoal = data?.goals.find((goal) => goal.id === selectedGoalId) ?? null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="목표"
				description="회사 목표와 핵심결과 진행률을 연결해 성과 리스크를 조기에 확인합니다."
				actions={
					<Button variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<GoalsStatCard
							title="전체 목표"
							value={`${numberLabel(data.summary.totalGoals)}개`}
							description={`진행중 ${numberLabel(data.summary.activeGoals)}개`}
							icon={Target}
						/>
						<GoalsStatCard
							title="평균 진행률"
							value={goalPercentLabel(data.summary.averageProgressPercent)}
							description="핵심결과 기준 평균"
							icon={TrendingUp}
						/>
						<GoalsStatCard
							title="리스크 목표"
							value={`${numberLabel(data.summary.atRiskGoals)}개`}
							description="기간 대비 진행률 낮음"
							icon={AlertTriangle}
						/>
						<GoalsStatCard
							title="완료"
							value={`${numberLabel(data.summary.completedGoals)}개`}
							description="완료 처리된 목표"
							icon={CheckCircle2}
						/>
					</div>

					{data.operations ? <GoalOperationsPanel operations={data.operations} /> : null}

					<GoalApprovalQueue requests={data.approvalRequests} onDone={load} />

					<div className="grid gap-4 xl:grid-cols-[1fr_0.95fr]">
						<Card>
							<CardHeader>
								<CardTitle>목표 현황</CardTitle>
								<CardDescription>목표별 범위, 담당자, 기간, 진행률을 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<GoalList
									goals={data.goals}
									selectedGoalId={selectedGoalId}
									onSelect={setSelectedGoalId}
								/>
							</CardContent>
						</Card>

						<div className="space-y-4">
							<Card>
								<CardHeader>
									<CardTitle>핵심결과 업데이트</CardTitle>
									<CardDescription>
										현재값을 저장하면 목표 진행률이 다시 계산됩니다.
									</CardDescription>
								</CardHeader>
								<CardContent>
									<GoalProgressPanel
										key={selectedGoal?.id ?? 'none'}
										goal={selectedGoal}
										onDone={load}
									/>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>목표 코멘트</CardTitle>
									<CardDescription>
										진행 근거와 첨부 링크를 목표별 히스토리로 남깁니다.
									</CardDescription>
								</CardHeader>
								<CardContent>
									<GoalCommentsPanel key={selectedGoal?.id ?? 'none'} goal={selectedGoal} />
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>목표 생성</CardTitle>
									<CardDescription>첫 핵심결과까지 함께 등록할 수 있습니다.</CardDescription>
								</CardHeader>
								<CardContent>
									<GoalCreateForm employees={data.employees} onDone={load} />
								</CardContent>
							</Card>
						</div>
					</div>
				</>
			) : null}
		</div>
	);
}

type ReviewsPageData = {
	summary: PerformanceReviewSummaryResponse;
	operations: PerformanceReviewOperationsSummaryResponse | null;
	analysis: PerformanceReviewAnalysisResponse | null;
	cycles: PerformanceReviewCycleResponse[];
	goals: PerformanceGoalResponse[];
	employees: EmployeeResponse[];
};

const reviewCycleStatusLabels: Record<PerformanceReviewCycleStatus, string> = {
	DRAFT: '초안',
	ACTIVE: '진행중',
	CLOSED: '종료'
};

function reviewStatusVariant(
	status: PerformanceReviewCycleStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'ACTIVE') return 'success';
	if (status === 'DRAFT') return 'warning';
	return 'outline';
}

function scoreLabel(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
	return value.toLocaleString('ko-KR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function reviewProgressLabel(cycle: PerformanceReviewCycleResponse): string {
	return `${numberLabel(cycle.submittedParticipantCount)} / ${numberLabel(cycle.totalParticipantCount)}명`;
}

function defaultReviewEndDate(): string {
	const date = new Date();
	date.setDate(date.getDate() + 14);
	return dateInputValue(date);
}

function defaultPeriodEndDate(): string {
	const date = new Date();
	date.setMonth(date.getMonth() + 6);
	return dateInputValue(date);
}

function ReviewStatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<div className="flex items-start justify-between gap-3">
					<div>
						<p className="text-sm font-medium text-muted-foreground">{title}</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">{value}</p>
					</div>
					<span className="rounded-md bg-primary/10 p-2 text-primary">
						<Icon className="size-4" />
					</span>
				</div>
				<p className="mt-2 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function reviewOperationsChecklistVariant(
	status: PerformanceReviewOperationsChecklistStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function reviewOperationsChecklistStatusLabel(
	status: PerformanceReviewOperationsChecklistStatus
): string {
	if (status === 'READY') return '완료';
	if (status === 'WARNING') return '주의';
	if (status === 'WAITING') return '대기';
	return '-';
}

function reviewOperationsRiskVariant(
	severity: PerformanceReviewOperationsRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function ReviewOperationsChecklistCard({
	item
}: {
	item: PerformanceReviewOperationsChecklistItem;
}) {
	const Icon =
		item.status === 'WARNING' ? AlertTriangle : item.status === 'READY' ? CheckCircle2 : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={reviewOperationsChecklistVariant(item.status)}>
					{reviewOperationsChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function ReviewOperationsRisks({ risks }: { risks: PerformanceReviewOperationsRiskItem[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">운영 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={reviewOperationsRiskVariant(risk.severity)}>
							{numberLabel(risk.count)}건
						</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function ReviewOperationsCycles({ cycles }: { cycles: PerformanceReviewOperationsCycleInsight[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<ClipboardCheck className="size-4 text-muted-foreground" />
				<p className="font-semibold">사이클 진척</p>
			</div>
			<div className="mt-4 space-y-3">
				{cycles.length > 0 ? (
					cycles.slice(0, 4).map((cycle) => (
						<div key={cycle.cycleId ?? cycle.cycleName} className="space-y-2">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="truncate font-medium">{cycle.cycleName}</p>
									<p className="mt-1 text-sm text-muted-foreground">
										마감 {dateLabel(cycle.reviewEnd)} · 미제출{' '}
										{numberLabel(cycle.pendingParticipantCount)}건
									</p>
								</div>
								<Badge variant={reviewStatusVariant(cycle.status)}>
									{reviewCycleStatusLabels[cycle.status]}
								</Badge>
							</div>
							<div className="flex items-center gap-3">
								<div className="min-w-0 flex-1">
									<GoalProgressBar progress={cycle.progressPercent} />
								</div>
								<span className="w-12 text-right text-xs font-semibold">
									{goalPercentLabel(cycle.progressPercent)}
								</span>
							</div>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">운영 중인 평가 사이클이 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function ReviewOperationsDepartments({
	departments
}: {
	departments: PerformanceReviewOperationsDepartmentInsight[];
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<Building2 className="size-4 text-muted-foreground" />
				<p className="font-semibold">부서별 참여</p>
			</div>
			<div className="mt-4 space-y-3">
				{departments.length > 0 ? (
					departments.slice(0, 5).map((department) => (
						<div key={department.department} className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="font-medium">{department.department}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									제출 {numberLabel(department.submittedParticipantCount)} /{' '}
									{numberLabel(department.participantCount)}명 · 평균{' '}
									{scoreLabel(department.averageScore)}
								</p>
							</div>
							<Badge variant={department.pendingParticipantCount > 0 ? 'warning' : 'success'}>
								목표 {goalPercentLabel(department.linkedGoalCoveragePercent)}
							</Badge>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">부서별 평가 대상자가 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function ReviewOperationsScoreDistribution({
	distribution
}: {
	distribution: PerformanceReviewOperationsScoreDistribution[];
}) {
	const maxCount = Math.max(1, ...distribution.map((item) => item.count));
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<TrendingUp className="size-4 text-muted-foreground" />
				<p className="font-semibold">점수 분포</p>
			</div>
			<div className="mt-4 space-y-3">
				{distribution.map((item) => (
					<div key={item.code} className="grid grid-cols-[4rem_1fr_2.5rem] items-center gap-3">
						<span className="text-sm text-muted-foreground">{item.label}</span>
						<div className="h-2 overflow-hidden rounded-full bg-muted">
							<div
								className="h-full rounded-full bg-primary"
								style={{ width: `${Math.round((item.count / maxCount) * 100)}%` }}
							/>
						</div>
						<span className="text-right text-sm font-semibold">{numberLabel(item.count)}</span>
					</div>
				))}
			</div>
		</div>
	);
}

function calibrationCandidateVariant(
	reasonCode: PerformanceReviewOperationsCalibrationCandidate['reasonCode']
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (reasonCode === 'LOW_SCORE') return 'destructive';
	if (reasonCode === 'HIGH_SCORE') return 'warning';
	if (reasonCode === 'GOAL_MISSING') return 'warning';
	return 'outline';
}

function ReviewOperationsCalibrationCandidates({
	candidates
}: {
	candidates: PerformanceReviewOperationsCalibrationCandidate[];
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					<BadgeCheck className="size-4 text-muted-foreground" />
					<p className="font-semibold">캘리브레이션 후보</p>
				</div>
				<Badge variant={candidates.length > 0 ? 'warning' : 'success'}>
					{numberLabel(candidates.length)}명
				</Badge>
			</div>
			<div className="mt-4 space-y-3">
				{candidates.length > 0 ? (
					candidates.slice(0, 5).map((candidate) => (
						<div
							key={candidate.participantId}
							className="rounded-md border border-border bg-muted/20 p-3"
						>
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="font-medium">
										{candidate.revieweeName ?? candidate.revieweeEmployeeId}
									</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{candidate.department} · {candidate.cycleName}
									</p>
								</div>
								<div className="flex flex-wrap gap-2">
									<Badge variant={calibrationCandidateVariant(candidate.reasonCode)}>
										{candidate.reasonLabel}
									</Badge>
									<Badge variant="outline">점수 {scoreLabel(candidate.score)}</Badge>
								</div>
							</div>
							<p className="mt-3 text-sm text-muted-foreground">{candidate.actionLabel}</p>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">
						보정 회의에서 우선 확인할 평가 후보가 없습니다.
					</p>
				)}
			</div>
		</div>
	);
}

function ReviewOperationsPanel({
	operations
}: {
	operations: PerformanceReviewOperationsSummaryResponse;
}) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>평가 운영</CardTitle>
				<CardDescription>
					목표 연계율, 제출 진척, 마감 리마인드와 점수 캘리브레이션 상태를 함께 점검합니다.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">목표 연계율</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{goalPercentLabel(operations.goalLinkageCoveragePercent)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							목표 연결 {numberLabel(operations.linkedGoalParticipantCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">제출 진척</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{goalPercentLabel(operations.reviewProgressPercent)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							제출 {numberLabel(operations.submittedParticipantCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">마감 초과</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.overduePendingParticipantCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							미제출 {numberLabel(operations.pendingParticipantCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">마감 임박</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.dueSoonPendingParticipantCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							활성 {numberLabel(operations.activeCycleCount)}개
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">캘리브레이션</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.calibrationReviewCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							평균 {scoreLabel(operations.averageScore)}
						</p>
					</div>
				</div>

				<div className="grid gap-3 lg:grid-cols-2">
					{operations.checklist.map((item) => (
						<ReviewOperationsChecklistCard key={item.code} item={item} />
					))}
				</div>

				<div className="grid gap-4 xl:grid-cols-2">
					<ReviewOperationsRisks risks={operations.riskItems} />
					<ReviewOperationsCycles cycles={operations.cycleInsights} />
					<ReviewOperationsDepartments departments={operations.departmentInsights} />
					<ReviewOperationsScoreDistribution distribution={operations.scoreDistribution} />
					<ReviewOperationsCalibrationCandidates candidates={operations.calibrationCandidates} />
				</div>

				<div className="flex flex-wrap gap-2 rounded-lg border border-border bg-muted/20 p-4">
					<Badge variant="outline">
						<ClipboardCheck className="mr-1 size-3" />
						전체 {numberLabel(operations.totalCycleCount)}
					</Badge>
					<Badge variant="outline">
						<CheckCircle2 className="mr-1 size-3" />
						제출 {numberLabel(operations.submittedParticipantCount)}
					</Badge>
					<Badge variant={operations.draftCycleCount > 0 ? 'warning' : 'success'}>
						<Clock3 className="mr-1 size-3" />
						초안 {numberLabel(operations.draftCycleCount)}
					</Badge>
					<Badge variant="outline">
						<BadgeCheck className="mr-1 size-3" />
						종료 {numberLabel(operations.closedCycleCount)}
					</Badge>
				</div>
			</CardContent>
		</Card>
	);
}

function reviewAnalysisBiasVariant(
	code: PerformanceReviewAnalysisResponse['biasSignalCode']
): 'success' | 'warning' | 'outline' {
	if (code === 'LENIENCY_RISK' || code === 'STRICTNESS_RISK') return 'warning';
	if (code === 'BALANCED') return 'success';
	return 'outline';
}

function ReviewAnalysisDistribution({
	distribution
}: {
	distribution: PerformanceReviewAnalysisScoreBucket[];
}) {
	const maxCount = Math.max(1, ...distribution.map((item) => item.count));
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<TrendingUp className="size-4 text-muted-foreground" />
				<p className="font-semibold">리포트 점수 분포</p>
			</div>
			<div className="mt-4 space-y-3">
				{distribution.map((item) => (
					<div key={item.code} className="grid grid-cols-[4rem_1fr_4rem] items-center gap-3">
						<span className="text-sm text-muted-foreground">{item.label}</span>
						<div className="h-2 overflow-hidden rounded-full bg-muted">
							<div
								className="h-full rounded-full bg-primary"
								style={{ width: `${Math.round((item.count / maxCount) * 100)}%` }}
							/>
						</div>
						<span className="text-right text-sm font-semibold">{numberLabel(item.count)}건</span>
					</div>
				))}
			</div>
		</div>
	);
}

function ReviewAnalysisParticipantItem({
	report
}: {
	report: PerformanceReviewAnalysisParticipantReport;
}) {
	return (
		<div className="rounded-lg border border-border bg-muted/20 p-4">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="font-semibold">
						{report.revieweeName ?? `직원 #${report.revieweeEmployeeId}`}
					</p>
					<p className="mt-1 text-sm text-muted-foreground">
						{[report.department, report.position].filter(Boolean).join(' · ') || '소속 정보 없음'}
					</p>
				</div>
				<div className="flex flex-wrap gap-2">
					<Badge variant={report.status === 'SUBMITTED' ? 'success' : 'warning'}>
						{report.status === 'SUBMITTED' ? '제출' : '미제출'}
					</Badge>
					<Badge variant="outline">점수 {scoreLabel(report.score)}</Badge>
				</div>
			</div>
			<div className="mt-3 grid gap-3 sm:grid-cols-2">
				<div>
					<p className="text-xs font-semibold text-muted-foreground">강점</p>
					<p className="mt-1 text-sm">{report.strengthLabel}</p>
				</div>
				<div>
					<p className="text-xs font-semibold text-muted-foreground">개선</p>
					<p className="mt-1 text-sm">{report.improvementLabel}</p>
				</div>
			</div>
			<p className="mt-3 text-sm text-muted-foreground">{report.nextActionLabel}</p>
			<div className="mt-3 flex flex-wrap gap-2">
				<Badge variant="outline">{report.insightSummary}</Badge>
				{report.linkedGoalTitle ? (
					<Badge variant="secondary">{report.linkedGoalTitle}</Badge>
				) : null}
			</div>
		</div>
	);
}

function ReviewAnalysisPanel({ analysis }: { analysis: PerformanceReviewAnalysisResponse }) {
	return (
		<Card>
			<CardHeader>
				<div className="flex flex-wrap items-start justify-between gap-3">
					<div>
						<CardTitle>평가 분석 리포트</CardTitle>
						<CardDescription>
							{analysis.cycleName}의 점수 분포, 편향 신호, 구성원별 성장 인사이트입니다.
						</CardDescription>
					</div>
					<Badge variant={reviewAnalysisBiasVariant(analysis.biasSignalCode)}>
						{analysis.biasSignalLabel}
					</Badge>
				</div>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid gap-3 md:grid-cols-[1.1fr_0.9fr]">
					<div className="rounded-lg border border-border bg-background p-4">
						<div className="flex items-start justify-between gap-3">
							<div>
								<p className="text-sm font-medium text-muted-foreground">편향 셀프 점검</p>
								<p className="mt-2 text-lg font-semibold">{analysis.biasSignalLabel}</p>
								<p className="mt-2 text-sm text-muted-foreground">
									{analysis.biasSignalDescription}
								</p>
							</div>
							<BadgeCheck className="size-5 text-muted-foreground" />
						</div>
					</div>
					<div className="grid grid-cols-3 gap-2">
						<div className="rounded-lg border border-border bg-background p-3">
							<p className="text-xs text-muted-foreground">평균</p>
							<p className="mt-2 text-xl font-semibold tracking-normal">
								{scoreLabel(analysis.averageScore)}
							</p>
						</div>
						<div className="rounded-lg border border-border bg-background p-3">
							<p className="text-xs text-muted-foreground">제출</p>
							<p className="mt-2 text-xl font-semibold tracking-normal">
								{numberLabel(analysis.submittedParticipantCount)}
							</p>
						</div>
						<div className="rounded-lg border border-border bg-background p-3">
							<p className="text-xs text-muted-foreground">미제출</p>
							<p className="mt-2 text-xl font-semibold tracking-normal">
								{numberLabel(analysis.pendingParticipantCount)}
							</p>
						</div>
					</div>
				</div>

				<div className="grid gap-4 xl:grid-cols-[0.85fr_1.15fr]">
					<ReviewAnalysisDistribution distribution={analysis.scoreDistribution} />
					<div className="rounded-lg border border-border bg-background p-4">
						<div className="flex items-center justify-between gap-3">
							<div className="flex items-center gap-2">
								<ClipboardCheck className="size-4 text-muted-foreground" />
								<p className="font-semibold">구성원별 리포트</p>
							</div>
							<Badge variant="outline">{numberLabel(analysis.participantReports.length)}명</Badge>
						</div>
						<div className="mt-4 space-y-3">
							{analysis.participantReports.length > 0 ? (
								analysis.participantReports
									.slice(0, 6)
									.map((report) => (
										<ReviewAnalysisParticipantItem key={report.participantId} report={report} />
									))
							) : (
								<p className="text-sm text-muted-foreground">분석할 평가 대상자가 없습니다.</p>
							)}
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function ReviewCycleList({
	cycles,
	selectedCycleId,
	onSelect
}: {
	cycles: PerformanceReviewCycleResponse[];
	selectedCycleId: number | null;
	onSelect: (cycleId: number) => void;
}) {
	if (cycles.length === 0) return <EmptyState message="등록된 평가 사이클이 없습니다." />;

	return (
		<div className="space-y-3">
			{cycles.map((cycle) => {
				const selected = cycle.id === selectedCycleId;
				const progress =
					cycle.totalParticipantCount === 0
						? 0
						: Math.round((cycle.submittedParticipantCount / cycle.totalParticipantCount) * 100);
				return (
					<button
						key={cycle.id}
						type="button"
						onClick={() => onSelect(cycle.id)}
						className={cn(
							'w-full rounded-lg border p-4 text-left transition-colors',
							selected ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-muted/40'
						)}
					>
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="truncate font-semibold">{cycle.name}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{dateLabel(cycle.periodStart)} - {dateLabel(cycle.periodEnd)}
								</p>
							</div>
							<div className="flex shrink-0 items-center gap-2">
								<Badge variant={reviewStatusVariant(cycle.status)}>
									{reviewCycleStatusLabels[cycle.status]}
								</Badge>
								<Badge variant="outline">{scoreLabel(cycle.averageScore)}점</Badge>
							</div>
						</div>
						<div className="mt-4 space-y-2">
							<div className="flex items-center justify-between text-xs text-muted-foreground">
								<span>제출</span>
								<span className="font-semibold text-foreground">{reviewProgressLabel(cycle)}</span>
							</div>
							<GoalProgressBar progress={progress} />
						</div>
					</button>
				);
			})}
		</div>
	);
}

type ReviewDraft = {
	score: string;
	comment: string;
	linkedGoalId: string;
};

function toReviewDrafts(cycle: PerformanceReviewCycleResponse | null): Record<number, ReviewDraft> {
	return Object.fromEntries(
		(cycle?.participants ?? []).map((participant) => [
			participant.id,
			{
				score: participant.score == null ? '' : String(participant.score),
				comment: participant.comment ?? '',
				linkedGoalId: participant.linkedGoalId == null ? '' : String(participant.linkedGoalId)
			}
		])
	);
}

function ReviewParticipantsPanel({
	cycle,
	goals,
	onDone
}: {
	cycle: PerformanceReviewCycleResponse | null;
	goals: PerformanceGoalResponse[];
	onDone: () => Promise<void>;
}) {
	const [drafts, setDrafts] = useState<Record<number, ReviewDraft>>(() => toReviewDrafts(cycle));
	const [submittingId, setSubmittingId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	if (!cycle) return <EmptyState message="평가 사이클을 선택하면 대상자를 평가할 수 있습니다." />;
	const selectedCycle = cycle;

	function updateDraft(participantId: number, patch: Partial<ReviewDraft>) {
		setDrafts((current) => ({
			...current,
			[participantId]: {
				...(current[participantId] ?? { score: '', comment: '', linkedGoalId: '' }),
				...patch
			}
		}));
	}

	async function submitParticipant(participantId: number) {
		const draft = drafts[participantId] ?? { score: '', comment: '', linkedGoalId: '' };
		const score = Number(draft.score);
		if (!Number.isFinite(score) || score < 1 || score > 5) {
			setError('평가 점수는 1~5 사이 숫자로 입력해주세요.');
			return;
		}
		setSubmittingId(participantId);
		setError(null);
		try {
			await api.patch<PerformanceReviewCycleResponse>(
				`/performance/reviews/${selectedCycle.id}/participants/${participantId}`,
				{
					score,
					comment: draft.comment.trim() || null,
					linkedGoalId: draft.linkedGoalId ? Number(draft.linkedGoalId) : null
				}
			);
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '평가를 제출하지 못했습니다.'));
		} finally {
			setSubmittingId(null);
		}
	}

	return (
		<div className="space-y-4">
			<FormError message={error} />
			<div className="rounded-lg border border-border bg-muted/20 p-4">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<p className="font-semibold">{selectedCycle.name}</p>
						<p className="mt-1 text-sm text-muted-foreground">
							평가 기간 {dateLabel(selectedCycle.reviewStart)} -{' '}
							{dateLabel(selectedCycle.reviewEnd)}
						</p>
					</div>
					<span className="text-lg font-semibold">{scoreLabel(selectedCycle.averageScore)}점</span>
				</div>
			</div>
			{selectedCycle.participants.length === 0 ? (
				<EmptyState message="평가 대상자가 없습니다." />
			) : (
				<div className="space-y-3">
					{selectedCycle.participants.map((participant) => {
						const draft = drafts[participant.id] ?? {
							score: '',
							comment: '',
							linkedGoalId: ''
						};
						return (
							<div key={participant.id} className="rounded-lg border border-border p-3">
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<p className="truncate text-sm font-semibold">
											{participant.revieweeEmployeeName ??
												`직원 #${participant.revieweeEmployeeId}`}
										</p>
										<p className="mt-1 text-xs text-muted-foreground">
											{[participant.department, participant.position].filter(Boolean).join(' · ') ||
												'소속 정보 없음'}
										</p>
									</div>
									<Badge variant={participant.status === 'SUBMITTED' ? 'success' : 'warning'}>
										{participant.status === 'SUBMITTED' ? '제출' : '미제출'}
									</Badge>
								</div>
								{participant.linkedGoalTitle ? (
									<p className="mt-2 rounded-md bg-muted/50 px-2 py-1 text-xs text-muted-foreground">
										연결 목표: {participant.linkedGoalTitle}
									</p>
								) : null}
								<div className="mt-3 grid gap-2 sm:grid-cols-[5.5rem_1fr]">
									<Input
										value={draft.score}
										onChange={(event) => updateDraft(participant.id, { score: event.target.value })}
										type="number"
										min="1"
										max="5"
										step="0.1"
										placeholder="점수"
										aria-label={`${participant.revieweeEmployeeName ?? participant.revieweeEmployeeId} 평가 점수`}
									/>
									<select
										value={draft.linkedGoalId}
										onChange={(event) =>
											updateDraft(participant.id, { linkedGoalId: event.target.value })
										}
										className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
										aria-label={`${participant.revieweeEmployeeName ?? participant.revieweeEmployeeId} 연결 목표`}
									>
										<option value="">연결 목표 없음</option>
										{goals.map((goal) => (
											<option key={goal.id} value={goal.id}>
												{goal.title}
											</option>
										))}
									</select>
								</div>
								<div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
									<Input
										value={draft.comment}
										onChange={(event) =>
											updateDraft(participant.id, { comment: event.target.value })
										}
										placeholder="성과 근거와 다음 성장 포인트"
										aria-label={`${participant.revieweeEmployeeName ?? participant.revieweeEmployeeId} 평가 코멘트`}
									/>
									<Button
										type="button"
										variant="outline"
										onClick={() => void submitParticipant(participant.id)}
										disabled={submittingId === participant.id}
									>
										{submittingId === participant.id ? '저장 중...' : '평가 저장'}
									</Button>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}

function ReviewCreateForm({
	employees,
	onDone
}: {
	employees: EmployeeResponse[];
	onDone: () => Promise<void>;
}) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [periodStart, setPeriodStart] = useState(() => dateInputValue(new Date()));
	const [periodEnd, setPeriodEnd] = useState(defaultPeriodEndDate);
	const [reviewStart, setReviewStart] = useState(() => dateInputValue(new Date()));
	const [reviewEnd, setReviewEnd] = useState(defaultReviewEndDate);
	const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const activeEmployees = employees.filter((employee) => employee.status === 'ACTIVE');

	function toggleEmployee(employeeId: number) {
		setSelectedEmployeeIds((current) =>
			current.includes(employeeId)
				? current.filter((id) => id !== employeeId)
				: [...current, employeeId]
		);
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmedName = name.trim();
		if (!trimmedName) {
			setError('평가명을 입력해주세요.');
			return;
		}
		if (periodEnd < periodStart || reviewEnd < reviewStart) {
			setError('성과 기간과 평가 기간을 확인해주세요.');
			return;
		}
		if (selectedEmployeeIds.length === 0) {
			setError('평가 대상자를 선택해주세요.');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			await api.post<PerformanceReviewCycleResponse>('/performance/reviews', {
				name: trimmedName,
				description: description.trim() || null,
				periodStart,
				periodEnd,
				reviewStart,
				reviewEnd,
				status: 'ACTIVE',
				participantEmployeeIds: selectedEmployeeIds
			});
			setName('');
			setDescription('');
			setSelectedEmployeeIds([]);
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '평가 사이클을 생성하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form className="space-y-4" onSubmit={handleSubmit}>
			<FormError message={error} />
			<div className="space-y-2">
				<Label htmlFor="review-cycle-name">평가명</Label>
				<Input
					id="review-cycle-name"
					value={name}
					onChange={(event) => setName(event.target.value)}
					placeholder="예: 2026 하반기 성과평가"
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="review-cycle-description">설명</Label>
				<Input
					id="review-cycle-description"
					value={description}
					onChange={(event) => setDescription(event.target.value)}
					placeholder="평가 기준이나 운영 메모"
				/>
			</div>
			<div className="grid gap-3 sm:grid-cols-2">
				<div className="space-y-2">
					<Label htmlFor="review-period-start">성과 시작일</Label>
					<Input
						id="review-period-start"
						type="date"
						value={periodStart}
						onChange={(event) => setPeriodStart(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="review-period-end">성과 종료일</Label>
					<Input
						id="review-period-end"
						type="date"
						value={periodEnd}
						onChange={(event) => setPeriodEnd(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="review-start">평가 시작일</Label>
					<Input
						id="review-start"
						type="date"
						value={reviewStart}
						onChange={(event) => setReviewStart(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="review-end">평가 종료일</Label>
					<Input
						id="review-end"
						type="date"
						value={reviewEnd}
						onChange={(event) => setReviewEnd(event.target.value)}
					/>
				</div>
			</div>
			<div className="space-y-2">
				<Label>평가 대상자</Label>
				{activeEmployees.length === 0 ? (
					<EmptyState message="평가 대상 직원이 없습니다." />
				) : (
					<div className="max-h-56 space-y-2 overflow-auto pr-1">
						{activeEmployees.map((employee) => (
							<label
								key={employee.id}
								className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 text-sm hover:bg-muted/40"
							>
								<input
									type="checkbox"
									checked={selectedEmployeeIds.includes(employee.id)}
									onChange={() => toggleEmployee(employee.id)}
									className="size-4"
								/>
								<span className="min-w-0">
									<span className="block font-semibold">{employee.name}</span>
									<span className="mt-1 block truncate text-xs text-muted-foreground">
										{[employee.department, employee.position].filter(Boolean).join(' · ') ||
											'소속 정보 없음'}
									</span>
								</span>
							</label>
						))}
					</div>
				)}
			</div>
			<Button
				type="submit"
				disabled={submitting || activeEmployees.length === 0}
				className="w-full"
			>
				<Plus className="size-4" />
				{submitting ? '생성 중...' : '평가 사이클 만들기'}
			</Button>
		</form>
	);
}

export function ReviewsPage() {
	const [state, setState] = useState<RequestState<ReviewsPageData>>({ status: 'idle' });
	const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
	const [downloadError, setDownloadError] = useState<string | null>(null);
	const selectedCycleIdRef = useRef<number | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			setDownloadError(null);
			try {
				const [summary, operations, cycles, goals, employeePage] = await Promise.all([
					api.get<PerformanceReviewSummaryResponse>('/performance/reviews/summary'),
					api
						.get<PerformanceReviewOperationsSummaryResponse>(
							'/performance/reviews/operations/summary'
						)
						.catch(() => null),
					api.get<PerformanceReviewCycleResponse[]>('/performance/reviews'),
					api.get<PerformanceGoalResponse[]>('/performance/goals'),
					api.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200')
				]);
				const currentSelectedCycleId = selectedCycleIdRef.current;
				const nextSelectedCycleId =
					cycles.find((cycle) => cycle.id === currentSelectedCycleId)?.id ?? cycles[0]?.id ?? null;
				const analysis =
					nextSelectedCycleId == null
						? null
						: await api
								.get<PerformanceReviewAnalysisResponse>(
									`/performance/reviews/${nextSelectedCycleId}/analysis`
								)
								.catch(() => null);
				selectedCycleIdRef.current = nextSelectedCycleId;
				setState({
					status: 'success',
					data: { summary, operations, analysis, cycles, goals, employees: employeePage.content }
				});
				setSelectedCycleId(nextSelectedCycleId);
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const selectedCycle = data?.cycles.find((cycle) => cycle.id === selectedCycleId) ?? null;

	async function selectCycle(cycleId: number) {
		selectedCycleIdRef.current = cycleId;
		setSelectedCycleId(cycleId);
		try {
			const analysis = await api
				.get<PerformanceReviewAnalysisResponse>(`/performance/reviews/${cycleId}/analysis`)
				.catch(() => null);
			setState((current) =>
				current.status === 'success'
					? { status: 'success', data: { ...current.data, analysis } }
					: current
			);
		} catch {
			setState((current) =>
				current.status === 'success'
					? { status: 'success', data: { ...current.data, analysis: null } }
					: current
			);
		}
	}

	async function downloadReviewResults() {
		if (!selectedCycle) return;
		setDownloadError(null);
		try {
			await api.downloadFile(
				`/performance/reviews/${selectedCycle.id}/results`,
				`performance_review_results_${selectedCycle.id}.csv`
			);
		} catch (error) {
			setDownloadError(getErrorMessage(error, '평가 결과를 다운로드하지 못했습니다.'));
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="평가"
				description="성과평가 사이클, 대상자 제출 현황, 목표 연동 평가를 관리합니다."
				actions={
					<>
						<Button
							variant="outline"
							onClick={() => void downloadReviewResults()}
							disabled={!selectedCycle}
						>
							<Download className="size-4" />
							평가 결과 다운로드
						</Button>
						<Button variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					</>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{downloadError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{downloadError}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<ReviewStatCard
							title="평가 사이클"
							value={`${numberLabel(data.summary.totalCycles)}개`}
							description={`진행중 ${numberLabel(data.summary.activeCycles)}개`}
							icon={ClipboardCheck}
						/>
						<ReviewStatCard
							title="평균 점수"
							value={scoreLabel(data.summary.averageScore)}
							description="제출된 평가 기준"
							icon={TrendingUp}
						/>
						<ReviewStatCard
							title="미제출"
							value={`${numberLabel(data.summary.pendingReviews)}건`}
							description="마감 전 리마인드 대상"
							icon={AlertTriangle}
						/>
						<ReviewStatCard
							title="제출"
							value={`${numberLabel(data.summary.submittedReviews)}건`}
							description="저장된 평가 응답"
							icon={CheckCircle2}
						/>
					</div>

					{data.operations ? <ReviewOperationsPanel operations={data.operations} /> : null}
					{data.analysis ? <ReviewAnalysisPanel analysis={data.analysis} /> : null}

					<div className="grid gap-4 xl:grid-cols-[1fr_0.95fr]">
						<Card>
							<CardHeader>
								<CardTitle>평가 사이클</CardTitle>
								<CardDescription>평가 기간, 대상자 제출률, 평균 점수를 확인합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<ReviewCycleList
									cycles={data.cycles}
									selectedCycleId={selectedCycleId}
									onSelect={(cycleId) => void selectCycle(cycleId)}
								/>
							</CardContent>
						</Card>

						<div className="space-y-4">
							<Card>
								<CardHeader>
									<CardTitle>대상자 평가</CardTitle>
									<CardDescription>목표를 연결해 점수와 성장 코멘트를 저장합니다.</CardDescription>
								</CardHeader>
								<CardContent>
									<ReviewParticipantsPanel
										key={selectedCycle?.id ?? 'none'}
										cycle={selectedCycle}
										goals={data.goals}
										onDone={load}
									/>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>평가 생성</CardTitle>
									<CardDescription>
										성과 기간과 대상자를 지정해 새 평가를 시작합니다.
									</CardDescription>
								</CardHeader>
								<CardContent>
									<ReviewCreateForm employees={data.employees} onDone={load} />
								</CardContent>
							</Card>
						</div>
					</div>
				</>
			) : null}
		</div>
	);
}

type OneOnOnesPageData = {
	summary: OneOnOneSummaryResponse;
	operations: OneOnOneOperationsSummaryResponse | null;
	sessions: OneOnOneSessionResponse[];
	goals: PerformanceGoalResponse[];
	reviewCycles: PerformanceReviewCycleResponse[];
	employees: EmployeeResponse[];
};

type OneOnOneDraft = {
	agenda: string;
	privateNote: string;
	sharedNote: string;
	checkInPersonalScore: string;
	checkInWorkScore: string;
	checkInOrgScore: string;
	linkedGoalId: string;
	linkedReviewCycleId: string;
	status: OneOnOneSessionStatus;
	summary: string;
	feedbackScore: string;
	feedbackComment: string;
};

const oneOnOneStatusLabels: Record<OneOnOneSessionStatus, string> = {
	SCHEDULED: '예정',
	COMPLETED: '완료',
	CANCELED: '취소'
};

const actionItemStatusLabels: Record<OneOnOneActionItemStatus, string> = {
	OPEN: '진행',
	DONE: '완료'
};

const actionImpactLabels: Record<OneOnOneActionImpact, string> = {
	HELPFUL: '도움됨',
	NEUTRAL: '보통',
	BLOCKED: '막힘'
};

function oneOnOneStatusVariant(
	status: OneOnOneSessionStatus
): 'success' | 'warning' | 'outline' | 'destructive' {
	if (status === 'COMPLETED') return 'success';
	if (status === 'SCHEDULED') return 'warning';
	if (status === 'CANCELED') return 'destructive';
	return 'outline';
}

function actionStatusVariant(
	status: OneOnOneActionItemStatus
): 'success' | 'warning' | 'outline' | 'destructive' {
	return status === 'DONE' ? 'success' : 'warning';
}

function scoreText(value: number | null | undefined): string {
	if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return '-';
	return `${numberLabel(value)}점`;
}

function dateTimeLabel(value: string | null | undefined): string {
	if (!value) return '-';
	const [date, time = ''] = value.split('T');
	const compactTime = time.slice(0, 5);
	return compactTime ? `${dateLabel(date)} ${compactTime}` : dateLabel(date);
}

function oneOnOneTimestamp(session: Pick<OneOnOneSessionResponse, 'scheduledAt'>): number {
	const timestamp = Date.parse(session.scheduledAt);
	return Number.isFinite(timestamp) ? timestamp : 0;
}

function dateTimeInputValue(date: Date): string {
	const offsetMs = date.getTimezoneOffset() * 60 * 1000;
	return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function defaultOneOnOneDateTime(): string {
	const date = new Date();
	date.setDate(date.getDate() + 7);
	date.setHours(10, 0, 0, 0);
	return dateTimeInputValue(date);
}

function toOneOnOneDraft(session: OneOnOneSessionResponse): OneOnOneDraft {
	return {
		agenda: session.agenda ?? '',
		privateNote: session.privateNote ?? '',
		sharedNote: session.sharedNote ?? '',
		checkInPersonalScore:
			session.checkInPersonalScore == null ? '' : String(session.checkInPersonalScore),
		checkInWorkScore: session.checkInWorkScore == null ? '' : String(session.checkInWorkScore),
		checkInOrgScore: session.checkInOrgScore == null ? '' : String(session.checkInOrgScore),
		linkedGoalId: session.linkedGoalId == null ? '' : String(session.linkedGoalId),
		linkedReviewCycleId:
			session.linkedReviewCycleId == null ? '' : String(session.linkedReviewCycleId),
		status: session.status,
		summary: session.summary ?? '',
		feedbackScore: session.feedbackScore == null ? '' : String(session.feedbackScore),
		feedbackComment: session.feedbackComment ?? ''
	};
}

function oneOnOneHistoryForSession(
	sessions: OneOnOneSessionResponse[],
	selectedSession: OneOnOneSessionResponse
): OneOnOneSessionResponse[] {
	const selectedTimestamp = oneOnOneTimestamp(selectedSession);
	return [...sessions]
		.filter(
			(session) =>
				session.id !== selectedSession.id &&
				session.status !== 'CANCELED' &&
				session.managerEmployeeId === selectedSession.managerEmployeeId &&
				session.revieweeEmployeeId === selectedSession.revieweeEmployeeId &&
				(selectedTimestamp === 0 || oneOnOneTimestamp(session) < selectedTimestamp)
		)
		.sort((a, b) => oneOnOneTimestamp(b) - oneOnOneTimestamp(a))
		.slice(0, 3);
}

function oneOnOneCheckInText(session: OneOnOneSessionResponse): string {
	return [
		`개인 ${scoreText(session.checkInPersonalScore)}`,
		`업무 ${scoreText(session.checkInWorkScore)}`,
		`조직 ${scoreText(session.checkInOrgScore)}`
	].join(' · ');
}

function oneOnOneHistoryMemo(session: OneOnOneSessionResponse): string {
	return (
		session.sharedNote ??
		session.summary ??
		session.agenda ??
		session.privateNote ??
		'저장된 대화 메모가 없습니다.'
	);
}

function OneOnOneStatCard({
	title,
	value,
	description,
	icon: Icon
}: {
	title: string;
	value: string;
	description: string;
	icon: ComponentType<{ className?: string }>;
}) {
	return (
		<Card>
			<CardContent className="p-4 sm:p-5">
				<div className="flex items-start justify-between gap-3">
					<div>
						<p className="text-sm font-medium text-muted-foreground">{title}</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">{value}</p>
					</div>
					<span className="rounded-md bg-primary/10 p-2 text-primary">
						<Icon className="size-4" />
					</span>
				</div>
				<p className="mt-2 text-xs text-muted-foreground">{description}</p>
			</CardContent>
		</Card>
	);
}

function oneOnOneOperationsChecklistVariant(
	status: OneOnOneOperationsChecklistStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function oneOnOneOperationsChecklistStatusLabel(status: OneOnOneOperationsChecklistStatus): string {
	if (status === 'READY') return '완료';
	if (status === 'WARNING') return '주의';
	if (status === 'WAITING') return '대기';
	return '-';
}

function oneOnOneOperationsRiskVariant(
	severity: OneOnOneOperationsRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function oneOnOnePreparationVariant(
	reasonCode: OneOnOneOperationsPreparationCandidate['reasonCode']
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (reasonCode === 'OVERDUE_SESSION') return 'destructive';
	if (reasonCode === 'CONTEXT_MISSING' || reasonCode === 'LOW_CHECK_IN') return 'warning';
	if (reasonCode === 'OPEN_ACTION') return 'outline';
	return 'success';
}

function OneOnOneOperationsChecklistCard({ item }: { item: OneOnOneOperationsChecklistItem }) {
	const Icon =
		item.status === 'WARNING' ? AlertTriangle : item.status === 'READY' ? CheckCircle2 : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={oneOnOneOperationsChecklistVariant(item.status)}>
					{oneOnOneOperationsChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function OneOnOneOperationsRisks({ risks }: { risks: OneOnOneOperationsRiskItem[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">운영 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={oneOnOneOperationsRiskVariant(risk.severity)}>
							{numberLabel(risk.count)}건
						</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function OneOnOneOperationsPreparationCandidates({
	candidates
}: {
	candidates: OneOnOneOperationsPreparationCandidate[];
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					<ClipboardCheck className="size-4 text-muted-foreground" />
					<p className="font-semibold">준비 우선순위</p>
				</div>
				<Badge variant={candidates.length > 0 ? 'warning' : 'success'}>
					{numberLabel(candidates.length)}건
				</Badge>
			</div>
			<div className="mt-4 space-y-3">
				{candidates.length > 0 ? (
					candidates.slice(0, 5).map((candidate) => (
						<div
							key={candidate.sessionId ?? candidate.title}
							className="rounded-md border border-border bg-muted/20 p-3"
						>
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="font-medium">{candidate.title}</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{candidate.managerName ?? '리더 미지정'} →{' '}
										{candidate.revieweeName ?? '대상자 미지정'} · {candidate.revieweeDepartment}
									</p>
								</div>
								<Badge variant={oneOnOnePreparationVariant(candidate.reasonCode)}>
									{candidate.reasonLabel}
								</Badge>
							</div>
							<div className="mt-3 flex flex-wrap gap-2">
								<Badge variant="outline">{dateTimeLabel(candidate.scheduledAt)}</Badge>
								<Badge variant={candidate.linkedContextCount > 0 ? 'success' : 'warning'}>
									맥락 {numberLabel(candidate.linkedContextCount)}
								</Badge>
								<Badge
									variant={
										candidate.averageCheckInScore > 0 && candidate.averageCheckInScore < 3
											? 'warning'
											: 'outline'
									}
								>
									체크인 {scoreText(candidate.averageCheckInScore)}
								</Badge>
								<Badge variant={candidate.openActionItemCount > 0 ? 'warning' : 'outline'}>
									액션 {numberLabel(candidate.openActionItemCount)}건
								</Badge>
							</div>
							<p className="mt-3 text-sm text-muted-foreground">{candidate.actionLabel}</p>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">우선 준비가 필요한 원온원이 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function OneOnOneOperationsSessions({
	sessions
}: {
	sessions: OneOnOneOperationsSessionInsight[];
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<CalendarCheck className="size-4 text-muted-foreground" />
				<p className="font-semibold">다가오는 대화</p>
			</div>
			<div className="mt-4 space-y-3">
				{sessions.length > 0 ? (
					sessions.slice(0, 4).map((session) => (
						<div key={session.sessionId ?? session.title} className="space-y-2">
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<p className="truncate font-medium">{session.title}</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{session.managerName ?? '리더 미지정'} →{' '}
										{session.revieweeName ?? '대상자 미지정'}
									</p>
								</div>
								<Badge variant={oneOnOneStatusVariant(session.status)}>
									{oneOnOneStatusLabels[session.status]}
								</Badge>
							</div>
							<div className="flex flex-wrap gap-2">
								<Badge variant="outline">{dateTimeLabel(session.scheduledAt)}</Badge>
								<Badge variant={session.linkedContextCount > 0 ? 'success' : 'warning'}>
									맥락 {numberLabel(session.linkedContextCount)}
								</Badge>
								<Badge variant={session.summaryReady ? 'success' : 'outline'}>
									요약 {session.summaryReady ? '있음' : '없음'}
								</Badge>
							</div>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">등록된 원온원이 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function OneOnOneOperationsRelationships({
	relationships
}: {
	relationships: OneOnOneOperationsRelationshipInsight[];
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<UsersRound className="size-4 text-muted-foreground" />
				<p className="font-semibold">관계별 히스토리</p>
			</div>
			<div className="mt-4 space-y-3">
				{relationships.length > 0 ? (
					relationships.slice(0, 5).map((relationship) => (
						<div
							key={`${relationship.managerEmployeeId ?? 'manager'}-${relationship.revieweeEmployeeId ?? 'reviewee'}`}
							className="flex items-start justify-between gap-3"
						>
							<div className="min-w-0">
								<p className="font-medium">
									{relationship.managerName ?? '리더 미지정'} →{' '}
									{relationship.revieweeName ?? '대상자 미지정'}
								</p>
								<p className="mt-1 text-sm text-muted-foreground">
									완료 {numberLabel(relationship.completedSessionCount)} /{' '}
									{numberLabel(relationship.sessionCount)}회 · 액션{' '}
									{numberLabel(relationship.openActionItemCount)}건
								</p>
							</div>
							<Badge variant={relationship.averageCheckInScore < 3 ? 'warning' : 'outline'}>
								{scoreText(relationship.averageCheckInScore)}
							</Badge>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">반복된 원온원 관계가 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function OneOnOneOperationsActionImpact({
	impactMix
}: {
	impactMix: OneOnOneOperationsActionImpactMix[];
}) {
	const maxCount = Math.max(1, ...impactMix.map((item) => item.count));
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<BadgeCheck className="size-4 text-muted-foreground" />
				<p className="font-semibold">액션 효과</p>
			</div>
			<div className="mt-4 space-y-3">
				{impactMix.map((item) => (
					<div key={item.impact} className="grid grid-cols-[4rem_1fr_2.5rem] items-center gap-3">
						<span className="text-sm text-muted-foreground">{item.label}</span>
						<div className="h-2 overflow-hidden rounded-full bg-muted">
							<div
								className={cn(
									'h-full rounded-full',
									item.impact === 'BLOCKED' ? 'bg-warning' : 'bg-primary'
								)}
								style={{ width: `${Math.round((item.count / maxCount) * 100)}%` }}
							/>
						</div>
						<span className="text-right text-sm font-semibold">{numberLabel(item.count)}</span>
					</div>
				))}
			</div>
		</div>
	);
}

function OneOnOneOperationsPanel({
	operations
}: {
	operations: OneOnOneOperationsSummaryResponse;
}) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>원온원 운영</CardTitle>
				<CardDescription>
					반복 주기, 체크인 신호, 목표·평가 맥락과 액션 실행 상태를 함께 점검합니다.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">반복 주기</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.recurringSessionCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							예정 {numberLabel(operations.scheduledSessionCount)}개
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">맥락 연결</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{goalPercentLabel(operations.contextCoveragePercent)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							목표 {numberLabel(operations.goalLinkedSessionCount)} · 평가{' '}
							{numberLabel(operations.reviewLinkedSessionCount)}
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">액션 완료율</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{goalPercentLabel(operations.actionCompletionPercent)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							지연 {numberLabel(operations.overdueActionItemCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">체크인 신호</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{scoreText(operations.averageCheckInScore)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							낮은 신호 {numberLabel(operations.lowCheckInSessionCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">요약 준비</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.summaryReadySessionCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							후기 {scoreText(operations.averageFeedbackScore)}
						</p>
					</div>
				</div>

				<div className="grid gap-3 lg:grid-cols-2">
					{operations.checklist.map((item) => (
						<OneOnOneOperationsChecklistCard key={item.code} item={item} />
					))}
				</div>

				<div className="grid gap-4 xl:grid-cols-2">
					<OneOnOneOperationsRisks risks={operations.riskItems} />
					<OneOnOneOperationsPreparationCandidates candidates={operations.preparationCandidates} />
					<OneOnOneOperationsSessions sessions={operations.sessionInsights} />
					<OneOnOneOperationsRelationships relationships={operations.relationshipInsights} />
					<OneOnOneOperationsActionImpact impactMix={operations.actionImpactMix} />
				</div>

				<div className="flex flex-wrap gap-2 rounded-lg border border-border bg-muted/20 p-4">
					<Badge variant="outline">
						<CalendarCheck className="mr-1 size-3" />
						임박 {numberLabel(operations.dueSoonSessionCount)}
					</Badge>
					<Badge variant={operations.overdueScheduledSessionCount > 0 ? 'warning' : 'success'}>
						<Clock3 className="mr-1 size-3" />
						지난 일정 {numberLabel(operations.overdueScheduledSessionCount)}
					</Badge>
					<Badge variant="outline">
						<FileText className="mr-1 size-3" />
						공유 메모 {numberLabel(operations.sharedNoteSessionCount)}
					</Badge>
					<Badge variant="outline">
						<ShieldCheck className="mr-1 size-3" />
						비밀 메모 {numberLabel(operations.privateNoteSessionCount)}
					</Badge>
					<Badge variant={operations.blockedActionItemCount > 0 ? 'warning' : 'success'}>
						<AlertTriangle className="mr-1 size-3" />
						막힌 액션 {numberLabel(operations.blockedActionItemCount)}
					</Badge>
				</div>
			</CardContent>
		</Card>
	);
}

function OneOnOneSessionList({
	sessions,
	selectedSessionId,
	onSelect
}: {
	sessions: OneOnOneSessionResponse[];
	selectedSessionId: number | null;
	onSelect: (sessionId: number) => void;
}) {
	if (sessions.length === 0) return <EmptyState message="등록된 원온원이 없습니다." />;

	return (
		<div className="space-y-3">
			{sessions.map((session) => {
				const selected = session.id === selectedSessionId;
				return (
					<button
						key={session.id}
						type="button"
						onClick={() => onSelect(session.id)}
						className={cn(
							'w-full rounded-lg border p-4 text-left transition-colors',
							selected ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-muted/40'
						)}
					>
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="truncate font-semibold">{session.title}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{session.managerEmployeeName ?? `직원 #${session.managerEmployeeId}`} →{' '}
									{session.revieweeEmployeeName ?? `직원 #${session.revieweeEmployeeId}`}
								</p>
							</div>
							<div className="flex shrink-0 items-center gap-2">
								<Badge variant={oneOnOneStatusVariant(session.status)}>
									{oneOnOneStatusLabels[session.status]}
								</Badge>
								<Badge variant="outline">{scoreText(session.averageCheckInScore)}</Badge>
							</div>
						</div>
						<div className="mt-4 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
							<span>{dateTimeLabel(session.scheduledAt)}</span>
							<span className="text-left sm:text-right">
								액션{' '}
								{numberLabel(session.actionItems.filter((item) => item.status === 'OPEN').length)}건
							</span>
						</div>
					</button>
				);
			})}
		</div>
	);
}

function OneOnOneActionItems({
	session,
	onDone
}: {
	session: OneOnOneSessionResponse;
	onDone: () => Promise<void>;
}) {
	const [updatingId, setUpdatingId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function updateActionItem(
		item: OneOnOneActionItemResponse,
		nextStatus: OneOnOneActionItemStatus
	) {
		setUpdatingId(item.id);
		setError(null);
		try {
			await api.patch<OneOnOneActionItemResponse>(
				`/performance/one-on-ones/${session.id}/action-items/${item.id}`,
				{
					status: nextStatus,
					impact: nextStatus === 'DONE' ? 'HELPFUL' : item.impact
				}
			);
			await onDone();
		} catch (updateError) {
			setError(getErrorMessage(updateError, '액션 아이템을 업데이트하지 못했습니다.'));
		} finally {
			setUpdatingId(null);
		}
	}

	return (
		<div className="space-y-3">
			<FormError message={error} />
			{session.actionItems.length === 0 ? (
				<EmptyState message="등록된 액션 아이템이 없습니다." />
			) : (
				session.actionItems.map((item) => (
					<div key={item.id} className="rounded-lg border border-border p-3">
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="text-sm font-semibold">{item.content}</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{item.ownerEmployeeName ?? `직원 #${item.ownerEmployeeId}`} · 마감{' '}
									{dateLabel(item.dueDate)}
								</p>
							</div>
							<div className="flex shrink-0 items-center gap-2">
								<Badge variant={actionStatusVariant(item.status)}>
									{actionItemStatusLabels[item.status]}
								</Badge>
								<Badge variant="outline">{actionImpactLabels[item.impact]}</Badge>
							</div>
						</div>
						<div className="mt-3 flex justify-end">
							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={() =>
									void updateActionItem(item, item.status === 'DONE' ? 'OPEN' : 'DONE')
								}
								disabled={updatingId === item.id}
							>
								{updatingId === item.id
									? '저장 중...'
									: item.status === 'DONE'
										? '진행으로 변경'
										: '완료 처리'}
							</Button>
						</div>
					</div>
				))
			)}
		</div>
	);
}

function OneOnOneActionCreateForm({
	session,
	employees,
	onDone
}: {
	session: OneOnOneSessionResponse;
	employees: EmployeeResponse[];
	onDone: () => Promise<void>;
}) {
	const [ownerEmployeeId, setOwnerEmployeeId] = useState(() => String(session.revieweeEmployeeId));
	const [content, setContent] = useState('');
	const [dueDate, setDueDate] = useState(() => dateInputValue(new Date()));
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const activeEmployees = employees.filter((employee) => employee.status === 'ACTIVE');

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmedContent = content.trim();
		if (!ownerEmployeeId) {
			setError('담당자를 선택해주세요.');
			return;
		}
		if (!trimmedContent) {
			setError('액션 아이템을 입력해주세요.');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			await api.post<OneOnOneActionItemResponse>(
				`/performance/one-on-ones/${session.id}/action-items`,
				{
					ownerEmployeeId: Number(ownerEmployeeId),
					content: trimmedContent,
					dueDate: dueDate || null
				}
			);
			setContent('');
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '액션 아이템을 추가하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form
			className="space-y-3 rounded-lg border border-border bg-muted/20 p-3"
			onSubmit={handleSubmit}
		>
			<FormError message={error} />
			<div className="grid gap-2 sm:grid-cols-[1fr_9rem]">
				<Input
					value={content}
					onChange={(event) => setContent(event.target.value)}
					placeholder="액션 아이템"
					aria-label="원온원 액션 아이템"
				/>
				<Input
					type="date"
					value={dueDate}
					onChange={(event) => setDueDate(event.target.value)}
					aria-label="액션 아이템 마감일"
				/>
			</div>
			<div className="grid gap-2 sm:grid-cols-[1fr_auto]">
				<select
					value={ownerEmployeeId}
					onChange={(event) => setOwnerEmployeeId(event.target.value)}
					className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					aria-label="액션 아이템 담당자"
				>
					<option value="">담당자 선택</option>
					{activeEmployees.map((employee) => (
						<option key={employee.id} value={employee.id}>
							{employee.name}
						</option>
					))}
				</select>
				<Button
					type="submit"
					variant="outline"
					disabled={submitting || activeEmployees.length === 0}
				>
					<Plus className="size-4" />
					{submitting ? '추가 중...' : '액션 추가'}
				</Button>
			</div>
		</form>
	);
}

function OneOnOneHistoryPanel({
	sessions,
	selectedSession
}: {
	sessions: OneOnOneSessionResponse[];
	selectedSession: OneOnOneSessionResponse;
}) {
	const historySessions = oneOnOneHistoryForSession(sessions, selectedSession);
	if (historySessions.length === 0) return null;

	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<Clock3 className="size-4 text-muted-foreground" />
						<p className="text-sm font-semibold">이전 원온원 히스토리</p>
					</div>
					<p className="mt-1 text-xs text-muted-foreground">
						{selectedSession.managerEmployeeName ?? `직원 #${selectedSession.managerEmployeeId}`} →{' '}
						{selectedSession.revieweeEmployeeName ?? `직원 #${selectedSession.revieweeEmployeeId}`}
					</p>
				</div>
				<Badge variant="outline">{numberLabel(historySessions.length)}회</Badge>
			</div>

			<div className="mt-4 space-y-3">
				{historySessions.map((historySession) => (
					<div key={historySession.id} className="rounded-md border border-border bg-muted/20 p-3">
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="truncate text-sm font-semibold">{historySession.title}</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{dateTimeLabel(historySession.scheduledAt)}
								</p>
							</div>
							<Badge variant={oneOnOneStatusVariant(historySession.status)}>
								{oneOnOneStatusLabels[historySession.status]}
							</Badge>
						</div>
						<p className="mt-3 text-xs font-medium text-muted-foreground">
							{oneOnOneCheckInText(historySession)}
						</p>
						<p className="mt-2 text-sm text-foreground">{oneOnOneHistoryMemo(historySession)}</p>
					</div>
				))}
			</div>
		</div>
	);
}

function OneOnOneDetailPanel({
	session,
	sessions,
	goals,
	reviewCycles,
	employees,
	onDone
}: {
	session: OneOnOneSessionResponse | null;
	sessions: OneOnOneSessionResponse[];
	goals: PerformanceGoalResponse[];
	reviewCycles: PerformanceReviewCycleResponse[];
	employees: EmployeeResponse[];
	onDone: () => Promise<void>;
}) {
	const [draft, setDraft] = useState<OneOnOneDraft | null>(() =>
		session ? toOneOnOneDraft(session) : null
	);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	if (!session || !draft)
		return <EmptyState message="원온원을 선택하면 노트와 액션을 관리할 수 있습니다." />;
	const selectedSession = session;
	const activeDraft = draft;

	function updateDraft(patch: Partial<OneOnOneDraft>) {
		setDraft((current) => (current ? { ...current, ...patch } : current));
	}

	function parseOptionalScore(value: string): number | null {
		if (!value) return null;
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : Number.NaN;
	}

	async function saveSession() {
		const scores = [
			parseOptionalScore(activeDraft.checkInPersonalScore),
			parseOptionalScore(activeDraft.checkInWorkScore),
			parseOptionalScore(activeDraft.checkInOrgScore),
			parseOptionalScore(activeDraft.feedbackScore)
		].filter((value): value is number => value !== null);
		if (scores.some((score) => !Number.isFinite(score) || score < 1 || score > 5)) {
			setError('체크인과 후기 점수는 1~5 사이 숫자로 입력해주세요.');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			await api.patch<OneOnOneSessionResponse>(`/performance/one-on-ones/${selectedSession.id}`, {
				agenda: activeDraft.agenda.trim() || null,
				privateNote: activeDraft.privateNote.trim() || null,
				sharedNote: activeDraft.sharedNote.trim() || null,
				checkInPersonalScore: parseOptionalScore(activeDraft.checkInPersonalScore),
				checkInWorkScore: parseOptionalScore(activeDraft.checkInWorkScore),
				checkInOrgScore: parseOptionalScore(activeDraft.checkInOrgScore),
				linkedGoalId: activeDraft.linkedGoalId ? Number(activeDraft.linkedGoalId) : null,
				linkedReviewCycleId: activeDraft.linkedReviewCycleId
					? Number(activeDraft.linkedReviewCycleId)
					: null,
				status: activeDraft.status,
				summary: activeDraft.summary.trim() || null,
				feedbackScore: parseOptionalScore(activeDraft.feedbackScore),
				feedbackComment: activeDraft.feedbackComment.trim() || null
			});
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '원온원 노트를 저장하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="space-y-4">
			<FormError message={error} />
			<div className="rounded-lg border border-border bg-muted/20 p-4">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<p className="font-semibold">{selectedSession.title}</p>
						<p className="mt-1 text-sm text-muted-foreground">
							{dateTimeLabel(selectedSession.scheduledAt)} ·{' '}
							{selectedSession.revieweeEmployeeName ??
								`직원 #${selectedSession.revieweeEmployeeId}`}
						</p>
					</div>
					<span className="text-lg font-semibold">
						{scoreText(selectedSession.averageCheckInScore)}
					</span>
				</div>
				<div className="mt-3 flex flex-wrap gap-2">
					{selectedSession.linkedGoalTitle ? (
						<Badge variant="outline">목표 {selectedSession.linkedGoalTitle}</Badge>
					) : null}
					{selectedSession.linkedReviewCycleName ? (
						<Badge variant="outline">평가 {selectedSession.linkedReviewCycleName}</Badge>
					) : null}
					{selectedSession.recurrenceRule ? (
						<Badge variant="outline">{selectedSession.recurrenceRule}</Badge>
					) : null}
				</div>
			</div>

			<OneOnOneHistoryPanel sessions={sessions} selectedSession={selectedSession} />

			<div className="grid gap-3 sm:grid-cols-3">
				<div className="space-y-2">
					<Label htmlFor="one-on-one-personal-score">개인 상태</Label>
					<Input
						id="one-on-one-personal-score"
						type="number"
						min="1"
						max="5"
						value={draft.checkInPersonalScore}
						onChange={(event) => updateDraft({ checkInPersonalScore: event.target.value })}
						placeholder="1-5"
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="one-on-one-work-score">업무 상태</Label>
					<Input
						id="one-on-one-work-score"
						type="number"
						min="1"
						max="5"
						value={draft.checkInWorkScore}
						onChange={(event) => updateDraft({ checkInWorkScore: event.target.value })}
						placeholder="1-5"
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="one-on-one-org-score">조직 상태</Label>
					<Input
						id="one-on-one-org-score"
						type="number"
						min="1"
						max="5"
						value={draft.checkInOrgScore}
						onChange={(event) => updateDraft({ checkInOrgScore: event.target.value })}
						placeholder="1-5"
					/>
				</div>
			</div>

			<div className="grid gap-3 sm:grid-cols-2">
				<div className="space-y-2">
					<Label htmlFor="one-on-one-linked-goal">연결 목표</Label>
					<select
						id="one-on-one-linked-goal"
						value={draft.linkedGoalId}
						onChange={(event) => updateDraft({ linkedGoalId: event.target.value })}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="">연결 목표 없음</option>
						{goals.map((goal) => (
							<option key={goal.id} value={goal.id}>
								{goal.title}
							</option>
						))}
					</select>
				</div>
				<div className="space-y-2">
					<Label htmlFor="one-on-one-linked-review">연결 평가</Label>
					<select
						id="one-on-one-linked-review"
						value={draft.linkedReviewCycleId}
						onChange={(event) => updateDraft({ linkedReviewCycleId: event.target.value })}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="">연결 평가 없음</option>
						{reviewCycles.map((cycle) => (
							<option key={cycle.id} value={cycle.id}>
								{cycle.name}
							</option>
						))}
					</select>
				</div>
			</div>

			<div className="space-y-2">
				<Label htmlFor="one-on-one-agenda">아젠다</Label>
				<Input
					id="one-on-one-agenda"
					value={draft.agenda}
					onChange={(event) => updateDraft({ agenda: event.target.value })}
					placeholder="대화 주제"
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="one-on-one-shared-note">공유 메모</Label>
				<Input
					id="one-on-one-shared-note"
					value={draft.sharedNote}
					onChange={(event) => updateDraft({ sharedNote: event.target.value })}
					placeholder="함께 확인할 합의 내용"
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="one-on-one-private-note">비밀 메모</Label>
				<Input
					id="one-on-one-private-note"
					value={draft.privateNote}
					onChange={(event) => updateDraft({ privateNote: event.target.value })}
					placeholder="리더만 보는 관찰 메모"
				/>
			</div>
			<div className="grid gap-3 sm:grid-cols-[1fr_7rem]">
				<div className="space-y-2">
					<Label htmlFor="one-on-one-summary">요약</Label>
					<Input
						id="one-on-one-summary"
						value={draft.summary}
						onChange={(event) => updateDraft({ summary: event.target.value })}
						placeholder="다음 원온원 준비에 필요한 요약"
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="one-on-one-feedback-score">후기</Label>
					<Input
						id="one-on-one-feedback-score"
						type="number"
						min="1"
						max="5"
						value={draft.feedbackScore}
						onChange={(event) => updateDraft({ feedbackScore: event.target.value })}
						placeholder="1-5"
					/>
				</div>
			</div>
			<div className="grid gap-3 sm:grid-cols-[1fr_auto]">
				<select
					value={draft.status}
					onChange={(event) => updateDraft({ status: event.target.value as OneOnOneSessionStatus })}
					className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					aria-label="원온원 상태"
				>
					<option value="SCHEDULED">예정</option>
					<option value="COMPLETED">완료</option>
					<option value="CANCELED">취소</option>
				</select>
				<Button type="button" onClick={() => void saveSession()} disabled={submitting}>
					{submitting ? '저장 중...' : '원온원 노트 저장'}
				</Button>
			</div>

			<div className="space-y-3">
				<div>
					<p className="text-sm font-semibold">액션 아이템</p>
					<p className="mt-1 text-xs text-muted-foreground">
						대화가 실행으로 이어지도록 후속 작업을 관리합니다.
					</p>
				</div>
				<OneOnOneActionItems session={selectedSession} onDone={onDone} />
				<OneOnOneActionCreateForm
					key={selectedSession.id}
					session={selectedSession}
					employees={employees}
					onDone={onDone}
				/>
			</div>
		</div>
	);
}

function OneOnOneCreateForm({
	employees,
	goals,
	reviewCycles,
	onDone
}: {
	employees: EmployeeResponse[];
	goals: PerformanceGoalResponse[];
	reviewCycles: PerformanceReviewCycleResponse[];
	onDone: () => Promise<void>;
}) {
	const activeEmployees = employees.filter((employee) => employee.status === 'ACTIVE');
	const [title, setTitle] = useState('');
	const [managerEmployeeId, setManagerEmployeeId] = useState('');
	const [revieweeEmployeeId, setRevieweeEmployeeId] = useState('');
	const [scheduledAt, setScheduledAt] = useState(defaultOneOnOneDateTime);
	const [agenda, setAgenda] = useState('');
	const [privateNote, setPrivateNote] = useState('');
	const [linkedGoalId, setLinkedGoalId] = useState('');
	const [linkedReviewCycleId, setLinkedReviewCycleId] = useState('');
	const [recurrenceRule, setRecurrenceRule] = useState('매월 첫째 주');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const trimmedTitle = title.trim();
		if (!trimmedTitle) {
			setError('원온원 제목을 입력해주세요.');
			return;
		}
		if (!managerEmployeeId || !revieweeEmployeeId) {
			setError('리더와 대상자를 선택해주세요.');
			return;
		}
		if (managerEmployeeId === revieweeEmployeeId) {
			setError('리더와 대상자는 서로 달라야 합니다.');
			return;
		}
		if (!scheduledAt) {
			setError('일정을 입력해주세요.');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			await api.post<OneOnOneSessionResponse>('/performance/one-on-ones', {
				title: trimmedTitle,
				managerEmployeeId: Number(managerEmployeeId),
				revieweeEmployeeId: Number(revieweeEmployeeId),
				scheduledAt,
				agenda: agenda.trim() || null,
				privateNote: privateNote.trim() || null,
				linkedGoalId: linkedGoalId ? Number(linkedGoalId) : null,
				linkedReviewCycleId: linkedReviewCycleId ? Number(linkedReviewCycleId) : null,
				recurrenceRule: recurrenceRule.trim() || null
			});
			setTitle('');
			setAgenda('');
			setPrivateNote('');
			await onDone();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '원온원을 생성하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<form className="space-y-4" onSubmit={handleSubmit}>
			<FormError message={error} />
			<div className="space-y-2">
				<Label htmlFor="one-on-one-title">제목</Label>
				<Input
					id="one-on-one-title"
					value={title}
					onChange={(event) => setTitle(event.target.value)}
					placeholder="예: 하반기 성장 원온원"
				/>
			</div>
			<div className="grid gap-3 sm:grid-cols-2">
				<div className="space-y-2">
					<Label htmlFor="one-on-one-manager">리더</Label>
					<select
						id="one-on-one-manager"
						value={managerEmployeeId}
						onChange={(event) => setManagerEmployeeId(event.target.value)}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="">리더 선택</option>
						{activeEmployees.map((employee) => (
							<option key={employee.id} value={employee.id}>
								{employee.name}
							</option>
						))}
					</select>
				</div>
				<div className="space-y-2">
					<Label htmlFor="one-on-one-reviewee">대상자</Label>
					<select
						id="one-on-one-reviewee"
						value={revieweeEmployeeId}
						onChange={(event) => setRevieweeEmployeeId(event.target.value)}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="">대상자 선택</option>
						{activeEmployees.map((employee) => (
							<option key={employee.id} value={employee.id}>
								{employee.name}
							</option>
						))}
					</select>
				</div>
				<div className="space-y-2">
					<Label htmlFor="one-on-one-scheduled-at">일정</Label>
					<Input
						id="one-on-one-scheduled-at"
						type="datetime-local"
						value={scheduledAt}
						onChange={(event) => setScheduledAt(event.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="one-on-one-recurrence">반복</Label>
					<Input
						id="one-on-one-recurrence"
						value={recurrenceRule}
						onChange={(event) => setRecurrenceRule(event.target.value)}
						placeholder="예: 매월 첫째 주"
					/>
				</div>
			</div>
			<div className="space-y-2">
				<Label htmlFor="one-on-one-create-agenda">아젠다</Label>
				<Input
					id="one-on-one-create-agenda"
					value={agenda}
					onChange={(event) => setAgenda(event.target.value)}
					placeholder="상태 체크, 목표, 평가 피드백 등"
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="one-on-one-create-private-note">비밀 메모</Label>
				<Input
					id="one-on-one-create-private-note"
					value={privateNote}
					onChange={(event) => setPrivateNote(event.target.value)}
					placeholder="리더만 보는 준비 메모"
				/>
			</div>
			<div className="grid gap-3 sm:grid-cols-2">
				<div className="space-y-2">
					<Label htmlFor="one-on-one-create-goal">연결 목표</Label>
					<select
						id="one-on-one-create-goal"
						value={linkedGoalId}
						onChange={(event) => setLinkedGoalId(event.target.value)}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="">연결 목표 없음</option>
						{goals.map((goal) => (
							<option key={goal.id} value={goal.id}>
								{goal.title}
							</option>
						))}
					</select>
				</div>
				<div className="space-y-2">
					<Label htmlFor="one-on-one-create-review">연결 평가</Label>
					<select
						id="one-on-one-create-review"
						value={linkedReviewCycleId}
						onChange={(event) => setLinkedReviewCycleId(event.target.value)}
						className="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
					>
						<option value="">연결 평가 없음</option>
						{reviewCycles.map((cycle) => (
							<option key={cycle.id} value={cycle.id}>
								{cycle.name}
							</option>
						))}
					</select>
				</div>
			</div>
			<Button type="submit" disabled={submitting || activeEmployees.length < 2} className="w-full">
				<Plus className="size-4" />
				{submitting ? '생성 중...' : '원온원 만들기'}
			</Button>
		</form>
	);
}

export function OneOnOnesPage() {
	const [state, setState] = useState<RequestState<OneOnOnesPageData>>({ status: 'idle' });
	const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [summary, operations, sessions, goals, reviewCycles, employeePage] =
					await Promise.all([
						api.get<OneOnOneSummaryResponse>('/performance/one-on-ones/summary'),
						api
							.get<OneOnOneOperationsSummaryResponse>('/performance/one-on-ones/operations/summary')
							.catch(() => null),
						api.get<OneOnOneSessionResponse[]>('/performance/one-on-ones'),
						api.get<PerformanceGoalResponse[]>('/performance/goals'),
						api.get<PerformanceReviewCycleResponse[]>('/performance/reviews'),
						api.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200')
					]);
				setState({
					status: 'success',
					data: {
						summary,
						operations,
						sessions,
						goals,
						reviewCycles,
						employees: employeePage.content
					}
				});
				setSelectedSessionId((current) =>
					sessions.some((session) => session.id === current) ? current : (sessions[0]?.id ?? null)
				);
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const selectedSession =
		data?.sessions.find((session) => session.id === selectedSessionId) ?? null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="원온원"
				description="목표와 평가 맥락을 보며 체크인, 노트, 액션 아이템을 관리합니다."
				actions={
					<Button variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<OneOnOneStatCard
							title="원온원"
							value={`${numberLabel(data.summary.totalSessions)}개`}
							description={`예정 ${numberLabel(data.summary.scheduledSessions)}개`}
							icon={UsersRound}
						/>
						<OneOnOneStatCard
							title="완료"
							value={`${numberLabel(data.summary.completedSessions)}개`}
							description="기록이 저장된 대화"
							icon={CheckCircle2}
						/>
						<OneOnOneStatCard
							title="미완료 액션"
							value={`${numberLabel(data.summary.openActionItems)}건`}
							description="후속 실행 필요"
							icon={AlertTriangle}
						/>
						<OneOnOneStatCard
							title="평균 체크인"
							value={scoreText(data.summary.averageCheckInScore)}
							description="개인·업무·조직 상태"
							icon={TrendingUp}
						/>
					</div>

					{data.operations ? <OneOnOneOperationsPanel operations={data.operations} /> : null}

					<div className="grid gap-4 xl:grid-cols-[1fr_0.95fr]">
						<Card>
							<CardHeader>
								<CardTitle>원온원 일정</CardTitle>
								<CardDescription>
									리더, 대상자, 목표·평가 연결, 액션 현황을 확인합니다.
								</CardDescription>
							</CardHeader>
							<CardContent>
								<OneOnOneSessionList
									sessions={data.sessions}
									selectedSessionId={selectedSessionId}
									onSelect={setSelectedSessionId}
								/>
							</CardContent>
						</Card>

						<div className="space-y-4">
							<Card>
								<CardHeader>
									<CardTitle>대화 노트</CardTitle>
									<CardDescription>
										상태 체크, 공유 메모, 비밀 메모, 후속 실행을 저장합니다.
									</CardDescription>
								</CardHeader>
								<CardContent>
									<OneOnOneDetailPanel
										key={selectedSession?.id ?? 'none'}
										session={selectedSession}
										sessions={data.sessions}
										goals={data.goals}
										reviewCycles={data.reviewCycles}
										employees={data.employees}
										onDone={load}
									/>
								</CardContent>
							</Card>

							<Card>
								<CardHeader>
									<CardTitle>원온원 생성</CardTitle>
									<CardDescription>
										반복 대화 주기와 참고할 목표·평가를 함께 설정합니다.
									</CardDescription>
								</CardHeader>
								<CardContent>
									<OneOnOneCreateForm
										employees={data.employees}
										goals={data.goals}
										reviewCycles={data.reviewCycles}
										onDone={load}
									/>
								</CardContent>
							</Card>
						</div>
					</div>
				</>
			) : null}
		</div>
	);
}

export function EmployeesPage() {
	const [state, setState] = useState<RequestState<EmployeesPageData>>({
		status: 'idle'
	});
	const [filters, setFilters] = useState<EmployeeSearchFilters>(emptyEmployeeSearchFilters);
	const [appliedFilters, setAppliedFilters] = useState<EmployeeSearchFilters>(
		emptyEmployeeSearchFilters
	);
	const [presetName, setPresetName] = useState('');
	const [filterFeedback, setFilterFeedback] = useState<string | null>(null);
	const [filterSubmitting, setFilterSubmitting] = useState<string | null>(null);
	const [inviteFeedback, setInviteFeedback] = useState<InviteFeedback | null>(null);

	const loadForFilters = useCallback(async (nextFilters: EmployeeSearchFilters) => {
		setState({ status: 'loading' });
		try {
			const [employees, operations, personnelSchedule, jobCatalogItems, searchPresets] =
				await Promise.all([
					api.get<PageResponse<EmployeeResponse>>(employeeListPath(nextFilters)),
					api
						.get<PersonnelManagementSummaryResponse>('/employees/operations/summary')
						.catch(() => null),
					api.get<PersonnelScheduleResponse>('/employees/operations/schedule').catch(() => null),
					api.get<EmployeeJobCatalogResponse[]>('/employees/job-catalog'),
					api.get<EmployeeSearchPresetResponse[]>('/employees/search-presets').catch(() => [])
				]);
			setState({
				status: 'success',
				data: { employees, operations, personnelSchedule, jobCatalogItems, searchPresets }
			});
		} catch (error) {
			setState({ status: 'error', message: getErrorMessage(error) });
		}
	}, []);

	const load = useCallback(async () => {
		await loadForFilters(appliedFilters);
	}, [appliedFilters, loadForFilters]);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await loadForFilters(emptyEmployeeSearchFilters);
		});
		return () => {
			active = false;
		};
	}, [loadForFilters]);

	const employeePage = state.status === 'success' ? state.data.employees : null;
	const operations = state.status === 'success' ? state.data.operations : null;
	const searchPresets = state.status === 'success' ? state.data.searchPresets : [];
	const employees = useMemo(() => employeePage?.content ?? [], [employeePage]);
	const departmentOptions = useMemo(() => {
		const names = new Set<string>();
		operations?.departmentInsights.forEach((insight) => {
			if (insight.department) names.add(insight.department);
		});
		employees.forEach((employee) => {
			if (employee.department) names.add(employee.department);
		});
		return [...names].sort((a, b) => a.localeCompare(b, 'ko'));
	}, [employees, operations]);

	const linkedCount = employees.filter((employee) => employee.inviteStatus === 'LINKED').length;
	const attendanceEnabledCount = employees.filter((employee) => employee.attendanceEnabled).length;
	const incompleteProfiles = employees.filter(
		(employee) => employee.profileComplete === false || (employee.profileCompletionRate ?? 100) < 80
	).length;
	const totalEmployees = employeePage?.totalElements ?? employees.length;

	async function handleInviteFeedback(feedback: InviteFeedback) {
		setInviteFeedback(feedback);
		if (feedback.kind === 'success') await load();
	}

	async function handleApplyFilters(nextFilters: EmployeeSearchFilters = filters) {
		const snapshot = cloneEmployeeSearchFilters(nextFilters);
		setFilterSubmitting('search');
		setFilterFeedback(null);
		try {
			setAppliedFilters(snapshot);
			await loadForFilters(snapshot);
		} finally {
			setFilterSubmitting(null);
		}
	}

	async function handleClearFilters() {
		const snapshot = cloneEmployeeSearchFilters(emptyEmployeeSearchFilters);
		setFilters(snapshot);
		await handleApplyFilters(snapshot);
	}

	async function handleApplyPreset(preset: EmployeeSearchPresetResponse) {
		const snapshot = filtersFromEmployeeSearchPreset(preset);
		setFilters(snapshot);
		setFilterSubmitting(`apply-${preset.id}`);
		setFilterFeedback(null);
		try {
			setAppliedFilters(snapshot);
			await loadForFilters(snapshot);
		} finally {
			setFilterSubmitting(null);
		}
	}

	async function handleSavePreset() {
		if (!presetName.trim()) {
			setFilterFeedback('저장 조건 이름을 입력해주세요.');
			return;
		}
		if (!hasEmployeeSearchFilter(filters)) {
			setFilterFeedback('저장할 검색 조건을 1개 이상 선택해주세요.');
			return;
		}
		const snapshot = cloneEmployeeSearchFilters(filters);
		setFilterSubmitting('save');
		setFilterFeedback(null);
		try {
			await api.post<EmployeeSearchPresetResponse>(
				'/employees/search-presets',
				requestFromEmployeeSearchFilters(presetName, snapshot)
			);
			setPresetName('');
			setAppliedFilters(snapshot);
			await loadForFilters(snapshot);
			setFilterFeedback('검색 조건을 저장했습니다.');
		} catch (error) {
			setFilterFeedback(getErrorMessage(error, '검색 조건을 저장하지 못했습니다.'));
		} finally {
			setFilterSubmitting(null);
		}
	}

	async function handleDeletePreset(preset: EmployeeSearchPresetResponse) {
		setFilterSubmitting(`delete-${preset.id}`);
		setFilterFeedback(null);
		try {
			await api.delete(`/employees/search-presets/${preset.id}`);
			await loadForFilters(appliedFilters);
			setFilterFeedback('검색 조건을 삭제했습니다.');
		} catch (error) {
			setFilterFeedback(getErrorMessage(error, '검색 조건을 삭제하지 못했습니다.'));
		} finally {
			setFilterSubmitting(null);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="직원"
				description="직원 정보, 초대 상태, 출퇴근 사용 여부를 한 화면에서 관리합니다."
				actions={
					<Button variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}

			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			<InviteFeedbackBanner feedback={inviteFeedback} />

			{state.status === 'success' ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<EmployeeStatCard
							title="전체 직원"
							value={totalEmployees}
							description="등록된 인사 기록"
						/>
						<EmployeeStatCard title="계정 연동" value={linkedCount} description="초대 수락 완료" />
						<EmployeeStatCard
							title="출퇴근 사용"
							value={attendanceEnabledCount}
							description="근태 기록 대상"
						/>
						<EmployeeStatCard
							title="보완 필요"
							value={incompleteProfiles}
							description="프로필 80% 미만"
						/>
					</div>

					{operations ? (
						<PersonnelOperationsPanel
							operations={operations}
							personnelSchedule={state.data.personnelSchedule}
						/>
					) : null}

					<EmployeeJobCatalogPanel items={state.data.jobCatalogItems} onDone={load} />

					<Card>
						<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
							<div>
								<CardTitle>직원 현황</CardTitle>
								<CardDescription>
									자주 쓰는 직원 검색 조건을 저장하고 초대, 프로필, 출퇴근 상태를 기준으로
									확인합니다.
								</CardDescription>
							</div>
							<Badge variant={hasEmployeeSearchFilter(appliedFilters) ? 'success' : 'outline'}>
								{hasEmployeeSearchFilter(appliedFilters) ? '필터 적용 중' : '전체 목록'}
							</Badge>
						</CardHeader>
						<CardContent className="space-y-5">
							<form
								className="space-y-4 rounded-lg border border-border bg-muted/20 p-4"
								onSubmit={(event) => {
									event.preventDefault();
									void handleApplyFilters();
								}}
							>
								<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
									<div className="space-y-2">
										<Label htmlFor="employee-search-keyword">검색어</Label>
										<div className="relative">
											<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
											<Input
												id="employee-search-keyword"
												value={filters.keyword}
												onChange={(event) =>
													setFilters({ ...filters, keyword: event.target.value })
												}
												placeholder="이름, 이메일, 사번"
												className="pl-9"
												aria-label="검색어"
											/>
										</div>
									</div>
									<div className="space-y-2">
										<Label htmlFor="employee-search-status">재직 상태</Label>
										<select
											id="employee-search-status"
											value={filters.status}
											onChange={(event) =>
												setFilters({
													...filters,
													status: event.target.value as EmployeeStatusFilter
												})
											}
											className={dialogControlClassName}
											aria-label="재직 상태"
										>
											{employeeStatusFilterOptions.map((option) => (
												<option key={option.value || 'all'} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="employee-search-invite-status">초대 상태</Label>
										<select
											id="employee-search-invite-status"
											value={filters.inviteStatus}
											onChange={(event) =>
												setFilters({
													...filters,
													inviteStatus: event.target.value as EmployeeInviteStatusFilter
												})
											}
											className={dialogControlClassName}
											aria-label="초대 상태"
										>
											{employeeInviteStatusFilterOptions.map((option) => (
												<option key={option.value || 'all'} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="employee-search-invite-email">초대 메일</Label>
										<select
											id="employee-search-invite-email"
											value={filters.inviteEmailDeliveryStatus}
											onChange={(event) =>
												setFilters({
													...filters,
													inviteEmailDeliveryStatus: event.target
														.value as EmployeeInviteEmailDeliveryStatusFilter
												})
											}
											className={dialogControlClassName}
											aria-label="초대 메일"
										>
											{employeeInviteEmailStatusFilterOptions.map((option) => (
												<option key={option.value || 'all'} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="employee-search-profile">프로필 완성도</Label>
										<select
											id="employee-search-profile"
											value={filters.profileCompletion}
											onChange={(event) =>
												setFilters({
													...filters,
													profileCompletion: event.target.value as EmployeeProfileCompletionFilter
												})
											}
											className={dialogControlClassName}
											aria-label="프로필 완성도"
										>
											{employeeProfileCompletionFilterOptions.map((option) => (
												<option key={option.value || 'all'} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="employee-search-attendance">근태 상태</Label>
										<select
											id="employee-search-attendance"
											value={filters.attendanceStatus}
											onChange={(event) =>
												setFilters({
													...filters,
													attendanceStatus: event.target.value as EmployeeAttendanceStatusFilter
												})
											}
											className={dialogControlClassName}
											aria-label="근태 상태"
										>
											{employeeAttendanceStatusFilterOptions.map((option) => (
												<option key={option.value || 'all'} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="employee-search-department">조직</Label>
										<Input
											id="employee-search-department"
											value={filters.department}
											onChange={(event) =>
												setFilters({
													...filters,
													department: event.target.value,
													departmentUnassigned: false
												})
											}
											list="employee-search-departments"
											placeholder="조직명"
											disabled={filters.departmentUnassigned}
											aria-label="조직"
										/>
										<datalist id="employee-search-departments">
											{departmentOptions.map((department) => (
												<option key={department} value={department} />
											))}
										</datalist>
									</div>
									<label className="flex min-h-10 items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm md:mt-7">
										<input
											type="checkbox"
											checked={filters.departmentUnassigned}
											onChange={(event) =>
												setFilters({
													...filters,
													department: '',
													departmentUnassigned: event.target.checked
												})
											}
											aria-label="조직 미배정"
										/>
										<span>조직 미배정</span>
									</label>
								</div>

								<div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
									<div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] xl:w-[560px]">
										<div className="space-y-2">
											<Label htmlFor="employee-search-preset-name">저장 조건 이름</Label>
											<Input
												id="employee-search-preset-name"
												value={presetName}
												onChange={(event) => setPresetName(event.target.value)}
												placeholder="예: 프로필 보완 대상"
												aria-label="저장 조건 이름"
											/>
										</div>
										<Button
											type="button"
											variant="outline"
											onClick={() => void handleSavePreset()}
											disabled={filterSubmitting === 'save'}
											className="sm:mt-7"
										>
											<Plus className="size-4" />
											{filterSubmitting === 'save' ? '저장 중...' : '조건 저장'}
										</Button>
									</div>
									<div className="flex flex-wrap gap-2 xl:justify-end">
										<Button
											type="button"
											variant="outline"
											onClick={() => void handleClearFilters()}
											disabled={filterSubmitting === 'search'}
										>
											초기화
										</Button>
										<Button type="submit" disabled={filterSubmitting === 'search'}>
											<Search className="size-4" />
											{filterSubmitting === 'search' ? '검색 중...' : '검색'}
										</Button>
									</div>
								</div>

								{filterFeedback ? (
									<div className="rounded-md border border-border bg-background px-3 py-2 text-sm">
										{filterFeedback}
									</div>
								) : null}
							</form>

							<div className="rounded-lg border border-border bg-background p-4">
								<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
									<div>
										<p className="font-semibold">저장된 검색 조건</p>
										<p className="mt-1 text-sm text-muted-foreground">
											반복 조회하는 직원 그룹을 한 번에 불러옵니다.
										</p>
									</div>
									<Badge variant="outline">{numberLabel(searchPresets.length)}개</Badge>
								</div>
								<div className="mt-4 grid gap-3 lg:grid-cols-2">
									{searchPresets.length > 0 ? (
										searchPresets.map((preset) => (
											<div
												key={preset.id}
												className="flex min-w-0 items-start gap-2 rounded-md border border-border bg-muted/20 p-3"
											>
												<button
													type="button"
													className="min-w-0 flex-1 text-left"
													onClick={() => void handleApplyPreset(preset)}
													disabled={filterSubmitting === `apply-${preset.id}`}
													aria-label={`${preset.name} 적용`}
												>
													<p className="font-medium">{preset.name}</p>
													<p className="mt-1 break-words text-sm text-muted-foreground">
														{preset.filterSummary}
													</p>
												</button>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => void handleDeletePreset(preset)}
													disabled={filterSubmitting === `delete-${preset.id}`}
													aria-label={`${preset.name} 삭제`}
												>
													<Trash2 className="size-4" />
												</Button>
											</div>
										))
									) : (
										<p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
											저장된 검색 조건이 없습니다.
										</p>
									)}
								</div>
							</div>

							<EmployeeTable employees={employees} onInviteFeedback={handleInviteFeedback} />
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}

function EmployeeInfoCard({ employee }: { employee: EmployeeResponse }) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>인사 기본 정보</CardTitle>
				<CardDescription>직원 식별 정보, 초대 상태, 출퇴근 사용 여부를 확인합니다.</CardDescription>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="flex flex-wrap items-start justify-between gap-4">
					<div>
						<p className="text-2xl font-semibold tracking-normal">{employee.name}</p>
						<p className="mt-1 text-sm text-muted-foreground">
							{employee.department ?? '부서 미지정'} · {employee.position ?? '직책 미지정'}
						</p>
					</div>
					<div className="flex flex-wrap gap-2">
						<Badge variant={employeeStatusVariant(employee.status)}>
							{employeeStatusLabel(employee.status)}
						</Badge>
						<Badge variant={inviteStatusVariant(employee.inviteStatus)}>
							{inviteStatusLabel(employee.inviteStatus)}
						</Badge>
						<Badge variant={employee.profileComplete ? 'success' : 'warning'}>
							{employeeProfileLabel(employee)}
						</Badge>
					</div>
				</div>
				<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
					<DetailTile label="사번" value={employee.employeeNumber ?? '-'} />
					<DetailTile label="이메일" value={employee.email ?? '-'} />
					<DetailTile label="연락처" value={employee.phone ?? '-'} />
					<DetailTile label="입사일" value={dateLabel(employee.joinDate)} />
					<DetailTile label="인정 입사일" value={dateLabel(employee.recognizedServiceStartDate)} />
					<DetailTile label="출퇴근" value={employee.attendanceEnabled ? '사용' : '미사용'} />
					<DetailTile label="단말 ID" value={employee.attendanceDeviceId ?? '-'} />
					<DetailTile label="프로필" value={employeeProfileLabel(employee)} />
				</div>
			</CardContent>
		</Card>
	);
}

function EmployeePayrollExclusionCard({
	employee,
	onChanged
}: {
	employee: EmployeeResponse;
	onChanged: () => void | Promise<void>;
}) {
	const excluded = employee.payrollExcluded === true;
	const [pendingPayrollExcluded, setPendingPayrollExcluded] = useState<boolean | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const nextPayrollExcluded = pendingPayrollExcluded ?? !excluded;

	function closeDialog() {
		if (submitting) return;
		setPendingPayrollExcluded(null);
		setError(null);
	}

	async function confirmChange(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (pendingPayrollExcluded === null) return;
		setSubmitting(true);
		setError(null);
		try {
			await api.patch<EmployeeResponse>(`/employees/${employee.id}/payroll-exclusion`, {
				payrollExcluded: nextPayrollExcluded
			});
			setPendingPayrollExcluded(null);
			await onChanged();
		} catch (error) {
			setError(getErrorMessage(error, '급여 정산 설정을 변경하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<>
			<Card>
				<CardHeader className="gap-4 lg:flex-row lg:items-center lg:justify-between">
					<div>
						<CardTitle>급여 정산</CardTitle>
						<CardDescription>
							무보수 대표 등 급여 지급 대상이 아닌 구성원은 정산에서 제외할 수 있습니다.
						</CardDescription>
					</div>
					<Badge variant={excluded ? 'outline' : 'success'}>
						{excluded ? '정산 제외' : '정산 대상'}
					</Badge>
				</CardHeader>
				<CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
					<p className="text-sm text-muted-foreground">
						{excluded
							? '급여 정산과 명세서 생성에서 제외되어 있습니다.'
							: '급여 정산과 명세서 생성 대상입니다.'}
					</p>
					<Button
						type="button"
						variant={excluded ? 'outline' : 'destructive'}
						onClick={() => setPendingPayrollExcluded(!excluded)}
					>
						{excluded ? '정산 대상에 포함' : '급여 정산에서 제외'}
					</Button>
				</CardContent>
			</Card>
			{pendingPayrollExcluded !== null ? (
				<Modal
					title="급여 정산 설정 변경"
					description={
						nextPayrollExcluded
							? '이 직원은 이후 급여 정산과 명세서 생성 대상에서 제외됩니다.'
							: '이 직원을 이후 급여 정산과 명세서 생성 대상에 포함합니다.'
					}
					onClose={closeDialog}
				>
					<form className="space-y-4" onSubmit={(event) => void confirmChange(event)}>
						<FormError message={error} />
						<p className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
							{nextPayrollExcluded
								? '무보수 대표를 포함해, 급여를 지급하지 않는 구성원에게 사용하세요.'
								: '다음 급여 정산부터 이 직원의 템플릿과 명세서를 다시 설정할 수 있습니다.'}
						</p>
						<div className="flex justify-end gap-2">
							<Button type="button" variant="outline" onClick={closeDialog} disabled={submitting}>
								취소
							</Button>
							<Button
								type="submit"
								variant={nextPayrollExcluded ? 'destructive' : 'default'}
								disabled={submitting}
							>
								{submitting
									? '저장 중...'
									: nextPayrollExcluded
										? '정산 제외 확인'
										: '정산 대상 포함 확인'}
							</Button>
						</div>
					</form>
				</Modal>
			) : null}
		</>
	);
}

function DetailTile({ label, value }: { label: string; value: ReactNode }) {
	return (
		<div className="rounded-lg border border-border bg-muted/20 p-4">
			<p className="text-xs font-medium text-muted-foreground">{label}</p>
			<p className="mt-2 break-words text-sm font-semibold">{value}</p>
		</div>
	);
}

function EmployeeAttendanceHistoryCard({ attendances }: { attendances: AttendanceResponse[] }) {
	const totalWorkMinutes = attendances.reduce(
		(sum, attendance) => sum + (attendance.workMinutes ?? 0),
		0
	);
	const totalOvertimeMinutes = attendances.reduce(
		(sum, attendance) => sum + (attendance.overtimeMinutes ?? 0),
		0
	);
	const riskCount = attendances.filter(hasAttendanceRisk).length;

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-center lg:justify-between">
				<div>
					<CardTitle>구성원 근무 이력</CardTitle>
					<CardDescription>
						이번 달 출퇴근 기록, 초과근무, 미퇴근·위치 검증 리스크를 확인합니다.
					</CardDescription>
				</div>
				<Badge variant={riskCount > 0 ? 'warning' : 'success'}>
					주의 {numberLabel(riskCount)}건
				</Badge>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="grid gap-3 sm:grid-cols-3">
					<DetailTile label="기록" value={`기록 ${numberLabel(attendances.length)}일`} />
					<DetailTile label="총 근무" value={`총 근무 ${minutesLabel(totalWorkMinutes)}`} />
					<DetailTile label="초과근무" value={minutesLabel(totalOvertimeMinutes)} />
				</div>
				{attendances.length === 0 ? (
					<EmptyState message="이번 달 근무 이력이 없습니다." />
				) : (
					<div className="overflow-x-auto rounded-lg border border-border">
						<table className="min-w-[760px] text-left text-sm">
							<thead className="text-xs text-muted-foreground">
								<tr className="border-b border-border">
									<th className="whitespace-nowrap px-4 py-3 font-semibold">일자</th>
									<th className="whitespace-nowrap px-4 py-3 font-semibold">출퇴근</th>
									<th className="whitespace-nowrap px-4 py-3 font-semibold">근무</th>
									<th className="whitespace-nowrap px-4 py-3 font-semibold">상태</th>
									<th className="whitespace-nowrap px-4 py-3 font-semibold">메모</th>
								</tr>
							</thead>
							<tbody>
								{attendances.slice(0, 8).map((attendance) => (
									<tr key={attendance.id ?? attendance.date} className="border-b border-border/60">
										<td className="whitespace-nowrap px-4 py-3 font-medium">
											{dateLabel(attendance.date)}
										</td>
										<td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
											{timeLabel(attendance.checkInTime)} - {timeLabel(attendance.checkOutTime)}
										</td>
										<td className="whitespace-nowrap px-4 py-3">
											<p className="font-medium">
												{attendance.workHoursFormatted ?? minutesLabel(attendance.workMinutes)}
											</p>
											<p className="mt-1 text-xs text-muted-foreground">
												휴게 {minutesLabel(attendanceBreakMinutes(attendance))} · 초과{' '}
												{minutesLabel(attendance.overtimeMinutes)}
											</p>
										</td>
										<td className="px-4 py-3">
											<div className="flex flex-wrap gap-2">
												<StatusBadge status={attendance.status} />
												{attendance.checkOutTime == null ? (
													<Badge variant="warning">미퇴근</Badge>
												) : null}
												{attendance.overWeeklyLimit ? (
													<Badge variant="destructive">주 한도</Badge>
												) : null}
												{attendance.locationVerified === false ? (
													<Badge variant="warning">위치 미확인</Badge>
												) : null}
											</div>
										</td>
										<td className="max-w-xs px-4 py-3 text-muted-foreground">
											<span className="line-clamp-2">{attendance.note ?? '-'}</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

type EmployeeDetailData = {
	employee: EmployeeResponse;
	profile: EmployeeProfile | null;
	customFields: EmployeeCustomFieldValueResponse[];
	personnelFile: EmployeePersonnelFileResponse | null;
	attendanceHistory: AttendanceResponse[];
	myProfileChangeRequests: EmployeeProfileChangeRequestResponse[];
	pendingProfileChangeRequests: EmployeeProfileChangeRequestResponse[];
	myCustomFieldChangeRequests: EmployeeCustomFieldChangeRequestResponse[];
	pendingCustomFieldChangeRequests: EmployeeCustomFieldChangeRequestResponse[];
};

function currentMonthAttendanceRange(): { startDate: string; endDate: string } {
	const now = new Date();
	const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
	const dateValue = (date: Date) =>
		`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
	return {
		startDate: dateValue(firstDay),
		endDate: dateValue(now)
	};
}

function hasAttendanceRisk(attendance: AttendanceResponse): boolean {
	return (
		attendance.status !== 'NORMAL' ||
		attendance.checkOutTime == null ||
		attendance.overWeeklyLimit === true ||
		attendance.locationVerified === false ||
		attendance.autoCheckedOut === true
	);
}

const personnelDocumentCategoryLabels: Record<PersonnelDocumentCategory, string> = {
	CONTRACT: '계약',
	IDENTITY: '신분',
	CERTIFICATE: '증명',
	EDUCATION: '학력',
	FAMILY: '가족',
	PAYROLL: '급여',
	ETC: '기타'
};

const personnelNoteCategoryLabels: Record<PersonnelNoteCategory, string> = {
	GENERAL: '일반',
	GROWTH: '성장',
	RISK: '리스크',
	COMPENSATION: '보상',
	ONBOARDING: '온보딩',
	RELATION: '관계'
};

const personnelEventTypeLabels: Record<PersonnelEventType, string> = {
	AWARD: '수상',
	DISCIPLINE: '징계'
};

const personnelEventSeverityLabels: Record<PersonnelEventSeverity, string> = {
	POSITIVE: '긍정',
	INFO: '기록',
	WARNING: '주의',
	CRITICAL: '중대'
};

function personnelSeverityVariant(severity: PersonnelEventSeverity) {
	if (severity === 'POSITIVE') return 'success';
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	return 'outline';
}

function profileChangeStatusLabel(status: EmployeeProfileChangeRequestStatus): string {
	if (status === 'PENDING') return '대기';
	if (status === 'APPROVED') return '승인';
	return '반려';
}

function profileChangeStatusVariant(
	status: EmployeeProfileChangeRequestStatus
): 'success' | 'warning' | 'destructive' {
	if (status === 'APPROVED') return 'success';
	if (status === 'REJECTED') return 'destructive';
	return 'warning';
}

function customFieldChangeStatusLabel(status: EmployeeCustomFieldChangeRequestStatus): string {
	if (status === 'PENDING') return '대기';
	if (status === 'APPROVED') return '승인';
	return '반려';
}

function customFieldChangeStatusVariant(
	status: EmployeeCustomFieldChangeRequestStatus
): 'success' | 'warning' | 'destructive' {
	if (status === 'APPROVED') return 'success';
	if (status === 'REJECTED') return 'destructive';
	return 'warning';
}

function profileChangeValue(value: string | null | undefined): string {
	const normalized = value?.trim();
	return normalized ? normalized : '-';
}

function customFieldChangeValue(value: string | null | undefined): string {
	return profileChangeValue(value);
}

function ProfileChangeItems({ request }: { request: EmployeeProfileChangeRequestResponse }) {
	if (request.changes.length === 0) return <EmptyState message="변경된 항목이 없습니다." />;
	return (
		<div className="space-y-3">
			{request.changes.map((change) => (
				<div
					key={`${request.id}-${change.fieldKey}`}
					className="rounded-lg border border-border bg-muted/20 p-4"
				>
					<div className="flex flex-wrap items-center justify-between gap-2">
						<p className="font-semibold">{change.fieldLabel}</p>
						<Badge variant="outline">변경</Badge>
					</div>
					<div className="mt-3 grid gap-3 sm:grid-cols-2">
						<div>
							<p className="text-xs font-medium text-muted-foreground">현재</p>
							<p className="mt-1 break-words text-sm">{profileChangeValue(change.currentValue)}</p>
						</div>
						<div>
							<p className="text-xs font-medium text-muted-foreground">요청</p>
							<p className="mt-1 break-words text-sm font-semibold">
								{profileChangeValue(change.requestedValue)}
							</p>
						</div>
					</div>
				</div>
			))}
		</div>
	);
}

function SelfProfileChangeRequestCard({
	profile,
	requests,
	onChanged
}: {
	profile: EmployeeProfile | null;
	requests: EmployeeProfileChangeRequestResponse[];
	onChanged: () => Promise<void>;
}) {
	const [nameEn, setNameEn] = useState('');
	const [birthday, setBirthday] = useState('');
	const [gender, setGender] = useState<Gender | ''>('');
	const [nationality, setNationality] = useState('');
	const [address, setAddress] = useState('');
	const [registeredAddress, setRegisteredAddress] = useState('');
	const [emergencyContactName, setEmergencyContactName] = useState('');
	const [emergencyContactRelation, setEmergencyContactRelation] = useState('');
	const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
	const [reason, setReason] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function submitProfileChange(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const request: CreateEmployeeProfileChangeRequest = {};
		let changedFieldCount = 0;

		if (nameEn.trim()) {
			request.nameEn = nameEn.trim();
			changedFieldCount += 1;
		}
		if (birthday) {
			request.birthday = birthday;
			changedFieldCount += 1;
		}
		if (gender) {
			request.gender = gender;
			changedFieldCount += 1;
		}
		if (nationality.trim()) {
			request.nationality = nationality.trim();
			changedFieldCount += 1;
		}
		if (address.trim()) {
			request.address = address.trim();
			changedFieldCount += 1;
		}
		if (registeredAddress.trim()) {
			request.registeredAddress = registeredAddress.trim();
			changedFieldCount += 1;
		}
		if (emergencyContactName.trim()) {
			request.emergencyContactName = emergencyContactName.trim();
			changedFieldCount += 1;
		}
		if (emergencyContactRelation.trim()) {
			request.emergencyContactRelation = emergencyContactRelation.trim();
			changedFieldCount += 1;
		}
		if (emergencyContactPhone.trim()) {
			request.emergencyContactPhone = emergencyContactPhone.trim();
			changedFieldCount += 1;
		}
		if (reason.trim()) request.reason = reason.trim();

		if (changedFieldCount === 0) {
			setError('변경할 항목을 하나 이상 입력해주세요.');
			return;
		}

		setSubmitting(true);
		setError(null);
		try {
			await api.post('/employees/profile-change-requests/me', request);
			setNameEn('');
			setBirthday('');
			setGender('');
			setNationality('');
			setAddress('');
			setRegisteredAddress('');
			setEmergencyContactName('');
			setEmergencyContactRelation('');
			setEmergencyContactPhone('');
			setReason('');
			await onChanged();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '인사정보 변경 요청을 저장하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>인사정보 변경 요청</CardTitle>
					<CardDescription>
						주소와 비상연락처 등 변경이 필요한 정보를 관리자에게 요청합니다.
					</CardDescription>
				</div>
				<Badge
					variant={requests.some((request) => request.status === 'PENDING') ? 'warning' : 'outline'}
				>
					대기 {requests.filter((request) => request.status === 'PENDING').length}건
				</Badge>
			</CardHeader>
			<CardContent className="space-y-5">
				<FormError message={error} />
				<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
					<div className="rounded-lg border border-border bg-muted/20 p-4">
						<p className="text-xs font-medium text-muted-foreground">영문 이름</p>
						<p className="mt-2 break-words text-sm font-semibold">
							{profileChangeValue(profile?.nameEn)}
						</p>
					</div>
					<div className="rounded-lg border border-border bg-muted/20 p-4">
						<p className="text-xs font-medium text-muted-foreground">생년월일</p>
						<p className="mt-2 break-words text-sm font-semibold">
							{profileChangeValue(profile?.birthday)}
						</p>
					</div>
					<div className="rounded-lg border border-border bg-muted/20 p-4">
						<p className="text-xs font-medium text-muted-foreground">주소</p>
						<p className="mt-2 break-words text-sm font-semibold">
							{profileChangeValue(profile?.address)}
						</p>
					</div>
					<div className="rounded-lg border border-border bg-muted/20 p-4">
						<p className="text-xs font-medium text-muted-foreground">비상연락처</p>
						<p className="mt-2 break-words text-sm font-semibold">
							{profileChangeValue(profile?.emergencyContactPhone)}
						</p>
					</div>
				</div>
				<form
					className="space-y-4 rounded-lg border border-border bg-muted/20 p-4"
					onSubmit={submitProfileChange}
				>
					<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
						<div className="space-y-2">
							<Label htmlFor="profile-change-name-en">변경 영문 이름</Label>
							<Input
								id="profile-change-name-en"
								value={nameEn}
								onChange={(event) => setNameEn(event.target.value)}
								placeholder={profileChangeValue(profile?.nameEn)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="profile-change-birthday">변경 생년월일</Label>
							<Input
								id="profile-change-birthday"
								type="date"
								value={birthday}
								onChange={(event) => setBirthday(event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="profile-change-gender">변경 성별</Label>
							<select
								id="profile-change-gender"
								value={gender}
								onChange={(event) => setGender(event.target.value as Gender | '')}
								className={dialogControlClassName}
							>
								<option value="">선택 안 함</option>
								<option value="MALE">남성</option>
								<option value="FEMALE">여성</option>
								<option value="OTHER">기타</option>
							</select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="profile-change-nationality">변경 국적</Label>
							<Input
								id="profile-change-nationality"
								value={nationality}
								onChange={(event) => setNationality(event.target.value)}
								placeholder={profileChangeValue(profile?.nationality)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="profile-change-address">변경 주소</Label>
							<Input
								id="profile-change-address"
								value={address}
								onChange={(event) => setAddress(event.target.value)}
								placeholder={profileChangeValue(profile?.address)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="profile-change-registered-address">변경 주민등록상 주소</Label>
							<Input
								id="profile-change-registered-address"
								value={registeredAddress}
								onChange={(event) => setRegisteredAddress(event.target.value)}
								placeholder={profileChangeValue(profile?.registeredAddress)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="profile-change-emergency-name">비상연락처 이름</Label>
							<Input
								id="profile-change-emergency-name"
								value={emergencyContactName}
								onChange={(event) => setEmergencyContactName(event.target.value)}
								placeholder={profileChangeValue(profile?.emergencyContactName)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="profile-change-emergency-relation">비상연락처 관계</Label>
							<Input
								id="profile-change-emergency-relation"
								value={emergencyContactRelation}
								onChange={(event) => setEmergencyContactRelation(event.target.value)}
								placeholder={profileChangeValue(profile?.emergencyContactRelation)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="profile-change-emergency-phone">비상연락처 전화번호</Label>
							<Input
								id="profile-change-emergency-phone"
								value={emergencyContactPhone}
								onChange={(event) => setEmergencyContactPhone(event.target.value)}
								placeholder={profileChangeValue(profile?.emergencyContactPhone)}
							/>
						</div>
					</div>
					<div className="space-y-2">
						<Label htmlFor="profile-change-reason">변경 사유</Label>
						<textarea
							id="profile-change-reason"
							value={reason}
							onChange={(event) => setReason(event.target.value)}
							className={dialogTextAreaClassName}
							placeholder="관리자가 확인할 변경 사유"
						/>
					</div>
					<Button type="submit" disabled={submitting}>
						<Plus className="size-4" />
						{submitting ? '요청 중...' : '변경 요청'}
					</Button>
				</form>
				<div className="space-y-3">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<p className="font-semibold">요청 이력</p>
						<Badge variant="outline">{requests.length}건</Badge>
					</div>
					{requests.length === 0 ? (
						<EmptyState message="아직 제출한 인사정보 변경 요청이 없습니다." />
					) : (
						requests.map((request) => (
							<div key={request.id} className="rounded-lg border border-border p-4">
								<div className="flex flex-wrap items-start justify-between gap-3">
									<div>
										<p className="font-semibold">요청 #{request.id}</p>
										<p className="mt-1 text-sm text-muted-foreground">
											{dateLabel(request.createdAt)} · {request.reason ?? '사유 없음'}
										</p>
									</div>
									<Badge variant={profileChangeStatusVariant(request.status)}>
										{profileChangeStatusLabel(request.status)}
									</Badge>
								</div>
								<div className="mt-4">
									<ProfileChangeItems request={request} />
								</div>
								{request.reviewComment ? (
									<p className="mt-3 text-sm text-muted-foreground">
										검토 메모: {request.reviewComment}
									</p>
								) : null}
							</div>
						))
					)}
				</div>
			</CardContent>
		</Card>
	);
}

function ProfileChangeReviewCard({
	employeeId,
	requests,
	onChanged
}: {
	employeeId: number;
	requests: EmployeeProfileChangeRequestResponse[];
	onChanged: () => Promise<void>;
}) {
	const [comments, setComments] = useState<Record<number, string>>({});
	const [submitting, setSubmitting] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const employeeRequests = requests.filter(
		(request) => request.employeeId === employeeId && request.status === 'PENDING'
	);

	async function submitReview(requestId: number, action: 'approve' | 'reject') {
		const request: ReviewEmployeeProfileChangeRequest = {};
		const comment = comments[requestId]?.trim();
		if (comment) request.comment = comment;

		setSubmitting(`${action}-${requestId}`);
		setError(null);
		try {
			await api.post(`/employees/profile-change-requests/${requestId}/${action}`, request);
			setComments((current) => ({ ...current, [requestId]: '' }));
			await onChanged();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '인사정보 변경 요청을 처리하지 못했습니다.'));
		} finally {
			setSubmitting(null);
		}
	}

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>인사정보 변경 검토</CardTitle>
					<CardDescription>
						구성원이 제출한 기본 인사정보 변경 요청을 확인하고 승인합니다.
					</CardDescription>
				</div>
				<Badge variant={employeeRequests.length > 0 ? 'warning' : 'outline'}>
					대기 {employeeRequests.length}건
				</Badge>
			</CardHeader>
			<CardContent className="space-y-4">
				<FormError message={error} />
				{employeeRequests.length === 0 ? (
					<EmptyState message="검토가 필요한 인사정보 변경 요청이 없습니다." />
				) : (
					employeeRequests.map((request) => (
						<div key={request.id} className="rounded-lg border border-border p-4">
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div>
									<p className="font-semibold">
										{request.employeeName ?? `직원 #${request.employeeId}`}
									</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{request.employeeDepartment ?? '-'} · {request.employeePosition ?? '-'} ·{' '}
										{dateLabel(request.createdAt)}
									</p>
									{request.reason ? (
										<p className="mt-2 text-sm text-muted-foreground">사유: {request.reason}</p>
									) : null}
								</div>
								<Badge variant="warning">대기</Badge>
							</div>
							<div className="mt-4">
								<ProfileChangeItems request={request} />
							</div>
							<div className="mt-4 space-y-3">
								<textarea
									value={comments[request.id] ?? ''}
									onChange={(event) =>
										setComments((current) => ({ ...current, [request.id]: event.target.value }))
									}
									className={dialogTextAreaClassName}
									placeholder="검토 메모"
									aria-label={`요청 ${request.id} 검토 메모`}
								/>
								<div className="flex flex-wrap gap-2">
									<Button
										type="button"
										onClick={() => void submitReview(request.id, 'approve')}
										disabled={submitting !== null}
									>
										<CheckCircle2 className="size-4" />
										승인
									</Button>
									<Button
										type="button"
										variant="outline"
										onClick={() => void submitReview(request.id, 'reject')}
										disabled={submitting !== null}
									>
										반려
									</Button>
								</div>
							</div>
						</div>
					))
				)}
			</CardContent>
		</Card>
	);
}

const customFieldTypeLabels: Record<EmployeeCustomFieldType, string> = {
	TEXT: '텍스트',
	SELECT: '선택',
	NUMBER: '숫자',
	DATE: '날짜',
	BOOLEAN: '예/아니오'
};

function customFieldDisplayValue(field: EmployeeCustomFieldValueResponse): string {
	if (!field.value) return '-';
	if (field.type === 'BOOLEAN') return field.value === 'true' ? '예' : '아니오';
	return field.value;
}

function customFieldApprovalPolicyLabel(field: EmployeeCustomFieldValueResponse): string {
	if (!field.selfEditable) return '관리자 수정';
	return field.approvalRequired ? '승인 필요' : '제출 즉시 반영';
}

function splitCustomFieldOptions(value: string): string[] {
	return Array.from(
		new Set(
			value
				.split(/[\n,]/)
				.map((option) => option.trim())
				.filter(Boolean)
		)
	);
}

function CustomFieldValueInput({
	id,
	field,
	value,
	onChange,
	ariaLabel
}: {
	id: string;
	field: EmployeeCustomFieldValueResponse;
	value: string;
	onChange: (value: string) => void;
	ariaLabel?: string;
}) {
	if (field.type === 'SELECT') {
		return (
			<select
				id={id}
				aria-label={ariaLabel ?? field.label}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				className={dialogControlClassName}
			>
				<option value="">선택 안 함</option>
				{field.options.map((option) => (
					<option key={option} value={option}>
						{option}
					</option>
				))}
			</select>
		);
	}
	if (field.type === 'BOOLEAN') {
		return (
			<select
				id={id}
				aria-label={ariaLabel ?? field.label}
				value={value}
				onChange={(event) => onChange(event.target.value)}
				className={dialogControlClassName}
			>
				<option value="">선택 안 함</option>
				<option value="true">예</option>
				<option value="false">아니오</option>
			</select>
		);
	}
	return (
		<Input
			id={id}
			aria-label={ariaLabel ?? field.label}
			type={field.type === 'DATE' ? 'date' : field.type === 'NUMBER' ? 'number' : 'text'}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			placeholder={field.label}
		/>
	);
}

function initialCustomFieldValues(
	customFields: EmployeeCustomFieldValueResponse[]
): Record<number, string> {
	return Object.fromEntries(
		customFields.map((field) => [field.fieldDefinitionId, field.value ?? ''])
	);
}

function customFieldValuesKey(customFields: EmployeeCustomFieldValueResponse[]): string {
	return customFields.map((field) => `${field.fieldDefinitionId}:${field.value ?? ''}`).join('|');
}

function CustomFieldValuesForm({
	employeeId,
	customFields,
	onChanged,
	setError
}: {
	employeeId: number;
	customFields: EmployeeCustomFieldValueResponse[];
	onChanged: () => Promise<void>;
	setError: (message: string | null) => void;
}) {
	const [values, setValues] = useState<Record<number, string>>(() =>
		initialCustomFieldValues(customFields)
	);
	const [saving, setSaving] = useState(false);

	async function saveValues(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const request: UpsertEmployeeCustomFieldValuesRequest = {
			values: customFields.map((field) => {
				const value = values[field.fieldDefinitionId]?.trim();
				return {
					fieldDefinitionId: field.fieldDefinitionId,
					value: value ? value : null
				};
			})
		};
		setSaving(true);
		setError(null);
		try {
			await api.put(`/employees/${employeeId}/custom-fields`, request);
			await onChanged();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '커스텀 정보를 저장하지 못했습니다.'));
		} finally {
			setSaving(false);
		}
	}

	return (
		<form
			className="space-y-4 rounded-lg border border-border bg-muted/20 p-4"
			onSubmit={saveValues}
		>
			<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
				{customFields.map((field) => (
					<div key={field.fieldDefinitionId} className="space-y-2">
						<Label htmlFor={`custom-field-${field.fieldDefinitionId}`}>{field.label}</Label>
						<CustomFieldValueInput
							id={`custom-field-${field.fieldDefinitionId}`}
							field={field}
							value={values[field.fieldDefinitionId] ?? ''}
							onChange={(nextValue) =>
								setValues((current) => ({
									...current,
									[field.fieldDefinitionId]: nextValue
								}))
							}
						/>
					</div>
				))}
			</div>
			<Button type="submit" disabled={saving}>
				<BadgeCheck className="size-4" />
				{saving ? '저장 중...' : '커스텀 정보 저장'}
			</Button>
		</form>
	);
}

function CustomFieldChangeRequestList({
	requests
}: {
	requests: EmployeeCustomFieldChangeRequestResponse[];
}) {
	if (requests.length === 0) {
		return <EmptyState message="아직 제출한 커스텀 정보 변경 요청이 없습니다." />;
	}
	return (
		<div className="space-y-3">
			{requests.map((request) => (
				<div key={request.id} className="rounded-lg border border-border p-4">
					<div className="flex flex-wrap items-start justify-between gap-3">
						<div>
							<p className="font-semibold">
								{request.label ?? `필드 #${request.fieldDefinitionId}`}
							</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{dateLabel(request.createdAt)} · {request.reason ?? '사유 없음'}
							</p>
						</div>
						<Badge variant={customFieldChangeStatusVariant(request.status)}>
							{customFieldChangeStatusLabel(request.status)}
						</Badge>
					</div>
					<p className="mt-3 text-sm font-semibold">
						{customFieldChangeValue(request.currentValue)} →{' '}
						{customFieldChangeValue(request.requestedValue)}
					</p>
					{request.reviewComment ? (
						<p className="mt-3 text-sm text-muted-foreground">검토 메모: {request.reviewComment}</p>
					) : null}
				</div>
			))}
		</div>
	);
}

function SelfCustomFieldChangeRequestCard({
	customFields,
	requests,
	onChanged
}: {
	customFields: EmployeeCustomFieldValueResponse[];
	requests: EmployeeCustomFieldChangeRequestResponse[];
	onChanged: () => Promise<void>;
}) {
	const selfEditableFields = customFields.filter((field) => field.selfEditable);
	const [fieldDefinitionId, setFieldDefinitionId] = useState(
		() => selfEditableFields[0]?.fieldDefinitionId ?? 0
	);
	const [value, setValue] = useState('');
	const [reason, setReason] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const selectedField =
		selfEditableFields.find((field) => field.fieldDefinitionId === fieldDefinitionId) ??
		selfEditableFields[0] ??
		null;

	async function submitRequest(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!selectedField) {
			setError('직접 수정 요청이 가능한 커스텀 정보가 없습니다.');
			return;
		}
		const request: CreateEmployeeCustomFieldChangeRequest = {
			fieldDefinitionId: selectedField.fieldDefinitionId,
			value: value.trim() ? value.trim() : null
		};
		if (reason.trim()) request.reason = reason.trim();

		setSubmitting(true);
		setError(null);
		try {
			await api.post('/employees/custom-field-change-requests/me', request);
			setValue('');
			setReason('');
			await onChanged();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '커스텀 정보 변경 요청을 저장하지 못했습니다.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>커스텀 정보 수정 요청</CardTitle>
					<CardDescription>
						직접 수정 가능한 회사별 정보는 필드 정책에 따라 즉시 반영되거나 승인 후 반영됩니다.
					</CardDescription>
				</div>
				<Badge
					variant={requests.some((request) => request.status === 'PENDING') ? 'warning' : 'outline'}
				>
					대기 {requests.filter((request) => request.status === 'PENDING').length}건
				</Badge>
			</CardHeader>
			<CardContent className="space-y-5">
				<FormError message={error} />
				{selfEditableFields.length === 0 ? (
					<EmptyState message="직접 수정 요청이 가능한 커스텀 정보가 없습니다." />
				) : (
					<form
						className="space-y-4 rounded-lg border border-border bg-muted/20 p-4"
						onSubmit={submitRequest}
					>
						<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
							<div className="space-y-2">
								<Label htmlFor="custom-field-change-field">수정할 커스텀 정보</Label>
								<select
									id="custom-field-change-field"
									value={String(selectedField?.fieldDefinitionId ?? '')}
									onChange={(event) => {
										setFieldDefinitionId(Number(event.target.value));
										setValue('');
									}}
									className={dialogControlClassName}
								>
									{selfEditableFields.map((field) => (
										<option key={field.fieldDefinitionId} value={field.fieldDefinitionId}>
											{field.label}
										</option>
									))}
								</select>
							</div>
							{selectedField ? (
								<div className="space-y-2">
									<div className="flex items-center justify-between gap-2">
										<Label htmlFor="custom-field-change-value">요청값</Label>
										<Badge variant={selectedField.approvalRequired ? 'warning' : 'success'}>
											{customFieldApprovalPolicyLabel(selectedField)}
										</Badge>
									</div>
									<CustomFieldValueInput
										id="custom-field-change-value"
										field={selectedField}
										value={value}
										onChange={setValue}
										ariaLabel="요청값"
									/>
								</div>
							) : null}
						</div>
						<div className="space-y-2">
							<Label htmlFor="custom-field-change-reason">커스텀 정보 변경 사유</Label>
							<textarea
								id="custom-field-change-reason"
								value={reason}
								onChange={(event) => setReason(event.target.value)}
								className={dialogTextAreaClassName}
								placeholder="관리자가 확인할 변경 사유"
							/>
						</div>
						<Button type="submit" disabled={submitting}>
							<Plus className="size-4" />
							{submitting ? '요청 중...' : '커스텀 정보 변경 요청'}
						</Button>
					</form>
				)}
				<div className="space-y-3">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<p className="font-semibold">요청 이력</p>
						<Badge variant="outline">{requests.length}건</Badge>
					</div>
					<CustomFieldChangeRequestList requests={requests} />
				</div>
			</CardContent>
		</Card>
	);
}

function CustomFieldChangeReviewCard({
	employeeId,
	requests,
	onChanged
}: {
	employeeId: number;
	requests: EmployeeCustomFieldChangeRequestResponse[];
	onChanged: () => Promise<void>;
}) {
	const [comments, setComments] = useState<Record<number, string>>({});
	const [submitting, setSubmitting] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const employeeRequests = requests.filter(
		(request) => request.employeeId === employeeId && request.status === 'PENDING'
	);

	async function submitReview(requestId: number, action: 'approve' | 'reject') {
		const request: { comment?: string } = {};
		const comment = comments[requestId]?.trim();
		if (comment) request.comment = comment;

		setSubmitting(`${action}-${requestId}`);
		setError(null);
		try {
			await api.post(`/employees/custom-field-change-requests/${requestId}/${action}`, request);
			setComments((current) => ({ ...current, [requestId]: '' }));
			await onChanged();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '커스텀 정보 변경 요청을 처리하지 못했습니다.'));
		} finally {
			setSubmitting(null);
		}
	}

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>커스텀 정보 변경 검토</CardTitle>
					<CardDescription>
						구성원이 요청한 회사별 커스텀 정보 변경을 승인하거나 반려합니다.
					</CardDescription>
				</div>
				<Badge variant={employeeRequests.length > 0 ? 'warning' : 'outline'}>
					대기 {employeeRequests.length}건
				</Badge>
			</CardHeader>
			<CardContent className="space-y-4">
				<FormError message={error} />
				{employeeRequests.length === 0 ? (
					<EmptyState message="검토가 필요한 커스텀 정보 변경 요청이 없습니다." />
				) : (
					employeeRequests.map((request) => (
						<div key={request.id} className="rounded-lg border border-border p-4">
							<div className="flex flex-wrap items-start justify-between gap-3">
								<div>
									<p className="font-semibold">
										{request.label ?? `필드 #${request.fieldDefinitionId}`}
									</p>
									<p className="mt-1 text-sm text-muted-foreground">
										{request.employeeName ?? `직원 #${request.employeeId}`} ·{' '}
										{dateLabel(request.createdAt)}
									</p>
									{request.reason ? (
										<p className="mt-2 text-sm text-muted-foreground">사유: {request.reason}</p>
									) : null}
								</div>
								<Badge variant="warning">대기</Badge>
							</div>
							<p className="mt-4 text-sm font-semibold">
								{customFieldChangeValue(request.currentValue)} →{' '}
								{customFieldChangeValue(request.requestedValue)}
							</p>
							<div className="mt-4 space-y-3">
								<textarea
									value={comments[request.id] ?? ''}
									onChange={(event) =>
										setComments((current) => ({ ...current, [request.id]: event.target.value }))
									}
									className={dialogTextAreaClassName}
									placeholder="검토 메모"
									aria-label={`커스텀 정보 요청 ${request.id} 검토 메모`}
								/>
								<div className="flex flex-wrap gap-2">
									<Button
										type="button"
										onClick={() => void submitReview(request.id, 'approve')}
										disabled={submitting !== null}
									>
										<CheckCircle2 className="size-4" />
										커스텀 정보 승인
									</Button>
									<Button
										type="button"
										variant="outline"
										onClick={() => void submitReview(request.id, 'reject')}
										disabled={submitting !== null}
									>
										커스텀 정보 반려
									</Button>
								</div>
							</div>
						</div>
					))
				)}
			</CardContent>
		</Card>
	);
}

function CustomFieldsCard({
	employeeId,
	customFields,
	editable,
	onChanged
}: {
	employeeId: number;
	customFields: EmployeeCustomFieldValueResponse[];
	editable: boolean;
	onChanged: () => Promise<void>;
}) {
	const [fieldKey, setFieldKey] = useState('');
	const [fieldLabel, setFieldLabel] = useState('');
	const [fieldType, setFieldType] = useState<EmployeeCustomFieldType>('TEXT');
	const [fieldOptions, setFieldOptions] = useState('');
	const [fieldRequired, setFieldRequired] = useState(false);
	const [fieldVisibleToEmployee, setFieldVisibleToEmployee] = useState(true);
	const [fieldSelfEditable, setFieldSelfEditable] = useState(false);
	const [fieldApprovalRequired, setFieldApprovalRequired] = useState(true);
	const [submitting, setSubmitting] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	async function createDefinition(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!fieldKey.trim() || !fieldLabel.trim()) {
			setError('필드 key와 필드명을 입력해주세요.');
			return;
		}
		const request: CreateEmployeeCustomFieldDefinitionRequest = {
			fieldKey: fieldKey.trim(),
			label: fieldLabel.trim(),
			type: fieldType,
			options: fieldType === 'SELECT' ? splitCustomFieldOptions(fieldOptions) : [],
			required: fieldRequired,
			visibleToEmployee: fieldVisibleToEmployee,
			selfEditable: fieldSelfEditable,
			approvalRequired: fieldApprovalRequired,
			displayOrder: 0
		};
		setSubmitting('definition');
		setError(null);
		try {
			await api.post('/employees/custom-fields', request);
			setFieldKey('');
			setFieldLabel('');
			setFieldType('TEXT');
			setFieldOptions('');
			setFieldRequired(false);
			setFieldVisibleToEmployee(true);
			setFieldSelfEditable(false);
			setFieldApprovalRequired(true);
			await onChanged();
		} catch (submitError) {
			setError(getErrorMessage(submitError, '커스텀 정보 항목을 추가하지 못했습니다.'));
		} finally {
			setSubmitting(null);
		}
	}

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>커스텀 정보</CardTitle>
					<CardDescription>
						Job Level, 사이즈, 보안등급처럼 회사별 직원 정보를 관리합니다.
					</CardDescription>
				</div>
				<Badge variant={customFields.length > 0 ? 'success' : 'outline'}>
					{numberLabel(customFields.length)}개 항목
				</Badge>
			</CardHeader>
			<CardContent className="space-y-5">
				<FormError message={error} />
				{customFields.length === 0 ? (
					<EmptyState message="등록된 커스텀 정보가 없습니다." />
				) : (
					<>
						<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
							{customFields.map((field) => (
								<div
									key={field.fieldDefinitionId}
									className="rounded-lg border border-border bg-muted/20 p-4"
								>
									<div className="flex items-start justify-between gap-3">
										<div>
											<p className="text-xs font-medium text-muted-foreground">{field.label}</p>
											<p className="mt-2 break-words text-sm font-semibold">
												{customFieldDisplayValue(field)}
											</p>
										</div>
										<div className="flex flex-col items-end gap-2">
											<Badge variant="outline">{customFieldTypeLabels[field.type]}</Badge>
											<Badge
												variant={
													field.selfEditable && !field.approvalRequired ? 'success' : 'outline'
												}
											>
												{customFieldApprovalPolicyLabel(field)}
											</Badge>
										</div>
									</div>
								</div>
							))}
						</div>
						{editable ? (
							<CustomFieldValuesForm
								key={customFieldValuesKey(customFields)}
								employeeId={employeeId}
								customFields={customFields}
								onChanged={onChanged}
								setError={setError}
							/>
						) : null}
					</>
				)}
				{editable ? (
					<form
						className="space-y-4 rounded-lg border border-border p-4"
						onSubmit={createDefinition}
					>
						<div className="flex flex-wrap items-center justify-between gap-2">
							<div>
								<p className="font-semibold">항목 추가</p>
								<p className="text-sm text-muted-foreground">
									회사에서 반복 관리할 직원 정보 필드를 만듭니다.
								</p>
							</div>
							<Plus className="size-4 text-muted-foreground" />
						</div>
						<div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
							<div className="space-y-2">
								<Label htmlFor="custom-field-key">필드 key</Label>
								<Input
									id="custom-field-key"
									value={fieldKey}
									onChange={(event) => setFieldKey(event.target.value)}
									placeholder="job_level"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="custom-field-label">필드명</Label>
								<Input
									id="custom-field-label"
									value={fieldLabel}
									onChange={(event) => setFieldLabel(event.target.value)}
									placeholder="Job Level"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="custom-field-type">필드 타입</Label>
								<select
									id="custom-field-type"
									value={fieldType}
									onChange={(event) => setFieldType(event.target.value as EmployeeCustomFieldType)}
									className={dialogControlClassName}
								>
									{Object.entries(customFieldTypeLabels).map(([value, label]) => (
										<option key={value} value={value}>
											{label}
										</option>
									))}
								</select>
							</div>
							<div className="space-y-2">
								<Label htmlFor="custom-field-options">선택 옵션</Label>
								<Input
									id="custom-field-options"
									value={fieldOptions}
									onChange={(event) => setFieldOptions(event.target.value)}
									placeholder="L1, L2, L3"
									disabled={fieldType !== 'SELECT'}
								/>
							</div>
						</div>
						<div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
							<label className="flex items-center gap-2">
								<input
									type="checkbox"
									checked={fieldRequired}
									onChange={(event) => setFieldRequired(event.target.checked)}
								/>
								필수 항목
							</label>
							<label className="flex items-center gap-2">
								<input
									type="checkbox"
									checked={fieldVisibleToEmployee}
									onChange={(event) => setFieldVisibleToEmployee(event.target.checked)}
								/>
								직원에게 공개
							</label>
							<label className="flex items-center gap-2">
								<input
									type="checkbox"
									checked={fieldSelfEditable}
									onChange={(event) => {
										const checked = event.target.checked;
										setFieldSelfEditable(checked);
										if (!checked) setFieldApprovalRequired(true);
									}}
								/>
								직원 직접 수정
							</label>
							<label className="flex items-center gap-2">
								<input
									type="checkbox"
									checked={fieldApprovalRequired}
									disabled={!fieldSelfEditable}
									onChange={(event) => setFieldApprovalRequired(event.target.checked)}
								/>
								직원 수정 시 승인 필요
							</label>
						</div>
						<Button type="submit" variant="outline" disabled={submitting !== null}>
							<Plus className="size-4" />
							{submitting === 'definition' ? '추가 중...' : '필드 추가'}
						</Button>
					</form>
				) : null}
			</CardContent>
		</Card>
	);
}

type PersonnelNoteDraft = {
	category: PersonnelNoteCategory;
	title: string;
	content: string;
	pinned: boolean;
	confidential: boolean;
};

function personnelNoteDraft(note: PersonnelNoteResponse): PersonnelNoteDraft {
	return {
		category: note.category,
		title: note.title,
		content: note.content,
		pinned: note.pinned,
		confidential: note.confidential
	};
}

type PersonnelEventDraft = {
	type: PersonnelEventType;
	severity: PersonnelEventSeverity;
	title: string;
	occurredOn: string;
	issuer: string;
	description: string;
	actionTaken: string;
};

function personnelEventDraft(event: PersonnelEventResponse): PersonnelEventDraft {
	return {
		type: event.type,
		severity: event.severity,
		title: event.title,
		occurredOn: event.occurredOn,
		issuer: event.issuer ?? '',
		description: event.description ?? '',
		actionTaken: event.actionTaken ?? ''
	};
}

function PersonnelFileCard({
	employeeId,
	file,
	onChanged
}: {
	employeeId: number;
	file: EmployeePersonnelFileResponse | null;
	onChanged: () => Promise<void>;
}) {
	const [documentTitle, setDocumentTitle] = useState('');
	const [documentCategory, setDocumentCategory] = useState<PersonnelDocumentCategory>('CONTRACT');
	const [documentFileName, setDocumentFileName] = useState('');
	const [documentExpiresOn, setDocumentExpiresOn] = useState('');
	const [noteTitle, setNoteTitle] = useState('');
	const [noteCategory, setNoteCategory] = useState<PersonnelNoteCategory>('GROWTH');
	const [noteContent, setNoteContent] = useState('');
	const [notePinned, setNotePinned] = useState(true);
	const [noteConfidential, setNoteConfidential] = useState(true);
	const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
	const [editNoteDraft, setEditNoteDraft] = useState<PersonnelNoteDraft | null>(null);
	const [eventTitle, setEventTitle] = useState('');
	const [eventType, setEventType] = useState<PersonnelEventType>('AWARD');
	const [eventSeverity, setEventSeverity] = useState<PersonnelEventSeverity>('POSITIVE');
	const [eventOccurredOn, setEventOccurredOn] = useState(new Date().toISOString().slice(0, 10));
	const [eventIssuer, setEventIssuer] = useState('');
	const [eventDescription, setEventDescription] = useState('');
	const [eventActionTaken, setEventActionTaken] = useState('');
	const [editingEventId, setEditingEventId] = useState<number | null>(null);
	const [editEventDraft, setEditEventDraft] = useState<PersonnelEventDraft | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState<string | null>(null);

	async function submit(kind: string, action: () => Promise<unknown>): Promise<boolean> {
		setSubmitting(kind);
		setError(null);
		try {
			await action();
			await onChanged();
			return true;
		} catch (submitError) {
			setError(getErrorMessage(submitError, '인사 파일을 저장하지 못했습니다.'));
			return false;
		} finally {
			setSubmitting(null);
		}
	}

	async function submitDocument(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!documentTitle.trim()) {
			setError('문서 제목을 입력해주세요.');
			return;
		}
		const request: CreatePersonnelDocumentRequest = {
			category: documentCategory,
			title: documentTitle.trim(),
			fileName: documentFileName.trim() || undefined,
			expiresOn: documentExpiresOn || undefined,
			confidential: true
		};
		await submit('document', () =>
			api.post(`/employees/${employeeId}/personnel-files/documents`, request)
		);
		setDocumentTitle('');
		setDocumentFileName('');
		setDocumentExpiresOn('');
	}

	async function submitNote(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!noteTitle.trim() || !noteContent.trim()) {
			setError('노트 제목과 내용을 입력해주세요.');
			return;
		}
		const request: CreatePersonnelNoteRequest = {
			category: noteCategory,
			title: noteTitle.trim(),
			content: noteContent.trim(),
			pinned: notePinned,
			confidential: noteConfidential
		};
		await submit('note', () => api.post(`/employees/${employeeId}/personnel-files/notes`, request));
		setNoteTitle('');
		setNoteContent('');
		setNoteConfidential(true);
	}

	function startEditNote(note: PersonnelNoteResponse) {
		setEditingNoteId(note.id);
		setEditNoteDraft(personnelNoteDraft(note));
		setError(null);
	}

	function patchEditNoteDraft(patch: Partial<PersonnelNoteDraft>) {
		setEditNoteDraft((current) => (current ? { ...current, ...patch } : current));
	}

	function cancelEditNote() {
		setEditingNoteId(null);
		setEditNoteDraft(null);
	}

	async function submitNoteEdit(event: FormEvent<HTMLFormElement>, noteId: number) {
		event.preventDefault();
		if (!editNoteDraft || editingNoteId !== noteId) return;
		if (!editNoteDraft.title.trim() || !editNoteDraft.content.trim()) {
			setError('노트 제목과 내용을 입력해주세요.');
			return;
		}
		const request: UpdatePersonnelNoteRequest = {
			category: editNoteDraft.category,
			title: editNoteDraft.title.trim(),
			content: editNoteDraft.content.trim(),
			pinned: editNoteDraft.pinned,
			confidential: editNoteDraft.confidential
		};
		const saved = await submit(`note-edit-${noteId}`, () =>
			api.patch(`/employees/${employeeId}/personnel-files/notes/${noteId}`, request)
		);
		if (saved) cancelEditNote();
	}

	async function deleteNote(note: PersonnelNoteResponse) {
		if (!window.confirm(`"${note.title}" 노트를 삭제할까요?`)) return;
		const deleted = await submit(`note-delete-${note.id}`, () =>
			api.delete(`/employees/${employeeId}/personnel-files/notes/${note.id}`)
		);
		if (deleted && editingNoteId === note.id) cancelEditNote();
	}

	async function submitEvent(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!eventTitle.trim()) {
			setError('이벤트 제목을 입력해주세요.');
			return;
		}
		const request: CreatePersonnelEventRequest = {
			type: eventType,
			severity: eventSeverity,
			title: eventTitle.trim(),
			occurredOn: eventOccurredOn,
			issuer: eventIssuer.trim() || undefined,
			description: eventDescription.trim() || undefined,
			actionTaken: eventActionTaken.trim() || undefined
		};
		await submit('event', () =>
			api.post(`/employees/${employeeId}/personnel-files/events`, request)
		);
		setEventTitle('');
		setEventIssuer('');
		setEventDescription('');
		setEventActionTaken('');
	}

	function startEditEvent(event: PersonnelEventResponse) {
		setEditingEventId(event.id);
		setEditEventDraft(personnelEventDraft(event));
		setError(null);
	}

	function patchEditEventDraft(patch: Partial<PersonnelEventDraft>) {
		setEditEventDraft((current) => (current ? { ...current, ...patch } : current));
	}

	function cancelEditEvent() {
		setEditingEventId(null);
		setEditEventDraft(null);
	}

	async function submitEventEdit(event: FormEvent<HTMLFormElement>, eventId: number) {
		event.preventDefault();
		if (!editEventDraft || editingEventId !== eventId) return;
		if (!editEventDraft.title.trim() || !editEventDraft.occurredOn) {
			setError('이벤트 제목과 발생일을 입력해주세요.');
			return;
		}
		const request: UpdatePersonnelEventRequest = {
			type: editEventDraft.type,
			severity: editEventDraft.severity,
			title: editEventDraft.title.trim(),
			occurredOn: editEventDraft.occurredOn,
			issuer: editEventDraft.issuer.trim() || undefined,
			description: editEventDraft.description.trim() || undefined,
			actionTaken: editEventDraft.actionTaken.trim() || undefined
		};
		const saved = await submit(`event-edit-${eventId}`, () =>
			api.patch(`/employees/${employeeId}/personnel-files/events/${eventId}`, request)
		);
		if (saved) cancelEditEvent();
	}

	async function deleteEvent(event: PersonnelEventResponse) {
		if (!window.confirm(`"${event.title}" 수상·징계 기록을 삭제할까요?`)) return;
		const deleted = await submit(`event-delete-${event.id}`, () =>
			api.delete(`/employees/${employeeId}/personnel-files/events/${event.id}`)
		);
		if (deleted && editingEventId === event.id) cancelEditEvent();
	}

	if (!file) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>인사 파일</CardTitle>
					<CardDescription>직원별 문서, 관리자 노트, 수상·징계 기록을 관리합니다.</CardDescription>
				</CardHeader>
				<CardContent>
					<EmptyState message="인사 파일을 불러올 권한이 없거나 아직 등록된 기록이 없습니다." />
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
				<div>
					<CardTitle>인사 파일</CardTitle>
					<CardDescription>
						직원별 문서, 관리자 노트, 수상·징계 기록을 한 곳에서 관리합니다.
					</CardDescription>
				</div>
				<div className="grid grid-cols-3 gap-2 text-right text-xs text-muted-foreground">
					<div>
						<p className="text-lg font-semibold text-foreground">{file.summary.documentCount}</p>
						<p>문서</p>
					</div>
					<div>
						<p className="text-lg font-semibold text-foreground">{file.summary.pinnedNoteCount}</p>
						<p>고정 노트</p>
					</div>
					<div>
						<p className="text-lg font-semibold text-foreground">
							{file.summary.awardCount}/{file.summary.disciplineCount}
						</p>
						<p>수상/징계</p>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-6">
				<FormError message={error} />
				<div className="grid gap-4 xl:grid-cols-3">
					<PersonnelDocumentList documents={file.documents} />
					<PersonnelNoteList
						notes={file.notes}
						editingNoteId={editingNoteId}
						editNoteDraft={editNoteDraft}
						submitting={submitting}
						onStartEdit={startEditNote}
						onCancelEdit={cancelEditNote}
						onPatchEditDraft={patchEditNoteDraft}
						onSubmitEdit={submitNoteEdit}
						onDelete={deleteNote}
					/>
					<PersonnelEventList
						events={file.events}
						editingEventId={editingEventId}
						editEventDraft={editEventDraft}
						submitting={submitting}
						onStartEdit={startEditEvent}
						onCancelEdit={cancelEditEvent}
						onPatchEditDraft={patchEditEventDraft}
						onSubmitEdit={submitEventEdit}
						onDelete={deleteEvent}
					/>
				</div>
				<div className="grid gap-4 xl:grid-cols-3">
					<form
						className="space-y-3 rounded-lg border border-border bg-muted/20 p-4"
						onSubmit={submitDocument}
					>
						<div className="flex items-center justify-between gap-3">
							<p className="font-semibold">문서 추가</p>
							<FileText className="size-4 text-muted-foreground" />
						</div>
						<select
							value={documentCategory}
							onChange={(event) =>
								setDocumentCategory(event.target.value as PersonnelDocumentCategory)
							}
							className={dialogControlClassName}
							aria-label="문서 분류"
						>
							{Object.entries(personnelDocumentCategoryLabels).map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</select>
						<Input
							value={documentTitle}
							onChange={(event) => setDocumentTitle(event.target.value)}
							placeholder="문서 제목"
						/>
						<Input
							value={documentFileName}
							onChange={(event) => setDocumentFileName(event.target.value)}
							placeholder="파일명"
						/>
						<Input
							type="date"
							value={documentExpiresOn}
							onChange={(event) => setDocumentExpiresOn(event.target.value)}
							aria-label="문서 만료일"
						/>
						<Button type="submit" className="w-full" disabled={submitting === 'document'}>
							<Plus className="size-4" />
							{submitting === 'document' ? '저장 중...' : '문서 추가'}
						</Button>
					</form>

					<form
						className="space-y-3 rounded-lg border border-border bg-muted/20 p-4"
						onSubmit={submitNote}
					>
						<div className="flex items-center justify-between gap-3">
							<p className="font-semibold">노트 추가</p>
							<BadgeCheck className="size-4 text-muted-foreground" />
						</div>
						<select
							value={noteCategory}
							onChange={(event) => setNoteCategory(event.target.value as PersonnelNoteCategory)}
							className={dialogControlClassName}
							aria-label="노트 분류"
						>
							{Object.entries(personnelNoteCategoryLabels).map(([value, label]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</select>
						<Input
							value={noteTitle}
							onChange={(event) => setNoteTitle(event.target.value)}
							placeholder="노트 제목"
						/>
						<textarea
							value={noteContent}
							onChange={(event) => setNoteContent(event.target.value)}
							className={dialogTextAreaClassName}
							placeholder="관리자 메모"
						/>
						<label className="flex items-center gap-2 text-sm text-muted-foreground">
							<input
								type="checkbox"
								checked={notePinned}
								onChange={(event) => setNotePinned(event.target.checked)}
							/>
							중요 노트로 고정
						</label>
						<label className="flex items-center gap-2 text-sm text-muted-foreground">
							<input
								type="checkbox"
								checked={noteConfidential}
								onChange={(event) => setNoteConfidential(event.target.checked)}
							/>
							비밀 노트
						</label>
						<Button type="submit" className="w-full" disabled={submitting === 'note'}>
							<Plus className="size-4" />
							{submitting === 'note' ? '저장 중...' : '노트 추가'}
						</Button>
					</form>

					<form
						className="space-y-3 rounded-lg border border-border bg-muted/20 p-4"
						onSubmit={submitEvent}
					>
						<div className="flex items-center justify-between gap-3">
							<p className="font-semibold">이벤트 추가</p>
							<AlertTriangle className="size-4 text-muted-foreground" />
						</div>
						<div className="grid grid-cols-2 gap-2">
							<select
								value={eventType}
								onChange={(event) => setEventType(event.target.value as PersonnelEventType)}
								className={dialogControlClassName}
								aria-label="이벤트 유형"
							>
								{Object.entries(personnelEventTypeLabels).map(([value, label]) => (
									<option key={value} value={value}>
										{label}
									</option>
								))}
							</select>
							<select
								value={eventSeverity}
								onChange={(event) => setEventSeverity(event.target.value as PersonnelEventSeverity)}
								className={dialogControlClassName}
								aria-label="이벤트 중요도"
							>
								{Object.entries(personnelEventSeverityLabels).map(([value, label]) => (
									<option key={value} value={value}>
										{label}
									</option>
								))}
							</select>
						</div>
						<Input
							value={eventTitle}
							onChange={(event) => setEventTitle(event.target.value)}
							placeholder="이벤트 제목"
						/>
						<Input
							type="date"
							value={eventOccurredOn}
							onChange={(event) => setEventOccurredOn(event.target.value)}
							aria-label="이벤트 발생일"
						/>
						<Input
							value={eventIssuer}
							onChange={(event) => setEventIssuer(event.target.value)}
							placeholder="처리 주체"
						/>
						<textarea
							value={eventDescription}
							onChange={(event) => setEventDescription(event.target.value)}
							className={dialogTextAreaClassName}
							placeholder="설명 또는 후속 조치"
						/>
						<textarea
							value={eventActionTaken}
							onChange={(event) => setEventActionTaken(event.target.value)}
							className={dialogTextAreaClassName}
							placeholder="후속 조치"
						/>
						<Button type="submit" className="w-full" disabled={submitting === 'event'}>
							<Plus className="size-4" />
							{submitting === 'event' ? '저장 중...' : '이벤트 추가'}
						</Button>
					</form>
				</div>
			</CardContent>
		</Card>
	);
}

function PersonnelDocumentList({ documents }: { documents: PersonnelDocumentResponse[] }) {
	if (documents.length === 0) return <EmptyState message="등록된 직원 문서가 없습니다." />;
	return (
		<div className="space-y-3">
			<div>
				<p className="font-semibold">구성원 문서</p>
				<p className="text-sm text-muted-foreground">계약서, 증명서, 가족 서류를 보관합니다.</p>
			</div>
			{documents.map((document) => (
				<div key={document.id} className="rounded-lg border border-border p-4">
					<div className="flex items-start justify-between gap-3">
						<div>
							<p className="font-semibold">{document.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">
								{document.fileName ?? '파일명 없음'} · 만료 {dateLabel(document.expiresOn)}
							</p>
						</div>
						<Badge variant={document.confidential ? 'warning' : 'outline'}>
							{personnelDocumentCategoryLabels[document.category]}
						</Badge>
					</div>
					{document.note ? (
						<p className="mt-3 text-sm text-muted-foreground">{document.note}</p>
					) : null}
				</div>
			))}
		</div>
	);
}

function PersonnelNoteList({
	notes,
	editingNoteId,
	editNoteDraft,
	submitting,
	onStartEdit,
	onCancelEdit,
	onPatchEditDraft,
	onSubmitEdit,
	onDelete
}: {
	notes: PersonnelNoteResponse[];
	editingNoteId: number | null;
	editNoteDraft: PersonnelNoteDraft | null;
	submitting: string | null;
	onStartEdit: (note: PersonnelNoteResponse) => void;
	onCancelEdit: () => void;
	onPatchEditDraft: (patch: Partial<PersonnelNoteDraft>) => void;
	onSubmitEdit: (event: FormEvent<HTMLFormElement>, noteId: number) => void;
	onDelete: (note: PersonnelNoteResponse) => void;
}) {
	if (notes.length === 0) return <EmptyState message="등록된 관리자 노트가 없습니다." />;
	return (
		<div className="space-y-3">
			<div>
				<p className="font-semibold">관리자 인사노트</p>
				<p className="text-sm text-muted-foreground">공유가 필요한 민감한 맥락을 남깁니다.</p>
			</div>
			{notes.map((note) => {
				const isEditing = editingNoteId === note.id && editNoteDraft;
				return (
					<div key={note.id} className="rounded-lg border border-border p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<div className="flex flex-wrap items-center gap-2">
									<p className="font-semibold">{note.title}</p>
									{note.pinned ? <Badge variant="success">고정</Badge> : null}
									{note.confidential ? <Badge variant="warning">비밀</Badge> : null}
									<Badge variant="outline">{personnelNoteCategoryLabels[note.category]}</Badge>
								</div>
								<p className="mt-2 text-xs text-muted-foreground">
									작성 <span>{dateTimeLabel(note.createdAt)}</span>
									{note.updatedAt && note.updatedAt !== note.createdAt ? (
										<>
											{' '}
											· 수정 <span>{dateTimeLabel(note.updatedAt)}</span>
										</>
									) : null}
								</p>
							</div>
							<div className="flex shrink-0 items-center gap-1">
								<Button
									type="button"
									variant="ghost"
									size="sm"
									aria-label={`${note.title} 수정`}
									onClick={() => onStartEdit(note)}
									disabled={submitting !== null}
								>
									<Pencil className="size-4" />
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									aria-label={`${note.title} 삭제`}
									onClick={() => onDelete(note)}
									disabled={submitting !== null}
								>
									<Trash2 className="size-4" />
								</Button>
							</div>
						</div>
						{isEditing ? (
							<form className="mt-3 space-y-3" onSubmit={(event) => onSubmitEdit(event, note.id)}>
								<select
									value={editNoteDraft.category}
									onChange={(event) =>
										onPatchEditDraft({ category: event.target.value as PersonnelNoteCategory })
									}
									className={dialogControlClassName}
									aria-label="수정 노트 분류"
								>
									{Object.entries(personnelNoteCategoryLabels).map(([value, label]) => (
										<option key={value} value={value}>
											{label}
										</option>
									))}
								</select>
								<Input
									value={editNoteDraft.title}
									onChange={(event) => onPatchEditDraft({ title: event.target.value })}
									aria-label="수정 노트 제목"
								/>
								<textarea
									value={editNoteDraft.content}
									onChange={(event) => onPatchEditDraft({ content: event.target.value })}
									className={dialogTextAreaClassName}
									aria-label="수정 노트 내용"
								/>
								<div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
									<label className="flex items-center gap-2">
										<input
											type="checkbox"
											checked={editNoteDraft.pinned}
											onChange={(event) => onPatchEditDraft({ pinned: event.target.checked })}
										/>
										수정 노트 고정
									</label>
									<label className="flex items-center gap-2">
										<input
											type="checkbox"
											checked={!editNoteDraft.confidential}
											onChange={(event) =>
												onPatchEditDraft({ confidential: !event.target.checked })
											}
										/>
										수정 노트 공개
									</label>
								</div>
								<div className="flex flex-wrap gap-2">
									<Button type="submit" size="sm" disabled={submitting === `note-edit-${note.id}`}>
										{submitting === `note-edit-${note.id}` ? '저장 중...' : '수정 저장'}
									</Button>
									<Button type="button" variant="outline" size="sm" onClick={onCancelEdit}>
										취소
									</Button>
								</div>
							</form>
						) : (
							<p className="mt-3 whitespace-pre-wrap text-sm text-muted-foreground">
								{note.content}
							</p>
						)}
					</div>
				);
			})}
		</div>
	);
}

function PersonnelEventList({
	events,
	editingEventId,
	editEventDraft,
	submitting,
	onStartEdit,
	onCancelEdit,
	onPatchEditDraft,
	onSubmitEdit,
	onDelete
}: {
	events: PersonnelEventResponse[];
	editingEventId: number | null;
	editEventDraft: PersonnelEventDraft | null;
	submitting: string | null;
	onStartEdit: (event: PersonnelEventResponse) => void;
	onCancelEdit: () => void;
	onPatchEditDraft: (patch: Partial<PersonnelEventDraft>) => void;
	onSubmitEdit: (event: FormEvent<HTMLFormElement>, eventId: number) => void;
	onDelete: (event: PersonnelEventResponse) => void;
}) {
	if (events.length === 0) return <EmptyState message="등록된 수상·징계 기록이 없습니다." />;
	return (
		<div className="space-y-3">
			<div>
				<p className="font-semibold">수상 · 징계</p>
				<p className="text-sm text-muted-foreground">포상과 주의 기록을 근거와 함께 남깁니다.</p>
			</div>
			{events.map((event) => {
				const isEditing = editingEventId === event.id && editEventDraft;
				return (
					<div key={event.id} className="rounded-lg border border-border p-4">
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="font-semibold">{event.title}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{dateLabel(event.occurredOn)} · {event.issuer ?? '처리 주체 미입력'}
								</p>
								<p className="mt-2 text-xs text-muted-foreground">
									기록 <span>{dateTimeLabel(event.createdAt)}</span>
									{event.updatedAt && event.updatedAt !== event.createdAt ? (
										<>
											{' '}
											· 수정 <span>{dateTimeLabel(event.updatedAt)}</span>
										</>
									) : null}
								</p>
							</div>
							<div className="flex shrink-0 flex-wrap justify-end gap-2">
								<Badge variant={event.type === 'AWARD' ? 'success' : 'warning'}>
									{personnelEventTypeLabels[event.type]}
								</Badge>
								<Badge variant={personnelSeverityVariant(event.severity)}>
									{personnelEventSeverityLabels[event.severity]}
								</Badge>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									aria-label={`${event.title} 수정`}
									onClick={() => onStartEdit(event)}
									disabled={submitting !== null}
								>
									<Pencil className="size-4" />
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									aria-label={`${event.title} 삭제`}
									onClick={() => onDelete(event)}
									disabled={submitting !== null}
								>
									<Trash2 className="size-4" />
								</Button>
							</div>
						</div>
						{isEditing ? (
							<form
								className="mt-3 space-y-3"
								onSubmit={(formEvent) => onSubmitEdit(formEvent, event.id)}
							>
								<div className="grid gap-2 sm:grid-cols-2">
									<select
										value={editEventDraft.type}
										onChange={(changeEvent) =>
											onPatchEditDraft({ type: changeEvent.target.value as PersonnelEventType })
										}
										className={dialogControlClassName}
										aria-label="수정 이벤트 유형"
									>
										{Object.entries(personnelEventTypeLabels).map(([value, label]) => (
											<option key={value} value={value}>
												{label}
											</option>
										))}
									</select>
									<select
										value={editEventDraft.severity}
										onChange={(changeEvent) =>
											onPatchEditDraft({
												severity: changeEvent.target.value as PersonnelEventSeverity
											})
										}
										className={dialogControlClassName}
										aria-label="수정 이벤트 중요도"
									>
										{Object.entries(personnelEventSeverityLabels).map(([value, label]) => (
											<option key={value} value={value}>
												{label}
											</option>
										))}
									</select>
								</div>
								<Input
									value={editEventDraft.title}
									onChange={(changeEvent) => onPatchEditDraft({ title: changeEvent.target.value })}
									aria-label="수정 이벤트 제목"
								/>
								<div className="grid gap-2 sm:grid-cols-2">
									<Input
										type="date"
										value={editEventDraft.occurredOn}
										onChange={(changeEvent) =>
											onPatchEditDraft({ occurredOn: changeEvent.target.value })
										}
										aria-label="수정 이벤트 발생일"
									/>
									<Input
										value={editEventDraft.issuer}
										onChange={(changeEvent) =>
											onPatchEditDraft({ issuer: changeEvent.target.value })
										}
										aria-label="수정 이벤트 처리 주체"
									/>
								</div>
								<textarea
									value={editEventDraft.description}
									onChange={(changeEvent) =>
										onPatchEditDraft({ description: changeEvent.target.value })
									}
									className={dialogTextAreaClassName}
									aria-label="수정 이벤트 설명"
								/>
								<textarea
									value={editEventDraft.actionTaken}
									onChange={(changeEvent) =>
										onPatchEditDraft({ actionTaken: changeEvent.target.value })
									}
									className={dialogTextAreaClassName}
									aria-label="수정 이벤트 후속 조치"
								/>
								<div className="flex flex-wrap gap-2">
									<Button
										type="submit"
										size="sm"
										disabled={submitting === `event-edit-${event.id}`}
									>
										{submitting === `event-edit-${event.id}` ? '저장 중...' : '이벤트 수정 저장'}
									</Button>
									<Button type="button" variant="outline" size="sm" onClick={onCancelEdit}>
										취소
									</Button>
								</div>
							</form>
						) : (
							<>
								{event.description ? (
									<p className="mt-3 text-sm text-muted-foreground">{event.description}</p>
								) : null}
								{event.actionTaken ? (
									<p className="mt-2 text-xs font-medium text-muted-foreground">
										후속 조치: {event.actionTaken}
									</p>
								) : null}
							</>
						)}
					</div>
				);
			})}
		</div>
	);
}

function profilePdfFilename(employee: EmployeeResponse): string {
	const source = employee.employeeNumber?.trim() || String(employee.id);
	return `employee_profile_${source.replace(/[^A-Za-z0-9._-]/g, '_')}.pdf`;
}

function EmployeeDetailContent({
	title,
	description,
	loadPath,
	actions
}: {
	title: string;
	description: string;
	loadPath: string;
	actions?: ReactNode;
}) {
	const [state, setState] = useState<RequestState<EmployeeDetailData>>({ status: 'idle' });
	const [inviteFeedback, setInviteFeedback] = useState<InviteFeedback | null>(null);
	const [profileDownloading, setProfileDownloading] = useState(false);
	const [profileDownloadError, setProfileDownloadError] = useState<string | null>(null);
	const isMyProfile = loadPath === '/employees/me';

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const employee = await api.get<EmployeeResponse>(loadPath);
				const attendanceRange = currentMonthAttendanceRange();
				const [
					personnelFile,
					attendanceHistory,
					profile,
					customFields,
					myProfileChangeRequests,
					pendingProfileChangeRequests,
					myCustomFieldChangeRequests,
					pendingCustomFieldChangeRequests
				] = await Promise.all([
					api
						.get<EmployeePersonnelFileResponse>(`/employees/${employee.id}/personnel-files`)
						.catch(() => null),
					api
						.get<
							AttendanceResponse[]
						>(`/attendances?employeeId=${employee.id}&startDate=${attendanceRange.startDate}&endDate=${attendanceRange.endDate}`)
						.catch(() => []),
					api.get<EmployeeProfile>(`/employees/${employee.id}/profile`).catch(() => null),
					api
						.get<EmployeeCustomFieldValueResponse[]>(`/employees/${employee.id}/custom-fields`)
						.catch(() => []),
					isMyProfile
						? api
								.get<
									EmployeeProfileChangeRequestResponse[]
								>('/employees/profile-change-requests/me')
								.catch(() => [])
						: Promise.resolve([]),
					isMyProfile
						? Promise.resolve([])
						: api
								.get<
									EmployeeProfileChangeRequestResponse[]
								>('/employees/profile-change-requests/pending')
								.catch(() => []),
					isMyProfile
						? api
								.get<
									EmployeeCustomFieldChangeRequestResponse[]
								>('/employees/custom-field-change-requests/me')
								.catch(() => [])
						: Promise.resolve([]),
					isMyProfile
						? Promise.resolve([])
						: api
								.get<
									EmployeeCustomFieldChangeRequestResponse[]
								>('/employees/custom-field-change-requests/pending')
								.catch(() => [])
				]);
				setState({
					status: 'success',
					data: {
						employee,
						profile,
						customFields,
						personnelFile,
						attendanceHistory,
						myProfileChangeRequests,
						pendingProfileChangeRequests,
						myCustomFieldChangeRequests,
						pendingCustomFieldChangeRequests
					}
				});
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[isMyProfile, loadPath]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function handleInviteFeedback(feedback: InviteFeedback) {
		setInviteFeedback(feedback);
		if (feedback.kind === 'success') await load();
	}

	async function downloadProfilePdf(employee: EmployeeResponse) {
		setProfileDownloadError(null);
		setProfileDownloading(true);
		try {
			await api.downloadFile(
				`/employees/${employee.id}/profile/detail/pdf`,
				profilePdfFilename(employee)
			);
		} catch (error) {
			setProfileDownloadError(getErrorMessage(error, '프로필 PDF를 다운로드하지 못했습니다.'));
		} finally {
			setProfileDownloading(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title={title}
				description={description}
				actions={
					<div className="flex flex-wrap gap-2">
						{state.status === 'success' ? (
							<>
								<ResendInviteButton
									employee={state.data.employee}
									onDone={handleInviteFeedback}
									size="md"
								/>
								<Button
									type="button"
									variant="outline"
									onClick={() => void downloadProfilePdf(state.data.employee)}
									disabled={profileDownloading}
								>
									<Download className="size-4" />
									{profileDownloading ? '프로필 PDF 다운로드 중' : '프로필 PDF'}
								</Button>
							</>
						) : null}
						{actions}
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					</div>
				}
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			<InviteFeedbackBanner feedback={inviteFeedback} />
			{profileDownloadError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{profileDownloadError}
				</div>
			) : null}
			{state.status === 'success' ? (
				<>
					<EmployeeInfoCard employee={state.data.employee} />
					{!isMyProfile ? (
						<EmployeePayrollExclusionCard employee={state.data.employee} onChanged={load} />
					) : null}
					{isMyProfile ? (
						<SelfProfileChangeRequestCard
							profile={state.data.profile}
							requests={state.data.myProfileChangeRequests}
							onChanged={load}
						/>
					) : (
						<ProfileChangeReviewCard
							employeeId={state.data.employee.id}
							requests={state.data.pendingProfileChangeRequests}
							onChanged={load}
						/>
					)}
					<CustomFieldsCard
						employeeId={state.data.employee.id}
						customFields={state.data.customFields}
						editable={!isMyProfile}
						onChanged={load}
					/>
					{isMyProfile ? (
						<SelfCustomFieldChangeRequestCard
							customFields={state.data.customFields}
							requests={state.data.myCustomFieldChangeRequests}
							onChanged={load}
						/>
					) : (
						<CustomFieldChangeReviewCard
							employeeId={state.data.employee.id}
							requests={state.data.pendingCustomFieldChangeRequests}
							onChanged={load}
						/>
					)}
					<EmployeeAttendanceHistoryCard attendances={state.data.attendanceHistory} />
					<PersonnelFileCard
						employeeId={state.data.employee.id}
						file={state.data.personnelFile}
						onChanged={load}
					/>
				</>
			) : null}
		</div>
	);
}

export function EmployeeDetailPage({ route }: { route: MatchedRoute }) {
	return (
		<EmployeeDetailContent
			title="직원 상세"
			description="직원 인사 기록, 근태 사용 여부, 계정 초대 상태를 확인합니다."
			loadPath={`/employees/${route.params.id}`}
			actions={
				<Link
					href="/employees"
					className="inline-flex h-10 items-center justify-center rounded-md border border-border bg-background px-4 text-sm font-semibold transition-colors hover:bg-muted"
				>
					목록
				</Link>
			}
		/>
	);
}

const accountRoleLabel: Record<string, string> = {
	OWNER: '오너',
	ADMIN: '관리자',
	EMPLOYEE: '직원',
	SUPER_ADMIN: '슈퍼어드민'
};

const accountRoleTone: Record<string, 'purple' | 'amber' | 'blue' | 'neutral'> = {
	OWNER: 'purple',
	ADMIN: 'amber',
	EMPLOYEE: 'blue',
	SUPER_ADMIN: 'neutral'
};

export function AccountProfilePage() {
	const { user, logout } = useAuth();
	const [empSummary, setEmpSummary] = useState<EmployeeResponse | null>(null);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [deletePassword, setDeletePassword] = useState('');
	const [deleteReason, setDeleteReason] = useState('');
	const [deleteError, setDeleteError] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		let active = true;
		api
			.get<EmployeeResponse>('/employees/me')
			.then((emp) => {
				if (active) setEmpSummary(emp);
			})
			.catch(() => {
				/* profile summary is best-effort */
			});
		return () => {
			active = false;
		};
	}, []);

	async function handleDeleteConfirm() {
		if (!deletePassword) return;
		setDeleting(true);
		setDeleteError(null);
		try {
			await api.delete('/auth/me', { password: deletePassword, reason: deleteReason || undefined });
			closeDeleteSheet();
			logout();
		} catch (err: unknown) {
			setDeleteError(getErrorMessage(err));
		} finally {
			setDeleting(false);
		}
	}

	function closeDeleteSheet() {
		setDeleteOpen(false);
		setDeletePassword('');
		setDeleteReason('');
		setDeleteError(null);
	}

	const nameInitial = user?.name?.charAt(0) ?? '?';
	const userRole = user?.role ?? '';

	return (
		<div className="space-y-5">
			<MobileHeader
				title="설정"
				subtitle="보안과 계정 상태 관리"
				trailing={
					<span
						aria-hidden="true"
						className="grid size-10 shrink-0 place-items-center rounded-full bg-primary text-[15px] font-bold text-white"
					>
						{nameInitial}
					</span>
				}
			/>

			{/* Profile summary card */}
			<Card>
				<CardContent className="flex items-center gap-4 pt-5 pb-5">
					<span
						aria-hidden="true"
						className="grid size-14 shrink-0 place-items-center rounded-full bg-primary text-[20px] font-bold text-white"
					>
						{nameInitial}
					</span>
					<div className="min-w-0 flex-1">
						<p className="text-[16px] font-bold text-foreground">{user?.name ?? '-'}</p>
						{empSummary ? (
							<p className="mt-0.5 text-xs text-muted-foreground">
								{[empSummary.position, empSummary.department ?? '본사'].filter(Boolean).join(' · ')}
							</p>
						) : null}
						{userRole ? (
							<div className="mt-2">
								<StatusChip tone={accountRoleTone[userRole] ?? 'neutral'}>
									{accountRoleLabel[userRole] ?? userRole}
								</StatusChip>
							</div>
						) : null}
					</div>
				</CardContent>
			</Card>

			{/* Mobile feature rows — Phase 2 placeholder */}
			<div className="space-y-2">
				<SettingToggleRow
					icon={Bell}
					title="푸시 알림"
					subtitle="업무 알림을 모바일로 받습니다"
					state={<StatusChip tone="neutral">준비 중</StatusChip>}
				/>
				<SettingToggleRow
					icon={Fingerprint}
					title="생체 인증"
					subtitle="지문·Face ID로 빠르게 로그인"
					state={<StatusChip tone="neutral">준비 중</StatusChip>}
				/>
				<SettingToggleRow
					icon={MapPin}
					title="위치 권한"
					subtitle="출퇴근 위치 검증에 사용합니다"
					state={<StatusChip tone="neutral">준비 중</StatusChip>}
				/>
			</div>

			{/* Account deletion */}
			<DangerCard
				title="계정 삭제"
				description="재인증 후 계정과 기기 토큰이 해지됩니다. 복구할 수 없습니다."
				action={
					<Button type="button" variant="destructive" onClick={() => setDeleteOpen(true)}>
						삭제 요청
					</Button>
				}
			/>

			<BottomSheet open={deleteOpen} onClose={closeDeleteSheet} title="계정 삭제">
				<div className="space-y-4">
					<FormError message={deleteError} />
					<div className="space-y-2">
						<Label htmlFor="delete-account-password">비밀번호</Label>
						<Input
							id="delete-account-password"
							type="password"
							placeholder="현재 비밀번호 입력"
							value={deletePassword}
							onChange={(e) => setDeletePassword(e.target.value)}
							aria-label="계정 삭제 비밀번호"
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="delete-account-reason">탈퇴 사유 (선택)</Label>
						<Input
							id="delete-account-reason"
							placeholder="탈퇴 사유를 입력해주세요"
							value={deleteReason}
							onChange={(e) => setDeleteReason(e.target.value)}
							aria-label="계정 삭제 사유"
						/>
					</div>
					<div className="flex gap-2">
						<Button type="button" variant="outline" className="flex-1" onClick={closeDeleteSheet}>
							취소
						</Button>
						<Button
							type="button"
							variant="destructive"
							className="flex-1"
							disabled={!deletePassword || deleting}
							onClick={() => void handleDeleteConfirm()}
						>
							{deleting ? '처리 중...' : '삭제 확인'}
						</Button>
					</div>
				</div>
			</BottomSheet>

			{/* Personnel detail — preserved */}
			<SectionHeader>내 인사정보</SectionHeader>
			<EmployeeDetailContent
				title="내 정보"
				description="내 인사정보와 계정 연동 상태를 확인합니다."
				loadPath="/employees/me"
			/>
		</div>
	);
}

function attendanceOperationsChecklistVariant(
	status: AttendanceOperationsChecklistStatus
): 'success' | 'warning' | 'outline' {
	if (status === 'READY') return 'success';
	if (status === 'WARNING') return 'warning';
	return 'outline';
}

function attendanceOperationsChecklistStatusLabel(
	status: AttendanceOperationsChecklistStatus
): string {
	if (status === 'READY') return '완료';
	if (status === 'WARNING') return '주의';
	if (status === 'WAITING') return '대기';
	return '-';
}

function attendanceOperationsRiskVariant(
	severity: AttendanceOperationsRiskSeverity
): 'success' | 'warning' | 'destructive' | 'outline' {
	if (severity === 'CRITICAL') return 'destructive';
	if (severity === 'WARNING') return 'warning';
	if (severity === 'READY') return 'success';
	return 'outline';
}

function AttendanceOperationsChecklistCard({ item }: { item: AttendanceOperationsChecklistItem }) {
	const Icon =
		item.status === 'WARNING' ? AlertTriangle : item.status === 'READY' ? CheckCircle2 : Clock3;
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 items-start gap-3">
					<div className="mt-0.5 rounded-md bg-muted p-2 text-muted-foreground">
						<Icon className="size-4" />
					</div>
					<div className="min-w-0">
						<p className="font-semibold">{item.label}</p>
						<p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
					</div>
				</div>
				<Badge variant={attendanceOperationsChecklistVariant(item.status)}>
					{attendanceOperationsChecklistStatusLabel(item.status)}
				</Badge>
			</div>
		</div>
	);
}

function AttendanceOperationsRisks({ risks }: { risks: AttendanceOperationsRiskItem[] }) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<AlertTriangle className="size-4 text-muted-foreground" />
				<p className="font-semibold">운영 리스크</p>
			</div>
			<div className="mt-4 space-y-3">
				{risks.map((risk) => (
					<div key={risk.code} className="flex items-start justify-between gap-3">
						<div className="min-w-0">
							<p className="font-medium">{risk.title}</p>
							<p className="mt-1 text-sm text-muted-foreground">{risk.actionLabel}</p>
						</div>
						<Badge variant={attendanceOperationsRiskVariant(risk.severity)}>
							{numberLabel(risk.count)}건
						</Badge>
					</div>
				))}
			</div>
		</div>
	);
}

function AttendanceOperationsDepartments({
	insights
}: {
	insights: AttendanceOperationsDepartmentInsight[];
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<Building2 className="size-4 text-muted-foreground" />
				<p className="font-semibold">조직별 근태</p>
			</div>
			<div className="mt-4 space-y-3">
				{insights.length > 0 ? (
					insights.map((insight) => (
						<div key={insight.department} className="flex items-start justify-between gap-3">
							<div className="min-w-0">
								<p className="font-medium">{insight.department}</p>
								<p className="mt-1 text-sm text-muted-foreground">
									기록 {numberLabel(insight.todayRecordedCount)}명 · 휴가{' '}
									{numberLabel(insight.todayOnLeaveCount)}명 · 대상{' '}
									{numberLabel(insight.attendanceEnabledEmployees)}명
								</p>
							</div>
							<Badge variant={insight.riskCount > 0 ? 'warning' : 'success'}>
								{insight.riskLabel}
							</Badge>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">조직별 근태 데이터가 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function AttendanceOperationsPolicyMix({
	policies
}: {
	policies: AttendanceOperationsWorkPolicyMix[];
}) {
	return (
		<div className="rounded-lg border border-border bg-background p-4">
			<div className="flex items-center gap-2">
				<GitBranch className="size-4 text-muted-foreground" />
				<p className="font-semibold">근무 정책</p>
			</div>
			<div className="mt-4 space-y-3">
				{policies.length > 0 ? (
					policies.map((policy) => (
						<div key={policy.type} className="flex items-center justify-between gap-3">
							<p className="font-medium">{policy.label}</p>
							<Badge variant="outline">{numberLabel(policy.count)}개</Badge>
						</div>
					))
				) : (
					<p className="text-sm text-muted-foreground">활성 근무 정책이 없습니다.</p>
				)}
			</div>
		</div>
	);
}

function attendanceRawEventTypeLabel(type: AttendanceRawEventResponse['eventType']): string {
	const labels: Record<AttendanceRawEventResponse['eventType'], string> = {
		CHECK_IN: '출근',
		CHECK_OUT: '퇴근',
		DEVICE_IMPORT: '기기 업로드',
		IN_PROGRESS_EDIT: '실시간 보정'
	};
	return labels[type];
}

function attendanceRawEventSourceLabel(source: AttendanceRawEventResponse['source']): string {
	const labels: Record<AttendanceRawEventResponse['source'], string> = {
		SELF_SERVICE: '셀프 기록',
		DEVICE_IMPORT: '기기 연동',
		MANUAL_EDIT: '관리 보정'
	};
	return labels[source];
}

function attendanceRawEventVariant(
	type: AttendanceRawEventResponse['eventType']
): 'success' | 'warning' | 'outline' {
	if (type === 'CHECK_IN' || type === 'CHECK_OUT') return 'success';
	if (type === 'IN_PROGRESS_EDIT') return 'warning';
	return 'outline';
}

function RawAttendanceEventsPanel({ events }: { events: AttendanceRawEventResponse[] }) {
	if (events.length === 0) return null;
	const latest = events[0];

	return (
		<Card>
			<CardHeader>
				<CardTitle>원본 출퇴근 이벤트</CardTitle>
				<CardDescription>
					{dateLabel(latest.eventDate)} 원본 기록 {numberLabel(events.length)}건을 최종 산정값과
					분리해 확인합니다.
				</CardDescription>
			</CardHeader>
			<CardContent>
				<div className="overflow-x-auto rounded-lg border border-border">
					<table className="w-full min-w-[820px] text-left text-sm">
						<thead className="bg-muted/40 text-xs text-muted-foreground">
							<tr>
								<th className="px-4 py-3 font-semibold">이벤트</th>
								<th className="px-4 py-3 font-semibold">직원</th>
								<th className="px-4 py-3 font-semibold">원본 시각</th>
								<th className="px-4 py-3 font-semibold">출처</th>
								<th className="px-4 py-3 font-semibold">위치</th>
								<th className="px-4 py-3 font-semibold">메모</th>
							</tr>
						</thead>
						<tbody>
							{events.map((event) => (
								<tr key={event.id} className="border-t border-border align-top">
									<td className="px-4 py-4">
										<Badge variant={attendanceRawEventVariant(event.eventType)}>
											{attendanceRawEventTypeLabel(event.eventType)}
										</Badge>
										<p className="mt-1 text-xs text-muted-foreground">
											{timeLabel(event.eventTime)}
										</p>
									</td>
									<td className="px-4 py-4">
										<p className="font-medium">
											{event.employeeName ?? `직원 #${event.employeeId}`}
										</p>
										<p className="mt-1 text-xs text-muted-foreground">
											산정 #{event.attendanceId ?? '-'}
										</p>
									</td>
									<td className="px-4 py-4 text-muted-foreground">
										<p>출근 {timeLabel(event.rawCheckInTime)}</p>
										<p className="mt-1">퇴근 {timeLabel(event.rawCheckOutTime)}</p>
									</td>
									<td className="px-4 py-4">
										<Badge variant="outline">{attendanceRawEventSourceLabel(event.source)}</Badge>
									</td>
									<td className="px-4 py-4 text-muted-foreground">
										<p>{event.locationVerified ? '검증 완료' : '검증 없음'}</p>
										{event.latitude && event.longitude ? (
											<p className="mt-1 text-xs">
												{event.latitude}, {event.longitude}
											</p>
										) : null}
									</td>
									<td className="px-4 py-4 text-muted-foreground">{event.note ?? '-'}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</CardContent>
		</Card>
	);
}

function AttendanceOperationsPanel({
	operations
}: {
	operations: AttendanceOperationsSummaryResponse;
}) {
	const pendingApprovalCount =
		operations.pendingAdjustmentCount +
		operations.pendingOvertimeRequestCount +
		operations.pendingLeaveRequestCount;
	return (
		<Card>
			<CardHeader>
				<CardTitle>근태 운영</CardTitle>
				<CardDescription>
					근무·휴가 입력, 유연근무 정책, 법정 근로시간, 보상 정산과 근태 마감을 함께 점검합니다.
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-5">
				<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">오늘 기록</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.todayRecordedCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							출근 {numberLabel(operations.todayCheckedInCount)}명 · 휴가{' '}
							{numberLabel(operations.todayOnLeaveCount)}명
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">이상 기록</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(
								operations.todayLateCount +
									operations.todayAbsentCount +
									operations.todayMissingCheckoutCount
							)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							미퇴근 {numberLabel(operations.todayMissingCheckoutCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">보상 연동</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{minutesLabel(operations.monthlyApprovedOvertimeMinutes)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							승인 {numberLabel(operations.monthlyApprovedOvertimeCount)}건
						</p>
					</div>
					<div className="rounded-lg border border-border bg-background p-4">
						<p className="text-sm text-muted-foreground">마감</p>
						<p className="mt-2 text-2xl font-semibold tracking-normal">
							{numberLabel(operations.currentMonthClosedDayCount)}
						</p>
						<p className="mt-1 text-xs text-muted-foreground">
							승인 대기 {numberLabel(pendingApprovalCount)}건
						</p>
					</div>
				</div>

				<div className="grid gap-3 lg:grid-cols-2">
					{operations.checklist.map((item) => (
						<AttendanceOperationsChecklistCard key={item.code} item={item} />
					))}
				</div>

				<div className="grid gap-4 xl:grid-cols-3">
					<AttendanceOperationsRisks risks={operations.riskItems} />
					<AttendanceOperationsDepartments insights={operations.departmentInsights} />
					<AttendanceOperationsPolicyMix policies={operations.workPolicyMix} />
				</div>

				<div className="flex flex-wrap gap-2 rounded-lg border border-border bg-muted/20 p-4">
					<Badge variant={operations.leavePolicyConfigured ? 'success' : 'outline'}>
						<CalendarCheck className="mr-1 size-3" />
						연차 정책
					</Badge>
					<Badge variant={operations.monthlyOverWeeklyLimitCount > 0 ? 'warning' : 'success'}>
						<ShieldCheck className="mr-1 size-3" />
						주간 한도 {numberLabel(operations.monthlyOverWeeklyLimitCount)}건
					</Badge>
					<Badge variant="outline">
						<Download className="mr-1 size-3" />
						원천 데이터
					</Badge>
				</div>
			</CardContent>
		</Card>
	);
}

export function AttendancePage() {
	const todayAttendance = useTodayAttendance();
	const [operationsState, setOperationsState] = useState<
		RequestState<AttendanceOperationsSummaryResponse | null>
	>({
		status: 'idle'
	});
	const [rawEventsState, setRawEventsState] = useState<RequestState<AttendanceRawEventResponse[]>>({
		status: 'idle'
	});
	const { state } = todayAttendance;
	const attendance = state.status === 'success' ? state.data : null;
	const attendanceLoading = state.status === 'idle' || state.status === 'loading';
	const attendanceError =
		todayAttendance.actionError ?? (state.status === 'error' ? state.message : null);
	const operations = operationsState.status === 'success' ? operationsState.data : null;
	const rawEvents = rawEventsState.status === 'success' ? rawEventsState.data : [];
	const rawEventDate = attendance?.date ?? new Date().toISOString().slice(0, 10);

	const loadOperations = useMemo(
		() => async () => {
			setOperationsState({ status: 'loading' });
			const data = await api
				.get<AttendanceOperationsSummaryResponse>('/attendances/operations/summary')
				.catch(() => null);
			setOperationsState({ status: 'success', data });
		},
		[]
	);

	const loadRawEvents = useMemo(
		() => async () => {
			setRawEventsState({ status: 'loading' });
			const events = await api
				.get<AttendanceRawEventResponse[]>(`/attendances/raw-events?date=${rawEventDate}`)
				.catch(() => []);
			setRawEventsState({ status: 'success', data: events });
		},
		[rawEventDate]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await loadOperations();
		});
		return () => {
			active = false;
		};
	}, [loadOperations]);

	useEffect(() => {
		if (state.status !== 'success') return;
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await loadRawEvents();
		});
		return () => {
			active = false;
		};
	}, [loadRawEvents, state.status]);

	async function refresh() {
		await Promise.all([todayAttendance.load(), loadOperations(), loadRawEvents()]);
	}

	const locationStatus: 'verified' | 'out' | 'locating' = attendanceLoading
		? 'locating'
		: attendance?.locationVerified === true
			? 'verified'
			: attendance?.locationVerified === false
				? 'out'
				: 'locating';

	const locationDistanceLabel =
		rawEvents[0]?.accuracyMeters != null ? `정확도 ${rawEvents[0].accuracyMeters}m` : undefined;

	return (
		<div className="space-y-5">
			<MobileHeader title="근태 체크" subtitle="오늘의 출퇴근" />

			{/* 위치 확인 상태 정보 밴드 */}
			<LocationCard status={locationStatus} distanceLabel={locationDistanceLabel} />

			{/* 체크인/아웃 액션 패널 — 독립 형제 요소 */}
			<AttendanceActionPanel
				attendance={attendance}
				loading={attendanceLoading}
				busy={todayAttendance.actionBusy}
				error={attendanceError}
				onAction={(kind, options) => void todayAttendance.performAction(kind, options)}
				onUpdate={(request) => todayAttendance.updateToday(request)}
				onCreateBreak={(request) => todayAttendance.createBreakRecord(request)}
				onUpdateBreak={(breakId, request) => todayAttendance.updateBreakRecord(breakId, request)}
				onDeleteBreak={(breakId) => todayAttendance.deleteBreakRecord(breakId)}
			/>

			{/* 자동 퇴근 알림 — 시스템이 자동 퇴근 처리한 경우 직원에게 안내 */}
			{attendance?.autoCheckedOut === true ? (
				<div className="flex items-center gap-2 rounded-card border border-border bg-card px-4 py-3 shadow-card">
					<StatusChip tone="amber">자동 퇴근</StatusChip>
					<span className="text-sm text-muted-foreground">
						시스템에 의해 자동 퇴근 처리되었습니다.
					</span>
				</div>
			) : null}

			{/* 메모 (관리자 기록) — 출근 상태에서 관리자가 기재한 메모를 직원에게 표시 */}
			{attendance?.note ? (
				<ListRow title="메모" subtitle={attendance.note} iconTone="neutral" />
			) : null}

			{/* 오늘 근무 계획: 원본 출퇴근 이벤트를 ListRow로 */}
			<section className="space-y-3">
				<SectionHeader
					action={
						<Button type="button" variant="outline" size="sm" onClick={() => void refresh()}>
							<RefreshCw className="size-4" />
						</Button>
					}
				>
					오늘 근무 계획
				</SectionHeader>
				{rawEventsState.status === 'loading' ? (
					<Spinner />
				) : rawEvents.length === 0 ? (
					<EmptyState message="오늘 기록된 출퇴근 이벤트가 없습니다." />
				) : (
					rawEvents.map((event) => (
						<ListRow
							key={event.id}
							title={attendanceRawEventTypeLabel(event.eventType)}
							subtitle={`${timeLabel(event.eventTime)}${event.note ? ` · ${event.note}` : ''} · ${attendanceRawEventSourceLabel(event.source)}`}
							iconTone={
								event.eventType === 'CHECK_IN' || event.eventType === 'CHECK_OUT'
									? 'green'
									: event.eventType === 'IN_PROGRESS_EDIT'
										? 'warning'
										: 'neutral'
							}
							trailing={
								<StatusChip tone={event.locationVerified ? 'green' : 'neutral'}>
									{event.locationVerified ? '위치 확인' : '미확인'}
								</StatusChip>
							}
						/>
					))
				)}
			</section>

			{/* 근태 운영 현황 (관리자/ADMIN 대상 — 데이터 있을 때만 표시) */}
			{operations ? <AttendanceOperationsPanel operations={operations} /> : null}
		</div>
	);
}

type LeaveAdminPanelData = {
	balance: LeaveBalanceResponse | null;
	grants: LeaveBalanceGrantResponse[];
};

function leaveGrantTypeLabel(
	type: LeaveBalanceGrantResponse['grantType'] | null | undefined
): string {
	const labels: Record<LeaveBalanceGrantResponse['grantType'], string> = {
		AUTO_FISCAL: '자동 부여',
		AUTO_JOIN_DATE: '입사일 부여',
		MANUAL_ADD: '수동 추가',
		MANUAL_DEDUCT: '수동 차감',
		MANUAL_RECALC: '재산정',
		CARRYOVER: '이월',
		COMPENSATION: '보상 휴가',
		COMPENSATION_REVOKE: '보상 회수'
	};
	return type ? labels[type] : '-';
}

function LeaveAdminActionDialog({
	employee,
	action,
	onClose,
	onSubmitted
}: {
	employee: EmployeeResponse;
	action: LeaveAdminActionKind;
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [year, setYear] = useState(new Date().getFullYear());
	const [days, setDays] = useState('');
	const [reason, setReason] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const needsDays = action !== 'recalculate';
	const title =
		action === 'add'
			? '수동 부여 (추가)'
			: action === 'deduct'
				? '수동 차감'
				: action === 'carryover'
					? '전년도 잔여 이월'
					: '총 연차 재산정';

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateLeaveAdminActionForm({ action, year, days, reason });
		if (!result.ok) {
			setError(result.message);
			return;
		}
		const actionPath: Record<LeaveAdminActionKind, string> = {
			add: 'grants',
			deduct: 'deductions',
			recalculate: 'recalculate',
			carryover: 'carryover'
		};
		setSubmitting(true);
		try {
			await api.post<LeaveBalanceResponse>(
				`/admin/leave/balances/${employee.id}/${actionPath[action]}`,
				result.request
			);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title={title}
			description={`${employee.name}님의 휴가 잔여를 조정합니다.`}
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="leave-admin-year">대상 연도</Label>
						<Input
							id="leave-admin-year"
							type="number"
							value={year}
							onChange={(event) => setYear(Number(event.target.value))}
						/>
					</div>
					{needsDays ? (
						<div className="space-y-2">
							<Label htmlFor="leave-admin-days">일수 *</Label>
							<Input
								id="leave-admin-days"
								type="number"
								value={days}
								onChange={(event) => setDays(event.target.value)}
							/>
						</div>
					) : null}
				</div>
				<div className="space-y-2">
					<Label htmlFor="leave-admin-reason">사유</Label>
					<textarea
						id="leave-admin-reason"
						value={reason}
						onChange={(event) => setReason(event.target.value)}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						확인
					</Button>
				</div>
			</form>
		</Modal>
	);
}

export function LeaveAdminPage() {
	const [employeesState, setEmployeesState] = useState<RequestState<EmployeeResponse[]>>({
		status: 'idle'
	});
	const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
	const [panelState, setPanelState] = useState<RequestState<LeaveAdminPanelData>>({
		status: 'idle'
	});
	const [action, setAction] = useState<LeaveAdminActionKind | null>(null);
	const year = new Date().getFullYear();

	const loadEmployees = useMemo(
		() => async () => {
			setEmployeesState({ status: 'loading' });
			try {
				const page = await api.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200');
				setEmployeesState({ status: 'success', data: page.content });
				setSelectedEmployeeId((current) => current ?? page.content[0]?.id ?? null);
			} catch (error) {
				setEmployeesState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	const loadPanel = useMemo(
		() => async (employeeId: number) => {
			setPanelState({ status: 'loading' });
			try {
				const [balance, grants] = await Promise.all([
					api
						.get<LeaveBalanceResponse>(`/admin/leave/balances/${employeeId}?year=${year}`)
						.catch(() => null),
					api
						.get<
							LeaveBalanceGrantResponse[]
						>(`/admin/leave/balances/${employeeId}/grants?year=${year}`)
						.catch(() => [])
				]);
				setPanelState({ status: 'success', data: { balance, grants } });
			} catch (error) {
				setPanelState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[year]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await loadEmployees();
		});
		return () => {
			active = false;
		};
	}, [loadEmployees]);

	useEffect(() => {
		if (selectedEmployeeId == null) return;
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await loadPanel(selectedEmployeeId);
		});
		return () => {
			active = false;
		};
	}, [loadPanel, selectedEmployeeId]);

	const employees = employeesState.status === 'success' ? employeesState.data : [];
	const selectedEmployee = employees.find((employee) => employee.id === selectedEmployeeId) ?? null;
	const panel = panelState.status === 'success' ? panelState.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="잔여 관리"
				description="직원별 휴가 잔여를 확인하고 수동 부여, 차감, 재산정을 처리합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button type="button" variant="outline" onClick={() => void loadEmployees()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						<Button type="button" disabled={!selectedEmployee} onClick={() => setAction('add')}>
							추가
						</Button>
						<Button
							type="button"
							variant="outline"
							disabled={!selectedEmployee}
							onClick={() => setAction('add')}
						>
							수동 조정
						</Button>
					</div>
				}
			/>
			{employeesState.status === 'loading' || employeesState.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{employeesState.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{employeesState.message}
				</div>
			) : null}
			{employeesState.status === 'success' ? (
				<div className="grid gap-4 xl:grid-cols-[1fr_22rem]">
					<Card>
						<CardHeader>
							<CardTitle>직원별 잔여</CardTitle>
							<CardDescription>
								직원을 선택하면 해당 연도 잔여와 조정 이력을 확인합니다.
							</CardDescription>
						</CardHeader>
						<CardContent>
							{employees.length === 0 ? (
								<EmptyState message="관리할 직원이 없습니다." />
							) : (
								<div className="overflow-x-auto rounded-lg border border-border">
									<table className="w-full min-w-[680px] text-left text-sm">
										<thead className="bg-muted/40 text-xs text-muted-foreground">
											<tr>
												<th className="px-4 py-3 font-semibold">직원</th>
												<th className="px-4 py-3 font-semibold">소속</th>
												<th className="px-4 py-3 font-semibold">입사일</th>
												<th className="px-4 py-3 font-semibold">상태</th>
											</tr>
										</thead>
										<tbody>
											{employees.map((employee) => (
												<tr
													key={employee.id}
													className="cursor-pointer border-t border-border hover:bg-muted/40"
													onClick={() => setSelectedEmployeeId(employee.id)}
												>
													<td className="px-4 py-4 font-semibold">{employee.name}</td>
													<td className="px-4 py-4 text-muted-foreground">
														{employee.department ?? '-'} · {employee.position ?? '-'}
													</td>
													<td className="px-4 py-4 text-muted-foreground">
														{dateLabel(employee.joinDate)}
													</td>
													<td className="px-4 py-4">
														<Badge
															variant={employee.id === selectedEmployeeId ? 'success' : 'outline'}
														>
															{employee.id === selectedEmployeeId
																? '선택됨'
																: employeeStatusLabel(employee.status)}
														</Badge>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</CardContent>
					</Card>
					<Card>
						<CardHeader>
							<CardTitle>
								{selectedEmployee ? `${selectedEmployee.name} 잔여` : '잔여 상세'}
							</CardTitle>
							<CardDescription>{year}년 기준</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							{panelState.status === 'loading' ? (
								<div className="flex min-h-32 items-center justify-center">
									<Spinner />
								</div>
							) : null}
							{panel?.balance ? (
								<div className="grid grid-cols-2 gap-3">
									<DetailTile label="총 부여" value={`${numberLabel(panel.balance.totalDays)}일`} />
									<DetailTile label="사용" value={`${numberLabel(panel.balance.usedDays)}일`} />
									<DetailTile
										label="잔여"
										value={dayHourLabel(panel.balance.displayDays, panel.balance.displayHours)}
									/>
									<DetailTile
										label="신청 가능"
										value={dayHourLabel(
											panel.balance.requestableDisplayDays,
											panel.balance.requestableDisplayHours
										)}
									/>
								</div>
							) : (
								<EmptyState message="직원을 선택하면 잔여 정보가 표시됩니다." />
							)}
							{panel?.grants.length ? (
								<div className="space-y-2">
									<p className="text-xs font-semibold text-muted-foreground">조정 이력</p>
									<div className="divide-y divide-border rounded-lg border border-border">
										{panel.grants.map((grant) => (
											<div key={grant.id} className="p-3 text-sm">
												<div className="flex items-center justify-between gap-3">
													<p className="font-semibold">{leaveGrantTypeLabel(grant.grantType)}</p>
													<p>{numberLabel(grant.days)}일</p>
												</div>
												<p className="mt-1 text-xs text-muted-foreground">
													{dateLabel(grant.grantedAt)} · {grant.reason ?? '사유 없음'}
												</p>
											</div>
										))}
									</div>
								</div>
							) : null}
						</CardContent>
					</Card>
				</div>
			) : null}
			{action && selectedEmployee ? (
				<LeaveAdminActionDialog
					employee={selectedEmployee}
					action={action}
					onClose={() => setAction(null)}
					onSubmitted={async () => {
						await loadPanel(selectedEmployee.id);
					}}
				/>
			) : null}
		</div>
	);
}

function leaveGrantBasisLabel(value: LeavePolicyResponse['grantBasis'] | null | undefined): string {
	if (value === 'JOIN_DATE') return '입사일 기준';
	return '회계연도 기준';
}

function leavePolicyFormFromResponse(policy: LeavePolicyResponse): LeavePolicyFormValues {
	return {
		grantBasis: policy.grantBasis,
		fiscalYearStartMonth: policy.fiscalYearStartMonth,
		carryoverEnabled: policy.carryoverEnabled,
		carryoverMaxDays: policy.carryoverMaxDays ?? '',
		advanceUsageEnabled: policy.advanceUsageEnabled,
		advanceUsageMaxDays: policy.advanceUsageMaxDays ?? ''
	};
}

export function LeavePolicyPage() {
	const [state, setState] = useState<RequestState<LeavePolicyResponse>>({ status: 'idle' });
	const [form, setForm] = useState<LeavePolicyFormValues>({
		grantBasis: 'FISCAL_YEAR',
		fiscalYearStartMonth: 1,
		carryoverEnabled: false,
		carryoverMaxDays: '',
		advanceUsageEnabled: false,
		advanceUsageMaxDays: ''
	});
	const [formError, setFormError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const data = await api.get<LeavePolicyResponse>('/admin/leave/policy');
				setForm(leavePolicyFormFromResponse(data));
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	function updateForm<T extends keyof LeavePolicyFormValues>(
		key: T,
		value: LeavePolicyFormValues[T]
	) {
		setForm((current) => ({ ...current, [key]: value }));
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateLeavePolicyForm(form);
		if (!result.ok) {
			setFormError(result.message);
			return;
		}
		setSaving(true);
		try {
			const data = await api.put<LeavePolicyResponse>('/admin/leave/policy', result.request);
			setState({ status: 'success', data });
			setForm(leavePolicyFormFromResponse(data));
			setFormError(null);
		} catch (error) {
			setFormError(getErrorMessage(error));
		} finally {
			setSaving(false);
		}
	}

	const policy = state.status === 'success' ? state.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="휴가 정책"
				description="연차 부여 기준, 이월, 선사용 정책을 관리합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{policy ? (
				<div className="grid gap-4 xl:grid-cols-[18rem_1fr]">
					<div className="grid gap-3">
						<ExpenseStatCard
							title="부여 기준"
							value={leaveGrantBasisLabel(policy.grantBasis)}
							description={`${numberLabel(policy.fiscalYearStartMonth)}월 시작`}
						/>
						<ExpenseStatCard
							title="이월"
							value={policy.carryoverEnabled ? '사용' : '미사용'}
							description={
								policy.carryoverMaxDays == null
									? '상한 없음'
									: `최대 ${numberLabel(policy.carryoverMaxDays)}일 이월`
							}
						/>
						<ExpenseStatCard
							title="선사용"
							value={policy.advanceUsageEnabled ? '사용' : '미사용'}
							description={
								policy.advanceUsageMaxDays == null
									? '한도 없음'
									: `최대 ${numberLabel(policy.advanceUsageMaxDays)}일`
							}
						/>
					</div>
					<Card>
						<CardHeader>
							<CardTitle>연차 부여 정책</CardTitle>
							<CardDescription>미래 연차 부여와 신청 가능 일수 계산에 적용됩니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<form aria-label="휴가 정책" className="space-y-5" onSubmit={handleSubmit}>
								<FormError message={formError} />
								<div className="grid gap-4 sm:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="leave-policy-basis">부여 기준</Label>
										<select
											id="leave-policy-basis"
											value={form.grantBasis}
											onChange={(event) => updateForm('grantBasis', event.target.value)}
											className={dialogControlClassName}
										>
											<option value="FISCAL_YEAR">회계연도 기준</option>
											<option value="JOIN_DATE">입사일 기준</option>
										</select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="leave-policy-month">회계연도 시작월</Label>
										<Input
											id="leave-policy-month"
											type="number"
											value={form.fiscalYearStartMonth}
											onChange={(event) => updateForm('fiscalYearStartMonth', event.target.value)}
										/>
									</div>
								</div>
								<label className="flex items-center gap-2 text-sm">
									<input
										type="checkbox"
										checked={form.carryoverEnabled}
										onChange={(event) => updateForm('carryoverEnabled', event.target.checked)}
										className="size-4 rounded border-border"
									/>
									전년도 잔여 이월 허용
								</label>
								<div className="space-y-2">
									<Label htmlFor="leave-policy-carryover">이월 상한 (일)</Label>
									<Input
										id="leave-policy-carryover"
										type="number"
										value={form.carryoverMaxDays}
										onChange={(event) => updateForm('carryoverMaxDays', event.target.value)}
									/>
								</div>
								<label className="flex items-center gap-2 text-sm">
									<input
										type="checkbox"
										checked={form.advanceUsageEnabled}
										onChange={(event) => updateForm('advanceUsageEnabled', event.target.checked)}
										className="size-4 rounded border-border"
									/>
									연차 선사용 허용
								</label>
								<div className="space-y-2">
									<Label htmlFor="leave-policy-advance">선사용 한도 (일)</Label>
									<Input
										id="leave-policy-advance"
										type="number"
										value={form.advanceUsageMaxDays}
										onChange={(event) => updateForm('advanceUsageMaxDays', event.target.value)}
									/>
								</div>
								<div className="flex justify-end">
									<Button type="submit" disabled={saving}>
										저장
									</Button>
								</div>
							</form>
						</CardContent>
					</Card>
				</div>
			) : null}
		</div>
	);
}

export function ApprovalDetailPage({ route }: { route: MatchedRoute }) {
	const [state, setState] = useState<RequestState<ApprovalResponse>>({ status: 'idle' });
	const [myEmployeeId, setMyEmployeeId] = useState<number | null>(null);
	const [actionError, setActionError] = useState<string | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const data = await api.get<ApprovalResponse>(`/approvals/${route.params.id}`);
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[route.params.id]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	useEffect(() => {
		let active = true;
		api
			.get<EmployeeResponse>('/employees/me')
			.then((emp) => {
				if (active) setMyEmployeeId(emp.id);
			})
			.catch(() => {
				/* action buttons stay hidden if this fails */
			});
		return () => {
			active = false;
		};
	}, []);

	const approval = state.status === 'success' ? state.data : null;
	const canAct =
		!!approval && approval.status === 'IN_PROGRESS' && approval.currentApproverId === myEmployeeId;

	async function approve() {
		if (!approval) return;
		setActionError(null);
		try {
			await api.post(`/approvals/${approval.id}/approve`, {});
			await load();
		} catch (err) {
			setActionError(getErrorMessage(err, '승인 처리 중 오류가 발생했습니다.'));
		}
	}

	async function reject() {
		if (!approval) return;
		setActionError(null);
		try {
			await api.post(`/approvals/${approval.id}/reject`, {});
			await load();
		} catch (err) {
			setActionError(getErrorMessage(err, '반려 처리 중 오류가 발생했습니다.'));
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="결재 상세"
				description="결재 문서, 승인 단계, 참조자를 확인합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{actionError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{actionError}
				</div>
			) : null}
			{approval ? (
				<>
					<Card>
						<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
							<div>
								<CardTitle>{approvalTitle(approval)}</CardTitle>
								<CardDescription>
									{approval.documentSummary?.subtitle ?? approvalEntityLabel(approval.entityType)}
								</CardDescription>
							</div>
							<Badge variant={approvalStatusVariant(approval.status)}>
								{approvalStatusLabel(approval.status)}
							</Badge>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
								<DetailTile
									label="요청자"
									value={approval.requesterName ?? `직원 #${approval.requesterId}`}
								/>
								<DetailTile label="현재 결재자" value={approval.currentApproverName ?? '-'} />
								<DetailTile label="요청일" value={dateLabel(approval.createdAt)} />
								<DetailTile label="완료일" value={dateLabel(approval.completedAt)} />
							</div>
							{approval.documentSummary?.fields.length ? (
								<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
									{approval.documentSummary.fields.map((field) => (
										<DetailTile key={field.label} label={field.label} value={field.value} />
									))}
								</div>
							) : null}
							{canAct ? (
								<div className="flex justify-end gap-2">
									<Button type="button" variant="outline" onClick={() => void reject()}>
										반려
									</Button>
									<Button type="button" onClick={() => void approve()}>
										승인
									</Button>
								</div>
							) : null}
						</CardContent>
					</Card>
					<div className="grid gap-4 xl:grid-cols-2">
						<Card>
							<CardHeader>
								<CardTitle>결재 단계</CardTitle>
								<CardDescription>단계별 승인자와 처리 상태입니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="space-y-3">
									{approval.steps.map((step) => (
										<div key={step.id} className="rounded-lg border border-border p-4">
											<div className="flex flex-wrap items-center justify-between gap-3">
												<p className="font-semibold">
													{numberLabel(step.stepOrder)}단계 ·{' '}
													{approvalStepActionLabel(step.actionLabel)} ·{' '}
													{step.approverName ?? `직원 #${step.approverId}`}
												</p>
												<div className="flex items-center gap-2">
													{step.finalDecision ? <Badge variant="outline">전결</Badge> : null}
													<StatusBadge status={step.status} />
												</div>
											</div>
											<p className="mt-1 text-sm text-muted-foreground">
												{dateLabel(step.decidedAt)} · {step.comment ?? '코멘트 없음'}
											</p>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle>참조자</CardTitle>
								<CardDescription>문서를 공유받은 직원입니다.</CardDescription>
							</CardHeader>
							<CardContent>
								{approval.references?.length ? (
									<div className="space-y-3">
										{approval.references.map((reference) => (
											<div
												key={reference.id}
												className="flex items-center justify-between rounded-lg border border-border p-4"
											>
												<div>
													<p className="font-semibold">
														{reference.employeeName ?? `직원 #${reference.employeeId}`}
													</p>
													<p className="mt-1 text-sm text-muted-foreground">
														{dateLabel(reference.createdAt)}
													</p>
												</div>
												<Badge variant={reference.read ? 'success' : 'warning'}>
													{reference.read ? '읽음' : '미확인'}
												</Badge>
											</div>
										))}
									</div>
								) : (
									<EmptyState message="참조자가 없습니다." />
								)}
							</CardContent>
						</Card>
					</div>
				</>
			) : null}
		</div>
	);
}

function ApprovalLineDialog({
	employees,
	onClose,
	onSubmitted
}: {
	employees: EmployeeResponse[];
	onClose: () => void;
	onSubmitted: () => Promise<void>;
}) {
	const [form, setForm] = useState<ApprovalLineFormValues>({
		name: '',
		description: '',
		steps: [{ stepOrder: 1, approverId: 0, actionLabel: '승인', finalDecision: false }]
	});
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	function updateStep(index: number, approverId: number) {
		setForm((current) => ({
			...current,
			steps: current.steps.map((step, stepIndex) =>
				stepIndex === index ? { ...step, approverId } : step
			)
		}));
	}

	function updateStepActionLabel(index: number, actionLabel: string) {
		setForm((current) => ({
			...current,
			steps: current.steps.map((step, stepIndex) =>
				stepIndex === index ? { ...step, actionLabel } : step
			)
		}));
	}

	function updateStepFinalDecision(index: number, finalDecision: boolean) {
		setForm((current) => ({
			...current,
			steps: current.steps.map((step, stepIndex) =>
				stepIndex === index ? { ...step, finalDecision } : step
			)
		}));
	}

	function addStep() {
		setForm((current) => ({
			...current,
			steps: [
				...current.steps,
				{
					stepOrder: current.steps.length + 1,
					approverId: 0,
					actionLabel: '승인',
					finalDecision: false
				}
			]
		}));
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateApprovalLineForm(form, employees);
		if (!result.ok) {
			setError(result.message);
			return;
		}
		setSubmitting(true);
		try {
			await api.post<ApprovalLineResponse>('/approval-lines', result.request);
			await onSubmitted();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title="결재선 추가"
			description="결재 문서에 적용할 승인 순서를 설정합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={handleSubmit}>
				<FormError message={error} />
				<div className="space-y-2">
					<Label htmlFor="approval-line-name">이름</Label>
					<Input
						id="approval-line-name"
						value={form.name}
						onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="approval-line-description">설명</Label>
					<textarea
						id="approval-line-description"
						value={form.description}
						onChange={(event) =>
							setForm((current) => ({ ...current, description: event.target.value }))
						}
						className={dialogTextAreaClassName}
					/>
				</div>
				<div className="space-y-3">
					{form.steps.map((step, index) => (
						<div key={step.stepOrder} className="space-y-2">
							<div className="grid gap-3 sm:grid-cols-[1fr_140px]">
								<div className="space-y-2">
									<Label htmlFor={`approval-line-step-${step.stepOrder}`}>
										{index + 1}단계 결재자
									</Label>
									<select
										id={`approval-line-step-${step.stepOrder}`}
										value={step.approverId || ''}
										onChange={(event) => updateStep(index, Number(event.target.value))}
										className={dialogControlClassName}
									>
										<option value="">선택</option>
										{employees.map((employee) => (
											<option key={employee.id} value={employee.id}>
												{employee.name}
											</option>
										))}
									</select>
								</div>
								<div className="space-y-2">
									<Label htmlFor={`approval-line-step-label-${step.stepOrder}`}>
										{index + 1}단계 승인 라벨
									</Label>
									<Input
										id={`approval-line-step-label-${step.stepOrder}`}
										value={step.actionLabel}
										onChange={(event) => updateStepActionLabel(index, event.target.value)}
										placeholder="승인"
									/>
								</div>
							</div>
							<label className="flex items-center gap-2 text-sm text-muted-foreground">
								<input
									type="checkbox"
									checked={Boolean(step.finalDecision)}
									onChange={(event) => updateStepFinalDecision(index, event.target.checked)}
									aria-label={`${index + 1}단계 전결`}
								/>
								전결 (이 단계에서 승인 시 이후 단계를 건너뛰고 즉시 완료)
							</label>
						</div>
					))}
					<Button type="button" variant="outline" size="sm" onClick={addStep}>
						+ 단계 추가
					</Button>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose}>
						취소
					</Button>
					<Button type="submit" disabled={submitting}>
						저장
					</Button>
				</div>
			</form>
		</Modal>
	);
}

export function ApprovalLinesPage() {
	const [state, setState] = useState<
		RequestState<{ lines: ApprovalLineResponse[]; employees: EmployeeResponse[] }>
	>({ status: 'idle' });
	const [dialogOpen, setDialogOpen] = useState(false);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [lines, employees] = await Promise.all([
					api.get<ApprovalLineResponse[]>('/approval-lines'),
					api
						.get<PageResponse<EmployeeResponse>>('/employees?page=0&size=200')
						.then((page) => page.content)
						.catch(() => [])
				]);
				setState({ status: 'success', data: { lines, employees } });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="결재선"
				description="승인자와 결재 단계를 관리합니다."
				actions={
					<div className="flex flex-wrap gap-2">
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						<Button type="button" onClick={() => setDialogOpen(true)}>
							결재선 추가
						</Button>
					</div>
				}
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{data ? (
				<Card>
					<CardHeader>
						<CardTitle>결재선 관리</CardTitle>
						<CardDescription>결재선 이름, 설명, 단계별 승인자를 확인합니다.</CardDescription>
					</CardHeader>
					<CardContent>
						{data.lines.length === 0 ? (
							<EmptyState message="등록된 결재선이 없습니다." />
						) : (
							<div className="overflow-x-auto rounded-lg border border-border">
								<table className="w-full min-w-[720px] text-left text-sm">
									<thead className="bg-muted/40 text-xs text-muted-foreground">
										<tr>
											<th className="px-4 py-3 font-semibold">결재선</th>
											<th className="px-4 py-3 font-semibold">단계</th>
											<th className="px-4 py-3 font-semibold">상태</th>
										</tr>
									</thead>
									<tbody>
										{data.lines.map((line) => (
											<tr key={line.id} className="border-t border-border">
												<td className="px-4 py-4">
													<p className="font-semibold">{line.name}</p>
													<p className="mt-1 text-xs text-muted-foreground">
														{line.description ?? '설명 없음'}
													</p>
												</td>
												<td className="px-4 py-4 text-muted-foreground">
													<div className="flex flex-wrap gap-2">
														{line.steps.map((step) => (
															<Badge key={`${line.id}-${step.stepOrder}`} variant="outline">
																{numberLabel(step.stepOrder)}단계 ·{' '}
																{approvalStepActionLabel(step.actionLabel)} · 직원 #
																{step.approverId}
																{step.finalDecision ? ' (전결)' : ''}
															</Badge>
														))}
													</div>
												</td>
												<td className="px-4 py-4">
													<Badge variant={line.active ? 'success' : 'outline'}>
														{line.active ? '활성' : '비활성'}
													</Badge>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</CardContent>
				</Card>
			) : null}
			{dialogOpen && data ? (
				<ApprovalLineDialog
					employees={data.employees}
					onClose={() => setDialogOpen(false)}
					onSubmitted={load}
				/>
			) : null}
		</div>
	);
}

function payslipPeriodLabel(payslip: { payYear: number; payMonth: number }): string {
	return `${payslip.payYear}년 ${payslip.payMonth}월`;
}

export function EmployeePayslipsPage({ route }: { route: MatchedRoute }) {
	const employeeId = route.params.employeeId;
	const [state, setState] = useState<
		RequestState<{ employeeName: string; payslips: PayslipResponse[] }>
	>({ status: 'idle' });

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [employee, payslips] = await Promise.all([
					api
						.get<EmployeeResponse>(`/employees/${employeeId}`)
						.then((e) => e.name)
						.catch(() => `직원 #${employeeId}`),
					api.get<PayslipResponse[]>(`/employees/${employeeId}/payslips`)
				]);
				setState({ status: 'success', data: { employeeName: employee, payslips } });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[employeeId]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title={data ? `${data.employeeName} 급여명세서` : '직원 급여명세서'}
				description="이 직원의 전 기간 급여명세서를 최신순으로 확인합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{data ? (
				<Card>
					<CardHeader>
						<CardTitle>명세서 목록</CardTitle>
						<CardDescription>행을 클릭하면 상세 명세서로 이동합니다.</CardDescription>
					</CardHeader>
					<CardContent>
						{data.payslips.length === 0 ? (
							<EmptyState message="등록된 급여명세서가 없습니다." />
						) : (
							<div className="overflow-x-auto rounded-lg border border-border">
								<table className="w-full min-w-[560px] text-left text-sm">
									<thead className="bg-muted/40 text-xs text-muted-foreground">
										<tr>
											<th className="px-4 py-3 font-semibold">귀속월</th>
											<th className="px-4 py-3 font-semibold">상태</th>
											<th className="px-4 py-3 font-semibold">지급일</th>
											<th className="px-4 py-3 font-semibold">금액 등록</th>
										</tr>
									</thead>
									<tbody>
										{data.payslips.map((payslip) => (
											<tr key={payslip.id} className="border-t border-border hover:bg-muted/20">
												<td className="px-4 py-4 font-semibold">
													<Link
														href={`/payroll/payslips/${employeeId}/${payslip.id}`}
														className="text-primary hover:underline"
													>
														{payslipPeriodLabel(payslip)}
													</Link>
												</td>
												<td className="px-4 py-4">
													<Badge variant={payrollStatusVariant(payslip.status)}>
														{payrollStatusLabel(payslip.status)}
													</Badge>
												</td>
												<td className="px-4 py-4 text-muted-foreground">
													{dateLabel(payslip.paymentDate)}
												</td>
												<td className="px-4 py-4 text-muted-foreground">
													{payslip.amountsRegistered ? '완료' : '대기'}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</CardContent>
				</Card>
			) : null}
		</div>
	);
}

function currentPayrollYear(): number {
	return new Date().getFullYear();
}

export function PayrollPayslipsPage() {
	const [filters, setFilters] = useState({ payYear: '', payMonth: '', status: '', name: '' });
	const [query, setQuery] = useState({ payYear: '', payMonth: '', status: '', name: '', page: 0 });
	const [state, setState] = useState<RequestState<PageResponse<PayslipSummaryResponse>>>({
		status: 'idle'
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const params = new URLSearchParams();
				if (query.payYear) params.set('payYear', query.payYear);
				if (query.payMonth) params.set('payMonth', query.payMonth);
				if (query.status) params.set('status', query.status);
				if (query.name.trim()) params.set('name', query.name.trim());
				params.set('page', String(query.page));
				params.set('size', '20');
				const data = await api.get<PageResponse<PayslipSummaryResponse>>(
					`/payroll/payslips/search?${params.toString()}`
				);
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[query]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	function applyFilters() {
		setQuery({ ...filters, page: 0 });
	}

	function movePage(delta: number) {
		setQuery((current) => ({ ...current, page: Math.max(0, current.page + delta) }));
	}

	const data = state.status === 'success' ? state.data : null;
	const years = Array.from({ length: 8 }, (_, offset) => currentPayrollYear() - offset);

	return (
		<div className="space-y-6">
			<PageHeader
				title="급여명세서 목록"
				description="회사 전체 급여명세서를 귀속월·상태·직원으로 검색합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>
			<Card>
				<CardContent className="grid gap-3 py-5 sm:grid-cols-2 lg:grid-cols-5 lg:items-end">
					<div className="space-y-2">
						<Label htmlFor="payslip-filter-year">귀속 연도</Label>
						<select
							id="payslip-filter-year"
							className={dialogControlClassName}
							value={filters.payYear}
							onChange={(event) => setFilters((f) => ({ ...f, payYear: event.target.value }))}
						>
							<option value="">전체</option>
							{years.map((year) => (
								<option key={year} value={year}>
									{year}년
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="payslip-filter-month">귀속 월</Label>
						<select
							id="payslip-filter-month"
							className={dialogControlClassName}
							value={filters.payMonth}
							onChange={(event) => setFilters((f) => ({ ...f, payMonth: event.target.value }))}
						>
							<option value="">전체</option>
							{Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
								<option key={month} value={month}>
									{month}월
								</option>
							))}
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="payslip-filter-status">상태</Label>
						<select
							id="payslip-filter-status"
							className={dialogControlClassName}
							value={filters.status}
							onChange={(event) => setFilters((f) => ({ ...f, status: event.target.value }))}
						>
							<option value="">전체</option>
							<option value="DRAFT">초안</option>
							<option value="CONFIRMED">확정</option>
							<option value="CANCELLED">취소</option>
						</select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="payslip-filter-name">직원명</Label>
						<Input
							id="payslip-filter-name"
							value={filters.name}
							onChange={(event) => setFilters((f) => ({ ...f, name: event.target.value }))}
							placeholder="직원명 검색"
							onKeyDown={(event) => {
								if (event.key === 'Enter') applyFilters();
							}}
						/>
					</div>
					<Button type="button" onClick={applyFilters}>
						<Search className="size-4" />
						조회
					</Button>
				</CardContent>
			</Card>

			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-40 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}

			{data ? (
				<Card>
					<CardHeader className="gap-2 sm:flex-row sm:items-center sm:justify-between">
						<CardTitle>명세서 {numberLabel(data.totalElements)}건</CardTitle>
						<CardDescription>행을 클릭하면 상세 명세서로 이동합니다.</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{data.content.length === 0 ? (
							<EmptyState message="조건에 맞는 급여명세서가 없습니다." />
						) : (
							<div className="overflow-x-auto rounded-lg border border-border">
								<table className="w-full min-w-[680px] text-left text-sm">
									<thead className="bg-muted/40 text-xs text-muted-foreground">
										<tr>
											<th className="px-4 py-3 font-semibold">직원</th>
											<th className="px-4 py-3 font-semibold">귀속월</th>
											<th className="px-4 py-3 font-semibold">상태</th>
											<th className="px-4 py-3 font-semibold">지급일</th>
											<th className="px-4 py-3 font-semibold">금액 등록</th>
										</tr>
									</thead>
									<tbody>
										{data.content.map((row) => (
											<tr key={row.id} className="border-t border-border hover:bg-muted/20">
												<td className="px-4 py-4 font-semibold">
													<Link
														href={`/payroll/payslips/${row.employeeId}/${row.id}`}
														className="text-primary hover:underline"
													>
														{row.employeeName ?? `직원 #${row.employeeId}`}
													</Link>
												</td>
												<td className="px-4 py-4 text-muted-foreground">
													{payrollMonthLabel(row.payYear, row.payMonth)}
												</td>
												<td className="px-4 py-4">
													<Badge variant={payrollStatusVariant(row.status)}>
														{payrollStatusLabel(row.status)}
													</Badge>
												</td>
												<td className="px-4 py-4 text-muted-foreground">
													{dateLabel(row.paymentDate)}
												</td>
												<td className="px-4 py-4 text-muted-foreground">
													{row.amountsRegistered ? '완료' : '대기'}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
						{data.totalPages > 1 ? (
							<div className="flex items-center justify-between">
								<Button
									type="button"
									variant="outline"
									size="sm"
									disabled={query.page <= 0}
									onClick={() => movePage(-1)}
								>
									<ChevronLeft className="size-4" />
									이전
								</Button>
								<span className="text-sm text-muted-foreground">
									{query.page + 1} / {data.totalPages}
								</span>
								<Button
									type="button"
									variant="outline"
									size="sm"
									disabled={query.page + 1 >= data.totalPages}
									onClick={() => movePage(1)}
								>
									다음
									<ChevronRight className="size-4" />
								</Button>
							</div>
						) : null}
					</CardContent>
				</Card>
			) : null}
		</div>
	);
}

export function PayslipDetailPage({ route }: { route: MatchedRoute }) {
	const { isAdmin } = useAuth();
	const [state, setState] = useState<
		RequestState<{ payslip: PayslipResponse; reveal: PayslipRevealResponse | null }>
	>({ status: 'idle' });
	const [resending, setResending] = useState(false);
	const [resendNotice, setResendNotice] = useState<string | null>(null);
	const [resendError, setResendError] = useState<string | null>(null);
	const [revealing, setRevealing] = useState(false);
	const [revealError, setRevealError] = useState<string | null>(null);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const payslip = await api.get<PayslipResponse>(
					`/employees/${route.params.employeeId}/payslips/${route.params.payslipId}`
				);
				setState({ status: 'success', data: { payslip, reveal: null } });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[route.params.employeeId, route.params.payslipId]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const data = state.status === 'success' ? state.data : null;
	const revealedAmounts = useMemo(
		() => (data?.reveal ? new Map(data.reveal.items.map((item) => [item.id, item.amount])) : null),
		[data]
	);

	async function revealAmounts() {
		if (!data || data.reveal) return;
		setRevealing(true);
		setRevealError(null);
		try {
			const reveal = await api.post<PayslipRevealResponse>(
				`/employees/${route.params.employeeId}/payslips/${route.params.payslipId}/reveal`,
				{}
			);
			setState((current) =>
				current.status === 'success'
					? { status: 'success', data: { ...current.data, reveal } }
					: current
			);
		} catch (error) {
			setRevealError(getErrorMessage(error));
		} finally {
			setRevealing(false);
		}
	}

	async function resendNotification() {
		if (!data) return;
		const confirmed = window.confirm(
			'이 명세서의 발행 알림(앱·푸시·이메일)을 다시 발송할까요? 직원에게 중복 알림이 갈 수 있습니다.'
		);
		if (!confirmed) return;
		setResending(true);
		setResendError(null);
		setResendNotice(null);
		try {
			await api.post(
				`/employees/${route.params.employeeId}/payslips/${route.params.payslipId}/resend`
			);
			setResendNotice('발행 알림을 다시 발송했습니다.');
		} catch (error) {
			setResendError(getErrorMessage(error));
		} finally {
			setResending(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="급여명세서 상세"
				description="개별 급여명세서의 산출 항목과 처리 상태를 확인합니다."
				actions={
					<div className="flex flex-wrap items-center gap-2">
						<Link href="/account/payslips" className="text-sm font-medium text-primary underline">
							내 급여명세서
						</Link>
						{data && data.payslip.amountsRegistered && !data.reveal ? (
							<Button
								type="button"
								variant="outline"
								disabled={revealing}
								onClick={() => void revealAmounts()}
							>
								<Eye className="size-4" />
								{revealing ? '확인 중...' : '금액 보기'}
							</Button>
						) : null}
						{isAdmin && data?.payslip.status === 'CONFIRMED' ? (
							<Button
								type="button"
								variant="outline"
								disabled={resending}
								onClick={() => void resendNotification()}
							>
								<Bell className="size-4" />
								{resending ? '재발송 중...' : '알림 재발송'}
							</Button>
						) : null}
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					</div>
				}
			/>
			{revealError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{revealError}
				</div>
			) : null}
			{resendNotice ? (
				<div className="rounded-lg bg-success/10 px-4 py-3 text-sm text-success-foreground">
					{resendNotice}
				</div>
			) : null}
			{resendError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{resendError}
				</div>
			) : null}
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{data ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<ExpenseStatCard
							title="귀속월"
							value={payslipPeriodLabel(data.payslip)}
							description={`지급일 ${dateLabel(data.payslip.paymentDate)}`}
						/>
						<ExpenseStatCard
							title="상태"
							value={payrollStatusLabel(data.payslip.status)}
							description={dateLabel(data.payslip.confirmedAt)}
						/>
						<ExpenseStatCard
							title="부양가족"
							value={`${numberLabel(data.payslip.dependentsCount)}명`}
							description={`자녀 ${numberLabel(data.payslip.childrenCount)}명`}
						/>
						<ExpenseStatCard
							title="금액 등록"
							value={data.payslip.amountsRegistered ? '완료' : '대기'}
							description={data.payslip.taxTableVersion ?? '세액표 미등록'}
						/>
					</div>
					{data.reveal ? (
						<div className="grid grid-cols-3 gap-3 sm:gap-4">
							<ExpenseStatCard
								title="지급 합계"
								value={wonLabel(data.reveal.grossPay)}
								description={`과세 ${wonLabel(data.reveal.taxablePay)}`}
							/>
							<ExpenseStatCard
								title="공제 합계"
								value={wonLabel(data.reveal.totalDeduction)}
								description="공제 항목 합산"
							/>
							<ExpenseStatCard
								title="실수령액"
								value={wonLabel(data.reveal.netPay)}
								description="지급 합계 − 공제 합계"
							/>
						</div>
					) : null}
					<Card>
						<CardHeader>
							<CardTitle>명세서 항목</CardTitle>
							<CardDescription>{data.payslip.memo ?? '메모 없음'}</CardDescription>
						</CardHeader>
						<CardContent>
							{data.payslip.items.length === 0 ? (
								<EmptyState message="명세서 항목이 없습니다." />
							) : (
								<div className="overflow-x-auto rounded-lg border border-border">
									<table className="w-full min-w-[760px] text-left text-sm">
										<thead className="bg-muted/40 text-xs text-muted-foreground">
											<tr>
												<th className="px-4 py-3 font-semibold">항목</th>
												<th className="px-4 py-3 font-semibold">유형</th>
												<th className="px-4 py-3 font-semibold">금액</th>
												<th className="px-4 py-3 font-semibold">과세</th>
												<th className="px-4 py-3 font-semibold">생성</th>
												<th className="px-4 py-3 font-semibold">산출 근거</th>
											</tr>
										</thead>
										<tbody>
											{data.payslip.items.map((item) => (
												<tr key={item.id} className="border-t border-border">
													<td className="px-4 py-4 font-semibold">{item.name}</td>
													<td className="px-4 py-4 text-muted-foreground">
														{item.itemType === 'EARNING' ? '지급' : '공제'}
													</td>
													<td className="px-4 py-4 font-semibold">
														{!data.payslip.amountsRegistered
															? '-'
															: revealedAmounts
																? wonLabel(revealedAmounts.get(item.id))
																: '₩ •••'}
													</td>
													<td className="px-4 py-4">
														{item.itemType === 'EARNING' ? (
															<Badge variant={item.taxable ? 'warning' : 'outline'}>
																{item.taxable ? '과세' : '비과세'}
															</Badge>
														) : (
															<span className="text-muted-foreground">-</span>
														)}
													</td>
													<td className="px-4 py-4 text-muted-foreground">
														{item.systemGenerated ? '자동' : '수동'}
														{item.overridden ? ' · 수정됨' : ''}
													</td>
													<td className="px-4 py-4 text-muted-foreground">
														{item.description ?? '-'}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</CardContent>
					</Card>
					{data.payslip.greeting ? (
						<Card>
							<CardContent className="py-4 text-sm text-muted-foreground">
								{data.payslip.greeting}
							</CardContent>
						</Card>
					) : null}
				</>
			) : null}
		</div>
	);
}

function digitalContractDocumentTypeLabel(
	documentType: DigitalContractRequestResponse['documentType']
): string {
	const labels: Record<DigitalContractRequestResponse['documentType'], string> = {
		EMPLOYMENT_CONTRACT: '근로계약서',
		NDA_SECURITY_PLEDGE: '비밀유지·보안각서',
		IP_ASSIGNMENT: '지식재산권 귀속 동의서',
		PERSONAL_INFO_CONSENT: '개인정보 수집·이용 동의서',
		NON_COMPETE_PLEDGE: '겸직·경업 제한 서약서',
		POLICY_ACKNOWLEDGEMENT: '회사 규정 준수 서약서',
		EQUIPMENT_PLEDGE: '회사 지급 장비 사용 서약서'
	};
	return labels[documentType];
}

function DigitalContractSignatureDialog({
	contract,
	onClose,
	onSigned
}: {
	contract: DigitalContractRequestResponse;
	onClose: () => void;
	onSigned: () => Promise<void>;
}) {
	const [signatureName, setSignatureName] = useState(contract.employeeName ?? '');
	const [agreed, setAgreed] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const label = digitalContractDocumentTypeLabel(contract.documentType);

	async function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!agreed || !signatureName.trim()) return;
		setSubmitting(true);
		setError(null);
		try {
			await api.post(`/digital-contracts/requests/${contract.id}/sign`, {
				signatureName: signatureName.trim(),
				agreed: true
			});
			await onSigned();
			onClose();
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Modal
			title={`${label} 서명`}
			description="내용을 확인하고 본인 명의로 전자서명합니다."
			onClose={onClose}
		>
			<form className="space-y-4" onSubmit={(event) => void submit(event)}>
				<FormError message={error} />
				<div className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/20 p-4 text-sm leading-6 text-muted-foreground">
					{contract.contentSnapshot}
				</div>
				<label className="flex items-start gap-2 text-sm font-medium">
					<input
						type="checkbox"
						checked={agreed}
						onChange={(event) => setAgreed(event.target.checked)}
						aria-label="계약 내용에 동의합니다"
					/>
					계약 내용에 동의합니다
				</label>
				<div className="space-y-2">
					<Label htmlFor="digital-contract-signature-name">서명자명</Label>
					<Input
						id="digital-contract-signature-name"
						aria-label="서명자명"
						value={signatureName}
						onChange={(event) => setSignatureName(event.target.value)}
					/>
				</div>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
						취소
					</Button>
					<Button type="submit" disabled={!agreed || !signatureName.trim() || submitting}>
						{submitting ? '서명 처리 중...' : '전자서명 완료'}
					</Button>
				</div>
			</form>
		</Modal>
	);
}

export function AccountContractsPage() {
	const [state, setState] = useState<RequestState<DigitalContractRequestResponse[]>>({
		status: 'idle'
	});
	const [selected, setSelected] = useState<DigitalContractRequestResponse | null>(null);
	const [packetTitle, setPacketTitle] = useState<string | null>(null);
	const searchParams = useSearchParams();
	const bundleId = searchParams.get('bundleId')?.trim() || null;

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				if (bundleId) {
					const packet = await api.get<DigitalContractPacketResponse>(
						`/digital-contracts/packets/${encodeURIComponent(bundleId)}`
					);
					setPacketTitle(packet.title);
					setState({ status: 'success', data: packet.documents });
					return;
				}
				const requests = await api.get<DigitalContractRequestResponse[]>(
					'/digital-contracts/requests/me'
				);
				setPacketTitle(null);
				setState({ status: 'success', data: requests });
			} catch (error) {
				setPacketTitle(null);
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[bundleId]
	);

	useEffect(() => {
		void load();
	}, [load]);

	const requests = state.status === 'success' ? state.data : [];
	const pendingCount = requests.filter((request) => request.status === 'SENT').length;

	return (
		<div className="space-y-5">
			<MobileHeader title="내 전자계약" subtitle="근로계약서와 입사 서류를 확인·서명합니다." />
			<div className="flex flex-wrap items-center justify-between gap-3">
				<Badge variant={pendingCount > 0 ? 'warning' : 'success'}>
					서명 대기 {numberLabel(pendingCount)}건
				</Badge>
				<Button type="button" variant="outline" size="sm" onClick={() => void load()}>
					<RefreshCw className="size-4" />
					새로고침
				</Button>
			</div>
			{packetTitle ? (
				<div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
					<p className="text-xs font-semibold text-primary">입사 서류 묶음</p>
					<p className="mt-1 text-sm font-semibold">{packetTitle}</p>
					<p className="mt-1 text-xs text-muted-foreground">이 메일로 요청된 서류만 표시합니다.</p>
				</div>
			) : null}
			{state.status === 'loading' || state.status === 'idle' ? (
				<div className="flex min-h-32 items-center justify-center">
					<Spinner size="lg" />
				</div>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{state.status === 'success' ? (
				requests.length === 0 ? (
					<EmptyState message="확인할 전자계약이 없습니다." />
				) : (
					<div className="space-y-3">
						{requests.map((contract) => {
							const label = digitalContractDocumentTypeLabel(contract.documentType);
							const legacyDocumentTypes =
								contract.source === 'LEGACY_MIGRATION'
									? contract.legacyDocumentTypes?.length
										? contract.legacyDocumentTypes
										: [contract.documentType]
									: [];
							const pending = contract.status === 'SENT';
							return (
								<Card key={contract.id}>
									<CardContent className="flex flex-col gap-4 pt-5 sm:flex-row sm:items-center sm:justify-between">
										<div className="min-w-0">
											<p className="font-semibold">{label}</p>
											<p className="mt-1 truncate text-sm text-muted-foreground">
												{contract.title}
											</p>
											<p className="mt-1 text-xs text-muted-foreground">
												{dateLabel(contract.startedAt)} ·{' '}
												{contract.source === 'LEGACY_MIGRATION' ? '기존 문서 이관' : '전자계약'}
											</p>
											{legacyDocumentTypes.length > 0 ? (
												<div className="mt-2 flex flex-wrap gap-1.5">
													{legacyDocumentTypes.map((documentType) => (
														<Badge key={`${contract.id}-${documentType}`} variant="secondary">
															{digitalContractDocumentTypeLabel(documentType)}
														</Badge>
													))}
												</div>
											) : null}
										</div>
										<div className="flex shrink-0 items-center gap-2">
											<Badge
												variant={
													pending ? 'warning' : contract.status === 'SIGNED' ? 'success' : 'outline'
												}
											>
												{digitalContractStatusLabel(contract.status)}
											</Badge>
											{pending ? (
												<Button type="button" size="sm" onClick={() => setSelected(contract)}>
													{label} 확인
												</Button>
											) : null}
										</div>
									</CardContent>
								</Card>
							);
						})}
					</div>
				)
			) : null}
			{selected ? (
				<DigitalContractSignatureDialog
					contract={selected}
					onClose={() => setSelected(null)}
					onSigned={load}
				/>
			) : null}
		</div>
	);
}

export function AccountPayslipsPage() {
	const [state, setState] = useState<RequestState<PayslipResponse[]>>({ status: 'idle' });
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [revealed, setRevealed] = useState(false);
	const [revealState, setRevealState] = useState<RequestState<PayslipRevealResponse>>({
		status: 'idle'
	});

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const employee = await api.get<EmployeeResponse>('/employees/me');
				const payslips = await api.get<PayslipResponse[]>(`/employees/${employee.id}/payslips`);
				setState({ status: 'success', data: payslips });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const payslips = state.status === 'success' ? state.data : [];

	// Default to the most recent payslip when list first loads
	useEffect(() => {
		if (payslips.length > 0 && selectedId === null) {
			setSelectedId(payslips[0].id);
		}
	}, [payslips, selectedId]);

	const selected = payslips.find((p) => p.id === selectedId) ?? payslips[0] ?? null;
	const revealData = revealState.status === 'success' ? revealState.data : null;

	function handleSelect(id: number) {
		if (id !== selectedId) {
			setSelectedId(id);
			setRevealed(false);
			setRevealState({ status: 'idle' });
		}
	}

	async function handleReveal() {
		if (!selected || revealed) return;
		setRevealState({ status: 'loading' });
		try {
			const data = await api.post<PayslipRevealResponse>(`/app/payslips/${selected.id}/reveal`, {});
			setRevealState({ status: 'success', data });
			setRevealed(true);
		} catch (error) {
			setRevealState({ status: 'error', message: getErrorMessage(error) });
		}
	}

	const breakdownRows =
		revealed && revealData
			? revealData.items.map((item) => ({
					label: item.name,
					amount: wonLabel(item.amount),
					negative: item.itemType === 'DEDUCTION'
				}))
			: (selected?.items ?? []).map((item) => ({
					label: item.name,
					amount: '₩ •••',
					negative: item.itemType === 'DEDUCTION'
				}));

	return (
		<div className="space-y-5">
			<MobileHeader
				title="급여명세서"
				subtitle="민감정보 보호 모드"
				trailing={
					<Button variant="outline" size="sm" onClick={() => window.print()}>
						PDF
					</Button>
				}
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<div className="flex min-h-32 items-center justify-center">
					<Spinner size="lg" />
				</div>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{state.status === 'success' ? (
				<>
					{selected ? (
						<>
							<CommandCard
								chip={
									<StatusChip tone={revealed ? 'green' : 'amber'}>
										{revealed ? '생체 인증 완료' : '보호됨'}
									</StatusChip>
								}
								title={revealed && revealData ? wonLabel(revealData.netPay) : '₩ ••••••'}
								subtitle={`${payslipPeriodLabel(selected)} 실지급액`}
								action={
									!revealed ? (
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={() => void handleReveal()}
											disabled={revealState.status === 'loading'}
										>
											{revealState.status === 'loading' ? <Spinner size="sm" /> : null}
											금액 확인
										</Button>
									) : null
								}
								aside={
									<div className="flex items-end gap-1" aria-hidden="true">
										{[40, 52, 64, 76].map((h) => (
											<div key={h} className="w-3 rounded-sm bg-white/30" style={{ height: h }} />
										))}
									</div>
								}
							/>
							{revealState.status === 'error' ? (
								<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
									{revealState.message}
								</div>
							) : null}
							{breakdownRows.length > 0 ? <MoneyBreakdown rows={breakdownRows} /> : null}
							{selected?.greeting ? (
								<p className="rounded-lg bg-muted/40 px-4 py-3 text-center text-sm text-muted-foreground">
									{selected.greeting}
								</p>
							) : null}
							{selected && revealed ? (
								<Button variant="outline" className="w-full" onClick={() => window.print()}>
									급여명세서 저장
								</Button>
							) : null}
						</>
					) : null}
					{payslips.length === 0 ? (
						<EmptyState message="조회 가능한 급여명세서가 없습니다." />
					) : (
						<div className="space-y-2">
							{payslips.map((payslip) => (
								<ListRow
									key={payslip.id}
									title={payslipPeriodLabel(payslip)}
									subtitle={dateLabel(payslip.paymentDate)}
									trailing={
										<Badge variant={payrollStatusVariant(payslip.status)}>
											{payrollStatusLabel(payslip.status)}
										</Badge>
									}
									onClick={() => handleSelect(payslip.id)}
									className={
										payslip.id === selectedId ? 'ring-2 ring-primary ring-offset-1' : undefined
									}
								/>
							))}
						</div>
					)}
				</>
			) : null}
		</div>
	);
}

export function AccountPasswordPage() {
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [message, setMessage] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateChangePasswordForm({ currentPassword, newPassword, confirmPassword });
		if (!result.ok) {
			setError(result.message);
			setMessage(null);
			return;
		}
		setSubmitting(true);
		try {
			await api.patch('/auth/me/password', result.request);
			setCurrentPassword('');
			setNewPassword('');
			setConfirmPassword('');
			setError(null);
			setMessage('비밀번호가 변경되었습니다.');
		} catch (submitError) {
			setError(getErrorMessage(submitError));
			setMessage(null);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="비밀번호 변경"
				description="현재 비밀번호 확인 후 새 비밀번호를 저장합니다."
			/>
			<Card className="max-w-xl">
				<CardHeader>
					<CardTitle>새 비밀번호 저장</CardTitle>
					<CardDescription>8자 이상이며 영문자와 숫자를 포함해야 합니다.</CardDescription>
				</CardHeader>
				<CardContent>
					<form aria-label="비밀번호 변경" className="space-y-4" onSubmit={handleSubmit}>
						<FormError message={error} />
						{message ? (
							<div className="rounded-lg bg-success/10 px-4 py-3 text-sm text-success">
								{message}
							</div>
						) : null}
						<div className="space-y-2">
							<Label htmlFor="account-current-password">현재 비밀번호</Label>
							<Input
								id="account-current-password"
								type="password"
								value={currentPassword}
								onChange={(event) => setCurrentPassword(event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="account-new-password">새 비밀번호</Label>
							<Input
								id="account-new-password"
								type="password"
								value={newPassword}
								onChange={(event) => setNewPassword(event.target.value)}
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="account-confirm-password">새 비밀번호 확인</Label>
							<Input
								id="account-confirm-password"
								type="password"
								value={confirmPassword}
								onChange={(event) => setConfirmPassword(event.target.value)}
							/>
						</div>
						<div className="flex justify-end">
							<Button type="submit" disabled={submitting}>
								저장
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}

export function NotificationsPage() {
	const [state, setState] = useState<
		RequestState<{
			summary: NotificationSummaryResponse;
			page: PageResponse<NotificationResponse>;
		}>
	>({
		status: 'idle'
	});
	const [actionError, setActionError] = useState<string | null>(null);
	const [updatingNotificationId, setUpdatingNotificationId] = useState<number | null>(null);
	const [bulkUpdating, setBulkUpdating] = useState(false);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			setActionError(null);
			try {
				const [summary, page] = await Promise.all([
					api.get<NotificationSummaryResponse>('/notifications/summary'),
					api.get<PageResponse<NotificationResponse>>('/notifications?page=0&size=20')
				]);
				setState({ status: 'success', data: { summary, page } });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const notifications = state.status === 'success' ? state.data.page.content : [];
	const summary = state.status === 'success' ? state.data.summary : null;

	function markLocalNotificationAsRead(notificationId: number) {
		setState((current) => {
			if (current.status !== 'success') return current;
			const notification = current.data.page.content.find((item) => item.id === notificationId);
			if (!notification || notification.isRead) return current;
			return {
				status: 'success',
				data: {
					summary: {
						...current.data.summary,
						unreadCount: Math.max(0, current.data.summary.unreadCount - 1),
						unreadActionRequiredCount: notification.actionRequired
							? Math.max(0, current.data.summary.unreadActionRequiredCount - 1)
							: current.data.summary.unreadActionRequiredCount
					},
					page: {
						...current.data.page,
						content: current.data.page.content.map((item) =>
							item.id === notificationId ? { ...item, isRead: true } : item
						)
					}
				}
			};
		});
	}

	async function handleMarkAsRead(notification: NotificationResponse) {
		if (notification.isRead || updatingNotificationId != null) return;
		setUpdatingNotificationId(notification.id);
		setActionError(null);
		try {
			await api.patch(`/notifications/${notification.id}/read`);
			markLocalNotificationAsRead(notification.id);
		} catch (error) {
			setActionError(getErrorMessage(error));
		} finally {
			setUpdatingNotificationId(null);
		}
	}

	async function handleMarkAllAsRead() {
		if (!summary || summary.unreadCount === 0 || bulkUpdating) return;
		setBulkUpdating(true);
		setActionError(null);
		try {
			await api.post('/notifications/read-all');
			setState((current) => {
				if (current.status !== 'success') return current;
				return {
					status: 'success',
					data: {
						summary: {
							...current.data.summary,
							unreadCount: 0,
							unreadActionRequiredCount: 0
						},
						page: {
							...current.data.page,
							content: current.data.page.content.map((item) => ({ ...item, isRead: true }))
						}
					}
				};
			});
		} catch (error) {
			setActionError(getErrorMessage(error));
		} finally {
			setBulkUpdating(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="알림"
				description="업무 처리 필요 알림과 변경 사항을 한곳에서 확인합니다."
				actions={
					<>
						{summary ? (
							<Button
								type="button"
								variant="outline"
								onClick={() => void handleMarkAllAsRead()}
								disabled={summary.unreadCount === 0 || bulkUpdating}
							>
								<CheckCircle2 className="size-4" />
								모두 읽음
							</Button>
						) : null}
						<Button type="button" variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
					</>
				}
			/>
			{actionError ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{actionError}
				</div>
			) : null}
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{state.status === 'success' ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-5">
						<ExpenseStatCard
							title="전체 알림"
							value={`${numberLabel(state.data.summary.totalCount)}건`}
							description={`${numberLabel(state.data.page.totalElements)}건 표시 가능`}
						/>
						<ExpenseStatCard
							title="처리할 일"
							value={`${numberLabel(state.data.summary.actionRequiredCount)}건`}
							description="승인·결제·기한 알림"
						/>
						<ExpenseStatCard
							title="안 읽은 처리할 일"
							value={`${numberLabel(state.data.summary.unreadActionRequiredCount)}건`}
							description={`${numberLabel(state.data.summary.unreadCount)}건 미읽음`}
						/>
						<ExpenseStatCard
							title="참조 알림"
							value={`${numberLabel(state.data.summary.referenceCount)}건`}
							description="공유·참조 문서"
						/>
						<ExpenseStatCard
							title="결과 알림"
							value={`${numberLabel(state.data.summary.resultCount)}건`}
							description="승인·급여·초대 결과"
						/>
					</div>
					<Card>
						<CardHeader>
							<CardTitle>알림 목록</CardTitle>
							<CardDescription>
								알림 제목, 처리 상태, 연결된 업무 화면을 확인합니다.
							</CardDescription>
						</CardHeader>
						<CardContent>
							{notifications.length === 0 ? (
								<EmptyState message="알림이 없습니다." />
							) : (
								<div className="divide-y divide-border rounded-lg border border-border">
									{notifications.map((notification) => (
										<div
											key={notification.id}
											className={cn(
												'p-4',
												notification.actionRequired && !notification.isRead && 'bg-warning/5'
											)}
										>
											<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
												<div className="min-w-0 space-y-2">
													<div className="flex flex-wrap items-center gap-2">
														<Badge variant={notificationCategoryVariant(notification.category)}>
															{notificationCategoryLabel(notification.category)}
														</Badge>
														<Badge variant={notification.isRead ? 'outline' : 'warning'}>
															{notification.isRead ? '읽음 완료' : '새 알림'}
														</Badge>
														<p className="text-xs text-muted-foreground">
															{dateTimeLabel(notification.createdAt)}
														</p>
													</div>
													<div>
														{notification.link ? (
															<Link
																href={notification.link}
																aria-label={`${notification.title} 열기`}
																className="inline-flex max-w-full items-center gap-1 font-semibold transition-colors hover:text-primary"
															>
																<span className="truncate">{notification.title}</span>
																<ChevronRight className="size-4 shrink-0" />
															</Link>
														) : (
															<p className="font-semibold">{notification.title}</p>
														)}
														{notification.message ? (
															<p className="mt-1 text-sm text-muted-foreground">
																{notification.message}
															</p>
														) : null}
													</div>
												</div>
												<div className="flex shrink-0 flex-wrap items-center gap-2">
													{notification.link ? (
														<Link
															href={notification.link}
															className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-semibold transition-colors hover:bg-muted"
														>
															업무 열기
														</Link>
													) : null}
													<Button
														type="button"
														variant="outline"
														size="sm"
														disabled={
															notification.isRead || updatingNotificationId === notification.id
														}
														onClick={() => void handleMarkAsRead(notification)}
													>
														<CheckCircle2 className="size-4" />
														{notification.isRead ? '읽음 완료' : '읽음 처리'}
													</Button>
												</div>
											</div>
										</div>
									))}
								</div>
							)}
						</CardContent>
					</Card>
				</>
			) : null}
		</div>
	);
}

function notificationCategoryLabel(category: NotificationResponse['category']): string {
	const labels: Record<NotificationResponse['category'], string> = {
		ACTION_REQUIRED: '업무 처리 필요',
		REFERENCE: '참조',
		RESULT: '결과',
		SYSTEM: '시스템'
	};
	return labels[category] ?? category;
}

function notificationCategoryVariant(
	category: NotificationResponse['category']
): 'default' | 'outline' | 'secondary' | 'warning' {
	if (category === 'ACTION_REQUIRED') return 'warning';
	if (category === 'REFERENCE') return 'default';
	if (category === 'RESULT') return 'outline';
	return 'secondary';
}

function timeInputValue(value: string | null | undefined): string {
	const label = timeLabel(value);
	return label === '-' ? '' : label;
}

function defaultCompanyForm(company?: CompanyResponse | null): CompanyFormValues {
	return {
		name: company?.name ?? '',
		businessNumber: company?.businessNumber ?? '',
		address: company?.address ?? '',
		phone: company?.phone ?? '',
		workStartTime: timeInputValue(company?.workStartTime) || '09:00',
		workEndTime: timeInputValue(company?.workEndTime) || '18:00'
	};
}

function defaultOfficeForm(company?: CompanyResponse | null): OfficeLocationForm {
	return {
		latitude: company?.officeLatitude == null ? '' : String(company.officeLatitude),
		longitude: company?.officeLongitude == null ? '' : String(company.officeLongitude),
		radiusMeters: company?.officeRadiusMeters ?? 200,
		verificationMethod: company?.locationVerificationMethod ?? 'NONE'
	};
}

function CompanyFormFields({
	form,
	onChange,
	nameLabel = '회사명'
}: {
	form: CompanyFormValues;
	onChange: <T extends keyof CompanyFormValues>(key: T, value: CompanyFormValues[T]) => void;
	nameLabel?: string;
}) {
	return (
		<div className="grid gap-4 sm:grid-cols-2">
			<div className="space-y-2 sm:col-span-2">
				<Label htmlFor="company-name">{nameLabel}</Label>
				<Input
					id="company-name"
					value={form.name}
					onChange={(event) => onChange('name', event.target.value)}
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="company-business-number">사업자등록번호</Label>
				<Input
					id="company-business-number"
					value={form.businessNumber}
					onChange={(event) => onChange('businessNumber', event.target.value)}
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="company-phone">대표 전화</Label>
				<Input
					id="company-phone"
					value={form.phone}
					onChange={(event) => onChange('phone', event.target.value)}
				/>
			</div>
			<div className="space-y-2 sm:col-span-2">
				<Label htmlFor="company-address">주소</Label>
				<Input
					id="company-address"
					value={form.address}
					onChange={(event) => onChange('address', event.target.value)}
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="company-work-start">업무 시작</Label>
				<Input
					id="company-work-start"
					type="time"
					value={form.workStartTime}
					onChange={(event) => onChange('workStartTime', event.target.value)}
				/>
			</div>
			<div className="space-y-2">
				<Label htmlFor="company-work-end">업무 종료</Label>
				<Input
					id="company-work-end"
					type="time"
					value={form.workEndTime}
					onChange={(event) => onChange('workEndTime', event.target.value)}
				/>
			</div>
		</div>
	);
}

export function CompanySetupPage() {
	const auth = useAuth();
	const router = useRouter();
	const [form, setForm] = useState<CompanyFormValues>(defaultCompanyForm());
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	function updateForm<T extends keyof CompanyFormValues>(key: T, value: CompanyFormValues[T]) {
		setForm((current) => ({ ...current, [key]: value }));
	}

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateCompanyForm(form);
		if (!result.ok) {
			setError(result.message);
			return;
		}
		setSubmitting(true);
		try {
			await api.post<CompanyResponse>('/companies', result.request);
			await auth.init();
			router.replace('/dashboard');
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title="회사 설정 시작"
				description="회사 정보를 등록해 AntFlow 사용을 시작합니다."
			/>
			<Card className="max-w-3xl">
				<CardHeader>
					<CardTitle>회사 등록</CardTitle>
					<CardDescription>회사명과 기본 근무 시간을 입력합니다.</CardDescription>
				</CardHeader>
				<CardContent>
					<form aria-label="회사 등록" className="space-y-5" onSubmit={handleSubmit}>
						<FormError message={error} />
						<CompanyFormFields form={form} onChange={updateForm} nameLabel="회사명 *" />
						<div className="flex justify-end">
							<Button type="submit" disabled={submitting}>
								회사 등록
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}

export function CompanySettingsPage() {
	const [state, setState] = useState<RequestState<CompanyResponse>>({ status: 'idle' });
	const [companyForm, setCompanyForm] = useState<CompanyFormValues>(defaultCompanyForm());
	const [officeForm, setOfficeForm] = useState<OfficeLocationForm>(defaultOfficeForm());
	const [companyError, setCompanyError] = useState<string | null>(null);
	const [officeError, setOfficeError] = useState<string | null>(null);
	const [savingCompany, setSavingCompany] = useState(false);
	const [savingOffice, setSavingOffice] = useState(false);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const company = await api.get<CompanyResponse>('/companies/me');
				setCompanyForm(defaultCompanyForm(company));
				setOfficeForm(defaultOfficeForm(company));
				setState({ status: 'success', data: company });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	function updateCompanyForm<T extends keyof CompanyFormValues>(
		key: T,
		value: CompanyFormValues[T]
	) {
		setCompanyForm((current) => ({ ...current, [key]: value }));
	}

	function updateOfficeForm<T extends keyof OfficeLocationForm>(
		key: T,
		value: OfficeLocationForm[T]
	) {
		setOfficeForm((current) => ({ ...current, [key]: value }));
	}

	async function handleCompanySubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateCompanyForm(companyForm);
		if (!result.ok) {
			setCompanyError(result.message);
			return;
		}
		setSavingCompany(true);
		try {
			const company = await api.patch<CompanyResponse>('/companies/me', result.request);
			setCompanyError(null);
			setState({ status: 'success', data: company });
		} catch (submitError) {
			setCompanyError(getErrorMessage(submitError));
		} finally {
			setSavingCompany(false);
		}
	}

	async function handleOfficeSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateOfficeLocationForm(officeForm);
		if (!result.ok) {
			setOfficeError(result.message);
			return;
		}
		setSavingOffice(true);
		try {
			const company = await api.put<CompanyResponse>(
				'/companies/me/office-location',
				result.request
			);
			setOfficeError(null);
			setState({ status: 'success', data: company });
		} catch (submitError) {
			setOfficeError(getErrorMessage(submitError));
		} finally {
			setSavingOffice(false);
		}
	}

	const company = state.status === 'success' ? state.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="회사"
				description="회사 정보, 근무 시간, 사무실 위치 검증 기준을 관리합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{company ? (
				<>
					<div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
						<ExpenseStatCard
							title="플랜"
							value={company.plan}
							description={`최대 ${numberLabel(company.maxEmployees)}명`}
						/>
						<ExpenseStatCard
							title="근무 시간"
							value={`${timeLabel(company.workStartTime)} - ${timeLabel(company.workEndTime)}`}
							description="회사 기본값"
						/>
						<ExpenseStatCard
							title="위치 검증"
							value={company.locationVerificationMethod}
							description={`반경 ${numberLabel(company.officeRadiusMeters)}m`}
						/>
						<ExpenseStatCard
							title="로고"
							value={company.logoFileName ?? '미등록'}
							description={dateLabel(company.logoUpdatedAt)}
						/>
					</div>
					<div className="grid gap-4 xl:grid-cols-2">
						<Card>
							<CardHeader>
								<CardTitle>회사 정보</CardTitle>
								<CardDescription>회사명, 연락처, 기본 근무 시간을 수정합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form aria-label="회사 정보" className="space-y-5" onSubmit={handleCompanySubmit}>
									<FormError message={companyError} />
									<CompanyFormFields form={companyForm} onChange={updateCompanyForm} />
									<div className="flex justify-end">
										<Button type="submit" disabled={savingCompany}>
											저장
										</Button>
									</div>
								</form>
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle>사무실 위치 (GPS 출퇴근)</CardTitle>
								<CardDescription>GPS 또는 NFC 검증 기준과 허용 반경을 설정합니다.</CardDescription>
							</CardHeader>
							<CardContent>
								<form
									aria-label="사무실 위치 설정"
									className="space-y-5"
									onSubmit={handleOfficeSubmit}
								>
									<FormError message={officeError} />
									<div className="grid gap-4 sm:grid-cols-2">
										<div className="space-y-2">
											<Label htmlFor="office-verification-method">검증 방식</Label>
											<select
												id="office-verification-method"
												value={officeForm.verificationMethod}
												onChange={(event) =>
													updateOfficeForm(
														'verificationMethod',
														event.target.value as LocationVerificationMethod
													)
												}
												className={dialogControlClassName}
											>
												<option value="NONE">사용 안 함</option>
												<option value="GPS">GPS</option>
												<option value="GPS_AND_NFC">GPS + NFC</option>
											</select>
										</div>
										<div className="space-y-2">
											<Label htmlFor="office-radius">허용 반경 (m)</Label>
											<Input
												id="office-radius"
												type="number"
												value={officeForm.radiusMeters}
												onChange={(event) => updateOfficeForm('radiusMeters', event.target.value)}
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="office-latitude">위도 (latitude)</Label>
											<Input
												id="office-latitude"
												value={officeForm.latitude}
												onChange={(event) => updateOfficeForm('latitude', event.target.value)}
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="office-longitude">경도 (longitude)</Label>
											<Input
												id="office-longitude"
												value={officeForm.longitude}
												onChange={(event) => updateOfficeForm('longitude', event.target.value)}
											/>
										</div>
									</div>
									<div className="flex justify-end">
										<Button type="submit" disabled={savingOffice}>
											저장
										</Button>
									</div>
								</form>
							</CardContent>
						</Card>
					</div>
				</>
			) : null}
		</div>
	);
}

function selectedPriceLabel(price: PlanPriceResponse | null): string {
	if (!price) return '-';
	return `${price.plan} · ${billingCycleLabel(price.billingCycle)}`;
}

export function BillingCheckoutPage() {
	const router = useRouter();
	const searchParams = useSearchParams();
	const [state, setState] = useState<
		RequestState<{ subscription: SubscriptionResponse; prices: PlanPriceResponse[] }>
	>({ status: 'idle' });
	const parsedSelection = parseBillingSelection(
		searchParams.get('plan'),
		searchParams.get('cycle')
	);
	const [plan, setPlan] = useState<BillablePlanCode>(parsedSelection.plan);
	const [billingCycle, setBillingCycle] = useState(parsedSelection.billingCycle);
	const [depositorName, setDepositorName] = useState('');
	const [payerBusinessNumber, setPayerBusinessNumber] = useState('');
	const [payerBusinessName, setPayerBusinessName] = useState('');
	const [payerEmail, setPayerEmail] = useState('');
	const [payerPhone, setPayerPhone] = useState('');
	const [error, setError] = useState<string | null>(parsedSelection.message);
	const [submitting, setSubmitting] = useState(false);

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const [subscription, prices] = await Promise.all([
					api.get<SubscriptionResponse>('/billing/subscription'),
					api.get<PlanPriceResponse[]>('/billing/plan-prices')
				]);
				setPayerBusinessNumber(subscription.payerBusinessNumber ?? '');
				setPayerBusinessName(subscription.payerBusinessName ?? '');
				setPayerEmail(subscription.payerEmail ?? '');
				setPayerPhone(subscription.payerPhone ?? '');
				setState({ status: 'success', data: { subscription, prices } });
			} catch (loadError) {
				setState({ status: 'error', message: getErrorMessage(loadError) });
			}
		},
		[]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const result = validateCheckoutPaymentForm({
			plan,
			billingCycle,
			depositorName,
			payerBusinessNumber,
			payerBusinessName,
			payerEmail,
			payerPhone
		});
		if (!result.ok) {
			setError(result.message);
			return;
		}
		setSubmitting(true);
		try {
			const payment = await api.post<PaymentResponse>('/billing/payments', result.request);
			router.replace(`/billing/payments/${payment.id}`);
		} catch (submitError) {
			setError(getErrorMessage(submitError));
		} finally {
			setSubmitting(false);
		}
	}

	const prices = state.status === 'success' ? state.data.prices : [];
	const selectedPrice =
		prices.find((price) => price.plan === plan && price.billingCycle === billingCycle) ?? null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="결제 요청"
				description="요금제와 결제 정보를 확인한 뒤 무통장 입금 요청을 생성합니다."
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{state.status === 'success' ? (
				<div className="grid gap-4 xl:grid-cols-[1fr_22rem]">
					<Card>
						<CardHeader>
							<CardTitle>요금제 선택</CardTitle>
							<CardDescription>결제할 플랜과 결제 주기를 선택합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							<form className="space-y-5" onSubmit={handleSubmit}>
								<FormError message={error} />
								<div className="grid gap-4 sm:grid-cols-2">
									<div className="space-y-2">
										<Label htmlFor="checkout-plan">플랜</Label>
										<select
											id="checkout-plan"
											value={plan}
											onChange={(event) => setPlan(event.target.value as BillablePlanCode)}
											className={dialogControlClassName}
										>
											<option value="BASIC">BASIC</option>
											<option value="STANDARD">STANDARD</option>
											<option value="PRO">PRO</option>
										</select>
									</div>
									<div className="space-y-2">
										<Label htmlFor="checkout-cycle">결제 주기</Label>
										<select
											id="checkout-cycle"
											value={billingCycle}
											onChange={(event) =>
												setBillingCycle(event.target.value as PaymentResponse['billingCycle'])
											}
											className={dialogControlClassName}
										>
											<option value="MONTHLY">월간</option>
											<option value="YEARLY">연간</option>
										</select>
									</div>
									<div className="space-y-2 sm:col-span-2">
										<Label htmlFor="checkout-depositor">입금자명</Label>
										<Input
											id="checkout-depositor"
											value={depositorName}
											onChange={(event) => setDepositorName(event.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="checkout-business-number">사업자등록번호</Label>
										<Input
											id="checkout-business-number"
											value={payerBusinessNumber}
											onChange={(event) => setPayerBusinessNumber(event.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="checkout-business-name">회사명/상호</Label>
										<Input
											id="checkout-business-name"
											value={payerBusinessName}
											onChange={(event) => setPayerBusinessName(event.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="checkout-email">영수증 받을 이메일</Label>
										<Input
											id="checkout-email"
											value={payerEmail}
											onChange={(event) => setPayerEmail(event.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<Label htmlFor="checkout-phone">담당자 연락처</Label>
										<Input
											id="checkout-phone"
											value={payerPhone}
											onChange={(event) => setPayerPhone(event.target.value)}
										/>
									</div>
								</div>
								<div className="flex justify-end">
									<Button type="submit" disabled={submitting}>
										결제 요청
									</Button>
								</div>
							</form>
						</CardContent>
					</Card>
					<Card>
						<CardHeader>
							<CardTitle>{selectedPriceLabel(selectedPrice)}</CardTitle>
							<CardDescription>입금 확인 후 운영자가 승인합니다.</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							<DetailTile label="금액" value={wonLabel(selectedPrice?.amountKrw)} />
							<DetailTile
								label="최대 직원"
								value={`${numberLabel(selectedPrice?.maxEmployees)}명`}
							/>
							<DetailTile
								label="현재 구독"
								value={`${state.data.subscription.plan} · ${subscriptionStatusLabel(state.data.subscription.status)}`}
							/>
						</CardContent>
					</Card>
				</div>
			) : null}
		</div>
	);
}

export function BillingPaymentDetailPage({ route }: { route: MatchedRoute }) {
	const [state, setState] = useState<RequestState<PaymentResponse>>({ status: 'idle' });

	const load = useMemo(
		() => async () => {
			setState({ status: 'loading' });
			try {
				const data = await api.get<PaymentResponse>(`/billing/payments/${route.params.id}`);
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[route.params.id]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (active) await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const payment = state.status === 'success' ? state.data : null;

	return (
		<div className="space-y-6">
			<PageHeader
				title="결제 상세"
				description="결제 요청 처리 상태와 입금 정보를 확인합니다."
				actions={
					<Button type="button" variant="outline" onClick={() => void load()}>
						<RefreshCw className="size-4" />
						새로고침
					</Button>
				}
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-64 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{payment ? (
				<Card>
					<CardHeader className="gap-4 lg:flex-row lg:items-start lg:justify-between">
						<div>
							<CardTitle>결제 요청 #{payment.id}</CardTitle>
							<CardDescription>
								{payment.plan} · {billingCycleLabel(payment.billingCycle)} ·{' '}
								{dateLabel(payment.createdAt)}
							</CardDescription>
						</div>
						<Badge variant={paymentStatusVariant(payment.status)}>
							{paymentStatusLabel(payment.status)}
						</Badge>
					</CardHeader>
					<CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
						<DetailTile label="금액" value={wonLabel(payment.amountKrw)} />
						<DetailTile label="입금자" value={payment.depositorName ?? '-'} />
						<DetailTile label="입금 계좌" value={payment.bankAccount ?? '-'} />
						<DetailTile label="만료" value={dateLabel(payment.expiresAt)} />
						<DetailTile label="승인일" value={dateLabel(payment.approvedAt)} />
						<DetailTile label="반려일" value={dateLabel(payment.rejectedAt)} />
						<DetailTile label="반려 사유" value={payment.rejectionReason ?? '-'} />
						<DetailTile label="요청자" value={`사용자 #${payment.requestedByUserId}`} />
					</CardContent>
				</Card>
			) : null}
		</div>
	);
}

export function ResourcePage({ route }: { route: MatchedRoute }) {
	const endpoint = resolveEndpoint(route);
	const [state, setState] = useState<RequestState<unknown>>({ status: 'idle' });

	const load = useMemo(
		() => async () => {
			if (!endpoint) {
				setState({ status: 'success', data: null });
				return;
			}
			setState({ status: 'loading' });
			try {
				const data = await api.get<unknown>(endpoint);
				setState({ status: 'success', data });
			} catch (error) {
				setState({ status: 'error', message: getErrorMessage(error) });
			}
		},
		[endpoint]
	);

	useEffect(() => {
		let active = true;
		void Promise.resolve().then(async () => {
			if (!active) return;
			await load();
		});
		return () => {
			active = false;
		};
	}, [load]);

	const rows = state.status === 'success' ? extractRows(state.data) : [];
	const summary = state.status === 'success' ? flattenSummary(state.data) : [];

	return (
		<div className="space-y-6">
			<PageHeader
				title={route.definition.title}
				description={route.definition.description}
				actions={
					<div className="flex gap-2">
						<Button variant="outline" onClick={() => void load()}>
							<RefreshCw className="size-4" />
							새로고침
						</Button>
						{endpoint ? (
							<Badge variant="outline" className="hidden sm:inline-flex">
								{endpoint}
							</Badge>
						) : null}
					</div>
				}
			/>
			{state.status === 'loading' || state.status === 'idle' ? (
				<Card>
					<CardContent className="flex min-h-48 items-center justify-center">
						<Spinner size="lg" />
					</CardContent>
				</Card>
			) : null}
			{state.status === 'error' ? (
				<div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
					{state.message}
				</div>
			) : null}
			{state.status === 'success' ? (
				<div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
					<Card>
						<CardHeader>
							<CardTitle>데이터</CardTitle>
							<CardDescription>연결된 업무 데이터를 화면에 표시합니다.</CardDescription>
						</CardHeader>
						<CardContent>
							{rows.length > 0 ? (
								<div className="overflow-x-auto rounded-lg border border-border">
									<table className="w-full min-w-[560px] text-left text-sm">
										<thead className="bg-muted/40 text-xs text-muted-foreground">
											<tr>
												{Object.keys((rows[0] as Record<string, unknown>) ?? {})
													.slice(0, 6)
													.map((key) => (
														<th key={key} className="px-4 py-3 font-semibold">
															{key}
														</th>
													))}
											</tr>
										</thead>
										<tbody>
											{rows.slice(0, 20).map((row, index) => (
												<tr key={index} className="border-t border-border">
													{Object.values((row as Record<string, unknown>) ?? {})
														.slice(0, 6)
														.map((value, valueIndex) => (
															<td key={valueIndex} className="max-w-[16rem] truncate px-4 py-3">
																{valueLabel(value)}
															</td>
														))}
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : summary.length > 0 ? (
								<div className="grid gap-3 sm:grid-cols-2">
									{summary.map(([key, value]) => (
										<div key={key} className="rounded-lg border border-border bg-muted/20 p-4">
											<p className="text-xs font-medium text-muted-foreground">{key}</p>
											<p className="mt-1 truncate text-sm font-semibold">{value}</p>
										</div>
									))}
								</div>
							) : (
								<EmptyState message="표시할 데이터가 없습니다." />
							)}
						</CardContent>
					</Card>
					<Card>
						<CardHeader>
							<CardTitle>연결 상태</CardTitle>
							<CardDescription>현재 화면의 조회와 파일 연결 상태를 확인합니다.</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3 text-sm">
							<div className="flex items-center gap-2 text-success">
								<BadgeCheck className="size-4" />
								화면 경로 연결
							</div>
							<div className="flex items-center gap-2 text-success">
								<BadgeCheck className="size-4" />
								권한 확인 후 조회
							</div>
							<div className="flex items-center gap-2 text-muted-foreground">
								<Download className="size-4" />
								파일 다운로드 연결
							</div>
						</CardContent>
					</Card>
				</div>
			) : null}
		</div>
	);
}
